export type AceStepAudioInpaintInput = {
  /**
   * URL of the audio file to be inpainted.
   */
  audio_url: string | Blob | File;
  /**
   * end time in seconds for the inpainting process. Default value: `30`
   */
  end_time?: number;
  /**
   * Whether the end time is relative to the start or end of the audio. Default value: `"start"`
   */
  end_time_relative_to?: "start" | "end";
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
  /**
   * Lyrics to be sung in the audio. If not provided or if [inst] or [instrumental] is the content of this field, no lyrics will be sung. Use control structures like [verse], [chorus] and [bridge] to control the structure of the song. Default value: `""`
   */
  lyrics?: string;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * start time in seconds for the inpainting process.
   */
  start_time?: number;
  /**
   * Whether the start time is relative to the start or end of the audio. Default value: `"start"`
   */
  start_time_relative_to?: "start" | "end";
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Comma-separated list of genre tags to control the style of the generated audio.
   */
  tags: string;
  /**
   * Variance for the inpainting process. Higher values can lead to more diverse results. Default value: `0.5`
   */
  variance?: number;
};
export type AceStepAudioOutpaintInput = {
  /**
   * URL of the audio file to be outpainted.
   */
  audio_url: string | Blob | File;
  /**
   * Duration in seconds to extend the audio from the end. Default value: `30`
   */
  extend_after_duration?: number;
  /**
   * Duration in seconds to extend the audio from the start.
   */
  extend_before_duration?: number;
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
  /**
   * Lyrics to be sung in the audio. If not provided or if [inst] or [instrumental] is the content of this field, no lyrics will be sung. Use control structures like [verse], [chorus] and [bridge] to control the structure of the song. Default value: `""`
   */
  lyrics?: string;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Comma-separated list of genre tags to control the style of the generated audio.
   */
  tags: string;
};
export type AceStepAudioToAudioInput = {
  /**
   * URL of the audio file to be outpainted.
   */
  audio_url: string | Blob | File;
  /**
   * Whether to edit the lyrics only or remix the audio. Default value: `"remix"`
   */
  edit_mode?: "lyrics" | "remix";
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
  /**
   * Lyrics to be sung in the audio. If not provided or if [inst] or [instrumental] is the content of this field, no lyrics will be sung. Use control structures like [verse], [chorus] and [bridge] to control the structure of the song. Default value: `""`
   */
  lyrics?: string;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Original lyrics of the audio file. Default value: `""`
   */
  original_lyrics?: string;
  /**
   * Original seed of the audio file.
   */
  original_seed?: number;
  /**
   * Original tags of the audio file.
   */
  original_tags: string;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Comma-separated list of genre tags to control the style of the generated audio.
   */
  tags: string;
};
export type AceStepInput = {
  /**
   * The duration of the generated audio in seconds. Default value: `60`
   */
  duration?: number;
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
  /**
   * Lyrics to be sung in the audio. If not provided or if [inst] or [instrumental] is the content of this field, no lyrics will be sung. Use control structures like [verse], [chorus] and [bridge] to control the structure of the song. Default value: `""`
   */
  lyrics?: string;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Comma-separated list of genre tags to control the style of the generated audio.
   */
  tags: string;
};
export type AceStepOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The lyrics used in the generation process.
   */
  lyrics: string;
  /**
   * The random seed used for the generation process.
   */
  seed: number;
  /**
   * The genre tags used in the generation process.
   */
  tags: string;
};
export type AceStepPromptToAudioInput = {
  /**
   * The duration of the generated audio in seconds. Default value: `60`
   */
  duration?: number;
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Whether to generate an instrumental version of the audio.
   */
  instrumental?: boolean;
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Prompt to control the style of the generated audio. This will be used to generate tags and lyrics.
   */
  prompt: string;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
};
export type AddSubtitlesToVideoInput = {
  /**
   * Background box color for subtitle text (use 'none' for no background, TikTok style uses no background) Default value: `none`
   */
  background_color?:
    | "black"
    | "white"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta"
    | "transparent"
    | "none";
  /**
   * Opacity of subtitle background box if used (0.0-1.0, 0.0=transparent)
   */
  background_opacity?: number;
  /**
   * Subtitle text color Default value: `"white"`
   */
  font_color?:
    | "white"
    | "black"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta";
  /**
   * Any Google Font name from fonts.google.com (e.g., 'Montserrat', 'Poppins', 'BBH Sans Hegarty'). TikTok commonly uses bold sans-serif fonts. Default value: `"Montserrat"`
   */
  font_name?: string;
  /**
   * Font size for subtitles (TikTok style uses larger, bold text) Default value: `70`
   */
  font_size?: number;
  /**
   * Font weight (TikTok style typically uses bold or black) Default value: `"bold"`
   */
  font_weight?: "normal" | "bold" | "black";
  /**
   * Vertical position of subtitles on screen Default value: `"bottom"`
   */
  position?: "bottom" | "top" | "center";
  /**
   * Text stroke/outline color Default value: `"black"`
   */
  stroke_color?:
    | "black"
    | "white"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta";
  /**
   * Text stroke/outline width in pixels (0 for no stroke, TikTok uses 2-4) Default value: `3`
   */
  stroke_width?: number;
  /**
   * List of subtitle segments. Click + to add more subtitle segments.
   */
  subtitles: Array<SubtitleSegment>;
  /**
   * URL of the video file to add subtitles to
   */
  video_url: string | Blob | File;
  /**
   * Vertical offset from position in pixels (-500 to 500, positive moves down)
   */
  y_offset?: number;
};
export type AddTextToImageInput = {
  /**
   * Text anchor point Default value: `"center"`
   */
  anchor?: "left" | "center" | "right";
  /**
   * Optional background color for text box (use 'none' or leave empty for no background)
   */
  background_color?:
    | "black"
    | "white"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta"
    | "transparent"
    | "none";
  /**
   * Padding around text in pixels (only if background_color is set) Default value: `10`
   */
  background_padding?: number;
  /**
   * Text color Default value: `"white"`
   */
  font_color?:
    | "white"
    | "black"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta";
  /**
   * Font size in pixels Default value: `40`
   */
  font_size?: number;
  /**
   * The URL of the image to add text to
   */
  image_url: string | Blob | File;
  /**
   * Output format for the result image Default value: `"png"`
   */
  output_format?: "png" | "jpg" | "jpeg" | "webp";
  /**
   * Color of text stroke/outline Default value: `"black"`
   */
  stroke_color?:
    | "black"
    | "white"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta";
  /**
   * Width of text outline/stroke in pixels (0 for no stroke)
   */
  stroke_width?: number;
  /**
   * Text to add to the image
   */
  text: string;
  /**
   * X position as percentage of image width (0-100) Default value: `50`
   */
  x_percent?: number;
  /**
   * Y position as percentage of image height (0-100) Default value: `50`
   */
  y_percent?: number;
};
export type AdvancedInput = {
  /**
   * Default caption to use if no caption is found for a media file.
   */
  default_caption?: string;
  /**
   * The multiplier for the learning rate for the high resolution training stage. This is useful when you want to train the high resolution stage with a different learning rate than the low resolution stage. Default value: `2.5`
   */
  hires_lr_multiplier?: number;
  /**
   * Whether to include synthetic captions.
   */
  include_synthetic_captions?: boolean;
  /**
   * The percentage of the image to use for low resolution training. 0.0 means no low resolution training, 1.0 means full low resolution training. Default value: `0.3`
   */
  low_res_percentage?: number;
  /**
   * URL to the training data.
   */
  training_data_url: string | Blob | File;
  /**
   * List of training stages. Each stage can have different parameters.
   */
  training_stages?: Array<TrainingStage>;
  /**
   * Trigger phrase for the model.
   */
  trigger_phrase: string;
  /**
   * Whether to use face cropping for the training data. When enabled, images will be cropped to the face before resizing.
   */
  use_face_cropping?: boolean;
  /**
   * Whether to use face detection for the training data. When enabled, images will use the center of the face as the center of the image when resizing. Default value: `true`
   */
  use_face_detection?: boolean;
  /**
   * Whether to use masks for the training data. Default value: `true`
   */
  use_masks?: boolean;
};
export type AdvancedLipsyncCreateTaskInput = {
  /**
   * Specified Face for Lip-Sync. Includes Face ID, lip movement reference data, etc. Currently only supports one person lip-sync.
   */
  face_choose: FaceChoice;
  /**
   * The session id of the lip-sync task
   */
  session_id: string;
};
export type AdvancedLipsyncOutput = {
  /**
   * The generated lip-sync video
   */
  videos: Array<File>;
};
export type Aesthetics = {
  /**
   * The aesthetic score of the image (e.g., 'very high', 'high', 'medium', 'low').
   */
  aesthetic_score?: string;
  /**
   * The color scheme of the image to be generated.
   */
  color_scheme?: string;
  /**
   * The composition of the image to be generated.
   */
  composition?: string;
  /**
   * The mood and atmosphere of the image to be generated.
   */
  mood_atmosphere?: string;
  /**
   * The preference score of the image (e.g., 'very high', 'high', 'medium', 'low').
   */
  preference_score?: string;
};
export type AgeModifyInput = {
  /**
   * Aspect ratio for 4K output (default: 3:4 for portraits)
   */
  aspect_ratio?: AspectRatio;
  /**
   * Portrait image URL for age modification
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `true`
   */
  preserve_identity?: boolean;
  /**
   *  Default value: `30`
   */
  target_age?: number;
};
export type AIAvatarInput = {
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * The URL of the image to use as your avatar
   */
  image_url: string | Blob | File;
  /**
   * The prompt to use for the video generation. Default value: `"."`
   */
  prompt?: string;
};
export type AiAvatarMultiInput = {
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The URL of the Person 1 audio file.
   */
  first_audio_url: string | Blob | File;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 81 to 129 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `181`
   */
  num_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The URL of the Person 2 audio file.
   */
  second_audio_url?: string | Blob | File;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `81`
   */
  seed?: number;
  /**
   * Whether to use only the first audio file.
   */
  use_only_first_audio?: boolean;
};
export type AiAvatarMultiTextInput = {
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The text input to guide video generation.
   */
  first_text_input: string;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 81 to 129 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `191`
   */
  num_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The text input to guide video generation.
   */
  second_text_input: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `81`
   */
  seed?: number;
  /**
   * The first person's voice to use for speech generation Default value: `"Sarah"`
   */
  voice1?:
    | "Aria"
    | "Roger"
    | "Sarah"
    | "Laura"
    | "Charlie"
    | "George"
    | "Callum"
    | "River"
    | "Liam"
    | "Charlotte"
    | "Alice"
    | "Matilda"
    | "Will"
    | "Jessica"
    | "Eric"
    | "Chris"
    | "Brian"
    | "Daniel"
    | "Lily"
    | "Bill";
  /**
   * The second person's voice to use for speech generation Default value: `"Roger"`
   */
  voice2?:
    | "Aria"
    | "Roger"
    | "Sarah"
    | "Laura"
    | "Charlie"
    | "George"
    | "Callum"
    | "River"
    | "Liam"
    | "Charlotte"
    | "Alice"
    | "Matilda"
    | "Will"
    | "Jessica"
    | "Eric"
    | "Chris"
    | "Brian"
    | "Daniel"
    | "Lily"
    | "Bill";
};
export type AIAvatarOutput = {
  /**
   * Duration of the output video in seconds.
   */
  duration: number;
  /**
   * The generated video
   */
  video: File;
};
export type AiAvatarSingleTextInput = {
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 81 to 129 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `136`
   */
  num_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `42`
   */
  seed?: number;
  /**
   * The text input to guide video generation.
   */
  text_input: string;
  /**
   * The voice to use for speech generation
   */
  voice:
    | "Aria"
    | "Roger"
    | "Sarah"
    | "Laura"
    | "Charlie"
    | "George"
    | "Callum"
    | "River"
    | "Liam"
    | "Charlotte"
    | "Alice"
    | "Matilda"
    | "Will"
    | "Jessica"
    | "Eric"
    | "Chris"
    | "Brian"
    | "Daniel"
    | "Lily"
    | "Bill";
};
export type AllInFocusInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image to deblur and restore to all-in-focus.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Lower values are faster but may reduce quality. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The same seed and the same input given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Resize the image so the longer side matches this value (in pixels). If not set, the original resolution is used (aligned to 16px). Recommended range: 512 to 2000. Default value: `512`
   */
  target_long_side?: number;
};
export type AmixAudioInput = {
  /**
   * List of audio file URLs to mix together
   */
  audio_urls: Array<string>;
  /**
   * Transition time in seconds for volume renormalization when an input ends Default value: `2`
   */
  dropout_transition?: number;
  /**
   * How to determine output duration Default value: `"longest"`
   */
  duration?: "longest" | "shortest" | "first";
  /**
   * Always scale inputs instead of only doing summation. Prevents clipping Default value: `true`
   */
  normalize?: boolean;
  /**
   * Optional weights for each audio input. If fewer weights than inputs, last weight applies to remaining. Default is 1.0 for all
   */
  weights?: Array<number>;
};
export type AMTFrameInterpolationInput = {
  /**
   * Frames to interpolate
   */
  frames: Array<Frame>;
  /**
   * Output frames per second Default value: `24`
   */
  output_fps?: number;
  /**
   * Number of recursive interpolation passes Default value: `4`
   */
  recursive_interpolation_passes?: number;
};
export type AmtInterpolationInput = {
  /**
   * Output frames per second Default value: `24`
   */
  output_fps?: number;
  /**
   * Number of recursive interpolation passes Default value: `2`
   */
  recursive_interpolation_passes?: number;
  /**
   * URL of the video to be processed
   */
  video_url: string | Blob | File;
};
export type AnimatediffSparsectrlLcmInput = {
  /**
   * The type of controlnet to use for generating the video. The controlnet determines how the video will be animated. Default value: `"scribble"`
   */
  controlnet_type?: "scribble" | "rgb";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The URL of the first keyframe to use for the generation.
   */
  keyframe_0_image_url?: string | Blob | File;
  /**
   * The frame index of the first keyframe to use for the generation.
   */
  keyframe_0_index?: number;
  /**
   * The URL of the second keyframe to use for the generation.
   */
  keyframe_1_image_url?: string | Blob | File;
  /**
   * The frame index of the second keyframe to use for the generation.
   */
  keyframe_1_index?: number;
  /**
   * The URL of the third keyframe to use for the generation.
   */
  keyframe_2_image_url?: string | Blob | File;
  /**
   * The frame index of the third keyframe to use for the generation.
   */
  keyframe_2_index?: number;
  /**
   * The negative prompt to use. Use it to specify what you don't want. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps to generate your final result which can increase the amount of detail in your image. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable
   * Diffusion will output the same image every time.
   */
  seed?: number;
};
export type AnimateDiffT2VInput = {
  /**
   * Number of frames per second to extract from the video. Default value: `8`
   */
  fps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The motions to apply to the video.
   */
  motions?: Array<
    "zoom-out" | "zoom-in" | "pan-left" | "pan-right" | "tilt-up" | "tilt-down"
  >;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"(bad quality, worst quality:1.2), ugly faces, bad anime"`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate for the video. Default value: `16`
   */
  num_frames?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the video. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The size of the video to generate. Default value: `square`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type AnimateDiffT2VTurboInput = {
  /**
   * Number of frames per second to extract from the video. Default value: `8`
   */
  fps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  guidance_scale?: number;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"(bad quality, worst quality:1.2), ugly faces, bad anime"`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate for the video. Default value: `16`
   */
  num_frames?: number;
  /**
   * The number of inference steps to perform. 4-12 is recommended for turbo mode. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the video. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The size of the video to generate. Default value: `square`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type AnimateDiffV2VInput = {
  /**
   * The first N number of seconds of video to animate. Default value: `3`
   */
  first_n_seconds?: number;
  /**
   * Number of frames per second to extract from the video. Default value: `8`
   */
  fps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The motions to apply to the video.
   */
  motions?: Array<
    "zoom-out" | "zoom-in" | "pan-left" | "pan-right" | "tilt-up" | "tilt-down"
  >;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"(bad quality, worst quality:1.2), ugly faces, bad anime"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the input video in the final output. Default value: `0.7`
   */
  strength?: number;
  /**
   * URL of the video.
   */
  video_url: string | Blob | File;
};
export type AnimateDiffV2VTurboInput = {
  /**
   * The first N number of seconds of video to animate. Default value: `3`
   */
  first_n_seconds?: number;
  /**
   * Number of frames per second to extract from the video. Default value: `8`
   */
  fps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  guidance_scale?: number;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"(bad quality, worst quality:1.2), ugly faces, bad anime"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. 4-12 is recommended for turbo mode. Default value: `12`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the input video in the final output. Default value: `0.7`
   */
  strength?: number;
  /**
   * URL of the video.
   */
  video_url: string | Blob | File;
};
export type ArbiterImageImageInput = {
  /**
   * The inputs to use for the measurement.
   */
  inputs: Array<ReferenceImageInput>;
  /**
   * The measurements to use for the measurement.
   */
  measurements: Array<"dists" | "mse" | "lpips" | "sdi" | "ssim">;
};
export type ArbiterImageInput = {
  /**
   * The inputs to use for the measurement.
   */
  inputs: Array<ImageInput>;
  /**
   * The measurements to use for the measurement.
   */
  measurements: Array<"arniqa" | "clip_iqa" | "musiq" | "nima" | "lapvar">;
};
export type ArbiterImageOutput = {
  /**
   * The values of the measurements.
   */
  values?: Array<unknown>;
};
export type ArbiterImageTextInput = {
  /**
   * The inputs to use for the measurement.
   */
  inputs: Array<SemanticImageInput>;
  /**
   * The measurements to use for the measurement.
   */
  measurements: Array<string>;
};
export type AspectRatio = {
  /**
   * Aspect ratio for 4K resolution output Default value: `"1:1"`
   */
  ratio?: "1:1" | "16:9" | "9:16" | "4:3" | "3:4";
};
export type Audio = {
  /**
   * Overall bitrate of the media in bits per second
   */
  bitrate: number;
  /**
   * Number of audio channels
   */
  channels: number;
  /**
   * Codec used to encode the media
   */
  codec: string;
  /**
   * Container format of the media file (e.g., 'mp4', 'mov')
   */
  container: string;
  /**
   * MIME type of the media file
   */
  content_type: string;
  /**
   * Duration of the media in seconds
   */
  duration: number;
  /**
   * Original filename of the media
   */
  file_name: string;
  /**
   * Size of the file in bytes
   */
  file_size: number;
  /**
   * Type of media (always 'audio') Default value: `"audio"`
   */
  media_type?: string;
  /**
   * Audio sample rate in Hz
   */
  sample_rate: number;
  /**
   * URL where the media file can be accessed
   */
  url: string;
};
export type Audio2VideoInput = {
  /**
   *
   */
  audio_url: string | Blob | File;
  /**
   * The avatar to use for the video
   */
  avatar_id:
    | "emily_vertical_primary"
    | "emily_vertical_secondary"
    | "marcus_vertical_primary"
    | "marcus_vertical_secondary"
    | "mira_vertical_primary"
    | "mira_vertical_secondary"
    | "jasmine_vertical_primary"
    | "jasmine_vertical_secondary"
    | "jasmine_vertical_walking"
    | "aisha_vertical_walking"
    | "elena_vertical_primary"
    | "elena_vertical_secondary"
    | "any_male_vertical_primary"
    | "any_female_vertical_primary"
    | "any_male_vertical_secondary"
    | "any_female_vertical_secondary"
    | "any_female_vertical_walking"
    | "emily_primary"
    | "emily_side"
    | "marcus_primary"
    | "marcus_side"
    | "aisha_walking"
    | "elena_primary"
    | "elena_side"
    | "any_male_primary"
    | "any_female_primary"
    | "any_male_side"
    | "any_female_side";
};
export type AudioCompressorInput = {
  /**
   * Attack time in milliseconds (how fast compression starts) Default value: `5`
   */
  attack?: number;
  /**
   * URL of the audio file to compress
   */
  audio_url: string | Blob | File;
  /**
   * Knee width in dB for soft knee compression (0 = hard knee) Default value: `2.83`
   */
  knee?: number;
  /**
   * Makeup gain in dB to compensate for volume reduction Default value: `8`
   */
  makeup?: number;
  /**
   * Output audio bitrate Default value: `"192k"`
   */
  output_bitrate?: "128k" | "192k" | "256k" | "320k";
  /**
   * Compression ratio (1 = no compression, higher = more compression) Default value: `3`
   */
  ratio?: number;
  /**
   * Release time in milliseconds (how fast compression stops) Default value: `50`
   */
  release?: number;
  /**
   * Threshold level in dB above which compression is applied (-60 to 0) Default value: `-18`
   */
  threshold?: number;
};
export type AudioEqualizerInput = {
  /**
   * URL of the audio file to equalize
   */
  audio_url: string | Blob | File;
  /**
   * List of EQ bands to apply. Each band has frequency, width, and gain.
   */
  bands: Array<EQBand>;
  /**
   * Output audio bitrate Default value: `"192k"`
   */
  output_bitrate?: "128k" | "192k" | "256k" | "320k";
};
export type AudioFile = {
  /**
   * The bitrate of the audio (e.g., '192k' or 192000)
   */
  bitrate?: string | number;
  /**
   * The number of channels in the audio
   */
  channels?: number;
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * The duration of the audio
   */
  duration?: number;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The sample rate of the audio
   */
  sample_rate?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
};
export type AudioInput = {
  /**
   * URL or data URI of the audio file to process. Supported formats: wav, mp3, aiff, aac, ogg, flac, m4a.
   */
  audio_url: string | Blob | File;
  /**
   * This sets the upper limit for the number of tokens the model can generate in response. It won't produce more than this limit. The maximum value is the context length minus the prompt length.
   */
  max_tokens?: number;
  /**
   * Name of the model to use. Charged based on actual token usage.
   */
  model: string;
  /**
   * Prompt to be used for the audio processing
   */
  prompt: string;
  /**
   * Should reasoning be the part of the final answer.
   */
  reasoning?: boolean;
  /**
   * System prompt to provide context or instructions to the model
   */
  system_prompt?: string;
  /**
   * This setting influences the variety in the model's responses. Lower values lead to more predictable and typical responses, while higher values encourage more diverse and less common responses. At 0, the model always gives the same response for a given input. Default value: `1`
   */
  temperature?: number;
};
export type AudioOutput = {
  /**
   * Generated output from audio processing
   */
  output: string;
  /**
   * Token usage information
   */
  usage: UsageInfo;
};
export type AudioSetting = {
  /**
   * Bitrate of generated audio Default value: `"128000"`
   */
  bitrate?: "32000" | "64000" | "128000" | "256000";
  /**
   * Number of audio channels (1=mono, 2=stereo) Default value: `"1"`
   */
  channel?: "1" | "2";
  /**
   * Audio format Default value: `"mp3"`
   */
  format?: "mp3" | "pcm" | "flac";
  /**
   * Sample rate of generated audio Default value: `"32000"`
   */
  sample_rate?: "8000" | "16000" | "22050" | "24000" | "32000" | "44100";
};
export type AudioTimeSpan = {
  /**
   * End time of the span in seconds
   */
  end: number;
  /**
   * Whether to include (True) or exclude (False) sounds in this span Default value: `true`
   */
  include?: boolean;
  /**
   * Start time of the span in seconds
   */
  start: number;
};
export type AudioToAudioInput = {
  /**
   * The audio clip to transform
   */
  audio_url: string | Blob | File;
  /**
   * How strictly the diffusion process adheres to the prompt text (higher values make your audio closer to your prompt). Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The number of steps to denoise the audio for Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The prompt to guide the audio generation
   */
  prompt: string;
  /**
   *
   */
  seed?: number;
  /**
   * Sometimes referred to as denoising, this parameter controls how much influence the `audio_url` parameter has on the generated audio. A value of 0 would yield audio that is identical to the input. A value of 1 would be as if you passed in no audio at all. Default value: `0.8`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The duration of the audio clip to generate. If not provided, it will be set to the duration of the input audio.
   */
  total_seconds?: number;
};
export type AudioTrack = {
  /**
   * Audio bitrate in bits per second
   */
  bitrate: number;
  /**
   * Number of audio channels
   */
  channels: number;
  /**
   * Audio codec used (e.g., 'aac', 'mp3')
   */
  codec: string;
  /**
   * Audio sample rate in Hz
   */
  sample_rate: number;
};
export type AudioUnderstandingInput = {
  /**
   * URL of the audio file to analyze
   */
  audio_url: string | Blob | File;
  /**
   * Whether to request a more detailed analysis of the audio
   */
  detailed_analysis?: boolean;
  /**
   * The question or prompt about the audio content.
   */
  prompt: string;
};
export type AudioVolumeInput = {
  /**
   * URL of the audio file to process
   */
  audio_url: string | Blob | File;
  /**
   * Fade-in duration in seconds from the start of the audio. None = no fade-in.
   */
  fade_in?: number;
  /**
   * Fade-out duration in seconds before the end of the audio. None = no fade-out.
   */
  fade_out?: number;
  /**
   * Output audio format. If not provided, the format is determined automatically.
   */
  output_format?: "mp3" | "wav" | "aac" | "flac";
  /**
   * Volume multiplier (0.0 = silent, 1.0 = unchanged, 2.0 = double volume) Default value: `1`
   */
  volume?: number;
};
export type AudioVolumeOutput = {
  /**
   * The processed audio file
   */
  audio: File;
  /**
   * Audio duration in seconds
   */
  duration_seconds: number;
};
export type AuraFlowInput = {
  /**
   * Whether to perform prompt expansion (recommended) Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Classifier free guidance scale Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to take Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate images from
   */
  prompt: string;
  /**
   * The seed to use for generating images
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type AuraFlowOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * The expanded prompt
   */
  prompt: string;
  /**
   * The seed used to generate the images
   */
  seed: number;
};
export type AuraSrInput = {
  /**
   * Checkpoint to use for upscaling. More coming soon. Default value: `"v1"`
   */
  checkpoint?: "v1" | "v2";
  /**
   * URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * Whether to use overlapping tiles for upscaling. Setting this to true helps remove seams but doubles the inference time.
   */
  overlapping_tiles?: boolean;
  /**
   * Upscaling factor. More coming soon. Default value: `4`
   */
  upscale_factor?: number;
};
export type AuraSrOutput = {
  /**
   * Upscaled image
   */
  image: Image;
  /**
   * Timings for each step in the pipeline.
   */
  timings: unknown;
};
export type AutoCaptionInput = {
  /**
   * Size of text in generated captions. Default value: `24`
   */
  font_size?: number;
  /**
   * Left-to-right alignment of the text. Can be a string ('left', 'center', 'right') or a float (0.0-1.0) Default value: `center`
   */
  left_align?: string | number;
  /**
   * Number of seconds the captions should stay on screen. A higher number will also result in more text being displayed at once. Default value: `1.5`
   */
  refresh_interval?: number;
  /**
   * Width of the text strokes in pixels Default value: `1`
   */
  stroke_width?: number;
  /**
   * Top-to-bottom alignment of the text. Can be a string ('top', 'center', 'bottom') or a float (0.0-1.0) Default value: `center`
   */
  top_align?: string | number;
  /**
   * Colour of the text. Can be a RGB tuple, a color name, or an hexadecimal notation. Default value: `"white"`
   */
  txt_color?: string;
  /**
   * Font for generated captions. Choose one in 'Arial','Standard','Garamond', 'Times New Roman','Georgia', or pass a url to a .ttf file Default value: `"Standard"`
   */
  txt_font?: string;
  /**
   * URL to the .mp4 video with audio. Only videos of size <400MB are allowed.
   */
  video_url: string | Blob | File;
};
export type AutoCaptionOutput = {
  /**
   * URL to the caption .mp4 video.
   */
  video_url: string | Blob | File;
};
export type AutoSubtitleInput = {
  /**
   * Background color behind text ('none' or 'transparent' for no background) Default value: `"none"`
   */
  background_color?:
    | "black"
    | "white"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta"
    | "none"
    | "transparent";
  /**
   * Background opacity (0.0 = fully transparent, 1.0 = fully opaque)
   */
  background_opacity?: number;
  /**
   * Enable animation effects for subtitles (bounce style entrance) Default value: `true`
   */
  enable_animation?: boolean;
  /**
   * Subtitle text color for non-active words Default value: `"white"`
   */
  font_color?:
    | "white"
    | "black"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta";
  /**
   * Any Google Font name from fonts.google.com (e.g., 'Montserrat', 'Poppins', 'BBH Sans Hegarty') Default value: `"Montserrat"`
   */
  font_name?: string;
  /**
   * Font size for subtitles (TikTok style uses larger text) Default value: `100`
   */
  font_size?: number;
  /**
   * Font weight (TikTok style typically uses bold or black) Default value: `"bold"`
   */
  font_weight?: "normal" | "bold" | "black";
  /**
   * Color for the currently speaking word (karaoke-style highlight) Default value: `"purple"`
   */
  highlight_color?:
    | "white"
    | "black"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta";
  /**
   * Language code for transcription (e.g., 'en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'ja', 'zh', 'ko') or 3-letter ISO code (e.g., 'eng', 'spa', 'fra') Default value: `"en"`
   */
  language?: string;
  /**
   * Vertical position of subtitles Default value: `"bottom"`
   */
  position?: "top" | "center" | "bottom";
  /**
   * Text stroke/outline color Default value: `"black"`
   */
  stroke_color?:
    | "black"
    | "white"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta";
  /**
   * Text stroke/outline width in pixels (0 for no stroke) Default value: `3`
   */
  stroke_width?: number;
  /**
   * URL of the video file to add automatic subtitles to
   */
  video_url: string | Blob | File;
  /**
   * Maximum number of words per subtitle segment. Use 1 for single-word display, 2-3 for short phrases, or 8-12 for full sentences. Default value: `3`
   */
  words_per_subtitle?: number;
  /**
   * Vertical offset in pixels (positive = move down, negative = move up) Default value: `75`
   */
  y_offset?: number;
};
export type AutoSubtitleOutput = {
  /**
   * Number of subtitle segments generated
   */
  subtitle_count: number;
  /**
   * Full transcription text
   */
  transcription: string;
  /**
   * Additional transcription metadata from ElevenLabs (language, segments, etc.)
   */
  transcription_metadata?: unknown;
  /**
   * The video with automatic subtitles
   */
  video: File;
  /**
   * Word-level timing information from transcription service
   */
  words?: Array<unknown>;
};
export type AvatarIVBackground = {
  /**
   * Type of background Default value: `"color"`
   */
  type?: "color" | "image" | "video";
  /**
   * Background value - hex color for 'color' type, URL for 'image' or 'video' type Default value: `"#FFFFFF"`
   */
  value?: string;
};
export type BabyVersionInput = {
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the image to transform into a baby version.
   */
  image_url: string | Blob | File;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BackgroundRemovalInput = {
  /**
   * Input image (JPEG or PNG)
   */
  image_url: string | Blob | File;
  /**
   * Output format Default value: `"rgba"`
   */
  output_format?: "rgba" | "alpha" | "zip";
  /**
   * When true, return result as a data URL instead of uploading to storage Default value: `true`
   */
  sync_mode?: boolean;
};
export type BackgroundRemovalOutput = {
  /**
   * Result file (for zip output format)
   */
  file?: File;
  /**
   * Result image (for rgba/alpha output formats)
   */
  image?: Image;
};
export type BagelEditInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to edit the image with.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * Whether to use thought tokens for generation. If set to true, the model will "think" to potentially improve generation quality. Increases generation time and increases the cost by 20%.
   */
  use_thought?: boolean;
};
export type bagelInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * Whether to use thought tokens for generation. If set to true, the model will "think" to potentially improve generation quality. Increases generation time and increases the cost by 20%.
   */
  use_thought?: boolean;
};
export type BagelUnderstandInput = {
  /**
   * The image for the query.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to query the image with.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
};
export type BagelUnderstandOutput = {
  /**
   * The query used for the generation.
   */
  prompt: string;
  /**
   * The seed used for the generation.
   */
  seed: number;
  /**
   * The answer to the query.
   */
  text: string;
  /**
   * The timings of the generation.
   */
  timings: unknown;
};
export type BaseImageToInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BaseInput = {
  /**
   * The strength of Classifier Free Guidance. Default value: `4.5`
   */
  cfg_strength?: number;
  /**
   * The duration of the audio to generate. Default value: `8`
   */
  duration?: number;
  /**
   * Whether to mask away the clip.
   */
  mask_away_clip?: boolean;
  /**
   * The negative prompt to generate the audio for. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of steps to generate the audio for. Default value: `25`
   */
  num_steps?: number;
  /**
   * The prompt to generate the audio for.
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * The URL of the video to generate the audio for.
   */
  video_url: string | Blob | File;
};
export type BaseKontextEditInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to edit.
   *
   * Max width: 14142px, Max height: 14142px, Timeout: 20s
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * Determines how the output resolution is set for image editing.
   * - `auto`: The model selects an optimal resolution from a predefined set that best matches the input image's aspect ratio. This is the recommended setting for most use cases as it's what the model was trained on.
   * - `match_input`: The model will attempt to use the same resolution as the input image. The resolution will be adjusted to be compatible with the model's requirements (e.g. dimensions must be multiples of 16 and within supported limits).
   * Apart from these, a few aspect ratios are also supported. Default value: `"match_input"`
   */
  resolution_mode?:
    | "auto"
    | "match_input"
    | "1:1"
    | "16:9"
    | "21:9"
    | "3:2"
    | "2:3"
    | "4:5"
    | "5:4"
    | "3:4"
    | "4:3"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BaseKontextImg2ImgInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image for image-to-image.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BaseKontextInpaintInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to be inpainted.
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The URL of the mask for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The URL of the reference image for inpainting.
   */
  reference_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BaseKontextInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BaseQwenEditImg2ImgInput = {
  /**
   * Acceleration level for image generation. Options: 'none', 'regular'. Higher acceleration increases speed. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate the image with
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Strength of the image-to-image transformation. Lower values preserve more of the original image. Default value: `0.94`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BaseReduxInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BasicAnimations = {
  /**
   * Running armature only in GLB format
   */
  running_armature_glb?: File;
  /**
   * Running animation in FBX format
   */
  running_fbx?: File;
  /**
   * Running animation in GLB format
   */
  running_glb?: File;
  /**
   * Walking armature only in GLB format
   */
  walking_armature_glb?: File;
  /**
   * Walking animation in FBX format
   */
  walking_fbx?: File;
  /**
   * Walking animation in GLB format
   */
  walking_glb?: File;
};
export type BatchMoonDreamOutput = {
  /**
   * URL to the generated captions JSON file containing filename-caption pairs.
   */
  captions_file: File;
  /**
   * List of generated captions
   */
  outputs: Array<string>;
};
export type BatchQueryInput = {
  /**
   * List of image URLs to be processed (maximum 32 images)
   */
  images_data_url: string | Blob | File;
  /**
   * Maximum number of tokens to generate Default value: `64`
   */
  max_tokens?: number;
  /**
   * Single prompt to apply to all images
   */
  prompt: string;
};
export type BboxInput = {
  /**
   * List of bounding box coordinates to erase (only one box prompt is supported)
   */
  box_prompts?: Array<BBoxPromptBase>;
  /**
   * The URL of the image to remove objects from.
   */
  image_url: string | Blob | File;
  /**
   * Amount of pixels to expand the mask by. Range: 0-50 Default value: `15`
   */
  mask_expansion?: number;
  /**
   *  Default value: `"best_quality"`
   */
  model?: "low_quality" | "medium_quality" | "high_quality" | "best_quality";
};
export type BBoxPromptBase = {
  /**
   * X Max Coordinate of the prompt (0-1)
   */
  x_max?: number;
  /**
   * X Min Coordinate of the box (0-1)
   */
  x_min?: number;
  /**
   * Y Max Coordinate of the prompt (0-1)
   */
  y_max?: number;
  /**
   * Y Min Coordinate of the box (0-1)
   */
  y_min?: number;
};
export type BenV2VideoInput = {
  /**
   * Optional RGB values (0-255) for the background color. If not provided, the background will be transparent. For ex: [0, 0, 0]
   */
  background_color?: Array<unknown>;
  /**
   * Output video format. Use "webm" for true transparency support (VP9 codec with alpha channel). MP4 format does not support transparency and will render transparent areas as black. Default value: `"mp4"`
   */
  output_format?: "mp4" | "webm";
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * URL of video to be used for background removal.
   */
  video_url: string | Blob | File;
};
export type BGRemoveBatchedInput = {
  /**
   * List of image URLs to be processed (maximum 32 images)
   */
  images_data_url: string | Blob | File;
};
export type BGRemoveInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BGReplaceInput = {
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of Images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt you would like to use to generate images.
   */
  prompt?: string;
  /**
   * The URL of the reference image to be used for generating the new background. Use "" to leave empty. Either ref_image_url or bg_prompt has to be provided but not both. If both ref_image_url and ref_image_file are provided, ref_image_url will be used. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  ref_image_url?: string | Blob | File;
  /**
   * Whether to refine prompt Default value: `true`
   */
  refine_prompt?: boolean;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type birefnetInput = {
  /**
   * URL of the image to remove background from
   */
  image_url: string | Blob | File;
  /**
   * Model to use for background removal.
   * The 'General Use (Light)' model is the original model used in the BiRefNet repository.
   * The 'General Use (Heavy)' model is a slower but more accurate model.
   * The 'Portrait' model is a model trained specifically for portrait images.
   * The 'General Use (Light)' model is recommended for most use cases.
   *
   * The corresponding models are as follows:
   * - 'General Use (Light)': BiRefNet-DIS_ep580.pth
   * - 'General Use (Heavy)': BiRefNet-massive-epoch_240.pth
   * - 'Portrait': BiRefNet-portrait-TR_P3M_10k-epoch_120.pth Default value: `"General Use (Light)"`
   */
  model?: "General Use (Light)" | "General Use (Heavy)" | "Portrait";
  /**
   * The resolution to operate on. The higher the resolution, the more accurate the output will be for high res input images. Default value: `"1024x1024"`
   */
  operating_resolution?: "1024x1024" | "2048x2048";
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "webp" | "png" | "gif";
  /**
   * Whether to output the mask used to remove the background
   */
  output_mask?: boolean;
  /**
   * Whether to refine the foreground using the estimated mask Default value: `true`
   */
  refine_foreground?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type birefnetOutput = {
  /**
   * Image with background removed
   */
  image: ImageFile;
  /**
   * Mask used to remove the background
   */
  mask_image?: ImageFile;
};
export type BirefnetV2Input = {
  /**
   * URL of the image to remove background from
   */
  image_url: string | Blob | File;
  /**
   * Whether to return only the segmentation mask without applying it to the image. When set to `True`, only the mask will be returned and foreground refinement will be skipped. Useful for reducing computation and data transfer when only the mask is needed.
   */
  mask_only?: boolean;
  /**
   * Model to use for background removal.
   * The 'General Use (Light)' model is the original model used in the BiRefNet repository.
   * The 'General Use (Light 2K)' model is the original model used in the BiRefNet repository but trained with 2K images.
   * The 'General Use (Heavy)' model is a slower but more accurate model.
   * The 'Matting' model is a model trained specifically for matting images.
   * The 'Portrait' model is a model trained specifically for portrait images.
   * The 'General Use (Dynamic)' model supports dynamic resolutions from 256x256 to 2304x2304.
   * The 'General Use (Light)' model is recommended for most use cases.
   *
   * The corresponding models are as follows:
   * - 'General Use (Light)': BiRefNet
   * - 'General Use (Light 2K)': BiRefNet_lite-2K
   * - 'General Use (Heavy)': BiRefNet_lite
   * - 'Matting': BiRefNet-matting
   * - 'Portrait': BiRefNet-portrait
   * - 'General Use (Dynamic)': BiRefNet_dynamic Default value: `"General Use (Light)"`
   */
  model?:
    | "General Use (Light)"
    | "General Use (Light 2K)"
    | "General Use (Heavy)"
    | "Matting"
    | "Portrait"
    | "General Use (Dynamic)";
  /**
   * The resolution to operate on. The higher the resolution, the more accurate the output will be for high res input images. The '2304x2304' option is only available for the 'General Use (Dynamic)' model. Default value: `"1024x1024"`
   */
  operating_resolution?: "1024x1024" | "2048x2048" | "2304x2304";
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "webp" | "png" | "gif";
  /**
   * Whether to output the mask used to remove the background
   */
  output_mask?: boolean;
  /**
   * Whether to refine the foreground using the estimated mask Default value: `true`
   */
  refine_foreground?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BirefnetV2VideoInput = {
  /**
   * Model to use for background removal.
   * The 'General Use (Light)' model is the original model used in the BiRefNet repository.
   * The 'General Use (Light 2K)' model is the original model used in the BiRefNet repository but trained with 2K images.
   * The 'General Use (Heavy)' model is a slower but more accurate model.
   * The 'Matting' model is a model trained specifically for matting images.
   * The 'Portrait' model is a model trained specifically for portrait images.
   * The 'General Use (Dynamic)' model supports dynamic resolutions from 256x256 to 2304x2304.
   * The 'General Use (Light)' model is recommended for most use cases.
   *
   * The corresponding models are as follows:
   * - 'General Use (Light)': BiRefNet
   * - 'General Use (Light 2K)': BiRefNet_lite-2K
   * - 'General Use (Heavy)': BiRefNet_lite
   * - 'Matting': BiRefNet-matting
   * - 'Portrait': BiRefNet-portrait
   * - 'General Use (Dynamic)': BiRefNet_dynamic Default value: `"General Use (Light)"`
   */
  model?:
    | "General Use (Light)"
    | "General Use (Light 2K)"
    | "General Use (Heavy)"
    | "Matting"
    | "Portrait"
    | "General Use (Dynamic)";
  /**
   * The resolution to operate on. The higher the resolution, the more accurate the output will be for high res input images. The '2304x2304' option is only available for the 'General Use (Dynamic)' model. Default value: `"1024x1024"`
   */
  operating_resolution?: "1024x1024" | "2048x2048" | "2304x2304";
  /**
   * Whether to output the mask used to remove the background
   */
  output_mask?: boolean;
  /**
   * Whether to refine the foreground using the estimated mask Default value: `true`
   */
  refine_foreground?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL of the video to remove background from
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type BirefnetV2VideoOutput = {
  /**
   * Mask used to remove the background
   */
  mask_video?: VideoFile;
  /**
   * Video with background removed
   */
  video: VideoFile;
};
export type bitdanceInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Higher values follow the prompt more closely. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Will be snapped to the nearest supported resolution. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of diffusion sampling steps per decoding step. Higher values (e.g. 50) improve quality at the cost of speed. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Text prompt for image generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. The same seed and prompt will produce the same image.
   */
  seed?: number;
  /**
   * If true, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type bitdanceOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
  /**
   * Seed of the generated image.
   */
  seed: number;
};
export type BlendingInput = {
  /**
   * The source image.
   */
  image_url: string | Blob | File;
  /**
   * Instruct what elements you would like to blend in your image.
   */
  instruction: string;
};
export type BlendVideoInput = {
  /**
   * Blend mode to use for combining the videos Default value: `"overlay"`
   */
  blend_mode?:
    | "addition"
    | "average"
    | "burn"
    | "darken"
    | "difference"
    | "divide"
    | "dodge"
    | "exclusion"
    | "grainextract"
    | "grainmerge"
    | "hardlight"
    | "lighten"
    | "multiply"
    | "negation"
    | "normal"
    | "overlay"
    | "phoenix"
    | "pinlight"
    | "reflect"
    | "screen"
    | "softlight"
    | "subtract"
    | "vividlight";
  /**
   * URL of the bottom layer video
   */
  bottom_video_url: string | Blob | File;
  /**
   * Opacity of the top layer (0.0-1.0) Default value: `1`
   */
  opacity?: number;
  /**
   * End output when the shortest input ends Default value: `true`
   */
  shortest?: boolean;
  /**
   * URL of the top layer video
   */
  top_video_url: string | Blob | File;
};
export type BlurInput = {
  /**
   * Blur radius Default value: `3`
   */
  blur_radius?: number;
  /**
   * Sigma for Gaussian blur Default value: `1`
   */
  blur_sigma?: number;
  /**
   * Type of blur to apply Default value: `"gaussian"`
   */
  blur_type?: "gaussian" | "kuwahara";
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
};
export type BlurOutput = {
  /**
   * The processed images with blur effect
   */
  images: Array<Image>;
};
export type BoundingBox = {
  /**
   * Height of the bounding box
   */
  h: number;
  /**
   * Label of the bounding box
   */
  label: string;
  /**
   * Width of the bounding box
   */
  w: number;
  /**
   * X-coordinate of the top-left corner
   */
  x: number;
  /**
   * Y-coordinate of the top-left corner
   */
  y: number;
};
export type BoundingBoxes = {
  /**
   * List of bounding boxes
   */
  bboxes: Array<BoundingBox>;
};
export type BoxPrompt = {
  /**
   * The frame index to interact with.
   */
  frame_index?: number;
  /**
   * Optional object identifier. Boxes sharing an object id refine the same object.
   */
  object_id?: number;
  /**
   * X Max Coordinate of the box
   */
  x_max?: number;
  /**
   * X Min Coordinate of the box
   */
  x_min?: number;
  /**
   * Y Max Coordinate of the box
   */
  y_max?: number;
  /**
   * Y Min Coordinate of the box
   */
  y_min?: number;
};
export type BoxPromptBase = {
  /**
   * Optional object identifier. Boxes sharing an object id refine the same object.
   */
  object_id?: number;
  /**
   * X Max Coordinate of the box
   */
  x_max?: number;
  /**
   * X Min Coordinate of the box
   */
  x_min?: number;
  /**
   * Y Max Coordinate of the box
   */
  y_max?: number;
  /**
   * Y Min Coordinate of the box
   */
  y_min?: number;
};
export type BriaExpandInput = {
  /**
   * The desired aspect ratio of the final image. Will be used over original_image_size and original_image_location if provided.
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * The desired size of the final image, after the expansion. should have an area of less than 5000x5000 pixels.
   */
  canvas_size: Array<number>;
  /**
   * The URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The desired location of the original image, inside the full canvas. Provide the location of the upper left corner of the original image. The location can also be outside the canvas (the original image will be cropped). Will be ignored if aspect_ratio is provided.
   */
  original_image_location?: Array<number>;
  /**
   * The desired size of the original image, inside the full canvas. Ensure that the ratio of input image foreground or main subject to the canvas area is greater than 15% to achieve optimal results. Will be ignored if aspect_ratio is provided.
   */
  original_image_size?: Array<number>;
  /**
   * Text on which you wish to base the image expansion. This parameter is optional. Bria currently supports prompts in English only, excluding special characters. Default value: `""`
   */
  prompt?: string;
  /**
   * You can choose whether you want your generated expension to be random or predictable. You can recreate the same result in the future by using the seed value of a result from the response. You can exclude this parameter if you are not interested in recreating your results. This parameter is optional.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BriaFiboVlmStructuredprompt = {
  /**
   * The aesthetics of the image to be generated.
   */
  aesthetics?: Aesthetics;
  /**
   * The artistic style of the image to be generated.
   */
  artistic_style?: string;
  /**
   * The background setting of the image to be generated.
   */
  background_setting?: string;
  /**
   * The context of the image to be generated.
   */
  context?: string;
  /**
   * The lighting of the image to be generated.
   */
  lighting?: Lighting;
  /**
   * A list of objects in the image to be generated, along with their attributes and relationships to other objects in the image.
   */
  objects?: Array<PromptObject>;
  /**
   * The photographic characteristics of the image to be generated.
   */
  photographic_characteristics?: PhotographicCharacteristics;
  /**
   * A short description of the image to be generated.
   */
  short_description?: string;
  /**
   * The style medium of the image to be generated.
   */
  style_medium?: string;
  /**
   * A list of text to be rendered in the image.
   */
  text_render?: Array<void>;
};
export type BriaTextToImageHdInput = {
  /**
   * The aspect ratio of the image. When a guidance method is being used, the aspect ratio is defined by the guidance image and this parameter is ignored. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * Guidance images to use for the generation. Up to 4 guidance methods can be combined during a single inference.
   */
  guidance?: Array<GuidanceInput>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Which medium should be included in your generated images. This parameter is optional.
   */
  medium?: "photography" | "art";
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * How many images you would like to generate. When using any Guidance Method, Value is set to 1. Default value: `4`
   */
  num_images?: number;
  /**
   * The number of iterations the model goes through to refine the generated image. This parameter is optional. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * When set to true, enhances the provided prompt by generating additional, more descriptive variations, resulting in more diverse and creative output images.
   */
  prompt_enhancement?: boolean;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type BytedanceUpscalerUpscaleVideoInput = {
  /**
   * The enhancement preset optimized for specific video scenarios. 'general' is a general-purpose template, 'ugc' targets user-generated short videos, 'short_series' is for short dramas, 'aigc' is for AI-generated content, and 'old_film' is for classic film restoration. Default value: `"general"`
   */
  enhancement_preset?: "general" | "ugc" | "short_series" | "aigc" | "old_film";
  /**
   * The enhancement quality tier. 'fast' provides essential upscaling with good speed, 'standard' uses adaptive algorithms for better visual texture, 'pro' uses large-model restoration for cinematic quality (longer processing time), and 10 times the cost of `standard` and `fast`. Default value: `"standard"`
   */
  enhancement_tier?: "fast" | "standard" | "pro";
  /**
   * The enhancement intensity. 'high' applies mild enhancement while keeping visual texture close to the source video. 'medium' provides a balanced image quality enhancement. Default value: `"high"`
   */
  fidelity?: "high" | "medium";
  /**
   * The scaling ratio for the output video resolution. When set, overrides target_resolution and scales the input resolution by this factor (e.g., 2.0 doubles the resolution). Range: 1.1 to 10.0. Please note that this is valid only up to 4k resolution, and trying to scale beyond 4k will result in an error. (4k is defined as having atotal pixel count of 3840x2160).
   */
  scale_ratio?: number;
  /**
   * The target FPS of the video to upscale. Default value: `"30fps"`
   */
  target_fps?: "30fps" | "60fps";
  /**
   * The target resolution of the video to upscale. Default value: `"1080p"`
   */
  target_resolution?: "1080p" | "2k" | "4k";
  /**
   * The URL of the video to upscale.
   */
  video_url: string | Blob | File;
};
export type CameraControl = {
  /**
   * The type of camera movement
   */
  movement_type: "horizontal" | "vertical" | "pan" | "tilt" | "roll" | "zoom";
  /**
   * The value of the camera movement
   */
  movement_value: number;
};
export type CannyInput = {
  /**
   * High threshold for the hysteresis procedure. Edges with a strength higher than the high threshold will always appear as edges in the output image. Default value: `200`
   */
  high_threshold?: number;
  /**
   * URL of the image to process
   */
  image_url: string | Blob | File;
  /**
   * Low threshold for the hysteresis procedure. Edges with a strength higher than the low threshold will appear in the output image, if there are strong edges nearby. Default value: `100`
   */
  low_threshold?: number;
};
export type cartoonifyInput = {
  /**
   * Whether to enable the safety checker Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance scale for the generation Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the image to apply Pixar style to
   */
  image_url: string | Blob | File;
  /**
   * Number of inference steps Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Scale factor for the Pixar effect Default value: `1`
   */
  scale?: number;
  /**
   * The seed for image generation. Same seed with same parameters will generate same image.
   */
  seed?: number;
  /**
   * Whether to use CFG zero
   */
  use_cfg_zero?: boolean;
};
export type CatVtonInput = {
  /**
   * Type of the Cloth to be tried on.
   *
   * Options:
   * upper: Upper body cloth
   * lower: Lower body cloth
   * overall: Full body cloth
   * inner: Inner cloth, like T-shirt inside a jacket
   * outer: Outer cloth, like a jacket over a T-shirt
   */
  cloth_type: "upper" | "lower" | "overall" | "inner" | "outer";
  /**
   * Url to the garment image.
   */
  garment_image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * Url for the human image.
   */
  human_image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `portrait_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same input given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
};
export type ccsrInput = {
  /**
   * Type of color correction for samples. Default value: `"adain"`
   */
  color_fix_type?: "none" | "wavelet" | "adain";
  /**
   * The URL or data URI of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * The scale of the output image. The higher the scale, the bigger the output image will be. Default value: `2`
   */
  scale?: number;
  /**
   * Seed for reproducibility. Different seeds will make slightly different results.
   */
  seed?: number;
  /**
   * The number of steps to run the model for. The higher the number the better the quality and longer it will take to generate. Default value: `50`
   */
  steps?: number;
  /**
   * The ending point of uniform sampling strategy. Default value: `0.6667`
   */
  t_max?: number;
  /**
   * The starting point of uniform sampling strategy. Default value: `0.3333`
   */
  t_min?: number;
  /**
   * If specified, a patch-based sampling strategy will be used for sampling. Default value: `"none"`
   */
  tile_diffusion?: "none" | "mix" | "gaussian";
  /**
   * Size of patch. Default value: `1024`
   */
  tile_diffusion_size?: number;
  /**
   * Stride of sliding patch. Default value: `512`
   */
  tile_diffusion_stride?: number;
  /**
   * If specified, a patch-based sampling strategy will be used for VAE decoding.
   */
  tile_vae?: boolean;
  /**
   * Size of VAE patch. Default value: `226`
   */
  tile_vae_decoder_size?: number;
  /**
   * Size of latent image Default value: `1024`
   */
  tile_vae_encoder_size?: number;
};
export type ccsrOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
  /**
   * The seed used for the generation.
   */
  seed: number;
};
export type ChainOfZoomInput = {
  /**
   * X coordinate of zoom center (0-1) Default value: `0.5`
   */
  center_x?: number;
  /**
   * Y coordinate of zoom center (0-1) Default value: `0.5`
   */
  center_y?: number;
  /**
   * Input image to zoom into
   */
  image_url: string | Blob | File;
  /**
   * Zoom scale in powers of 2 Default value: `5`
   */
  scale?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Additional prompt text to guide the zoom enhancement Default value: `""`
   */
  user_prompt?: string;
};
export type ChainOfZoomOutput = {
  /**
   * List of intermediate images
   */
  images: Array<Image>;
  /**
   * Actual linear zoom scale applied
   */
  scale: number;
  /**
   * Center coordinates used for zoom
   */
  zoom_center: Array<number>;
};
export type Character = {
  /**
   * ID of the avatar to use in the video
   */
  avatar:
    | "Abigail (Upper Body)"
    | "Abigail Office Front"
    | "Abigail Office Side"
    | "Abigail Sofa Front"
    | "Abigail Sofa Side"
    | "Aditya in Brown blazer"
    | "Aditya in Blue blazer"
    | "Aditya in Blue shirt"
    | "Aditya in Blue t-shirt"
    | "Aditya in Beige blazer"
    | "Adrian in Blue Shirt"
    | "Adrian in Blue Suit"
    | "Adrian in Blue Sweater"
    | "Adriana BizTalk Front"
    | "Adriana BizTalk Side"
    | "Adriana Business Front 2"
    | "Adriana Business Front "
    | "Adriana Business Side"
    | "Adriana Nurse Front 2"
    | "Adriana Nurse Front"
    | "Adriana Nurse Side 2"
    | "Adriana Nurse Side"
    | "Adriana Nurse Sitting Side"
    | "Adriana SuitSofa Front"
    | "Aiko"
    | "Albert in Blue Polo shirt"
    | "Albert in Blue blazer"
    | "Albert in Blue shirt"
    | "Albert in Blue suit"
    | "Albert in Khaki blazer"
    | "Albert in White shirt"
    | "Amanda in Blue Shirt (Front)"
    | "Amanda in Blue Shirt (Left)"
    | "Amanda in Blue Shirt (Right)"
    | "Amanda in Grey Shirt (Front)"
    | "Amanda in Grey Shirt (Left)"
    | "Amanda in Grey Shirt (Right)"
    | "Amanda in Maintenance (Front)"
    | "Amanda in Maintenance (Left)"
    | "Amanda in Maintenance (Right)"
    | "Amelia Business Training Front 2"
    | "Amelia Business Training Front"
    | "Amelia Business Training Side 2"
    | "Amelia Business Training Side"
    | "Amelia Lounge Front 2"
    | "Amelia Lounge Front"
    | "Amelia Lounge Side 2"
    | "Amelia Lounge Side"
    | "Amelia Yoga Front 2"
    | "Amelia Yoga Front"
    | "Amelia Yoga Side 2"
    | "Amelia Yoga Side"
    | "Alex in Black Suit"
    | "Alex in Jacket"
    | "Alex in White Coat"
    | "Alex in Yellow Sweater"
    | "Anja Office Front"
    | "Anja Office Side "
    | "Anja Sofa Front"
    | "Anja Sofa Side "
    | "Ann Business Front"
    | "Ann Business Sitting"
    | "Ann Casual Front"
    | "Ann Casual Lying"
    | "Ann Casual Sitting 2"
    | "Ann Casual Sitting"
    | "Ann Doctor Sitting"
    | "Ann Doctor Standing"
    | "Ann Sweater Front"
    | "Ann Sweater Side"
    | "Ann Therapist"
    | "Anna in White T-shirt"
    | "Anna in Turtleneck T-shirt"
    | "Anna in Brown T-shirt"
    | "Annelise in Dark blue dress"
    | "Annelise in Off white dress"
    | "Annelise in Sky blue dress"
    | "Annelise in White dress"
    | "Annelore in Blue blazer"
    | "Annelore in Blue dress"
    | "Annelore in Red blazer"
    | "Annelore in Red sweater"
    | "Annelore in White shirt"
    | "Annie in Grey Jacket"
    | "Annie in White Shirt"
    | "Annie in Tan Jacket"
    | "Annie in Blue Casual"
    | "Annie in Light Blue"
    | "Annie in Brown Shirt"
    | "Annie in Black Shirt"
    | "Annie in Pink Suit"
    | "Annie in Grey Dress"
    | "Annie in Blue Vest"
    | "Annie in Black V-neck Shirt"
    | "Annie in Blue Suit"
    | "Annie Bar Sitting Front"
    | "Annie Bar Sitting Side"
    | "Annie Bar Standing Front 2"
    | "Annie Bar Standing Front 3"
    | "Annie Bar Standing Front"
    | "Annie Bar Standing Side 2"
    | "Annie Bar Standing Side 3"
    | "Annie Bar Standing Side"
    | "Annie Business Casual Standing Front 2"
    | "Annie Business Casual Standing Front"
    | "Annie Business Casual Standing Side 2"
    | "Annie Business Casual Standing Side"
    | "Annie Casual Sitting Front 2"
    | "Annie Casual Sitting Front"
    | "Annie Casual Sitting Side 2"
    | "Annie Casual Sitting Side"
    | "Annie Casual Standing Front 2"
    | "Annie Casual Standing Front"
    | "Annie Casual Standing Side 2"
    | "Annie Casual Standing Side"
    | "Annie Desk Sitting Front 2"
    | "Annie Desk Sitting Front"
    | "Annie Desk Sitting Side 2"
    | "Annie Desk Sitting Side"
    | "Annie Lounge Standing Front"
    | "Annie Lounge Standing Side"
    | "Annie Office Sitting Front 2"
    | "Annie Office Sitting Front"
    | "Annie Office Sitting Side 2"
    | "Annie Office Sitting Side"
    | "Annie Office Standing Front"
    | "Annie Office Standing Side"
    | "Annie Sofa Sitting Front 3"
    | "Annie Sofa Sitting Front"
    | "Annie Sofa Sitting Side 2"
    | "Annie Sofa Sitting Side 3"
    | "Annie Sofa Sitting Side"
    | "Annie Sofa Sitting Front 2"
    | "Annie Studio Pink Sitting Front"
    | "Annie Studio Pink Sitting Side"
    | "Annie Studio Pink Standing Front"
    | "Annie Studio Pink Standing Side"
    | "Armando Casual Front"
    | "Armando Casual Side"
    | "Armando Suit Front"
    | "Armando Suit Side"
    | "Armando Sweater Front 2"
    | "Armando Sweater Front"
    | "Armando Sweater Side 2"
    | "Armando Sweater Side"
    | "Artur Office Front 2"
    | "Artur Office Front"
    | "Artur Office Side 2"
    | "Artur Office Side"
    | "Artur Sofa Casual Front 2"
    | "Artur Sofa Casual Front"
    | "Artur Sofa Casual Side 2"
    | "Artur Sofa Causal Side"
    | "Aubrey Sofa Side"
    | "Aubrey (Upper Body)"
    | "Aubrey Night Scene Front"
    | "Aubrey Outdoor Sport Front"
    | "Aubrey Outdoor Sport Side"
    | "Aubrey Sofa Front"
    | "August Casual Front 2"
    | "August Casual Front"
    | "August Cool Sitting"
    | "August Cool Style"
    | "August Employee Front"
    | "August Hoodies Front 2"
    | "August Hoodies Front"
    | "Bahar (Upper Body)"
    | "Bahar Business Front"
    | "Bahar Business Side"
    | "Bahar Business Sitting Front"
    | "Bahar Business Sitting Side"
    | "Bahar Casual Sitting Front 2"
    | "Bahar Casual Sitting Front"
    | "Bahar Casual Sitting Side 2"
    | "Bahar Casual Sitting Side"
    | "Bahar Denim Casual Front"
    | "Bahar Denim Casual Side"
    | "Bahar Denim Front"
    | "Bahar Denim Side"
    | "Bahar Jacket Casual Front"
    | "Bahar Jacket Casual Side"
    | "Bahar Jacket Front"
    | "Bahar Jacket Side"
    | "Bahar Suit Front"
    | "Bahar Suit Side"
    | "Bastien in Blue T-shirt"
    | "Bastien in Blue blazer"
    | "Bastien in Blue shirt"
    | "Bastien in White shirt"
    | "Blanka Lounge Front"
    | "Blanka Lounge Side"
    | "Blanka Outdoor Business Front"
    | "Blanka Outdoor Business Side"
    | "Blanka Outdoor Reading Front"
    | "Blanka Outdoor Reading Side"
    | "Blanka Picnic Front"
    | "Blanka Picnic Side"
    | "Bojan Business Training Front 2"
    | "Bojan Business Training Front"
    | "Bojan Business Training Side 2"
    | "Bojan Business Training Side"
    | "Bojan Lounge Front 2"
    | "Bojan Lounge Front"
    | "Bojan Lounge Side 2"
    | "Bojan Lounge Side"
    | "Bojan Sport Front 2"
    | "Bojan Sport Front"
    | "Bojan Sport Side 2"
    | "Bojan Sport Side"
    | "Bradley in Blue Polo (Front)"
    | "Bradley in Blue Polo (Left)"
    | "Bradley in Blue Polo (Right)"
    | "Bradley in Blue Shirt (Front)"
    | "Bradley in Blue Shirt (Left)"
    | "Bradley in Blue Shirt (Right)"
    | "Bradley in Doctor (Front)"
    | "Bradley in Doctor (Left)"
    | "Bradley in Doctor (Right)"
    | "Brandon in Grey Suit"
    | "Brandon in Blue Sweater"
    | "Brandon in White Shirt"
    | "Brandon Business Sitting Front"
    | "Brandon Business Sitting Side"
    | "Brandon Business Standing Front"
    | "Brandon Business Standing Side"
    | "Brandon Casual Sitting Front"
    | "Brandon Casual Sitting Side"
    | "Brandon Kitchen Standing Front"
    | "Brandon Kitchen Standing Side"
    | "Brandon Lobby Sitting Front"
    | "Brandon Lobby Sitting Side"
    | "Brandon Lobby Standing Front"
    | "Brandon Lobby Standing Side"
    | "Brandon Office Sitting Front"
    | "Brandon Office Sitting Side"
    | "Brandon Office Standing Front"
    | "Brandon Office Standing Side"
    | "Brandon Sofa Sitting Front"
    | "Brandon Sofa Sitting Side"
    | "Brent Office Front 2"
    | "Brent Office Front"
    | "Brent Office Side 2"
    | "Brent Office Side"
    | "Brent Sofa Front 2"
    | "Brent Sofa Front"
    | "Brent Sofa Side 2"
    | "Brent Sofa Side"
    | "Briana in White shirt"
    | "Briana in Striped T-shirt"
    | "Briana in Brown suit"
    | "Bruce"
    | "Bryan Casual Front"
    | "Bryan Casual Side"
    | "Bryan Fitness Coach"
    | "Bryan Tech Expert"
    | "Bryan Plaid Shirt Front"
    | "Bryan Plaid Shirt Side"
    | "Bryan Suit Front"
    | "Bryan Suit Side"
    | "Bryce in Black t-shirt"
    | "Bryce in Blue blazer"
    | "Bryce in Blue shirt"
    | "Bryce in Grey blazer"
    | "Bryce in White shirt"
    | "Byron Business Front 2"
    | "Byron Business Front"
    | "Byron Business Side 2"
    | "Byron Business Side"
    | "Byron Business Sitting Front"
    | "Byron Business Sitting Size"
    | "Byron Casual Front 2"
    | "Byron Casual Front"
    | "Byron Casual Side 2"
    | "Byron Casual Side"
    | "Byron Casual Sitting Front"
    | "Byron Casual Sitting Side"
    | "Byron Jacket Front"
    | "Byron Jacket Side"
    | "Byron Sitting Front"
    | "Byron Sitting Side"
    | "Byron Suit Front"
    | "Byron Suit Side"
    | "Candace in Beige Dress (Front)"
    | "Candace in Beige Dress (Left)"
    | "Candace in Beige Dress (Right)"
    | "Candace in Doctor (Front)"
    | "Candace in Doctor (Left)"
    | "Candace in Doctor (Right)"
    | "Candace in Pink Blazer (Front)"
    | "Candace in Pink Blazer (Right)"
    | "Candace in Pink Blazer  (Left)"
    | "Carla in Doctor (Front)"
    | "Carla in Doctor (Left)"
    | "Carla in Doctor (Right)"
    | "Carla in Dress  (Left)"
    | "Carla in Shirt (Front)"
    | "Carla in Shirt (Left)"
    | "Carla in Shirt (Right)"
    | "Carlotta BizTalk Front"
    | "Carlotta BizTalk Side"
    | "Carlotta Business Front"
    | "Carlotta Business Side"
    | "Carlotta Casual Front"
    | "Carlotta Casual Side"
    | "Carlotta Casual Sitting Front"
    | "Carlotta Casual Sitting Side"
    | "Carlotta Half Front"
    | "Carlotta Pink Jumpsuit Front 2"
    | "Carlotta Pink Jumpsuit Front"
    | "Carlotta Pink Jumpsuit Side 2"
    | "Carlotta Pink Jumpsuit Side"
    | "Carlotta Pink Jumpsuit Sitting Front"
    | "Carlotta Pink Jumpsuit Sitting Side"
    | "Caroline Business Sitting Front"
    | "Caroline Business Sitting Side"
    | "Caroline Business Standing Front"
    | "Caroline Business Standing Side"
    | "Caroline in Blue Suit"
    | "Caroline in White Shirt"
    | "Caroline in Yellow Casual"
    | "Caroline in Yellow Skirt"
    | "Caroline Casual Sitting Front"
    | "Caroline Casual Sitting Side"
    | "Caroline Kitchen Standing Front"
    | "Caroline Kitchen Standing Side"
    | "Caroline Lobby Sitting Front"
    | "Caroline Lobby Sitting Side"
    | "Caroline Lobby Standing Front"
    | "Caroline Lobby Standing Side"
    | "Caroline Office Sitting Front"
    | "Caroline Office Sitting Side"
    | "Caroline Office Standing Front"
    | "Caroline Office Standing Side"
    | "Caroline Sofa Sitting Front"
    | "Caroline Sofa Sitting Side"
    | "Chad in Blue Shirt (Front)"
    | "Chad in Blue Shirt (Left)"
    | "Chad in Blue Shirt (Right)"
    | "Chad in Grey Shirt (Front)"
    | "Chad in Grey Shirt (Left)"
    | "Chad in Grey Shirt (Right)"
    | "Chad in Maintenance (Front)"
    | "Chad in Maintenance (Left)"
    | "Chad in Maintenance (Right)"
    | "Chakir in Headscarf (Front)"
    | "Chakir in Headscarf (Left)"
    | "Chakir in Headscarf (Right)"
    | "Chakir in White Shirt (Front)"
    | "Chakir in White Shirt (Right)"
    | "Chakir in white Shirt (Left)"
    | "Chloe (Upper Body)"
    | "Chloe Lounge Front"
    | "Chloe Lounge Side"
    | "Chloe Outdoor Side"
    | "Colin Business Front 2"
    | "Colin Business Front"
    | "Colin Jacket Front"
    | "Colin Sitting Cool Style 2"
    | "Colin Sitting Cool Style"
    | "Colin Suit Front"
    | "Colin Sweater Front"
    | "Colin Sweater Sitting Front"
    | "Connie Business Front"
    | "Connie Business Sitting Front"
    | "Connie Casual Sitting Front"
    | "Connie Casual Sitting Side"
    | "Connie Education Front"
    | "Connie Education Side 2"
    | "Connie Education Side"
    | "Connie Skirt Front"
    | "Connie Skirt Side"
    | "Connie Skirt Sitting Front"
    | "Connie Skirt Sitting Side"
    | "Connie Suit Front"
    | "Conrad House Front"
    | "Conrad House Side"
    | "Conrad Sofa Front"
    | "Conrad Sofa Side"
    | "Crisanto Business Front"
    | "Crisanto Business Side"
    | "Crisanto Chef Front 2"
    | "Crisanto Chef Front"
    | "Crisanto Chef Side 2"
    | "Crisanto Chef Side"
    | "Crisanto Chef Sitting "
    | "Crisanto Education Front"
    | "Crisanto Education Side"
    | "Crisanto Nurse Front 2"
    | "Crisanto Nurse Front"
    | "Crisanto Nurse Side 2"
    | "Crisanto Nurse Side"
    | "Crisanto Nurse Sitting Front"
    | "Crisanto Nurse Sitting Side"
    | "Crisanto Suit Front"
    | "Crisanto Suit Side"
    | "Daphne in Blue blazer"
    | "Daphne in Blue shirt"
    | "Daphne in Grey blazer"
    | "Daphne in Grey suit"
    | "Daphne in Pink hoodie"
    | "Daphne in White t-shirt"
    | "Darnell in Blue Shirt (Front)"
    | "Darnell in Blue Shirt (Left)"
    | "Darnell in Blue Shirt (Right)"
    | "Darnell in Bordeaux Polo (Front)"
    | "Darnell in Bordeaux Polo (Left)"
    | "Darnell in Bordeaux Polo (Right)"
    | "Darnell in Doctor (Front)"
    | "Darnell in Doctor (Left)"
    | "Darnell in Doctor (Right)"
    | "Derya Indoor Front 2"
    | "Derya Indoor Front"
    | "Derya Indoor Side 2"
    | "Derya Indoor Side"
    | "Derya Office Front 2"
    | "Derya Office Front"
    | "Derya Office Side 2"
    | "Derya Office Side"
    | "Dexter Casual Front"
    | "Dexter Casuat Side"
    | "Dexter Doctor Sitting"
    | "Dexter Doctor Standing"
    | "Dexter Lawyer"
    | "Dexter Suit Front"
    | "Dexter Suit Side"
    | "Dexter Winter Coat Front"
    | "Dexter Winter Coat Side"
    | "Diana in Black Camisole Top"
    | "Diana in Striped Shirt"
    | "Diana in White Shirt"
    | "Diora in Blue blazer"
    | "Diora in Green blazer"
    | "Diora in Pink shirt"
    | "Diora in White shirt"
    | "Diora in White t-shirt"
    | "Diran Casual Front"
    | "Diran Casual Side"
    | "Diran Jacket Front"
    | "Diran Jacket Side"
    | "Diran Macbook Business Front"
    | "Diran Macbook Business Side"
    | "Diran Macbook Casual Front"
    | "Diran Macbook Casual Side"
    | "Diran Macbook Sitting Front 2"
    | "Diran Macbook Sitting Front"
    | "Diran Macbook Sitting Side 2"
    | "Diran Macbook Sitting Side"
    | "Diran Suit Front"
    | "Diran Suit Side"
    | "Diran iPad Front"
    | "Diran iPad Side"
    | "Diran iPad Sitting Front"
    | "Diran iPad Sitting Side"
    | "Elenora Casual Front"
    | "Elenora Casual Side"
    | "Elenora Fitness Coach"
    | "Elenora Fitness Coach 2"
    | "Elenora Tech Expert"
    | "Elenora Suit Front"
    | "Elenora Suit Side"
    | "Elenora YellowDress Front"
    | "Elenora YellowDress Side"
    | "Emanuel Office Front"
    | "Emanuel Office Side"
    | "Emanuel Sofa Front"
    | "Emanuel Sofa Side"
    | "Emery in Blue blazer"
    | "Emery in Blue suit"
    | "Emery in Green shirt"
    | "Emery in Khaki blazer"
    | "Emery in Red blazer"
    | "Emery in White t-shirt"
    | "Emilia Outdoor Business Front"
    | "Emilia Outdoor Business Side"
    | "Emilia Outdoor Yoga Front 2"
    | "Emilia Outdoor Yoga Front"
    | "Emilia Outdoor Yoga Side"
    | "Emilia Picnic Front"
    | "Emilia Picnic Side"
    | "Edward in Black Suit"
    | "Edward"
    | "Edward in Blue Shirt"
    | "Esmond in Black coat"
    | "Esmond in Black shirt"
    | "Esmond in Blue blazer"
    | "Esmond in Blue suit"
    | "Esmond in Grey sweater"
    | "Fernando Business Indoor Front"
    | "Fernando Business Indoor Side"
    | "Fernando OutdoorChair Front 2"
    | "Fernando Outdoor Chair Front"
    | "Fernando Outdoor Chair Side"
    | "Fernando Outdoor Front"
    | "Fernando Outdoor Side"
    | "Fernando Outdoor Table Front"
    | "Fernando Outdoor Table Side"
    | "Fina Business Sitting Front"
    | "Fina Business Sitting Side"
    | "Fina Casual Front 2"
    | "Fina Casual Front"
    | "Fina Casual Side 2"
    | "Fina Casual Side"
    | "Fina Casual Sitting Front"
    | "Fina Casual Sitting Side"
    | "Fina Denim Front"
    | "Fina Denim Side"
    | "Fina Denim Sitting Front"
    | "Fina Employee Front"
    | "Fina Employee Side"
    | "Fina Suit Front"
    | "Fina Suit Side"
    | "Fina Support Front"
    | "Fina Support Side"
    | "Florin Business Sitting Front"
    | "Florin Business Sitting Side"
    | "Florin Maintain Front 2"
    | "Florin Maintain Front"
    | "Florin Maintain Side 2"
    | "Florin Maintain Side"
    | "Florin Maintain Siiting Front"
    | "Florin Maintain Siiting Side"
    | "Florin Suit Front 2"
    | "Florin Suit Front"
    | "Florin Suit Side 2"
    | "Florin Suit Side"
    | "Francis in Blazer (Front)"
    | "Francis in Blazer (Left)"
    | "Francis in Blazer (Right)"
    | "Francis in Doctor (Front)"
    | "Francis in Doctor (Left)"
    | "Francis in Doctor (Right)"
    | "Francis in Shirt (Front)"
    | "Francis in Shirt (Left)"
    | "Francis in Shirt (Right)"
    | "Fred in Blue Long Shirt (Front)"
    | "Fred in Blue Long Shirt (Left)"
    | "Fred in Blue Long Shirt (Right)"
    | "Fred in Blue Short Shirt (Front)"
    | "Fred in Blue Short Shirt (Left)"
    | "Fred in Blue Short Shirt (Right)"
    | "Freja in Blue blazer"
    | "Freja in Grey blazer"
    | "Freja in White blazer"
    | "Freja in White polo shirt"
    | "Freja in White shirt"
    | "Gabriel in Black Sweatshirt"
    | "Gabriel in Blue Suit"
    | "Gabriel in Gray Shirt"
    | "Gala Bedroom Front"
    | "Gala Business Sofa Front 2"
    | "Gala Business Sofa Front 3"
    | "Gala Business Sofa Front"
    | "Gala Business Sofa Side 2"
    | "Gala Business Sofa Side 3"
    | "Gala Business Sofa Side"
    | "Gala Casual Sofa with iPad Front"
    | "Gala Casual Sofa with iPad Side 2"
    | "Gala Casual Sofa with iPad Side"
    | "Gala Office Front"
    | "Gala Office Side"
    | "Gala Sofa Front 2"
    | "Gala Sofa Front 3"
    | "Gala Sofa Front"
    | "Gala Sofa Side 2"
    | "Gala Sofa Side 3"
    | "Gala Sofa Side"
    | "Georgia (Upper Body)"
    | "Georgia Casual Front"
    | "Georgia Casual Side"
    | "Georgia Office Front"
    | "Georgia Office Side"
    | "Gerardo Sofa Side"
    | "Gerardo Indoor Front"
    | "Gerardo Indoor Side"
    | "Gerardo Night Scene Front 2"
    | "Gerardo Night Scene Front"
    | "Gerardo Outdoor Sport Front"
    | "Gerardo Outdoor Sport Side"
    | "Gerardo Sofa Front"
    | "Giulia Office Front 2"
    | "Giulia Office Front"
    | "Giulia Office Side 2"
    | "Giulia Office Side"
    | "Giulia Sofa Front"
    | "Giulia Sofa Side"
    | "Giulia Sofa  Front 2"
    | "Giulia Sofa  Side 3"
    | "Hada Casual Cup Front"
    | "Hada Casual Cup Side"
    | "Hada Casual Front"
    | "Hada Casual Side"
    | "Hada Casual Sitting Front 2"
    | "Hada Casual Sitting Front"
    | "Hada Casual Sitting Side 2"
    | "Hada Casual Sitting Side"
    | "Hada LivelyGestures Front"
    | "Hada LivelyGestures Side"
    | "Hada LivelyGestures Sitting Front"
    | "Hada LivelyGestures Sitting Side"
    | "Hada Suit Front 2"
    | "Hada Suit Front"
    | "Hada Suit Side 2"
    | "Hada Suit Side"
    | "Hada Suit Sitting Front 2"
    | "Hada Suit Sitting Front"
    | "Hada Suit Sitting Side"
    | "Harrison in Black Suit"
    | "Harrison in Gray Suit"
    | "Harrison in White Shirt"
    | "Ian in Beige Shirt"
    | "Ian in Black Jacket"
    | "Ian in Gray Suit"
    | "Ida Lounge Front 2"
    | "Ida Lounge Front"
    | "Ida Lounge Side 2"
    | "Ida Lounge Side"
    | "Ida Sofa Front 2"
    | "Ida Sofa Front"
    | "Ida Sofa Side 2"
    | "Ida Sofa Side"
    | "Iker in Almond sweater"
    | "Iker in Black blazer"
    | "Iker in Blue shirt"
    | "Iker in Grey blazer"
    | "Iker in White shirt"
    | "Imelda BizTalk Front"
    | "Imelda BizTalk Side"
    | "Imelda Business Front"
    | "Imelda Business Side"
    | "Imelda Business Sitting Front 2"
    | "Imelda Business Sitting Front"
    | "Imelda Business Sitting Side 2"
    | "Imelda Business Sitting Side"
    | "Imelda Casual Front"
    | "Imelda Casual Side"
    | "Imelda Coat Front 2"
    | "Imelda Coat Front"
    | "Imelda Coat Side"
    | "Imelda Customer Support Front"
    | "Imelda Customer Support Side"
    | "Imelda Full Side "
    | "Imelda Suit Front"
    | "Imelda Suit Side"
    | "Ivan in Black Suit"
    | "Ivan in Gary Suit"
    | "Ivan in Suit"
    | "Ivan in Sweater"
    | "Jason in Black Jacket"
    | "Jason in Blue Suit"
    | "Jason in Gray Shirt"
    | "Javi Intense Sitting Speaking Front"
    | "Javi Intense Sitting Speaking Side"
    | "Javi Intense Sitting Speaking "
    | "Javi Intense Speaking Front 2"
    | "Javi Intense Speaking Front 3"
    | "Javi Intense Speaking Front"
    | "Javi Intense Speaking Side 3"
    | "Javi Intense Speaking Side"
    | "Javi in Passionate Gestures 2"
    | "Javi in Passionate Gestures 3"
    | "Javi in Passionate Gestures"
    | "Jin (Upper Body)"
    | "Jin Blue Casual Front"
    | "Jin Blue Casual Side"
    | "Jin Business Café Mode Front"
    | "Jin Business Café Mode Side"
    | "Jin Business Sitting Front"
    | "Jin Business Sitting Side"
    | "Jin Casual Café Mode Front"
    | "Jin Casual Café Mode Side"
    | "Jin Casual Sitting Front"
    | "Jin Casual Sitting Side"
    | "Jin Suit Front"
    | "Jin Suit Side"
    | "Jin Vest Front"
    | "Jin Vest Side"
    | "Jin Vest Sitting Front"
    | "Jin Vest Sitting Side"
    | "Jin in Education Front"
    | "Jin in Education Side"
    | "Jinwoo in Black vest"
    | "Jinwoo in Blue suit"
    | "Jinwoo in White T-shirt"
    | "Jinwoo in White shirt"
    | "Jinwoo in White suit"
    | "Jocelyn Office Front 2"
    | "Jocelyn Office Front"
    | "Jocelyn Office Side 2"
    | "Jocelyn Office Side"
    | "Jocelyn Sofa Front 2"
    | "Jocelyn Sofa Front"
    | "Jocelyn Sofa Side 2"
    | "Jocelyn Sofa Side"
    | "Joel Couch Front"
    | "Joel Couch Side"
    | "Joel Gym Front"
    | "Joel Gym Side"
    | "Joel Mountain Front"
    | "Joel Mountain Side"
    | "Jonas (Upper Body)"
    | "Jonas Gym Front 2"
    | "Jonas Gym Front"
    | "Jonas Gym Side 2"
    | "Jonas Gym Side "
    | "June Office Front 2"
    | "Juan Office Front"
    | "Juan Office Side 2"
    | "Juan Office Side"
    | "Juan Sofa Front 2"
    | "Juan Sofa Front"
    | "Juan Sofa Side 2"
    | "Juan Sofa Side"
    | "Judita (Upper Body)"
    | "Judita Yoga Front 2"
    | "Judita Yoga Front"
    | "Judita Yoga Side 2"
    | "Judita Yoga Side"
    | "Judith (Upper Body)"
    | "Judith Business Front 2"
    | "Judith Business Front"
    | "Judith Business Sitting Front"
    | "Judith Casual Front"
    | "Judith Casual Sitting Front"
    | "Judith Suit Front"
    | "Judy Business Front"
    | "Judy Business Side"
    | "Judy Business Sitting Front"
    | "Judy Casual Front"
    | "Judy Casual Side"
    | "Judy Casual Sitting"
    | "Judy ConfidentSpeaking Front"
    | "Judy ConfidentSpeaking Side"
    | "Judy Doctor Sitting"
    | "Judy Doctor Standing"
    | "Judy Lawyer"
    | "Judy NurseSitting Front 2"
    | "Judy NurseSitting Front"
    | "Judy NurseSitting Side 2"
    | "Judy NurseSitting Side"
    | "Judy Nurse Front 2"
    | "Judy Nurse Front"
    | "Judy Nurse OnDuty Front"
    | "Judy Nurse OnDuty Side"
    | "Judy Nurse Side 2"
    | "Judy Nurse Side"
    | "Judy Suit Front"
    | "Judy Suit Side"
    | "Judy Teacher Sitting"
    | "Judy HR"
    | "Judy Teacher Standing"
    | "June (Upper Body)"
    | "June HR"
    | "June Office Front"
    | "June Office Side 2"
    | "June Office Side"
    | "June Sofa Casual Front 2"
    | "June Sofa Casual Front"
    | "June Sofa Casual Side 2"
    | "June Sofa Casual Side"
    | "Justin in Black Shirt"
    | "Justin in Black Suit"
    | "Justin in White Shirt"
    | "Justo Business Front 2"
    | "Justo Business Front"
    | "Justo Business Side 2"
    | "Justo Business Side"
    | "Justo Casual Sitting"
    | "Justo CustomerService Front 2"
    | "Justo CustomerService Front"
    | "Justo CustomerService Side"
    | "Justo CustomerService Sitting Front"
    | "Justo CustomerService Sitting Side"
    | "Justo EmployeeTraining Front"
    | "Justo Suit Casual Front "
    | "Justo Suit Casual Side "
    | "Karolin in Gray Suit"
    | "Karolin in Sweatshirt"
    | "Karolin in Black Suit"
    | "Kavya Indoor Front"
    | "Kavya Indoor Side"
    | "Kavya Outdoor Side"
    | "Kavya Outdoor Sport Front"
    | "Kavya Outdoor Sport Side"
    | "Kavya Sofa Front"
    | "Kavya Sofa Side"
    | "Kelly in Blue Shirt  (Front)"
    | "Kelly in Blue Shirt (Left)"
    | "Kelly in Blue Shirt (Right)"
    | "Kelly in Doctor (Front)"
    | "Kelly in Doctor (Left)"
    | "Kelly in Doctor (Right)"
    | "Kelly in Pink Shirt (Front)"
    | "Kelly in Pink Shirt (Left)"
    | "Kelly in Pink Shirt (Right)"
    | "Klara in Black blazer"
    | "Klara in Blue blazer"
    | "Klara in Blue dress"
    | "Klara in Pink shirt"
    | "Klara in White blazer"
    | "Klara in White shirt"
    | "Kristin in Lace Dress"
    | "Kristin in V-neck Shirt"
    | "Kristin in Black Suit"
    | "Leos Office Front 2"
    | "Leos Office Front"
    | "Leos Office Side 2"
    | "Leos Office Side"
    | "Leos Sofa Front 2"
    | "Leos Sofa Front"
    | "Leos Sofa Side 2"
    | "Leos Sofa Side"
    | "Leszek Lounge Front"
    | "Leszek Lounge Side"
    | "Leszek Outdoor Business Front"
    | "Leszek Outdoor Business Side"
    | "Leszek Outdoor Casual Front"
    | "Leszek Outdoor Casual Side"
    | "Leszek Sofa Front"
    | "Leszek Sofa Side"
    | "Leah"
    | "Leah in Black Suit"
    | "Lina Casual Front 2"
    | "Lina Casual Front"
    | "Lina Casual Side 2"
    | "Lina Casual Side"
    | "Lina Casual Sitting Front"
    | "Lina Casual Sitting Side"
    | "Lina Dress Front"
    | "Lina Dress Side 2"
    | "Lina Dress Side"
    | "Lina Dress Sitting Front"
    | "Lina Dress Sitting Side"
    | "Lina Sweater Front 2"
    | "Lina Sweater Front"
    | "Lina Sweater Side 2"
    | "Lina Sweater Side"
    | "Lina Sweater Sitting Front 2"
    | "Lina Sweater Sitting Front"
    | "Lina Sweater Sitting Side 2"
    | "Lina Sweater Sitting Side"
    | "Lisa"
    | "Luca"
    | "Lucien in Black coat"
    | "Lucien in Black polo-shirt"
    | "Lucien in Blue blazer"
    | "Lucien in Blue shirt"
    | "Lucien in Grey blazer"
    | "Lucien in White shirt"
    | "Luke in Brown Suit"
    | "Luke in Yellow Jacket"
    | "Luke in Blue Suit"
    | "Marcus (Upper Body)"
    | "Marcus Café Front 2"
    | "Marcus Café Front"
    | "Marcus Café Side 2"
    | "Marcus Café Side"
    | "Marcus Casual Front"
    | "Marcus Casual Side"
    | "Marcus Casual Sitting Front 2"
    | "Marcus Casual Sitting Front"
    | "Marcus Casual Sitting Side 2"
    | "Marcus Casual Sitting Side"
    | "Marcus Denim Jacket Front"
    | "Marcus Denim Jacket Side"
    | "Marcus Education Front"
    | "Marcus Education Side"
    | "Marcus Sitting Front"
    | "Marcus Sitting Side"
    | "Marcus Suit Front"
    | "Marcus Suit Side"
    | "Maria in Suit"
    | "Maria in Sweater"
    | "Maria in Black Suit"
    | "Martina Office Front 2"
    | "Martina Office Front"
    | "Martina Office Side 2"
    | "Martina Office Side"
    | "Martina Sofa Front 2"
    | "Martina Sofa Front"
    | "Martina Sofa Side 2"
    | "Martina Sofa Side"
    | "Masha Office Front 2"
    | "Masha Office Front"
    | "Masha Office Side 2"
    | "Masha Office Side"
    | "Masha Sofa Casual Front 2"
    | "Masha Sofa Casual Front"
    | "Masha Sofa Casual Side 2"
    | "Masha Sofa Casual Side"
    | "Mason in Blue Suit"
    | "Mason in White Shirt"
    | "Mason in Blue Sweater"
    | "Matteo Office Front 2"
    | "Matteo Office Front"
    | "Matteo Office Side 2"
    | "Matteo Office Side"
    | "Matteo Sofa Front 2"
    | "Matteo Sofa Front"
    | "Matteo Sofa Side 2"
    | "Matteo Sofa Side"
    | "Max (Upper Body)"
    | "Max Indoor Front"
    | "Max Indoor Side"
    | "Max Outdoor Sport Front"
    | "Max Outdoor Sport Side"
    | "Milena Office Front 2"
    | "Milena Office Front"
    | "Milena Office Side 2"
    | "Milena Office Side"
    | "Milena Sofa Front 2"
    | "Milena Sofa Front"
    | "Milena Sofa Side 2"
    | "Milena Sofa Side"
    | "Miles (Upper Body)"
    | "Miles Outdoor Front"
    | "Miles Outdoor Side"
    | "Miles Sofa Front 2"
    | "Miles Sofa Front"
    | "Miles Sofa Side 2"
    | "Miles Sofa Side"
    | "Minho in Blue blazer"
    | "Minho in Blue shirt"
    | "Minho in Green polo-shirt"
    | "Minho in Khaki jacket"
    | "Minho in White shirt"
    | "Minho in White t-shirt"
    | "Mireia Business Indoor Front"
    | "Mireia Business Indoor Side"
    | "Mireia Outdoor Chair Front"
    | "Mireia Outdoor Front"
    | "Mireia Outdoor Side"
    | "Mireia Outdoor Table Front"
    | "Mireia Outdoor Table Side"
    | "Miyu Office Front 2"
    | "Miyu Office Front"
    | "Miyu Office Side 2"
    | "Miyu Office Side"
    | "Miyu Sofa Business Front"
    | "Miyu Sofa Business Side"
    | "Miyu Sofa Casual Front 2"
    | "Miyu Sofa Casual Front"
    | "Miyu Sofa Casual Side 2 "
    | "Miyu Sofa Casual Side"
    | "Nadim in Black blazer"
    | "Nadim in Blue blazer"
    | "Nadim in Blue jacket"
    | "Nadim in Puffer vest"
    | "Nadim in White shirt"
    | "Neil in Black Shirt"
    | "Neil in Black Suit "
    | "Neil in Yellow Jacket"
    | "Nico"
    | "Noah Lobby Front 2"
    | "Noah Lobby Front"
    | "Noah Lobby Side 2"
    | "Noah Lobby Side"
    | "Noah Office Front 2"
    | "Noah Office Front"
    | "Noah Office Side 2"
    | "Noah Office Side"
    | "Noah Sofa Front 2"
    | "Noah Sofa Front "
    | "Noah Sofa Side 2"
    | "Noah Sofa Side"
    | "Nour in Black blazer"
    | "Nour in Brown dress"
    | "Nour in Grey hoodie"
    | "Nour in Sporty vest"
    | "Nour in White shirt"
    | "Odelia in Blue Suit"
    | "Odelia in Red Dress "
    | "Odelia in Yellow Suit"
    | "Onat (Upper Body)"
    | "Onat Casual Front"
    | "Onat Casual Side"
    | "Onat Casual Sitting Front"
    | "Onat Casual Sitting Side"
    | "Onat Macbook Front 2"
    | "Onat Macbook Front"
    | "Onat Macbook Side 2"
    | "Onat Macbook Side"
    | "Onat Suit Front"
    | "Onat Suit Side"
    | "Onat Suit Sitting Front"
    | "Onat Suit Sitting Side"
    | "Onat iPad Front"
    | "Onat iPad Side"
    | "Onat iPad Sitting Front"
    | "Onat iPad Sitting Side"
    | "Oxana (Upper Body)"
    | "Oxana Gym Front 2"
    | "Oxana Gym Front"
    | "Oxana Gym Side 2"
    | "Oxana Gym Side"
    | "Oxana Office Front 2"
    | "Oxana Office Front"
    | "Oxana Office Side 2"
    | "Oxana Office Side"
    | "Oxana Sofa Front 2"
    | "Oxana Sofa Front"
    | "Oxana Sofa Side 2"
    | "Oxana Sofa Side"
    | "Oxana Yoga Front 2"
    | "Oxana Yoga Front"
    | "Oxana Yoga Side 2"
    | "Oxana Yoga Side"
    | "Patrizio Business Training Front"
    | "Patrizio Business Training Side"
    | "Patrizio Office Front"
    | "Patrizio Office Side 2"
    | "Patrizio Office Side"
    | "Patrizio Sofa Front"
    | "Patrizio Sofa Side"
    | "Piper Business Sofa Front"
    | "Piper Business Sofa Side"
    | "Piper Education Front"
    | "Piper Education Side"
    | "Rasmus Lounge Front 2"
    | "Rasmus Lounge Front"
    | "Rasmus Lounge Side 2"
    | "Rasmus Lounge Side"
    | "Rasmus Sofa Front 2"
    | "Rasmus Sofa Front"
    | "Rasmus Sofa Side 2"
    | "Rasmus Sofa Side"
    | "Raul (Upper Body)"
    | "Raul Business Sofa Front 2"
    | "Raul Business Sofa Front"
    | "Raul Business Sofa Side 2"
    | "Raul Business Sofa Side"
    | "Raul Casual Sofa Front 2"
    | "Raul Casual Sofa Front"
    | "Raul Casual Sofa Side 2"
    | "Raul Casual Sofa Side"
    | "Raul Casual Sofa no iPad Front"
    | "Raul Casual Sofa no iPad Side"
    | "Raul Casual Sofa with iPad Front"
    | "Raul Casual Sofa with iPad Side"
    | "Raul Office Front 2"
    | "Raul Office Front"
    | "Raul Office Side"
    | "Raul Sofa Front 2"
    | "Raul Sofa Front"
    | "Raul Sofa Side 2"
    | "Raul Sofa Side"
    | "Rebecca"
    | "Ren (Upper Body)"
    | "Ren Office Front 2"
    | "Ren Office Front"
    | "Ren Office Side 2"
    | "Ren Office Side"
    | "Ren Sofa Business Front"
    | "Ren Sofa Business Side"
    | "Ren Sofa Casual Front 2"
    | "Ren Sofa Casual Front"
    | "Ren Sofa Casual Side 2 "
    | "Ren Sofa Casual Side"
    | "Riley (Upper Body)"
    | "Riley Casual Front"
    | "Riley Casual Side"
    | "Riley Office Front"
    | "Riley Office Side"
    | "Roman Outdoor Sport Front"
    | "Roman Outdoor Sport Side"
    | "Sabine Office Front 2"
    | "Sabine Office Front"
    | "Sabine Office Side"
    | "Sabine Sofa Side"
    | "Salma in headscarf (Front)"
    | "Salma in headscarf (Left)"
    | "Salma in headscarf (Right)"
    | "Santa Avatar Present Standing "
    | "Santa Avatar Sitting 2"
    | "Santa Avatar Sitting"
    | "Santa Avatar Sitting Side"
    | "Santa Avatar Standing"
    | "Santa Fireplace Front"
    | "Santa Fireplace Side"
    | "Saskia in Blue blazer"
    | "Saskia in Blue shirt"
    | "Saskia in Green blazer"
    | "Saskia in Grey vest"
    | "Saskia in White blazer"
    | "Scarlett Couch Front 2"
    | "Scarlett Couch Front"
    | "Scarlett Couch Side 2"
    | "Scarlett Couch Side"
    | "Scarlett Fireplace Front"
    | "Scarlett Fireplace Side"
    | "Scarlett Hall Front"
    | "Scarlett Hall Side"
    | "Scarlett Yoga Front"
    | "Scarlett Yoga Side"
    | "Seema Business Front"
    | "Seema Business Side"
    | "Seema Business Sitting Side"
    | "Seema Casual Front"
    | "Seema Casual Side"
    | "Seema Casual Sitting Front"
    | "Seema Casual Sitting Side"
    | "Seema Nurse Front 2"
    | "Seema Nurse Front"
    | "Seema Nurse Side 2"
    | "Seema Nurse Side"
    | "Shawn Business Front"
    | "Shawn Business Side"
    | "Shawn Casual Sitting Front"
    | "Shawn Casual Sitting Side"
    | "Shawn Sitting Front"
    | "Shawn Sitting Side"
    | "Shawn Suit Front"
    | "Shawn Suit Side"
    | "Shawn Therapist"
    | "Shirley Business Front"
    | "Shirley Business Side"
    | "Shirley Casual Front 2"
    | "Shirley Casual Front"
    | "Shirley Casual Side"
    | "Shirley Casual Sitting Front 2"
    | "Shirley Casual Sitting Front"
    | "Shirley Casual Sitting Side 2"
    | "Shirley Casual Sitting Side"
    | "Shirley Education Front"
    | "Shirley Education Side 2"
    | "Shirley Education Side"
    | "Shirley Sitting Front"
    | "Shirley Sitting Side"
    | "Shirley Skirt Front"
    | "Shirley Skirt Side"
    | "Shirley Suit Front"
    | "Shirley Suit Side"
    | "Silas (Upper Body)"
    | "Silas Customer Support"
    | "Silas HR"
    | "Silas Lounge Front"
    | "Silas Lounge Side"
    | "Silas Sofa Side 2"
    | "Silas Sofa Side"
    | "Sloane in Blue dress"
    | "Sloane in Grey suit"
    | "Sloane in Pink sweater"
    | "Sophia in Black Shirt"
    | "Sophia in Suit "
    | "Sophia in White Suit"
    | "Sophie"
    | "Stacy in Doctor (Front)"
    | "Stacy in Doctor (Left)"
    | "Stacy in Doctor (Right)"
    | "Stacy in Dress (Front)"
    | "Stacy in Dress (Left)"
    | "Stacy in Dress (Right)"
    | "Stacy in Shirt (Front)"
    | "Stacy in Shirt (Left)"
    | "Stacy in Shirt (Right)"
    | "Susan in Black Shirt"
    | "Susan in Black Suit"
    | "Susan in Suit"
    | "Tahlia in Blue dress"
    | "Tahlia in Blue suit"
    | "Tahlia in Dark blue suit"
    | "Tahlia in Red suit"
    | "Tahlia in White shirt"
    | "Teodor (Upper Body)"
    | "Teodor Office Front 2"
    | "Teodor Office Front"
    | "Teodor Office Side 2"
    | "Teodor Office Side"
    | "Teodor Sofa Front 2"
    | "Teodor Sofa Front"
    | "Teodor Sofa Side 2"
    | "Teodor Sofa Side"
    | "Timothy (Upper Body)"
    | "Timothy Casual Front"
    | "Timothy Casual Side"
    | "Timothy Office Front"
    | "Timothy Office Side"
    | "Tito Casual Front 2"
    | "Tito Casual Front"
    | "Tito Casual Side 2"
    | "Tito Casual Side"
    | "Tito Casual Sitting Front"
    | "Tito Casual Sitting Side"
    | "Tito Coat Front 2"
    | "Tito Coat Front"
    | "Tito Coat Full Side"
    | "Tito Coat Side 2"
    | "Tito Coat Side"
    | "Tito Painter Front"
    | "Tito Painter Full Side"
    | "Tito Painter Side"
    | "Tito Painter Sitting Front"
    | "Tito Painter Sitting Side"
    | "Travis in Polo Shirt"
    | "Travis in Gray Suit"
    | "Travis in Black Suit"
    | "Trevor  in Plaid Shirt"
    | "Trevor in Blue Shirt"
    | "Trevor in Blue Suit"
    | "Trevor in Suit"
    | "Tuba Business Chair Front"
    | "Tuba Business Chair Side"
    | "Tuba Business Front"
    | "Tuba Business Side"
    | "Tuba Business Sofa Front"
    | "Tuba Business Sofa Side"
    | "Tuba Casual Front"
    | "Tuba Casual Side"
    | "Tuba Casual Sitting Front"
    | "Tuba Casual Sitting Side"
    | "Tuba Macbook Front"
    | "Tuba Macbook Side"
    | "Veit Office Front"
    | "Veit Office Side"
    | "Veit Sofa Front"
    | "Veit Sofa Side"
    | "Vernon Office Front 2"
    | "Vernon Office Front"
    | "Vernon Office Side 2"
    | "Vernon Office Side"
    | "Verena Office Front"
    | "Verena Office Side"
    | "Verena Sofa Front"
    | "Verena Sofa Side"
    | "Vernon (Upper Body)"
    | "Vernon Lounge Front 2"
    | "Vernon Lounge Side 2"
    | "Vernon Lounge Side"
    | "Vince (Upper Body)"
    | "Vince Business Sofa Front"
    | "Vince Business Training Front"
    | "Vince Business Training Side 2"
    | "Vince Business Training Side"
    | "Vince Sofa Casual Front 2"
    | "Vince Sofa Casual Front"
    | "Vince Sofa Casual Side 2"
    | "Vince Sofa Casual Side"
    | "Violante Brown Suit Front 2"
    | "Violante Brown Suit Front "
    | "Violante Business Sitting Front"
    | "Violante Business Sitting Side"
    | "Violante Casual Sitting Front "
    | "Violante Casual Sitting Side"
    | "Violante Sport Front 2"
    | "Violante Sport Front"
    | "Violante Sport Side"
    | "Violante Sport Sitting Side"
    | "Violante Suit Front"
    | "Vivianna in Black shirt"
    | "Wade in Black Suit"
    | "Wade in Black Jacket"
    | "Wade in Gray Jacket"
    | "Yola Active Speaking Front"
    | "Yola Active Speaking Side"
    | "Yola Business Front"
    | "Yola Business Side"
    | "Yola Calm Speaking Front"
    | "Yola Calm Speaking Side"
    | "Yola Casual Front"
    | "Yola Casual Side"
    | "Yola Employee Badge Front"
    | "Yola Employee Badge Side"
    | "Zosia in Blue blazer"
    | "Zosia in Green dress"
    | "Zosia in Khaki blazer"
    | "Zosia in White dress"
    | "Zosia in Yellow shirt"
    | "Austin in Suit"
    | "Austin in Blue Casual Suit"
    | "Austin in Black Jacket"
    | "Austin in Blue Suit"
    | "Candace in Pink Blazer (Upper Body)"
    | "Chad in Blue Shirt (Upper Body)"
    | "Daisy in Suit"
    | "Daisy in Shirt"
    | "Daisy in Dress"
    | "Daisy in T-shirt"
    | "Francis in Blazer (Upper Body)"
    | "Matthew"
    | "Matthew in Suit"
    | "Matthew in Flowery Shirt"
    | "Matthew in Grey Sweater"
    | "Nik in Black Shirt"
    | "Nik in Blue Sweater"
    | "Tyler in Casual Suit"
    | "Tyler in Shirt"
    | "Tyler in Suit";
  /**
   * Style of the avatar display Default value: `"closeUp"`
   */
  avatar_style?: "normal" | "circle" | "closeUp";
};
export type ChatInput = {
  /**
   * This sets the upper limit for the number of tokens the model can generate in response. It won't produce more than this limit. The maximum value is the context length minus the prompt length.
   */
  max_tokens?: number;
  /**
   * Name of the model to use. Charged based on actual token usage.
   */
  model: string;
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * Should reasoning be the part of the final answer.
   */
  reasoning?: boolean;
  /**
   * System prompt to provide context or instructions to the model
   */
  system_prompt?: string;
  /**
   * This setting influences the variety in the model's responses. Lower values lead to more predictable and typical responses, while higher values encourage more diverse and less common responses. At 0, the model always gives the same response for a given input. Default value: `1`
   */
  temperature?: number;
};
export type ChatOutput = {
  /**
   * Error message if an error occurred
   */
  error?: string;
  /**
   * Generated output
   */
  output: string;
  /**
   * Whether the output is partial
   */
  partial?: boolean;
  /**
   * Usage information
   */
  usage?: CompletionUsage;
};
export type ChatterboxSpeechToSpeechInput = {
  /**
   *
   */
  source_audio_url: string | Blob | File;
  /**
   * Optional URL to an audio file to use as a reference for the generated speech. If provided, the model will try to match the style and tone of the reference audio.
   */
  target_voice_audio_url?: string | Blob | File;
};
export type ChatterboxTextToSpeechInput = {
  /**
   * Optional URL to an audio file to use as a reference for the generated speech. If provided, the model will try to match the style and tone of the reference audio. Default value: `"https://storage.googleapis.com/chatterbox-demo-samples/prompts/male_rickmorty.mp3"`
   */
  audio_url?: string | Blob | File;
  /**
   *  Default value: `0.5`
   */
  cfg?: number;
  /**
   * Exaggeration factor for the generated speech (0.0 = no exaggeration, 1.0 = maximum exaggeration). Default value: `0.25`
   */
  exaggeration?: number;
  /**
   * Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file. Set to 0 for random seed..
   */
  seed?: number;
  /**
   * Temperature for generation (higher = more creative). Default value: `0.7`
   */
  temperature?: number;
  /**
   * The text to be converted to speech (maximum 5000 characters). You can additionally add the following emotive tags: <laugh>, <chuckle>, <sigh>, <cough>, <sniffle>, <groan>, <yawn>, <gasp>
   */
  text: string;
};
export type ChatterboxTextToSpeechMultilingualInput = {
  /**
   * Configuration/pace weight controlling generation guidance (0.0-1.0). Use 0.0 for language transfer to mitigate accent inheritance. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * If using a custom audio URL, specify the language of the audio here. Ignored if voice is not a custom url.
   */
  custom_audio_language?:
    | "english"
    | "arabic"
    | "danish"
    | "german"
    | "greek"
    | "spanish"
    | "finnish"
    | "french"
    | "hebrew"
    | "hindi"
    | "italian"
    | "japanese"
    | "korean"
    | "malay"
    | "dutch"
    | "norwegian"
    | "polish"
    | "portuguese"
    | "russian"
    | "swedish"
    | "swahili"
    | "turkish"
    | "chinese";
  /**
   * Controls speech expressiveness and emotional intensity (0.25-2.0). 0.5 is neutral, higher values increase expressiveness. Extreme values may be unstable. Default value: `0.5`
   */
  exaggeration?: number;
  /**
   * Random seed for reproducible results. Set to 0 for random generation, or provide a specific number for consistent outputs.
   */
  seed?: number;
  /**
   * Controls randomness and variation in generation (0.05-5.0). Higher values create more varied speech patterns. Default value: `0.8`
   */
  temperature?: number;
  /**
   * The text to be converted to speech (maximum 300 characters). Supports 23 languages including English, French, German, Spanish, Italian, Portuguese, Hindi, Arabic, Chinese, Japanese, Korean, and more.
   */
  text: string;
  /**
   * Language code for synthesis. In case using custom please provide audio url and select custom_audio_language. Default value: `"english"`
   */
  voice?: string;
};
export type ChromaticAberrationInput = {
  /**
   * Blue channel shift direction Default value: `"horizontal"`
   */
  blue_direction?: "horizontal" | "vertical";
  /**
   * Blue channel shift amount
   */
  blue_shift?: number;
  /**
   * Green channel shift direction Default value: `"horizontal"`
   */
  green_direction?: "horizontal" | "vertical";
  /**
   * Green channel shift amount
   */
  green_shift?: number;
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Red channel shift direction Default value: `"horizontal"`
   */
  red_direction?: "horizontal" | "vertical";
  /**
   * Red channel shift amount
   */
  red_shift?: number;
};
export type ChronoEditInput = {
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable temporal reasoning.
   */
  enable_temporal_reasoning?: boolean;
  /**
   * The guidance scale for the inference. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The number of temporal reasoning steps to perform. Default value: `8`
   */
  num_temporal_reasoning_steps?: number;
  /**
   * The format of the output image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The resolution of the output image. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The seed for the inference.
   */
  seed?: number;
  /**
   * Whether to return the image in sync mode.
   */
  sync_mode?: boolean;
  /**
   * Enable turbo mode to use for faster inference. Default value: `true`
   */
  turbo_mode?: boolean;
};
export type ChronoEditLoraInput = {
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable temporal reasoning.
   */
  enable_temporal_reasoning?: boolean;
  /**
   * The guidance scale for the inference. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The image to edit.
   */
  image_url: string | Blob | File;
  /**
   * Optional additional LoRAs to merge for this request (max 3).
   */
  loras?: Array<ChronoLoraWeight>;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The number of temporal reasoning steps to perform. Default value: `8`
   */
  num_temporal_reasoning_steps?: number;
  /**
   * The format of the output image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The resolution of the output image. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The seed for the inference.
   */
  seed?: number;
  /**
   * Whether to return the image in sync mode.
   */
  sync_mode?: boolean;
  /**
   * Enable turbo mode to use for faster inference. Default value: `true`
   */
  turbo_mode?: boolean;
};
export type ChronoEditOutput = {
  /**
   * The edited image.
   */
  images: Array<ImageFile>;
  /**
   * The prompt used for the inference.
   */
  prompt: string;
  /**
   * The seed for the inference.
   */
  seed: number;
};
export type ChronoEditPaintBrushInput = {
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The scale factor for the LoRA adapter. Default value: `1`
   */
  lora_scale?: number;
  /**
   * Optional additional LoRAs to merge (max 3).
   */
  loras?: Array<ChronoLoraWeight>;
  /**
   * Optional mask image where black areas indicate regions to sketch/paint.
   */
  mask_url?: string | Blob | File;
  /**
   * Number of denoising steps to run. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Describe how to transform the sketched regions.
   */
  prompt: string;
  /**
   * The resolution of the output image. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The seed for the inference.
   */
  seed?: number;
  /**
   * Whether to return the image in sync mode.
   */
  sync_mode?: boolean;
  /**
   * Enable turbo mode to use faster inference. Default value: `true`
   */
  turbo_mode?: boolean;
};
export type ChronoEditUpscalerInput = {
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale for the inference. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * The scale factor for the LoRA adapter. Default value: `1`
   */
  lora_scale?: number;
  /**
   * Optional additional LoRAs to merge (max 3).
   */
  loras?: Array<ChronoLoraWeight>;
  /**
   * Number of inference steps for the upscaling pass. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The seed for the inference.
   */
  seed?: number;
  /**
   * Whether to return the image in sync mode.
   */
  sync_mode?: boolean;
  /**
   * Target scale factor for the output resolution. Default value: `2`
   */
  upscale_factor?: number;
};
export type ChronoLoraWeight = {
  /**
   * URL or path to the LoRA weights (Safetensors).
   */
  path: string;
  /**
   * Scale factor controlling LoRA strength. Default value: `1`
   */
  scale?: number;
};
export type CityTeleportInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   * Camera angle for the shot Default value: `"eye_level"`
   */
  camera_angle?:
    | "eye_level"
    | "low_angle"
    | "high_angle"
    | "dutch_angle"
    | "birds_eye_view"
    | "worms_eye_view"
    | "overhead"
    | "side_angle";
  /**
   * Optional city background image URL. When provided, the person will be blended into this custom scene.
   */
  city_image_url?: string | Blob | File;
  /**
   * City name (used when city_image_url is not provided)
   */
  city_name: string;
  /**
   * Person photo URL
   */
  person_image_url: string | Blob | File;
  /**
   * Type of photo shot Default value: `"medium_shot"`
   */
  photo_shot?:
    | "extreme_close_up"
    | "close_up"
    | "medium_close_up"
    | "medium_shot"
    | "medium_long_shot"
    | "long_shot"
    | "extreme_long_shot"
    | "full_body";
};
export type ClarityUpscalerInput = {
  /**
   * The creativity of the model. The higher the creativity, the more the model will deviate from the prompt.
   * Refers to the denoise strength of the sampling. Default value: `0.35`
   */
  creativity?: number;
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want in the image. Default value: `"(worst quality, low quality, normal quality:2)"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `18`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results. Default value: `"masterpiece, best quality, highres"`
   */
  prompt?: string;
  /**
   * The resemblance of the upscaled image to the original image. The higher the resemblance, the more the model will try to keep the original image.
   * Refers to the strength of the ControlNet. Default value: `0.6`
   */
  resemblance?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The upscale factor Default value: `2`
   */
  upscale_factor?: number;
};
export type ClarityUpscalerOutput = {
  /**
   * The URL of the generated image.
   */
  image: Image;
  /**
   * The seed used to generate the image.
   */
  seed: number;
  /**
   * The timings of the different steps in the workflow.
   */
  timings: unknown;
};
export type codeformerInput = {
  /**
   * Should faces etc should be aligned.
   */
  aligned?: boolean;
  /**
   * Should faces be upscaled Default value: `true`
   */
  face_upscale?: boolean;
  /**
   * Weight of the fidelity factor. Default value: `0.5`
   */
  fidelity?: number;
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * Should only center face be restored
   */
  only_center_face?: boolean;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * Upscaling factor Default value: `2`
   */
  upscale_factor?: number;
};
export type Cogvideox5bImageToVideoInput = {
  /**
   * The target FPS of the video Default value: `16`
   */
  export_fps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related video to show you. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * The URL to the image to generate the video from.
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. We currently support one lora.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to generate video from Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * Use RIFE for video interpolation Default value: `true`
   */
  use_rife?: boolean;
  /**
   * The size of the generated video.
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type Cogvideox5bInput = {
  /**
   * The target FPS of the video Default value: `16`
   */
  export_fps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related video to show you. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * The LoRAs to use for the image generation. We currently support one lora.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to generate video from Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * Use RIFE for video interpolation Default value: `true`
   */
  use_rife?: boolean;
  /**
   * The size of the generated video.
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type Cogvideox5bOutput = {
  /**
   * The prompt used for generating the video.
   */
  prompt: string;
  /**
   * Seed of the generated video. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
  /**
   * The URL to the generated video
   */
  video: File;
};
export type Cogvideox5bVideoToVideoInput = {
  /**
   * The target FPS of the video Default value: `16`
   */
  export_fps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related video to show you. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * The LoRAs to use for the image generation. We currently support one lora.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to generate video from Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The strength to use for Video to Video.  1.0 completely remakes the video while 0.0 preserves the original. Default value: `0.8`
   */
  strength?: number;
  /**
   * Use RIFE for video interpolation Default value: `true`
   */
  use_rife?: boolean;
  /**
   * The size of the generated video.
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The video to generate the video from.
   */
  video_url: string | Blob | File;
};
export type cogview4Input = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Color = {
  /**
   * The blue component of the color (0-255).
   */
  blue: number;
  /**
   * The green component of the color (0-255).
   */
  green: number;
  /**
   * The name of the color.
   */
  name?: string;
  /**
   * The red component of the color (0-255).
   */
  red: number;
};
export type ColorCorrectionInput = {
  /**
   * Brightness adjustment
   */
  brightness?: number;
  /**
   * Contrast adjustment
   */
  contrast?: number;
  /**
   * Gamma adjustment Default value: `1`
   */
  gamma?: number;
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Saturation adjustment
   */
  saturation?: number;
  /**
   * Color temperature adjustment
   */
  temperature?: number;
};
export type ColorizeInput = {
  /**
   * Select the color palette or aesthetic for the output image
   */
  color:
    | "contemporary color"
    | "vivid color"
    | "black and white colors"
    | "sepia vintage";
  /**
   * The source image.
   */
  image_url: string | Blob | File;
};
export type ColorPalette = {
  /**
   * A list of color palette members that define the color palette
   */
  members?: Array<ColorPaletteMember>;
  /**
   * A color palette preset value
   */
  name?:
    | "EMBER"
    | "FRESH"
    | "JUNGLE"
    | "MAGIC"
    | "MELON"
    | "MOSAIC"
    | "PASTEL"
    | "ULTRAMARINE";
};
export type ColorPaletteMember = {
  /**
   * The weight of the color in the color palette Default value: `0.5`
   */
  color_weight?: number;
  /**
   * RGB color value for the palette member
   */
  rgb: RGBColor;
};
export type ColorTintInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Tint color mode Default value: `"sepia"`
   */
  tint_mode?:
    | "sepia"
    | "red"
    | "green"
    | "blue"
    | "cyan"
    | "magenta"
    | "yellow"
    | "purple"
    | "orange"
    | "warm"
    | "cool"
    | "lime"
    | "navy"
    | "vintage"
    | "rose"
    | "teal"
    | "maroon"
    | "peach"
    | "lavender"
    | "olive";
  /**
   * Tint strength Default value: `1`
   */
  tint_strength?: number;
};
export type CombineInput = {
  /**
   * URL of the audio file to use as the audio track
   */
  audio_url: string | Blob | File;
  /**
   * Offset in seconds for when the audio should start relative to the video
   */
  start_offset?: number;
  /**
   * URL of the video file to use as the video track
   */
  video_url: string | Blob | File;
};
export type ComfyInput = {
  /**
   * Disable saving prompt metadata in files.
   */
  disable_metadata?: boolean;
  /**
   *
   */
  extra_data?: unknown;
  /**
   *
   */
  prompt: unknown;
};
export type CompletionUsage = {
  /**
   * Number of tokens in the completion
   */
  completion_tokens: number;
  /**
   * Number of tokens in the prompt
   */
  prompt_tokens: number;
  /**
   * Total tokens used
   */
  total_tokens: number;
};
export type ComposeOutput = {
  /**
   * URL of the video's thumbnail image
   */
  thumbnail_url: string | Blob | File;
  /**
   * URL of the processed video file
   */
  video_url: string | Blob | File;
};
export type CompressImageInput = {
  /**
   * The URL of the image to compress
   */
  image_url: string | Blob | File;
  /**
   * Maximum height in pixels (resizes if larger, maintains aspect ratio)
   */
  max_height?: number;
  /**
   * Maximum width in pixels (resizes if larger, maintains aspect ratio) Default value: `470`
   */
  max_width?: number;
  /**
   * Apply additional optimization (slightly slower but better compression) Default value: `true`
   */
  optimize?: boolean;
  /**
   * Output format (jpg recommended for compression) Default value: `"jpg"`
   */
  output_format?: "jpg" | "jpeg" | "webp" | "png";
  /**
   * Compression quality (1-100, higher = better quality, larger file) Default value: `99`
   */
  quality?: number;
};
export type CompressImageOutput = {
  /**
   * Compressed image size in bytes
   */
  compressed_size: number;
  /**
   * Compression ratio (compressed/original)
   */
  compression_ratio: number;
  /**
   * Compressed image
   */
  image: Image;
  /**
   * Original image size in bytes
   */
  original_size: number;
};
export type ConcatImageInput = {
  /**
   * Alignment of images Default value: `"center"`
   */
  alignment?: "start" | "center" | "end";
  /**
   * Background color for spacing Default value: `"white"`
   */
  background_color?:
    | "white"
    | "black"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta"
    | "transparent";
  /**
   * Direction of concatenation Default value: `"horizontal"`
   */
  direction?: "horizontal" | "vertical";
  /**
   * List of image URLs to concatenate
   */
  image_urls: Array<string>;
  /**
   * Output format for the concatenated image Default value: `"png"`
   */
  output_format?: "png" | "jpg" | "jpeg" | "webp";
  /**
   * Spacing between images in pixels
   */
  spacing?: number;
};
export type ControlLoraWeight = {
  /**
   * URL of the image to be used as the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * URL or the path to the LoRA weights.
   */
  path: string;
  /**
   * Type of preprocessing to apply to the input image. Default value: `"None"`
   */
  preprocess?: "canny" | "depth" | "None";
  /**
   * The scale of the LoRA weight. This is used to scale the LoRA weight
   * before merging it with the base model. Providing a dictionary as {"layer_name":layer_scale} allows per-layer lora scale settings. Layers with no scale provided will have scale 1.0. Default value: `1`
   */
  scale?: unknown | number;
};
export type ControlNet = {
  /**
   * The scale of the control net weight. This is used to scale the control net weight
   * before merging it with the base model. Default value: `1`
   */
  conditioning_scale?: number;
  /**
   * optional URL to the controlnet config.json file.
   */
  config_url?: string | Blob | File;
  /**
   * URL of the image to be used as the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * The percentage of the image to end applying the controlnet in terms of the total timesteps. Default value: `1`
   */
  end_percentage?: number;
  /**
   * URL of the mask for the control image.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Threshold for mask. Default value: `0.5`
   */
  mask_threshold?: number;
  /**
   * URL or the path to the control net weights.
   */
  path: string;
  /**
   * The percentage of the image to start applying the controlnet in terms of the total timesteps.
   */
  start_percentage?: number;
  /**
   * The optional variant if a Hugging Face repo key is used.
   */
  variant?: string;
};
export type controlnetsdxlInput = {
  /**
   * The scale of the ControlNet. Default value: `0.5`
   */
  controlnet_conditioning_scale?: number;
  /**
   * Url to input image
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps
   * to generate your final result which can increase the amount of detail in your image. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
};
export type ControlNetUnion = {
  /**
   * optional URL to the controlnet config.json file.
   */
  config_url?: string | Blob | File;
  /**
   * The control images and modes to use for the control net.
   */
  controls: Array<ControlNetUnionInput>;
  /**
   * URL or the path to the control net weights.
   */
  path: string;
  /**
   * The optional variant if a Hugging Face repo key is used.
   */
  variant?: string;
};
export type ControlNetUnionInput = {
  /**
   * The scale of the control net weight. This is used to scale the control net weight
   * before merging it with the base model. Default value: `1`
   */
  conditioning_scale?: number;
  /**
   * URL of the image to be used as the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * Control Mode for Flux Controlnet Union. Supported values are:
   * - canny: Uses the edges for guided generation.
   * - tile: Uses the tiles for guided generation.
   * - depth: Utilizes a grayscale depth map for guided generation.
   * - blur: Adds a blur to the image.
   * - pose: Uses the pose of the image for guided generation.
   * - gray: Converts the image to grayscale.
   * - low-quality: Converts the image to a low-quality image.
   */
  control_mode:
    | "canny"
    | "tile"
    | "depth"
    | "blur"
    | "pose"
    | "gray"
    | "low-quality";
  /**
   * The percentage of the image to end applying the controlnet in terms of the total timesteps. Default value: `1`
   */
  end_percentage?: number;
  /**
   * URL of the mask for the control image.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Threshold for mask. Default value: `0.5`
   */
  mask_threshold?: number;
  /**
   * The percentage of the image to start applying the controlnet in terms of the total timesteps.
   */
  start_percentage?: number;
};
export type controlnextInput = {
  /**
   * Number of frames to process in each batch. Default value: `24`
   */
  batch_frames?: number;
  /**
   * Condition scale for ControlNeXt. Default value: `1`
   */
  controlnext_cond_scale?: number;
  /**
   * Chunk size for decoding frames. Default value: `2`
   */
  decode_chunk_size?: number;
  /**
   * Frames per second for the output video. Default value: `7`
   */
  fps?: number;
  /**
   * Guidance scale for the diffusion process. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * Height of the output video. Default value: `1024`
   */
  height?: number;
  /**
   * URL of the reference image.
   */
  image_url: string | Blob | File;
  /**
   * Maximum number of frames to process. Default value: `240`
   */
  max_frame_num?: number;
  /**
   * Motion bucket ID for the pipeline. Default value: `127`
   */
  motion_bucket_id?: number;
  /**
   * Number of inference steps. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * Number of overlapping frames between batches. Default value: `6`
   */
  overlap?: number;
  /**
   * Stride for sampling frames from the input video. Default value: `2`
   */
  sample_stride?: number;
  /**
   * URL of the input video.
   */
  video_url: string | Blob | File;
  /**
   * Width of the output video. Default value: `576`
   */
  width?: number;
};
export type ConversationStreamInput = {
  /**
   * URL to the input audio file (user's speech).
   */
  audio_url: string | Blob | File;
  /**
   * Output audio format. 'wav' for standard audio with headers, 'pcm' for raw s16le PCM (lowest latency). Default value: `"wav"`
   */
  output_format?: "wav" | "pcm";
  /**
   * Text prompt describing the AI persona and conversation context. Default value: `"You are a wise and friendly teacher. Answer questions or provide advice in a clear and engaging way."`
   */
  prompt?: string;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * Audio sampling temperature. Higher values produce more diverse outputs. Default value: `0.8`
   */
  temperature_audio?: number;
  /**
   * Text sampling temperature. Higher values produce more diverse outputs. Default value: `0.7`
   */
  temperature_text?: number;
  /**
   * Top-K sampling for audio tokens. Default value: `250`
   */
  top_k_audio?: number;
  /**
   * Top-K sampling for text tokens. Default value: `25`
   */
  top_k_text?: number;
  /**
   * Voice ID for the AI response. NAT = natural, VAR = variety. F = female, M = male. Ignored when voice_audio_url is provided. Default value: `"NATF2"`
   */
  voice?:
    | "NATF0"
    | "NATF1"
    | "NATF2"
    | "NATF3"
    | "NATM0"
    | "NATM1"
    | "NATM2"
    | "NATM3"
    | "VARF0"
    | "VARF1"
    | "VARF2"
    | "VARF3"
    | "VARF4"
    | "VARM0"
    | "VARM1"
    | "VARM2"
    | "VARM3"
    | "VARM4";
  /**
   * URL to a voice sample audio for on-the-fly voice cloning. When provided, the AI responds in the cloned voice instead of the preset 'voice'. 10+ seconds of clear speech recommended. Billed at 2x rate.
   */
  voice_audio_url?: string | Blob | File;
};
export type ConvertFormatInput = {
  /**
   * URL of 3D file to convert (FBX, OBJ, GLB). Max size: 60MB.
   */
  input_file_url: string | Blob | File;
  /**
   * Target output format. STL: 3D printing. USDZ: AR/iOS. FBX: animation. MP4/GIF: preview video/animation.
   */
  output_format: "STL" | "USDZ" | "FBX" | "MP4" | "GIF";
};
export type ConvertFormatOutput = {
  /**
   * Converted file in the requested format.
   */
  result_file: File;
};
export type Coordinates = {
  /**
   * Height of the product in the image.
   */
  height: number;
  /**
   * Width of the product in the image.
   */
  width: number;
  /**
   * X coordinate of the product in the image.
   */
  x: number;
  /**
   * Y coordinate of the product in the image.
   */
  y: number;
};
export type CosmosPredict25DistilledTextToVideoInput = {
  /**
   * A negative prompt to guide generation away from undesired content. Default value: `"The video captures a series of frames showing ugly scenes, static with no motion, motion blur, over-saturation, shaky footage, low resolution, grainy texture, pixelated images, poorly lit areas, underexposed and overexposed scenes, poor color balance, washed out colors, choppy sequences, jerky movements, low frame rate, artifacting, color banding, unnatural transitions, outdated special effects, fake elements, unconvincing visuals, poorly edited content, jump cuts, visual noise, and flickering. Overall, the video is of poor quality."`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 9 and 93. Default value: `93`
   */
  num_frames?: number;
  /**
   * Number of denoising steps. Distilled model works well with fewer steps. Default value: `10`
   */
  num_inference_steps?: number;
  /**
   * The text prompt describing the video to generate.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The format of the output video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the output video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
};
export type CosmosPredict25ImageToVideoInput = {
  /**
   * Classifier-free guidance scale. Higher values increase prompt adherence. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * URL of the input image to use as first frame.
   */
  image_url: string | Blob | File;
  /**
   * A negative prompt to guide generation away from undesired content. Default value: `"The video captures a series of frames showing ugly scenes, static with no motion, motion blur, over-saturation, shaky footage, low resolution, grainy texture, pixelated images, poorly lit areas, underexposed and overexposed scenes, poor color balance, washed out colors, choppy sequences, jerky movements, low frame rate, artifacting, color banding, unnatural transitions, outdated special effects, fake elements, unconvincing visuals, poorly edited content, jump cuts, visual noise, and flickering. Overall, the video is of poor quality."`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 9 and 93. Default value: `93`
   */
  num_frames?: number;
  /**
   * Number of denoising steps. More steps yield higher quality but take longer. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The text prompt describing the video to generate.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The format of the output video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the output video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
};
export type CosmosPredict25TextToVideoInput = {
  /**
   * Classifier-free guidance scale. Higher values increase prompt adherence. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * A negative prompt to guide generation away from undesired content. Default value: `"The video captures a series of frames showing ugly scenes, static with no motion, motion blur, over-saturation, shaky footage, low resolution, grainy texture, pixelated images, poorly lit areas, underexposed and overexposed scenes, poor color balance, washed out colors, choppy sequences, jerky movements, low frame rate, artifacting, color banding, unnatural transitions, outdated special effects, fake elements, unconvincing visuals, poorly edited content, jump cuts, visual noise, and flickering. Overall, the video is of poor quality."`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 9 and 93. Default value: `93`
   */
  num_frames?: number;
  /**
   * Number of denoising steps. More steps yield higher quality but take longer. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The text prompt describing the video to generate.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The format of the output video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the output video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
};
export type CosmosPredict25VideoToVideoInput = {
  /**
   * Classifier-free guidance scale. Higher values increase prompt adherence. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * A negative prompt to guide generation away from undesired content. Default value: `"The video captures a series of frames showing ugly scenes, static with no motion, motion blur, over-saturation, shaky footage, low resolution, grainy texture, pixelated images, poorly lit areas, underexposed and overexposed scenes, poor color balance, washed out colors, choppy sequences, jerky movements, low frame rate, artifacting, color banding, unnatural transitions, outdated special effects, fake elements, unconvincing visuals, poorly edited content, jump cuts, visual noise, and flickering. Overall, the video is of poor quality."`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 9 and 93. Default value: `93`
   */
  num_frames?: number;
  /**
   * Number of denoising steps. More steps yield higher quality but take longer. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The text prompt describing the video to generate.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The format of the output video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the output video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL of the input video to use as conditioning.
   */
  video_url: string | Blob | File;
};
export type CreateCharacterInput = {
  /**
   * Name for the character (1–80 characters). Refer to this name in prompts when using the character.
   */
  name: string;
  /**
   * URL of an MP4 video (minimum 720p, max ~2.67:1 aspect ratio) to define the character. Videos exceeding 1080p are automatically scaled down. Non-standard aspect ratios are automatically padded to 16:9 (landscape) or 9:16 (portrait). Videos longer than 4 seconds are trimmed to the first 4 seconds.
   */
  video_url: string | Blob | File;
};
export type CreateCharacterOutput = {
  /**
   * API character ID (format char_...). Use this in character_ids when generating video.
   */
  id: string;
  /**
   * The character name
   */
  name: string;
};
export type CreateVoiceInput = {
  /**
   * URL of the voice audio file. Supports .mp3/.wav audio or .mp4/.mov video. Duration must be 5-30 seconds with clean, single-voice audio.
   */
  voice_url: string | Blob | File;
};
export type CreateVoiceOutput = {
  /**
   * Unique identifier for the created voice
   */
  voice_id: string;
};
export type CreatifyAuroraInput = {
  /**
   * Guidance scale to be used for audio adherence. Default value: `2`
   */
  audio_guidance_scale?: number;
  /**
   * The URL of the audio file to be used for video generation.
   */
  audio_url: string | Blob | File;
  /**
   * Guidance scale to be used for text prompt adherence. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The URL of the image file to be used for video generation.
   */
  image_url: string | Blob | File;
  /**
   * A text prompt to guide the video generation process.
   */
  prompt?: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
};
export type CreativeUpscalerInput = {
  /**
   * The URL to the additional embeddings to use for the upscaling. Default is None
   */
  additional_embedding_url?: string | Blob | File;
  /**
   * The scale of the additional LORA model to use for the upscaling. Default is 1.0 Default value: `1`
   */
  additional_lora_scale?: number;
  /**
   * The URL to the additional LORA model to use for the upscaling. Default is None
   */
  additional_lora_url?: string | Blob | File;
  /**
   * The URL to the base model to use for the upscaling
   */
  base_model_url?: string | Blob | File;
  /**
   * How much the output can deviate from the original Default value: `0.5`
   */
  creativity?: number;
  /**
   * How much detail to add Default value: `1`
   */
  detail?: number;
  /**
   * If set to true, the resulting image will be checked whether it includes any
   * potentially unsafe content. If it does, it will be replaced with a black
   * image. Default value: `true`
   */
  enable_safety_checks?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * The type of model to use for the upscaling. Default is SD_1_5 Default value: `"SD_1_5"`
   */
  model_type?: "SD_1_5" | "SDXL";
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"blurry, low resolution, bad, ugly, low quality, pixelated, interpolated, compression artifacts, noisey, grainy"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to use for generating the image. The more steps
   * the better the image will be but it will also take longer to generate. Default value: `20`
   */
  num_inference_steps?: number;
  /**
   * Allow for large uploads that could take a very long time.
   */
  override_size_limits?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results. If no prompt is provide BLIP2 will be used to generate a prompt.
   */
  prompt?: string;
  /**
   * The suffix to add to the prompt. This is useful to add a common ending to all prompts such as 'high quality' etc or embedding tokens. Default value: `" high quality, highly detailed, high resolution, sharp"`
   */
  prompt_suffix?: string;
  /**
   * The scale of the output image. The higher the scale, the bigger the output image will be. Default value: `2`
   */
  scale?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * How much to preserve the shape of the original image Default value: `0.25`
   */
  shape_preservation?: number;
  /**
   * If set to true, the image will not be processed by the CCSR model before
   * being processed by the creativity model.
   */
  skip_ccsr?: boolean;
};
export type CropImageInput = {
  /**
   * Height as percentage of image height (0-100) Default value: `100`
   */
  height_percent?: number;
  /**
   * The URL of the image to crop
   */
  image_url: string | Blob | File;
  /**
   * Output format for the cropped image Default value: `"png"`
   */
  output_format?: "png" | "jpg" | "jpeg" | "webp";
  /**
   * Width as percentage of image width (0-100) Default value: `100`
   */
  width_percent?: number;
  /**
   * X coordinate as percentage of image width (0-100)
   */
  x_percent?: number;
  /**
   * Y coordinate as percentage of image height (0-100)
   */
  y_percent?: number;
};
export type CrystalUpscaleInput = {
  /**
   * Creativity level for upscaling
   */
  creativity?: number;
  /**
   * URL to the input image
   */
  image_url: string | Blob | File;
  /**
   * Scale factor Default value: `2`
   */
  scale_factor?: number;
};
export type CrystalVideoUpscaleInput = {
  /**
   * Scale factor. The scale factor must be chosen such that the upscaled video does not exceed 5K resolution. Default value: `2`
   */
  scale_factor?: number;
  /**
   * URL to the input video.
   */
  video_url: string | Blob | File;
};
export type Csm1bInput = {
  /**
   * The context to generate an audio from.
   */
  context?: Array<Speaker>;
  /**
   * The text to generate an audio from.
   */
  scene: Array<Turn>;
};
export type Csm1bOutput = {
  /**
   * The generated audio.
   */
  audio: File | string;
};
export type DavinciMagihumanInput = {
  /**
   * Optional URL of the driving audio for lipsync mode. If omitted, audio is generated from the prompt.
   */
  audio_url?: string | Blob | File;
  /**
   * Duration of the generated video in seconds. Default value: `5`
   */
  duration?: number;
  /**
   * If enabled, runs safety checks on the prompt and input image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL of the reference image for image-to-video generation.
   */
  image_url: string | Blob | File;
  /**
   * Number of denoising steps. Defaults to 8 for 256p and 32 for 540p/720p/1080p (base + SR).
   */
  num_inference_steps?: number;
  /**
   * Text prompt describing the desired video content.
   */
  prompt: string;
  /**
   * Output resolution. '256p' uses the official base-model 448x256 path. '1080p' uses the official base + 1080p super-resolution pipeline. '540p' and '720p' reuse that sharper 1080p SR path and downsample to the requested output size. Default value: `"256p"`
   */
  resolution?: "256p" | "540p" | "720p" | "1080p";
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
};
export type DavinciMagihumanOutput = {
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video with synchronized audio.
   */
  video?: File;
};
export type deepfilternet3Output = {
  /**
   * The audio file that was enhanced.
   */
  audio_file: AudioFile;
  /**
   * Timings for each step in the pipeline.
   */
  timings: DeepFilterNetTimings;
};
export type demucsInput = {
  /**
   * URL of the audio file to separate into stems
   */
  audio_url: string | Blob | File;
  /**
   * Demucs model to use for separation Default value: `"htdemucs_6s"`
   */
  model?:
    | "htdemucs"
    | "htdemucs_ft"
    | "htdemucs_6s"
    | "hdemucs_mmi"
    | "mdx"
    | "mdx_extra"
    | "mdx_q"
    | "mdx_extra_q";
  /**
   * Output audio format for the separated stems Default value: `"mp3"`
   */
  output_format?: "wav" | "mp3";
  /**
   * Overlap between segments (0.0 to 1.0). Higher values may improve quality but increase processing time. Default value: `0.25`
   */
  overlap?: number;
  /**
   * Length in seconds of each segment for processing. Smaller values use less memory but may reduce quality. Default is model-specific.
   */
  segment_length?: number;
  /**
   * Number of random shifts for equivariant stabilization. Higher values improve quality but increase processing time. Default value: `1`
   */
  shifts?: number;
  /**
   * Specific stems to extract. If None, extracts all available stems. Available stems depend on model: vocals, drums, bass, other, guitar, piano (for 6s model)
   */
  stems?: Array<"vocals" | "drums" | "bass" | "other" | "guitar" | "piano">;
};
export type demucsOutput = {
  /**
   * Separated bass audio file
   */
  bass?: File;
  /**
   * Separated drums audio file
   */
  drums?: File;
  /**
   * Separated guitar audio file (only available for 6s models)
   */
  guitar?: File;
  /**
   * Separated other instruments audio file
   */
  other?: File;
  /**
   * Separated piano audio file (only available for 6s models)
   */
  piano?: File;
  /**
   * Separated vocals audio file
   */
  vocals?: File;
};
export type DepthAnythingVideoInput = {
  /**
   * Colormap for depth visualization. 'turbo' (recommended) shows near=warm, far=cool. 'grayscale' for raw normalized depth. 'inferno'/'magma' for perceptually uniform. 'viridis' for colorblind-friendly. Default value: `"grayscale"`
   */
  colormap?: "grayscale" | "turbo" | "inferno" | "magma" | "viridis";
  /**
   * Export raw float32 depths as .npz file with: 'depths' [N,H,W], 'min_depth', 'max_depth', 'fps', 'model', 'shape'.
   */
  include_raw_depths?: boolean;
  /**
   * Max frames to process. None = all frames.
   */
  max_frames?: number;
  /**
   * Depth estimation model size. VDA-Large = best quality, VDA-Small = fastest. Default value: `"VDA-Large"`
   */
  model?: "VDA-Small" | "VDA-Base" | "VDA-Large";
  /**
   * Output video FPS. None = same as input.
   */
  output_fps?: number;
  /**
   * Output resolution. 'auto' preserves input (max 1080p). Options: 'auto', '360p', '480p', '720p', '1080p'. Default value: `"auto"`
   */
  resolution?: "auto" | "360p" | "480p" | "720p" | "1080p";
  /**
   * Output original | depth comparison video.
   */
  side_by_side?: boolean;
  /**
   * URL of the input video to estimate depth for.
   */
  video_url: string | Blob | File;
};
export type DepthAnythingVideoOutput = {
  /**
   * Raw depth values as .npz (if include_raw_depths=True).
   */
  raw_depths?: File;
  /**
   * Depth visualization video (MP4, H.264).
   */
  video: File;
};
export type DepthLoraInput = {
  /**
   * The image to use for control lora. This is used to control the style of the generated image.
   */
  control_lora_image_url: string | Blob | File;
  /**
   * The strength of the control lora. Default value: `1`
   */
  control_lora_strength?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for image-to-image generation.
   */
  image_url?: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * If set to true, the input image will be preprocessed to extract depth information.
   * This is useful for generating depth maps from images. Default value: `true`
   */
  preprocess_depth?: boolean;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type DepthMapInput = {
  /**
   * a Default value: `6.283185307179586`
   */
  a?: number;
  /**
   * bg_th Default value: `0.1`
   */
  bg_th?: number;
  /**
   * depth_and_normal
   */
  depth_and_normal?: boolean;
  /**
   * Input image url.
   */
  image_url: string | Blob | File;
};
export type DesaturateInput = {
  /**
   * Desaturation factor Default value: `1`
   */
  desaturate_factor?: number;
  /**
   * Desaturation method Default value: `"luminance (Rec.709)"`
   */
  desaturate_method?:
    | "luminance (Rec.709)"
    | "luminance (Rec.601)"
    | "average"
    | "lightness";
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
};
export type DetectionInput = {
  /**
   * Whether to combine points into a single point for point detection. This has no effect for bbox detection or gaze detection.
   */
  combine_points?: boolean;
  /**
   * Text description of what to detect
   */
  detection_prompt: string;
  /**
   * Image URL to be processed
   */
  image_url: string | Blob | File;
  /**
   * Whether to show visualization for detection Default value: `true`
   */
  show_visualization?: boolean;
  /**
   * Type of detection to perform
   */
  task_type: "bbox_detection" | "point_detection" | "gaze_detection";
  /**
   * Whether to use ensemble for gaze detection
   */
  use_ensemble?: boolean;
};
export type DetectionOutput = {
  /**
   * Output image with detection visualization
   */
  image?: Image;
  /**
   * Detection results as text
   */
  text_output: string;
};
export type DevImageToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type DevReduxInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type DialogueBlock = {
  /**
   * The dialogue text
   */
  text: string;
  /**
   * The name or the ID of the voice to be used for the generation.
   */
  voice: string;
};
export type DiaOutput = {
  /**
   * The generated speech audio
   */
  audio: File;
};
export type DiarizationSegment = {
  /**
   * Speaker ID of the segment
   */
  speaker: string;
  /**
   * Start and end timestamp of the segment
   */
  timestamp: Array<unknown>;
};
export type DiaTtsInput = {
  /**
   * The text to be converted to speech.
   */
  text: string;
};
export type DictOutput = {
  /**
   * The value of the measurement.
   */
  value?: unknown;
};
export type DifferentialDiffusionInput = {
  /**
   * Base shift for the scheduled timesteps Default value: `0.5`
   */
  base_shift?: number;
  /**
   * URL of change map.
   */
  change_map_image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation which use a control image. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  control_loras?: Array<ControlLoraWeight>;
  /**
   * The controlnet unions to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnet_unions?: Array<ControlNetUnion>;
  /**
   * The controlnets to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnets?: Array<ControlNet>;
  /**
   * EasyControl Inputs to use for image generation.
   */
  easycontrols?: Array<EasyControlWeight>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Use an image input to influence the generation. Can be used to fill images in masked areas.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use as initial image.
   */
  image_url: string | Blob | File;
  /**
   * IP-Adapter to use for image generation.
   */
  ip_adapters?: Array<IPAdapter>;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * Max shift for the scheduled timesteps Default value: `1.15`
   */
  max_shift?: number;
  /**
   * The alpha value for NAG. This value is used as a final weighting
   * factor for steering the normalized guidance (positive and negative prompts)
   * in the direction of the positive prompt. Higher values will result in less
   * steering on the normalized guidance where lower values will result in
   * considering the positive prompt guidance more. Default value: `0.25`
   */
  nag_alpha?: number;
  /**
   * The proportion of steps to apply NAG. After the specified proportion
   * of steps has been iterated, the remaining steps will use original
   * attention processors in FLUX. Default value: `0.25`
   */
  nag_end?: number;
  /**
   * The scale for NAG. Higher values will result in a image that is more distant
   * to the negative prompt. Default value: `3`
   */
  nag_scale?: number;
  /**
   * The tau for NAG. Controls the normalization of the hidden state.
   * Higher values will result in a less aggressive normalization,
   * but may also lead to unexpected changes with respect to the original image.
   * Not recommended to change this value. Default value: `2.5`
   */
  nag_tau?: number;
  /**
   * Negative prompt to steer the image generation away from unwanted features.
   * By default, we will be using NAG for processing the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  real_cfg_scale?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to be ended. Default value: `1`
   */
  reference_end?: number;
  /**
   * URL of Image for Reference-Only
   */
  reference_image_url?: string | Blob | File;
  /**
   * The percentage of the total timesteps when the reference guidance is to bestarted.
   */
  reference_start?: number;
  /**
   * Strength of reference_only generation. Only used if a reference image is provided. Default value: `0.65`
   */
  reference_strength?: number;
  /**
   * Scheduler for the denoising process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmpp_2m";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Sigmas schedule for the denoising process.
   */
  sigma_schedule?: string;
  /**
   * The strength to use for differential diffusion. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Specifies whether beta sigmas ought to be used.
   */
  use_beta_schedule?: boolean;
  /**
   * Uses CFG-zero init sampling as in https://arxiv.org/abs/2503.18886.
   */
  use_cfg_zero?: boolean;
  /**
   * Uses classical CFG as in SD1.5, SDXL, etc. Increases generation times and price when set to be true.
   * If using XLabs IP-Adapter v1, this will be turned on!.
   */
  use_real_cfg?: boolean;
};
export type diffrhythmInput = {
  /**
   * The CFG strength to use for the music generation. Default value: `4`
   */
  cfg_strength?: number;
  /**
   * The prompt to generate the song from. Must have two sections. Sections start with either [chorus] or a [verse].
   */
  lyrics: string;
  /**
   * The duration of the music to generate. Default value: `"95s"`
   */
  music_duration?: "95s" | "285s";
  /**
   * The number of inference steps to use for the music generation. Default value: `32`
   */
  num_inference_steps?: number;
  /**
   * The URL of the reference audio to use for the music generation.
   */
  reference_audio_url?: string | Blob | File;
  /**
   * The scheduler to use for the music generation. Default value: `"euler"`
   */
  scheduler?: "euler" | "midpoint" | "rk4" | "implicit_adams";
  /**
   * The style prompt to use for the music generation.
   */
  style_prompt?: string;
};
export type DissolveInput = {
  /**
   * Dissolve blend factor Default value: `0.5`
   */
  dissolve_factor?: number;
  /**
   * URL of second image for dissolve
   */
  dissolve_image_url: string | Blob | File;
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
};
export type DistilledExtendVideoInput = {
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `29`
   */
  constant_rate_factor?: number;
  /**
   * Whether to use a detail pass. If True, the model will perform a second pass to refine the video and enhance details. This incurs a 2.0x cost multiplier on the base price.
   */
  enable_detail_pass?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * The frame rate of the video. Default value: `24`
   */
  frame_rate?: number;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The factor for adaptive instance normalization (AdaIN) applied to generated video chunks after the first. This can help deal with a gradual increase in saturation/contrast in the generated video by normalizing the color distribution across the video. A high value will ensure the color distribution is more consistent across the video, while a low value will allow for more variation in color distribution. Default value: `0.5`
   */
  temporal_adain_factor?: number;
  /**
   * The compression ratio for tone mapping. This is used to compress the dynamic range of the video to improve visual quality. A value of 0.0 means no compression, while a value of 1.0 means maximum compression.
   */
  tone_map_compression_ratio?: number;
  /**
   * Video to be extended.
   */
  video: ExtendVideoConditioningInput;
};
export type DistilledImageToVideoInput = {
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `29`
   */
  constant_rate_factor?: number;
  /**
   * Whether to use a detail pass. If True, the model will perform a second pass to refine the video and enhance details. This incurs a 2.0x cost multiplier on the base price.
   */
  enable_detail_pass?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * The frame rate of the video. Default value: `24`
   */
  frame_rate?: number;
  /**
   * Image URL for Image-to-Video task
   */
  image_url: string | Blob | File;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The factor for adaptive instance normalization (AdaIN) applied to generated video chunks after the first. This can help deal with a gradual increase in saturation/contrast in the generated video by normalizing the color distribution across the video. A high value will ensure the color distribution is more consistent across the video, while a low value will allow for more variation in color distribution. Default value: `0.5`
   */
  temporal_adain_factor?: number;
  /**
   * The compression ratio for tone mapping. This is used to compress the dynamic range of the video to improve visual quality. A value of 0.0 means no compression, while a value of 1.0 means maximum compression.
   */
  tone_map_compression_ratio?: number;
};
export type DistilledMultiConditioningVideoInput = {
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `29`
   */
  constant_rate_factor?: number;
  /**
   * Whether to use a detail pass. If True, the model will perform a second pass to refine the video and enhance details. This incurs a 2.0x cost multiplier on the base price.
   */
  enable_detail_pass?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * The frame rate of the video. Default value: `24`
   */
  frame_rate?: number;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The factor for adaptive instance normalization (AdaIN) applied to generated video chunks after the first. This can help deal with a gradual increase in saturation/contrast in the generated video by normalizing the color distribution across the video. A high value will ensure the color distribution is more consistent across the video, while a low value will allow for more variation in color distribution. Default value: `0.5`
   */
  temporal_adain_factor?: number;
  /**
   * The compression ratio for tone mapping. This is used to compress the dynamic range of the video to improve visual quality. A value of 0.0 means no compression, while a value of 1.0 means maximum compression.
   */
  tone_map_compression_ratio?: number;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type docresInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Task to perform
   */
  task: "deshadowing" | "appearance" | "deblurring" | "binarization";
};
export type DodgeBurnInput = {
  /**
   * Dodge and burn intensity Default value: `0.5`
   */
  dodge_burn_intensity?: number;
  /**
   * Dodge and burn mode Default value: `"dodge"`
   */
  dodge_burn_mode?:
    | "dodge"
    | "burn"
    | "dodge_and_burn"
    | "burn_and_dodge"
    | "color_dodge"
    | "color_burn"
    | "linear_dodge"
    | "linear_burn";
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
};
export type DrctSuperResolutionInput = {
  /**
   * URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * Upscaling factor. Default value: `4`
   */
  upscale_factor?: number;
};
export type DreamActor2Input = {
  /**
   * The URL of the reference image to animate. Supports real people, animation, pets, etc. Format: jpeg, jpg or png. Max size: 4.7 MB. Resolution: between 480x480 and 1920x1080 (larger images will be proportionally reduced).
   */
  image_url: string | Blob | File;
  /**
   * Whether to crop the first second of the output video. The output has a 1-second transition at the beginning; enable this to remove it. Default value: `true`
   */
  trim_first_second?: boolean;
  /**
   * The URL of the driving template video providing motion, facial expressions, and lip movement reference. Max duration: 30 seconds. Format: mp4, mov or webm. Resolution: between 200x200 and 2048x1440. Supports full face and body driving.
   */
  video_url: string | Blob | File;
};
export type DreaminaInput = {
  /**
   * Whether to use an LLM to enhance the prompt
   */
  enhance_prompt?: boolean;
  /**
   * The size of the generated image. Width and height must be between 512 and 2048.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type dreamoInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of first reference image to use for generation.
   */
  first_image_url?: string | Blob | File;
  /**
   * Task for first reference image (ip/id/style). Default value: `"ip"`
   */
  first_reference_task?: "ip" | "id" | "style";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `12`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Resolution for reference images. Default value: `512`
   */
  ref_resolution?: number;
  /**
   * URL of second reference image to use for generation.
   */
  second_image_url?: string | Blob | File;
  /**
   * Task for second reference image (ip/id/style). Default value: `"ip"`
   */
  second_reference_task?: "ip" | "id" | "style";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The weight of the CFG loss. Default value: `1`
   */
  true_cfg?: number;
};
export type Dreamomni2EditInput = {
  /**
   * List of URLs of input images for editing.
   */
  image_urls: Array<string>;
  /**
   * The prompt to edit the image.
   */
  prompt: string;
};
export type DreamshaperImageToImageInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The Dreamshaper model to use.
   */
  model_name?:
    | "Lykon/dreamshaper-xl-1-0"
    | "Lykon/dreamshaper-xl-v2-turbo"
    | "Lykon/dreamshaper-8";
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type DreamshaperInpaintingInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The Dreamshaper model to use.
   */
  model_name?:
    | "Lykon/dreamshaper-xl-1-0"
    | "Lykon/dreamshaper-xl-v2-turbo"
    | "Lykon/dreamshaper-8";
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type dreamshaperInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The Dreamshaper model to use.
   */
  model_name?:
    | "Lykon/dreamshaper-xl-1-0"
    | "Lykon/dreamshaper-xl-v2-turbo"
    | "Lykon/dreamshaper-8";
  /**
   * The negative prompt to use. Use it to address details that you don't want in the image. Default value: `"(worst quality, low quality, normal quality, lowres, low details, oversaturated, undersaturated, overexposed, underexposed, grayscale, bw, bad photo, bad photography, bad art:1.4), (watermark, signature, text font, username, error, logo, words, letters, digits, autograph, trademark, name:1.2), (blur, blurry, grainy), morbid, ugly, asymmetrical, mutated malformed, mutilated, poorly lit, bad shadow, draft, cropped, out of frame, cut off, censored, jpeg artifacts, out of focus, glitch, duplicate, (airbrushed, cartoon, anime, semi-realistic, cgi, render, blender, digital art, manga, amateur:1.3), (3D ,3D Game, 3D Game Scene, 3D Character:1.1), (bad hands, bad anatomy, bad body, bad face, bad teeth, bad arms, bad legs, deformities:1.3)"`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type DubbingAudioOutput = {
  /**
   * The dubbed audio file.
   */
  audio: File;
  /**
   * The target language of the dubbed content
   */
  target_lang: string;
};
export type dubbingInput = {
  /**
   * Whether to lip sync the audio to the video Default value: `true`
   */
  do_lipsync?: boolean;
  /**
   * Target language to dub the video to Default value: `"hindi"`
   */
  target_language?: "hindi" | "turkish" | "english";
  /**
   * Input video URL to be dubbed.
   */
  video_url: string | Blob | File;
};
export type DubbingVideoOutput = {
  /**
   * The target language of the dubbed content
   */
  target_lang: string;
  /**
   * The dubbed video file. Will be populated if video_url was provided in the request.
   */
  video: File;
};
export type dwposeInput = {
  /**
   * Mode of drawing the pose on the image. Options are: 'full-pose', 'body-pose', 'face-pose', 'hand-pose', 'face-hand-mask', 'face-mask', 'hand-mask'. Default value: `"body-pose"`
   */
  draw_mode?:
    | "full-pose"
    | "body-pose"
    | "face-pose"
    | "hand-pose"
    | "face-hand-mask"
    | "face-mask"
    | "hand-mask";
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
};
export type DwposeVideoInput = {
  /**
   * Mode of drawing the pose on the video. Options are: 'full-pose', 'body-pose', 'face-pose', 'hand-pose', 'face-hand-mask', 'face-mask', 'hand-mask'. Default value: `"body-pose"`
   */
  draw_mode?:
    | "full-pose"
    | "body-pose"
    | "face-pose"
    | "hand-pose"
    | "face-hand-mask"
    | "face-mask"
    | "hand-mask";
  /**
   * URL of video to be used for pose estimation
   */
  video_url: string | Blob | File;
};
export type DynamicMask = {
  /**
   * URL of the image for Dynamic Brush Application Area (Mask image created by users using the motion brush)
   */
  mask_url: string | Blob | File;
  /**
   * List of trajectories
   */
  trajectories?: Array<Trajectory>;
};
export type EasyControlWeight = {
  /**
   * URL to safetensor weights of control method to be applied. Can also be one of `canny`, `depth`, `hedsketch`, `inpainting`, `pose`, `seg`, `subject`, `ghibli`
   */
  control_method_url: string | Blob | File;
  /**
   * Control type of the image. Must be one of `spatial` or `subject`.
   */
  image_control_type: "subject" | "spatial";
  /**
   * URL of an image to use as a control
   */
  image_url: string | Blob | File;
  /**
   * Scale for the control method. Default value: `1`
   */
  scale?: number;
};
export type EchomimicV3Input = {
  /**
   * The audio guidance scale to use for the video generation. Default value: `2.5`
   */
  audio_guidance_scale?: number;
  /**
   * The URL of the audio to use as a reference for the video generation.
   */
  audio_url: string | Blob | File;
  /**
   * The guidance scale to use for the video generation. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to use as a reference for the video generation.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use for the video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate at once. Default value: `121`
   */
  num_frames_per_generation?: number;
  /**
   * The prompt to use for the video generation.
   */
  prompt: string;
  /**
   * The seed to use for the video generation.
   */
  seed?: number;
};
export type EditImageInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the image generation. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If None, uses the input image dimensions.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images to edit.
   */
  image_urls: Array<string>;
  /**
   * The negative prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image with.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type EditImageLoraInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the image generation. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If None, uses the input image dimensions.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images to edit.
   */
  image_urls: Array<string>;
  /**
   * The LoRAs to use for the image generation. You can use up to 3 LoRAs and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image with.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type edittoInput = {
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster. Default value: `regular`
   */
  acceleration?: "none" | "low" | "regular";
  /**
   * Aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * If true, the model will automatically temporally downsample the video to an appropriate frame length for the model, then will interpolate it back to the original frame length.
   */
  enable_auto_downsample?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between the original frames. A value of 0 means no interpolation.
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video. Default value: `"auto"`
   */
  resolution?: "auto" | "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * If true, also return a ZIP file containing all generated frames.
   */
  return_frames_zip?: boolean;
  /**
   * Sampler to use for video generation. Default value: `"unipc"`
   */
  sampler?: "unipc" | "dpm++" | "euler";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Temporal downsample factor for the video. This is an integer value that determines how many frames to skip in the video. A value of 0 means no downsampling. For each downsample factor, one upsample factor will automatically be applied.
   */
  temporal_downsample_factor?: number;
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL to the source video file. Required for inpainting.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type edittoOutput = {
  /**
   * ZIP archive of all video frames if requested.
   */
  frames_zip?: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated image to video file.
   */
  video: VideoFile;
};
export type EffectInput = {
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * The effect to apply to the video
   */
  effect:
    | "Kiss Me AI"
    | "Kiss"
    | "Muscle Surge"
    | "Warmth of Jesus"
    | "Anything, Robot"
    | "The Tiger Touch"
    | "Hug"
    | "Holy Wings"
    | "Microwave"
    | "Zombie Mode"
    | "Squid Game"
    | "Baby Face"
    | "Black Myth: Wukong"
    | "Long Hair Magic"
    | "Leggy Run"
    | "Fin-tastic Mermaid"
    | "Punch Face"
    | "Creepy Devil Smile"
    | "Thunder God"
    | "Eye Zoom Challenge"
    | "Who's Arrested?"
    | "Baby Arrived"
    | "Werewolf Rage"
    | "Bald Swipe"
    | "BOOM DROP"
    | "Huge Cutie"
    | "Liquid Metal"
    | "Sharksnap!"
    | "Dust Me Away"
    | "3D Figurine Factor"
    | "Bikini Up"
    | "My Girlfriends"
    | "My Boyfriends"
    | "Subject 3 Fever"
    | "Earth Zoom"
    | "Pole Dance"
    | "Vroom Dance"
    | "GhostFace Terror"
    | "Dragon Evoker"
    | "Skeletal Bae"
    | "Summoning succubus"
    | "Halloween Voodoo Doll"
    | "3D Naked-Eye AD"
    | "Package Explosion"
    | "Dishes Served"
    | "Ocean ad"
    | "Supermarket AD"
    | "Tree doll"
    | "Come Feel My Abs"
    | "The Bicep Flex"
    | "London Elite Vibe"
    | "Flora Nymph Gown"
    | "Christmas Costume"
    | "It's Snowy"
    | "Reindeer Cruiser"
    | "Snow Globe Maker"
    | "Pet Christmas Outfit"
    | "Adopt a Polar Pal"
    | "Cat Christmas Box"
    | "Starlight Gift Box"
    | "Xmas Poster"
    | "Pet Christmas Tree"
    | "City Santa Hat"
    | "Stocking Sweetie"
    | "Christmas Night"
    | "Xmas Front Page Karma"
    | "Grinch's Xmas Hijack"
    | "Giant Product"
    | "Truck Fashion Shoot"
    | "Beach AD"
    | "Shoal Surround"
    | "Mechanical Assembly"
    | "Lighting AD"
    | "Billboard AD"
    | "Product close-up"
    | "Parachute Delivery"
    | "Dreamlike Cloud"
    | "Macaron Machine"
    | "Poster AD"
    | "Truck AD"
    | "Graffiti AD"
    | "3D Figurine Factory"
    | "The Exclusive First Class"
    | "Art Zoom Challenge"
    | "I Quit"
    | "Hitchcock Dolly Zoom"
    | "Smell the Lens"
    | "I believe I can fly"
    | "Strikout Dance"
    | "Pixel World"
    | "Mint in Box"
    | "Hands up, Hand"
    | "Flora Nymph Go"
    | "Somber Embrace"
    | "Beam me up"
    | "Suit Swagger";
  /**
   * Optional URL of the image to use as the first frame. If not provided, generates from text
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
};
export type ElementInput = {
  /**
   * The frontal image of the element (main view). Optional.
   */
  frontal_image_url?: string | Blob | File;
  /**
   * Additional reference images from different angles. 0-3 images supported. Optional.
   */
  reference_image_urls?: Array<string>;
};
export type ElevenlabsAudioIsolationInput = {
  /**
   * URL of the audio file to isolate voice from
   */
  audio_url?: string | Blob | File;
  /**
   * Video file to use for audio isolation. Either `audio_url` or `video_url` must be provided.
   */
  video_url?: string | Blob | File;
};
export type ElevenlabsDubbingInput = {
  /**
   * URL of the audio file to dub. Either audio_url or video_url must be provided.
   */
  audio_url?: string | Blob | File;
  /**
   * Whether to use the highest resolution for dubbing. Default value: `true`
   */
  highest_resolution?: boolean;
  /**
   * Number of speakers in the audio. If not provided, will be auto-detected.
   */
  num_speakers?: number;
  /**
   * Source language code. If not provided, will be auto-detected.
   */
  source_lang?: string;
  /**
   * Target language code for dubbing (ISO 639-1)
   */
  target_lang: string;
  /**
   * URL of the video file to dub. Either audio_url or video_url must be provided. If both are provided, video_url takes priority.
   */
  video_url?: string | Blob | File;
};
export type ElevenlabsMusicInput = {
  /**
   * The composition plan for the music
   */
  composition_plan?: MusicCompositionPlan;
  /**
   * If true, guarantees that the generated song will be instrumental. If false, the song may or may not be instrumental depending on the prompt. Can only be used with prompt.
   */
  force_instrumental?: boolean;
  /**
   * The length of the song to generate in milliseconds. Used only in conjunction with prompt. Must be between 3000ms and 600000ms. Optional - if not provided, the model will choose a length based on the prompt.
   */
  music_length_ms?: number;
  /**
   * Output format of the generated audio. Formatted as codec_sample_rate_bitrate. So an mp3 with 22.05kHz sample rate at 32kbs is represented as mp3_22050_32. MP3 with 192kbps bitrate requires you to be subscribed to Creator tier or above. PCM with 44.1kHz sample rate requires you to be subscribed to Pro tier or above. Note that the μ-law format (sometimes written mu-law, often approximated as u-law) is commonly used for Twilio audio inputs. Default value: `"mp3_44100_128"`
   */
  output_format?:
    | "mp3_22050_32"
    | "mp3_44100_32"
    | "mp3_44100_64"
    | "mp3_44100_96"
    | "mp3_44100_128"
    | "mp3_44100_192"
    | "pcm_8000"
    | "pcm_16000"
    | "pcm_22050"
    | "pcm_24000"
    | "pcm_44100"
    | "pcm_48000"
    | "ulaw_8000"
    | "alaw_8000"
    | "opus_48000_32"
    | "opus_48000_64"
    | "opus_48000_96"
    | "opus_48000_128"
    | "opus_48000_192";
  /**
   * The text prompt describing the music to generate
   */
  prompt?: string;
  /**
   * Controls how strictly section durations in the composition_plan are enforced. It will only have an effect if it is used with composition_plan. When set to true, the model will precisely respect each section's duration_ms from the plan. When set to false, the model may adjust individual section durations which will generally lead to better generation quality and improved latency, while always preserving the total song duration from the plan. Default value: `true`
   */
  respect_sections_durations?: boolean;
};
export type ElevenlabsSoundEffectsV2Input = {
  /**
   * Duration in seconds (0.5-22). If None, optimal duration will be determined from prompt.
   */
  duration_seconds?: number;
  /**
   * Whether to create a sound effect that loops smoothly.
   */
  loop?: boolean;
  /**
   * Output format of the generated audio. Formatted as codec_sample_rate_bitrate. Default value: `"mp3_44100_128"`
   */
  output_format?:
    | "mp3_22050_32"
    | "mp3_44100_32"
    | "mp3_44100_64"
    | "mp3_44100_96"
    | "mp3_44100_128"
    | "mp3_44100_192"
    | "pcm_8000"
    | "pcm_16000"
    | "pcm_22050"
    | "pcm_24000"
    | "pcm_44100"
    | "pcm_48000"
    | "ulaw_8000"
    | "alaw_8000"
    | "opus_48000_32"
    | "opus_48000_64"
    | "opus_48000_96"
    | "opus_48000_128"
    | "opus_48000_192";
  /**
   * How closely to follow the prompt (0-1). Higher values mean less variation. Default value: `0.3`
   */
  prompt_influence?: number;
  /**
   * The text describing the sound effect to generate
   */
  text: string;
};
export type ElevenlabsSpeechToTextInput = {
  /**
   * URL of the audio file to transcribe
   */
  audio_url: string | Blob | File;
  /**
   * Whether to annotate who is speaking Default value: `true`
   */
  diarize?: boolean;
  /**
   * Language code of the audio
   */
  language_code?: string;
  /**
   * Tag audio events like laughter, applause, etc. Default value: `true`
   */
  tag_audio_events?: boolean;
};
export type ElevenlabsSpeechToTextScribeV2Input = {
  /**
   * URL of the audio file to transcribe
   */
  audio_url: string | Blob | File;
  /**
   * Whether to annotate who is speaking Default value: `true`
   */
  diarize?: boolean;
  /**
   * Words or sentences to bias the model towards transcribing. Up to 100 keyterms, max 50 characters each. Adds 30% premium over base transcription price.
   */
  keyterms?: Array<string>;
  /**
   * Language code of the audio
   */
  language_code?: string;
  /**
   * Tag audio events like laughter, applause, etc. Default value: `true`
   */
  tag_audio_events?: boolean;
};
export type ElevenlabsTextToDialogueElevenV3Input = {
  /**
   * A list of dialogue inputs, each containing text and a voice ID which will be converted into speech.
   */
  inputs: Array<DialogueBlock>;
  /**
   * Language code (ISO 639-1) used to enforce a language for the model. An error will be returned if language code is not supported by the model.
   */
  language_code?: string;
  /**
   * A list of pronunciation dictionary locators (id, version_id) to be applied to the text. They will be applied in order. You may have up to 3 locators per request
   */
  pronunciation_dictionary_locators?: Array<PronunciationDictionaryLocator>;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * Determines how stable the voice is and the randomness between each generation. Lower values introduce broader emotional range for the voice. Higher values can result in a monotonous voice with limited emotion. Must be one of 0.0, 0.5, 1.0, else it will be rounded to the nearest value.
   */
  stability?: number;
  /**
   * This setting boosts the similarity to the original speaker. Using this setting requires a slightly higher computational load, which in turn increases latency.
   */
  use_speaker_boost?: boolean;
};
export type ElevenlabsTtsElevenV3Input = {
  /**
   * This parameter controls text normalization with three modes: 'auto', 'on', and 'off'. When set to 'auto', the system will automatically decide whether to apply text normalization (e.g., spelling out numbers). With 'on', text normalization will always be applied, while with 'off', it will be skipped. Default value: `"auto"`
   */
  apply_text_normalization?: "auto" | "on" | "off";
  /**
   * Language code (ISO 639-1) used to enforce a language for the model.
   */
  language_code?: string;
  /**
   * Voice stability (0-1) Default value: `0.5`
   */
  stability?: number;
  /**
   * The text to convert to speech
   */
  text: string;
  /**
   * Whether to return timestamps for each word in the generated speech
   */
  timestamps?: boolean;
  /**
   * The voice to use for speech generation Default value: `"Rachel"`
   */
  voice?: string;
};
export type ElevenlabsTtsElevenV3Output = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Timestamps for each word in the generated speech. Only returned if `timestamps` is set to True in the request.
   */
  timestamps?: Array<void>;
};
export type ElevenlabsTtsTurboV25Input = {
  /**
   * This parameter controls text normalization with three modes: 'auto', 'on', and 'off'. When set to 'auto', the system will automatically decide whether to apply text normalization (e.g., spelling out numbers). With 'on', text normalization will always be applied, while with 'off', it will be skipped. Default value: `"auto"`
   */
  apply_text_normalization?: "auto" | "on" | "off";
  /**
   * Language code (ISO 639-1) used to enforce a language for the model. An error will be returned if language code is not supported by the model.
   */
  language_code?: string;
  /**
   * The text that comes after the text of the current request. Can be used to improve the speech's continuity when concatenating together multiple generations or to influence the speech's continuity in the current generation.
   */
  next_text?: string;
  /**
   * The text that came before the text of the current request. Can be used to improve the speech's continuity when concatenating together multiple generations or to influence the speech's continuity in the current generation.
   */
  previous_text?: string;
  /**
   * Similarity boost (0-1) Default value: `0.75`
   */
  similarity_boost?: number;
  /**
   * Speech speed (0.7-1.2). Values below 1.0 slow down the speech, above 1.0 speed it up. Extreme values may affect quality. Default value: `1`
   */
  speed?: number;
  /**
   * Voice stability (0-1) Default value: `0.5`
   */
  stability?: number;
  /**
   * Style exaggeration (0-1)
   */
  style?: number;
  /**
   * The text to convert to speech
   */
  text: string;
  /**
   * Whether to return timestamps for each word in the generated speech
   */
  timestamps?: boolean;
  /**
   * The voice to use for speech generation Default value: `"Rachel"`
   */
  voice?: string;
};
export type ElevenlabsVoiceChangerInput = {
  /**
   * The input audio file
   */
  audio_url: string | Blob | File;
  /**
   * Output format of the generated audio. Formatted as codec_sample_rate_bitrate. Default value: `"mp3_44100_128"`
   */
  output_format?:
    | "mp3_22050_32"
    | "mp3_44100_32"
    | "mp3_44100_64"
    | "mp3_44100_96"
    | "mp3_44100_128"
    | "mp3_44100_192"
    | "pcm_8000"
    | "pcm_16000"
    | "pcm_22050"
    | "pcm_24000"
    | "pcm_44100"
    | "pcm_48000"
    | "ulaw_8000"
    | "alaw_8000"
    | "opus_48000_32"
    | "opus_48000_64"
    | "opus_48000_96"
    | "opus_48000_128"
    | "opus_48000_192";
  /**
   * If set, will remove the background noise from your audio input using our audio isolation model.
   */
  remove_background_noise?: boolean;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * The voice to use for speech generation Default value: `"Rachel"`
   */
  voice?: string;
};
export type Embedding = {
  /**
   * URL or the path to the embedding weights.
   */
  path: string;
  /**
   * The list of tokens to use for the embedding.
   */
  tokens?: Array<string>;
};
export type EmbedItem = {
  /**
   *
   */
  coordinates: Coordinates;
  /**
   * URL of the image.
   */
  image_source?: string;
};
export type EmbedProductInput = {
  /**
   * URL of the image.
   */
  image_source?: string;
  /**
   * List of products to embed in the image.
   */
  products?: Array<EmbedItem>;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type EmotionalStrengths = {
  /**
   * Strength of fear emotion
   */
  afraid?: number;
  /**
   * Strength of anger emotion
   */
  angry?: number;
  /**
   * Strength of calm emotion
   */
  calm?: number;
  /**
   * Strength of disgust emotion
   */
  disgusted?: number;
  /**
   * Strength of happiness emotion
   */
  happy?: number;
  /**
   * Strength of melancholic emotion
   */
  melancholic?: number;
  /**
   * Strength of sadness emotion
   */
  sad?: number;
  /**
   * Strength of surprise emotion
   */
  surprised?: number;
};
export type Emu35ImageEditInput = {
  /**
   * The aspect ratio of the output image. Default value: `"auto"`
   */
  aspect_ratio?:
    | "auto"
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The format of the output image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The resolution of the output image. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The seed for the inference.
   */
  seed?: number;
  /**
   * Whether to return the image in sync mode.
   */
  sync_mode?: boolean;
};
export type Emu35ImageInput = {
  /**
   * The aspect ratio of the output image. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the output image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to create the image.
   */
  prompt: string;
  /**
   * The resolution of the output image. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The seed for the inference.
   */
  seed?: number;
  /**
   * Whether to return the image in sync mode.
   */
  sync_mode?: boolean;
};
export type Emu35Output = {
  /**
   * The edited image.
   */
  images: Array<ImageFile>;
  /**
   * The seed for the inference.
   */
  seed: number;
};
export type EQBand = {
  /**
   * Center frequency in Hz (20-20000)
   */
  frequency: number;
  /**
   * Gain in dB (-30 to 30, negative = cut, positive = boost)
   */
  gain: number;
  /**
   * Bandwidth in Hz Default value: `100`
   */
  width?: number;
};
export type Era3dInput = {
  /**
   * Background removal Default value: `true`
   */
  background_removal?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  cfg?: number;
  /**
   * Size of the image to crop to Default value: `400`
   */
  crop_size?: number;
  /**
   * URL of the image to remove background from
   */
  image_url: string | Blob | File;
  /**
   * Seed for random number generation Default value: `-1`
   */
  seed?: number;
  /**
   * Number of steps to run the model for Default value: `40`
   */
  steps?: number;
};
export type Era3dOutput = {
  /**
   * Images with background removed
   */
  images: Array<Image>;
  /**
   * Normal images with background removed
   */
  normal_images: Array<Image>;
  /**
   * Seed used for random number generation
   */
  seed: number;
};
export type EraseByTextInput = {
  /**
   * The source image.
   */
  image_url: string | Blob | File;
  /**
   * The name of the object to remove.
   */
  object_name: string;
};
export type EraserInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * You can use this parameter to specify the type of the input mask from the list. 'manual' opttion should be used in cases in which the mask had been generated by a user (e.g. with a brush tool), and 'automatic' mask type should be used when mask had been generated by an algorithm like 'SAM'. Default value: `"manual"`
   */
  mask_type?: "manual" | "automatic";
  /**
   * The URL of the binary mask image that represents the area that will be cleaned.
   */
  mask_url: string | Blob | File;
  /**
   * If set to true, attempts to preserve the alpha channel of the input image.
   */
  preserve_alpha?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type esrganInput = {
  /**
   * Upscaling a face
   */
  face?: boolean;
  /**
   * Url to input image
   */
  image_url: string | Blob | File;
  /**
   * Model to use for upscaling Default value: `"RealESRGAN_x4plus"`
   */
  model?:
    | "RealESRGAN_x4plus"
    | "RealESRGAN_x2plus"
    | "RealESRGAN_x4plus_anime_6B"
    | "RealESRGAN_x4_v3"
    | "RealESRGAN_x4_wdn_v3"
    | "RealESRGAN_x4_anime_v3";
  /**
   * Output image format (png or jpeg) Default value: `"png"`
   */
  output_format?: "png" | "jpeg";
  /**
   * Rescaling factor Default value: `2`
   */
  scale?: number;
  /**
   * Tile size. Default is 0, that is no tile. When encountering the out-of-GPU-memory issue, please specify it, e.g., 400 or 200
   */
  tile?: number;
};
export type EvfSamInput = {
  /**
   * Apply Gaussian blur to the mask. Value determines kernel size (must be odd number)
   */
  blur_mask?: number;
  /**
   * Expand/dilate the mask by specified pixels
   */
  expand_mask?: number;
  /**
   * Fill holes in the mask using morphological operations
   */
  fill_holes?: boolean;
  /**
   * URL of the input image
   */
  image_url: string | Blob | File;
  /**
   * Output only the binary mask instead of masked image Default value: `true`
   */
  mask_only?: boolean;
  /**
   * Areas to exclude from segmentation (will be subtracted from prompt results)
   */
  negative_prompt?: string;
  /**
   * The prompt to generate segmentation from.
   */
  prompt: string;
  /**
   * Invert the mask (background becomes foreground and vice versa)
   */
  revert_mask?: boolean;
  /**
   * Enable semantic level segmentation for body parts, background or multi objects
   */
  semantic_type?: boolean;
  /**
   * Use GroundingDINO instead of SAM for segmentation
   */
  use_grounding_dino?: boolean;
};
export type EvfSamOutput = {
  /**
   * The segmented output image
   */
  image: File;
};
export type ExtendInput = {
  /**
   * The URL of the audio file to alter. Must be a valid publicly accessible URL.
   */
  audio_url: string | Blob | File;
  /**
   * Greater means more natural vocals. Lower means sharper instrumentals. We recommend 0.7. Default value: `0.7`
   */
  balance_strength?: number;
  /**
   * Duration in seconds to crop from the selected side before extending from that side.
   */
  crop_duration?: number;
  /**
   * Duration in seconds to extend the song. If not provided, will attempt to automatically determine.
   */
  extend_duration?: number;
  /**
   * The lyrics sung in the generated song. An empty string will generate an instrumental track.
   */
  lyrics_prompt?: string;
  /**
   * Generating 2 songs costs 1.5x the price of generating 1 song. Also, note that using the same seed may not result in identical songs if the number of songs generated is changed. Default value: `1`
   */
  num_songs?: number;
  /**
   * The bit rate to use for mp3 and m4a formats. Not available for other formats.
   */
  output_bit_rate?: "128" | "192" | "256" | "320";
  /**
   *  Default value: `"wav"`
   */
  output_format?: "flac" | "mp3" | "wav" | "ogg" | "m4a";
  /**
   * A description of the track you want to generate. This prompt will be used to automatically generate the tags and lyrics unless you manually set them. For example, if you set prompt and tags, then the prompt will be used to generate only the lyrics.
   */
  prompt?: string;
  /**
   * Controls how strongly your prompt influences the output. Greater values adhere more to the prompt but sound less natural. (This is CFG.) Default value: `1.8`
   */
  prompt_strength?: number;
  /**
   * The seed to use for generation. Will pick a random seed if not provided. Repeating a request with identical parameters (must use lyrics and tags, not prompt) and the same seed will generate the same song.
   */
  seed?: number;
  /**
   * Add more to the beginning (left) or end (right) of the song
   */
  side: "left" | "right";
  /**
   * Tags/styles of the music to generate. You can view a list of all available tags at https://sonauto.ai/tag-explorer.
   */
  tags?: Array<string>;
};
export type ExtendOutput = {
  /**
   * The generated audio files.
   */
  audio: Array<File>;
  /**
   * The duration in seconds that the song was extended by.
   */
  extend_duration: number;
  /**
   * The lyrics used for generation.
   */
  lyrics?: string;
  /**
   * The seed used for generation. This can be used to generate an identical song by passing the same parameters with this seed in a future request.
   */
  seed: number;
  /**
   * The style tags used for generation.
   */
  tags?: Array<string>;
};
export type ExtendVideoConditioningInput = {
  /**
   * Whether to limit the number of frames used from the video. If True, the `max_num_frames` parameter will be used to limit the number of frames.
   */
  limit_num_frames?: boolean;
  /**
   * Maximum number of frames to use from the video. If None, all frames will be used. Default value: `1441`
   */
  max_num_frames?: number;
  /**
   * Whether to resample the video to a specific FPS. If True, the `target_fps` parameter will be used to resample the video.
   */
  resample_fps?: boolean;
  /**
   * Whether to reverse the video. This is useful for tasks where the video conditioning should be applied in reverse order.
   */
  reverse_video?: boolean;
  /**
   * Frame number of the video from which the conditioning starts. Must be a multiple of 8.
   */
  start_frame_num?: number;
  /**
   * Strength of the conditioning. 0.0 means no conditioning, 1.0 means full conditioning. Default value: `1`
   */
  strength?: number;
  /**
   * Target FPS to resample the video to. Only relevant if `resample_fps` is True. Default value: `24`
   */
  target_fps?: number;
  /**
   * URL of video to use as conditioning
   */
  video_url: string | Blob | File;
};
export type ExtendVideoInput = {
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Video to be extended.
   */
  video: VideoConditioningInput;
};
export type ExtendVideoOutput = {
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video file.
   */
  video: File;
};
export type ExtractAudioInput = {
  /**
   * Audio bitrate Default value: `"192k"`
   */
  audio_bitrate?: "128k" | "192k" | "256k" | "320k";
  /**
   * Output audio format Default value: `"mp3"`
   */
  audio_format?: "mp3" | "wav" | "aac" | "flac";
  /**
   * URL of the video file to extract audio from
   */
  video_url: string | Blob | File;
};
export type ExtractNthFrameInput = {
  /**
   * Extract every Nth frame (e.g., 3 = every 3rd frame, 12 = every 12th frame) Default value: `12`
   */
  frame_interval?: number;
  /**
   * Maximum number of frames to extract Default value: `100`
   */
  max_frames?: number;
  /**
   * Output format for extracted frames Default value: `"png"`
   */
  output_format?: "png" | "jpg" | "jpeg" | "webp";
  /**
   * Quality for jpg/webp output (1-100) Default value: `95`
   */
  quality?: number;
  /**
   * URL of the video file to extract frames from
   */
  video_url: string | Blob | File;
};
export type ExtractNthFrameOutput = {
  /**
   * Total number of frames extracted
   */
  frame_count: number;
  /**
   * Array of extracted frame images
   */
  images: Array<Image>;
};
export type F5TtsInput = {
  /**
   * The text to be converted to speech. Maximum 5000 characters.
   */
  gen_text: string;
  /**
   * The name of the model to be used for TTS.
   */
  model_type: "F5-TTS" | "E2-TTS";
  /**
   * The URL of the reference audio file.
   */
  ref_audio_url: string | Blob | File;
  /**
   * The reference text to be used for TTS. If not provided, an ASR (Automatic Speech Recognition) model will be used to generate the reference text. Default value: `""`
   */
  ref_text?: string;
  /**
   * Whether to remove the silence from the audio file. Default value: `true`
   */
  remove_silence?: boolean;
};
export type F5TtsOutput = {
  /**
   * The audio file containing the generated speech.
   */
  audio_url: AudioFile;
};
export type Fabric10Input = {
  /**
   *
   */
  audio_url: string | Blob | File;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * Resolution
   */
  resolution: "720p" | "480p";
};
export type Fabric10TextInput = {
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * Resolution
   */
  resolution: "720p" | "480p";
  /**
   *
   */
  text: string;
  /**
   * Optional additional voice description. The primary voice description is auto-generated from the image. You can use simple descriptors like 'British accent' or 'Confident' or provide a detailed description like 'Confident male voice, mid-20s, with notes of...'
   */
  voice_description?: string;
};
export type FaceChoice = {
  /**
   * Publicly accessible URL to the audio file. Supported formats: .mp3, .wav, .m4a (max 5MB). Duration must be between 2–60 seconds.
   */
  audio_url: string | Blob | File;
  /**
   * ID of the face in the video. Returned by the `identify_face` API.
   */
  face_id: string;
  /**
   * Volume multiplier for the video's original audio track. Range: [0, 2]. Has no effect if the source video contains no audio. Default value: `1`
   */
  original_audio_volume?: number;
  /**
   * End time (ms) for cropping the source audio. Must be greater than `sound_start_time` and within the original audio duration. The cropped segment must be at least 2 seconds long.
   */
  sound_end_time: number;
  /**
   * Time (ms) at which the cropped audio will be inserted into the video. Must meet both of the following conditions:
   * 1. `sound_insert_time` must be within the duration of the video (it cannot be greater than the total video length).
   * 2. The cropped audio segment must fully fit within the video when inserted — meaning `sound_insert_time + cropped_sound_length` must not exceed the video's total duration.
   *
   * In other words: the insert point must be inside the video, and the inserted audio must not extend past the end of the video.
   */
  sound_insert_time: number;
  /**
   * Start time (ms) for cropping the source audio. Must be between 0 and the audio duration. The cropped audio must remain at least 2 seconds long.
   */
  sound_start_time: number;
  /**
   * Volume multiplier for the inserted audio. Range: [0, 2], where 1 = original volume. Default value: `1`
   */
  sound_volume?: number;
};
export type FaceData = {
  /**
   * This face can be used as the ending time of lip-sync (milliseconds). Note: This value has a millisecond level error and will be longer than the actual ending time.
   */
  end_time: number;
  /**
   * The face id of video. When the same person's face is separated by more than 1 second in the video, it will be considered as different IDs.
   */
  face_id: string;
  /**
   * A schematic diagram of a face captured from a video (URL).
   */
  face_image: string;
  /**
   * This face can be used as the starting time of lip-sync (milliseconds).
   */
  start_time: number;
};
export type FaceToStickerInput = {
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of the video.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the instant ID. Default value: `0.7`
   */
  instant_id_strength?: number;
  /**
   * The amount of noise to add to the IP adapter. Default value: `0.5`
   */
  ip_adapter_noise?: number;
  /**
   * The weight of the IP adapter. Default value: `0.2`
   */
  ip_adapter_weight?: number;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps
   * to generate your final result which can increase the amount of detail in your image. Default value: `20`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Whether to upscale the image 2x.
   */
  upscale?: boolean;
  /**
   * The number of steps to use for upscaling. Only used if `upscale` is `true`. Default value: `10`
   */
  upscale_steps?: number;
};
export type FaceToStickerOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   * The key is the image type and the value is a boolean.
   */
  has_nsfw_concepts: unknown;
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   * Seed used during the inference.
   */
  seed: number;
  /**
   * The generated face sticker image.
   */
  sticker_image: Image;
  /**
   * The generated face sticker image with the background removed.
   */
  sticker_image_background_removed: Image;
};
export type FastFooocusSdxlInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, a smaller model will try to refine the output after it was processed. Default value: `true`
   */
  enable_refiner?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
};
export type FastImageToVideoHailuo02Input = {
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type FastLightningSdxlInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `"4"`
   */
  num_inference_steps?: "1" | "2" | "4" | "8";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FastSdxlControlnetCannyOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type FastSdxlInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FastSvdLcmInput = {
  /**
   * The conditoning augmentation determines the amount of noise that will be
   * added to the conditioning frame. The higher the number, the more noise
   * there will be, and the less the video will look like the initial image.
   * Increase it for more motion. Default value: `0.02`
   */
  cond_aug?: number;
  /**
   * The FPS of the generated video. The higher the number, the faster the video will
   * play. Total video length is 25 frames. Default value: `10`
   */
  fps?: number;
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The motion bucket id determines the motion of the generated video. The
   * higher the number, the more motion there will be. Default value: `127`
   */
  motion_bucket_id?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The number of steps to run the model for. The higher the number the better
   * the quality and longer it will take to generate. Default value: `4`
   */
  steps?: number;
};
export type FastSVDTextInput = {
  /**
   * The conditoning augmentation determines the amount of noise that will be
   * added to the conditioning frame. The higher the number, the more noise
   * there will be, and the less the video will look like the initial image.
   * Increase it for more motion. Default value: `0.02`
   */
  cond_aug?: number;
  /**
   * The FPS of the generated video. The higher the number, the faster the video will
   * play. Total video length is 25 frames. Default value: `10`
   */
  fps?: number;
  /**
   * The motion bucket id determines the motion of the generated video. The
   * higher the number, the more motion there will be. Default value: `127`
   */
  motion_bucket_id?: number;
  /**
   * The prompt to use as a starting point for the generation.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The number of steps to run the model for. The higher the number the better
   * the quality and longer it will take to generate. Default value: `4`
   */
  steps?: number;
  /**
   * The size of the generated video. Default value: `landscape_16_9`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type FastSvdTextToVideoInput = {
  /**
   * The conditoning augmentation determines the amount of noise that will be
   * added to the conditioning frame. The higher the number, the more noise
   * there will be, and the less the video will look like the initial image.
   * Increase it for more motion. Default value: `0.02`
   */
  cond_aug?: number;
  /**
   * The FPS of the generated video. The higher the number, the faster the video will
   * play. Total video length is 25 frames. Default value: `10`
   */
  fps?: number;
  /**
   * The motion bucket id determines the motion of the generated video. The
   * higher the number, the more motion there will be. Default value: `127`
   */
  motion_bucket_id?: number;
  /**
   * The negative prompt to use as a starting point for the generation. Default value: `"unrealistic, saturated, high contrast, big nose, painting, drawing, sketch, cartoon, anime, manga, render, CG, 3d, watermark, signature, label"`
   */
  negative_prompt?: string;
  /**
   * The prompt to use as a starting point for the generation.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The number of steps to run the model for. The higher the number the better
   * the quality and longer it will take to generate. Default value: `20`
   */
  steps?: number;
  /**
   * The size of the generated video. Default value: `landscape_16_9`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type FiboBbqPreviewGenerateInput = {
  /**
   * Aspect ratio. Options: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9 Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * Guidance scale for text. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Reference image (file or URL).
   */
  image_url?: string | Blob | File;
  /**
   * Negative prompt for image generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Prompt for image generation.
   */
  prompt?: string;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * Number of inference steps. Default value: `50`
   */
  steps_num?: number;
  /**
   * The structured prompt to generate an image from.
   */
  structured_prompt?: StructuredPrompt | string | unknown;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type FiboEditEditInput = {
  /**
   * Guidance scale for text. Default value: `5`
   */
  guidance_scale?: number | number;
  /**
   * Reference image (file or URL).
   */
  image_url?: string | Blob | File;
  /**
   * Instruction for image editing.
   */
  instruction?: string;
  /**
   * Mask image (file or URL). Optional
   */
  mask_url?: string | Blob | File;
  /**
   * Negative prompt for image generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The new vgl describing image after edit.
   */
  new_vgl?: StructuredInstruction;
  /**
   * The original vgl used to generate the image.
   */
  original_vgl?: StructuredInstruction;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * Number of inference steps. Default value: `30`
   */
  steps_num?: number;
  /**
   * The structured prompt to generate an image from.
   */
  structured_instruction?: StructuredInstruction;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type FiboEditEditOutput = {
  /**
   * Generated image.
   */
  image: Image;
  /**
   * Generated images.
   */
  images?: Array<Image>;
  /**
   * Current instruction.
   */
  structured_instruction: unknown;
};
export type FiboEditEditStructuredInstructionInput = {
  /**
   * Reference image (file or URL).
   */
  image_url?: string | Blob | File;
  /**
   * Instruction for image editing.
   */
  instruction?: string;
  /**
   * Reference image mask (file or URL). Optional.
   */
  mask_url?: string | Blob | File;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type FiboFastImageGenerationInput = {
  /**
   * Aspect ratio. Options: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9 Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * Input image URL
   */
  image_url?: string | Blob | File;
  /**
   * Negative prompt for image generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The prompt to generate.
   */
  prompt?: string;
  /**
   * Seed for the random number generator. Default value: `7`
   */
  seed?: number;
  /**
   * Number of inference steps. Default value: `8`
   */
  steps_num?: number;
  /**
   * The structured prompt to generate.
   */
  structured_prompt?: StructuredPrompt;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type FiboFastImageGenerationOutput = {
  /**
   * Generated image.
   */
  image: Image;
  /**
   * Generated images.
   */
  images?: Array<unknown>;
  /**
   * Current prompt.
   */
  structured_prompt: StructuredPrompt;
};
export type FiboGenerateInput = {
  /**
   * Aspect ratio. Options: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9 Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * Reference image (file or URL).
   */
  image_url?: string | Blob | File;
  /**
   * Negative prompt for image generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Prompt for image generation.
   */
  prompt?: string;
  /**
   * Output image resolution Default value: `"1MP"`
   */
  resolution?: "1MP" | "4MP";
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * Number of inference steps. Default value: `50`
   */
  steps_num?: number;
  /**
   * The structured prompt to generate an image from.
   */
  structured_prompt?: StructuredPrompt;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type FiboGenerateOutput = {
  /**
   * Generated image.
   */
  image: Image;
  /**
   * Generated images.
   */
  images?: Array<unknown>;
  /**
   * Current prompt.
   */
  structured_prompt: unknown;
};
export type FiboLiteGenerateStructuredPromptLiteInput = {
  /**
   * Reference image (file or URL).
   */
  image_url?: string | Blob | File;
  /**
   * Prompt for image generation.
   */
  prompt?: string;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * The structured prompt to generate an image from.
   */
  structured_prompt?: BriaFiboVlmStructuredprompt;
};
export type File = {
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * File data
   */
  file_data?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
};
export type filmInput = {
  /**
   * The URL of the second image to use as the ending point for interpolation.
   */
  end_image_url: string | Blob | File;
  /**
   * Frames per second for the output video. Only applicable if output_type is 'video'. Default value: `8`
   */
  fps?: number;
  /**
   * The format of the output images. Only applicable if output_type is 'images'. Default value: `"jpeg"`
   */
  image_format?: "png" | "jpeg";
  /**
   * Whether to include the end image in the output.
   */
  include_end?: boolean;
  /**
   * Whether to include the start image in the output.
   */
  include_start?: boolean;
  /**
   * The number of frames to generate between the input images. Default value: `1`
   */
  num_frames?: number;
  /**
   * The type of output to generate; either individual images or a video. Default value: `"images"`
   */
  output_type?: "images" | "video";
  /**
   * The URL of the first image to use as the starting point for interpolation.
   */
  start_image_url: string | Blob | File;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The quality of the output video. Only applicable if output_type is 'video'. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Only applicable if output_type is 'video'. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type filmOutput = {
  /**
   * The generated frames as individual images.
   */
  images?: Array<ImageFile>;
  /**
   * The generated video file, if output_type is 'video'.
   */
  video?: VideoFile;
};
export type FilmVideoInput = {
  /**
   * Frames per second for the output video. Only applicable if use_calculated_fps is False. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the final frame will be looped back to the first frame to create a seamless loop. If False, the final frame will not loop back.
   */
  loop?: boolean;
  /**
   * The number of frames to generate between the input video frames. Default value: `1`
   */
  num_frames?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * If True, the function will use the calculated FPS of the input video multiplied by the number of frames to determine the output FPS. If False, the passed FPS will be used. Default value: `true`
   */
  use_calculated_fps?: boolean;
  /**
   * If True, the input video will be split into scenes before interpolation. This removes smear frames between scenes, but can result in false positives if the scene detection is not accurate. If False, the entire video will be treated as a single scene.
   */
  use_scene_detection?: boolean;
  /**
   * The quality of the output video. Only applicable if output_type is 'video'. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The URL of the video to use for interpolation.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the output video. Only applicable if output_type is 'video'. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type FinegrainEraserBboxInput = {
  /**
   * List of bounding box coordinates to erase (only one box prompt is supported)
   */
  box_prompts: Array<BoxPromptBase>;
  /**
   * URL of the image to edit
   */
  image_url: string | Blob | File;
  /**
   * Erase quality mode Default value: `"standard"`
   */
  mode?: "express" | "standard" | "premium";
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type FinegrainEraserInput = {
  /**
   * URL of the image to edit
   */
  image_url: string | Blob | File;
  /**
   * Erase quality mode Default value: `"standard"`
   */
  mode?: "express" | "standard" | "premium";
  /**
   * Text description of what to erase
   */
  prompt: string;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type FinegrainEraserMaskInput = {
  /**
   * URL of the image to edit
   */
  image_url: string | Blob | File;
  /**
   * URL of the mask image. Should be a binary mask where white (255) indicates areas to erase
   */
  mask_url: string | Blob | File;
  /**
   * Erase quality mode Default value: `"standard"`
   */
  mode?: "express" | "standard" | "premium";
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type FinegrainEraserOutput = {
  /**
   * The edited image with content erased
   */
  image: File;
  /**
   * Seed used for generation
   */
  used_seed: number;
};
export type FlashvsrUpscaleVideoInput = {
  /**
   * Acceleration mode for VAE decoding. Options: regular (best quality), high (balanced), full (fastest). More accerleation means longer duration videos can be processed too. Default value: `"regular"`
   */
  acceleration?: "regular" | "high" | "full";
  /**
   * Color correction enabled. Default value: `true`
   */
  color_fix?: boolean;
  /**
   * The format of the output video. Default value: `"X264 (.mp4)"`
   */
  output_format?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the output video. Default value: `"high"`
   */
  output_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Default value: `"balanced"`
   */
  output_write_mode?: "fast" | "balanced" | "small";
  /**
   * Copy the original audio tracks into the upscaled video using FFmpeg when possible.
   */
  preserve_audio?: boolean;
  /**
   * Quality level for tile blending (0-100). Controls overlap between tiles to prevent grid artifacts. Higher values provide better quality with more overlap. Recommended: 70-85 for high-res videos, 50-70 for faster processing. Default value: `70`
   */
  quality?: number;
  /**
   * The random seed used for the generation process.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned inline and not stored in history.
   */
  sync_mode?: boolean;
  /**
   * Upscaling factor to be used. Default value: `2`
   */
  upscale_factor?: number;
  /**
   * The input video to be upscaled
   */
  video_url: string | Blob | File;
};
export type FLiteTextureInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Negative Prompt for generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FloatOutput = {
  /**
   * The value of the measurement.
   */
  value: number;
};
export type Florence2LargeOcrWithRegionOutput = {
  /**
   * Processed image
   */
  image?: Image;
  /**
   * Results from the model
   */
  results: OCRBoundingBox;
};
export type Florence2LargeRegionProposalOutput = {
  /**
   * Processed image
   */
  image?: Image;
  /**
   * Results from the model
   */
  results: BoundingBoxes;
};
export type Florence2LargeRegionToSegmentationOutput = {
  /**
   * Processed image
   */
  image?: Image;
  /**
   * Results from the model
   */
  results: PolygonOutput;
};
export type floweditInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * Average step count Default value: `1`
   */
  n_avg?: number;
  /**
   * Control the strength of the edit Default value: `23`
   */
  n_max?: number;
  /**
   * Minimum step for improved style edits
   */
  n_min?: number;
  /**
   * Steps for which the model should run. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Random seed for reproducible generation. If set none, a random seed will be used.
   */
  seed?: number;
  /**
   * Prompt of the image to be used.
   */
  source_prompt: string;
  /**
   * Guidance scale for the source. Default value: `1.5`
   */
  src_guidance_scale?: number;
  /**
   * Guidance scale for target. Default value: `5.5`
   */
  tar_guidance_scale?: number;
  /**
   * Prompt of the image to be made.
   */
  target_prompt: string;
};
export type Flux2EditInput = {
  /**
   * The acceleration level to use for the image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the prompt will be expanded for better results.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images for editing. A maximum of 4 images are allowed, if more are provided, only the first 4 will be used.
   */
  image_urls: Array<string>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2FlashEditInput = {
  /**
   * If set to true, the prompt will be expanded for better results.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images for editing. A maximum of 4 images are allowed, if more are provided, only the first 4 will be used.
   */
  image_urls: Array<string>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2FlashInput = {
  /**
   * If set to true, the prompt will be expanded for better results.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2FlexEditInput = {
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the generation. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If `auto`, the size will be determined by the model. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of URLs of input images for editing
   */
  image_urls: Array<string>;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5";
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2FlexInput = {
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the generation. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5";
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2Input = {
  /**
   * The acceleration level to use for the image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the prompt will be expanded for better results.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2Klein4bEditInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. If not provided, uses the input image size.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images for editing. A maximum of 4 images are allowed.
   */
  image_urls: Array<string>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI. Output is not stored when this is True.
   */
  sync_mode?: boolean;
};
export type Flux2Klein4bEditLoraInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. If not provided, uses the input image size.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images for editing. A maximum of 4 images are allowed.
   */
  image_urls: Array<string>;
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<FalAiFlux2KleinLorainput>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI. Output is not stored when this is True.
   */
  sync_mode?: boolean;
};
export type Flux2LoraEditInput = {
  /**
   * The acceleration level to use for the image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the prompt will be expanded for better results.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URsL of the images for editing. A maximum of 3 images are allowed, if more are provided, only the first 3 will be used.
   */
  image_urls: Array<string>;
  /**
   * List of LoRA weights to apply (maximum 3). Each LoRA can be a URL, HuggingFace repo ID, or local path.
   */
  loras?: Array<LoRAInput>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2LoraGalleryAddBackgroundInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale. Controls how closely the model follows the prompt. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If not provided, the size of the input image will be used.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images. Provide an image with a white or clean background.
   */
  image_urls: Array<string>;
  /**
   * The strength of the add background effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * The prompt describing the background to add. Must start with 'Add Background' followed by your description. Default value: `"Add Background forest"`
   */
  prompt?: string;
  /**
   * Random seed for reproducibility. Same seed with same prompt will produce same result.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and won't be saved in history.
   */
  sync_mode?: boolean;
};
export type Flux2LoraGalleryMultipleAnglesInput = {
  /**
   * Acceleration level for image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * Horizontal rotation angle around the object in degrees. 0°=front view, 90°=right side, 180°=back view, 270°=left side, 360°=front view again.
   */
  horizontal_angle?: number;
  /**
   * The size of the generated image. If not provided, the size of the input image will be used.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to adjust camera angle for.
   */
  image_urls: Array<string>;
  /**
   * The strength of the multiple angles effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image. Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * If True, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
  /**
   * Vertical camera angle in degrees. 0°=eye-level shot, 30°=elevated shot, 60°=high-angle shot (looking down from above).
   */
  vertical_angle?: number;
  /**
   * Camera zoom/distance. 0=wide shot (far away), 5=medium shot (normal), 10=close-up (very close). Default value: `5`
   */
  zoom?: number;
};
export type Flux2LoraInput = {
  /**
   * The acceleration level to use for the image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the prompt will be expanded for better results.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of LoRA weights to apply (maximum 3). Each LoRA can be a URL, HuggingFace repo ID, or local path.
   */
  loras?: Array<LoRAInput>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2MaxEditInput = {
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. If `auto`, the size will be determined by the model. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of URLs of input images for editing
   */
  image_urls: Array<string>;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5";
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2MaxInput = {
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5";
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Flux2Output = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated images
   */
  images: Array<ImageFile>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type Flux2TrainerInput = {
  /**
   * Default caption to use when caption files are missing. If None, missing captions will cause an error.
   */
  default_caption?: string;
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images, although more is better.
   *
   * The zip can also contain a text file for each image. The text file should be named:
   * ROOT.txt
   * For example:
   * photo.txt
   *
   * This text file can be used to specify the edit instructions for the image pair.
   *
   * If no text file is provided, the default_caption will be used.
   *
   * If no default_caption is provided, the training will fail.
   */
  image_data_url: string | Blob | File;
  /**
   * Learning rate applied to trainable parameters. Default value: `0.00005`
   */
  learning_rate?: number;
  /**
   * Dictates the naming scheme for the output weights Default value: `"fal"`
   */
  output_lora_format?: "fal" | "comfy";
  /**
   * Total number of training steps. Default value: `1000`
   */
  steps?: number;
};
export type Flux2TrainerOutput = {
  /**
   * URL to the configuration file for the trained model.
   */
  config_file: File;
  /**
   * URL to the trained diffusers lora weights.
   */
  diffusers_lora_file: File;
};
export type FluxControlLoraCannyImageToImageInput = {
  /**
   * The image to use for control lora. This is used to control the style of the generated image.
   */
  control_lora_image_url: string | Blob | File;
  /**
   * The strength of the control lora. Default value: `1`
   */
  control_lora_strength?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxDevInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxDifferentialDiffusionInput = {
  /**
   * URL of change map.
   */
  change_map_image_url: string | Blob | File;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of image to use as initial image.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength to use for image-to-image. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxGeneralImageToImageInput = {
  /**
   * Base shift for the scheduled timesteps Default value: `0.5`
   */
  base_shift?: number;
  /**
   * The LoRAs to use for the image generation which use a control image. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  control_loras?: Array<ControlLoraWeight>;
  /**
   * The controlnet unions to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnet_unions?: Array<ControlNetUnion>;
  /**
   * The controlnets to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnets?: Array<ControlNet>;
  /**
   * EasyControl Inputs to use for image generation.
   */
  easycontrols?: Array<EasyControlWeight>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Use an image input to influence the generation. Can be used to fill images in masked areas.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * IP-Adapter to use for image generation.
   */
  ip_adapters?: Array<IPAdapter>;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * Max shift for the scheduled timesteps Default value: `1.15`
   */
  max_shift?: number;
  /**
   * The alpha value for NAG. This value is used as a final weighting
   * factor for steering the normalized guidance (positive and negative prompts)
   * in the direction of the positive prompt. Higher values will result in less
   * steering on the normalized guidance where lower values will result in
   * considering the positive prompt guidance more. Default value: `0.25`
   */
  nag_alpha?: number;
  /**
   * The proportion of steps to apply NAG. After the specified proportion
   * of steps has been iterated, the remaining steps will use original
   * attention processors in FLUX. Default value: `0.25`
   */
  nag_end?: number;
  /**
   * The scale for NAG. Higher values will result in a image that is more distant
   * to the negative prompt. Default value: `3`
   */
  nag_scale?: number;
  /**
   * The tau for NAG. Controls the normalization of the hidden state.
   * Higher values will result in a less aggressive normalization,
   * but may also lead to unexpected changes with respect to the original image.
   * Not recommended to change this value. Default value: `2.5`
   */
  nag_tau?: number;
  /**
   * Negative prompt to steer the image generation away from unwanted features.
   * By default, we will be using NAG for processing the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  real_cfg_scale?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to be ended. Default value: `1`
   */
  reference_end?: number;
  /**
   * URL of Image for Reference-Only
   */
  reference_image_url?: string | Blob | File;
  /**
   * The percentage of the total timesteps when the reference guidance is to bestarted.
   */
  reference_start?: number;
  /**
   * Strength of reference_only generation. Only used if a reference image is provided. Default value: `0.65`
   */
  reference_strength?: number;
  /**
   * Scheduler for the denoising process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmpp_2m";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Sigmas schedule for the denoising process.
   */
  sigma_schedule?: string;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Specifies whether beta sigmas ought to be used.
   */
  use_beta_schedule?: boolean;
  /**
   * Uses CFG-zero init sampling as in https://arxiv.org/abs/2503.18886.
   */
  use_cfg_zero?: boolean;
  /**
   * Uses classical CFG as in SD1.5, SDXL, etc. Increases generation times and price when set to be true.
   * If using XLabs IP-Adapter v1, this will be turned on!.
   */
  use_real_cfg?: boolean;
};
export type FluxGeneralInpaintingInput = {
  /**
   * Base shift for the scheduled timesteps Default value: `0.5`
   */
  base_shift?: number;
  /**
   * The LoRAs to use for the image generation which use a control image. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  control_loras?: Array<ControlLoraWeight>;
  /**
   * The controlnet unions to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnet_unions?: Array<ControlNetUnion>;
  /**
   * The controlnets to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnets?: Array<ControlNet>;
  /**
   * EasyControl Inputs to use for image generation.
   */
  easycontrols?: Array<EasyControlWeight>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Use an image input to influence the generation. Can be used to fill images in masked areas.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * IP-Adapter to use for image generation.
   */
  ip_adapters?: Array<IPAdapter>;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
  /**
   * Max shift for the scheduled timesteps Default value: `1.15`
   */
  max_shift?: number;
  /**
   * The alpha value for NAG. This value is used as a final weighting
   * factor for steering the normalized guidance (positive and negative prompts)
   * in the direction of the positive prompt. Higher values will result in less
   * steering on the normalized guidance where lower values will result in
   * considering the positive prompt guidance more. Default value: `0.25`
   */
  nag_alpha?: number;
  /**
   * The proportion of steps to apply NAG. After the specified proportion
   * of steps has been iterated, the remaining steps will use original
   * attention processors in FLUX. Default value: `0.25`
   */
  nag_end?: number;
  /**
   * The scale for NAG. Higher values will result in a image that is more distant
   * to the negative prompt. Default value: `3`
   */
  nag_scale?: number;
  /**
   * The tau for NAG. Controls the normalization of the hidden state.
   * Higher values will result in a less aggressive normalization,
   * but may also lead to unexpected changes with respect to the original image.
   * Not recommended to change this value. Default value: `2.5`
   */
  nag_tau?: number;
  /**
   * Negative prompt to steer the image generation away from unwanted features.
   * By default, we will be using NAG for processing the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  real_cfg_scale?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to be ended. Default value: `1`
   */
  reference_end?: number;
  /**
   * URL of Image for Reference-Only
   */
  reference_image_url?: string | Blob | File;
  /**
   * The percentage of the total timesteps when the reference guidance is to bestarted.
   */
  reference_start?: number;
  /**
   * Strength of reference_only generation. Only used if a reference image is provided. Default value: `0.65`
   */
  reference_strength?: number;
  /**
   * Scheduler for the denoising process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmpp_2m";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Sigmas schedule for the denoising process.
   */
  sigma_schedule?: string;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Specifies whether beta sigmas ought to be used.
   */
  use_beta_schedule?: boolean;
  /**
   * Uses CFG-zero init sampling as in https://arxiv.org/abs/2503.18886.
   */
  use_cfg_zero?: boolean;
  /**
   * Uses classical CFG as in SD1.5, SDXL, etc. Increases generation times and price when set to be true.
   * If using XLabs IP-Adapter v1, this will be turned on!.
   */
  use_real_cfg?: boolean;
};
export type FluxGeneralInput = {
  /**
   * Base shift for the scheduled timesteps Default value: `0.5`
   */
  base_shift?: number;
  /**
   * The LoRAs to use for the image generation which use a control image. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  control_loras?: Array<ControlLoraWeight>;
  /**
   * The controlnet unions to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnet_unions?: Array<ControlNetUnion>;
  /**
   * The controlnets to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnets?: Array<ControlNet>;
  /**
   * EasyControl Inputs to use for image generation.
   */
  easycontrols?: Array<EasyControlWeight>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Use an image input to influence the generation. Can be used to fill images in masked areas.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * IP-Adapter to use for image generation.
   */
  ip_adapters?: Array<IPAdapter>;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * Max shift for the scheduled timesteps Default value: `1.15`
   */
  max_shift?: number;
  /**
   * The alpha value for NAG. This value is used as a final weighting
   * factor for steering the normalized guidance (positive and negative prompts)
   * in the direction of the positive prompt. Higher values will result in less
   * steering on the normalized guidance where lower values will result in
   * considering the positive prompt guidance more. Default value: `0.25`
   */
  nag_alpha?: number;
  /**
   * The proportion of steps to apply NAG. After the specified proportion
   * of steps has been iterated, the remaining steps will use original
   * attention processors in FLUX. Default value: `0.25`
   */
  nag_end?: number;
  /**
   * The scale for NAG. Higher values will result in a image that is more distant
   * to the negative prompt. Default value: `3`
   */
  nag_scale?: number;
  /**
   * The tau for NAG. Controls the normalization of the hidden state.
   * Higher values will result in a less aggressive normalization,
   * but may also lead to unexpected changes with respect to the original image.
   * Not recommended to change this value. Default value: `2.5`
   */
  nag_tau?: number;
  /**
   * Negative prompt to steer the image generation away from unwanted features.
   * By default, we will be using NAG for processing the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  real_cfg_scale?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to be ended. Default value: `1`
   */
  reference_end?: number;
  /**
   * URL of Image for Reference-Only
   */
  reference_image_url?: string | Blob | File;
  /**
   * The percentage of the total timesteps when the reference guidance is to bestarted.
   */
  reference_start?: number;
  /**
   * Strength of reference_only generation. Only used if a reference image is provided. Default value: `0.65`
   */
  reference_strength?: number;
  /**
   * Scheduler for the denoising process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmpp_2m";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Sigmas schedule for the denoising process.
   */
  sigma_schedule?: string;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Specifies whether beta sigmas ought to be used.
   */
  use_beta_schedule?: boolean;
  /**
   * Uses CFG-zero init sampling as in https://arxiv.org/abs/2503.18886.
   */
  use_cfg_zero?: boolean;
  /**
   * Uses classical CFG as in SD1.5, SDXL, etc. Increases generation times and price when set to be true.
   * If using XLabs IP-Adapter v1, this will be turned on!.
   */
  use_real_cfg?: boolean;
};
export type FluxKontextDevInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * Determines how the output resolution is set for image editing.
   * - `auto`: The model selects an optimal resolution from a predefined set that best matches the input image's aspect ratio. This is the recommended setting for most use cases as it's what the model was trained on.
   * - `match_input`: The model will attempt to use the same resolution as the input image. The resolution will be adjusted to be compatible with the model's requirements (e.g. dimensions must be multiples of 16 and within supported limits).
   * Apart from these, a few aspect ratios are also supported. Default value: `"match_input"`
   */
  resolution_mode?:
    | "auto"
    | "match_input"
    | "1:1"
    | "16:9"
    | "21:9"
    | "3:2"
    | "2:3"
    | "4:5"
    | "5:4"
    | "3:4"
    | "4:3"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxKontextInput = {
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxKontextMultiInput = {
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Image prompt for the omni model.
   */
  image_urls: Array<string>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxKontextOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image files info.
   */
  images: Array<FalToolkitImageImageImage>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type FluxKreaLoraInpaintingInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxKreaTrainerInput = {
  /**
   * If True segmentation masks will be used in the weight the training loss. For people a face mask is used if possible. Default value: `true`
   */
  create_masks?: boolean;
  /**
   * The format of the archive. If not specified, the format will be inferred from the URL.
   */
  data_archive_format?: string;
  /**
   * URL to zip archive with images. Try to use at least 4 images in general the more the better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image file it corresponds to.
   */
  images_data_url: string | Blob | File;
  /**
   * Specifies whether the input data is already in a processed format. When set to False (default), the system expects raw input where image files and their corresponding caption files share the same name (e.g., 'photo.jpg' and 'photo.txt'). Set to True if your data is already in a preprocessed format.
   */
  is_input_format_already_preprocessed?: boolean;
  /**
   * If True, the training will be for a style. This will deactivate segmentation, captioning and will use trigger word instead. Use the trigger word to specify the style.
   */
  is_style?: boolean;
  /**
   * Number of steps to train the LoRA on.
   */
  steps?: number;
  /**
   * Trigger word to be used in the captions. If None, a trigger word will not be used.
   * If no captions are provide the trigger_word will be used instead of captions. If captions are the trigger word will not be used.
   */
  trigger_word?: string;
};
export type FluxKreaTrainerOutput = {
  /**
   * URL to the training configuration file.
   */
  config_file: File;
  /**
   * URL to the preprocessed images.
   */
  debug_preprocessed_output?: File;
  /**
   * URL to the trained diffusers lora weights.
   */
  diffusers_lora_file: File;
};
export type FluxLoraCannyInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `30`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for canny input
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type FluxLoraDepthInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for depth input
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxLoraFillInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Use an image fill input to fill in particular images into the masked area.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `30`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for fill operation
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Specifies whether to paste-back the original image onto to the non-inpainted areas of the output Default value: `true`
   */
  paste_back?: boolean;
  /**
   * The prompt to generate an image from. Default value: `""`
   */
  prompt?: string;
  /**
   * Resizes the image back to the original size. Use when you wish to preserve the exact image size as the originally provided image.
   */
  resize_to_original?: boolean;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxLoraImageToImageInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxLoraInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxLoraPortraitTrainerInput = {
  /**
   * If True, masks will be created for the subject.
   */
  create_masks?: boolean;
  /**
   * The format of the archive. If not specified, the format will be inferred from the URL.
   */
  data_archive_format?: string;
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image file it corresponds to.
   *
   * The captions can include a special string `[trigger]`. If a trigger_word is specified, it will replace `[trigger]` in the captions.
   */
  images_data_url: string | Blob | File;
  /**
   * Learning rate to use for training. Default value: `0.00009`
   */
  learning_rate?: number;
  /**
   * If True, multiresolution training will be used. Default value: `true`
   */
  multiresolution_training?: boolean;
  /**
   * URL to a checkpoint to resume training from. Default value: `""`
   */
  resume_from_checkpoint?: string;
  /**
   * Number of steps to train the LoRA on. Default value: `2500`
   */
  steps?: number;
  /**
   * If True, the subject will be cropped from the image. Default value: `true`
   */
  subject_crop?: boolean;
  /**
   * Trigger phrase to be used in the captions. If None, a trigger word will not be used.
   * If no captions are provide the trigger_work will be used instead of captions. If captions are provided, the trigger word will replace the `[trigger]` string in the captions.
   */
  trigger_phrase?: string;
};
export type FluxProCannyControlFinetunedInput = {
  /**
   * The control image URL to generate the Canny edge map from.
   */
  control_image_url: string | Blob | File;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `30`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProCannyControlInput = {
  /**
   * The control image URL to generate the Canny edge map from.
   */
  control_image_url: string | Blob | File;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProFillFinetunedInput = {
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProFillInput = {
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProKontextTextToImageInput = {
  /**
   * The aspect ratio of the generated image. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProNewInput = {
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProNewOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image files info.
   */
  images: Array<RegistryImageFastSdxlModelsImage>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type FluxProOutpaintInput = {
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * Pixels to expand at the bottom
   */
  expand_bottom?: number;
  /**
   * Pixels to expand on the left
   */
  expand_left?: number;
  /**
   * Pixels to expand on the right
   */
  expand_right?: number;
  /**
   * Pixels to expand at the top
   */
  expand_top?: number;
  /**
   * The image URL to expand using outpainting
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProTextToImageFinetunedInput = {
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProV11Input = {
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProV11UltraFinetunedInput = {
  /**
   * The aspect ratio of the generated image. Default value: `16:9`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21"
    | string;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
  /**
   * The strength of the image prompt, between 0 and 1. Default value: `0.1`
   */
  image_prompt_strength?: number;
  /**
   * The image URL to generate an image from.
   */
  image_url?: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Generate less processed, more natural-looking images.
   */
  raw?: boolean;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProV11UltraInput = {
  /**
   * The aspect ratio of the generated image. Default value: `16:9`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21"
    | string;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The strength of the image prompt, between 0 and 1. Default value: `0.1`
   */
  image_prompt_strength?: number;
  /**
   * The image URL to generate an image from.
   */
  image_url?: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Generate less processed, more natural-looking images.
   */
  raw?: boolean;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProV11UltraReduxInput = {
  /**
   * The aspect ratio of the generated image. Default value: `16:9`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21"
    | string;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The strength of the image prompt, between 0 and 1. Default value: `0.1`
   */
  image_prompt_strength?: number;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from. Default value: `""`
   */
  prompt?: string;
  /**
   * Generate less processed, more natural-looking images.
   */
  raw?: boolean;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxProV1ReduxInput = {
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from. Default value: `""`
   */
  prompt?: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxPulidInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The weight of the ID loss. Default value: `1`
   */
  id_weight?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The maximum sequence length for the model. Default value: `"128"`
   */
  max_sequence_length?: "128" | "256" | "512";
  /**
   * The prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `20`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * URL of image to use for inpainting.
   */
  reference_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The number of steps to start the CFG from.
   */
  start_step?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The weight of the CFG loss. Default value: `1`
   */
  true_cfg?: number;
};
export type FluxSchnellReduxInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type FluxSubjectInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image of the subject
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type FluxVisionUpscalerInput = {
  /**
   * The creativity of the model. The higher the creativity, the more the model will deviate from the original. Refers to the denoise strength of the sampling. Default value: `0.3`
   */
  creativity?: number;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * CFG/guidance scale (1-4). Controls how closely the model follows the prompt. Default value: `1`
   */
  guidance?: number;
  /**
   * The URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * The seed to use for the upscale. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Number of inference steps (4-50). Default value: `20`
   */
  steps?: number;
  /**
   * The upscale factor (1-4x). Default value: `2`
   */
  upscale_factor?: number;
};
export type FluxVisionUpscalerOutput = {
  /**
   * The VLM-generated caption used for upscaling.
   */
  caption: string;
  /**
   * The URL of the generated image.
   */
  image: Image;
  /**
   * The seed used to generate the image.
   */
  seed: number;
  /**
   * The timings of the different steps in the workflow.
   */
  timings: unknown;
};
export type FooocusImagePromptInput = {
  /**
   * The size of the generated image. You can choose between some presets or
   * custom height and width that **must be multiples of 8**. Default value: `"1024x1024"`
   */
  aspect_ratio?: string;
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_prompt_1: ImagePrompt;
  /**
   *
   */
  image_prompt_2?: ImagePrompt;
  /**
   *
   */
  image_prompt_3?: ImagePrompt;
  /**
   *
   */
  image_prompt_4?: ImagePrompt;
  /**
   * Describe what you want to inpaint. Default value: `""`
   */
  inpaint_additional_prompt?: string;
  /**
   * The image to use as a reference for inpainting.
   */
  inpaint_image_url?: string | Blob | File;
  /**
   * The mode to use for inpainting. Default value: `"Inpaint or Outpaint (default)"`
   */
  inpaint_mode?:
    | "Inpaint or Outpaint (default)"
    | "Improve Detail (face, hand, eyes, etc.)"
    | "Modify Content (add objects, change background, etc.)";
  /**
   * The LoRAs to use for the image generation. You can use up to 5 LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The image to use as a mask for the generated image.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Mixing Image Prompt and Inpaint
   */
  mixing_image_prompt_and_inpaint?: boolean;
  /**
   * Mixing Image Prompt and Vary/Upscale
   */
  mixing_image_prompt_and_vary_upscale?: boolean;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate in one request Default value: `1`
   */
  num_images?: number;
  /**
   * The directions to outpaint.
   */
  outpaint_selections?: Array<"Left" | "Right" | "Top" | "Bottom">;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * You can choose Speed or Quality Default value: `"Extreme Speed"`
   */
  performance?: "Speed" | "Quality" | "Extreme Speed" | "Lightning";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results. Default value: `""`
   */
  prompt?: string;
  /**
   * Refiner (SDXL or SD 1.5) Default value: `"None"`
   */
  refiner_model?: "None" | "realisticVisionV60B1_v51VAE.safetensors";
  /**
   * Use 0.4 for SD1.5 realistic models; 0.667 for SD1.5 anime models
   * 0.8 for XL-refiners; or any value for switching two SDXL models. Default value: `0.8`
   */
  refiner_switch?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The sharpness of the generated image. Use it to control how sharp the generated
   * image should be. Higher value means image and texture are sharper. Default value: `2`
   */
  sharpness?: number;
  /**
   * The style to use.
   */
  styles?: Array<
    | "Fooocus V2"
    | "Fooocus Enhance"
    | "Fooocus Sharp"
    | "Fooocus Semi Realistic"
    | "Fooocus Masterpiece"
    | "Fooocus Photograph"
    | "Fooocus Negative"
    | "Fooocus Cinematic"
    | "SAI 3D Model"
    | "SAI Analog Film"
    | "SAI Anime"
    | "SAI Cinematic"
    | "SAI Comic Book"
    | "SAI Craft Clay"
    | "SAI Digital Art"
    | "SAI Enhance"
    | "SAI Fantasy Art"
    | "SAI Isometric"
    | "SAI Line Art"
    | "SAI Lowpoly"
    | "SAI Neonpunk"
    | "SAI Origami"
    | "SAI Photographic"
    | "SAI Pixel Art"
    | "SAI Texture"
    | "MRE Cinematic Dynamic"
    | "MRE Spontaneous Picture"
    | "MRE Artistic Vision"
    | "MRE Dark Dream"
    | "MRE Gloomy Art"
    | "MRE Bad Dream"
    | "MRE Underground"
    | "MRE Surreal Painting"
    | "MRE Dynamic Illustration"
    | "MRE Undead Art"
    | "MRE Elemental Art"
    | "MRE Space Art"
    | "MRE Ancient Illustration"
    | "MRE Brave Art"
    | "MRE Heroic Fantasy"
    | "MRE Dark Cyberpunk"
    | "MRE Lyrical Geometry"
    | "MRE Sumi E Symbolic"
    | "MRE Sumi E Detailed"
    | "MRE Manga"
    | "MRE Anime"
    | "MRE Comic"
    | "Ads Advertising"
    | "Ads Automotive"
    | "Ads Corporate"
    | "Ads Fashion Editorial"
    | "Ads Food Photography"
    | "Ads Gourmet Food Photography"
    | "Ads Luxury"
    | "Ads Real Estate"
    | "Ads Retail"
    | "Artstyle Abstract"
    | "Artstyle Abstract Expressionism"
    | "Artstyle Art Deco"
    | "Artstyle Art Nouveau"
    | "Artstyle Constructivist"
    | "Artstyle Cubist"
    | "Artstyle Expressionist"
    | "Artstyle Graffiti"
    | "Artstyle Hyperrealism"
    | "Artstyle Impressionist"
    | "Artstyle Pointillism"
    | "Artstyle Pop Art"
    | "Artstyle Psychedelic"
    | "Artstyle Renaissance"
    | "Artstyle Steampunk"
    | "Artstyle Surrealist"
    | "Artstyle Typography"
    | "Artstyle Watercolor"
    | "Futuristic Biomechanical"
    | "Futuristic Biomechanical Cyberpunk"
    | "Futuristic Cybernetic"
    | "Futuristic Cybernetic Robot"
    | "Futuristic Cyberpunk Cityscape"
    | "Futuristic Futuristic"
    | "Futuristic Retro Cyberpunk"
    | "Futuristic Retro Futurism"
    | "Futuristic Sci Fi"
    | "Futuristic Vaporwave"
    | "Game Bubble Bobble"
    | "Game Cyberpunk Game"
    | "Game Fighting Game"
    | "Game Gta"
    | "Game Mario"
    | "Game Minecraft"
    | "Game Pokemon"
    | "Game Retro Arcade"
    | "Game Retro Game"
    | "Game Rpg Fantasy Game"
    | "Game Strategy Game"
    | "Game Streetfighter"
    | "Game Zelda"
    | "Misc Architectural"
    | "Misc Disco"
    | "Misc Dreamscape"
    | "Misc Dystopian"
    | "Misc Fairy Tale"
    | "Misc Gothic"
    | "Misc Grunge"
    | "Misc Horror"
    | "Misc Kawaii"
    | "Misc Lovecraftian"
    | "Misc Macabre"
    | "Misc Manga"
    | "Misc Metropolis"
    | "Misc Minimalist"
    | "Misc Monochrome"
    | "Misc Nautical"
    | "Misc Space"
    | "Misc Stained Glass"
    | "Misc Techwear Fashion"
    | "Misc Tribal"
    | "Misc Zentangle"
    | "Papercraft Collage"
    | "Papercraft Flat Papercut"
    | "Papercraft Kirigami"
    | "Papercraft Paper Mache"
    | "Papercraft Paper Quilling"
    | "Papercraft Papercut Collage"
    | "Papercraft Papercut Shadow Box"
    | "Papercraft Stacked Papercut"
    | "Papercraft Thick Layered Papercut"
    | "Photo Alien"
    | "Photo Film Noir"
    | "Photo Glamour"
    | "Photo Hdr"
    | "Photo Iphone Photographic"
    | "Photo Long Exposure"
    | "Photo Neon Noir"
    | "Photo Silhouette"
    | "Photo Tilt Shift"
    | "Cinematic Diva"
    | "Abstract Expressionism"
    | "Academia"
    | "Action Figure"
    | "Adorable 3D Character"
    | "Adorable Kawaii"
    | "Art Deco"
    | "Art Nouveau"
    | "Astral Aura"
    | "Avant Garde"
    | "Baroque"
    | "Bauhaus Style Poster"
    | "Blueprint Schematic Drawing"
    | "Caricature"
    | "Cel Shaded Art"
    | "Character Design Sheet"
    | "Classicism Art"
    | "Color Field Painting"
    | "Colored Pencil Art"
    | "Conceptual Art"
    | "Constructivism"
    | "Cubism"
    | "Dadaism"
    | "Dark Fantasy"
    | "Dark Moody Atmosphere"
    | "Dmt Art Style"
    | "Doodle Art"
    | "Double Exposure"
    | "Dripping Paint Splatter Art"
    | "Expressionism"
    | "Faded Polaroid Photo"
    | "Fauvism"
    | "Flat 2d Art"
    | "Fortnite Art Style"
    | "Futurism"
    | "Glitchcore"
    | "Glo Fi"
    | "Googie Art Style"
    | "Graffiti Art"
    | "Harlem Renaissance Art"
    | "High Fashion"
    | "Idyllic"
    | "Impressionism"
    | "Infographic Drawing"
    | "Ink Dripping Drawing"
    | "Japanese Ink Drawing"
    | "Knolling Photography"
    | "Light Cheery Atmosphere"
    | "Logo Design"
    | "Luxurious Elegance"
    | "Macro Photography"
    | "Mandola Art"
    | "Marker Drawing"
    | "Medievalism"
    | "Minimalism"
    | "Neo Baroque"
    | "Neo Byzantine"
    | "Neo Futurism"
    | "Neo Impressionism"
    | "Neo Rococo"
    | "Neoclassicism"
    | "Op Art"
    | "Ornate And Intricate"
    | "Pencil Sketch Drawing"
    | "Pop Art 2"
    | "Rococo"
    | "Silhouette Art"
    | "Simple Vector Art"
    | "Sketchup"
    | "Steampunk 2"
    | "Surrealism"
    | "Suprematism"
    | "Terragen"
    | "Tranquil Relaxing Atmosphere"
    | "Sticker Designs"
    | "Vibrant Rim Light"
    | "Volumetric Lighting"
    | "Watercolor 2"
    | "Whimsical And Playful"
    | "Mk Chromolithography"
    | "Mk Cross Processing Print"
    | "Mk Dufaycolor Photograph"
    | "Mk Herbarium"
    | "Mk Punk Collage"
    | "Mk Mosaic"
    | "Mk Van Gogh"
    | "Mk Coloring Book"
    | "Mk Singer Sargent"
    | "Mk Pollock"
    | "Mk Basquiat"
    | "Mk Andy Warhol"
    | "Mk Halftone Print"
    | "Mk Gond Painting"
    | "Mk Albumen Print"
    | "Mk Aquatint Print"
    | "Mk Anthotype Print"
    | "Mk Inuit Carving"
    | "Mk Bromoil Print"
    | "Mk Calotype Print"
    | "Mk Color Sketchnote"
    | "Mk Cibulak Porcelain"
    | "Mk Alcohol Ink Art"
    | "Mk One Line Art"
    | "Mk Blacklight Paint"
    | "Mk Carnival Glass"
    | "Mk Cyanotype Print"
    | "Mk Cross Stitching"
    | "Mk Encaustic Paint"
    | "Mk Embroidery"
    | "Mk Gyotaku"
    | "Mk Luminogram"
    | "Mk Lite Brite Art"
    | "Mk Mokume Gane"
    | "Pebble Art"
    | "Mk Palekh"
    | "Mk Suminagashi"
    | "Mk Scrimshaw"
    | "Mk Shibori"
    | "Mk Vitreous Enamel"
    | "Mk Ukiyo E"
    | "Mk Vintage Airline Poster"
    | "Mk Vintage Travel Poster"
    | "Mk Bauhaus Style"
    | "Mk Afrofuturism"
    | "Mk Atompunk"
    | "Mk Constructivism"
    | "Mk Chicano Art"
    | "Mk De Stijl"
    | "Mk Dayak Art"
    | "Mk Fayum Portrait"
    | "Mk Illuminated Manuscript"
    | "Mk Kalighat Painting"
    | "Mk Madhubani Painting"
    | "Mk Pictorialism"
    | "Mk Pichwai Painting"
    | "Mk Patachitra Painting"
    | "Mk Samoan Art Inspired"
    | "Mk Tlingit Art"
    | "Mk Adnate Style"
    | "Mk Ron English Style"
    | "Mk Shepard Fairey Style"
  >;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The image to upscale or vary.
   */
  uov_image_url?: string | Blob | File;
  /**
   * The method to use for upscaling or varying. Default value: `"Disabled"`
   */
  uov_method?:
    | "Disabled"
    | "Vary (Subtle)"
    | "Vary (Strong)"
    | "Upscale (1.5x)"
    | "Upscale (2x)"
    | "Upscale (Fast 2x)";
};
export type FooocusInpaintInput = {
  /**
   * The size of the generated image. You can choose between some presets or
   * custom height and width that **must be multiples of 8**. Default value: `"1024x1024"`
   */
  aspect_ratio?: string;
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_prompt_1?: ImagePrompt;
  /**
   *
   */
  image_prompt_2?: ImagePrompt;
  /**
   *
   */
  image_prompt_3?: ImagePrompt;
  /**
   *
   */
  image_prompt_4?: ImagePrompt;
  /**
   * Describe what you want to inpaint. Default value: `""`
   */
  inpaint_additional_prompt?: string;
  /**
   * If set to true, the initial preprocessing will be disabled.
   */
  inpaint_disable_initial_latent?: boolean;
  /**
   * Version of Fooocus inpaint model Default value: `"v2.6"`
   */
  inpaint_engine?: "None" | "v1" | "v2.5" | "v2.6";
  /**
   * Positive value will make white area in the mask larger, negative value will
   * make white area smaller. (default is 0, always process before any mask
   * invert)
   */
  inpaint_erode_or_dilate?: number;
  /**
   * The image to use as a reference for inpainting.
   */
  inpaint_image_url: string | Blob | File;
  /**
   * The mode to use for inpainting. Default value: `"Inpaint or Outpaint (default)"`
   */
  inpaint_mode?:
    | "Inpaint or Outpaint (default)"
    | "Improve Detail (face, hand, eyes, etc.)"
    | "Modify Content (add objects, change background, etc.)";
  /**
   * The area to inpaint. Value 0 is same as "Only Masked" in A1111. Value 1 is
   * same as "Whole Image" in A1111. Only used in inpaint, not used in outpaint.
   * (Outpaint always use 1.0) Default value: `0.618`
   */
  inpaint_respective_field?: number;
  /**
   * Same as the denoising strength in A1111 inpaint. Only used in inpaint, not
   * used in outpaint. (Outpaint always use 1.0) Default value: `1`
   */
  inpaint_strength?: number;
  /**
   * If set to true, the mask will be inverted.
   */
  invert_mask?: boolean;
  /**
   * The LoRAs to use for the image generation. You can use up to 5 LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The image to use as a mask for the generated image.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Mixing Image Prompt and Inpaint
   */
  mixing_image_prompt_and_inpaint?: boolean;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate in one request Default value: `1`
   */
  num_images?: number;
  /**
   * The directions to outpaint.
   */
  outpaint_selections?: Array<"Left" | "Right" | "Top" | "Bottom">;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * If set to true, the advanced inpaint options ('inpaint_disable_initial_latent',
   * 'inpaint_engine', 'inpaint_strength', 'inpaint_respective_field',
   * 'inpaint_erode_or_dilate') will be overridden.
   * Otherwise, the default values will be used.
   */
  override_inpaint_options?: boolean;
  /**
   * You can choose Speed or Quality Default value: `"Extreme Speed"`
   */
  performance?: "Speed" | "Quality" | "Extreme Speed" | "Lightning";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results. Default value: `""`
   */
  prompt?: string;
  /**
   * Refiner (SDXL or SD 1.5) Default value: `"None"`
   */
  refiner_model?: "None" | "realisticVisionV60B1_v51VAE.safetensors";
  /**
   * Use 0.4 for SD1.5 realistic models; 0.667 for SD1.5 anime models
   * 0.8 for XL-refiners; or any value for switching two SDXL models. Default value: `0.8`
   */
  refiner_switch?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The sharpness of the generated image. Use it to control how sharp the generated
   * image should be. Higher value means image and texture are sharper. Default value: `2`
   */
  sharpness?: number;
  /**
   * The style to use.
   */
  styles?: Array<
    | "Fooocus V2"
    | "Fooocus Enhance"
    | "Fooocus Sharp"
    | "Fooocus Semi Realistic"
    | "Fooocus Masterpiece"
    | "Fooocus Photograph"
    | "Fooocus Negative"
    | "Fooocus Cinematic"
    | "SAI 3D Model"
    | "SAI Analog Film"
    | "SAI Anime"
    | "SAI Cinematic"
    | "SAI Comic Book"
    | "SAI Craft Clay"
    | "SAI Digital Art"
    | "SAI Enhance"
    | "SAI Fantasy Art"
    | "SAI Isometric"
    | "SAI Line Art"
    | "SAI Lowpoly"
    | "SAI Neonpunk"
    | "SAI Origami"
    | "SAI Photographic"
    | "SAI Pixel Art"
    | "SAI Texture"
    | "MRE Cinematic Dynamic"
    | "MRE Spontaneous Picture"
    | "MRE Artistic Vision"
    | "MRE Dark Dream"
    | "MRE Gloomy Art"
    | "MRE Bad Dream"
    | "MRE Underground"
    | "MRE Surreal Painting"
    | "MRE Dynamic Illustration"
    | "MRE Undead Art"
    | "MRE Elemental Art"
    | "MRE Space Art"
    | "MRE Ancient Illustration"
    | "MRE Brave Art"
    | "MRE Heroic Fantasy"
    | "MRE Dark Cyberpunk"
    | "MRE Lyrical Geometry"
    | "MRE Sumi E Symbolic"
    | "MRE Sumi E Detailed"
    | "MRE Manga"
    | "MRE Anime"
    | "MRE Comic"
    | "Ads Advertising"
    | "Ads Automotive"
    | "Ads Corporate"
    | "Ads Fashion Editorial"
    | "Ads Food Photography"
    | "Ads Gourmet Food Photography"
    | "Ads Luxury"
    | "Ads Real Estate"
    | "Ads Retail"
    | "Artstyle Abstract"
    | "Artstyle Abstract Expressionism"
    | "Artstyle Art Deco"
    | "Artstyle Art Nouveau"
    | "Artstyle Constructivist"
    | "Artstyle Cubist"
    | "Artstyle Expressionist"
    | "Artstyle Graffiti"
    | "Artstyle Hyperrealism"
    | "Artstyle Impressionist"
    | "Artstyle Pointillism"
    | "Artstyle Pop Art"
    | "Artstyle Psychedelic"
    | "Artstyle Renaissance"
    | "Artstyle Steampunk"
    | "Artstyle Surrealist"
    | "Artstyle Typography"
    | "Artstyle Watercolor"
    | "Futuristic Biomechanical"
    | "Futuristic Biomechanical Cyberpunk"
    | "Futuristic Cybernetic"
    | "Futuristic Cybernetic Robot"
    | "Futuristic Cyberpunk Cityscape"
    | "Futuristic Futuristic"
    | "Futuristic Retro Cyberpunk"
    | "Futuristic Retro Futurism"
    | "Futuristic Sci Fi"
    | "Futuristic Vaporwave"
    | "Game Bubble Bobble"
    | "Game Cyberpunk Game"
    | "Game Fighting Game"
    | "Game Gta"
    | "Game Mario"
    | "Game Minecraft"
    | "Game Pokemon"
    | "Game Retro Arcade"
    | "Game Retro Game"
    | "Game Rpg Fantasy Game"
    | "Game Strategy Game"
    | "Game Streetfighter"
    | "Game Zelda"
    | "Misc Architectural"
    | "Misc Disco"
    | "Misc Dreamscape"
    | "Misc Dystopian"
    | "Misc Fairy Tale"
    | "Misc Gothic"
    | "Misc Grunge"
    | "Misc Horror"
    | "Misc Kawaii"
    | "Misc Lovecraftian"
    | "Misc Macabre"
    | "Misc Manga"
    | "Misc Metropolis"
    | "Misc Minimalist"
    | "Misc Monochrome"
    | "Misc Nautical"
    | "Misc Space"
    | "Misc Stained Glass"
    | "Misc Techwear Fashion"
    | "Misc Tribal"
    | "Misc Zentangle"
    | "Papercraft Collage"
    | "Papercraft Flat Papercut"
    | "Papercraft Kirigami"
    | "Papercraft Paper Mache"
    | "Papercraft Paper Quilling"
    | "Papercraft Papercut Collage"
    | "Papercraft Papercut Shadow Box"
    | "Papercraft Stacked Papercut"
    | "Papercraft Thick Layered Papercut"
    | "Photo Alien"
    | "Photo Film Noir"
    | "Photo Glamour"
    | "Photo Hdr"
    | "Photo Iphone Photographic"
    | "Photo Long Exposure"
    | "Photo Neon Noir"
    | "Photo Silhouette"
    | "Photo Tilt Shift"
    | "Cinematic Diva"
    | "Abstract Expressionism"
    | "Academia"
    | "Action Figure"
    | "Adorable 3D Character"
    | "Adorable Kawaii"
    | "Art Deco"
    | "Art Nouveau"
    | "Astral Aura"
    | "Avant Garde"
    | "Baroque"
    | "Bauhaus Style Poster"
    | "Blueprint Schematic Drawing"
    | "Caricature"
    | "Cel Shaded Art"
    | "Character Design Sheet"
    | "Classicism Art"
    | "Color Field Painting"
    | "Colored Pencil Art"
    | "Conceptual Art"
    | "Constructivism"
    | "Cubism"
    | "Dadaism"
    | "Dark Fantasy"
    | "Dark Moody Atmosphere"
    | "Dmt Art Style"
    | "Doodle Art"
    | "Double Exposure"
    | "Dripping Paint Splatter Art"
    | "Expressionism"
    | "Faded Polaroid Photo"
    | "Fauvism"
    | "Flat 2d Art"
    | "Fortnite Art Style"
    | "Futurism"
    | "Glitchcore"
    | "Glo Fi"
    | "Googie Art Style"
    | "Graffiti Art"
    | "Harlem Renaissance Art"
    | "High Fashion"
    | "Idyllic"
    | "Impressionism"
    | "Infographic Drawing"
    | "Ink Dripping Drawing"
    | "Japanese Ink Drawing"
    | "Knolling Photography"
    | "Light Cheery Atmosphere"
    | "Logo Design"
    | "Luxurious Elegance"
    | "Macro Photography"
    | "Mandola Art"
    | "Marker Drawing"
    | "Medievalism"
    | "Minimalism"
    | "Neo Baroque"
    | "Neo Byzantine"
    | "Neo Futurism"
    | "Neo Impressionism"
    | "Neo Rococo"
    | "Neoclassicism"
    | "Op Art"
    | "Ornate And Intricate"
    | "Pencil Sketch Drawing"
    | "Pop Art 2"
    | "Rococo"
    | "Silhouette Art"
    | "Simple Vector Art"
    | "Sketchup"
    | "Steampunk 2"
    | "Surrealism"
    | "Suprematism"
    | "Terragen"
    | "Tranquil Relaxing Atmosphere"
    | "Sticker Designs"
    | "Vibrant Rim Light"
    | "Volumetric Lighting"
    | "Watercolor 2"
    | "Whimsical And Playful"
    | "Mk Chromolithography"
    | "Mk Cross Processing Print"
    | "Mk Dufaycolor Photograph"
    | "Mk Herbarium"
    | "Mk Punk Collage"
    | "Mk Mosaic"
    | "Mk Van Gogh"
    | "Mk Coloring Book"
    | "Mk Singer Sargent"
    | "Mk Pollock"
    | "Mk Basquiat"
    | "Mk Andy Warhol"
    | "Mk Halftone Print"
    | "Mk Gond Painting"
    | "Mk Albumen Print"
    | "Mk Aquatint Print"
    | "Mk Anthotype Print"
    | "Mk Inuit Carving"
    | "Mk Bromoil Print"
    | "Mk Calotype Print"
    | "Mk Color Sketchnote"
    | "Mk Cibulak Porcelain"
    | "Mk Alcohol Ink Art"
    | "Mk One Line Art"
    | "Mk Blacklight Paint"
    | "Mk Carnival Glass"
    | "Mk Cyanotype Print"
    | "Mk Cross Stitching"
    | "Mk Encaustic Paint"
    | "Mk Embroidery"
    | "Mk Gyotaku"
    | "Mk Luminogram"
    | "Mk Lite Brite Art"
    | "Mk Mokume Gane"
    | "Pebble Art"
    | "Mk Palekh"
    | "Mk Suminagashi"
    | "Mk Scrimshaw"
    | "Mk Shibori"
    | "Mk Vitreous Enamel"
    | "Mk Ukiyo E"
    | "Mk Vintage Airline Poster"
    | "Mk Vintage Travel Poster"
    | "Mk Bauhaus Style"
    | "Mk Afrofuturism"
    | "Mk Atompunk"
    | "Mk Constructivism"
    | "Mk Chicano Art"
    | "Mk De Stijl"
    | "Mk Dayak Art"
    | "Mk Fayum Portrait"
    | "Mk Illuminated Manuscript"
    | "Mk Kalighat Painting"
    | "Mk Madhubani Painting"
    | "Mk Pictorialism"
    | "Mk Pichwai Painting"
    | "Mk Patachitra Painting"
    | "Mk Samoan Art Inspired"
    | "Mk Tlingit Art"
    | "Mk Adnate Style"
    | "Mk Ron English Style"
    | "Mk Shepard Fairey Style"
  >;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type fooocusInput = {
  /**
   * The size of the generated image. You can choose between some presets or
   * custom height and width that **must be multiples of 8**. Default value: `"1024x1024"`
   */
  aspect_ratio?: string;
  /**
   * The stop at value of the control image. Use it to control how much the generated image
   * should look like the control image. Default value: `1`
   */
  control_image_stop_at?: number;
  /**
   * The image to use as a reference for the generated image.
   */
  control_image_url?: string | Blob | File;
  /**
   * The strength of the control image. Use it to control how much the generated image
   * should look like the control image. Default value: `1`
   */
  control_image_weight?: number;
  /**
   * The type of image control Default value: `"PyraCanny"`
   */
  control_type?: "ImagePrompt" | "PyraCanny" | "CPDS" | "FaceSwap";
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The image to use as a reference for inpainting.
   */
  inpaint_image_url?: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use up to 5 LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The image to use as a mask for the generated image.
   */
  mask_image_url?: string | Blob | File;
  /**
   *
   */
  mixing_image_prompt_and_inpaint?: boolean;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate in one request Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * You can choose Speed or Quality Default value: `"Extreme Speed"`
   */
  performance?: "Speed" | "Quality" | "Extreme Speed" | "Lightning";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results. Default value: `""`
   */
  prompt?: string;
  /**
   * Refiner (SDXL or SD 1.5) Default value: `"None"`
   */
  refiner_model?: "None" | "realisticVisionV60B1_v51VAE.safetensors";
  /**
   * Use 0.4 for SD1.5 realistic models; 0.667 for SD1.5 anime models
   * 0.8 for XL-refiners; or any value for switching two SDXL models. Default value: `0.8`
   */
  refiner_switch?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The sharpness of the generated image. Use it to control how sharp the generated
   * image should be. Higher value means image and texture are sharper. Default value: `2`
   */
  sharpness?: number;
  /**
   * The style to use.
   */
  styles?: Array<
    | "Fooocus V2"
    | "Fooocus Enhance"
    | "Fooocus Sharp"
    | "Fooocus Semi Realistic"
    | "Fooocus Masterpiece"
    | "Fooocus Photograph"
    | "Fooocus Negative"
    | "Fooocus Cinematic"
    | "SAI 3D Model"
    | "SAI Analog Film"
    | "SAI Anime"
    | "SAI Cinematic"
    | "SAI Comic Book"
    | "SAI Craft Clay"
    | "SAI Digital Art"
    | "SAI Enhance"
    | "SAI Fantasy Art"
    | "SAI Isometric"
    | "SAI Line Art"
    | "SAI Lowpoly"
    | "SAI Neonpunk"
    | "SAI Origami"
    | "SAI Photographic"
    | "SAI Pixel Art"
    | "SAI Texture"
    | "MRE Cinematic Dynamic"
    | "MRE Spontaneous Picture"
    | "MRE Artistic Vision"
    | "MRE Dark Dream"
    | "MRE Gloomy Art"
    | "MRE Bad Dream"
    | "MRE Underground"
    | "MRE Surreal Painting"
    | "MRE Dynamic Illustration"
    | "MRE Undead Art"
    | "MRE Elemental Art"
    | "MRE Space Art"
    | "MRE Ancient Illustration"
    | "MRE Brave Art"
    | "MRE Heroic Fantasy"
    | "MRE Dark Cyberpunk"
    | "MRE Lyrical Geometry"
    | "MRE Sumi E Symbolic"
    | "MRE Sumi E Detailed"
    | "MRE Manga"
    | "MRE Anime"
    | "MRE Comic"
    | "Ads Advertising"
    | "Ads Automotive"
    | "Ads Corporate"
    | "Ads Fashion Editorial"
    | "Ads Food Photography"
    | "Ads Gourmet Food Photography"
    | "Ads Luxury"
    | "Ads Real Estate"
    | "Ads Retail"
    | "Artstyle Abstract"
    | "Artstyle Abstract Expressionism"
    | "Artstyle Art Deco"
    | "Artstyle Art Nouveau"
    | "Artstyle Constructivist"
    | "Artstyle Cubist"
    | "Artstyle Expressionist"
    | "Artstyle Graffiti"
    | "Artstyle Hyperrealism"
    | "Artstyle Impressionist"
    | "Artstyle Pointillism"
    | "Artstyle Pop Art"
    | "Artstyle Psychedelic"
    | "Artstyle Renaissance"
    | "Artstyle Steampunk"
    | "Artstyle Surrealist"
    | "Artstyle Typography"
    | "Artstyle Watercolor"
    | "Futuristic Biomechanical"
    | "Futuristic Biomechanical Cyberpunk"
    | "Futuristic Cybernetic"
    | "Futuristic Cybernetic Robot"
    | "Futuristic Cyberpunk Cityscape"
    | "Futuristic Futuristic"
    | "Futuristic Retro Cyberpunk"
    | "Futuristic Retro Futurism"
    | "Futuristic Sci Fi"
    | "Futuristic Vaporwave"
    | "Game Bubble Bobble"
    | "Game Cyberpunk Game"
    | "Game Fighting Game"
    | "Game Gta"
    | "Game Mario"
    | "Game Minecraft"
    | "Game Pokemon"
    | "Game Retro Arcade"
    | "Game Retro Game"
    | "Game Rpg Fantasy Game"
    | "Game Strategy Game"
    | "Game Streetfighter"
    | "Game Zelda"
    | "Misc Architectural"
    | "Misc Disco"
    | "Misc Dreamscape"
    | "Misc Dystopian"
    | "Misc Fairy Tale"
    | "Misc Gothic"
    | "Misc Grunge"
    | "Misc Horror"
    | "Misc Kawaii"
    | "Misc Lovecraftian"
    | "Misc Macabre"
    | "Misc Manga"
    | "Misc Metropolis"
    | "Misc Minimalist"
    | "Misc Monochrome"
    | "Misc Nautical"
    | "Misc Space"
    | "Misc Stained Glass"
    | "Misc Techwear Fashion"
    | "Misc Tribal"
    | "Misc Zentangle"
    | "Papercraft Collage"
    | "Papercraft Flat Papercut"
    | "Papercraft Kirigami"
    | "Papercraft Paper Mache"
    | "Papercraft Paper Quilling"
    | "Papercraft Papercut Collage"
    | "Papercraft Papercut Shadow Box"
    | "Papercraft Stacked Papercut"
    | "Papercraft Thick Layered Papercut"
    | "Photo Alien"
    | "Photo Film Noir"
    | "Photo Glamour"
    | "Photo Hdr"
    | "Photo Iphone Photographic"
    | "Photo Long Exposure"
    | "Photo Neon Noir"
    | "Photo Silhouette"
    | "Photo Tilt Shift"
    | "Cinematic Diva"
    | "Abstract Expressionism"
    | "Academia"
    | "Action Figure"
    | "Adorable 3D Character"
    | "Adorable Kawaii"
    | "Art Deco"
    | "Art Nouveau"
    | "Astral Aura"
    | "Avant Garde"
    | "Baroque"
    | "Bauhaus Style Poster"
    | "Blueprint Schematic Drawing"
    | "Caricature"
    | "Cel Shaded Art"
    | "Character Design Sheet"
    | "Classicism Art"
    | "Color Field Painting"
    | "Colored Pencil Art"
    | "Conceptual Art"
    | "Constructivism"
    | "Cubism"
    | "Dadaism"
    | "Dark Fantasy"
    | "Dark Moody Atmosphere"
    | "Dmt Art Style"
    | "Doodle Art"
    | "Double Exposure"
    | "Dripping Paint Splatter Art"
    | "Expressionism"
    | "Faded Polaroid Photo"
    | "Fauvism"
    | "Flat 2d Art"
    | "Fortnite Art Style"
    | "Futurism"
    | "Glitchcore"
    | "Glo Fi"
    | "Googie Art Style"
    | "Graffiti Art"
    | "Harlem Renaissance Art"
    | "High Fashion"
    | "Idyllic"
    | "Impressionism"
    | "Infographic Drawing"
    | "Ink Dripping Drawing"
    | "Japanese Ink Drawing"
    | "Knolling Photography"
    | "Light Cheery Atmosphere"
    | "Logo Design"
    | "Luxurious Elegance"
    | "Macro Photography"
    | "Mandola Art"
    | "Marker Drawing"
    | "Medievalism"
    | "Minimalism"
    | "Neo Baroque"
    | "Neo Byzantine"
    | "Neo Futurism"
    | "Neo Impressionism"
    | "Neo Rococo"
    | "Neoclassicism"
    | "Op Art"
    | "Ornate And Intricate"
    | "Pencil Sketch Drawing"
    | "Pop Art 2"
    | "Rococo"
    | "Silhouette Art"
    | "Simple Vector Art"
    | "Sketchup"
    | "Steampunk 2"
    | "Surrealism"
    | "Suprematism"
    | "Terragen"
    | "Tranquil Relaxing Atmosphere"
    | "Sticker Designs"
    | "Vibrant Rim Light"
    | "Volumetric Lighting"
    | "Watercolor 2"
    | "Whimsical And Playful"
    | "Mk Chromolithography"
    | "Mk Cross Processing Print"
    | "Mk Dufaycolor Photograph"
    | "Mk Herbarium"
    | "Mk Punk Collage"
    | "Mk Mosaic"
    | "Mk Van Gogh"
    | "Mk Coloring Book"
    | "Mk Singer Sargent"
    | "Mk Pollock"
    | "Mk Basquiat"
    | "Mk Andy Warhol"
    | "Mk Halftone Print"
    | "Mk Gond Painting"
    | "Mk Albumen Print"
    | "Mk Aquatint Print"
    | "Mk Anthotype Print"
    | "Mk Inuit Carving"
    | "Mk Bromoil Print"
    | "Mk Calotype Print"
    | "Mk Color Sketchnote"
    | "Mk Cibulak Porcelain"
    | "Mk Alcohol Ink Art"
    | "Mk One Line Art"
    | "Mk Blacklight Paint"
    | "Mk Carnival Glass"
    | "Mk Cyanotype Print"
    | "Mk Cross Stitching"
    | "Mk Encaustic Paint"
    | "Mk Embroidery"
    | "Mk Gyotaku"
    | "Mk Luminogram"
    | "Mk Lite Brite Art"
    | "Mk Mokume Gane"
    | "Pebble Art"
    | "Mk Palekh"
    | "Mk Suminagashi"
    | "Mk Scrimshaw"
    | "Mk Shibori"
    | "Mk Vitreous Enamel"
    | "Mk Ukiyo E"
    | "Mk Vintage Airline Poster"
    | "Mk Vintage Travel Poster"
    | "Mk Bauhaus Style"
    | "Mk Afrofuturism"
    | "Mk Atompunk"
    | "Mk Constructivism"
    | "Mk Chicano Art"
    | "Mk De Stijl"
    | "Mk Dayak Art"
    | "Mk Fayum Portrait"
    | "Mk Illuminated Manuscript"
    | "Mk Kalighat Painting"
    | "Mk Madhubani Painting"
    | "Mk Pictorialism"
    | "Mk Pichwai Painting"
    | "Mk Patachitra Painting"
    | "Mk Samoan Art Inspired"
    | "Mk Tlingit Art"
    | "Mk Adnate Style"
    | "Mk Ron English Style"
    | "Mk Shepard Fairey Style"
  >;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type fooocusOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image file info.
   */
  images: Array<Image>;
  /**
   * The time taken for the generation process.
   */
  timings: unknown;
};
export type FooocusUpscaleOrVaryInput = {
  /**
   * The size of the generated image. You can choose between some presets or
   * custom height and width that **must be multiples of 8**. Default value: `"1024x1024"`
   */
  aspect_ratio?: string;
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_prompt_1?: ImagePrompt;
  /**
   *
   */
  image_prompt_2?: ImagePrompt;
  /**
   *
   */
  image_prompt_3?: ImagePrompt;
  /**
   *
   */
  image_prompt_4?: ImagePrompt;
  /**
   * The LoRAs to use for the image generation. You can use up to 5 LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * Mixing Image Prompt and Vary/Upscale
   */
  mixing_image_prompt_and_vary_upscale?: boolean;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate in one request Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * You can choose Speed or Quality Default value: `"Extreme Speed"`
   */
  performance?: "Speed" | "Quality" | "Extreme Speed" | "Lightning";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results. Default value: `""`
   */
  prompt?: string;
  /**
   * Refiner (SDXL or SD 1.5) Default value: `"None"`
   */
  refiner_model?: "None" | "realisticVisionV60B1_v51VAE.safetensors";
  /**
   * Use 0.4 for SD1.5 realistic models; 0.667 for SD1.5 anime models
   * 0.8 for XL-refiners; or any value for switching two SDXL models. Default value: `0.8`
   */
  refiner_switch?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The sharpness of the generated image. Use it to control how sharp the generated
   * image should be. Higher value means image and texture are sharper. Default value: `2`
   */
  sharpness?: number;
  /**
   * The style to use.
   */
  styles?: Array<
    | "Fooocus V2"
    | "Fooocus Enhance"
    | "Fooocus Sharp"
    | "Fooocus Semi Realistic"
    | "Fooocus Masterpiece"
    | "Fooocus Photograph"
    | "Fooocus Negative"
    | "Fooocus Cinematic"
    | "SAI 3D Model"
    | "SAI Analog Film"
    | "SAI Anime"
    | "SAI Cinematic"
    | "SAI Comic Book"
    | "SAI Craft Clay"
    | "SAI Digital Art"
    | "SAI Enhance"
    | "SAI Fantasy Art"
    | "SAI Isometric"
    | "SAI Line Art"
    | "SAI Lowpoly"
    | "SAI Neonpunk"
    | "SAI Origami"
    | "SAI Photographic"
    | "SAI Pixel Art"
    | "SAI Texture"
    | "MRE Cinematic Dynamic"
    | "MRE Spontaneous Picture"
    | "MRE Artistic Vision"
    | "MRE Dark Dream"
    | "MRE Gloomy Art"
    | "MRE Bad Dream"
    | "MRE Underground"
    | "MRE Surreal Painting"
    | "MRE Dynamic Illustration"
    | "MRE Undead Art"
    | "MRE Elemental Art"
    | "MRE Space Art"
    | "MRE Ancient Illustration"
    | "MRE Brave Art"
    | "MRE Heroic Fantasy"
    | "MRE Dark Cyberpunk"
    | "MRE Lyrical Geometry"
    | "MRE Sumi E Symbolic"
    | "MRE Sumi E Detailed"
    | "MRE Manga"
    | "MRE Anime"
    | "MRE Comic"
    | "Ads Advertising"
    | "Ads Automotive"
    | "Ads Corporate"
    | "Ads Fashion Editorial"
    | "Ads Food Photography"
    | "Ads Gourmet Food Photography"
    | "Ads Luxury"
    | "Ads Real Estate"
    | "Ads Retail"
    | "Artstyle Abstract"
    | "Artstyle Abstract Expressionism"
    | "Artstyle Art Deco"
    | "Artstyle Art Nouveau"
    | "Artstyle Constructivist"
    | "Artstyle Cubist"
    | "Artstyle Expressionist"
    | "Artstyle Graffiti"
    | "Artstyle Hyperrealism"
    | "Artstyle Impressionist"
    | "Artstyle Pointillism"
    | "Artstyle Pop Art"
    | "Artstyle Psychedelic"
    | "Artstyle Renaissance"
    | "Artstyle Steampunk"
    | "Artstyle Surrealist"
    | "Artstyle Typography"
    | "Artstyle Watercolor"
    | "Futuristic Biomechanical"
    | "Futuristic Biomechanical Cyberpunk"
    | "Futuristic Cybernetic"
    | "Futuristic Cybernetic Robot"
    | "Futuristic Cyberpunk Cityscape"
    | "Futuristic Futuristic"
    | "Futuristic Retro Cyberpunk"
    | "Futuristic Retro Futurism"
    | "Futuristic Sci Fi"
    | "Futuristic Vaporwave"
    | "Game Bubble Bobble"
    | "Game Cyberpunk Game"
    | "Game Fighting Game"
    | "Game Gta"
    | "Game Mario"
    | "Game Minecraft"
    | "Game Pokemon"
    | "Game Retro Arcade"
    | "Game Retro Game"
    | "Game Rpg Fantasy Game"
    | "Game Strategy Game"
    | "Game Streetfighter"
    | "Game Zelda"
    | "Misc Architectural"
    | "Misc Disco"
    | "Misc Dreamscape"
    | "Misc Dystopian"
    | "Misc Fairy Tale"
    | "Misc Gothic"
    | "Misc Grunge"
    | "Misc Horror"
    | "Misc Kawaii"
    | "Misc Lovecraftian"
    | "Misc Macabre"
    | "Misc Manga"
    | "Misc Metropolis"
    | "Misc Minimalist"
    | "Misc Monochrome"
    | "Misc Nautical"
    | "Misc Space"
    | "Misc Stained Glass"
    | "Misc Techwear Fashion"
    | "Misc Tribal"
    | "Misc Zentangle"
    | "Papercraft Collage"
    | "Papercraft Flat Papercut"
    | "Papercraft Kirigami"
    | "Papercraft Paper Mache"
    | "Papercraft Paper Quilling"
    | "Papercraft Papercut Collage"
    | "Papercraft Papercut Shadow Box"
    | "Papercraft Stacked Papercut"
    | "Papercraft Thick Layered Papercut"
    | "Photo Alien"
    | "Photo Film Noir"
    | "Photo Glamour"
    | "Photo Hdr"
    | "Photo Iphone Photographic"
    | "Photo Long Exposure"
    | "Photo Neon Noir"
    | "Photo Silhouette"
    | "Photo Tilt Shift"
    | "Cinematic Diva"
    | "Abstract Expressionism"
    | "Academia"
    | "Action Figure"
    | "Adorable 3D Character"
    | "Adorable Kawaii"
    | "Art Deco"
    | "Art Nouveau"
    | "Astral Aura"
    | "Avant Garde"
    | "Baroque"
    | "Bauhaus Style Poster"
    | "Blueprint Schematic Drawing"
    | "Caricature"
    | "Cel Shaded Art"
    | "Character Design Sheet"
    | "Classicism Art"
    | "Color Field Painting"
    | "Colored Pencil Art"
    | "Conceptual Art"
    | "Constructivism"
    | "Cubism"
    | "Dadaism"
    | "Dark Fantasy"
    | "Dark Moody Atmosphere"
    | "Dmt Art Style"
    | "Doodle Art"
    | "Double Exposure"
    | "Dripping Paint Splatter Art"
    | "Expressionism"
    | "Faded Polaroid Photo"
    | "Fauvism"
    | "Flat 2d Art"
    | "Fortnite Art Style"
    | "Futurism"
    | "Glitchcore"
    | "Glo Fi"
    | "Googie Art Style"
    | "Graffiti Art"
    | "Harlem Renaissance Art"
    | "High Fashion"
    | "Idyllic"
    | "Impressionism"
    | "Infographic Drawing"
    | "Ink Dripping Drawing"
    | "Japanese Ink Drawing"
    | "Knolling Photography"
    | "Light Cheery Atmosphere"
    | "Logo Design"
    | "Luxurious Elegance"
    | "Macro Photography"
    | "Mandola Art"
    | "Marker Drawing"
    | "Medievalism"
    | "Minimalism"
    | "Neo Baroque"
    | "Neo Byzantine"
    | "Neo Futurism"
    | "Neo Impressionism"
    | "Neo Rococo"
    | "Neoclassicism"
    | "Op Art"
    | "Ornate And Intricate"
    | "Pencil Sketch Drawing"
    | "Pop Art 2"
    | "Rococo"
    | "Silhouette Art"
    | "Simple Vector Art"
    | "Sketchup"
    | "Steampunk 2"
    | "Surrealism"
    | "Suprematism"
    | "Terragen"
    | "Tranquil Relaxing Atmosphere"
    | "Sticker Designs"
    | "Vibrant Rim Light"
    | "Volumetric Lighting"
    | "Watercolor 2"
    | "Whimsical And Playful"
    | "Mk Chromolithography"
    | "Mk Cross Processing Print"
    | "Mk Dufaycolor Photograph"
    | "Mk Herbarium"
    | "Mk Punk Collage"
    | "Mk Mosaic"
    | "Mk Van Gogh"
    | "Mk Coloring Book"
    | "Mk Singer Sargent"
    | "Mk Pollock"
    | "Mk Basquiat"
    | "Mk Andy Warhol"
    | "Mk Halftone Print"
    | "Mk Gond Painting"
    | "Mk Albumen Print"
    | "Mk Aquatint Print"
    | "Mk Anthotype Print"
    | "Mk Inuit Carving"
    | "Mk Bromoil Print"
    | "Mk Calotype Print"
    | "Mk Color Sketchnote"
    | "Mk Cibulak Porcelain"
    | "Mk Alcohol Ink Art"
    | "Mk One Line Art"
    | "Mk Blacklight Paint"
    | "Mk Carnival Glass"
    | "Mk Cyanotype Print"
    | "Mk Cross Stitching"
    | "Mk Encaustic Paint"
    | "Mk Embroidery"
    | "Mk Gyotaku"
    | "Mk Luminogram"
    | "Mk Lite Brite Art"
    | "Mk Mokume Gane"
    | "Pebble Art"
    | "Mk Palekh"
    | "Mk Suminagashi"
    | "Mk Scrimshaw"
    | "Mk Shibori"
    | "Mk Vitreous Enamel"
    | "Mk Ukiyo E"
    | "Mk Vintage Airline Poster"
    | "Mk Vintage Travel Poster"
    | "Mk Bauhaus Style"
    | "Mk Afrofuturism"
    | "Mk Atompunk"
    | "Mk Constructivism"
    | "Mk Chicano Art"
    | "Mk De Stijl"
    | "Mk Dayak Art"
    | "Mk Fayum Portrait"
    | "Mk Illuminated Manuscript"
    | "Mk Kalighat Painting"
    | "Mk Madhubani Painting"
    | "Mk Pictorialism"
    | "Mk Pichwai Painting"
    | "Mk Patachitra Painting"
    | "Mk Samoan Art Inspired"
    | "Mk Tlingit Art"
    | "Mk Adnate Style"
    | "Mk Ron English Style"
    | "Mk Shepard Fairey Style"
  >;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The image to upscale or vary.
   */
  uov_image_url: string | Blob | File;
  /**
   * The method to use for upscaling or varying. Default value: `"Vary (Strong)"`
   */
  uov_method?:
    | "Disabled"
    | "Vary (Subtle)"
    | "Vary (Strong)"
    | "Upscale (1.5x)"
    | "Upscale (2x)"
    | "Upscale (Fast 2x)";
};
export type ForceParams = {
  /**
   * Direction of force in degrees. 0 = right, 90 = up, 180 = left, 270 = down.
   */
  angle: number;
  /**
   * Strength of the force (30 = gentle push, 400 = strong impact).
   */
  magnitude: number;
  /**
   * Mass of the object (1.0 to 4.0). Use -1 to let the model infer mass automatically. Default value: `-1`
   */
  mass?: number;
  /**
   * Normalized x position of the force application point (0 = left, 1 = right).
   */
  x: number;
  /**
   * Normalized y position of the force application point (0 = top, 1 = bottom).
   */
  y: number;
};
export type Frame = {
  /**
   * URL of the frame
   */
  url: string;
};
export type FrameInput = {
  /**
   * Type of frame to extract: first, middle, or last frame of the video Default value: `"first"`
   */
  frame_type?: "first" | "middle" | "last";
  /**
   * URL of the video file to use as the video track
   */
  video_url: string | Blob | File;
};
export type FramepackFlf2vInput = {
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Classifier-Free Guidance scale for the generation. Default value: `1`
   */
  cfg_scale?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the end image input.
   */
  end_image_url: string | Blob | File;
  /**
   * Guidance scale for the generation. Default value: `10`
   */
  guidance_scale?: number;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `240`
   */
  num_frames?: number;
  /**
   * Text prompt for video generation (max 500 characters).
   */
  prompt: string;
  /**
   * The resolution of the video to generate. 720p generations cost 1.5x more than 480p generations. Default value: `"480p"`
   */
  resolution?: "720p" | "480p";
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * Determines the influence of the final frame on the generated video. Higher values result in the output being more heavily influenced by the last frame. Default value: `0.8`
   */
  strength?: number;
};
export type framepackInput = {
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Classifier-Free Guidance scale for the generation. Default value: `1`
   */
  cfg_scale?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance scale for the generation. Default value: `10`
   */
  guidance_scale?: number;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `180`
   */
  num_frames?: number;
  /**
   * Text prompt for video generation (max 500 characters).
   */
  prompt: string;
  /**
   * The resolution of the video to generate. 720p generations cost 1.5x more than 480p generations. Default value: `"480p"`
   */
  resolution?: "720p" | "480p";
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
};
export type GeminiFlashEditMultiInput = {
  /**
   * List of URLs of input images for editing
   */
  input_image_urls: Array<string>;
  /**
   * The prompt for image generation or editing
   */
  prompt: string;
};
export type GeminiFlashEditOutput = {
  /**
   * Text description or response from Gemini
   */
  description: string;
  /**
   * The generated or edited image
   */
  image: Image;
};
export type GeminiTtsInput = {
  /**
   * Language for multilingual synthesis. When set, steers the model to speak in the specified language. Supports 24 GA languages and 60+ Preview languages. If not set, the model auto-detects the language from the text.
   */
  language_code?:
    | "Arabic (Egypt)"
    | "Bangla (Bangladesh)"
    | "Dutch (Netherlands)"
    | "English (India)"
    | "English (US)"
    | "French (France)"
    | "German (Germany)"
    | "Hindi (India)"
    | "Indonesian (Indonesia)"
    | "Italian (Italy)"
    | "Japanese (Japan)"
    | "Korean (South Korea)"
    | "Marathi (India)"
    | "Polish (Poland)"
    | "Portuguese (Brazil)"
    | "Romanian (Romania)"
    | "Russian (Russia)"
    | "Spanish (Spain)"
    | "Tamil (India)"
    | "Telugu (India)"
    | "Thai (Thailand)"
    | "Turkish (Turkey)"
    | "Ukrainian (Ukraine)"
    | "Vietnamese (Vietnam)"
    | "Afrikaans (South Africa)"
    | "Albanian (Albania)"
    | "Amharic (Ethiopia)"
    | "Arabic (World)"
    | "Armenian (Armenia)"
    | "Azerbaijani (Azerbaijan)"
    | "Basque (Spain)"
    | "Belarusian (Belarus)"
    | "Bulgarian (Bulgaria)"
    | "Burmese (Myanmar)"
    | "Catalan (Spain)"
    | "Cebuano (Philippines)"
    | "Chinese Mandarin (China)"
    | "Chinese Mandarin (Taiwan)"
    | "Croatian (Croatia)"
    | "Czech (Czech Republic)"
    | "Danish (Denmark)"
    | "English (Australia)"
    | "English (UK)"
    | "Estonian (Estonia)"
    | "Filipino (Philippines)"
    | "Finnish (Finland)"
    | "French (Canada)"
    | "Galician (Spain)"
    | "Georgian (Georgia)"
    | "Greek (Greece)"
    | "Gujarati (India)"
    | "Haitian Creole (Haiti)"
    | "Hebrew (Israel)"
    | "Hungarian (Hungary)"
    | "Icelandic (Iceland)"
    | "Javanese (Java)"
    | "Kannada (India)"
    | "Konkani (India)"
    | "Lao (Laos)"
    | "Latin (Vatican City)"
    | "Latvian (Latvia)"
    | "Lithuanian (Lithuania)"
    | "Luxembourgish (Luxembourg)"
    | "Macedonian (North Macedonia)"
    | "Maithili (India)"
    | "Malagasy (Madagascar)"
    | "Malay (Malaysia)"
    | "Malayalam (India)"
    | "Mongolian (Mongolia)"
    | "Nepali (Nepal)"
    | "Norwegian Bokmal (Norway)"
    | "Norwegian Nynorsk (Norway)"
    | "Odia (India)"
    | "Pashto (Afghanistan)"
    | "Persian (Iran)"
    | "Portuguese (Portugal)"
    | "Punjabi (India)"
    | "Serbian (Serbia)"
    | "Sindhi (India)"
    | "Sinhala (Sri Lanka)"
    | "Slovak (Slovakia)"
    | "Slovenian (Slovenia)"
    | "Spanish (Latin America)"
    | "Spanish (Mexico)"
    | "Swahili (Kenya)"
    | "Swedish (Sweden)"
    | "Urdu (Pakistan)";
  /**
   * Which Gemini TTS model to use. gemini-2.5-flash-tts: low latency, cost-efficient for everyday applications (recommended). gemini-2.5-pro-tts: highest quality, best for structured workflows like podcasts, audiobooks, and customer support. Default value: `"gemini-2.5-flash-tts"`
   */
  model?: "gemini-2.5-flash-tts" | "gemini-2.5-pro-tts";
  /**
   * Audio output format. mp3: compressed, small file size (recommended). wav: uncompressed PCM wrapped in WAV (24 kHz, 16-bit mono). ogg_opus: Ogg container with Opus codec, good quality-to-size ratio. Default value: `"mp3"`
   */
  output_format?: "wav" | "mp3" | "ogg_opus";
  /**
   * The text to convert to speech. Gemini TTS supports natural-language prompting for style, pace, accent, and emotional expression — include delivery instructions inline with the text (e.g. 'Say cheerfully: Have a wonderful day!'). For multi-speaker synthesis, prefix lines with speaker aliases defined in the speakers field (e.g. 'Alice: Hello!
   * Bob: Hi!'). Supports inline pace/style markers like [slowly], [whispering], [excited], [extremely fast].
   */
  prompt: string;
  /**
   * Multi-speaker voice configuration. When set, enables multi-speaker synthesis where different parts of the text are spoken by different voices. Each speaker needs a voice and a speaker_id (alias) that matches prefixes in the prompt. Requires gemini-2.5-pro-tts or gemini-2.5-flash-tts model. Not supported with gemini-2.5-flash-lite-preview-tts.
   */
  speakers?: Array<SpeakerConfig>;
  /**
   * Optional style and delivery instructions prepended to the prompt. Controls expressiveness, accent, pace, tone, and emotional expression using natural language. Use this to separate style control from the text content. Examples: 'Speak warmly and slowly', 'Read this as a dramatic newscast', 'Use a British accent with a cheerful tone', 'Whisper mysteriously'.
   */
  style_instructions?: string;
  /**
   * Controls the randomness of the speech output. Higher values produce more creative and varied delivery, while lower values make the output more predictable and focused. Default value: `1`
   */
  temperature?: number;
  /**
   * Voice preset for single-speaker synthesis. 30 distinct voices are available. Ignored when speakers is set. Popular choices: Kore (strong, firm female), Puck (upbeat, lively male), Charon (calm, professional male), Zephyr (bright, clear female), Aoede (warm, melodic female). Default value: `"Kore"`
   */
  voice?:
    | "Achernar"
    | "Achird"
    | "Algenib"
    | "Algieba"
    | "Alnilam"
    | "Aoede"
    | "Autonoe"
    | "Callirrhoe"
    | "Charon"
    | "Despina"
    | "Enceladus"
    | "Erinome"
    | "Fenrir"
    | "Gacrux"
    | "Iapetus"
    | "Kore"
    | "Laomedeia"
    | "Leda"
    | "Orus"
    | "Pulcherrima"
    | "Puck"
    | "Rasalgethi"
    | "Sadachbia"
    | "Sadaltager"
    | "Schedar"
    | "Sulafat"
    | "Umbriel"
    | "Vindemiatrix"
    | "Zephyr"
    | "Zubenelgenubi";
};
export type GeneralRembgInput = {
  /**
   * Single VP9 video with alpha channel or two videos (rgb and alpha) in H264 format. H264 is recommended for better RGB quality. Default value: `"vp9"`
   */
  output_codec?: "vp9" | "h264";
  /**
   * Improves the quality of the extracted object's edges. Default value: `true`
   */
  refine_foreground_edges?: boolean;
  /**
   * Set to False if the subject is not a person. Default value: `true`
   */
  subject_is_person?: boolean;
  /**
   *
   */
  video_url: string | Blob | File;
};
export type GeneralRembgOutput = {
  /**
   *
   */
  video: Array<File>;
};
export type GenerateInput = {
  /**
   * Greater means more natural vocals. Lower means sharper instrumentals. We recommend 0.7. Default value: `0.7`
   */
  balance_strength?: number;
  /**
   * The beats per minute of the song. This can be set to an integer or the literal string "auto" to pick a suitable bpm based on the tags. Set bpm to null to not condition the model on bpm information. Default value: `auto`
   */
  bpm?: number | string;
  /**
   * The lyrics sung in the generated song. An empty string will generate an instrumental track.
   */
  lyrics_prompt?: string;
  /**
   * Generating 2 songs costs 1.5x the price of generating 1 song. Also, note that using the same seed may not result in identical songs if the number of songs generated is changed. Default value: `1`
   */
  num_songs?: number;
  /**
   * The bit rate to use for mp3 and m4a formats. Not available for other formats.
   */
  output_bit_rate?: "128" | "192" | "256" | "320";
  /**
   *  Default value: `"wav"`
   */
  output_format?: "flac" | "mp3" | "wav" | "ogg" | "m4a";
  /**
   * A description of the track you want to generate. This prompt will be used to automatically generate the tags and lyrics unless you manually set them. For example, if you set prompt and tags, then the prompt will be used to generate only the lyrics.
   */
  prompt?: string;
  /**
   * Controls how strongly your prompt influences the output. Greater values adhere more to the prompt but sound less natural. (This is CFG.) Default value: `2`
   */
  prompt_strength?: number;
  /**
   * The seed to use for generation. Will pick a random seed if not provided. Repeating a request with identical parameters (must use lyrics and tags, not prompt) and the same seed will generate the same song.
   */
  seed?: number;
  /**
   * Tags/styles of the music to generate. You can view a list of all available tags at https://sonauto.ai/tag-explorer.
   */
  tags?: Array<string>;
};
export type GenerateOutput = {
  /**
   * The generated audio files.
   */
  audio: Array<File>;
  /**
   * The lyrics used for generation.
   */
  lyrics?: string;
  /**
   * The seed used for generation. This can be used to generate an identical song by passing the same parameters with this seed in a future request.
   */
  seed: number;
  /**
   * The style tags used for generation.
   */
  tags?: Array<string>;
};
export type GenFillInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The URL of the binary mask image that represents the area that will be cleaned.
   */
  mask_url: string | Blob | File;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of Images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ghiblifyInput = {
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * The seed to use for the upscale. If not provided, a random seed will be used.
   */
  seed?: number;
};
export type GifToVideoInput = {
  /**
   * URL of the GIF file to convert
   */
  gif_url: string | Blob | File;
  /**
   * Output video format. 'webm' (VP9) supports transparency, 'mp4' (H.264) is more widely compatible but does not support transparency. Default value: `"webm"`
   */
  output_format?: "webm" | "mp4";
  /**
   * Preserve GIF transparency as alpha channel. Only works with webm format. When False or when using mp4, transparent areas become black. Default value: `true`
   */
  preserve_transparency?: boolean;
  /**
   * Quality level for the output video. Low=smaller file, lossless=best quality. 'lossless' only available for webm. Default value: `"medium"`
   */
  quality?: "low" | "medium" | "high" | "lossless";
};
export type GlmImageInput = {
  /**
   * If True, the prompt will be enhanced using an LLM for more detailed and higher quality results.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Enable NSFW safety checking on the generated images. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Higher values make the model follow the prompt more closely. Default value: `1.5`
   */
  guidance_scale?: number;
  /**
   * Output image size. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "portrait_3_2"
    | "landscape_3_2"
    | "portrait_hd"
    | "landscape_hd";
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of diffusion denoising steps. More steps generally produce higher quality images. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Output image format. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Text prompt for image generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. The same seed with the same prompt will produce the same image.
   */
  seed?: number;
  /**
   * If True, the image will be returned as a base64 data URI instead of a URL.
   */
  sync_mode?: boolean;
};
export type GlmImageToImageInput = {
  /**
   * If True, the prompt will be enhanced using an LLM for more detailed and higher quality results.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Enable NSFW safety checking on the generated images. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Higher values make the model follow the prompt more closely. Default value: `1.5`
   */
  guidance_scale?: number;
  /**
   * Output image size. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "portrait_3_2"
    | "landscape_3_2"
    | "portrait_hd"
    | "landscape_hd";
  /**
   * URL(s) of the condition image(s) for image-to-image generation. Supports up to 4 URLs for multi-image references.
   */
  image_urls: Array<string>;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of diffusion denoising steps. More steps generally produce higher quality images. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Output image format. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Text prompt for image generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. The same seed with the same prompt will produce the same image.
   */
  seed?: number;
  /**
   * If True, the image will be returned as a base64 data URI instead of a URL.
   */
  sync_mode?: boolean;
};
export type GlowInput = {
  /**
   * Glow intensity Default value: `1`
   */
  glow_intensity?: number;
  /**
   * Glow blur radius Default value: `5`
   */
  glow_radius?: number;
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
};
export type GoalForceInput = {
  /**
   * Enable safety checker for generated content. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Indirect goal force applied to a target object. The model generates physics-plausible interactions to move the target in the specified direction. Specify either this, projectile_force, or both.
   */
  goal_force?: ForceParams;
  /**
   * Classifier-free guidance scale. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL of the input image (first frame). The image will be resized to 832x480. Default value: `"https://raw.githubusercontent.com/brown-palm/goal-force/2d0209dc27333bffdb3f06df657ae05b11407615/datasets/examples/human-object-interaction/images/_bulb_tool.png"`
   */
  image_url?: string | Blob | File;
  /**
   * Negative prompt for generation. Default value: `"色调艳丽，过曝，静态，细节模糊不清，字幕，风格，作品，画作，画面，静止，整体发灰，最差质量，低质量，JPEG压缩残留，丑陋的，残缺的，多余的手指，画得不好的手部，画得不好的脸部，畸形的，毁容的，形态畸形的肢体，手指融合，静止不动的画面，杂乱的背景，三条腿，背景人很多，倒着走"`
   */
  negative_prompt?: string;
  /**
   * Number of denoising steps. Higher values produce better quality but take longer. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Direct force applied to a projectile object, causing it to move along the force direction. Specify either this, goal_force, or both.
   */
  projectile_force?: ForceParams;
  /**
   * Text description of the scene and the desired physics interaction. Default value: `"A hand pushes the hanging bulb, causing it to sway."`
   */
  prompt?: string;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
};
export type GoalForceOutput = {
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * Timing information.
   */
  timings: unknown;
  /**
   * The generated video.
   */
  video: VideoFile;
};
export type GotOcrV2Input = {
  /**
   * Generate the output in formatted mode.
   */
  do_format?: boolean;
  /**
   * URL of images.
   */
  input_image_urls?: Array<string>;
  /**
   * Use provided images to generate a single output.
   */
  multi_page?: boolean;
};
export type GotOcrV2Output = {
  /**
   * Generated output
   */
  outputs: Array<string>;
};
export type GptImage15EditInput = {
  /**
   * Background for the generated image Default value: `"auto"`
   */
  background?: "auto" | "transparent" | "opaque";
  /**
   * Aspect ratio for the generated image Default value: `"auto"`
   */
  image_size?: "auto" | "1024x1024" | "1536x1024" | "1024x1536";
  /**
   * The URLs of the images to use as a reference for the generation.
   */
  image_urls: Array<string>;
  /**
   * Input fidelity for the generated image Default value: `"high"`
   */
  input_fidelity?: "low" | "high";
  /**
   * The URL of the mask image to use for the generation. This indicates what part of the image to edit.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Output format for the images Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt for image generation
   */
  prompt: string;
  /**
   * Quality for the generated image Default value: `"high"`
   */
  quality?: "low" | "medium" | "high";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type GptImage15Input = {
  /**
   * Background for the generated image Default value: `"auto"`
   */
  background?: "auto" | "transparent" | "opaque";
  /**
   * Aspect ratio for the generated image Default value: `"1024x1024"`
   */
  image_size?: "1024x1024" | "1536x1024" | "1024x1536";
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Output format for the images Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt for image generation
   */
  prompt: string;
  /**
   * Quality for the generated image Default value: `"high"`
   */
  quality?: "low" | "medium" | "high";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type GptImage1EditImageInput = {
  /**
   * Background for the generated image Default value: `"auto"`
   */
  background?: "auto" | "transparent" | "opaque";
  /**
   * Aspect ratio for the generated image Default value: `"auto"`
   */
  image_size?: "auto" | "1024x1024" | "1536x1024" | "1024x1536";
  /**
   * The URLs of the images to use as a reference for the generation.
   */
  image_urls: Array<string>;
  /**
   * Input fidelity for the generated image Default value: `"high"`
   */
  input_fidelity?: "low" | "high";
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Output format for the images Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt for image generation
   */
  prompt: string;
  /**
   * Quality for the generated image Default value: `"auto"`
   */
  quality?: "auto" | "low" | "medium" | "high";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type GptImage1MiniEditInput = {
  /**
   * Background for the generated image Default value: `"auto"`
   */
  background?: "auto" | "transparent" | "opaque";
  /**
   * Aspect ratio for the generated image Default value: `"auto"`
   */
  image_size?: "auto" | "1024x1024" | "1536x1024" | "1024x1536";
  /**
   * The URLs of the images to use as a reference for the generation.
   */
  image_urls: Array<string>;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Output format for the images Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt for image generation
   */
  prompt: string;
  /**
   * Quality for the generated image Default value: `"auto"`
   */
  quality?: "auto" | "low" | "medium" | "high";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type GrainInput = {
  /**
   * Film grain intensity Default value: `0.4`
   */
  grain_intensity?: number;
  /**
   * Film grain scale Default value: `10`
   */
  grain_scale?: number;
  /**
   * Style of film grain to apply Default value: `"modern"`
   */
  grain_style?:
    | "modern"
    | "analog"
    | "kodak"
    | "fuji"
    | "cinematic"
    | "newspaper";
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
};
export type GreenScreenRembgInput = {
  /**
   * Single VP9 video with alpha channel or two videos (rgb and alpha) in H264 format. H264 is recommended for better RGB quality. Default value: `"vp9"`
   */
  output_codec?: "vp9" | "h264";
  /**
   * Increase the value if green spots remain in the video, decrease if color changes are noticed on the extracted subject. Default value: `0.8`
   */
  spill_suppression_strength?: number;
  /**
   *
   */
  video_url: string | Blob | File;
};
export type GuidanceInput = {
  /**
   * The image that should be used as guidance, in base64 format, with the method defined in guidance_method_1. Accepted formats are jpeg, jpg, png, webp. Maximum file size 12MB. If more then one guidance method is used, all guidance images must be of the same aspect ratio, and this will be the aspect ratio of the generated results. If guidance_method_1 is selected, an image must be provided.
   */
  image_url: string | Blob | File;
  /**
   * Which guidance type you would like to include in the generation. Up to 4 guidance methods can be combined during a single inference. This parameter is optional.
   */
  method?:
    | "controlnet_canny"
    | "controlnet_depth"
    | "controlnet_recoloring"
    | "controlnet_color_grid";
  /**
   * Impact of the guidance. Default value: `1`
   */
  scale?: number;
};
export type HdrStyleInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale. Controls how closely the model follows the prompt. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The strength of the HDR style effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * The prompt to generate an HDR style image. The trigger word 'Hyp3rRe4list1c' will be automatically prepended.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. Same seed with same prompt will produce same result.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and won't be saved in history.
   */
  sync_mode?: boolean;
};
export type HeadshotInput = {
  /**
   * Aspect ratio for 4K output (default: 3:4 for portraits)
   */
  aspect_ratio?: AspectRatio;
  /**
   *  Default value: `"professional"`
   */
  background_style?: "professional" | "corporate" | "clean" | "gradient";
  /**
   * Portrait image URL to convert to professional headshot
   */
  image_url: string | Blob | File;
};
export type HEDInput = {
  /**
   * URL of the image to process
   */
  image_url: string | Blob | File;
  /**
   * Whether to use the safe version of the HED detector
   */
  safe?: boolean;
  /**
   * Whether to use the scribble version of the HED detector
   */
  scribble?: boolean;
};
export type HEDOutput = {
  /**
   * Image with lines detected using the HED detector
   */
  image: Image;
};
export type HeygenAvatar3DigitalTwinInput = {
  /**
   * Aspect ratio of the output video. Use '9:16' for portrait (vertical) videos, '16:9' for landscape, or '1:1' for square. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * URL of an audio file for the avatar to lip-sync to. When provided, the avatar uses this audio instead of text-to-speech.
   */
  audio_url?: string | Blob | File;
  /**
   * Character configuration for the video
   */
  character: Character;
  /**
   * Video resolution preset. Options: 360p, 480p, 540p, 720p, 1080p Default value: `"720p"`
   */
  resolution?: "360p" | "480p" | "540p" | "720p" | "1080p";
  /**
   * Voice configuration for the character
   */
  voice: TextVoice;
};
export type HeygenAvatar4ImageToVideoInput = {
  /**
   * Aspect ratio of the output video. Use '9:16' for portrait (vertical) videos, '16:9' for landscape, or '1:1' for square. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * URL of an audio file for the avatar to lip-sync to. When provided, overrides prompt and voice.
   */
  audio_url?: string | Blob | File;
  /**
   * Background configuration
   */
  background?: AvatarIVBackground;
  /**
   * Whether to add captions to the video
   */
  caption?: boolean;
  /**
   * Facial expression
   */
  expression?: string;
  /**
   * URL of the image to animate. The image should contain a clear face.
   */
  image_url: string | Blob | File;
  /**
   * The text the avatar will speak
   */
  prompt?: string;
  /**
   * Video resolution preset. Options: 360p, 480p, 540p, 720p, 1080p Default value: `"720p"`
   */
  resolution?: "360p" | "480p" | "540p" | "720p" | "1080p";
  /**
   * Talking style - 'stable' for minimal movement, 'expressive' for more animation Default value: `"stable"`
   */
  talking_style?: "stable" | "expressive";
  /**
   * Name of the voice to use for the avatar
   */
  voice?: string;
};
export type HeygenV2TranslateSpeedInput = {
  /**
   * Enable dynamic duration to enhance conversational fluidity between languages with different speaking rates Default value: `true`
   */
  enable_dynamic_duration?: boolean;
  /**
   * The target language to translate the video into
   */
  output_language:
    | "English"
    | "Spanish"
    | "French"
    | "Hindi"
    | "Italian"
    | "German"
    | "Polish"
    | "Portuguese"
    | "Chinese"
    | "Japanese"
    | "Dutch"
    | "Turkish"
    | "Korean"
    | "Danish"
    | "Arabic"
    | "Romanian"
    | "Mandarin"
    | "Filipino"
    | "Swedish"
    | "Indonesian"
    | "Ukrainian"
    | "Greek"
    | "Czech"
    | "Bulgarian"
    | "Malay"
    | "Slovak"
    | "Croatian"
    | "Tamil"
    | "Finnish"
    | "Russian"
    | "Afrikaans (South Africa)"
    | "Albanian (Albania)"
    | "Amharic (Ethiopia)"
    | "Arabic (Algeria)"
    | "Arabic (Bahrain)"
    | "Arabic (Egypt)"
    | "Arabic (Iraq)"
    | "Arabic (Jordan)"
    | "Arabic (Kuwait)"
    | "Arabic (Lebanon)"
    | "Arabic (Libya)"
    | "Arabic (Morocco)"
    | "Arabic (Oman)"
    | "Arabic (Qatar)"
    | "Arabic (Saudi Arabia)"
    | "Arabic (Syria)"
    | "Arabic (Tunisia)"
    | "Arabic (United Arab Emirates)"
    | "Arabic (Yemen)"
    | "Armenian (Armenia)"
    | "Azerbaijani (Latin, Azerbaijan)"
    | "Bangla (Bangladesh)"
    | "Basque"
    | "Bengali (India)"
    | "Bosnian (Bosnia and Herzegovina)"
    | "Bulgarian (Bulgaria)"
    | "Burmese (Myanmar)"
    | "Catalan"
    | "Chinese (Cantonese, Traditional)"
    | "Chinese (Jilu Mandarin, Simplified)"
    | "Chinese (Mandarin, Simplified)"
    | "Chinese (Northeastern Mandarin, Simplified)"
    | "Chinese (Southwestern Mandarin, Simplified)"
    | "Chinese (Taiwanese Mandarin, Traditional)"
    | "Chinese (Wu, Simplified)"
    | "Chinese (Zhongyuan Mandarin Henan, Simplified)"
    | "Chinese (Zhongyuan Mandarin Shaanxi, Simplified)"
    | "Croatian (Croatia)"
    | "Czech (Czechia)"
    | "Danish (Denmark)"
    | "Dutch (Belgium)"
    | "Dutch (Netherlands)"
    | "English (Australia)"
    | "English (Canada)"
    | "English (Hong Kong SAR)"
    | "English (India)"
    | "English (Ireland)"
    | "English (Kenya)"
    | "English (New Zealand)"
    | "English (Nigeria)"
    | "English (Philippines)"
    | "English (Singapore)"
    | "English (South Africa)"
    | "English (Tanzania)"
    | "English (UK)"
    | "English (United States)"
    | "Estonian (Estonia)"
    | "Filipino (Philippines)"
    | "Finnish (Finland)"
    | "French (Belgium)"
    | "French (Canada)"
    | "French (France)"
    | "French (Switzerland)"
    | "Galician"
    | "Georgian (Georgia)"
    | "German (Austria)"
    | "German (Germany)"
    | "German (Switzerland)"
    | "Greek (Greece)"
    | "Gujarati (India)"
    | "Hebrew (Israel)"
    | "Hindi (India)"
    | "Hungarian (Hungary)"
    | "Icelandic (Iceland)"
    | "Indonesian (Indonesia)"
    | "Irish (Ireland)"
    | "Italian (Italy)"
    | "Japanese (Japan)"
    | "Javanese (Latin, Indonesia)"
    | "Kannada (India)"
    | "Kazakh (Kazakhstan)"
    | "Khmer (Cambodia)"
    | "Korean (Korea)"
    | "Lao (Laos)"
    | "Latvian (Latvia)"
    | "Lithuanian (Lithuania)"
    | "Macedonian (North Macedonia)"
    | "Malay (Malaysia)"
    | "Malayalam (India)"
    | "Maltese (Malta)"
    | "Marathi (India)"
    | "Mongolian (Mongolia)"
    | "Nepali (Nepal)"
    | "Norwegian Bokmål (Norway)"
    | "Pashto (Afghanistan)"
    | "Persian (Iran)"
    | "Polish (Poland)"
    | "Portuguese (Brazil)"
    | "Portuguese (Portugal)"
    | "Romanian (Romania)"
    | "Russian (Russia)"
    | "Serbian (Latin, Serbia)"
    | "Sinhala (Sri Lanka)"
    | "Slovak (Slovakia)"
    | "Slovenian (Slovenia)"
    | "Somali (Somalia)"
    | "Spanish (Argentina)"
    | "Spanish (Bolivia)"
    | "Spanish (Chile)"
    | "Spanish (Colombia)"
    | "Spanish (Costa Rica)"
    | "Spanish (Cuba)"
    | "Spanish (Dominican Republic)"
    | "Spanish (Ecuador)"
    | "Spanish (El Salvador)"
    | "Spanish (Equatorial Guinea)"
    | "Spanish (Guatemala)"
    | "Spanish (Honduras)"
    | "Spanish (Mexico)"
    | "Spanish (Nicaragua)"
    | "Spanish (Panama)"
    | "Spanish (Paraguay)"
    | "Spanish (Peru)"
    | "Spanish (Puerto Rico)"
    | "Spanish (Spain)"
    | "Spanish (United States)"
    | "Spanish (Uruguay)"
    | "Spanish (Venezuela)"
    | "Sundanese (Indonesia)"
    | "Swahili (Kenya)"
    | "Swahili (Tanzania)"
    | "Swedish (Sweden)"
    | "Tamil (India)"
    | "Tamil (Malaysia)"
    | "Tamil (Singapore)"
    | "Tamil (Sri Lanka)"
    | "Telugu (India)"
    | "Thai (Thailand)"
    | "Turkish (Türkiye)"
    | "Ukrainian (Ukraine)"
    | "Urdu (India)"
    | "Urdu (Pakistan)"
    | "Uzbek (Latin, Uzbekistan)"
    | "Vietnamese (Vietnam)"
    | "Welsh (United Kingdom)"
    | "Zulu (South Africa)"
    | "English - Your Accent"
    | "English - American Accent";
  /**
   * Number of speakers in the video
   */
  speaker_num?: number;
  /**
   * Translate only the audio, ignore the faces and only translate the voice track
   */
  translate_audio_only?: boolean;
  /**
   * URL of the video to translate.
   */
  video_url: string | Blob | File;
};
export type HeygenV2VideoAgentInput = {
  /**
   * Video configuration options
   */
  config?: VideoAgentConfig;
  /**
   * Natural language prompt describing the video to generate. Include details about style, visual elements, and desired length for best results.
   */
  prompt: string;
};
export type HidreamE11Input = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your initial image when looking for a related image to show you. Default value: `2`
   */
  image_guidance_scale?: number;
  /**
   * URL of an input image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"low resolution, blur"`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The description of the target image after your edits have been made. Leave this blank to allow the model to use its own imagination.
   */
  target_image_description?: string;
};
export type HidreamI1DevInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type HidreamI1FullImageToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Setting to None uses the input image's size.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The image URL to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * A list of LoRAs to apply to the model. Each LoRA specifies its path, scale, and optional weight name.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Denoising strength for image-to-image generation. Default value: `0.75`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type HidreamI1FullInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * A list of LoRAs to apply to the model. Each LoRA specifies its path, scale, and optional weight name.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Hunyuan3DInput = {
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV21Output = {
  /**
   * Generated 3D object.
   */
  model_glb: File;
  /**
   * Generated 3D object with PBR materials.
   */
  model_glb_pbr?: File;
  /**
   * Generated 3D object assets zip.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type Hunyuan3dV2MultiViewInput = {
  /**
   * URL of image to use while generating the 3D model.
   */
  back_image_url: string | Blob | File;
  /**
   * URL of image to use while generating the 3D model.
   */
  front_image_url: string | Blob | File;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * URL of image to use while generating the 3D model.
   */
  left_image_url: string | Blob | File;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV2Output = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type Hunyuan3dV3ImageTo3dInput = {
  /**
   * Optional back view image URL for better 3D reconstruction.
   */
  back_image_url?: string | Blob | File;
  /**
   * Whether to enable PBR material generation. Does not take effect when generate_type is Geometry.
   */
  enable_pbr?: boolean;
  /**
   * Target face count. Range: 40000-1500000 Default value: `500000`
   */
  face_count?: number;
  /**
   * Generation type. Normal: textured model. LowPoly: polygon reduction. Geometry: white model without texture. Default value: `"Normal"`
   */
  generate_type?: "Normal" | "LowPoly" | "Geometry";
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * Optional left view image URL for better 3D reconstruction.
   */
  left_image_url?: string | Blob | File;
  /**
   * Polygon type. Only takes effect when GenerateType is LowPoly. Default value: `"triangle"`
   */
  polygon_type?: "triangle" | "quadrilateral";
  /**
   * Optional right view image URL for better 3D reconstruction.
   */
  right_image_url?: string | Blob | File;
};
export type Hunyuan3dV3TextTo3dInput = {
  /**
   * Whether to enable PBR material generation
   */
  enable_pbr?: boolean;
  /**
   * Target face count. Range: 40000-1500000 Default value: `500000`
   */
  face_count?: number;
  /**
   * Generation type. Normal: textured model. LowPoly: polygon reduction. Geometry: white model without texture. Default value: `"Normal"`
   */
  generate_type?: "Normal" | "LowPoly" | "Geometry";
  /**
   * Polygon type. Only takes effect when GenerateType is LowPoly. Default value: `"triangle"`
   */
  polygon_type?: "triangle" | "quadrilateral";
  /**
   * Text description of the 3D content to generate. Supports up to 1024 UTF-8 characters.
   */
  prompt: string;
};
export type HunyuanAvatarInput = {
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * The URL of the reference image.
   */
  image_url: string | Blob | File;
  /**
   * Number of video frames to generate at 25 FPS. If greater than the input audio length, it will capped to the length of the input audio. Default value: `129`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Random seed for generation.
   */
  seed?: number;
  /**
   * Text prompt describing the scene. Default value: `"A cat is singing."`
   */
  text?: string;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality. Default value: `true`
   */
  turbo_mode?: boolean;
};
export type HunyuanCustomInput = {
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Classifier-Free Guidance scale for the generation. Default value: `7.5`
   */
  cfg_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt for video generation. Default value: `"Aerial view, aerial view, overexposed, low quality, deformation, a poor composition, bad hands, bad teeth, bad eyes, bad limbs, distortion, blurring, text, subtitles, static, picture, black border."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `129`
   */
  num_frames?: number;
  /**
   * The number of inference steps to run. Lower gets faster results, higher gets better results. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Text prompt for video generation (max 500 characters).
   */
  prompt: string;
  /**
   * The resolution of the video to generate. 720p generations cost 1.5x more than 480p generations. Default value: `"512p"`
   */
  resolution?: "512p" | "720p";
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
};
export type HunyuanImageToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Controls how much the model adheres to the prompt. Higher values mean stricter adherence. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The desired size of the generated image. By default, attempts to preserve the original size.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of the Image for Image-to-Image
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to guide the image generation away from certain concepts. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of denoising steps. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The text prompt for image-to-image.
   */
  prompt: string;
  /**
   * Random seed for reproducible results. If None, a random seed is used.
   */
  seed?: number;
  /**
   * The strength to use for image-to-image. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.6`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Enable the refiner model for improved image quality.
   */
  use_refiner?: boolean;
  /**
   * Enable prompt enhancement for potentially better results. Default value: `true`
   */
  use_reprompt?: boolean;
};
export type HunyuanImageV3InstructEditInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Controls how much the model adheres to the prompt. Higher values mean stricter adherence. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The desired size of the generated image. If auto, image size will be determined by the model. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images to use as a reference for the generation. A maximum of 3 images are supported.
   */
  image_urls: Array<string>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The text prompt to generate an image from.
   */
  prompt: string;
  /**
   * Random seed for reproducible results. If None, a random seed is used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type HunyuanImageV3InstructTextToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Controls how much the model adheres to the prompt. Higher values mean stricter adherence. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The desired size of the generated image. If auto, image size will be determined by the model. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The text prompt to generate an image from.
   */
  prompt: string;
  /**
   * Random seed for reproducible results. If None, a random seed is used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type HunyuanImageV3TextToImageInput = {
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Controls how much the model adheres to the prompt. Higher values mean stricter adherence. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The desired size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to guide the image generation away from certain concepts. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of denoising steps. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The text prompt for image-to-image.
   */
  prompt: string;
  /**
   * Random seed for reproducible results. If None, a random seed is used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type HunyuanMotionInput = {
  /**
   * Motion duration in seconds (0.5-12.0). Default value: `5`
   */
  duration?: number;
  /**
   * Classifier-free guidance scale. Higher = more faithful to prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Output format: 'fbx' for animation files, 'dict' for raw JSON. Default value: `"fbx"`
   */
  output_format?: "fbx" | "dict";
  /**
   * Text prompt describing the motion to generate.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
};
export type HunyuanMotionOutput = {
  /**
   * Generated FBX animation file.
   */
  fbx_file?: File;
  /**
   * Generated motion data as JSON.
   */
  motion_json?: File;
  /**
   * Seed used for generation.
   */
  seed: number;
};
export type HunyuanPortraitInput = {
  /**
   * The URL of the source image.
   */
  image_url: string | Blob | File;
  /**
   * Random seed for generation. If None, a random seed will be used.
   */
  seed?: number;
  /**
   * Whether to use ArcFace for face recognition. Default value: `true`
   */
  use_arcface?: boolean;
  /**
   * The URL of the driving video.
   */
  video_url: string | Blob | File;
};
export type HunyuanTextToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Controls how much the model adheres to the prompt. Higher values mean stricter adherence. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The desired size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to guide the image generation away from certain concepts. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of denoising steps. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The text prompt to generate an image from.
   */
  prompt: string;
  /**
   * Random seed for reproducible results. If None, a random seed is used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Enable the refiner model for improved image quality.
   */
  use_refiner?: boolean;
  /**
   * Enable prompt enhancement for potentially better results. Default value: `true`
   */
  use_reprompt?: boolean;
};
export type HunyuanVideoFoleyInput = {
  /**
   * Guidance scale for audio generation. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * Negative prompt to avoid certain audio characteristics. Default value: `"noisy, harsh"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps for generation. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * Text description of the desired audio (optional).
   */
  text_prompt: string;
  /**
   * The URL of the video to generate audio for.
   */
  video_url: string | Blob | File;
};
export type HunyuanVideoImageToVideoInput = {
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Turning on I2V Stability reduces hallucination but also reduces motion.
   */
  i2v_stability?: boolean;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * The number of frames to generate. Default value: `"129"`
   */
  num_frames?: string;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The resolution of the video to generate. Default value: `"720p"`
   */
  resolution?: string;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
};
export type HunyuanVideoLoraInput = {
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of frames to generate. Default value: `"129"`
   */
  num_frames?: "129" | "85";
  /**
   * By default, generations are done with 35 steps. Pro mode does 55 steps which results in higher quality videos but will take more time and cost 2x more billing units.
   */
  pro_mode?: boolean;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The resolution of the video to generate. Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
};
export type HunyuanVideoLoraTrainingInput = {
  /**
   * The format of the archive. If not specified, the format will be inferred from the URL.
   */
  data_archive_format?: string;
  /**
   * Whether to generate captions for the images. Default value: `true`
   */
  do_caption?: boolean;
  /**
   * URL to zip archive with images. Try to use at least 4 images in general the more the better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image file it corresponds to.
   */
  images_data_url: string | Blob | File;
  /**
   * Learning rate to use for training. Default value: `0.0001`
   */
  learning_rate?: number;
  /**
   * Number of steps to train the LoRA on.
   */
  steps: number;
  /**
   * The trigger word to use. Default value: `""`
   */
  trigger_word?: string;
};
export type HunyuanVideoLoraVideoToVideoInput = {
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of frames to generate. Default value: `"129"`
   */
  num_frames?: "129" | "85";
  /**
   * By default, generations are done with 35 steps. Pro mode does 55 steps which results in higher quality videos but will take more time and cost 2x more billing units.
   */
  pro_mode?: boolean;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The resolution of the video to generate. Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * Strength of video-to-video Default value: `0.75`
   */
  strength?: number;
  /**
   * URL of the video
   */
  video_url: string | Blob | File;
};
export type HunyuanVideoV15ImageToVideoInput = {
  /**
   * The aspect ratio of the video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Enable prompt expansion to enhance the input prompt. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * URL of the reference image for image-to-video generation.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to guide what not to generate. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The resolution of the video. Default value: `"480p"`
   */
  resolution?: string;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
};
export type HunyuanVideoV15TextToVideoInput = {
  /**
   * The aspect ratio of the video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Enable prompt expansion to enhance the input prompt. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * The negative prompt to guide what not to generate. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video.
   */
  prompt: string;
  /**
   * The resolution of the video. Default value: `"480p"`
   */
  resolution?: string;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
};
export type HunyuanVideoVideoToVideoInput = {
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The number of frames to generate. Default value: `"129"`
   */
  num_frames?: "129" | "85";
  /**
   * By default, generations are done with 35 steps. Pro mode does 55 steps which results in higher quality videos but will take more time and cost 2x more billing units.
   */
  pro_mode?: boolean;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The resolution of the video to generate. Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * Strength for Video-to-Video Default value: `0.85`
   */
  strength?: number;
  /**
   * URL of the video input.
   */
  video_url: string | Blob | File;
};
export type HunyuanWorldImageToWorldInput = {
  /**
   * Classes to use for the world generation.
   */
  classes: string;
  /**
   * Whether to export DRC (Dynamic Resource Configuration).
   */
  export_drc?: boolean;
  /**
   * The URL of the image to convert to a world.
   */
  image_url: string | Blob | File;
  /**
   * Labels for the first foreground object.
   */
  labels_fg1: string;
  /**
   * Labels for the second foreground object.
   */
  labels_fg2: string;
};
export type HunyuanWorldImageToWorldOutput = {
  /**
   * The generated world.
   */
  world_file: File;
};
export type HunyuanWorldInput = {
  /**
   * The URL of the image to convert to a panorama.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to use for the panorama generation.
   */
  prompt: string;
};
export type HyWuEditInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Enable thinking mode. The model reasons about the edit before generating, producing higher quality results at the cost of longer inference time. Disable for faster results on straightforward edits. Default value: `true`
   */
  enable_thinking?: boolean;
  /**
   * The desired size of the generated image. If auto, image size will be determined by the model. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URLs of input images for editing. Typically 2 images: the base image and the reference image. Supports up to 3 images.
   */
  image_urls: Array<string>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of diffusion denoising steps. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The text prompt describing the desired edit. Supports both English and Chinese. Use specific instructions like 'Replace the clothing on figure 1 with the outfit from figure 2'.
   */
  prompt: string;
  /**
   * Random seed for reproducible results. If None, a random seed is used.
   */
  seed?: number;
  /**
   * If True, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type HyWuEditOutput = {
  /**
   * A list of the generated/edited images.
   */
  images: Array<Image>;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * Performance timing breakdown.
   */
  timings?: unknown;
};
export type I2VOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type IclightV2Input = {
  /**
   * Threshold for the background removal algorithm. A high threshold will produce sharper masks. Note: This parameter is currently deprecated and has no effect on the output. Default value: `0.67`
   */
  background_threshold?: number;
  /**
   * The real classifier-free-guidance scale for the generation. Default value: `1`
   */
  cfg?: number;
  /**
   * Use HR fix
   */
  enable_hr_fix?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Strength for high-resolution pass. Only used if enable_hr_fix is True. Default value: `0.95`
   */
  highres_denoise?: number;
  /**
   *  Default value: `0.5`
   */
  hr_downscale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * Provide lighting conditions for the model Default value: `"None"`
   */
  initial_latent?: "None" | "Left" | "Right" | "Top" | "Bottom";
  /**
   * Strength for low-resolution pass. Default value: `0.98`
   */
  lowres_denoise?: number;
  /**
   * URL of mask to be used for ic-light conditioning image
   */
  mask_image_url?: string | Blob | File;
  /**
   * Negative Prompt for the image Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdentifyFaceOutput = {
  /**
   * List of detected faces in the video with their time ranges.
   */
  face_data: Array<FaceData>;
  /**
   * The session id of the lip-sync task
   */
  session_id: string;
};
export type IdeogramCharacterEditInput = {
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The image URL to generate an image from. MUST have the exact same dimensions (width and height) as the mask image.
   */
  image_url: string | Blob | File;
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The mask URL to inpaint the image. MUST have the exact same dimensions (width and height) as the input image.
   */
  mask_url: string | Blob | File;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * A set of images to use as character references. Currently only 1 image is supported, rest will be ignored. (maximum total size 10MB across all character references). The images should be in JPEG, PNG or WebP format
   */
  reference_image_urls: Array<string>;
  /**
   * A set of masks to apply to the character references. Currently only 1 mask is supported, rest will be ignored. (maximum total size 10MB across all character references). The masks should be in JPEG, PNG or WebP format
   */
  reference_mask_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramCharacterInput = {
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The resolution of the generated image Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * Description of what to exclude from an image. Descriptions in the prompt take precedence to descriptions in the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * A set of images to use as character references. Currently only 1 image is supported, rest will be ignored. (maximum total size 10MB across all character references). The images should be in JPEG, PNG or WebP format
   */
  reference_image_urls: Array<string>;
  /**
   * A set of masks to apply to the character references. Currently only 1 mask is supported, rest will be ignored. (maximum total size 10MB across all character references). The masks should be in JPEG, PNG or WebP format
   */
  reference_mask_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style type to generate with. Cannot be used with style_codes. Default value: `"AUTO"`
   */
  style?: "AUTO" | "REALISTIC" | "FICTION";
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramCharacterRemixInput = {
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The resolution of the generated image Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * Description of what to exclude from an image. Descriptions in the prompt take precedence to descriptions in the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * A set of images to use as character references. Currently only 1 image is supported, rest will be ignored. (maximum total size 10MB across all character references). The images should be in JPEG, PNG or WebP format
   */
  reference_image_urls: Array<string>;
  /**
   * A set of masks to apply to the character references. Currently only 1 mask is supported, rest will be ignored. (maximum total size 10MB across all character references). The masks should be in JPEG, PNG or WebP format
   */
  reference_mask_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * The style type to generate with. Cannot be used with style_codes. Default value: `"AUTO"`
   */
  style?: "AUTO" | "REALISTIC" | "FICTION";
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramV2aInput = {
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   *
   */
  prompt: string;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramV2EditInput = {
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramV2Input = {
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * A negative prompt to avoid in the generated image Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramV3EditInput = {
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The image URL to generate an image from. MUST have the exact same dimensions (width and height) as the mask image.
   */
  image_url: string | Blob | File;
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The mask URL to inpaint the image. MUST have the exact same dimensions (width and height) as the input image.
   */
  mask_url: string | Blob | File;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * Style preset for generation. The chosen style preset will guide the generation.
   */
  style_preset?:
    | "80S_ILLUSTRATION"
    | "90S_NOSTALGIA"
    | "ABSTRACT_ORGANIC"
    | "ANALOG_NOSTALGIA"
    | "ART_BRUT"
    | "ART_DECO"
    | "ART_POSTER"
    | "AURA"
    | "AVANT_GARDE"
    | "BAUHAUS"
    | "BLUEPRINT"
    | "BLURRY_MOTION"
    | "BRIGHT_ART"
    | "C4D_CARTOON"
    | "CHILDRENS_BOOK"
    | "COLLAGE"
    | "COLORING_BOOK_I"
    | "COLORING_BOOK_II"
    | "CUBISM"
    | "DARK_AURA"
    | "DOODLE"
    | "DOUBLE_EXPOSURE"
    | "DRAMATIC_CINEMA"
    | "EDITORIAL"
    | "EMOTIONAL_MINIMAL"
    | "ETHEREAL_PARTY"
    | "EXPIRED_FILM"
    | "FLAT_ART"
    | "FLAT_VECTOR"
    | "FOREST_REVERIE"
    | "GEO_MINIMALIST"
    | "GLASS_PRISM"
    | "GOLDEN_HOUR"
    | "GRAFFITI_I"
    | "GRAFFITI_II"
    | "HALFTONE_PRINT"
    | "HIGH_CONTRAST"
    | "HIPPIE_ERA"
    | "ICONIC"
    | "JAPANDI_FUSION"
    | "JAZZY"
    | "LONG_EXPOSURE"
    | "MAGAZINE_EDITORIAL"
    | "MINIMAL_ILLUSTRATION"
    | "MIXED_MEDIA"
    | "MONOCHROME"
    | "NIGHTLIFE"
    | "OIL_PAINTING"
    | "OLD_CARTOONS"
    | "PAINT_GESTURE"
    | "POP_ART"
    | "RETRO_ETCHING"
    | "RIVIERA_POP"
    | "SPOTLIGHT_80S"
    | "STYLIZED_RED"
    | "SURREAL_COLLAGE"
    | "TRAVEL_POSTER"
    | "VINTAGE_GEO"
    | "VINTAGE_POSTER"
    | "WATERCOLOR"
    | "WEIRD"
    | "WOODBLOCK_PRINT";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramV3Input = {
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The resolution of the generated image Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * Description of what to exclude from an image. Descriptions in the prompt take precedence to descriptions in the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   *
   */
  prompt: string;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style type to generate with. Cannot be used with style_codes.
   */
  style?: "AUTO" | "GENERAL" | "REALISTIC" | "DESIGN";
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * Style preset for generation. The chosen style preset will guide the generation.
   */
  style_preset?:
    | "80S_ILLUSTRATION"
    | "90S_NOSTALGIA"
    | "ABSTRACT_ORGANIC"
    | "ANALOG_NOSTALGIA"
    | "ART_BRUT"
    | "ART_DECO"
    | "ART_POSTER"
    | "AURA"
    | "AVANT_GARDE"
    | "BAUHAUS"
    | "BLUEPRINT"
    | "BLURRY_MOTION"
    | "BRIGHT_ART"
    | "C4D_CARTOON"
    | "CHILDRENS_BOOK"
    | "COLLAGE"
    | "COLORING_BOOK_I"
    | "COLORING_BOOK_II"
    | "CUBISM"
    | "DARK_AURA"
    | "DOODLE"
    | "DOUBLE_EXPOSURE"
    | "DRAMATIC_CINEMA"
    | "EDITORIAL"
    | "EMOTIONAL_MINIMAL"
    | "ETHEREAL_PARTY"
    | "EXPIRED_FILM"
    | "FLAT_ART"
    | "FLAT_VECTOR"
    | "FOREST_REVERIE"
    | "GEO_MINIMALIST"
    | "GLASS_PRISM"
    | "GOLDEN_HOUR"
    | "GRAFFITI_I"
    | "GRAFFITI_II"
    | "HALFTONE_PRINT"
    | "HIGH_CONTRAST"
    | "HIPPIE_ERA"
    | "ICONIC"
    | "JAPANDI_FUSION"
    | "JAZZY"
    | "LONG_EXPOSURE"
    | "MAGAZINE_EDITORIAL"
    | "MINIMAL_ILLUSTRATION"
    | "MIXED_MEDIA"
    | "MONOCHROME"
    | "NIGHTLIFE"
    | "OIL_PAINTING"
    | "OLD_CARTOONS"
    | "PAINT_GESTURE"
    | "POP_ART"
    | "RETRO_ETCHING"
    | "RIVIERA_POP"
    | "SPOTLIGHT_80S"
    | "STYLIZED_RED"
    | "SURREAL_COLLAGE"
    | "TRAVEL_POSTER"
    | "VINTAGE_GEO"
    | "VINTAGE_POSTER"
    | "WATERCOLOR"
    | "WEIRD"
    | "WOODBLOCK_PRINT";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramV3ReframeInput = {
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * The resolution for the reframed output image
   */
  image_size:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The image URL to reframe
   */
  image_url: string | Blob | File;
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style type to generate with. Cannot be used with style_codes.
   */
  style?: "AUTO" | "GENERAL" | "REALISTIC" | "DESIGN";
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * Style preset for generation. The chosen style preset will guide the generation.
   */
  style_preset?:
    | "80S_ILLUSTRATION"
    | "90S_NOSTALGIA"
    | "ABSTRACT_ORGANIC"
    | "ANALOG_NOSTALGIA"
    | "ART_BRUT"
    | "ART_DECO"
    | "ART_POSTER"
    | "AURA"
    | "AVANT_GARDE"
    | "BAUHAUS"
    | "BLUEPRINT"
    | "BLURRY_MOTION"
    | "BRIGHT_ART"
    | "C4D_CARTOON"
    | "CHILDRENS_BOOK"
    | "COLLAGE"
    | "COLORING_BOOK_I"
    | "COLORING_BOOK_II"
    | "CUBISM"
    | "DARK_AURA"
    | "DOODLE"
    | "DOUBLE_EXPOSURE"
    | "DRAMATIC_CINEMA"
    | "EDITORIAL"
    | "EMOTIONAL_MINIMAL"
    | "ETHEREAL_PARTY"
    | "EXPIRED_FILM"
    | "FLAT_ART"
    | "FLAT_VECTOR"
    | "FOREST_REVERIE"
    | "GEO_MINIMALIST"
    | "GLASS_PRISM"
    | "GOLDEN_HOUR"
    | "GRAFFITI_I"
    | "GRAFFITI_II"
    | "HALFTONE_PRINT"
    | "HIGH_CONTRAST"
    | "HIPPIE_ERA"
    | "ICONIC"
    | "JAPANDI_FUSION"
    | "JAZZY"
    | "LONG_EXPOSURE"
    | "MAGAZINE_EDITORIAL"
    | "MINIMAL_ILLUSTRATION"
    | "MIXED_MEDIA"
    | "MONOCHROME"
    | "NIGHTLIFE"
    | "OIL_PAINTING"
    | "OLD_CARTOONS"
    | "PAINT_GESTURE"
    | "POP_ART"
    | "RETRO_ETCHING"
    | "RIVIERA_POP"
    | "SPOTLIGHT_80S"
    | "STYLIZED_RED"
    | "SURREAL_COLLAGE"
    | "TRAVEL_POSTER"
    | "VINTAGE_GEO"
    | "VINTAGE_POSTER"
    | "WATERCOLOR"
    | "WEIRD"
    | "WOODBLOCK_PRINT";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramV3RemixInput = {
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The resolution of the generated image Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * Description of what to exclude from an image. Descriptions in the prompt take precedence to descriptions in the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * The style type to generate with. Cannot be used with style_codes.
   */
  style?: "AUTO" | "GENERAL" | "REALISTIC" | "DESIGN";
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IdeogramV3ReplaceBackgroundInput = {
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The image URL whose background needs to be replaced
   */
  image_url: string | Blob | File;
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Cyber punk city with neon lights and skyscrappers
   */
  prompt: string;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style type to generate with. Cannot be used with style_codes.
   */
  style?: "AUTO" | "GENERAL" | "REALISTIC" | "DESIGN";
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * Style preset for generation. The chosen style preset will guide the generation.
   */
  style_preset?:
    | "80S_ILLUSTRATION"
    | "90S_NOSTALGIA"
    | "ABSTRACT_ORGANIC"
    | "ANALOG_NOSTALGIA"
    | "ART_BRUT"
    | "ART_DECO"
    | "ART_POSTER"
    | "AURA"
    | "AVANT_GARDE"
    | "BAUHAUS"
    | "BLUEPRINT"
    | "BLURRY_MOTION"
    | "BRIGHT_ART"
    | "C4D_CARTOON"
    | "CHILDRENS_BOOK"
    | "COLLAGE"
    | "COLORING_BOOK_I"
    | "COLORING_BOOK_II"
    | "CUBISM"
    | "DARK_AURA"
    | "DOODLE"
    | "DOUBLE_EXPOSURE"
    | "DRAMATIC_CINEMA"
    | "EDITORIAL"
    | "EMOTIONAL_MINIMAL"
    | "ETHEREAL_PARTY"
    | "EXPIRED_FILM"
    | "FLAT_ART"
    | "FLAT_VECTOR"
    | "FOREST_REVERIE"
    | "GEO_MINIMALIST"
    | "GLASS_PRISM"
    | "GOLDEN_HOUR"
    | "GRAFFITI_I"
    | "GRAFFITI_II"
    | "HALFTONE_PRINT"
    | "HIGH_CONTRAST"
    | "HIPPIE_ERA"
    | "ICONIC"
    | "JAPANDI_FUSION"
    | "JAZZY"
    | "LONG_EXPOSURE"
    | "MAGAZINE_EDITORIAL"
    | "MINIMAL_ILLUSTRATION"
    | "MIXED_MEDIA"
    | "MONOCHROME"
    | "NIGHTLIFE"
    | "OIL_PAINTING"
    | "OLD_CARTOONS"
    | "PAINT_GESTURE"
    | "POP_ART"
    | "RETRO_ETCHING"
    | "RIVIERA_POP"
    | "SPOTLIGHT_80S"
    | "STYLIZED_RED"
    | "SURREAL_COLLAGE"
    | "TRAVEL_POSTER"
    | "VINTAGE_GEO"
    | "VINTAGE_POSTER"
    | "WATERCOLOR"
    | "WEIRD"
    | "WOODBLOCK_PRINT";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type IllusionDiffusionInput = {
  /**
   *  Default value: `1`
   */
  control_guidance_end?: number;
  /**
   *
   */
  control_guidance_start?: number;
  /**
   * The scale of the ControlNet. Default value: `1`
   */
  controlnet_conditioning_scale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. You can choose between some presets or
   * custom height and width that **must be multiples of 8**. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Input image url.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps
   * to generate your final result which can increase the amount of detail in your image. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * Scheduler / sampler to use for the image denoising process. Default value: `"Euler"`
   */
  scheduler?: "DPM++ Karras SDE" | "Euler";
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed?: number;
};
export type Image = {
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * File data
   */
  file_data?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The height of the image in pixels.
   */
  height?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
  /**
   * The width of the image in pixels.
   */
  width?: number;
};
export type image2pixelInput = {
  /**
   * Alpha binarization threshold (0-255). Default value: `128`
   */
  alpha_threshold?: number;
  /**
   * Enable automatic detection of optimal number of colors.
   */
  auto_color_detect?: boolean;
  /**
   * Controls where to flood-fill from when removing the background. Default value: `"corners"`
   */
  background_mode?: "edges" | "corners" | "midpoints";
  /**
   * Background tolerance (0-255).
   */
  background_tolerance?: number;
  /**
   * Remove isolated diagonal pixels (jaggy edge cleanup).
   */
  cleanup_jaggy?: boolean;
  /**
   * Apply morphological operations to remove noise.
   */
  cleanup_morph?: boolean;
  /**
   * Scale detection method to use. Default value: `"auto"`
   */
  detect_method?: "auto" | "runs" | "edge";
  /**
   * Dominant color threshold (0.0-1.0). Default value: `0.05`
   */
  dominant_color_threshold?: number;
  /**
   * Downscaling method to produce the pixel-art output. Default value: `"dominant"`
   */
  downscale_method?:
    | "dominant"
    | "median"
    | "mode"
    | "mean"
    | "content-adaptive";
  /**
   * Optional fixed color palette as hex strings (e.g., ['#000000', '#ffffff']).
   */
  fixed_palette?: Array<string>;
  /**
   * The image URL to process into improved pixel art
   */
  image_url: string | Blob | File;
  /**
   * Maximum number of colors in the output palette. Set None to disable limit. Default value: `32`
   */
  max_colors?: number;
  /**
   * Force a specific pixel scale. If None, auto-detect.
   */
  scale?: number;
  /**
   * Align output to the pixel grid. Default value: `true`
   */
  snap_grid?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Remove background of the image. This will check for contiguous color regions from the edges after correction and make them transparent.
   */
  transparent_background?: boolean;
  /**
   * Trim borders of the image.
   */
  trim_borders?: boolean;
};
export type image2pixelOutput = {
  /**
   * The processed pixel-art image (PNG) and the scaled image (PNG).
   */
  images: Array<ImageFile>;
  /**
   * The number of colors in the processed media.
   */
  num_colors: number;
  /**
   * The palette of the processed media.
   */
  palette: Array<string>;
  /**
   * The detected pixel scale of the input.
   */
  pixel_scale: number;
};
export type image2svgInput = {
  /**
   * Color quantization level Default value: `6`
   */
  color_precision?: number;
  /**
   * Choose between color or binary (black and white) output Default value: `"color"`
   */
  colormode?: "color" | "binary";
  /**
   * Corner detection threshold in degrees Default value: `60`
   */
  corner_threshold?: number;
  /**
   * Filter out small speckles and noise Default value: `4`
   */
  filter_speckle?: number;
  /**
   * Hierarchical mode: stacked or cutout Default value: `"stacked"`
   */
  hierarchical?: "stacked" | "cutout";
  /**
   * The image to convert to SVG
   */
  image_url: string | Blob | File;
  /**
   * Layer difference threshold for hierarchical mode Default value: `16`
   */
  layer_difference?: number;
  /**
   * Length threshold for curves/lines Default value: `4`
   */
  length_threshold?: number;
  /**
   * Maximum number of iterations for optimization Default value: `10`
   */
  max_iterations?: number;
  /**
   * Mode: spline (curved) or polygon (straight lines) Default value: `"spline"`
   */
  mode?: "spline" | "polygon";
  /**
   * Decimal precision for path coordinates Default value: `3`
   */
  path_precision?: number;
  /**
   * Splice threshold for joining paths Default value: `45`
   */
  splice_threshold?: number;
};
export type ImageAppsV2ExpressionChangeInput = {
  /**
   * Aspect ratio for 4K output (default: 3:4 for portraits)
   */
  aspect_ratio?: AspectRatio;
  /**
   * Portrait image URL for expression change
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"smile"`
   */
  target_expression?:
    | "smile"
    | "surprise"
    | "glare"
    | "panic"
    | "shyness"
    | "laugh"
    | "cry"
    | "angry"
    | "sad"
    | "happy"
    | "excited"
    | "shocked"
    | "confused"
    | "focused"
    | "dreamy"
    | "serious"
    | "playful"
    | "mysterious"
    | "confident"
    | "thoughtful";
};
export type ImageAppsV2HairChangeInput = {
  /**
   * Aspect ratio for 4K output (default: 3:4 for portraits)
   */
  aspect_ratio?: AspectRatio;
  /**
   *  Default value: `"natural"`
   */
  hair_color?:
    | "black"
    | "dark_brown"
    | "light_brown"
    | "blonde"
    | "platinum_blonde"
    | "red"
    | "auburn"
    | "gray"
    | "silver"
    | "blue"
    | "green"
    | "purple"
    | "pink"
    | "rainbow"
    | "natural"
    | "highlights"
    | "ombre"
    | "balayage";
  /**
   * Portrait image URL for hair change
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"long_hair"`
   */
  target_hairstyle?:
    | "short_hair"
    | "medium_long_hair"
    | "long_hair"
    | "curly_hair"
    | "wavy_hair"
    | "high_ponytail"
    | "bun"
    | "bob_cut"
    | "pixie_cut"
    | "braids"
    | "straight_hair"
    | "afro"
    | "dreadlocks"
    | "buzz_cut"
    | "mohawk"
    | "bangs"
    | "side_part"
    | "middle_part";
};
export type ImageAppsV2ObjectRemovalInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   * Image URL containing object to remove
   */
  image_url: string | Blob | File;
  /**
   * Object to remove
   */
  object_to_remove: string;
};
export type ImageAppsV2PhotoRestorationInput = {
  /**
   * Aspect ratio for 4K output (default: 4:3 for classic photos)
   */
  aspect_ratio?: AspectRatio;
  /**
   *  Default value: `true`
   */
  enhance_resolution?: boolean;
  /**
   *  Default value: `true`
   */
  fix_colors?: boolean;
  /**
   * Old or damaged photo URL to restore
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `true`
   */
  remove_scratches?: boolean;
};
export type ImageAppsV2StyleTransferInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   * Image URL for style transfer
   */
  image_url: string | Blob | File;
  /**
   * Optional reference image URL. When provided, the style will be inferred from this image instead of the selected preset style.
   */
  style_reference_image_url?: string | Blob | File;
  /**
   *  Default value: `"impressionist"`
   */
  target_style?:
    | "anime_character"
    | "cartoon_3d"
    | "hand_drawn_animation"
    | "cyberpunk_future"
    | "anime_game_style"
    | "comic_book_animation"
    | "animated_series"
    | "cartoon_animation"
    | "lofi_aesthetic"
    | "cottagecore"
    | "dark_academia"
    | "y2k"
    | "vaporwave"
    | "liminal_space"
    | "weirdcore"
    | "dreamcore"
    | "synthwave"
    | "outrun"
    | "photorealistic"
    | "hyperrealistic"
    | "digital_art"
    | "concept_art"
    | "impressionist"
    | "anime"
    | "pixel_art"
    | "claymation";
};
export type ImageChatOutput = {
  /**
   * Dictionary of label: mask image
   */
  masks: Array<Image>;
  /**
   * Generated output
   */
  output: string;
};
export type ImageCondition = {
  /**
   * The URL of the image to use as input.
   */
  image_url: string | Blob | File;
  /**
   * The frame number to start the condition on.
   */
  start_frame_number?: number;
  /**
   * The strength of the condition. Default value: `1`
   */
  strength?: number;
};
export type ImageConditioningInput = {
  /**
   * URL of image to use as conditioning
   */
  image_url: string | Blob | File;
  /**
   * Frame number of the image from which the conditioning starts. Must be a multiple of 8.
   */
  start_frame_num?: number;
  /**
   * Strength of the conditioning. 0.0 means no conditioning, 1.0 means full conditioning. Default value: `1`
   */
  strength?: number;
};
export type ImageEditInput = {
  /**
   * Enable LLM prompt optimization. Significantly improves results for simple prompts but adds 3-4 seconds processing time. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output image size. Use presets like 'square_hd', 'landscape_16_9', 'portrait_9_16', or specify exact dimensions with ImageSize(width=1280, height=720). Total pixels must be between 768*768 and 1280*1280. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Reference images for editing (1-3 images required). Order matters: reference as 'image 1', 'image 2', 'image 3' in prompt. Resolution: 384-5000px each dimension. Max size: 10MB each. Formats: JPEG, JPG, PNG (no alpha), BMP, WEBP.
   */
  image_urls: Array<string>;
  /**
   * Content to avoid in the generated image. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate (1-4). Directly affects billing cost. Default value: `1`
   */
  num_images?: number;
  /**
   * Text prompt describing the desired image. Supports Chinese and English. Max 2000 characters. Example: 'Generate an image using the style of image 1 and background of image 2'.
   */
  prompt: string;
  /**
   * Random seed for reproducibility (0-2147483647). Same seed produces more consistent results.
   */
  seed?: number;
};
export type ImageFile = {
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The height of the image
   */
  height?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
  /**
   * The width of the image
   */
  width?: number;
};
export type ImageFillInput = {
  /**
   * URLs of images to be filled into the masked area.
   */
  fill_image_url?: Array<string | Blob | File> | string | Blob | File;
  /**
   * Uses the provided fill image in context with the base image to fill in more faithfully. Will increase price.
   */
  in_context_fill?: boolean;
  /**
   * Whether to use the prompt as well in the generation, along with the redux image.
   */
  use_prompt?: boolean;
};
export type ImageGridInput = {
  /**
   * Background color for empty cells and spacing Default value: `"white"`
   */
  background_color?:
    | "white"
    | "black"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta"
    | "transparent";
  /**
   * Height of each cell in pixels (if not set, uses first image height)
   */
  cell_height?: number;
  /**
   * Width of each cell in pixels (if not set, uses first image width)
   */
  cell_width?: number;
  /**
   * Number of columns in the grid Default value: `2`
   */
  columns?: number;
  /**
   * How images fit in cells Default value: `"cover"`
   */
  fit_mode?: "cover" | "contain" | "stretch";
  /**
   * List of image URLs to arrange in grid
   */
  image_urls: Array<string>;
  /**
   * Output format for the grid image Default value: `"png"`
   */
  output_format?: "png" | "jpg" | "jpeg" | "webp";
  /**
   * Spacing between cells in pixels
   */
  spacing?: number;
};
export type ImageInput = {
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * File data
   */
  file_data?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The height of the image in pixels.
   */
  height?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
  /**
   * The width of the image in pixels.
   */
  width?: number;
};
export type imagen3Input = {
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "3:4" | "4:3";
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt describing what you want to see
   */
  prompt: string;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type imagen3Output = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type Imagen4PreviewFastInput = {
  /**
   * The aspect ratio of the generated image. Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "4:3" | "3:4";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The text prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Imagen4PreviewInput = {
  /**
   * The aspect ratio of the generated image. Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "4:3" | "3:4";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The text prompt to generate an image from.
   */
  prompt: string;
  /**
   * The resolution of the generated image. Default value: `"1K"`
   */
  resolution?: "1K" | "2K";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ImageOutput = {
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The height of the image in pixels.
   */
  height?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
  /**
   * The width of the image in pixels.
   */
  width?: number;
};
export type ImagePrompt = {
  /**
   *
   */
  image_url?: string | Blob | File;
  /**
   *  Default value: `0.5`
   */
  stop_at?: number;
  /**
   *  Default value: `"ImagePrompt"`
   */
  type?: "ImagePrompt" | "PyraCanny" | "CPDS" | "FaceSwap";
  /**
   *  Default value: `1`
   */
  weight?: number;
};
export type ImageSize = {
  /**
   * The height of the generated image. Default value: `512`
   */
  height?: number;
  /**
   * The width of the generated image. Default value: `512`
   */
  width?: number;
};
export type ImageTo3dInput = {
  /**
   * Automatically scale the model to real-world dimensions, with the unit in meters. The default value is False.
   */
  auto_size?: boolean;
  /**
   * Limits the number of faces on the output model. If this option is not set, the face limit will be adaptively determined.
   */
  face_limit?: number;
  /**
   * URL of the image to use for model generation.
   */
  image_url: string | Blob | File;
  /**
   * Set orientation=align_image to automatically rotate the model to align the original image. The default value is default. Default value: `"default"`
   */
  orientation?: "default" | "align_image";
  /**
   * A boolean option to enable pbr. The default value is True, set False to get a model without pbr. If this option is set to True, texture will be ignored and used as True.
   */
  pbr?: boolean;
  /**
   * Set True to enable quad mesh output (extra $0.05 per generation). If quad=True and face_limit is not set, the default face_limit will be 10000. Note: Enabling this option will force the output to be an FBX model.
   */
  quad?: boolean;
  /**
   * This is the random seed for model generation. The seed controls the geometry generation process, ensuring identical models when the same seed is used. This parameter is an integer and is randomly chosen if not set.
   */
  seed?: number;
  /**
   * An option to enable texturing. Default is 'standard', set 'no' to get a model without any textures, and set 'HD' to get a model with hd quality textures. Default value: `"standard"`
   */
  texture?: "no" | "standard" | "HD";
  /**
   * Determines the prioritization of texture alignment in the 3D model. The default value is original_image. Default value: `"original_image"`
   */
  texture_alignment?: "original_image" | "geometry";
  /**
   * This is the random seed for texture generation. Using the same seed will produce identical textures. This parameter is an integer and is randomly chosen if not set. If you want a model with different textures, please use same seed and different texture_seed.
   */
  texture_seed?: number;
};
export type ImageTo3DInput = {
  /**
   * Animation preset ID from Meshy's library (500+ presets). Only used when enable_animation is true. See https://docs.meshy.ai/en/api/animation-library for available action IDs. Default value: `1001`
   */
  animation_action_id?: number;
  /**
   * Apply an animation preset to the rigged model. Requires enable_rigging to be true.
   */
  enable_animation?: boolean;
  /**
   * Generate PBR Maps (metallic, roughness, normal) in addition to base color
   */
  enable_pbr?: boolean;
  /**
   * Automatically rig the generated model as a humanoid character. Includes basic walking and running animations. Best results with humanoid characters that have clearly defined limbs.
   */
  enable_rigging?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Image URL or base64 data URI for 3D model creation. Supports .jpg, .jpeg, and .png formats. Also supports AVIF and HEIF formats which will be automatically converted.
   */
  image_url: string | Blob | File;
  /**
   * Pose mode for the generated model. 'a-pose' generates an A-pose, 't-pose' generates a T-pose, empty string for no specific pose. Default value: `""`
   */
  pose_mode?: "a-pose" | "t-pose" | "";
  /**
   * Approximate height of the character in meters. Only used when enable_rigging is true. Default value: `1.7`
   */
  rigging_height_meters?: number;
  /**
   * Whether to enable the remesh phase Default value: `true`
   */
  should_remesh?: boolean;
  /**
   * Whether to generate textures Default value: `true`
   */
  should_texture?: boolean;
  /**
   * Controls symmetry behavior during model generation. Off disables symmetry, Auto determines it automatically, On enforces symmetry. Default value: `"auto"`
   */
  symmetry_mode?: "off" | "auto" | "on";
  /**
   * Target number of polygons in the generated model Default value: `30000`
   */
  target_polycount?: number;
  /**
   * 2D image to guide the texturing process
   */
  texture_image_url?: string | Blob | File;
  /**
   * Text prompt to guide the texturing process
   */
  texture_prompt?: string;
  /**
   * Specify the topology of the generated model. Quad for smooth surfaces, Triangle for detailed geometry. Default value: `"triangle"`
   */
  topology?: "quad" | "triangle";
};
export type ImageTo3DOutput = {
  /**
   * Animated 3D model in FBX format. Only present when enable_animation is true.
   */
  animation_fbx?: File;
  /**
   * Animated 3D model in GLB format. Only present when enable_animation is true.
   */
  animation_glb?: File;
  /**
   * Basic walking and running animations. Only present when enable_rigging is true.
   */
  basic_animations?: BasicAnimations;
  /**
   * Generated 3D object in GLB format.
   */
  model_glb: File;
  /**
   * URLs for different 3D model formats
   */
  model_urls: ModelUrls;
  /**
   * Rigging task ID. Only present when enable_rigging is true.
   */
  rig_task_id?: string;
  /**
   * Rigged character in FBX format. Only present when enable_rigging is true.
   */
  rigged_character_fbx?: File;
  /**
   * Rigged character in GLB format. Only present when enable_rigging is true.
   */
  rigged_character_glb?: File;
  /**
   * The seed used for generation (if available)
   */
  seed?: number;
  /**
   * Array of texture file objects, matching Meshy API structure
   */
  texture_urls?: Array<TextureFiles>;
  /**
   * Preview thumbnail of the generated model
   */
  thumbnail?: File;
};
export type ImageToImageControlNetInput = {
  /**
   * The URL of the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * The scale of the controlnet conditioning. Default value: `0.5`
   */
  controlnet_conditioning_scale?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Leave it none to automatically infer from the control image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageToImageControlNetUnionInput = {
  /**
   * The URL of the control image.
   */
  canny_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the canny image. Default value: `true`
   */
  canny_preprocess?: boolean;
  /**
   * The scale of the controlnet conditioning. Default value: `0.5`
   */
  controlnet_conditioning_scale?: number;
  /**
   * If set to true, the output cropped to the proper aspect ratio after generating.
   */
  crop_output?: boolean;
  /**
   * The URL of the control image.
   */
  depth_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the depth image. Default value: `true`
   */
  depth_preprocess?: boolean;
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Leave it none to automatically infer from the control image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The URL of the control image.
   */
  normal_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the normal image. Default value: `true`
   */
  normal_preprocess?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The URL of the control image.
   */
  openpose_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the openpose image. Default value: `true`
   */
  openpose_preprocess?: boolean;
  /**
   * If set to true, the aspect ratio of the generated image will be preserved even
   * if the image size is too large. However, if the image is not a multiple of 32
   * in width or height, it will be resized to the nearest multiple of 32. By default,
   * this snapping to the nearest multiple of 32 will not preserve the aspect ratio.
   * Set crop_output to True, to crop the output to the proper aspect ratio
   * after generating.
   */
  preserve_aspect_ratio?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The URL of the control image.
   */
  segmentation_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the segmentation image. Default value: `true`
   */
  segmentation_preprocess?: boolean;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The URL of the control image.
   */
  teed_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the teed image. Default value: `true`
   */
  teed_preprocess?: boolean;
};
export type ImageToImageFooocusInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, a smaller model will try to refine the output after it was processed. Default value: `true`
   */
  enable_refiner?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Leave it none to automatically infer from the prompt image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
};
export type ImageToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageToImageLCMInput = {
  /**
   * If set to true, the output cropped to the proper aspect ratio after generating.
   */
  crop_output?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `1.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The name of the model to use. Default value: `"stabilityai/stable-diffusion-xl-base-1.0"`
   */
  model_name?:
    | "stabilityai/stable-diffusion-xl-base-1.0"
    | "runwayml/stable-diffusion-v1-5";
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `6`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the aspect ratio of the generated image will be preserved even
   * if the image size is too large. However, if the image is not a multiple of 32
   * in width or height, it will be resized to the nearest multiple of 32. By default,
   * this snapping to the nearest multiple of 32 will not preserve the aspect ratio.
   * Set crop_output to True, to crop the output to the proper aspect ratio
   * after generating.
   */
  preserve_aspect_ratio?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history. Default value: `true`
   */
  sync_mode?: boolean;
};
export type ImageToImageLightningInput = {
  /**
   * If set to true, the output cropped to the proper aspect ratio after generating.
   */
  crop_output?: boolean;
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `"4"`
   */
  num_inference_steps?: "1" | "2" | "4" | "8";
  /**
   * If set to true, the aspect ratio of the generated image will be preserved even
   * if the image size is too large. However, if the image is not a multiple of 32
   * in width or height, it will be resized to the nearest multiple of 32. By default,
   * this snapping to the nearest multiple of 32 will not preserve the aspect ratio.
   * Set crop_output to True, to crop the output to the proper aspect ratio
   * after generating.
   */
  preserve_aspect_ratio?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ImageToImagePlaygroundv25Input = {
  /**
   * If set to true, the output cropped to the proper aspect ratio after generating.
   */
  crop_output?: boolean;
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the aspect ratio of the generated image will be preserved even
   * if the image size is too large. However, if the image is not a multiple of 32
   * in width or height, it will be resized to the nearest multiple of 32. By default,
   * this snapping to the nearest multiple of 32 will not preserve the aspect ratio.
   * Set crop_output to True, to crop the output to the proper aspect ratio
   * after generating.
   */
  preserve_aspect_ratio?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
};
export type ImageToImageSD15Input = {
  /**
   * If set to true, the output cropped to the proper aspect ratio after generating.
   */
  crop_output?: boolean;
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the aspect ratio of the generated image will be preserved even
   * if the image size is too large. However, if the image is not a multiple of 32
   * in width or height, it will be resized to the nearest multiple of 32. By default,
   * this snapping to the nearest multiple of 32 will not preserve the aspect ratio.
   * Set crop_output to True, to crop the output to the proper aspect ratio
   * after generating.
   */
  preserve_aspect_ratio?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ImageToImageTurboInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you.
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Defaults to landscape_4_3 if no controlnet has been passed, otherwise defaults to the size of the controlnet conditioning image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of Image for Image-to-Image
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Strength for Image-to-Image. Default value: `0.83`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ImageToLottieInput = {
  /**
   * URL of the reference image to animate.
   */
  image_url: string | Blob | File;
  /**
   * Maximum number of Lottie tokens to generate. Default value: `4096`
   */
  max_tokens?: number;
  /**
   * Text description guiding the animation of the image.
   */
  prompt: string;
  /**
   * Sampling temperature for generation. Default value: `0.9`
   */
  temperature?: number;
  /**
   * Top-k sampling parameter. Default value: `5`
   */
  top_k?: number;
  /**
   * Nucleus sampling probability threshold. Default value: `0.25`
   */
  top_p?: number;
};
export type ImageToSVGInput = {
  /**
   * Enable input safety checking. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Fill color for the generated glyphs. Accepts any valid SVG/CSS color value. Default value: `"black"`
   */
  fill_color?: string;
  /**
   * Maximum tokens to generate. Default value: `8192`
   */
  max_tokens?: number;
  /**
   * Maximum dimension (width or height) of the output SVG in pixels. The aspect ratio is preserved. Default value: `512`
   */
  output_size?: number;
  /**
   * The target text to generate as vector glyphs, styled to match the reference images.
   */
  prompt: string;
  /**
   * URLs of reference glyph images whose style should be matched. Provide 1 to 8 images of individual glyph characters.
   */
  reference_image_urls: Array<string>;
  /**
   * Repetition penalty. Default value: `1`
   */
  repetition_penalty?: number;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * Optional stroke (outline) color for the generated glyphs. When set, adds an outline around each glyph path.
   */
  stroke_color?: string;
  /**
   * Stroke width in SVG units. Only applies when stroke_color is set. Default value: `1`
   */
  stroke_width?: number;
  /**
   * Sampling temperature. Default value: `0.1`
   */
  temperature?: number;
  /**
   * Top-k sampling parameter. Default value: `5`
   */
  top_k?: number;
  /**
   * Top-p (nucleus) sampling parameter. Default value: `0.95`
   */
  top_p?: number;
};
export type ImageToVideoInput = {
  /**
   * URL of the audio to use as the background music. Must be publicly accessible.
   * Limit handling: If the audio duration exceeds the duration value (5, 10, or 15 seconds),
   * the audio is truncated to the first N seconds, and the rest is discarded. If
   * the audio is shorter than the video, the remaining part of the video will be silent.
   * For example, if the audio is 3 seconds long and the video duration is 5 seconds, the
   * first 3 seconds of the output video will have sound, and the last 2 seconds will be silent.
   * - Format: WAV, MP3.
   * - Duration: 3 to 30 s.
   * - File size: Up to 15 MB.
   */
  audio_url?: string | Blob | File;
  /**
   * Duration of the generated video in seconds. Choose between 5, 10 or 15 seconds. Default value: `"5"`
   */
  duration?: "5" | "10" | "15";
  /**
   * Whether to enable prompt rewriting using LLM. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the image to use as the first frame. Must be publicly accessible or base64 data URI. Image dimensions must be between 240 and 7680.
   */
  image_url: string | Blob | File;
  /**
   * When true, enables intelligent multi-shot segmentation. Only active when enable_prompt_expansion is True. Set to false for single-shot generation.
   */
  multi_shots?: boolean;
  /**
   * Negative prompt to describe content to avoid. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The text prompt describing the desired video motion. Max 800 characters.
   */
  prompt: string;
  /**
   * Video resolution. Valid values: 720p, 1080p Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type ImageToVideov21Input = {
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The seed for the random number generator
   */
  seed?: number;
};
export type ImageutilsNsfwOutput = {
  /**
   * The probability of the image being NSFW.
   */
  nsfw_probability: number;
};
export type ImageutilsRembgInput = {
  /**
   * If set to true, the resulting image be cropped to a bounding box around the subject
   */
  crop_to_bbox?: boolean;
  /**
   * Input image url.
   */
  image_url: string | Blob | File;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ImageWithTextInput = {
  /**
   * The URL of the image to be processed.
   */
  image_url: string | Blob | File;
  /**
   * Text input for the task
   */
  text_input: string;
};
export type ImageWithUserCoordinatesInput = {
  /**
   * The URL of the image to be processed.
   */
  image_url: string | Blob | File;
  /**
   * The user input coordinates
   */
  region: Region;
};
export type Imagineart15PreviewTextToImageOutput = {
  /**
   * Generated image
   */
  images: Array<ImageOutput>;
};
export type ImpulseResponseInput = {
  /**
   * URL of the main audio file to process
   */
  audio_url: string | Blob | File;
  /**
   * Level of the original (dry) signal in the mix (0.0-1.0) Default value: `0.7`
   */
  dry_level?: number;
  /**
   * URL of the impulse response WAV file (reverb/effect profile)
   */
  impulse_response_url: string | Blob | File;
  /**
   * Target integrated loudness in LUFS (typically -24 to -14) Default value: `-18`
   */
  loudness_i?: number;
  /**
   * Loudness Range target in LU (typically 5-15) Default value: `8`
   */
  loudness_lra?: number;
  /**
   * Maximum true peak in dBTP (typically -2 to -1) Default value: `-1.5`
   */
  loudness_tp?: number;
  /**
   * Output audio bitrate Default value: `"192k"`
   */
  output_bitrate?: "128k" | "192k" | "256k" | "320k";
  /**
   * Level of the processed (wet) signal in the mix (0.0-1.0) Default value: `0.3`
   */
  wet_level?: number;
};
export type IndexTts2TextToSpeechInput = {
  /**
   * The audio file to generate the speech from.
   */
  audio_url: string | Blob | File;
  /**
   * The emotional prompt to influence the emotional style. Must be used together with should_use_prompt_for_emotion.
   */
  emotion_prompt?: string;
  /**
   * The emotional reference audio file to extract the style from.
   */
  emotional_audio_url?: string | Blob | File;
  /**
   * The strengths of individual emotions for fine-grained control.
   */
  emotional_strengths?: EmotionalStrengths;
  /**
   * The speech prompt to generate
   */
  prompt: string;
  /**
   * Whether to use the `prompt` to calculate emotional strengths, if enabled it will overwrite the `emotional_strengths` values. If `emotion_prompt` is provided, it will be used to instead of `prompt` to extract the emotional style.
   */
  should_use_prompt_for_emotion?: boolean;
  /**
   * The strength of the emotional style transfer. Higher values result in stronger emotional influence. Default value: `1`
   */
  strength?: number;
};
export type infinitalkInput = {
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 41 to 721. Default value: `145`
   */
  num_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `42`
   */
  seed?: number;
};
export type InfinitalkVideoToVideoInput = {
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 81 to 129 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `145`
   */
  num_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `42`
   */
  seed?: number;
  /**
   * URL of the input video.
   */
  video_url: string | Blob | File;
};
export type InfinityStarTextToVideoInput = {
  /**
   * Aspect ratio of the generated output Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * Whether to use an LLM to enhance the prompt. Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * Guidance scale for generation Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Negative prompt to guide what to avoid in generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Text prompt for generating the video
   */
  prompt: string;
  /**
   * Random seed for reproducibility. Leave empty for random generation.
   */
  seed?: number;
  /**
   * Tau value for video scale Default value: `0.4`
   */
  tau_video?: number;
  /**
   * Whether to use APG Default value: `true`
   */
  use_apg?: boolean;
};
export type InpaintingControlNetInput = {
  /**
   * The URL of the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * The scale of the controlnet conditioning. Default value: `0.5`
   */
  controlnet_conditioning_scale?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Leave it none to automatically infer from the control image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type InpaintingControlNetUnionInput = {
  /**
   * The URL of the control image.
   */
  canny_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the canny image. Default value: `true`
   */
  canny_preprocess?: boolean;
  /**
   * The scale of the controlnet conditioning. Default value: `0.5`
   */
  controlnet_conditioning_scale?: number;
  /**
   * The URL of the control image.
   */
  depth_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the depth image. Default value: `true`
   */
  depth_preprocess?: boolean;
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Leave it none to automatically infer from the control image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The URL of the control image.
   */
  normal_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the normal image. Default value: `true`
   */
  normal_preprocess?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The URL of the control image.
   */
  openpose_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the openpose image. Default value: `true`
   */
  openpose_preprocess?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The URL of the control image.
   */
  segmentation_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the segmentation image. Default value: `true`
   */
  segmentation_preprocess?: boolean;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The URL of the control image.
   */
  teed_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the teed image. Default value: `true`
   */
  teed_preprocess?: boolean;
};
export type InpaintingFooocusInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, a smaller model will try to refine the output after it was processed. Default value: `true`
   */
  enable_refiner?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
};
export type InpaintingInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type InpaintingLCMInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `1.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The name of the model to use. Default value: `"stabilityai/stable-diffusion-xl-base-1.0"`
   */
  model_name?:
    | "stabilityai/stable-diffusion-xl-base-1.0"
    | "runwayml/stable-diffusion-v1-5";
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `6`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history. Default value: `true`
   */
  sync_mode?: boolean;
};
export type InpaintingLightningInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `"4"`
   */
  num_inference_steps?: "1" | "2" | "4" | "8";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type InpaintingPlaygroundv25Input = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
};
export type inpaintInput = {
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Input image for img2img or inpaint mode
   */
  image_url: string | Blob | File;
  /**
   * Input mask for inpaint mode. Black areas will be preserved, white areas will be inpainted.
   */
  mask_url: string | Blob | File;
  /**
   * URL or HuggingFace ID of the base model to generate the image.
   */
  model_name: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps
   * to generate your final result which can increase the amount of detail in your image. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
};
export type InpaintInput = {
  /**
   * The URL of the audio file to alter. Must be a valid publicly accessible URL.
   */
  audio_url: string | Blob | File;
  /**
   * Greater means more natural vocals. Lower means sharper instrumentals. We recommend 0.7. Default value: `0.7`
   */
  balance_strength?: number;
  /**
   * The lyrics sung in the generated song. An empty string will generate an instrumental track.
   */
  lyrics_prompt: string;
  /**
   * Generating 2 songs costs 1.5x the price of generating 1 song. Also, note that using the same seed may not result in identical songs if the number of songs generated is changed. Default value: `1`
   */
  num_songs?: number;
  /**
   * The bit rate to use for mp3 and m4a formats. Not available for other formats.
   */
  output_bit_rate?: "128" | "192" | "256" | "320";
  /**
   *  Default value: `"wav"`
   */
  output_format?: "flac" | "mp3" | "wav" | "ogg" | "m4a";
  /**
   * Controls how strongly your prompt influences the output. Greater values adhere more to the prompt but sound less natural. (This is CFG.) Default value: `2`
   */
  prompt_strength?: number;
  /**
   * List of sections to inpaint. Currently, only one section is supported so the list length must be 1.
   */
  sections: Array<InpaintSection>;
  /**
   * The seed to use for generation. Will pick a random seed if not provided. Repeating a request with identical parameters (must use lyrics and tags, not prompt) and the same seed will generate the same song.
   */
  seed?: number;
  /**
   * Crop to the selected region
   */
  selection_crop?: boolean;
  /**
   * Tags/styles of the music to generate. You can view a list of all available tags at https://sonauto.ai/tag-explorer.
   */
  tags?: Array<string>;
};
export type InpaintOutput = {
  /**
   * The generated audio files.
   */
  audio: Array<File>;
  /**
   * The seed used for generation. This can be used to generate an identical song by passing the same parameters with this seed in a future request.
   */
  seed: number;
};
export type InpaintSection = {
  /**
   * End time in seconds of the section to inpaint.
   */
  end: number;
  /**
   * Start time in seconds of the section to inpaint.
   */
  start: number;
};
export type InpaintTurboInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you.
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Defaults to landscape_4_3 if no controlnet has been passed, otherwise defaults to the size of the controlnet conditioning image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of Image for inpainting
   */
  image_url: string | Blob | File;
  /**
   * URL of mask image for inpainting.
   */
  mask_image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Strength for Image-to-Image. Default value: `0.83`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Input = {
  /**
   * List of tracks to be combined into the final media
   */
  tracks: Array<Track>;
};
export type InstantCharacterInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The scale of the subject image. Higher values will make the subject image more prominent in the generated image. Default value: `1`
   */
  scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type InterleaveVideoInput = {
  /**
   * List of video URLs to interleave in order
   */
  video_urls: Array<string>;
};
export type InvisibleWatermarkInput = {
  /**
   * Whether to decode a watermark from the image instead of encoding
   */
  decode?: boolean;
  /**
   * URL of image to be watermarked or decoded
   */
  image_url: string | Blob | File;
  /**
   * Length of watermark bits to decode (required when decode=True)
   */
  length?: number;
  /**
   * Text to use as watermark (for encoding only) Default value: `"watermark"`
   */
  watermark?: string;
};
export type InvisibleWatermarkOutput = {
  /**
   * The extracted watermark text (when decoding)
   */
  extracted_watermark?: string;
  /**
   * The watermarked image file info (when encoding)
   */
  image?: Image;
  /**
   * Length of the watermark bits used (helpful for future decoding)
   */
  length?: number;
};
export type InworldTtsInput = {
  /**
   * The sample rate in Hz for the output audio. Default value: `"48000"`
   */
  sample_rate_hertz?: "8000" | "16000" | "24000" | "32000" | "40000" | "48000";
  /**
   * The text to synthesize into speech.
   */
  text: string;
  /**
   * The voice to use for synthesis. Default value: `"Craig (en)"`
   */
  voice?:
    | "Loretta (en)"
    | "Darlene (en)"
    | "Marlene (en)"
    | "Hank (en)"
    | "Evelyn (en)"
    | "Celeste (en)"
    | "Pippa (en)"
    | "Tessa (en)"
    | "Liam (en)"
    | "Callum (en)"
    | "Hamish (en)"
    | "Abby (en)"
    | "Graham (en)"
    | "Rupert (en)"
    | "Mortimer (en)"
    | "Snik (en)"
    | "Anjali (en)"
    | "Saanvi (en)"
    | "Arjun (en)"
    | "Claire (en)"
    | "Oliver (en)"
    | "Simon (en)"
    | "Elliot (en)"
    | "James (en)"
    | "Serena (en)"
    | "Gareth (en)"
    | "Vinny (en)"
    | "Lauren (en)"
    | "Jessica (en)"
    | "Ethan (en)"
    | "Tyler (en)"
    | "Jason (en)"
    | "Chloe (en)"
    | "Veronica (en)"
    | "Victoria (en)"
    | "Miranda (en)"
    | "Sebastian (en)"
    | "Victor (en)"
    | "Malcolm (en)"
    | "Kayla (en)"
    | "Nate (en)"
    | "Jake (en)"
    | "Brian (en)"
    | "Amina (en)"
    | "Kelsey (en)"
    | "Derek (en)"
    | "Grant (en)"
    | "Evan (en)"
    | "Alex (en)"
    | "Ashley (en)"
    | "Craig (en)"
    | "Deborah (en)"
    | "Dennis (en)"
    | "Edward (en)"
    | "Elizabeth (en)"
    | "Hades (en)"
    | "Julia (en)"
    | "Pixie (en)"
    | "Mark (en)"
    | "Olivia (en)"
    | "Priya (en)"
    | "Ronald (en)"
    | "Sarah (en)"
    | "Shaun (en)"
    | "Theodore (en)"
    | "Timothy (en)"
    | "Wendy (en)"
    | "Dominus (en)"
    | "Hana (en)"
    | "Clive (en)"
    | "Carter (en)"
    | "Blake (en)"
    | "Luna (en)"
    | "Yichen (zh)"
    | "Xiaoyin (zh)"
    | "Xinyi (zh)"
    | "Jing (zh)"
    | "Erik (nl)"
    | "Katrien (nl)"
    | "Lennart (nl)"
    | "Lore (nl)"
    | "Alain (fr)"
    | "Hélène (fr)"
    | "Mathieu (fr)"
    | "Étienne (fr)"
    | "Johanna (de)"
    | "Josef (de)"
    | "Gianni (it)"
    | "Orietta (it)"
    | "Asuka (ja)"
    | "Satoshi (ja)"
    | "Hyunwoo (ko)"
    | "Minji (ko)"
    | "Seojun (ko)"
    | "Yoona (ko)"
    | "Szymon (pl)"
    | "Wojciech (pl)"
    | "Heitor (pt)"
    | "Maitê (pt)"
    | "Diego (es)"
    | "Lupita (es)"
    | "Miguel (es)"
    | "Rafael (es)"
    | "Svetlana (ru)"
    | "Elena (ru)"
    | "Dmitry (ru)"
    | "Nikolai (ru)"
    | "Riya (hi)"
    | "Manoj (hi)"
    | "Yael (he)"
    | "Oren (he)"
    | "Nour (ar)"
    | "Omar (ar)";
};
export type IPAdapter = {
  /**
   * Path to the Image Encoder for the IP-Adapter, for example 'openai/clip-vit-large-patch14'
   */
  image_encoder_path: string;
  /**
   * Subfolder in which the image encoder weights exist.
   */
  image_encoder_subfolder?: string;
  /**
   * Name of the image encoder.
   */
  image_encoder_weight_name?: string;
  /**
   * URL of Image for IP-Adapter conditioning.
   */
  image_url: string | Blob | File;
  /**
   * URL of the mask for the control image.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Threshold for mask. Default value: `0.5`
   */
  mask_threshold?: number;
  /**
   * Hugging Face path to the IP-Adapter
   */
  path: string;
  /**
   * Scale for ip adapter.
   */
  scale: number;
  /**
   * Subfolder in which the ip_adapter weights exist
   */
  subfolder?: string;
  /**
   * Name of the safetensors file containing the ip-adapter weights
   */
  weight_name?: string;
};
export type IpAdapterFaceIdInput = {
  /**
   * The URL to the base 1.5 model. Default is SG161222/Realistic_Vision_V4.0_noVAE Default value: `"SG161222/Realistic_Vision_V4.0_noVAE"`
   */
  base_1_5_model_repo?: string;
  /**
   * The URL to the base SDXL model. Default is SG161222/RealVisXL_V3.0 Default value: `"SG161222/RealVisXL_V3.0"`
   */
  base_sdxl_model_repo?: string;
  /**
   * The size of the face detection model. The higher the number the more accurate
   * the detection will be but it will also take longer to run. The higher the number the more
   * likely it will fail to find a face as well. Lower it if you are having trouble
   * finding a face in the image. Default value: `640`
   */
  face_id_det_size?: number;
  /**
   * An image of a face to match. If an image with a size of 640x640 is not provided, it will be scaled and cropped to that size.
   */
  face_image_url?: string | Blob | File;
  /**
   * URL to zip archive with images of faces. The images embedding will be averaged to
   * create a more accurate face id.
   */
  face_images_data_url?: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The height of the generated image. Default value: `512`
   */
  height?: number;
  /**
   * The model type to use. 1_5 is the default and is recommended for most use cases. Default value: `"1_5-v1"`
   */
  model_type?:
    | "1_5-v1"
    | "1_5-v1-plus"
    | "1_5-v2-plus"
    | "SDXL-v1"
    | "SDXL-v2-plus"
    | "1_5-auraface-v1";
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"blurry, low resolution, bad, ugly, low quality, pixelated, interpolated, compression artifacts, noisey, grainy"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to use for generating the image. The more steps
   * the better the image will be but it will also take longer to generate. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The number of samples for face id. The more samples the better the image will
   * be but it will also take longer to generate. Default is 4. Default value: `4`
   */
  num_samples?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The width of the generated image. Default value: `512`
   */
  width?: number;
};
export type janusInput = {
  /**
   * Classifier Free Guidance scale - how closely to follow the prompt. Default value: `5`
   */
  cfg_weight?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `square`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Number of images to generate in parallel. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * Controls randomness in the generation. Higher values make output more random. Default value: `1`
   */
  temperature?: number;
};
export type JsonExtractInput = {
  /**
   * In 'extract' mode: a JSON string to parse and extract from. In 'stringify' mode: any JSON value (object, array, number, etc.) to convert to a string.
   */
  json_input: void;
  /**
   * Operation mode. 'extract' parses json_input and extracts values at given paths. 'stringify' converts json_input to a JSON string. Default value: `"extract"`
   */
  mode?: "extract" | "stringify";
  /**
   * List of JSON paths to extract (only used in 'extract' mode). Use dot notation for nested keys and brackets for array indices.
   */
  paths?: Array<string>;
};
export type JsonExtractOutput = {
  /**
   * In 'stringify' mode: the JSON string representation. Null in 'extract' mode.
   */
  text?: string;
  /**
   * In 'extract' mode: extracted values in the same order as input paths. In 'stringify' mode: single-element list containing the JSON string.
   */
  values: Array<void>;
};
export type JuggernautFluxLoraInpaintingInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type Kandinsky5ProImageToVideoInput = {
  /**
   * Acceleration level for faster generation. Default value: `regular`
   */
  acceleration?: "none" | "regular";
  /**
   * Video duration. Default value: `"5s"`
   */
  duration?: string;
  /**
   * The URL of the image to use as a reference for the video generation.
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * Video resolution: 512p or 1024p. Default value: `"512P"`
   */
  resolution?: "512P" | "1024P";
};
export type Kandinsky5ProTextToVideoInput = {
  /**
   * Acceleration level for faster generation. Default value: `regular`
   */
  acceleration?: "none" | "regular";
  /**
   * Aspect ratio of the generated video. One of (3:2, 1:1, 2:3). Default value: `"3:2"`
   */
  aspect_ratio?: "3:2" | "1:1" | "2:3";
  /**
   * The length of the video to generate. Default value: `"5s"`
   */
  duration?: string;
  /**
   * The number of inference steps. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Video resolution: 512p or 1024p. Default value: `"512P"`
   */
  resolution?: "512P" | "1024P";
};
export type Kandinsky5TextToVideoDistillInput = {
  /**
   * Aspect ratio of the generated video. One of (3:2, 1:1, 2:3). Default value: `"3:2"`
   */
  aspect_ratio?: "3:2" | "1:1" | "2:3";
  /**
   * The length of the video to generate (5s or 10s) Default value: `"5s"`
   */
  duration?: "5s" | "10s";
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video in W:H format. Will be calculated based on the aspect ratio(768x512, 512x512, 512x768). Default value: `"768x512"`
   */
  resolution?: string;
};
export type Kandinsky5TextToVideoInput = {
  /**
   * Aspect ratio of the generated video. One of (3:2, 1:1, 2:3). Default value: `"3:2"`
   */
  aspect_ratio?: "3:2" | "1:1" | "2:3";
  /**
   * The length of the video to generate (5s or 10s) Default value: `"5s"`
   */
  duration?: "5s" | "10s";
  /**
   * The number of inference steps. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video in W:H format. Will be calculated based on the aspect ratio(768x512, 512x512, 512x768). Default value: `"768x512"`
   */
  resolution?: string;
};
export type Kandinsky5TextToVideoOutput = {
  /**
   * The generated video file.
   */
  video?: File;
};
export type Keyframe = {
  /**
   * The duration in milliseconds of this keyframe
   */
  duration: number;
  /**
   * The timestamp in milliseconds where this keyframe starts
   */
  timestamp: number;
  /**
   * The URL where this keyframe's media file can be accessed
   */
  url: string;
};
export type KeyframeTransition = {
  /**
   * Duration of this transition in seconds Default value: `5`
   */
  duration?: number;
  /**
   * Specific prompt for this transition. Overrides the global prompt if provided.
   */
  prompt?: string;
};
export type KleinBaseEditLoRAInput = {
  /**
   * The acceleration level to use for image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance scale for classifier-free guidance. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If not provided, uses the input image size.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images for editing. A maximum of 4 images are allowed.
   */
  image_urls: Array<string>;
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<FalAiFlux2KleinLorainput>;
  /**
   * Negative prompt for classifier-free guidance. Describes what to avoid in the image. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI. Output is not stored when this is True.
   */
  sync_mode?: boolean;
};
export type KleinBaseLoRAInput = {
  /**
   * The acceleration level to use for image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance scale for classifier-free guidance. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<FalAiFlux2KleinLorainput>;
  /**
   * Negative prompt for classifier-free guidance. Describes what to avoid in the image. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI. Output is not stored when this is True.
   */
  sync_mode?: boolean;
};
export type KleinLoRAInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the image to generate. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<FalAiFlux2KleinLorainput>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI. Output is not stored when this is True.
   */
  sync_mode?: boolean;
};
export type KlingImageO1Input = {
  /**
   * Aspect ratio of generated images. 'auto' intelligently determines based on input content. Default value: `"auto"`
   */
  aspect_ratio?:
    | "auto"
    | "16:9"
    | "9:16"
    | "1:1"
    | "4:3"
    | "3:4"
    | "3:2"
    | "2:3"
    | "21:9";
  /**
   * Elements (characters/objects) to include in the image. Reference in prompt as @Element1, @Element2, etc. Maximum 10 total (elements + reference images).
   */
  elements?: Array<ElementInput>;
  /**
   * List of reference images. Reference images in prompt using @Image1, @Image2, etc. (1-indexed). Max 10 images.
   */
  image_urls: Array<string>;
  /**
   * Number of images to generate (1-9). Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text prompt for image generation. Reference images using @Image1, @Image2, etc. (or @Image if only one image). Max 2500 characters.
   */
  prompt: string;
  /**
   * Image generation resolution. 1K: standard, 2K: high-res. Default value: `"1K"`
   */
  resolution?: "1K" | "2K";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type KlingImageO3ImageToImageInput = {
  /**
   * Aspect ratio of generated images. 'auto' intelligently determines based on input content. Default value: `"auto"`
   */
  aspect_ratio?:
    | "16:9"
    | "9:16"
    | "1:1"
    | "4:3"
    | "3:4"
    | "3:2"
    | "2:3"
    | "21:9"
    | "auto";
  /**
   * Optional: Elements (characters/objects) for face control. Reference in prompt as @Element1, @Element2, etc.
   */
  elements?: Array<ElementInput>;
  /**
   * List of reference images. Reference images in prompt using @Image1, @Image2, etc. (1-indexed). Max 10 images.
   */
  image_urls: Array<string>;
  /**
   * Number of images to generate (1-9). Only used when result_type is 'single'. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text prompt for image generation. Reference images using @Image1, @Image2, etc. (or @Image if only one image). Max 2500 characters.
   */
  prompt: string;
  /**
   * Image generation resolution. 1K: standard, 2K: high-res, 4K: ultra high-res. Default value: `"1K"`
   */
  resolution?: "1K" | "2K" | "4K";
  /**
   * Result type. 'single' for one image, 'series' for a series of related images. Default value: `"single"`
   */
  result_type?: "single" | "series";
  /**
   * Number of images in series (2-9). Only used when result_type is 'series'.
   */
  series_amount?: number;
  /**
   * If `True`, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type KlingImageO3TextToImageInput = {
  /**
   * Aspect ratio of generated images. Default value: `"16:9"`
   */
  aspect_ratio?:
    | "16:9"
    | "9:16"
    | "1:1"
    | "4:3"
    | "3:4"
    | "3:2"
    | "2:3"
    | "21:9";
  /**
   * Optional: Elements (characters/objects) for face control. Reference in prompt as @Element1, @Element2, etc.
   */
  elements?: Array<ElementInput>;
  /**
   * Number of images to generate (1-9). Only used when result_type is 'single'. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text prompt for image generation. Max 2500 characters.
   */
  prompt: string;
  /**
   * Image generation resolution. 1K: standard, 2K: high-res, 4K: ultra high-res. Default value: `"1K"`
   */
  resolution?: "1K" | "2K" | "4K";
  /**
   * Result type. 'single' for one image, 'series' for a series of related images. Default value: `"single"`
   */
  result_type?: "single" | "series";
  /**
   * Number of images in series (2-9). Only used when result_type is 'series'.
   */
  series_amount?: number;
  /**
   * If `True`, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type KlingImageV3ImageToImageInput = {
  /**
   * Aspect ratio of generated images. Default value: `"16:9"`
   */
  aspect_ratio?:
    | "16:9"
    | "9:16"
    | "1:1"
    | "4:3"
    | "3:4"
    | "3:2"
    | "2:3"
    | "21:9";
  /**
   * Optional: Elements (characters/objects) to include in the image for face control.
   */
  elements?: Array<ElementInput>;
  /**
   * Reference image for image-to-image generation.
   *
   * Max file size: 10.0MB, Min width: 300px, Min height: 300px, Min aspect ratio: 0.40, Max aspect ratio: 2.50, Timeout: 20.0s
   */
  image_url: string | Blob | File;
  /**
   * Number of images to generate (1-9). Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text prompt for image generation. Max 2500 characters.
   */
  prompt: string;
  /**
   * Image generation resolution. 1K: standard, 2K: high-res. Default value: `"1K"`
   */
  resolution?: "1K" | "2K";
  /**
   * If `True`, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type KlingImageV3TextToImageInput = {
  /**
   * Aspect ratio of generated images. Default value: `"16:9"`
   */
  aspect_ratio?:
    | "16:9"
    | "9:16"
    | "1:1"
    | "4:3"
    | "3:4"
    | "3:2"
    | "2:3"
    | "21:9";
  /**
   * Optional: Elements (characters/objects) to include in the image for face control. Each element can have a frontal image and optionally reference images.
   */
  elements?: Array<ElementInput>;
  /**
   * Negative text prompt. It is recommended to supplement negative prompt information through negative sentences directly within positive prompts.
   */
  negative_prompt?: string;
  /**
   * Number of images to generate (1-9). Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text prompt for image generation. Max 2500 characters.
   */
  prompt: string;
  /**
   * Image generation resolution. 1K: standard, 2K: high-res. Default value: `"1K"`
   */
  resolution?: "1K" | "2K";
  /**
   * If `True`, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type KlingV15KolorsVirtualTryOnInput = {
  /**
   * Url to the garment image.
   */
  garment_image_url: string | Blob | File;
  /**
   * Url for the human image.
   */
  human_image_url: string | Blob | File;
  /**
   * If true, the function will return the image in the response.
   */
  sync_mode?: boolean;
};
export type KlingV3ComboElementInput = {
  /**
   * The frontal image of the element (main view).
   */
  frontal_image_url?: string | Blob | File;
  /**
   * Additional reference images from different angles. 1-3 images supported. At least one image is required.
   */
  reference_image_urls?: Array<string>;
  /**
   * The video URL of the element. A request can only have one element with a video.
   */
  video_url?: string | Blob | File;
  /**
   * The voice ID for this element. The voice will be binded to the element and references to this element will use the binded voice. Voice binding is only supported for video elements, and cannot be used with image elements. Get voice IDs from the following endpoint: https://fal.ai/models/fal-ai/kling-video/create-voice
   */
  voice_id?: string;
};
export type KlingV3MultiPromptElement = {
  /**
   * The duration of this shot in seconds Default value: `"5"`
   */
  duration?:
    | "1"
    | "2"
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * The prompt for this shot.
   */
  prompt: string;
};
export type KlingVideoLipsyncTextToVideoInput = {
  /**
   * Text content for lip-sync video generation. Max 120 characters.
   */
  text: string;
  /**
   * The URL of the video to generate the lip sync for. Supports .mp4/.mov, ≤100MB, 2-60s, 720p/1080p only, width/height 720–1920px. If validation fails, an error is returned.
   */
  video_url: string | Blob | File;
  /**
   * Voice ID to use for speech synthesis
   */
  voice_id:
    | "genshin_vindi2"
    | "zhinen_xuesheng"
    | "AOT"
    | "ai_shatang"
    | "genshin_klee2"
    | "genshin_kirara"
    | "ai_kaiya"
    | "oversea_male1"
    | "ai_chenjiahao_712"
    | "girlfriend_4_speech02"
    | "chat1_female_new-3"
    | "chat_0407_5-1"
    | "cartoon-boy-07"
    | "uk_boy1"
    | "cartoon-girl-01"
    | "PeppaPig_platform"
    | "ai_huangzhong_712"
    | "ai_huangyaoshi_712"
    | "ai_laoguowang_712"
    | "chengshu_jiejie"
    | "you_pingjing"
    | "calm_story1"
    | "uk_man2"
    | "laopopo_speech02"
    | "heainainai_speech02"
    | "reader_en_m-v1"
    | "commercial_lady_en_f-v1"
    | "tiyuxi_xuedi"
    | "tiexin_nanyou"
    | "girlfriend_1_speech02"
    | "girlfriend_2_speech02"
    | "zhuxi_speech02"
    | "uk_oldman3"
    | "dongbeilaotie_speech02"
    | "chongqingxiaohuo_speech02"
    | "chuanmeizi_speech02"
    | "chaoshandashu_speech02"
    | "ai_taiwan_man2_speech02"
    | "xianzhanggui_speech02"
    | "tianjinjiejie_speech02"
    | "diyinnansang_DB_CN_M_04-v2"
    | "yizhipiannan-v1"
    | "guanxiaofang-v2"
    | "tianmeixuemei-v1"
    | "daopianyansang-v1"
    | "mengwa-v1";
  /**
   * The voice language corresponding to the Voice ID Default value: `"en"`
   */
  voice_language?: "zh" | "en";
  /**
   * Speech rate for Text to Video generation Default value: `1`
   */
  voice_speed?: number;
};
export type KlingVideoV15ProEffectsInput = {
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The effect scene to use for the video generation
   */
  effect_scene:
    | "hug"
    | "kiss"
    | "heart_gesture"
    | "squish"
    | "expansion"
    | "fuzzyfuzzy"
    | "bloombloom"
    | "dizzydizzy"
    | "jelly_press"
    | "jelly_slice"
    | "jelly_squish"
    | "jelly_jiggle"
    | "pixelpixel"
    | "yearbook"
    | "instant_film"
    | "anime_figure"
    | "rocketrocket"
    | "fly_fly"
    | "disappear"
    | "lightning_power"
    | "bullet_time"
    | "bullet_time_360"
    | "media_interview"
    | "day_to_night"
    | "let's_ride"
    | "jumpdrop"
    | "swish_swish"
    | "running_man"
    | "jazz_jazz"
    | "swing_swing"
    | "skateskate"
    | "building_sweater"
    | "pure_white_wings"
    | "black_wings"
    | "golden_wing"
    | "pink_pink_wings"
    | "rampage_ape"
    | "a_list_look"
    | "countdown_teleport"
    | "firework_2026"
    | "instant_christmas"
    | "birthday_star"
    | "firework"
    | "celebration"
    | "tiger_hug_pro"
    | "pet_lion_pro"
    | "guardian_spirit"
    | "squeeze_scream"
    | "inner_voice"
    | "memory_alive"
    | "guess_what"
    | "eagle_snatch"
    | "hug_from_past"
    | "instant_kid"
    | "dollar_rain"
    | "cry_cry"
    | "building_collapse"
    | "mushroom"
    | "jesus_hug"
    | "shark_alert"
    | "lie_flat"
    | "polar_bear_hug"
    | "brown_bear_hug"
    | "office_escape_plow"
    | "watermelon_bomb"
    | "boss_coming"
    | "wig_out"
    | "car_explosion"
    | "tiger_hug"
    | "siblings"
    | "construction_worker"
    | "snatched"
    | "felt_felt"
    | "plushcut"
    | "drunk_dance"
    | "drunk_dance_pet"
    | "daoma_dance"
    | "bouncy_dance"
    | "smooth_sailing_dance"
    | "new_year_greeting"
    | "lion_dance"
    | "prosperity"
    | "great_success"
    | "golden_horse_fortune"
    | "red_packet_box"
    | "lucky_horse_year"
    | "lucky_red_packet"
    | "lucky_money_come"
    | "lion_dance_pet"
    | "dumpling_making_pet"
    | "fish_making_pet"
    | "pet_red_packet"
    | "lantern_glow"
    | "expression_challenge"
    | "overdrive"
    | "heart_gesture_dance"
    | "poping"
    | "martial_arts"
    | "running"
    | "nezha"
    | "motorcycle_dance"
    | "subject_3_dance"
    | "ghost_step_dance"
    | "phantom_jewel"
    | "zoom_out"
    | "cheers_2026"
    | "kiss_pro"
    | "fight_pro"
    | "hug_pro"
    | "heart_gesture_pro"
    | "dollar_rain_pro"
    | "pet_bee_pro"
    | "santa_random_surprise"
    | "magic_match_tree"
    | "happy_birthday"
    | "thumbs_up_pro"
    | "surprise_bouquet"
    | "bouquet_drop"
    | "3d_cartoon_1_pro"
    | "glamour_photo_shoot"
    | "box_of_joy"
    | "first_toast_of_the_year"
    | "my_santa_pic"
    | "santa_gift"
    | "steampunk_christmas"
    | "snowglobe"
    | "christmas_photo_shoot"
    | "ornament_crash"
    | "santa_express"
    | "particle_santa_surround"
    | "coronation_of_frost"
    | "spark_in_the_snow"
    | "scarlet_and_snow"
    | "cozy_toon_wrap"
    | "bullet_time_lite"
    | "magic_cloak"
    | "balloon_parade"
    | "jumping_ginger_joy"
    | "c4d_cartoon_pro"
    | "venomous_spider"
    | "throne_of_king"
    | "luminous_elf"
    | "woodland_elf"
    | "japanese_anime_1"
    | "american_comics"
    | "snowboarding"
    | "witch_transform"
    | "vampire_transform"
    | "pumpkin_head_transform"
    | "demon_transform"
    | "mummy_transform"
    | "zombie_transform"
    | "cute_pumpkin_transform"
    | "cute_ghost_transform"
    | "knock_knock_halloween"
    | "halloween_escape"
    | "baseball"
    | "trampoline"
    | "trampoline_night"
    | "pucker_up"
    | "feed_mooncake"
    | "flyer"
    | "dishwasher"
    | "pet_chinese_opera"
    | "magic_fireball"
    | "gallery_ring"
    | "pet_moto_rider"
    | "muscle_pet"
    | "pet_delivery"
    | "mythic_style"
    | "steampunk"
    | "3d_cartoon_2"
    | "pet_chef"
    | "santa_gifts"
    | "santa_hug"
    | "girlfriend"
    | "boyfriend"
    | "heart_gesture_1"
    | "pet_wizard"
    | "smoke_smoke"
    | "gun_shot"
    | "double_gun"
    | "pet_warrior"
    | "long_hair"
    | "pet_dance"
    | "wool_curly"
    | "pet_bee"
    | "marry_me"
    | "piggy_morph"
    | "ski_ski"
    | "magic_broom"
    | "splashsplash"
    | "surfsurf"
    | "fairy_wing"
    | "angel_wing"
    | "dark_wing"
    | "emoji";
  /**
   * URL of images to be used for hug, kiss or heart_gesture video.
   */
  input_image_urls?: Array<string>;
};
export type KlingVideoV15ProImageToVideoInput = {
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * List of dynamic masks
   */
  dynamic_masks?: Array<DynamicMask>;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image for Static Brush Application Area (Mask image created by users using the motion brush)
   */
  static_mask_url?: string | Blob | File;
  /**
   * URL of the image to be used for the end of the video
   */
  tail_image_url?: string | Blob | File;
};
export type KlingVideoV15ProTextToVideoInput = {
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
};
export type KlingVideoV16ProElementsInput = {
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * List of image URLs to use for video generation. Supports up to 4 images.
   */
  input_image_urls: Array<string>;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
};
export type KlingVideoV16ProImageToVideoInput = {
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to be used for the end of the video
   */
  tail_image_url?: string | Blob | File;
};
export type KlingVideoV1StandardImageToVideoInput = {
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * List of dynamic masks
   */
  dynamic_masks?: Array<DynamicMask>;
  /**
   * URL of the image to be used for the video
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The prompt for the video
   */
  prompt: string;
  /**
   * URL of the image for Static Brush Application Area (Mask image created by users using the motion brush)
   */
  static_mask_url?: string | Blob | File;
  /**
   * URL of the image to be used for the end of the video
   */
  tail_image_url?: string | Blob | File;
};
export type KlingVideoV1StandardTextToVideoInput = {
  /**
   * Advanced Camera control parameters
   */
  advanced_camera_control?: CameraControl;
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Camera control parameters
   */
  camera_control?:
    | "down_back"
    | "forward_up"
    | "right_turn_forward"
    | "left_turn_forward";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
};
export type KlingVideoV1TtsInput = {
  /**
   * The text to be converted to speech
   */
  text: string;
  /**
   * The voice ID to use for speech synthesis Default value: `"genshin_vindi2"`
   */
  voice_id?:
    | "genshin_vindi2"
    | "zhinen_xuesheng"
    | "AOT"
    | "ai_shatang"
    | "genshin_klee2"
    | "genshin_kirara"
    | "ai_kaiya"
    | "oversea_male1"
    | "ai_chenjiahao_712"
    | "girlfriend_4_speech02"
    | "chat1_female_new-3"
    | "chat_0407_5-1"
    | "cartoon-boy-07"
    | "uk_boy1"
    | "cartoon-girl-01"
    | "PeppaPig_platform"
    | "ai_huangzhong_712"
    | "ai_huangyaoshi_712"
    | "ai_laoguowang_712"
    | "chengshu_jiejie"
    | "you_pingjing"
    | "calm_story1"
    | "uk_man2"
    | "laopopo_speech02"
    | "heainainai_speech02"
    | "reader_en_m-v1"
    | "commercial_lady_en_f-v1"
    | "tiyuxi_xuedi"
    | "tiexin_nanyou"
    | "girlfriend_1_speech02"
    | "girlfriend_2_speech02"
    | "zhuxi_speech02"
    | "uk_oldman3"
    | "dongbeilaotie_speech02"
    | "chongqingxiaohuo_speech02"
    | "chuanmeizi_speech02"
    | "chaoshandashu_speech02"
    | "ai_taiwan_man2_speech02"
    | "xianzhanggui_speech02"
    | "tianjinjiejie_speech02"
    | "diyinnansang_DB_CN_M_04-v2"
    | "yizhipiannan-v1"
    | "guanxiaofang-v2"
    | "tianmeixuemei-v1"
    | "daopianyansang-v1"
    | "mengwa-v1";
  /**
   * Rate of speech Default value: `1`
   */
  voice_speed?: number;
};
export type KlingVideoV25TurboProImageToVideoInput = {
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * URL of the image to be used for the video
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to be used for the end of the video
   */
  tail_image_url?: string | Blob | File;
};
export type KlingVideoV26ProImageToVideoInput = {
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * URL of the image to be used for the end of the video
   */
  end_image_url?: string | Blob | File;
  /**
   * Whether to generate native audio for the video. Supports Chinese and English voice output. Other languages are automatically translated to English. For English speech, use lowercase letters; for acronyms or proper nouns, use uppercase. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to be used for the video
   */
  start_image_url: string | Blob | File;
  /**
   * Optional Voice IDs for video generation. Reference voices in your prompt with <<<voice_1>>> and <<<voice_2>>> (maximum 2 voices per task). Get voice IDs from the kling video create-voice endpoint: https://fal.ai/models/fal-ai/kling-video/create-voice
   */
  voice_ids?: Array<string>;
};
export type KlingVideoV26ProMotionControlInput = {
  /**
   * Controls whether the output character's orientation matches the reference image or video. 'video': orientation matches reference video - better for complex motions (max 30s). 'image': orientation matches reference image - better for following camera movements (max 10s).
   */
  character_orientation: "image" | "video";
  /**
   * Reference image URL. The characters, backgrounds, and other elements in the generated video are based on this reference image. Characters should have clear body proportions, avoid occlusion, and occupy more than 5% of the image area.
   */
  image_url: string | Blob | File;
  /**
   * Whether to keep the original sound from the reference video. Default value: `true`
   */
  keep_original_sound?: boolean;
  /**
   *
   */
  prompt?: string;
  /**
   * Reference video URL. The character actions in the generated video will be consistent with this reference video. Should contain a realistic style character with entire body or upper body visible, including head, without obstruction. Duration limit depends on character_orientation: 10s max for 'image', 30s max for 'video'.
   */
  video_url: string | Blob | File;
};
export type KlingVideoV26ProTextToVideoInput = {
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * Whether to generate native audio for the video. Supports Chinese and English voice output. Other languages are automatically translated to English. For English speech, use lowercase letters; for acronyms or proper nouns, use uppercase. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
};
export type KlingVideoV2MasterImageToVideoInput = {
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * URL of the image to be used for the video
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
};
export type KlingVideoV3ProImageToVideoInput = {
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?:
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Elements (characters/objects) to include in the video. Each example can either be an image set (frontal + reference images) or a video. Reference in prompt as @Element1, @Element2, etc.
   */
  elements?: Array<KlingV3ComboElementInput>;
  /**
   * URL of the image to be used for the end of the video
   */
  end_image_url?: string | Blob | File;
  /**
   * Whether to generate native audio for the video. Supports Chinese and English voice output. Other languages are automatically translated to English. For English speech, use lowercase letters; for acronyms or proper nouns, use uppercase. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * List of prompts for multi-shot video generation. If provided, divides the video into multiple shots.
   */
  multi_prompt?: Array<KlingV3MultiPromptElement>;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * Text prompt for video generation. Either prompt or multi_prompt must be provided, but not both.
   */
  prompt?: string;
  /**
   * The type of multi-shot video generation. Required when multi_prompt is provided. Default value: `"customize"`
   */
  shot_type?: string;
  /**
   * URL of the image to be used for the video
   */
  start_image_url: string | Blob | File;
};
export type KlingVideoV3ProMotionControlInput = {
  /**
   * Controls whether the output character's orientation matches the reference image or video. 'video': orientation matches reference video - better for complex motions (max 30s). 'image': orientation matches reference image - better for following camera movements (max 10s).
   */
  character_orientation: "image" | "video";
  /**
   * Optional element for facial consistency binding. Upload a facial element to enhance identity preservation in the generated video. Only 1 element is supported. Reference in prompt as @Element1. Element binding is only supported when character_orientation is 'video'.
   */
  elements?: Array<KlingV3ImageElementInput>;
  /**
   * Reference image URL. The characters, backgrounds, and other elements in the generated video are based on this reference image. Characters should have clear body proportions, avoid occlusion, and occupy more than 5% of the image area.
   */
  image_url: string | Blob | File;
  /**
   * Whether to keep the original sound from the reference video. Default value: `true`
   */
  keep_original_sound?: boolean;
  /**
   *
   */
  prompt?: string;
  /**
   * Reference video URL. The character actions in the generated video will be consistent with this reference video. Should contain a realistic style character with entire body or upper body visible, including head, without obstruction. Duration limit depends on character_orientation: 10s max for 'image', 30s max for 'video'.
   */
  video_url: string | Blob | File;
};
export type KlingVideoV3ProTextToVideoInput = {
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?:
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Whether to generate native audio for the video. Supports Chinese and English voice output. Other languages are automatically translated to English. For English speech, use lowercase letters; for acronyms or proper nouns, use uppercase. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * List of prompts for multi-shot video generation. If provided, overrides the single prompt and divides the video into multiple shots with specified prompts and durations.
   */
  multi_prompt?: Array<KlingV3MultiPromptElement>;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * Text prompt for video generation. Either prompt or multi_prompt must be provided, but not both.
   */
  prompt?: string;
  /**
   * The type of multi-shot video generation Default value: `"customize"`
   */
  shot_type?: "customize" | "intelligent";
};
export type KokoroAmericanEnglishInput = {
  /**
   *  Default value: `""`
   */
  prompt?: string;
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
  /**
   * Voice ID for the desired voice. Default value: `"af_heart"`
   */
  voice?:
    | "af_heart"
    | "af_alloy"
    | "af_aoede"
    | "af_bella"
    | "af_jessica"
    | "af_kore"
    | "af_nicole"
    | "af_nova"
    | "af_river"
    | "af_sarah"
    | "af_sky"
    | "am_adam"
    | "am_echo"
    | "am_eric"
    | "am_fenrir"
    | "am_liam"
    | "am_michael"
    | "am_onyx"
    | "am_puck"
    | "am_santa";
};
export type KokoroFrenchInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
  /**
   * Voice ID for the desired voice.
   */
  voice: string;
};
export type KokoroHindiInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
  /**
   * Voice ID for the desired voice.
   */
  voice: "hf_alpha" | "hf_beta" | "hm_omega" | "hm_psi";
};
export type KolorsImg2ImgInput = {
  /**
   * Enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show
   * you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for image to image
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small
   * details (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The scheduler to use for the model. Default value: `"EulerDiscreteScheduler"`
   */
  scheduler?:
    | "EulerDiscreteScheduler"
    | "EulerAncestralDiscreteScheduler"
    | "DPMSolverMultistepScheduler"
    | "DPMSolverMultistepScheduler_SDE_karras"
    | "UniPCMultistepScheduler"
    | "DEISMultistepScheduler";
  /**
   * Seed
   */
  seed?: number;
  /**
   * The strength to use for image-to-image. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and
   * uploaded before returning the response. This will increase the latency of
   * the function but it allows you to get the image directly in the response
   * without going through the CDN.
   */
  sync_mode?: boolean;
};
export type kolorsInput = {
  /**
   * Enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show
   * you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small
   * details (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible
   * for best results.
   */
  prompt: string;
  /**
   * The scheduler to use for the model. Default value: `"EulerDiscreteScheduler"`
   */
  scheduler?:
    | "EulerDiscreteScheduler"
    | "EulerAncestralDiscreteScheduler"
    | "DPMSolverMultistepScheduler"
    | "DPMSolverMultistepScheduler_SDE_karras"
    | "UniPCMultistepScheduler"
    | "DEISMultistepScheduler";
  /**
   * Seed
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and
   * uploaded before returning the response. This will increase the latency of
   * the function but it allows you to get the image directly in the response
   * without going through the CDN.
   */
  sync_mode?: boolean;
};
export type KreaWan14bTextToVideoInput = {
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Number of frames to generate. Must be a multiple of 12 plus 6, for example 6, 18, 30, 42, etc. Default value: `78`
   */
  num_frames?: number;
  /**
   * Prompt for the video-to-video generation.
   */
  prompt: string;
  /**
   * Seed for the video-to-video generation.
   */
  seed?: number;
};
export type latentsyncInput = {
  /**
   * The URL of the audio to generate the lip sync for.
   */
  audio_url: string | Blob | File;
  /**
   * Guidance scale for the model inference Default value: `1`
   */
  guidance_scale?: number;
  /**
   * Video loop mode when audio is longer than video. Options: pingpong, loop
   */
  loop_mode?: "pingpong" | "loop";
  /**
   * Random seed for generation. If None, a random seed will be used.
   */
  seed?: number;
  /**
   * The URL of the video to generate the lip sync for.
   */
  video_url: string | Blob | File;
};
export type LavaSrInput = {
  /**
   * The format for the output audio. Default value: `"mp3"`
   */
  audio_format?: "mp3" | "aac" | "m4a" | "ogg" | "opus" | "flac" | "wav";
  /**
   * The URL of the audio file to enhance.
   */
  audio_url: string | Blob | File;
  /**
   * The bitrate of the output audio. Default value: `"192k"`
   */
  bitrate?: string;
  /**
   * If `True`, applies UL-UNAS noise filtering before bandwidth extension.
   */
  denoise?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type LavaSrOutput = {
  /**
   * The enhanced audio file.
   */
  audio: AudioFile;
  /**
   * Timings for each step in the pipeline.
   */
  timings: LavaSRTimings;
};
export type LavaSRTimings = {
  /**
   * Time taken to run the inference in seconds.
   */
  inference: number;
  /**
   * Time taken to postprocess the audio in seconds.
   */
  postprocess: number;
  /**
   * Time taken to preprocess the audio in seconds.
   */
  preprocess: number;
};
export type LayerDiffusionInput = {
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale for the model. Default value: `8`
   */
  guidance_scale?: number;
  /**
   * The prompt to use for generating the negative image. Be as descriptive as possible for best results. Default value: `"text, watermark"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps for the model. Default value: `20`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results. Default value: `""`
   */
  prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
};
export type lcmInput = {
  /**
   * If set to true, the inpainting pipeline will use controlnet inpainting.
   * Only effective for inpainting pipelines.
   */
  controlnet_inpaint?: boolean;
  /**
   * If set to true, the resulting image will be checked whether it includes any
   * potentially unsafe content. If it does, it will be replaced with a black
   * image. Default value: `true`
   */
  enable_safety_checks?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. You can choose between some presets or
   * custom height and width that **must be multiples of 8**.
   *
   * If not provided:
   * - For text-to-image generations, the default size is 512x512.
   * - For image-to-image generations, the default size is the same as the input image.
   * - For inpainting generations, the default size is the same as the input image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The base image to use for guiding the image generation on image-to-image
   * generations. If the either width or height of the image is larger than 1024
   * pixels, the image will be resized to 1024 pixels while keeping the aspect ratio.
   */
  image_url?: string | Blob | File;
  /**
   * If set to true, the inpainting pipeline will only inpaint the provided mask
   * area. Only effective for inpainting pipelines.
   */
  inpaint_mask_only?: boolean;
  /**
   * The scale of the lora server to use for image generation. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The url of the lora server to use for image generation.
   */
  lora_url?: string | Blob | File;
  /**
   * The mask to use for guiding the image generation on image
   * inpainting. The model will focus on the mask area and try to fill it with
   * the most relevant content.
   *
   * The mask must be a black and white image where the white area is the area
   * that needs to be filled and the black area is the area that should be
   * ignored.
   *
   * The mask must have the same dimensions as the image passed as `image_url`.
   */
  mask_url?: string | Blob | File;
  /**
   * The model to use for generating the image. Default value: `"sdv1-5"`
   */
  model?: "sdxl" | "sdv1-5";
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. The function will return a list of images
   * with the same prompt and negative prompt but different seeds. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to use for generating the image. The more steps
   * the better the image will be but it will also take longer to generate. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the image that is passed as `image_url`. The strength
   * determines how much the generated image will be similar to the image passed as
   * `image_url`. The higher the strength the more model gets "creative" and
   * generates an image that's different from the initial image. A strength of 1.0
   * means that the initial image is more or less ignored and the model will try to
   * generate an image that's as close as possible to the prompt. Default value: `0.8`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type lcmOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   * A list of booleans indicating whether the generated image contains any
   * potentially unsafe content. If the safety check is disabled, this field
   * will all will be false.
   */
  nsfw_content_detected: Array<boolean>;
  /**
   * Number of inference steps used to generate the image. It will be the same value of the one passed in the
   * input or the default one in case none was passed. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type LcmSd15I2iInput = {
  /**
   * If set to true, the resulting image will be checked whether it includes any
   * potentially unsafe content. If it does, it will be replaced with a black
   * image. Default value: `true`
   */
  enable_safety_checks?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The image to use as a base.
   */
  image_url: string | Blob | File;
  /**
   * Mask URL for compatibility with generic LCM processing.
   */
  mask_url?: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. The function will return a list of images
   * with the same prompt and negative prompt but different seeds. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to use for generating the image. The more steps
   * the better the image will be but it will also take longer to generate. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the image. Default value: `0.8`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Lighting = {
  /**
   * The conditions of the lighting in the image to be generated.
   */
  conditions?: string;
  /**
   * The direction of the lighting in the image to be generated.
   */
  direction?: string;
  /**
   * The shadows in the image to be generated.
   */
  shadows?: string;
};
export type LightningModelsImageToImageInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The Lightning model to use.
   */
  model_name?: string;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `5`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * Scheduler / sampler to use for the image denoising process.
   */
  scheduler?:
    | "DPM++ 2M"
    | "DPM++ 2M Karras"
    | "DPM++ 2M SDE"
    | "DPM++ 2M SDE Karras"
    | "DPM++ SDE"
    | "DPM++ SDE Karras"
    | "KDPM 2A"
    | "Euler"
    | "Euler (trailing timesteps)"
    | "Euler A"
    | "LCM"
    | "EDMDPMSolverMultistepScheduler"
    | "TCDScheduler";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type LightningModelsInpaintingInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The Lightning model to use.
   */
  model_name?: string;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `5`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * Scheduler / sampler to use for the image denoising process.
   */
  scheduler?:
    | "DPM++ 2M"
    | "DPM++ 2M Karras"
    | "DPM++ 2M SDE"
    | "DPM++ 2M SDE Karras"
    | "DPM++ SDE"
    | "DPM++ SDE Karras"
    | "KDPM 2A"
    | "Euler"
    | "Euler (trailing timesteps)"
    | "Euler A"
    | "LCM"
    | "EDMDPMSolverMultistepScheduler"
    | "TCDScheduler";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type LightningModelsInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The Lightning model to use.
   */
  model_name?: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want in the image. Default value: `"(worst quality, low quality, normal quality, lowres, low details, oversaturated, undersaturated, overexposed, underexposed, grayscale, bw, bad photo, bad photography, bad art:1.4), (watermark, signature, text font, username, error, logo, words, letters, digits, autograph, trademark, name:1.2), (blur, blurry, grainy), morbid, ugly, asymmetrical, mutated malformed, mutilated, poorly lit, bad shadow, draft, cropped, out of frame, cut off, censored, jpeg artifacts, out of focus, glitch, duplicate, (airbrushed, cartoon, anime, semi-realistic, cgi, render, blender, digital art, manga, amateur:1.3), (3D ,3D Game, 3D Game Scene, 3D Character:1.1), (bad hands, bad anatomy, bad body, bad face, bad teeth, bad arms, bad legs, deformities:1.3)"`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `5`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * Scheduler / sampler to use for the image denoising process.
   */
  scheduler?:
    | "DPM++ 2M"
    | "DPM++ 2M Karras"
    | "DPM++ 2M SDE"
    | "DPM++ 2M SDE Karras"
    | "DPM++ SDE"
    | "DPM++ SDE Karras"
    | "KDPM 2A"
    | "Euler"
    | "Euler (trailing timesteps)"
    | "Euler A"
    | "LCM"
    | "EDMDPMSolverMultistepScheduler"
    | "TCDScheduler";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type LightxRecameraInput = {
  /**
   * Camera control mode. Default value: `"traj"`
   */
  camera?: "traj" | "target";
  /**
   * Camera motion mode. Default value: `"gradual"`
   */
  mode?: "gradual" | "bullet" | "direct" | "dolly-zoom";
  /**
   * Optional text prompt. If omitted, Light-X will auto-caption the video.
   */
  prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Target camera pose [theta, phi, radius, x, y] (required when camera='target').
   */
  target_pose?: Array<number>;
  /**
   * Camera trajectory parameters (required for recamera mode).
   */
  trajectory?: TrajectoryParameters;
  /**
   * URL of the input video.
   */
  video_url: string | Blob | File;
};
export type LightxRelightInput = {
  /**
   * Optional text prompt. If omitted, Light-X will auto-caption the video.
   */
  prompt?: string;
  /**
   * Frame index to use as referencen to relight the video with reference.
   */
  ref_id?: number;
  /**
   * Relighting parameters (required for relight_condition_type='ic'). Not used for 'bg' (which expects a background image URL instead).
   */
  relight_parameters?: RelightParameters;
  /**
   * URL of conditioning image. Required for relight_condition_type='ref'/'hdr'. Also required for relight_condition_type='bg' (background image).
   */
  relit_cond_img_url?: string | Blob | File;
  /**
   * Relight condition type. Default value: `"ic"`
   */
  relit_cond_type?: "ic" | "ref" | "hdr" | "bg";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * URL of the input video.
   */
  video_url: string | Blob | File;
};
export type LightxRelightOutput = {
  /**
   * Optional: normalized/processed input video (if produced by the pipeline).
   */
  input_video?: File;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video file.
   */
  video: File;
  /**
   * Optional: visualization/debug video (if produced by the pipeline).
   */
  viz_video?: File;
};
export type LineartInput = {
  /**
   * Whether to use the coarse model
   */
  coarse?: boolean;
  /**
   * URL of the image to process
   */
  image_url: string | Blob | File;
};
export type lipsyncInput = {
  /**
   *
   */
  audio_url: string | Blob | File;
  /**
   *
   */
  video_url: string | Blob | File;
};
export type LipSyncInput = {
  /**
   * URL of the input audio
   */
  audio_url: string | Blob | File;
  /**
   * The model to use for lipsyncing Default value: `"lipsync-1.9.0-beta"`
   */
  model?: "lipsync-1.8.0" | "lipsync-1.7.1" | "lipsync-1.9.0-beta";
  /**
   * Lipsync mode when audio and video durations are out of sync. Default value: `"cut_off"`
   */
  sync_mode?: "cut_off" | "loop" | "bounce" | "silence" | "remap";
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
};
export type ListAvatarsOutput = {
  /**
   * List of available avatar names
   */
  avatars: Array<string>;
};
export type ListVoicesInput = Record<string, never>;
export type ListVoicesOutput = {
  /**
   * List of available voice names
   */
  voices: Array<string>;
};
export type LiveAvatarInput = {
  /**
   * Acceleration level for faster video decoding Default value: `"none"`
   */
  acceleration?: "none" | "light" | "regular" | "high";
  /**
   * The URL of the driving audio file (WAV or MP3). The avatar will be animated to match this audio.
   */
  audio_url: string | Blob | File;
  /**
   * Enable safety checker for content moderation. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Number of frames per clip. Must be a multiple of 4. Higher values = smoother but slower generation. Default value: `48`
   */
  frames_per_clip?: number;
  /**
   * Classifier-free guidance scale. Higher values follow the prompt more closely.
   */
  guidance_scale?: number;
  /**
   * The URL of the reference image for avatar generation. The character in this image will be animated.
   */
  image_url: string | Blob | File;
  /**
   * Number of video clips to generate. Each clip is approximately 3 seconds. Set higher for longer videos. Default value: `10`
   */
  num_clips?: number;
  /**
   * A text prompt describing the scene and character. Helps guide the video generation style and context.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
};
export type LivePortraitImageInput = {
  /**
   * Amount to open mouth in 'aaa' shape
   */
  aaa?: number;
  /**
   * Amount to blink the eyes
   */
  blink?: number;
  /**
   * Size of the output image. Default value: `512`
   */
  dsize?: number;
  /**
   * Amount to shape mouth in 'eee' position
   */
  eee?: number;
  /**
   * Whether to enable the safety checker. If enabled, the model will check if the input image contains a face before processing it.
   * The safety checker will process the input image
   */
  enable_safety_checker?: boolean;
  /**
   * Amount to raise or lower eyebrows
   */
  eyebrow?: number;
  /**
   * Whether to crop the source portrait to the face-cropping space. Default value: `true`
   */
  flag_do_crop?: boolean;
  /**
   * Whether to conduct the rotation when flag_do_crop is True. Default value: `true`
   */
  flag_do_rot?: boolean;
  /**
   * Whether to set the lip to closed state before animation. Only takes effect when flag_eye_retargeting and flag_lip_retargeting are False. Default value: `true`
   */
  flag_lip_zero?: boolean;
  /**
   * Whether to paste-back/stitch the animated face cropping from the face-cropping space to the original image space. Default value: `true`
   */
  flag_pasteback?: boolean;
  /**
   * URL of the image to be animated
   */
  image_url: string | Blob | File;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Amount to move pupils horizontally
   */
  pupil_x?: number;
  /**
   * Amount to move pupils vertically
   */
  pupil_y?: number;
  /**
   * Amount to rotate the face in pitch
   */
  rotate_pitch?: number;
  /**
   * Amount to rotate the face in roll
   */
  rotate_roll?: number;
  /**
   * Amount to rotate the face in yaw
   */
  rotate_yaw?: number;
  /**
   * Scaling factor for the face crop. Default value: `2.3`
   */
  scale?: number;
  /**
   * Amount to smile
   */
  smile?: number;
  /**
   * Horizontal offset ratio for face crop.
   */
  vx_ratio?: number;
  /**
   * Vertical offset ratio for face crop. Positive values move up, negative values move down. Default value: `-0.125`
   */
  vy_ratio?: number;
  /**
   * Amount to wink
   */
  wink?: number;
  /**
   * Amount to shape mouth in 'woo' position
   */
  woo?: number;
};
export type LivePortraitInput = {
  /**
   * Amount to open mouth in 'aaa' shape
   */
  aaa?: number;
  /**
   * Batch size for the model. The larger the batch size, the faster the model will run, but the more memory it will consume. Default value: `32`
   */
  batch_size?: number;
  /**
   * Amount to blink the eyes
   */
  blink?: number;
  /**
   * Size of the output image. Default value: `512`
   */
  dsize?: number;
  /**
   * Amount to shape mouth in 'eee' position
   */
  eee?: number;
  /**
   * Whether to enable the safety checker. If enabled, the model will check if the input image contains a face before processing it.
   * The safety checker will process the input image
   */
  enable_safety_checker?: boolean;
  /**
   * Amount to raise or lower eyebrows
   */
  eyebrow?: number;
  /**
   * Whether to crop the source portrait to the face-cropping space. Default value: `true`
   */
  flag_do_crop?: boolean;
  /**
   * Whether to conduct the rotation when flag_do_crop is True. Default value: `true`
   */
  flag_do_rot?: boolean;
  /**
   * Whether to enable eye retargeting.
   */
  flag_eye_retargeting?: boolean;
  /**
   * Whether to enable lip retargeting.
   */
  flag_lip_retargeting?: boolean;
  /**
   * Whether to set the lip to closed state before animation. Only takes effect when flag_eye_retargeting and flag_lip_retargeting are False. Default value: `true`
   */
  flag_lip_zero?: boolean;
  /**
   * Whether to paste-back/stitch the animated face cropping from the face-cropping space to the original image space. Default value: `true`
   */
  flag_pasteback?: boolean;
  /**
   * Whether to use relative motion. Default value: `true`
   */
  flag_relative?: boolean;
  /**
   * Whether to enable stitching. Recommended to set to True. Default value: `true`
   */
  flag_stitching?: boolean;
  /**
   * URL of the image to be animated
   */
  image_url: string | Blob | File;
  /**
   * Amount to move pupils horizontally
   */
  pupil_x?: number;
  /**
   * Amount to move pupils vertically
   */
  pupil_y?: number;
  /**
   * Amount to rotate the face in pitch
   */
  rotate_pitch?: number;
  /**
   * Amount to rotate the face in roll
   */
  rotate_roll?: number;
  /**
   * Amount to rotate the face in yaw
   */
  rotate_yaw?: number;
  /**
   * Scaling factor for the face crop. Default value: `2.3`
   */
  scale?: number;
  /**
   * Amount to smile
   */
  smile?: number;
  /**
   * URL of the video to drive the lip syncing.
   */
  video_url: string | Blob | File;
  /**
   * Horizontal offset ratio for face crop.
   */
  vx_ratio?: number;
  /**
   * Vertical offset ratio for face crop. Positive values move up, negative values move down. Default value: `-0.125`
   */
  vy_ratio?: number;
  /**
   * Amount to wink
   */
  wink?: number;
  /**
   * Amount to shape mouth in 'woo' position
   */
  woo?: number;
};
export type LivePortraitVideoInput = {
  /**
   * Whether to prioritize source or driving audio. Default value: `"source"`
   */
  audio_priority?: "source" | "driving";
  /**
   * URL of the video to drive the lip syncing.
   */
  driving_video_url: string | Blob | File;
  /**
   * Whether to filter out NSFW content. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the video to drive the lip syncing.
   */
  source_video_url: string | Blob | File;
};
export type LlavaNextInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Maximum number of tokens to generate Default value: `64`
   */
  max_tokens?: number;
  /**
   * Prompt to be used for the image
   */
  prompt: string;
  /**
   * Temperature for sampling Default value: `0.2`
   */
  temperature?: number;
  /**
   * Top P for sampling Default value: `1`
   */
  top_p?: number;
};
export type LongcatImageEditInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the image generation. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image with.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type LongcatMultiAvatarImageAudioToVideoInput = {
  /**
   * The audio guidance scale. Higher values may lead to exaggerated mouth movements. Default value: `4`
   */
  audio_guidance_scale?: number;
  /**
   * How to combine the two audio tracks. 'para' (parallel) plays both simultaneously, 'add' (sequential) plays person 1 first then person 2. Default value: `"para"`
   */
  audio_type?: "para" | "add";
  /**
   * The URL of the audio file for person 1 (left side). Default value: `"https://raw.githubusercontent.com/meituan-longcat/LongCat-Video/refs/heads/main/assets/avatar/multi/sing_man.WAV"`
   */
  audio_url_person1?: string;
  /**
   * The URL of the audio file for person 2 (right side). Default value: `"https://raw.githubusercontent.com/meituan-longcat/LongCat-Video/refs/heads/main/assets/avatar/multi/sing_woman.WAV"`
   */
  audio_url_person2?: string;
  /**
   * Bounding box for person 1. If not provided, defaults to left half of image.
   */
  bbox_person1?: BoundingBox;
  /**
   * Bounding box for person 2. If not provided, defaults to right half of image.
   */
  bbox_person2?: BoundingBox;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image containing two speakers.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to avoid in the video generation. Default value: `"Close-up, Bright tones, overexposed, static, blurred details, subtitles, style, works, paintings, images, static, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, misshapen limbs, fused fingers, still picture, messy background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to use. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Number of video segments to generate. Each segment adds ~5 seconds of video. First segment is ~5.8s, additional segments are 5s each. Default value: `1`
   */
  num_segments?: number;
  /**
   * The prompt to guide the video generation. Default value: `"Two people are having a conversation with natural expressions and movements."`
   */
  prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Billing is per video-second (16 frames): 480p is 1 unit per second and 720p is 4 units per second. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * The text guidance scale for classifier-free guidance. Default value: `4`
   */
  text_guidance_scale?: number;
};
export type LongcatSingleAvatarAudioToVideoInput = {
  /**
   * The audio guidance scale. Higher values may lead to exaggerated mouth movements. Default value: `4`
   */
  audio_guidance_scale?: number;
  /**
   * The URL of the audio file to drive the avatar.
   */
  audio_url: string | Blob | File;
  /**
   * Whether to enable prompt expansion using an LLM to enhance the prompt for better video quality.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The negative prompt to avoid in the video generation. Default value: `"Close-up, Bright tones, overexposed, static, blurred details, subtitles, style, works, paintings, images, static, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, misshapen limbs, fused fingers, still picture, messy background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to use. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Number of video segments to generate. Each segment adds ~5 seconds of video. First segment is ~5.8s, additional segments are 5s each. Default value: `1`
   */
  num_segments?: number;
  /**
   * The prompt to guide the video generation. Default value: `"A person is talking naturally with natural expressions and movements."`
   */
  prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Billing is per video-second (16 frames): 480p is 1 unit per second and 720p is 4 units per second. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * The text guidance scale for classifier-free guidance. Default value: `4`
   */
  text_guidance_scale?: number;
};
export type LongcatSingleAvatarImageAudioToVideoInput = {
  /**
   * The audio guidance scale. Higher values may lead to exaggerated mouth movements. Default value: `4`
   */
  audio_guidance_scale?: number;
  /**
   * The URL of the audio file to drive the avatar.
   */
  audio_url: string | Blob | File;
  /**
   * Whether to enable prompt expansion using an LLM to enhance the prompt for better video quality.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image to animate.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to avoid in the video generation. Default value: `"Close-up, Bright tones, overexposed, static, blurred details, subtitles, style, works, paintings, images, static, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, misshapen limbs, fused fingers, still picture, messy background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to use. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Number of video segments to generate. Each segment adds ~5 seconds of video. First segment is ~5.8s, additional segments are 5s each. Default value: `1`
   */
  num_segments?: number;
  /**
   * The prompt to guide the video generation. Default value: `"A person is talking naturally with natural expressions and movements."`
   */
  prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Billing is per video-second (16 frames): 480p is 1 unit per second and 720p is 4 units per second. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * The text guidance scale for classifier-free guidance. Default value: `4`
   */
  text_guidance_scale?: number;
};
export type LongcatVideoDistilledImageToVideo480pInput = {
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `15`
   */
  fps?: number;
  /**
   * The URL of the image to generate a video from.
   */
  image_url: string | Blob | File;
  /**
   * The number of frames to generate. Default value: `162`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `12`
   */
  num_inference_steps?: number;
  /**
   * The prompt to guide the video generation. Default value: `"First-person view from the cockpit of a Formula 1 car. The driver's gloved hands firmly grip the intricate, carbon-fiber steering wheel adorned with numerous colorful buttons and a vibrant digital display showing race data. Beyond the windshield, a sun-drenched racetrack stretches ahead, lined with cheering spectators in the grandstands. Several rival cars are visible in the distance, creating a dynamic sense of competition. The sky above is a clear, brilliant blue, reflecting the exhilarating atmosphere of a high-speed race. high resolution 4k"`
   */
  prompt?: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LongcatVideoDistilledImageToVideo720pInput = {
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `30`
   */
  fps?: number;
  /**
   * The URL of the image to generate a video from.
   */
  image_url: string | Blob | File;
  /**
   * The number of frames to generate. Default value: `162`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `12`
   */
  num_inference_steps?: number;
  /**
   * The number of inference steps to use for refinement. Default value: `12`
   */
  num_refine_inference_steps?: number;
  /**
   * The prompt to guide the video generation. Default value: `"First-person view from the cockpit of a Formula 1 car. The driver's gloved hands firmly grip the intricate, carbon-fiber steering wheel adorned with numerous colorful buttons and a vibrant digital display showing race data. Beyond the windshield, a sun-drenched racetrack stretches ahead, lined with cheering spectators in the grandstands. Several rival cars are visible in the distance, creating a dynamic sense of competition. The sky above is a clear, brilliant blue, reflecting the exhilarating atmosphere of a high-speed race. high resolution 4k"`
   */
  prompt?: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LongcatVideoDistilledTextToVideo480pInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `15`
   */
  fps?: number;
  /**
   * The number of frames to generate. Default value: `162`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `12`
   */
  num_inference_steps?: number;
  /**
   * The prompt to guide the video generation.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LongcatVideoDistilledTextToVideo720pInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `30`
   */
  fps?: number;
  /**
   * The number of frames to generate. Default value: `162`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `12`
   */
  num_inference_steps?: number;
  /**
   * The number of inference steps to use for refinement. Default value: `12`
   */
  num_refine_inference_steps?: number;
  /**
   * The prompt to guide the video generation.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LongcatVideoImageToVideo480pInput = {
  /**
   * The acceleration level to use for the video generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `15`
   */
  fps?: number;
  /**
   * The guidance scale to use for the video generation. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to generate a video from.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use for the video generation. Default value: `"Bright tones, overexposed, static, blurred details, subtitles, style, works, paintings, images, static, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, misshapen limbs, fused fingers, still picture, messy background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `162`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use for the video generation. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to guide the video generation. Default value: `"First-person view from the cockpit of a Formula 1 car. The driver's gloved hands firmly grip the intricate, carbon-fiber steering wheel adorned with numerous colorful buttons and a vibrant digital display showing race data. Beyond the windshield, a sun-drenched racetrack stretches ahead, lined with cheering spectators in the grandstands. Several rival cars are visible in the distance, creating a dynamic sense of competition. The sky above is a clear, brilliant blue, reflecting the exhilarating atmosphere of a high-speed race. high resolution 4k"`
   */
  prompt?: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LongcatVideoImageToVideo720pInput = {
  /**
   * The acceleration level to use for the video generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `30`
   */
  fps?: number;
  /**
   * The guidance scale to use for the video generation. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to generate a video from.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use for the video generation. Default value: `"Bright tones, overexposed, static, blurred details, subtitles, style, works, paintings, images, static, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, misshapen limbs, fused fingers, still picture, messy background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `162`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use for the video generation. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The number of inference steps to use for refinement. Default value: `40`
   */
  num_refine_inference_steps?: number;
  /**
   * The prompt to guide the video generation. Default value: `"First-person view from the cockpit of a Formula 1 car. The driver's gloved hands firmly grip the intricate, carbon-fiber steering wheel adorned with numerous colorful buttons and a vibrant digital display showing race data. Beyond the windshield, a sun-drenched racetrack stretches ahead, lined with cheering spectators in the grandstands. Several rival cars are visible in the distance, creating a dynamic sense of competition. The sky above is a clear, brilliant blue, reflecting the exhilarating atmosphere of a high-speed race. high resolution 4k"`
   */
  prompt?: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LongcatVideoTextToVideo480pInput = {
  /**
   * The acceleration level to use for the video generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `15`
   */
  fps?: number;
  /**
   * The guidance scale to use for the video generation. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The negative prompt to use for the video generation. Default value: `"Bright tones, overexposed, static, blurred details, subtitles, style, works, paintings, images, static, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, misshapen limbs, fused fingers, still picture, messy background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `162`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use for the video generation. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to guide the video generation.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LongcatVideoTextToVideo720pInput = {
  /**
   * The acceleration level to use for the video generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `30`
   */
  fps?: number;
  /**
   * The guidance scale to use for the video generation. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The negative prompt to use for the video generation. Default value: `"Bright tones, overexposed, static, blurred details, subtitles, style, works, paintings, images, static, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, misshapen limbs, fused fingers, still picture, messy background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `162`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use for the video generation. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The number of inference steps to use for refinement. Default value: `40`
   */
  num_refine_inference_steps?: number;
  /**
   * The prompt to guide the video generation.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LoraImageToImageInput = {
  /**
   * Skips part of the image generation process, leading to slightly different results.
   * This means the image renders faster, too.
   */
  clip_skip?: number;
  /**
   * If set to true, the controlnet will be applied to only the conditional predictions.
   */
  controlnet_guess_mode?: boolean;
  /**
   * The control nets to use for the image generation. You can use any number of control nets
   * and they will be applied to the image at the specified timesteps.
   */
  controlnets?: Array<ControlNet>;
  /**
   * If set to true, the latents will be saved for debugging.
   */
  debug_latents?: boolean;
  /**
   * If set to true, the latents will be saved for debugging per pass.
   */
  debug_per_pass_latents?: boolean;
  /**
   * The embeddings to use for the image generation. Only a single embedding is supported at the moment.
   * The embeddings will be used to map the tokens in the prompt to the embedding weights.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The eta value to be used for the image generation.
   */
  eta?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the IC Light model image to use for the image generation.
   */
  ic_light_image_url?: string | Blob | File;
  /**
   * The URL of the IC Light model background image to use for the image generation.
   * Make sure to use a background compatible with the model.
   */
  ic_light_model_background_image_url?: string | Blob | File;
  /**
   * The URL of the IC Light model to use for the image generation.
   */
  ic_light_model_url?: string | Blob | File;
  /**
   * The path to the image encoder model to use for the image generation.
   */
  image_encoder_path?: string;
  /**
   * The subfolder of the image encoder model to use for the image generation.
   */
  image_encoder_subfolder?: string;
  /**
   * The weight name of the image encoder model to use for the image generation. Default value: `"pytorch_model.bin"`
   */
  image_encoder_weight_name?: string;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  image_format?: "jpeg" | "png";
  /**
   * URL of image to use for image to image/inpainting.
   */
  image_url?: string | Blob | File;
  /**
   * The IP adapter to use for the image generation.
   */
  ip_adapter?: Array<IPAdapter>;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * URL or HuggingFace ID of the base model to generate the image.
   */
  model_name: string;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The amount of noise to add to noise image for image. Only used if the image_url is provided. 1.0 is complete noise and 0 is no noise. Default value: `0.5`
   */
  noise_strength?: number;
  /**
   * Number of images to generate in one request. Note that the higher the batch size,
   * the longer it will take to generate the images. Default value: `1`
   */
  num_images?: number;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps
   * to generate your final result which can increase the amount of detail in your image. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The type of prediction to use for the image generation.
   * The `epsilon` is the default. Default value: `"epsilon"`
   */
  prediction_type?: "v_prediction" | "epsilon";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * If set to true, the prompt weighting syntax will be used.
   * Additionally, this will lift the 77 token limit by averaging embeddings.
   */
  prompt_weighting?: boolean;
  /**
   * Whether to set the rescale_betas_snr_zero option or not for the sampler
   */
  rescale_betas_snr_zero?: boolean;
  /**
   * Scheduler / sampler to use for the image denoising process.
   */
  scheduler?:
    | "DPM++ 2M"
    | "DPM++ 2M Karras"
    | "DPM++ 2M SDE"
    | "DPM++ 2M SDE Karras"
    | "Euler"
    | "Euler A"
    | "Euler (trailing timesteps)"
    | "LCM"
    | "LCM (trailing timesteps)"
    | "DDIM"
    | "TCD";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Optionally override the sigmas to use for the denoising process. Only works with schedulers which support the `sigmas` argument in their `set_sigmas` method.
   * Defaults to not overriding, in which case the scheduler automatically sets the sigmas based on the `num_inference_steps` parameter.
   * If set to a custom sigma schedule, the `num_inference_steps` parameter will be ignored. Cannot be set if `timesteps` is set.
   */
  sigmas?: SigmasInput;
  /**
   * The size of the tiles to be used for the image generation. Default value: `4096`
   */
  tile_height?: number;
  /**
   * The stride of the tiles to be used for the image generation. Default value: `2048`
   */
  tile_stride_height?: number;
  /**
   * The stride of the tiles to be used for the image generation. Default value: `2048`
   */
  tile_stride_width?: number;
  /**
   * The size of the tiles to be used for the image generation. Default value: `4096`
   */
  tile_width?: number;
  /**
   * Optionally override the timesteps to use for the denoising process. Only works with schedulers which support the `timesteps` argument in their `set_timesteps` method.
   * Defaults to not overriding, in which case the scheduler automatically sets the timesteps based on the `num_inference_steps` parameter.
   * If set to a custom timestep schedule, the `num_inference_steps` parameter will be ignored. Cannot be set if `sigmas` is set.
   */
  timesteps?: TimestepsInput;
  /**
   * URL or HuggingFace ID of the custom U-Net model to use for the image generation.
   */
  unet_name?: string;
  /**
   * The variant of the model to use for huggingface models, e.g. 'fp16'.
   */
  variant?: string;
};
export type LoraInpaintInput = {
  /**
   * Skips part of the image generation process, leading to slightly different results.
   * This means the image renders faster, too.
   */
  clip_skip?: number;
  /**
   * If set to true, the controlnet will be applied to only the conditional predictions.
   */
  controlnet_guess_mode?: boolean;
  /**
   * The control nets to use for the image generation. You can use any number of control nets
   * and they will be applied to the image at the specified timesteps.
   */
  controlnets?: Array<ControlNet>;
  /**
   * If set to true, the latents will be saved for debugging.
   */
  debug_latents?: boolean;
  /**
   * If set to true, the latents will be saved for debugging per pass.
   */
  debug_per_pass_latents?: boolean;
  /**
   * The embeddings to use for the image generation. Only a single embedding is supported at the moment.
   * The embeddings will be used to map the tokens in the prompt to the embedding weights.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The eta value to be used for the image generation.
   */
  eta?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the IC Light model image to use for the image generation.
   */
  ic_light_image_url?: string | Blob | File;
  /**
   * The URL of the IC Light model background image to use for the image generation.
   * Make sure to use a background compatible with the model.
   */
  ic_light_model_background_image_url?: string | Blob | File;
  /**
   * The URL of the IC Light model to use for the image generation.
   */
  ic_light_model_url?: string | Blob | File;
  /**
   * The path to the image encoder model to use for the image generation.
   */
  image_encoder_path?: string;
  /**
   * The subfolder of the image encoder model to use for the image generation.
   */
  image_encoder_subfolder?: string;
  /**
   * The weight name of the image encoder model to use for the image generation. Default value: `"pytorch_model.bin"`
   */
  image_encoder_weight_name?: string;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  image_format?: "jpeg" | "png";
  /**
   * URL of image to use for image to image/inpainting.
   */
  image_url?: string | Blob | File;
  /**
   * The IP adapter to use for the image generation.
   */
  ip_adapter?: Array<IPAdapter>;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * URL of black-and-white image to use as mask during inpainting.
   */
  mask_url?: string | Blob | File;
  /**
   * URL or HuggingFace ID of the base model to generate the image.
   */
  model_name: string;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The amount of noise to add to noise image for image. Only used if the image_url is provided. 1.0 is complete noise and 0 is no noise. Default value: `0.5`
   */
  noise_strength?: number;
  /**
   * Number of images to generate in one request. Note that the higher the batch size,
   * the longer it will take to generate the images. Default value: `1`
   */
  num_images?: number;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps
   * to generate your final result which can increase the amount of detail in your image. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The type of prediction to use for the image generation.
   * The `epsilon` is the default. Default value: `"epsilon"`
   */
  prediction_type?: "v_prediction" | "epsilon";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * If set to true, the prompt weighting syntax will be used.
   * Additionally, this will lift the 77 token limit by averaging embeddings.
   */
  prompt_weighting?: boolean;
  /**
   * Whether to set the rescale_betas_snr_zero option or not for the sampler
   */
  rescale_betas_snr_zero?: boolean;
  /**
   * Scheduler / sampler to use for the image denoising process.
   */
  scheduler?:
    | "DPM++ 2M"
    | "DPM++ 2M Karras"
    | "DPM++ 2M SDE"
    | "DPM++ 2M SDE Karras"
    | "Euler"
    | "Euler A"
    | "Euler (trailing timesteps)"
    | "LCM"
    | "LCM (trailing timesteps)"
    | "DDIM"
    | "TCD";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Optionally override the sigmas to use for the denoising process. Only works with schedulers which support the `sigmas` argument in their `set_sigmas` method.
   * Defaults to not overriding, in which case the scheduler automatically sets the sigmas based on the `num_inference_steps` parameter.
   * If set to a custom sigma schedule, the `num_inference_steps` parameter will be ignored. Cannot be set if `timesteps` is set.
   */
  sigmas?: SigmasInput;
  /**
   * The size of the tiles to be used for the image generation. Default value: `4096`
   */
  tile_height?: number;
  /**
   * The stride of the tiles to be used for the image generation. Default value: `2048`
   */
  tile_stride_height?: number;
  /**
   * The stride of the tiles to be used for the image generation. Default value: `2048`
   */
  tile_stride_width?: number;
  /**
   * The size of the tiles to be used for the image generation. Default value: `4096`
   */
  tile_width?: number;
  /**
   * Optionally override the timesteps to use for the denoising process. Only works with schedulers which support the `timesteps` argument in their `set_timesteps` method.
   * Defaults to not overriding, in which case the scheduler automatically sets the timesteps based on the `num_inference_steps` parameter.
   * If set to a custom timestep schedule, the `num_inference_steps` parameter will be ignored. Cannot be set if `sigmas` is set.
   */
  timesteps?: TimestepsInput;
  /**
   * URL or HuggingFace ID of the custom U-Net model to use for the image generation.
   */
  unet_name?: string;
  /**
   * The variant of the model to use for huggingface models, e.g. 'fp16'.
   */
  variant?: string;
};
export type loraInput = {
  /**
   * Skips part of the image generation process, leading to slightly different results.
   * This means the image renders faster, too.
   */
  clip_skip?: number;
  /**
   * If set to true, the controlnet will be applied to only the conditional predictions.
   */
  controlnet_guess_mode?: boolean;
  /**
   * The control nets to use for the image generation. You can use any number of control nets
   * and they will be applied to the image at the specified timesteps.
   */
  controlnets?: Array<ControlNet>;
  /**
   * If set to true, the latents will be saved for debugging.
   */
  debug_latents?: boolean;
  /**
   * If set to true, the latents will be saved for debugging per pass.
   */
  debug_per_pass_latents?: boolean;
  /**
   * The embeddings to use for the image generation. Only a single embedding is supported at the moment.
   * The embeddings will be used to map the tokens in the prompt to the embedding weights.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The eta value to be used for the image generation.
   */
  eta?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the IC Light model image to use for the image generation.
   */
  ic_light_image_url?: string | Blob | File;
  /**
   * The URL of the IC Light model background image to use for the image generation.
   * Make sure to use a background compatible with the model.
   */
  ic_light_model_background_image_url?: string | Blob | File;
  /**
   * The URL of the IC Light model to use for the image generation.
   */
  ic_light_model_url?: string | Blob | File;
  /**
   * The path to the image encoder model to use for the image generation.
   */
  image_encoder_path?: string;
  /**
   * The subfolder of the image encoder model to use for the image generation.
   */
  image_encoder_subfolder?: string;
  /**
   * The weight name of the image encoder model to use for the image generation. Default value: `"pytorch_model.bin"`
   */
  image_encoder_weight_name?: string;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  image_format?: "jpeg" | "png";
  /**
   * The size of the generated image. You can choose between some presets or custom height and width
   * that **must be multiples of 8**. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The IP adapter to use for the image generation.
   */
  ip_adapter?: Array<IPAdapter>;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * URL or HuggingFace ID of the base model to generate the image.
   */
  model_name: string;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate in one request. Note that the higher the batch size,
   * the longer it will take to generate the images. Default value: `1`
   */
  num_images?: number;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps
   * to generate your final result which can increase the amount of detail in your image. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The type of prediction to use for the image generation.
   * The `epsilon` is the default. Default value: `"epsilon"`
   */
  prediction_type?: "v_prediction" | "epsilon";
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * If set to true, the prompt weighting syntax will be used.
   * Additionally, this will lift the 77 token limit by averaging embeddings.
   */
  prompt_weighting?: boolean;
  /**
   * Whether to set the rescale_betas_snr_zero option or not for the sampler
   */
  rescale_betas_snr_zero?: boolean;
  /**
   * Scheduler / sampler to use for the image denoising process.
   */
  scheduler?:
    | "DPM++ 2M"
    | "DPM++ 2M Karras"
    | "DPM++ 2M SDE"
    | "DPM++ 2M SDE Karras"
    | "Euler"
    | "Euler A"
    | "Euler (trailing timesteps)"
    | "LCM"
    | "LCM (trailing timesteps)"
    | "DDIM"
    | "TCD";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Optionally override the sigmas to use for the denoising process. Only works with schedulers which support the `sigmas` argument in their `set_sigmas` method.
   * Defaults to not overriding, in which case the scheduler automatically sets the sigmas based on the `num_inference_steps` parameter.
   * If set to a custom sigma schedule, the `num_inference_steps` parameter will be ignored. Cannot be set if `timesteps` is set.
   */
  sigmas?: SigmasInput;
  /**
   * The size of the tiles to be used for the image generation. Default value: `4096`
   */
  tile_height?: number;
  /**
   * The stride of the tiles to be used for the image generation. Default value: `2048`
   */
  tile_stride_height?: number;
  /**
   * The stride of the tiles to be used for the image generation. Default value: `2048`
   */
  tile_stride_width?: number;
  /**
   * The size of the tiles to be used for the image generation. Default value: `4096`
   */
  tile_width?: number;
  /**
   * Optionally override the timesteps to use for the denoising process. Only works with schedulers which support the `timesteps` argument in their `set_timesteps` method.
   * Defaults to not overriding, in which case the scheduler automatically sets the timesteps based on the `num_inference_steps` parameter.
   * If set to a custom timestep schedule, the `num_inference_steps` parameter will be ignored. Cannot be set if `sigmas` is set.
   */
  timesteps?: TimestepsInput;
  /**
   * URL or HuggingFace ID of the custom U-Net model to use for the image generation.
   */
  unet_name?: string;
  /**
   * The variant of the model to use for huggingface models, e.g. 'fp16'.
   */
  variant?: string;
};
export type LoraInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the image generation. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The LoRAs to use for the image generation. You can use up to 3 LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type LoRAInput = {
  /**
   * URL, HuggingFace repo ID (owner/repo) to lora weights.
   */
  path: string;
  /**
   * Scale factor for LoRA application (0.0 to 4.0). Default value: `1`
   */
  scale?: number;
};
export type loraOutput = {
  /**
   * The latents saved for debugging.
   */
  debug_latents: File;
  /**
   * The latents saved for debugging per pass.
   */
  debug_per_pass_latents: File;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
};
export type LoraWeight = {
  /**
   * If set to true, the embedding will be forced to be used.
   */
  force?: boolean;
  /**
   * URL or the path to the LoRA weights. Or HF model name.
   */
  path: string;
  /**
   * The scale of the LoRA weight. This is used to scale the LoRA weight
   * before merging it with the base model. Default value: `1`
   */
  scale?: number;
};
export type LoRAWeight = {
  /**
   * URL or the path to the LoRA weights.
   */
  path: string;
  /**
   * The scale of the LoRA weight. This is used to scale the LoRA weight
   * before merging it with the base model. Default value: `1`
   */
  scale?: number;
  /**
   * Specifies the transformer to load the lora weight into. 'high' loads into the high-noise transformer, 'low' loads it into the low-noise transformer, while 'both' loads the LoRA into both transformers. Default value: `"high"`
   */
  transformer?: "high" | "low" | "both";
  /**
   * Name of the LoRA weight. Used only if `path` is a Hugging Face repository, and required only if you have more than 1 safetensors file in the repo.
   */
  weight_name?: string;
};
export type LoudnessNormalizationSetting = {
  /**
   * Enable loudness normalization for the audio Default value: `true`
   */
  enabled?: boolean;
  /**
   * Target loudness in LUFS (default -18.0) Default value: `-18`
   */
  target_loudness?: number;
  /**
   * Target peak level in dBTP (default -0.5). Default value: `-0.5`
   */
  target_peak?: number;
  /**
   * Target loudness range in LU (default 8.0) Default value: `8`
   */
  target_range?: number;
};
export type LoudnormInput = {
  /**
   * URL of the audio file to normalize
   */
  audio_url: string | Blob | File;
  /**
   * Treat mono input files as dual-mono for correct EBU R128 measurement on stereo systems
   */
  dual_mono?: boolean;
  /**
   * Integrated loudness target in LUFS. Default value: `-18`
   */
  integrated_loudness?: number;
  /**
   * Use linear normalization mode (single-pass). If false, uses dynamic mode (two-pass for better quality).
   */
  linear?: boolean;
  /**
   * Loudness range target in LU Default value: `7`
   */
  loudness_range?: number;
  /**
   * Measured integrated loudness of input file in LUFS. Required for linear mode.
   */
  measured_i?: number;
  /**
   * Measured loudness range of input file in LU. Required for linear mode.
   */
  measured_lra?: number;
  /**
   * Measured threshold of input file in LUFS. Required for linear mode.
   */
  measured_thresh?: number;
  /**
   * Measured true peak of input file in dBTP. Required for linear mode.
   */
  measured_tp?: number;
  /**
   * Offset gain in dB applied before the true-peak limiter
   */
  offset?: number;
  /**
   * Return loudness measurement summary with the normalized audio
   */
  print_summary?: boolean;
  /**
   * Maximum true peak in dBTP. Default value: `-0.1`
   */
  true_peak?: number;
};
export type LoudnormOutput = {
  /**
   * Normalized audio file
   */
  audio: File;
  /**
   * Structured loudness measurement summary (if requested)
   */
  summary?: LoudnormSummary;
};
export type LoudnormSummary = {
  /**
   * Input integrated loudness in LUFS
   */
  input_integrated?: number;
  /**
   * Input loudness range in LU
   */
  input_lra?: number;
  /**
   * Input threshold in LUFS
   */
  input_threshold?: number;
  /**
   * Input true peak in dBTP
   */
  input_true_peak?: number;
  /**
   * Type of normalization applied (Dynamic/Linear)
   */
  normalization_type?: string;
  /**
   * Output integrated loudness in LUFS
   */
  output_integrated?: number;
  /**
   * Output loudness range in LU
   */
  output_lra?: number;
  /**
   * Output threshold in LUFS
   */
  output_threshold?: number;
  /**
   * Output true peak in dBTP
   */
  output_true_peak?: number;
  /**
   * Target offset in LU
   */
  target_offset?: number;
};
export type Ltx23AudioToVideoInput = {
  /**
   * The aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image, or defaults to 16:9 if no image is provided. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16";
  /**
   * URL of the audio file to generate a video from. Duration must be between 2 and 20 seconds. Must be publicly accessible or base64 data URI.
   */
  audio_url: string | Blob | File;
  /**
   * Guidance scale for video generation. Higher values make the output more closely follow the prompt. Defaults to 5 for text-to-video, or 9 when providing an image.
   */
  guidance_scale?: number;
  /**
   * URL of an image to use as the first frame of the video. If not provided, prompt is required.
   */
  image_url?: string | Blob | File;
  /**
   * Text description of how the video should be generated. Required if image_url is not provided. When image_url is provided, this describes how the image should be animated.
   */
  prompt?: string;
};
export type Ltx23ImageToVideoInput = {
  /**
   * The aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16";
  /**
   * The duration of the generated video in seconds Default value: `"6"`
   */
  duration?: "6" | "8" | "10";
  /**
   * The URL of the end image to use for the generated video. When provided, generates a transition video between start and end frames.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video Default value: `"25"`
   */
  fps?: "24" | "25" | "48" | "50";
  /**
   * Whether to generate audio for the generated video Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The URL of the start image to use for the generated video.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to use for the generated video
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"1080p"`
   */
  resolution?: "1080p" | "1440p" | "2160p";
};
export type Ltx23TextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The duration of the generated video in seconds Default value: `"6"`
   */
  duration?: "6" | "8" | "10";
  /**
   * The frames per second of the generated video Default value: `"25"`
   */
  fps?: "24" | "25" | "48" | "50";
  /**
   * Whether to generate audio for the generated video Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The prompt to use for the generated video
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"1080p"`
   */
  resolution?: "1080p" | "1440p" | "2160p";
};
export type Ltx2AudioToVideoInput = {
  /**
   * URL of the audio file to generate a video from. Duration must be between 2 and 20 seconds. Must be publicly accessible or base64 data URI.
   */
  audio_url: string | Blob | File;
  /**
   * Guidance scale for video generation. Higher values make the output more closely follow the prompt. Defaults to 5 for text-to-video, or 9 when providing an image.
   */
  guidance_scale?: number;
  /**
   * URL of an image to use as the first frame of the video. If not provided, prompt is required.
   */
  image_url?: string | Blob | File;
  /**
   * Text description of how the video should be generated. Required if image_url is not provided. When image_url is provided, this describes how the image should be animated.
   */
  prompt?: string;
};
export type LTX2AudioToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Values below 1.0 will allow the model to change the audio, while a value of exactly 1.0 will use the input audio without modification. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The URL of the audio to generate the video from.
   */
  audio_url: string | Blob | File;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * Optional URL of an image to use as the first frame of the video.
   */
  image_url?: string | Blob | File;
  /**
   * When enabled, the number of frames will be calculated based on the audio duration and FPS. When disabled, use the specified num_frames. Default value: `true`
   */
  match_audio_length?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to preprocess the audio before using it as conditioning. Default value: `true`
   */
  preprocess_audio?: boolean;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Use 'auto' to match the input image dimensions if provided. Default value: `landscape_4_3`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2DistilledAudioToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Values below 1.0 will allow the model to change the audio, while a value of exactly 1.0 will use the input audio without modification. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The URL of the audio to generate the video from.
   */
  audio_url: string | Blob | File;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * Optional URL of an image to use as the first frame of the video.
   */
  image_url?: string | Blob | File;
  /**
   * When enabled, the number of frames will be calculated based on the audio duration and FPS. When disabled, use the specified num_frames. Default value: `true`
   */
  match_audio_length?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * Whether to preprocess the audio before using it as conditioning. Default value: `true`
   */
  preprocess_audio?: boolean;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Use 'auto' to match the input image dimensions if provided. Default value: `landscape_4_3`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2DistilledExtendVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the extended video.
   */
  end_image_url?: string | Blob | File;
  /**
   * Direction to extend the video. 'forward' extends from the end of the video, 'backward' extends from the beginning. Default value: `"forward"`
   */
  extend_direction?: "forward" | "backward";
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to use as context for the extension. Default value: `25`
   */
  num_context_frames?: number;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to extend.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2DistilledImageToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * The URL of the image to generate the video from.
   */
  image_url: string | Blob | File;
  /**
   * The direction to interpolate the image sequence in. 'Forward' goes from the start image to the end image, 'Backward' goes from the end image to the start image. Default value: `"forward"`
   */
  interpolation_direction?: "forward" | "backward";
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The prompt used for the generation.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2DistilledRetakeVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to use as end context for the retake. Default value: `25`
   */
  num_end_context_frames?: number;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of frames to use as start context for the retake. Default value: `25`
   */
  num_start_context_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The retake mode to use for the retake Default value: `"replace_audio_and_video"`
   */
  retake_mode?: "replace_audio" | "replace_video" | "replace_audio_and_video";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * The frame of the video to begin retaking from Default value: `24`
   */
  start_frame?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to retake.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2DistilledTextToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `landscape_4_3`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2DistilledVideoToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * An optional URL of an audio to use as the audio for the video. If not provided, any audio present in the input video will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The type of IC-LoRA to load. In-Context LoRA weights are used to condition the video based on edge, depth, or pose videos. Only change this from `match_preprocessor` if your videos are already preprocessed (or you are using the detailer.) Default value: `"match_preprocessor"`
   */
  ic_lora?:
    | "match_preprocessor"
    | "canny"
    | "depth"
    | "pose"
    | "detailer"
    | "none";
  /**
   * The scale of the IC-LoRA to use. This allows you to control the strength of the IC-LoRA. Default value: `1`
   */
  ic_lora_scale?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * An optional URL of an image to use as the first frame of the video.
   */
  image_url?: string | Blob | File;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * When enabled, the number of frames will be calculated based on the video duration and FPS. When disabled, use the specified num_frames. Default value: `true`
   */
  match_video_length?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The preprocessor to use for the video. When a preprocessor is used and `ic_lora_type` is set to `match_preprocessor`, the IC-LoRA will be loaded based on the preprocessor type. Default value: `"none"`
   */
  preprocessor?: "depth" | "canny" | "pose" | "none";
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to generate the video from.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type Ltx2ExtendVideoInput = {
  /**
   * Number of seconds from the input video to use as context for the extension (minimum 1 second, maximum 20 seconds). If not provided, defaults to maximize available context within the 505 frame limit.
   */
  context?: number;
  /**
   * Duration in seconds to extend the video. Maximum 20 seconds. Default value: `5`
   */
  duration?: number;
  /**
   * Where to extend the video: 'end' extends at the end, 'start' extends at the beginning. Default value: `"end"`
   */
  mode?: "start" | "end";
  /**
   * Description of what should happen in the extended portion of the video.
   */
  prompt?: string;
  /**
   * The URL of the video to extend
   */
  video_url: string | Blob | File;
};
export type LTX2ExtendVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the extended video.
   */
  end_image_url?: string | Blob | File;
  /**
   * Direction to extend the video. 'forward' extends from the end of the video, 'backward' extends from the beginning. Default value: `"forward"`
   */
  extend_direction?: "forward" | "backward";
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to use as context for the extension. Default value: `25`
   */
  num_context_frames?: number;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to extend.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2ExtendVideoOutput = {
  /**
   * The prompt used for the generation.
   */
  prompt: string;
  /**
   * The seed used for the random number generator.
   */
  seed: number;
  /**
   * The generated video.
   */
  video: VideoFile;
};
export type Ltx2ImageToVideoInput = {
  /**
   * The duration of the generated video in seconds Default value: `"6"`
   */
  duration?: "6" | "8" | "10";
  /**
   * The frames per second of the generated video Default value: `"25"`
   */
  fps?: "25" | "50";
  /**
   * Whether to generate audio for the generated video Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * URL of the image to generate the video from. Must be publicly accessible or base64 data URI. Supports PNG, JPEG, WebP, AVIF, and HEIF formats.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to generate the video from
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"1080p"`
   */
  resolution?: "1080p" | "1440p" | "2160p";
};
export type LTX2ImageToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * The URL of the image to generate the video from.
   */
  image_url: string | Blob | File;
  /**
   * The direction to interpolate the image sequence in. 'Forward' goes from the start image to the end image, 'Backward' goes from the end image to the start image. Default value: `"forward"`
   */
  interpolation_direction?: "forward" | "backward";
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt used for the generation.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRAAudioToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Values below 1.0 will allow the model to change the audio, while a value of exactly 1.0 will use the input audio without modification. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The URL of the audio to generate the video from.
   */
  audio_url: string | Blob | File;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * Optional URL of an image to use as the first frame of the video.
   */
  image_url?: string | Blob | File;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * When enabled, the number of frames will be calculated based on the audio duration and FPS. When disabled, use the specified num_frames. Default value: `true`
   */
  match_audio_length?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to preprocess the audio before using it as conditioning. Default value: `true`
   */
  preprocess_audio?: boolean;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Use 'auto' to match the input image dimensions if provided. Default value: `landscape_4_3`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRADistilledAudioToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Values below 1.0 will allow the model to change the audio, while a value of exactly 1.0 will use the input audio without modification. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The URL of the audio to generate the video from.
   */
  audio_url: string | Blob | File;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * Optional URL of an image to use as the first frame of the video.
   */
  image_url?: string | Blob | File;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * When enabled, the number of frames will be calculated based on the audio duration and FPS. When disabled, use the specified num_frames. Default value: `true`
   */
  match_audio_length?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * Whether to preprocess the audio before using it as conditioning. Default value: `true`
   */
  preprocess_audio?: boolean;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Use 'auto' to match the input image dimensions if provided. Default value: `landscape_4_3`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRADistilledExtendVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the extended video.
   */
  end_image_url?: string | Blob | File;
  /**
   * Direction to extend the video. 'forward' extends from the end of the video, 'backward' extends from the beginning. Default value: `"forward"`
   */
  extend_direction?: "forward" | "backward";
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to use as context for the extension. Default value: `25`
   */
  num_context_frames?: number;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to extend.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRADistilledImageToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * The URL of the image to generate the video from.
   */
  image_url: string | Blob | File;
  /**
   * The direction to interpolate the image sequence in. 'Forward' goes from the start image to the end image, 'Backward' goes from the end image to the start image. Default value: `"forward"`
   */
  interpolation_direction?: "forward" | "backward";
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The prompt used for the generation.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRADistilledRetakeVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to use as end context for the retake. Default value: `25`
   */
  num_end_context_frames?: number;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of frames to use as start context for the retake. Default value: `25`
   */
  num_start_context_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The retake mode to use for the retake Default value: `"replace_audio_and_video"`
   */
  retake_mode?: "replace_audio" | "replace_video" | "replace_audio_and_video";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * The frame of the video to begin retaking from Default value: `24`
   */
  start_frame?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to retake.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRADistilledTextToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `landscape_4_3`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRADistilledVideoToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * An optional URL of an audio to use as the audio for the video. If not provided, any audio present in the input video will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The type of IC-LoRA to load. In-Context LoRA weights are used to condition the video based on edge, depth, or pose videos. Only change this from `match_preprocessor` if your videos are already preprocessed (or you are using the detailer.) Default value: `"match_preprocessor"`
   */
  ic_lora?:
    | "match_preprocessor"
    | "canny"
    | "depth"
    | "pose"
    | "detailer"
    | "none";
  /**
   * The scale of the IC-LoRA to use. This allows you to control the strength of the IC-LoRA. Default value: `1`
   */
  ic_lora_scale?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * An optional URL of an image to use as the first frame of the video.
   */
  image_url?: string | Blob | File;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * When enabled, the number of frames will be calculated based on the video duration and FPS. When disabled, use the specified num_frames. Default value: `true`
   */
  match_video_length?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The preprocessor to use for the video. When a preprocessor is used and `ic_lora_type` is set to `match_preprocessor`, the IC-LoRA will be loaded based on the preprocessor type. Default value: `"none"`
   */
  preprocessor?: "depth" | "canny" | "pose" | "none";
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to generate the video from.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRAExtendVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the extended video.
   */
  end_image_url?: string | Blob | File;
  /**
   * Direction to extend the video. 'forward' extends from the end of the video, 'backward' extends from the beginning. Default value: `"forward"`
   */
  extend_direction?: "forward" | "backward";
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to use as context for the extension. Default value: `25`
   */
  num_context_frames?: number;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to extend.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRAImageToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * The URL of the image to generate the video from.
   */
  image_url: string | Blob | File;
  /**
   * The direction to interpolate the image sequence in. 'Forward' goes from the start image to the end image, 'Backward' goes from the end image to the start image. Default value: `"forward"`
   */
  interpolation_direction?: "forward" | "backward";
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt used for the generation.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRATextToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `landscape_4_3`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2LoRAVideoToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * An optional URL of an audio to use as the audio for the video. If not provided, any audio present in the input video will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The type of IC-LoRA to load. In-Context LoRA weights are used to condition the video based on edge, depth, or pose videos. Only change this from `match_preprocessor` if your videos are already preprocessed (or you are using the detailer.) Default value: `"match_preprocessor"`
   */
  ic_lora?:
    | "match_preprocessor"
    | "canny"
    | "depth"
    | "pose"
    | "detailer"
    | "none";
  /**
   * The scale of the IC-LoRA to use. This allows you to control the strength of the IC-LoRA. Default value: `1`
   */
  ic_lora_scale?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * An optional URL of an image to use as the first frame of the video.
   */
  image_url?: string | Blob | File;
  /**
   * The LoRAs to use for the generation.
   */
  loras: Array<LoRAInput>;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * When enabled, the number of frames will be calculated based on the video duration and FPS. When disabled, use the specified num_frames. Default value: `true`
   */
  match_video_length?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The preprocessor to use for the video. When a preprocessor is used and `ic_lora_type` is set to `match_preprocessor`, the IC-LoRA will be loaded based on the preprocessor type. Default value: `"none"`
   */
  preprocessor?: "depth" | "canny" | "pose" | "none";
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to generate the video from.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type Ltx2RetakeVideoInput = {
  /**
   * The duration of the video to retake in seconds Default value: `5`
   */
  duration?: number;
  /**
   * The prompt to retake the video with
   */
  prompt: string;
  /**
   * The retake mode to use for the retake Default value: `"replace_audio_and_video"`
   */
  retake_mode?: "replace_audio" | "replace_video" | "replace_audio_and_video";
  /**
   * The start time of the video to retake in seconds
   */
  start_time?: number;
  /**
   * The URL of the video to retake
   */
  video_url: string | Blob | File;
};
export type LTX2RetakeVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to use as end context for the retake. Default value: `25`
   */
  num_end_context_frames?: number;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The number of frames to use as start context for the retake. Default value: `25`
   */
  num_start_context_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The retake mode to use for the retake Default value: `"replace_audio_and_video"`
   */
  retake_mode?: "replace_audio" | "replace_video" | "replace_audio_and_video";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * The frame of the video to begin retaking from Default value: `24`
   */
  start_frame?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to retake.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type Ltx2TextToVideoInput = {
  /**
   * The duration of the generated video in seconds Default value: `"6"`
   */
  duration?: "6" | "8" | "10";
  /**
   * The frames per second of the generated video Default value: `"25"`
   */
  fps?: "25" | "50";
  /**
   * Whether to generate audio for the generated video Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The prompt to generate the video from
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"1080p"`
   */
  resolution?: "1080p" | "1440p" | "2160p";
};
export type LTX2TextToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `landscape_4_3`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type LTX2VideoToVideoInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high" | "full";
  /**
   * Audio conditioning strength. Lower values represent more freedom given to the model to change the audio content. Default value: `1`
   */
  audio_strength?: number;
  /**
   * An optional URL of an audio to use as the audio for the video. If not provided, any audio present in the input video will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * The camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `"none"`
   */
  camera_lora?:
    | "dolly_in"
    | "dolly_out"
    | "dolly_left"
    | "dolly_right"
    | "jib_up"
    | "jib_down"
    | "static"
    | "none";
  /**
   * The scale of the camera LoRA to use. This allows you to control the camera movement of the generated video more accurately than just prompting the model to move the camera. Default value: `1`
   */
  camera_lora_scale?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The strength of the end image to use for the video generation. Default value: `1`
   */
  end_image_strength?: number;
  /**
   * The URL of the image to use as the end of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The type of IC-LoRA to load. In-Context LoRA weights are used to condition the video based on edge, depth, or pose videos. Only change this from `match_preprocessor` if your videos are already preprocessed (or you are using the detailer.) Default value: `"match_preprocessor"`
   */
  ic_lora?:
    | "match_preprocessor"
    | "canny"
    | "depth"
    | "pose"
    | "detailer"
    | "none";
  /**
   * The scale of the IC-LoRA to use. This allows you to control the strength of the IC-LoRA. Default value: `1`
   */
  ic_lora_scale?: number;
  /**
   * The strength of the image to use for the video generation. Default value: `1`
   */
  image_strength?: number;
  /**
   * An optional URL of an image to use as the first frame of the video.
   */
  image_url?: string | Blob | File;
  /**
   * When true, match the output FPS to the input video's FPS instead of using the default target FPS. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * When enabled, the number of frames will be calculated based on the video duration and FPS. When disabled, use the specified num_frames. Default value: `true`
   */
  match_video_length?: boolean;
  /**
   * The negative prompt to generate the video from. Default value: `"blurry, out of focus, overexposed, underexposed, low contrast, washed out colors, excessive noise, grainy texture, poor lighting, flickering, motion blur, distorted proportions, unnatural skin tones, deformed facial features, asymmetrical face, missing facial features, extra limbs, disfigured hands, wrong hand count, artifacts around text, inconsistent perspective, camera shake, incorrect depth of field, background too sharp, background clutter, distracting reflections, harsh shadows, inconsistent lighting direction, color banding, cartoonish rendering, 3D CGI look, unrealistic materials, uncanny valley effect, incorrect ethnicity, wrong gender, exaggerated expressions, wrong gaze direction, mismatched lip sync, silent or muted audio, distorted voice, robotic voice, echo, background noise, off-sync audio,incorrect dialogue, added dialogue, repetitive speech, jittery movement, awkward pauses, incorrect timing, unnatural transitions, inconsistent framing, tilted camera, flat lighting, inconsistent tone, cinematic oversaturation, stylized filters, or AI artifacts."`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Default value: `121`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The preprocessor to use for the video. When a preprocessor is used and `ic_lora_type` is set to `match_preprocessor`, the IC-LoRA will be loaded based on the preprocessor type. Default value: `"none"`
   */
  preprocessor?: "depth" | "canny" | "pose" | "none";
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Whether to use multi-scale generation. If True, the model will generate the video at a smaller scale first, then use the smaller video to guide the generation of a video at or above your requested size. This results in better coherence and details. Default value: `true`
   */
  use_multiscale?: boolean;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The size of the generated video. Default value: `auto`
   */
  video_size?:
    | ImageSize
    | "auto"
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Video conditioning strength. Lower values represent more freedom given to the model to change the video content. Default value: `1`
   */
  video_strength?: number;
  /**
   * The URL of the video to generate the video from.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type Ltx2VideoTrainerInput = {
  /**
   * Aspect ratio to use for training. Default value: `"1:1"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * Normalize audio peak amplitude to a consistent level. Recommended for consistent audio levels across the dataset. Default value: `true`
   */
  audio_normalize?: boolean;
  /**
   * When audio duration doesn't match video duration, stretch/compress audio without changing pitch. If disabled, audio is trimmed or padded with silence. Default value: `true`
   */
  audio_preserve_pitch?: boolean;
  /**
   * If true, videos will be automatically scaled to the target frame count and fps. This option has no effect on image datasets.
   */
  auto_scale_input?: boolean;
  /**
   * When enabled, the trainer returns a downloadable archive of your preprocessed training data for manual inspection. Use this to verify that your videos, images, and captions were processed correctly before committing to a full training run.
   */
  debug_dataset?: boolean;
  /**
   * Probability of conditioning on the first frame during training. Higher values improve image-to-video performance. Default value: `0.5`
   */
  first_frame_conditioning_p?: number;
  /**
   * Target frames per second for the video. Default value: `25`
   */
  frame_rate?: number;
  /**
   * Whether to generate audio in validation samples. Default value: `true`
   */
  generate_audio_in_validation?: boolean;
  /**
   * Learning rate for optimization. Higher values can lead to faster training but may cause overfitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * Number of frames per training sample. Must satisfy frames % 8 == 1 (e.g., 1, 9, 17, 25, 33, 41, 49, 57, 65, 73, 81, 89, 97). Default value: `89`
   */
  number_of_frames?: number;
  /**
   * The number of training steps. Default value: `2000`
   */
  number_of_steps?: number;
  /**
   * The rank of the LoRA adaptation. Higher values increase capacity but use more memory. Default value: `"32"`
   */
  rank?: "8" | "16" | "32" | "64" | "128";
  /**
   * Resolution to use for training. Higher resolutions require more memory. Default value: `"medium"`
   */
  resolution?: "low" | "medium" | "high";
  /**
   * The duration threshold in seconds. If a video is longer than this, it will be split into scenes. Default value: `30`
   */
  split_input_duration_threshold?: number;
  /**
   * If true, videos above a certain duration threshold will be split into scenes. Default value: `true`
   */
  split_input_into_scenes?: boolean;
  /**
   * STG (Spatio-Temporal Guidance) scale. 0.0 disables STG. Recommended value is 1.0. Default value: `1`
   */
  stg_scale?: number;
  /**
   * URL to zip archive with videos or images. Try to use at least 10 files, although more is better.
   *
   * **Supported video formats:** .mp4, .mov, .avi, .mkv
   * **Supported image formats:** .png, .jpg, .jpeg
   *
   * Note: The dataset must contain ONLY videos OR ONLY images - mixed datasets are not supported.
   *
   * The archive can also contain text files with captions. Each text file should have the same name as the media file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * A phrase that will trigger the LoRA style. Will be prepended to captions during training. Default value: `""`
   */
  trigger_phrase?: string;
  /**
   * A list of validation prompts to use during training. When providing an image, _all_ validation inputs must have an image.
   */
  validation?: Array<Validation>;
  /**
   * The aspect ratio to use for validation. Default value: `"1:1"`
   */
  validation_aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * Target frames per second for validation videos. Default value: `25`
   */
  validation_frame_rate?: number;
  /**
   * A negative prompt to use for validation. Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  validation_negative_prompt?: string;
  /**
   * The number of frames in validation videos. Default value: `89`
   */
  validation_number_of_frames?: number;
  /**
   * The resolution to use for validation. Default value: `"high"`
   */
  validation_resolution?: "low" | "medium" | "high";
  /**
   * Enable joint audio-video training. If None (default), automatically detects whether input videos have audio. Set to True to force audio training, or False to disable.
   */
  with_audio?: boolean;
};
export type Ltx2VideoTrainerOutput = {
  /**
   * Configuration used for setting up inference endpoints.
   */
  config_file: File;
  /**
   * A downloadable archive containing the preprocessed training data, including decoded videos and audio. Only present when `debug_dataset` is enabled in the input.
   */
  debug_dataset?: File;
  /**
   * URL to the trained LoRA weights (.safetensors).
   */
  lora_file: File;
  /**
   * The URL to the validation videos, if any.
   */
  video?: File;
};
export type LtxVideo13bDevInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9";
  /**
   * Whether to use a detail pass. If True, the model will perform a second pass to refine the video and enhance details. This incurs a 2.0x cost multiplier on the base price.
   */
  enable_detail_pass?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Number of inference steps during the first pass. Default value: `30`
   */
  first_pass_num_inference_steps?: number;
  /**
   * The frame rate of the video. Default value: `24`
   */
  frame_rate?: number;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Number of inference steps during the second pass. Default value: `30`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `17`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The factor for adaptive instance normalization (AdaIN) applied to generated video chunks after the first. This can help deal with a gradual increase in saturation/contrast in the generated video by normalizing the color distribution across the video. A high value will ensure the color distribution is more consistent across the video, while a low value will allow for more variation in color distribution. Default value: `0.5`
   */
  temporal_adain_factor?: number;
  /**
   * The compression ratio for tone mapping. This is used to compress the dynamic range of the video to improve visual quality. A value of 0.0 means no compression, while a value of 1.0 means maximum compression.
   */
  tone_map_compression_ratio?: number;
};
export type LtxVideoImageToVideoInput = {
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to generate the video from.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to generate the video from. Default value: `"low quality, worst quality, deformed, distorted, disfigured, motion smear, motion artifacts, fused fingers, bad anatomy, weird hand, ugly"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to take. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed to use for random number generation.
   */
  seed?: number;
};
export type LtxVideoInput = {
  /**
   * The guidance scale to use. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The negative prompt to generate the video from. Default value: `"low quality, worst quality, deformed, distorted, disfigured, motion smear, motion artifacts, fused fingers, bad anatomy, weird hand, ugly"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to take. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed to use for random number generation.
   */
  seed?: number;
};
export type LtxVideoLoraImageToVideoInput = {
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16" | "auto";
  /**
   * Whether to expand the prompt using the LLM.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the video. Default value: `25`
   */
  frames_per_second?: number;
  /**
   * The URL of the image to use as input.
   */
  image_url: string | Blob | File;
  /**
   * The LoRA weights to use for generation.
   */
  loras?: Array<LoRAWeight>;
  /**
   * The negative prompt to use. Default value: `"blurry, low quality, low resolution, inconsistent motion, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to use. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The number of frames in the video. Default value: `89`
   */
  number_of_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The resolution of the video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * The seed to use for generation.
   */
  seed?: number;
};
export type LtxVideoLoraInput = {
  /**
   * The aspect ratio of the video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * Whether to expand the prompt using the LLM.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the video. Default value: `25`
   */
  frames_per_second?: number;
  /**
   * The LoRA weights to use for generation.
   */
  loras?: Array<LoRAWeight>;
  /**
   * The negative prompt to use. Default value: `"blurry, low quality, low resolution, inconsistent motion, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to use. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The number of frames in the video. Default value: `89`
   */
  number_of_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The resolution of the video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * The seed to use for generation.
   */
  seed?: number;
};
export type LtxVideoTrainerInput = {
  /**
   * The aspect ratio to use for training. This is the aspect ratio of the video. Default value: `"1:1"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * If true, videos will be automatically scaled to the target frame count and fps. This option has no effect on image datasets.
   */
  auto_scale_input?: boolean;
  /**
   * The target frames per second for the video. Default value: `25`
   */
  frame_rate?: number;
  /**
   * The rate at which the model learns. Higher values can lead to faster training, but over-fitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * The number of frames to use for training. This is the number of frames per second multiplied by the number of seconds. Default value: `81`
   */
  number_of_frames?: number;
  /**
   * The number of steps to train for. Default value: `1000`
   */
  number_of_steps?: number;
  /**
   * The rank of the LoRA. Default value: `"128"`
   */
  rank?: "8" | "16" | "32" | "64" | "128";
  /**
   * The resolution to use for training. This is the resolution of the video. Default value: `"medium"`
   */
  resolution?: "low" | "medium" | "high";
  /**
   * The duration threshold in seconds. If a video is longer than this, it will be split into scenes. If you provide captions for a split video, the caption will be applied to each scene. If you do not provide captions, scenes will be auto-captioned. Default value: `30`
   */
  split_input_duration_threshold?: number;
  /**
   * If true, videos above a certain duration threshold will be split into scenes. If you provide captions for a split video, the caption will be applied to each scene. If you do not provide captions, scenes will be auto-captioned. This option has no effect on image datasets. Default value: `true`
   */
  split_input_into_scenes?: boolean;
  /**
   * URL to zip archive with videos or images. Try to use at least 10 files, although more is better.
   *
   * **Supported video formats:** .mp4, .mov, .avi, .mkv
   * **Supported image formats:** .png, .jpg, .jpeg
   *
   * Note: The dataset must contain ONLY videos OR ONLY images - mixed datasets are not supported.
   *
   * The archive can also contain text files with captions. Each text file should have the same name as the media file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * The phrase that will trigger the model to generate an image. Default value: `""`
   */
  trigger_phrase?: string;
  /**
   * A list of validation prompts to use during training. When providing an image, _all_ validation inputs must have an image.
   */
  validation?: Array<Validation>;
  /**
   * The aspect ratio to use for validation. Default value: `"1:1"`
   */
  validation_aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * A negative prompt to use for validation. Default value: `"blurry, low quality, bad quality, out of focus"`
   */
  validation_negative_prompt?: string;
  /**
   * The number of frames to use for validation. Default value: `81`
   */
  validation_number_of_frames?: number;
  /**
   * The resolution to use for validation. Default value: `"high"`
   */
  validation_resolution?: "low" | "medium" | "high";
  /**
   * If true, the validation videos will be reversed. This is useful for effects that are learned in reverse and then applied in reverse.
   */
  validation_reverse?: boolean;
};
export type LtxVideoTrainerOutput = {
  /**
   * Configuration used for setting up the inference endpoints.
   */
  config_file: File;
  /**
   * URL to the trained LoRA weights.
   */
  lora_file: File;
  /**
   * The URL to the validations video.
   */
  video?: File;
};
export type LtxVideoV095ImageToVideoInput = {
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Image URL for Image-to-Video task
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type LtxVideoV095Input = {
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type lucidfluxInput = {
  /**
   * The guidance to use for the diffusion process. Default value: `4`
   */
  guidance?: number;
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * Seed used for random number generation Default value: `42`
   */
  seed?: number;
  /**
   * The height of the output image. Default value: `1024`
   */
  target_height?: number;
  /**
   * The width of the output image. Default value: `1024`
   */
  target_width?: number;
};
export type LucyEditDevInput = {
  /**
   * Whether to enhance the prompt for better results. Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * Text description of the desired video content
   */
  prompt: string;
  /**
   * If set to true, the function will wait for the video to be generated
   * and uploaded before returning the response. This will increase the
   * latency of the function but it allows you to get the video directly
   * in the response without going through the CDN. Default value: `true`
   */
  sync_mode?: boolean;
  /**
   * URL of the video to edit
   */
  video_url: string | Blob | File;
};
export type LucyEditProInput = {
  /**
   * Whether to enhance the prompt for better results. Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * Text description of the desired video content
   */
  prompt: string;
  /**
   * Resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p";
  /**
   * If set to true, the function will wait for the video to be generated
   * and uploaded before returning the response. This will increase the
   * latency of the function but it allows you to get the video directly
   * in the response without going through the CDN. Default value: `true`
   */
  sync_mode?: boolean;
  /**
   * URL of the video to edit
   */
  video_url: string | Blob | File;
};
export type LucyI2vInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Text description of the desired video content
   */
  prompt: string;
  /**
   * If set to true, the function will wait for the image to be generated
   * and uploaded before returning the response. This will increase the
   * latency of the function but it allows you to get the image directly
   * in the response without going through the CDN. Default value: `true`
   */
  sync_mode?: boolean;
};
export type LucyRestyleInput = {
  /**
   * Whether to enhance the prompt for better results. Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * Text description of the desired video content
   */
  prompt: string;
  /**
   * Resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p";
  /**
   * Seed for video generation
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the video to be generated
   * and uploaded before returning the response. This will increase the
   * latency of the function but it allows you to get the video directly
   * in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * URL of the video to edit
   */
  video_url: string | Blob | File;
};
export type LumaDreamMachineRay2FlashModifyInput = {
  /**
   * Optional URL of the first frame image for modification
   */
  image_url?: string | Blob | File;
  /**
   * Amount of modification to apply to the video, adhere_1 is the least amount of modification, reimagine_3 is the most Default value: `"flex_1"`
   */
  mode?:
    | "adhere_1"
    | "adhere_2"
    | "adhere_3"
    | "flex_1"
    | "flex_2"
    | "flex_3"
    | "reimagine_1"
    | "reimagine_2"
    | "reimagine_3";
  /**
   * Instruction for modifying the video
   */
  prompt?: string;
  /**
   * URL of the input video to modify
   */
  video_url: string | Blob | File;
};
export type LumaDreamMachineRay2ImageToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * The duration of the generated video Default value: `"5s"`
   */
  duration?: "5s" | "9s";
  /**
   * Final image to end the video with. Can be used together with image_url.
   */
  end_image_url?: string | Blob | File;
  /**
   * Initial image to start the video from. Can be used together with end_image_url.
   */
  image_url?: string | Blob | File;
  /**
   * Whether the video should loop (end of video is blended with the beginning)
   */
  loop?: boolean;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video (720p costs 2x more, 1080p costs 4x more) Default value: `"540p"`
   */
  resolution?: "540p" | "720p" | "1080p";
};
export type LumaDreamMachineRay2Input = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * The duration of the generated video (9s costs 2x more) Default value: `"5s"`
   */
  duration?: "5s" | "9s";
  /**
   * Whether the video should loop (end of video is blended with the beginning)
   */
  loop?: boolean;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video (720p costs 2x more, 1080p costs 4x more) Default value: `"540p"`
   */
  resolution?: "540p" | "720p" | "1080p";
};
export type LumaDreamMachineRay2ReframeInput = {
  /**
   * The aspect ratio of the reframed video
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * X position of the grid for reframing
   */
  grid_position_x?: number;
  /**
   * Y position of the grid for reframing
   */
  grid_position_y?: number;
  /**
   * Optional URL of the first frame image for reframing
   */
  image_url?: string | Blob | File;
  /**
   * Optional prompt for reframing
   */
  prompt?: string;
  /**
   * URL of the input video to reframe
   */
  video_url: string | Blob | File;
  /**
   * End X coordinate for reframing
   */
  x_end?: number;
  /**
   * Start X coordinate for reframing
   */
  x_start?: number;
  /**
   * End Y coordinate for reframing
   */
  y_end?: number;
  /**
   * Start Y coordinate for reframing
   */
  y_start?: number;
};
export type LumaPhotonInput = {
  /**
   * The aspect ratio of the generated video Default value: `"1:1"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   *
   */
  prompt: string;
};
export type LumaPhotonModifyInput = {
  /**
   * The aspect ratio of the reframed image
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * URL of the input image to reframe
   */
  image_url: string | Blob | File;
  /**
   * Instruction for modifying the image
   */
  prompt?: string;
  /**
   * The strength of the initial image. Higher strength values are corresponding to more influence of the initial image on the output.
   */
  strength: number;
};
export type LumaPhotonReframeInput = {
  /**
   * The aspect ratio of the reframed image
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * X position of the grid for reframing
   */
  grid_position_x?: number;
  /**
   * Y position of the grid for reframing
   */
  grid_position_y?: number;
  /**
   * URL of the input image to reframe
   */
  image_url: string | Blob | File;
  /**
   * Optional prompt for reframing
   */
  prompt?: string;
  /**
   * End X coordinate for reframing
   */
  x_end?: number;
  /**
   * Start X coordinate for reframing
   */
  x_start?: number;
  /**
   * End Y coordinate for reframing
   */
  y_end?: number;
  /**
   * Start Y coordinate for reframing
   */
  y_start?: number;
};
export type LuminaImageV2Input = {
  /**
   * Whether to apply normalization-based guidance scale. Default value: `true`
   */
  cfg_normalization?: boolean;
  /**
   * The ratio of the timestep interval to apply normalization-based guidance scale. Default value: `1`
   */
  cfg_trunc_ratio?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The system prompt to use. Default value: `"You are an assistant designed to generate superior images with the superior degree of image-text alignment based on textual prompts or user prompts."`
   */
  system_prompt?: string;
};
export type LuxTtsInput = {
  /**
   * URL of the reference audio file for voice cloning. The model will mimic the voice characteristics from this audio.
   */
  audio_url: string | Blob | File;
  /**
   * Classifier-free guidance scale. Higher values increase adherence to the reference voice at the cost of diversity. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * Maximum length of the reference audio to use for voice encoding, in seconds. Longer durations capture more voice characteristics but increase processing time. Default value: `5`
   */
  max_ref_length?: number;
  /**
   * Number of flow-matching inference steps. 4 is recommended for best efficiency. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The text to be converted to speech.
   */
  prompt: string;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
};
export type LuxTtsOutput = {
  /**
   * The generated speech audio file at 48kHz.
   */
  audio: File;
  /**
   *
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type lynxInput = {
  /**
   * Aspect ratio of the generated video (16:9, 9:16, or 1:1) Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Image guidance scale. Controls how closely the generated video follows the reference image. Higher values increase adherence to the reference image but may decrease quality. Default value: `2`
   */
  guidance_scale_2?: number;
  /**
   * The URL of the subject image to be used for video generation
   */
  image_url: string | Blob | File;
  /**
   * Identity preservation scale. Controls how closely the generated video preserves the subject's identity from the reference image. Default value: `1`
   */
  ip_scale?: number;
  /**
   * Negative prompt to guide what should not appear in the generated video Default value: `"Bright tones, overexposed, blurred background, static, subtitles, style, works, paintings, images, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, misshapen limbs, fused fingers, still picture, messy background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames in the generated video. Must be between 9 to 100. Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Text prompt to guide video generation
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p) Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Reference image scale. Controls the influence of the reference image on the generated video. Default value: `1`
   */
  strength?: number;
};
export type lynxOutput = {
  /**
   * The seed used for generation
   */
  seed: number;
  /**
   * The generated video file
   */
  video: VideoFile;
};
export type lyria2Input = {
  /**
   * A description of what to exclude from the generated audio Default value: `"low quality"`
   */
  negative_prompt?: string;
  /**
   * The text prompt describing the music you want to generate
   */
  prompt: string;
  /**
   * A seed for deterministic generation. If provided, the model will attempt to produce the same audio given the same prompt and other parameters.
   */
  seed?: number;
};
export type MagiExtendVideoInput = {
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32" | "64";
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * The frame to begin the generation from, with the remaining frames will be treated as the prefix video. The final video will contain the frames up until this number unchanged, followed by the generated frames. The default start frame is 32 frames before the end of the video, which gives optimal results.
   */
  start_frame?: number;
  /**
   * URL of the input video to represent the beginning of the video. If the input video does not match the chosen aspect ratio, it is resized and center cropped.
   */
  video_url: string | Blob | File;
};
export type MagiImageToVideoInput = {
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the input image to represent the first frame of the video. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32" | "64";
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type magiInput = {
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32" | "64";
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type magiOutput = {
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video file.
   */
  video: File;
};
export type MakeupApplicationInput = {
  /**
   * Aspect ratio for 4K output (default: 3:4 for portraits)
   */
  aspect_ratio?: AspectRatio;
  /**
   * Portrait image URL for makeup application
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"medium"`
   */
  intensity?: "light" | "medium" | "heavy" | "dramatic";
  /**
   *  Default value: `"natural"`
   */
  makeup_style?:
    | "natural"
    | "glamorous"
    | "smoky_eyes"
    | "bold_lips"
    | "no_makeup"
    | "remove_makeup"
    | "dramatic"
    | "bridal"
    | "professional"
    | "korean_style"
    | "artistic";
};
export type MareyI2vInput = {
  /**
   * The dimensions of the generated video in width x height format. Default value: `"1920x1080"`
   */
  dimensions?:
    | "1920x1080"
    | "1080x1920"
    | "1152x1152"
    | "1536x1152"
    | "1152x1536";
  /**
   * The duration of the generated video. Default value: `"5s"`
   */
  duration?: "5s" | "10s";
  /**
   * Controls how strongly the generation is guided by the prompt (0-20). Higher values follow the prompt more closely.
   */
  guidance_scale?: number;
  /**
   * The URL of the image to use as the first frame of the video.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt used to guide the model away from undesirable features. Default value: `"<synthetic> <scene cut> low-poly, flat shader, bad rigging, stiff animation, uncanny eyes, low-quality textures, looping glitch, cheap effect, overbloom, bloom spam, default lighting, game asset, stiff face, ugly specular, AI artifacts"`
   */
  negative_prompt?: string;
  /**
   * The prompt to generate a video from
   */
  prompt: string;
  /**
   * Seed for random number generation. Use -1 for random seed each run. Default value: `-1`
   */
  seed?: number;
};
export type MareyPoseTransferInput = {
  /**
   * Optional first frame image URL to use as the first frame of the generated video
   */
  first_frame_image_url?: string | Blob | File;
  /**
   * Negative prompt used to guide the model away from undesirable features. Default value: `"<synthetic> <scene cut> low-poly, flat shader, bad rigging, stiff animation, uncanny eyes, low-quality textures, looping glitch, cheap effect, overbloom, bloom spam, default lighting, game asset, stiff face, ugly specular, AI artifacts"`
   */
  negative_prompt?: string;
  /**
   * The prompt to generate a video from
   */
  prompt: string;
  /**
   * Optional reference image URL to use for pose control or as a starting frame
   */
  reference_image_url?: string | Blob | File;
  /**
   * Seed for random number generation. Use -1 for random seed each run. Default value: `-1`
   */
  seed?: number;
  /**
   * The URL of the video to use as the control video.
   */
  video_url: string | Blob | File;
};
export type MareyT2vInput = {
  /**
   * The dimensions of the generated video in width x height format. Default value: `"1920x1080"`
   */
  dimensions?: "1920x1080" | "1152x1152" | "1536x1152" | "1152x1536";
  /**
   * The duration of the generated video. Default value: `"5s"`
   */
  duration?: "5s" | "10s";
  /**
   * Controls how strongly the generation is guided by the prompt (0-20). Higher values follow the prompt more closely.
   */
  guidance_scale?: number;
  /**
   * Negative prompt used to guide the model away from undesirable features. Default value: `"<synthetic> <scene cut> low-poly, flat shader, bad rigging, stiff animation, uncanny eyes, low-quality textures, looping glitch, cheap effect, overbloom, bloom spam, default lighting, game asset, stiff face, ugly specular, AI artifacts"`
   */
  negative_prompt?: string;
  /**
   * The prompt to generate a video from
   */
  prompt: string;
  /**
   * Seed for random number generation. Use -1 for random seed each run. Default value: `-1`
   */
  seed?: number;
};
export type MarigoldDepthMapInput = {
  /**
   * Number of predictions to average over. Defaults to `10`. The higher the number, the more accurate the result, but the slower the inference. Default value: `10`
   */
  ensemble_size?: number;
  /**
   * Input image url.
   */
  image_url: string | Blob | File;
  /**
   * Number of denoising steps. Defaults to `10`. The higher the number, the more accurate the result, but the slower the inference. Default value: `10`
   */
  num_inference_steps?: number;
  /**
   * Maximum processing resolution. Defaults `0` which means it uses the size of the input image.
   */
  processing_res?: number;
};
export type MaskInput = {
  /**
   * The URL of the image to remove objects from.
   */
  image_url: string | Blob | File;
  /**
   * Amount of pixels to expand the mask by. Range: 0-50 Default value: `15`
   */
  mask_expansion?: number;
  /**
   * The URL of the mask image. White pixels (255) indicate areas to remove.
   */
  mask_url: string | Blob | File;
  /**
   *  Default value: `"best_quality"`
   */
  model?: "low_quality" | "medium_quality" | "high_quality" | "best_quality";
};
export type MaskMetadata = {
  /**
   * Bounding box for the mask in normalized cxcywh coordinates.
   */
  box?: Array<number>;
  /**
   * Index of the mask inside the model output.
   */
  index: number;
  /**
   * Score for this mask.
   */
  score?: number;
};
export type MayaBatchInput = {
  /**
   * Maximum SNAC tokens per generation. Default value: `2000`
   */
  max_tokens?: number;
  /**
   * Output audio format for all generated speech files Default value: `"wav"`
   */
  output_format?: "wav" | "mp3";
  /**
   * List of voice descriptions for each text. Must match the length of texts list. Each describes the voice/character attributes.
   */
  prompts: Array<string>;
  /**
   * Repetition penalty for all generations. Default value: `1.1`
   */
  repetition_penalty?: number;
  /**
   * Output audio sample rate for all generations. 48 kHz provides higher quality, 24 kHz is faster. Default value: `"48 kHz"`
   */
  sample_rate?: "48 kHz" | "24 kHz";
  /**
   * Sampling temperature for all generations. Default value: `0.4`
   */
  temperature?: number;
  /**
   * List of texts to synthesize into speech. You can embed emotion tags in each text using the format <emotion_name>.
   */
  texts: Array<string>;
  /**
   * Nucleus sampling parameter for all generations. Default value: `0.9`
   */
  top_p?: number;
};
export type MayaBatchOutput = {
  /**
   * List of generated audio files
   */
  audios: Array<File>;
  /**
   * Average real-time factor across all generations
   */
  average_rtf: number;
  /**
   * Duration of each generated audio in seconds
   */
  durations: Array<number>;
  /**
   * Sample rate of all generated audio files
   */
  sample_rate: string;
  /**
   * Total time taken to generate all audio files in seconds
   */
  total_generation_time: number;
};
export type mayaInput = {
  /**
   * Maximum number of SNAC tokens to generate (7 tokens per frame). Controls maximum audio length. Default value: `2000`
   */
  max_tokens?: number;
  /**
   * Output audio format for the generated speech Default value: `"wav"`
   */
  output_format?: "wav" | "mp3";
  /**
   * Description of the voice/character. Includes attributes like age, accent, pitch, timbre, pacing, tone, and intensity. See examples for format.
   */
  prompt: string;
  /**
   * Penalty for repeating tokens. Higher values reduce repetition artifacts. Default value: `1.1`
   */
  repetition_penalty?: number;
  /**
   * Output audio sample rate. 48 kHz provides higher quality audio, 24 kHz is faster. Default value: `"48 kHz"`
   */
  sample_rate?: "48 kHz" | "24 kHz";
  /**
   * Sampling temperature. Lower values (0.2-0.5) produce more stable/consistent audio. Higher values add variation. Default value: `0.4`
   */
  temperature?: number;
  /**
   * The text to synthesize into speech. You can embed emotion tags anywhere in the text using the format <emotion_name>. Available emotions: laugh, laugh_harder, sigh, chuckle, gasp, angry, excited, whisper, cry, scream, sing, snort, exhale, gulp, giggle, sarcastic, curious. Example: 'Hello world! <excited> This is amazing!' or 'I can't believe this <sigh> happened again.'
   */
  text: string;
  /**
   * Nucleus sampling parameter. Controls diversity of token selection. Default value: `0.9`
   */
  top_p?: number;
};
export type mayaOutput = {
  /**
   * The generated audio file containing the speech (WAV or MP3 format, 24kHz or 48kHz mono depending on upsampler)
   */
  audio: File;
  /**
   * Duration of the generated audio in seconds
   */
  duration: number;
  /**
   * Time taken to generate the audio in seconds
   */
  generation_time: number;
  /**
   * Real-time factor (generation_time / audio_duration). Lower is better.
   */
  rtf: number;
  /**
   * Sample rate of the generated audio
   */
  sample_rate: string;
};
export type MergeAudioInput = {
  /**
   * List of audio file URLs to merge in order
   */
  audio_urls: Array<string>;
  /**
   * Optional list of gap durations in seconds between each audio. Length should be len(audio_urls)-1
   */
  gaps?: Array<number>;
};
export type MergeAudioIntoVideoInput = {
  /**
   * URL of the audio file to add to the video
   */
  audio_url: string | Blob | File;
  /**
   * If true, the original audio from the video is preserved and mixed with the new audio. If false (default), the original audio is replaced.
   */
  keep_original_audio?: boolean;
  /**
   * Offset in seconds for when the new audio should start relative to the video
   */
  start_offset?: number;
  /**
   * URL of the video file
   */
  video_url: string | Blob | File;
};
export type MergeAudiosInput = {
  /**
   * List of audio URLs to merge in order. The 0th stream of the audio will be considered as the merge candidate.
   */
  audio_urls: Array<string>;
  /**
   * Output format of the combined audio. If not used, will be determined automatically using FFMPEG. Formatted as codec_sample_rate_bitrate.
   */
  output_format?:
    | "mp3_22050_32"
    | "mp3_44100_32"
    | "mp3_44100_64"
    | "mp3_44100_96"
    | "mp3_44100_128"
    | "mp3_44100_192"
    | "pcm_8000"
    | "pcm_16000"
    | "pcm_22050"
    | "pcm_24000"
    | "pcm_44100"
    | "pcm_48000"
    | "ulaw_8000"
    | "alaw_8000"
    | "opus_48000_32"
    | "opus_48000_64"
    | "opus_48000_96"
    | "opus_48000_128"
    | "opus_48000_192";
};
export type MergeImagesInput = {
  /**
   * List of image URLs to merge into an array
   */
  image_urls: Array<string>;
  /**
   * Output format for processed images Default value: `"png"`
   */
  output_format?: "png" | "jpg" | "jpeg" | "webp";
};
export type MergeTextInput = {
  /**
   * Separator to join texts Default value: `"--"`
   */
  separator?: string;
  /**
   * List of text strings to merge
   */
  texts: Array<string>;
};
export type MergeVideosInput = {
  /**
   * Resolution of the final video. Width and height must be between 512 and 2048.
   */
  resolution?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Target FPS for the output video. If not provided, uses the lowest FPS from input videos.
   */
  target_fps?: number;
  /**
   * List of video URLs to merge in order
   */
  video_urls: Array<string>;
};
export type MergeVideosOutput = {
  /**
   * Metadata about the merged video including original video info
   */
  metadata: unknown;
  /**
   * Merged video file
   */
  video: File;
};
export type MetadataInput = {
  /**
   * Whether to extract the start and end frames for videos. Note that when true the request will be slower.
   */
  extract_frames?: boolean;
  /**
   * URL of the media file (video or audio) to analyze
   */
  media_url: string | Blob | File;
};
export type MetadataOutput = {
  /**
   * Metadata for the analyzed media file (either Video or Audio)
   */
  media: Video | Audio;
};
export type MiDaSInput = {
  /**
   * A parameter for the MiDaS detector Default value: `6.283185307179586`
   */
  a?: number;
  /**
   * Background threshold for the MiDaS detector Default value: `0.1`
   */
  background_threshold?: number;
  /**
   * URL of the image to process
   */
  image_url: string | Blob | File;
};
export type MiDaSOutput = {
  /**
   * Image with MiDaS depth map
   */
  depth_map: Image;
  /**
   * Image with MiDaS normal map
   */
  normal_map: Image;
};
export type MinimaxImage01Input = {
  /**
   * Aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "16:9"
    | "4:3"
    | "3:2"
    | "2:3"
    | "3:4"
    | "9:16"
    | "21:9";
  /**
   * Number of images to generate (1-9) Default value: `1`
   */
  num_images?: number;
  /**
   * Text prompt for image generation (max 1500 characters)
   */
  prompt: string;
  /**
   * Whether to enable automatic prompt optimization
   */
  prompt_optimizer?: boolean;
};
export type MinimaxImage01SubjectReferenceInput = {
  /**
   * Aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "16:9"
    | "4:3"
    | "3:2"
    | "2:3"
    | "3:4"
    | "9:16"
    | "21:9";
  /**
   * URL of the subject reference image to use for consistent character appearance
   */
  image_url: string | Blob | File;
  /**
   * Number of images to generate (1-9) Default value: `1`
   */
  num_images?: number;
  /**
   * Text prompt for image generation (max 1500 characters)
   */
  prompt: string;
  /**
   * Whether to enable automatic prompt optimization
   */
  prompt_optimizer?: boolean;
};
export type MinimaxMusicV2Input = {
  /**
   * Audio configuration settings
   */
  audio_setting?: AudioSetting;
  /**
   * Lyrics of the song. Use n to separate lines. You may add structure tags like [Intro], [Verse], [Chorus], [Bridge], [Outro] to enhance the arrangement. 10-3000 characters.
   */
  lyrics_prompt: string;
  /**
   * A description of the music, specifying style, mood, and scenario. 10-300 characters.
   */
  prompt: string;
};
export type MinimaxSpeech02HdInput = {
  /**
   * Audio configuration settings
   */
  audio_setting?: AudioSetting;
  /**
   * Enhance recognition of specified languages and dialects
   */
  language_boost?:
    | "Chinese"
    | "Chinese,Yue"
    | "English"
    | "Arabic"
    | "Russian"
    | "Spanish"
    | "French"
    | "Portuguese"
    | "German"
    | "Turkish"
    | "Dutch"
    | "Ukrainian"
    | "Vietnamese"
    | "Indonesian"
    | "Japanese"
    | "Italian"
    | "Korean"
    | "Thai"
    | "Polish"
    | "Romanian"
    | "Greek"
    | "Czech"
    | "Finnish"
    | "Hindi"
    | "Bulgarian"
    | "Danish"
    | "Hebrew"
    | "Malay"
    | "Slovak"
    | "Swedish"
    | "Croatian"
    | "Hungarian"
    | "Norwegian"
    | "Slovenian"
    | "Catalan"
    | "Nynorsk"
    | "Afrikaans"
    | "auto";
  /**
   * Format of the output content (non-streaming only) Default value: `"hex"`
   */
  output_format?: "url" | "hex";
  /**
   * Custom pronunciation dictionary for text replacement
   */
  pronunciation_dict?: PronunciationDict;
  /**
   * Text to convert to speech (max 5000 characters, minimum 1 non-whitespace character)
   */
  text: string;
  /**
   * Voice configuration settings
   */
  voice_setting?: VoiceSetting;
};
export type MinimaxSpeech26HdInput = {
  /**
   * Audio configuration settings
   */
  audio_setting?: AudioSetting;
  /**
   * Enhance recognition of specified languages and dialects
   */
  language_boost?:
    | "Chinese"
    | "Chinese,Yue"
    | "English"
    | "Arabic"
    | "Russian"
    | "Spanish"
    | "French"
    | "Portuguese"
    | "German"
    | "Turkish"
    | "Dutch"
    | "Ukrainian"
    | "Vietnamese"
    | "Indonesian"
    | "Japanese"
    | "Italian"
    | "Korean"
    | "Thai"
    | "Polish"
    | "Romanian"
    | "Greek"
    | "Czech"
    | "Finnish"
    | "Hindi"
    | "Bulgarian"
    | "Danish"
    | "Hebrew"
    | "Malay"
    | "Slovak"
    | "Swedish"
    | "Croatian"
    | "Hungarian"
    | "Norwegian"
    | "Slovenian"
    | "Catalan"
    | "Nynorsk"
    | "Afrikaans"
    | "auto";
  /**
   * Loudness normalization settings for the audio
   */
  normalization_setting?: LoudnessNormalizationSetting;
  /**
   * Format of the output content (non-streaming only) Default value: `"hex"`
   */
  output_format?: "url" | "hex";
  /**
   * Text to convert to speech. Paragraph breaks should be marked with newline characters. **NOTE**: You can customize speech pauses by adding markers in the form `<#x#>`, where `x` is the pause duration in seconds. Valid range: `[0.01, 99.99]`, up to two decimal places. Pause markers must be placed between speakable text segments and cannot be used consecutively.
   */
  prompt: string;
  /**
   * Custom pronunciation dictionary for text replacement
   */
  pronunciation_dict?: PronunciationDict;
  /**
   * Voice configuration settings
   */
  voice_setting?: VoiceSetting;
};
export type MinimaxSpeech28HdInput = {
  /**
   * Audio configuration settings
   */
  audio_setting?: AudioSetting;
  /**
   * Enhance recognition of specified languages and dialects
   */
  language_boost?:
    | "Chinese"
    | "Chinese,Yue"
    | "English"
    | "Arabic"
    | "Russian"
    | "Spanish"
    | "French"
    | "Portuguese"
    | "German"
    | "Turkish"
    | "Dutch"
    | "Ukrainian"
    | "Vietnamese"
    | "Indonesian"
    | "Japanese"
    | "Italian"
    | "Korean"
    | "Thai"
    | "Polish"
    | "Romanian"
    | "Greek"
    | "Czech"
    | "Finnish"
    | "Hindi"
    | "Bulgarian"
    | "Danish"
    | "Hebrew"
    | "Malay"
    | "Slovak"
    | "Swedish"
    | "Croatian"
    | "Hungarian"
    | "Norwegian"
    | "Slovenian"
    | "Catalan"
    | "Nynorsk"
    | "Afrikaans"
    | "auto";
  /**
   * Loudness normalization settings for the audio
   */
  normalization_setting?: LoudnessNormalizationSetting;
  /**
   * Format of the output content (non-streaming only) Default value: `"hex"`
   */
  output_format?: "url" | "hex";
  /**
   * Text to convert to speech. Use `<#x#>` for pauses (x = 0.01-99.99 seconds). Supports interjection tags: `(laughs)`, `(sighs)`, `(coughs)`, `(clears throat)`, `(gasps)`, `(sniffs)`, `(groans)`, `(yawns)`.
   */
  prompt: string;
  /**
   * Custom pronunciation dictionary for text replacement
   */
  pronunciation_dict?: PronunciationDict;
  /**
   * Voice modification settings to adjust pitch, intensity, and timbre.
   */
  voice_modify?: VoiceModify;
  /**
   * Voice configuration settings
   */
  voice_setting?: VoiceSetting;
};
export type MinimaxVideo01Input = {
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxVideo01SubjectReferenceInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
  /**
   * URL of the subject reference image to use for consistent subject appearance
   */
  subject_reference_image_url: string | Blob | File;
};
export type MinimaxVoiceCloneInput = {
  /**
   * Text validation accuracy threshold (0-1)
   */
  accuracy?: number;
  /**
   * URL of the input audio file for voice cloning. Should be at least 10 seconds
   * long. To retain the voice permanently, use it with a TTS (text-to-speech)
   * endpoint at least once within 7 days. Otherwise, it will be
   * automatically deleted.
   */
  audio_url: string | Blob | File;
  /**
   * TTS model to use for preview. Options: speech-02-hd, speech-02-turbo, speech-01-hd, speech-01-turbo Default value: `"speech-02-hd"`
   */
  model?:
    | "speech-02-hd"
    | "speech-02-turbo"
    | "speech-01-hd"
    | "speech-01-turbo";
  /**
   * Enable volume normalization for the cloned voice
   */
  need_volume_normalization?: boolean;
  /**
   * Enable noise reduction for the cloned voice
   */
  noise_reduction?: boolean;
  /**
   * Text to generate a TTS preview with the cloned voice (optional) Default value: `"Hello, this is a preview of your cloned voice! I hope you like it!"`
   */
  text?: string;
};
export type MinimaxVoiceDesignInput = {
  /**
   * Text for audio preview. Limited to 500 characters. A fee of $30 per 1M characters will be charged for the generation of the preview audio.
   */
  preview_text: string;
  /**
   * Voice description prompt for generating a personalized voice
   */
  prompt: string;
};
export type MiniOutput = {
  /**
   * The generated speech audio file.
   */
  audio: AudioFile;
};
export type MixDehazeNetInput = {
  /**
   * URL of image to be used for image enhancement
   */
  image_url: string | Blob | File;
  /**
   * Model to be used for dehazing Default value: `"indoor"`
   */
  model?: "indoor" | "outdoor";
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type MLSDInput = {
  /**
   * Distance threshold for the MLSD detector Default value: `0.1`
   */
  distance_threshold?: number;
  /**
   * URL of the image to process
   */
  image_url: string | Blob | File;
  /**
   * Score threshold for the MLSD detector Default value: `0.1`
   */
  score_threshold?: number;
};
export type MmaudioV2TextToAudioInput = {
  /**
   * The strength of Classifier Free Guidance. Default value: `4.5`
   */
  cfg_strength?: number;
  /**
   * The duration of the audio to generate. Default value: `8`
   */
  duration?: number;
  /**
   * Whether to mask away the clip.
   */
  mask_away_clip?: boolean;
  /**
   * The negative prompt to generate the audio for. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of steps to generate the audio for. Default value: `25`
   */
  num_steps?: number;
  /**
   * The prompt to generate the audio for.
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
};
export type MochiV1Input = {
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * The negative prompt for the video. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The prompt to generate a video from.
   */
  prompt: string;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
};
export type ModelUrls = {
  /**
   * FBX format 3D model
   */
  fbx?: File;
  /**
   * GLB format 3D model
   */
  glb?: File;
  /**
   * MTL material file for OBJ model
   */
  mtl?: File;
  /**
   * OBJ format 3D model
   */
  obj?: File;
  /**
   * Texture image for the 3D model
   */
  texture?: File;
  /**
   * USDZ format 3D model
   */
  usdz?: File;
};
export type MoondreamBatchedInput = {
  /**
   * List of input prompts and image URLs
   */
  inputs: Array<MoondreamInputParam>;
  /**
   * Maximum number of new tokens to generate Default value: `64`
   */
  max_tokens?: number;
  /**
   * Model ID to use for inference Default value: `"vikhyatk/moondream2"`
   */
  model_id?: "vikhyatk/moondream2" | "fal-ai/moondream2-docci";
  /**
   * Repetition penalty for sampling Default value: `1`
   */
  repetition_penalty?: number;
  /**
   * Temperature for sampling Default value: `0.2`
   */
  temperature?: number;
  /**
   * Top P for sampling Default value: `1`
   */
  top_p?: number;
};
export type MoondreamBatchedOutput = {
  /**
   * Filenames of the images processed
   */
  filenames?: Array<string>;
  /**
   * List of generated outputs
   */
  outputs: Array<string>;
  /**
   * Whether the output is partial
   */
  partial?: boolean;
  /**
   * Timings for different parts of the process
   */
  timings: unknown;
};
export type MoondreamCaptionInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Length of the caption to generate Default value: `"normal"`
   */
  length?: "short" | "normal" | "long";
  /**
   * Sampling temperature to use, between 0 and 1. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic. If not set, defaults to 0.
   */
  temperature?: number;
  /**
   * Nucleus sampling probability mass to use, between 0 and 1.
   */
  top_p?: number;
};
export type MoondreamCaptionOutput = {
  /**
   * Reason for finishing the output generation
   */
  finish_reason: string;
  /**
   * Generated caption for the image
   */
  output: string;
  /**
   * Usage information for the request
   */
  usage_info: UsageInfo;
};
export type MoondreamDetectOutput = {
  /**
   * Reason for finishing the output generation
   */
  finish_reason: string;
  /**
   * Image with bounding boxes drawn around detected objects
   */
  image?: ImageFile;
  /**
   * List of detected objects with their bounding boxes
   */
  objects: Array<ObjectBoundingBox>;
  /**
   * Usage information for the request
   */
  usage_info: UsageInfo;
};
export type MoondreamInputParam = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Prompt to be used for the image Default value: `"Describe this image."`
   */
  prompt?: string;
};
export type MoondreamObjectInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Object to be detected in the image
   */
  object: string;
};
export type MoondreamObjectOutput = {
  /**
   * Image with detected objects
   */
  image: Image;
  /**
   * Objects detected in the image
   */
  objects: Array<unknown>;
};
export type MoondreamOutput = {
  /**
   * Output for the given query
   */
  output: string;
};
export type MoondreamPointInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Whether to preview the output
   */
  preview?: boolean;
  /**
   * Object to be located in the image
   */
  prompt: string;
};
export type MoondreamPointOutput = {
  /**
   * Reason for finishing the output generation
   */
  finish_reason: string;
  /**
   * Image with points drawn on detected objects
   */
  image?: ImageFile;
  /**
   * List of points marking the detected objects
   */
  points: Array<Point>;
  /**
   * Usage information for the request
   */
  usage_info: UsageInfo;
};
export type MoondreamQueryInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Query to be asked in the image
   */
  prompt: string;
  /**
   * Whether to include detailed reasoning behind the answer Default value: `true`
   */
  reasoning?: boolean;
  /**
   * Sampling temperature to use, between 0 and 1. Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic. If not set, defaults to 0.
   */
  temperature?: number;
  /**
   * Nucleus sampling probability mass to use, between 0 and 1.
   */
  top_p?: number;
};
export type MoondreamQueryOutput = {
  /**
   * Reason for finishing the output generation
   */
  finish_reason: string;
  /**
   * Answer to the query about the image
   */
  output: string;
  /**
   * Detailed reasoning behind the answer, if enabled
   */
  reasoning?: string;
  /**
   * Usage information for the request
   */
  usage_info: UsageInfo;
};
export type MoondreamSegementationInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Object to be segmented in the image
   */
  object: string;
  /**
   * Whether to preview the output and return a binary mask of the image
   */
  preview?: boolean;
  /**
   * Sampling settings for the segmentation model
   */
  settings?: SegmentSamplingSettings;
  /**
   * Spatial references to guide the segmentation. By feeding in references you can help the segmentation process. Must be either list of Point object with x and y members, or list of arrays containing either 2 floats (x,y) or 4 floats (x1,y1,x2,y2).
   * **NOTE**: You can also use the [**point endpoint**](https://fal.ai/models/fal-ai/moondream3-preview/point) to get points for the objects, and pass them in here.
   */
  spatial_references?: Array<Point | Array<number>>;
};
export type MoondreamSegementationOutput = {
  /**
   * Bounding box of the segmented object. If not detected, will be null.
   */
  bbox?: ObjectBoundingBox;
  /**
   * Reason for finishing the output generation
   */
  finish_reason: string;
  /**
   * Segmentation mask image. If no object detected or preview not requested, will be null.
   */
  image?: ImageFile;
  /**
   * SVG path data representing the segmentation mask. If not detected, will be null.
   */
  path?: string;
  /**
   * Usage information for the request
   */
  usage_info: UsageInfo;
};
export type MulticonditioningVideoInput = {
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16" | "auto";
  /**
   * Whether to expand the prompt using the LLM.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the video. Default value: `25`
   */
  frames_per_second?: number;
  /**
   * The image conditions to use for generation.
   */
  images?: Array<ImageCondition>;
  /**
   * The LoRA weights to use for generation.
   */
  loras?: Array<LoRAWeight>;
  /**
   * The negative prompt to use. Default value: `"blurry, low quality, low resolution, inconsistent motion, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to use. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The number of frames in the video. Default value: `89`
   */
  number_of_frames?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The resolution of the video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * The seed to use for generation.
   */
  seed?: number;
  /**
   * The video conditions to use for generation.
   */
  videos?: Array<VideoCondition>;
};
export type MultiConditioningVideoInput = {
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type MultiImageTo3DInput = {
  /**
   * Animation preset ID from Meshy's library (500+ presets). Only used when enable_animation is true. See https://docs.meshy.ai/en/api/animation-library for available action IDs. Default value: `1001`
   */
  animation_action_id?: number;
  /**
   * Apply an animation preset to the rigged model. Requires enable_rigging to be true.
   */
  enable_animation?: boolean;
  /**
   * Generate PBR Maps (metallic, roughness, normal) in addition to base color. Requires should_texture to be true.
   */
  enable_pbr?: boolean;
  /**
   * Automatically rig the generated model as a humanoid character. Includes basic walking and running animations. Best results with humanoid characters that have clearly defined limbs.
   */
  enable_rigging?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * 1 to 4 images for 3D model creation. All images should depict the same object from different angles. Supports .jpg, .jpeg, .png formats, and AVIF/HEIF which will be automatically converted. If more than 4 images are provided, only the first 4 will be used.
   */
  image_urls: Array<string>;
  /**
   * Pose mode for the generated model. 'a-pose' generates an A-pose, 't-pose' generates a T-pose, empty string for no specific pose. Default value: `""`
   */
  pose_mode?: "a-pose" | "t-pose" | "";
  /**
   * Approximate height of the character in meters. Only used when enable_rigging is true. Default value: `1.7`
   */
  rigging_height_meters?: number;
  /**
   * Whether to enable the remesh phase. When false, returns triangular mesh ignoring topology and target_polycount. Default value: `true`
   */
  should_remesh?: boolean;
  /**
   * Whether to generate textures. False provides mesh without textures for 5 credits, True adds texture generation for additional 10 credits. Default value: `true`
   */
  should_texture?: boolean;
  /**
   * Controls symmetry behavior during model generation. Default value: `"auto"`
   */
  symmetry_mode?: "off" | "auto" | "on";
  /**
   * Target number of polygons in the generated model Default value: `30000`
   */
  target_polycount?: number;
  /**
   * 2D image to guide the texturing process. Requires should_texture to be true.
   */
  texture_image_url?: string | Blob | File;
  /**
   * Text prompt to guide the texturing process. Requires should_texture to be true.
   */
  texture_prompt?: string;
  /**
   * Specify the topology of the generated model. Quad for smooth surfaces, Triangle for detailed geometry. Default value: `"triangle"`
   */
  topology?: "quad" | "triangle";
};
export type MultipleAnglesInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale. Controls how closely the model follows the prompt. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If not provided, the size of the final input image will be used.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to adjust camera angle for.
   */
  image_urls: Array<string>;
  /**
   * The scale factor for the LoRA model. Controls the strength of the camera control effect. Default value: `1.25`
   */
  lora_scale?: number;
  /**
   * Move camera forward (0=no movement, 10=close-up)
   */
  move_forward?: number;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `6`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * Rotate camera left (positive) or right (negative) in degrees. Positive values rotate left, negative values rotate right.
   */
  rotate_right_left?: number;
  /**
   * Random seed for reproducibility. Same seed with same prompt will produce same result.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and won't be saved in history.
   */
  sync_mode?: boolean;
  /**
   * Adjust vertical camera angle (-1=bird's-eye view/looking down, 0=neutral, 1=worm's-eye view/looking up)
   */
  vertical_angle?: number;
  /**
   * Enable wide-angle lens effect
   */
  wide_angle_lens?: boolean;
};
export type MultishotMasterInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Enable safety checker for input/output content. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the output video. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Negative prompt describing undesired content in the generated video. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of denoising steps. Higher values produce better quality but take longer. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Global story caption describing the overall scene, subjects, setting, and visual style. This provides inter-shot consistency.
   */
  prompt: string;
  /**
   * Resolution of the generated video. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * List of shots to generate. Each shot has its own caption and frame count. Maximum 5 shots with a combined maximum of 308 frames.
   */
  shots: Array<Shot>;
};
export type MultiviewTo3dInput = {
  /**
   * Automatically scale the model to real-world dimensions, with the unit in meters. The default value is False.
   */
  auto_size?: boolean;
  /**
   * Back view image of the object.
   */
  back_image_url?: string | Blob | File;
  /**
   * Limits the number of faces on the output model. If this option is not set, the face limit will be adaptively determined.
   */
  face_limit?: number;
  /**
   * Front view image of the object.
   */
  front_image_url: string | Blob | File;
  /**
   * Left view image of the object.
   */
  left_image_url?: string | Blob | File;
  /**
   * Set orientation=align_image to automatically rotate the model to align the original image. The default value is default. Default value: `"default"`
   */
  orientation?: "default" | "align_image";
  /**
   * A boolean option to enable pbr. The default value is True, set False to get a model without pbr. If this option is set to True, texture will be ignored and used as True.
   */
  pbr?: boolean;
  /**
   * Set True to enable quad mesh output (extra $0.05 per generation). If quad=True and face_limit is not set, the default face_limit will be 10000. Note: Enabling this option will force the output to be an FBX model.
   */
  quad?: boolean;
  /**
   * Right view image of the object.
   */
  right_image_url?: string | Blob | File;
  /**
   * This is the random seed for model generation. The seed controls the geometry generation process, ensuring identical models when the same seed is used. This parameter is an integer and is randomly chosen if not set.
   */
  seed?: number;
  /**
   * An option to enable texturing. Default is 'standard', set 'no' to get a model without any textures, and set 'HD' to get a model with hd quality textures. Default value: `"standard"`
   */
  texture?: "no" | "standard" | "HD";
  /**
   * Determines the prioritization of texture alignment in the 3D model. The default value is original_image. Default value: `"original_image"`
   */
  texture_alignment?: "original_image" | "geometry";
  /**
   * This is the random seed for texture generation. Using the same seed will produce identical textures. This parameter is an integer and is randomly chosen if not set. If you want a model with different textures, please use same seed and different texture_seed.
   */
  texture_seed?: number;
};
export type MusePoseInput = {
  /**
   * Classifier free guidance Default value: `3.5`
   */
  cfg?: number;
  /**
   * The frame to align the pose to.
   */
  dwpose_align_frame?: number;
  /**
   * The resolution to use for the pose detection. Default value: `512`
   */
  dwpose_detection_resolution?: number;
  /**
   * The resolution to use for the image during pose calculation. Default value: `720`
   */
  dwpose_image_resolution?: number;
  /**
   * The frames per second of the output video.
   */
  fps?: number;
  /**
   * The height of the output video. Default value: `748`
   */
  height?: number;
  /**
   * URL of the image to animate.
   */
  image_url: string | Blob | File;
  /**
   * The length of the output video. Default value: `300`
   */
  length?: number;
  /**
   * The video slice overlap frame number Default value: `4`
   */
  overlap?: number;
  /**
   * The seed to use for the random number generator.
   */
  seed?: number;
  /**
   * Number of input frames to skip. Skipping 1 effectively reduces the fps in half. Default value: `1`
   */
  skip?: number;
  /**
   * The video slice frame number Default value: `48`
   */
  slice?: number;
  /**
   * DDIM sampling steps Default value: `20`
   */
  steps?: number;
  /**
   * The URL of the video to drive the animation
   */
  video_url: string | Blob | File;
  /**
   * The width of the output video. Default value: `748`
   */
  width?: number;
};
export type musetalkInput = {
  /**
   * URL of the audio
   */
  audio_url: string | Blob | File;
  /**
   * URL of the source video
   */
  source_video_url: string | Blob | File;
};
export type MusicCompositionPlan = {
  /**
   * The styles that should not be present in the entire song.
   */
  negative_global_styles: Array<string>;
  /**
   * The styles that should be present in the entire song.
   */
  positive_global_styles: Array<string>;
  /**
   * The sections of the song.
   */
  sections: Array<MusicSection>;
};
export type MusicGeneratorInput = {
  /**
   * The duration of the generated music in seconds.
   */
  duration: number;
  /**
   * The prompt to generate music from.
   */
  prompt: string;
};
export type MusicSection = {
  /**
   * The duration of the section in milliseconds. Must be between 3000ms and 120000ms.
   */
  duration_ms: number;
  /**
   * The lyrics of the section. Each line must be at most 200 characters long.
   */
  lines: Array<string>;
  /**
   * The styles that should not be present in this section.
   */
  negative_local_styles: Array<string>;
  /**
   * The styles that should be present in this section.
   */
  positive_local_styles: Array<string>;
  /**
   * The name of the section. Must be between 1 and 100 characters.
   */
  section_name: string;
};
export type NafnetInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type NanoBanana2EditInput = {
  /**
   * The aspect ratio of the generated image. Supports extreme ratios: 4:1, 1:4, 8:1, 1:8. Default value: `auto`
   */
  aspect_ratio?:
    | "auto"
    | "21:9"
    | "16:9"
    | "3:2"
    | "4:3"
    | "5:4"
    | "1:1"
    | "4:5"
    | "3:4"
    | "2:3"
    | "9:16"
    | "4:1"
    | "1:4"
    | "8:1"
    | "1:8";
  /**
   * Enable web search for the image generation task. This will allow the model to use the latest information from the web to generate the image.
   */
  enable_web_search?: boolean;
  /**
   * The URLs of the images to use for image-to-image generation or image editing.
   */
  image_urls: Array<string>;
  /**
   * Experimental parameter to limit the number of generations from each round of prompting to 1. Set to `True` to to disregard any instructions in the prompt regarding the number of images to generate and ignore any intermediate images generated by the model. This may affect generation quality. Default value: `true`
   */
  limit_generations?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt for image editing.
   */
  prompt: string;
  /**
   * The resolution of the image to generate. Default value: `"1K"`
   */
  resolution?: "0.5K" | "1K" | "2K" | "4K";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * When set, enables model thinking with the given level ('minimal' or 'high') and includes thoughts in the generation. Omit to disable.
   */
  thinking_level?: "minimal" | "high";
};
export type NanoBanana2Input = {
  /**
   * The aspect ratio of the generated image. Supports extreme ratios: 4:1, 1:4, 8:1, 1:8. Use "auto" to let the model decide based on the prompt. Default value: `auto`
   */
  aspect_ratio?:
    | "auto"
    | "21:9"
    | "16:9"
    | "3:2"
    | "4:3"
    | "5:4"
    | "1:1"
    | "4:5"
    | "3:4"
    | "2:3"
    | "9:16"
    | "4:1"
    | "1:4"
    | "8:1"
    | "1:8";
  /**
   * Enable web search for the image generation task. This will allow the model to use the latest information from the web to generate the image.
   */
  enable_web_search?: boolean;
  /**
   * Experimental parameter to limit the number of generations from each round of prompting to 1. Set to `True` to to disregard any instructions in the prompt regarding the number of images to generate and ignore any intermediate images generated by the model. This may affect generation quality. Default value: `true`
   */
  limit_generations?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The text prompt to generate an image from.
   */
  prompt: string;
  /**
   * The resolution of the image to generate. Default value: `"1K"`
   */
  resolution?: "0.5K" | "1K" | "2K" | "4K";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * When set, enables model thinking with the given level ('minimal' or 'high') and includes thoughts in the generation. Omit to disable.
   */
  thinking_level?: "minimal" | "high";
};
export type NanoBananaEditInput = {
  /**
   * The aspect ratio of the generated image. Default value: `auto`
   */
  aspect_ratio?:
    | "auto"
    | "21:9"
    | "16:9"
    | "3:2"
    | "4:3"
    | "5:4"
    | "1:1"
    | "4:5"
    | "3:4"
    | "2:3"
    | "9:16";
  /**
   * The URLs of the images to use for image-to-image generation or image editing.
   */
  image_urls: Array<string>;
  /**
   * Experimental parameter to limit the number of generations from each round of prompting to 1. Set to `True` to to disregard any instructions in the prompt regarding the number of images to generate.
   */
  limit_generations?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt for image editing.
   */
  prompt: string;
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type NanoBananaInput = {
  /**
   * The aspect ratio of the generated image. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "3:2"
    | "4:3"
    | "5:4"
    | "1:1"
    | "4:5"
    | "3:4"
    | "2:3"
    | "9:16";
  /**
   * Experimental parameter to limit the number of generations from each round of prompting to 1. Set to `True` to to disregard any instructions in the prompt regarding the number of images to generate.
   */
  limit_generations?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The text prompt to generate an image from.
   */
  prompt: string;
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type NanoBananaOutput = {
  /**
   * The description of the generated images.
   */
  description: string;
  /**
   * The generated images.
   */
  images: Array<ImageFile>;
};
export type NanoBananaProEditInput = {
  /**
   * The aspect ratio of the generated image. Default value: `auto`
   */
  aspect_ratio?:
    | "auto"
    | "21:9"
    | "16:9"
    | "3:2"
    | "4:3"
    | "5:4"
    | "1:1"
    | "4:5"
    | "3:4"
    | "2:3"
    | "9:16";
  /**
   * Enable web search for the image generation task. This will allow the model to use the latest information from the web to generate the image.
   */
  enable_web_search?: boolean;
  /**
   * The URLs of the images to use for image-to-image generation or image editing.
   */
  image_urls: Array<string>;
  /**
   * Experimental parameter to limit the number of generations from each round of prompting to 1. Set to `True` to to disregard any instructions in the prompt regarding the number of images to generate.
   */
  limit_generations?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt for image editing.
   */
  prompt: string;
  /**
   * The resolution of the image to generate. Default value: `"1K"`
   */
  resolution?: "1K" | "2K" | "4K";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type NanoBananaProInput = {
  /**
   * The aspect ratio of the generated image. Default value: `1:1`
   */
  aspect_ratio?:
    | "auto"
    | "21:9"
    | "16:9"
    | "3:2"
    | "4:3"
    | "5:4"
    | "1:1"
    | "4:5"
    | "3:4"
    | "2:3"
    | "9:16";
  /**
   * Enable web search for the image generation task. This will allow the model to use the latest information from the web to generate the image.
   */
  enable_web_search?: boolean;
  /**
   * Experimental parameter to limit the number of generations from each round of prompting to 1. Set to `True` to to disregard any instructions in the prompt regarding the number of images to generate.
   */
  limit_generations?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The text prompt to generate an image from.
   */
  prompt: string;
  /**
   * The resolution of the image to generate. Default value: `"1K"`
   */
  resolution?: "1K" | "2K" | "4K";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type NemotronAsrInput = {
  /**
   * Controls the speed/accuracy trade-off. 'none' = best accuracy (1.12s chunks, ~7.16% WER), 'low' = balanced (0.56s chunks, ~7.22% WER), 'medium' = faster (0.16s chunks, ~7.84% WER), 'high' = fastest (0.08s chunks, ~8.53% WER). Default value: `"none"`
   */
  acceleration?: "none" | "low" | "medium" | "high";
  /**
   * URL of the audio file.
   */
  audio_url: string | Blob | File;
};
export type NextSceneInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale. Controls how closely the model follows the prompt. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If not provided, the size of the final input image will be used.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to create the next scene from.
   */
  image_urls: Array<string>;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `6`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * Describe the camera movement, framing change, or scene transition. Start with 'Next Scene:' for best results. Examples: camera movements (dolly, push-in, pull-back), framing changes (wide to close-up), new elements entering frame. Default value: `"Next Scene: The camera moves forward revealing more of the scene"`
   */
  prompt?: string;
  /**
   * Random seed for reproducibility. Same seed with same prompt will produce same result.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and won't be saved in history.
   */
  sync_mode?: boolean;
};
export type Nextstep1Input = {
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   */
  negative_prompt: string;
  /**
   * The prompt to edit the image.
   */
  prompt: string;
};
export type NovaSrInput = {
  /**
   * The format for the output audio. Default value: `"mp3"`
   */
  audio_format?: "mp3" | "aac" | "m4a" | "ogg" | "opus" | "flac" | "wav";
  /**
   * The URL of the audio file to enhance.
   */
  audio_url: string | Blob | File;
  /**
   * The bitrate of the output audio. Default value: `"192k"`
   */
  bitrate?: string;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type NovaSrOutput = {
  /**
   * The enhanced audio file.
   */
  audio: AudioFile;
  /**
   * Timings for each step in the pipeline.
   */
  timings: NovaSRTimings;
};
export type O3ProEditVideoV2VInput = {
  /**
   * Elements (characters/objects) to include. Reference in prompt as @Element1, @Element2.
   */
  elements?: Array<KlingV3ImageElementInput>;
  /**
   * Reference images for style/appearance. Reference in prompt as @Image1, @Image2, etc. Maximum 4 total (elements + reference images) when using video.
   */
  image_urls?: Array<string>;
  /**
   * Whether to keep the original audio from the reference video. Default value: `true`
   */
  keep_audio?: boolean;
  /**
   * Text prompt for video generation. Reference video as @Video1.
   */
  prompt: string;
  /**
   * The type of multi-shot video generation. Default value: `"customize"`
   */
  shot_type?: string;
  /**
   * Reference video URL. Only .mp4/.mov formats, 3-10s duration, 720-2160px resolution, max 200MB.
   */
  video_url: string | Blob | File;
};
export type O3ProImageToVideoInput = {
  /**
   * Video duration in seconds (3-15s). Default value: `"5"`
   */
  duration?:
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * URL of the end frame image (optional).
   */
  end_image_url?: string | Blob | File;
  /**
   * Whether to generate native audio for the video.
   */
  generate_audio?: boolean;
  /**
   * URL of the start frame image.
   */
  image_url: string | Blob | File;
  /**
   * List of prompts for multi-shot video generation.
   */
  multi_prompt?: Array<KlingV3MultiPromptElement>;
  /**
   * Text prompt for video generation. Either prompt or multi_prompt must be provided, but not both.
   */
  prompt?: string;
  /**
   * The type of multi-shot video generation. Default value: `"customize"`
   */
  shot_type?: string;
};
export type O3ProReferenceVideoI2VInput = {
  /**
   * The aspect ratio of the generated video frame. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Video duration in seconds (3-15s). Default value: `"5"`
   */
  duration?:
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Elements (characters/objects) to include. Reference in prompt as @Element1, @Element2.
   */
  elements?: Array<KlingV3ComboElementInput>;
  /**
   * Image to use as the last frame of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * Whether to generate native audio for the video.
   */
  generate_audio?: boolean;
  /**
   * Reference images for style/appearance. Reference in prompt as @Image1, @Image2, etc. Maximum 4 total (elements + reference images) when using video.
   */
  image_urls?: Array<string>;
  /**
   * List of prompts for multi-shot video generation.
   */
  multi_prompt?: Array<KlingV3MultiPromptElement>;
  /**
   * Text prompt for video generation. Either prompt or multi_prompt must be provided, but not both.
   */
  prompt?: string;
  /**
   * The type of multi-shot video generation. Default value: `"customize"`
   */
  shot_type?: string;
  /**
   * Image to use as the first frame of the video.
   */
  start_image_url?: string | Blob | File;
};
export type O3ProReferenceVideoV2VInput = {
  /**
   * Aspect ratio. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * Video duration in seconds (3-15s for reference video).
   */
  duration?:
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Elements (characters/objects) to include. Reference in prompt as @Element1, @Element2.
   */
  elements?: Array<KlingV3ImageElementInput>;
  /**
   * Reference images for style/appearance. Reference in prompt as @Image1, @Image2, etc. Maximum 4 total (elements + reference images) when using video.
   */
  image_urls?: Array<string>;
  /**
   * Whether to keep the original audio from the reference video. Default value: `true`
   */
  keep_audio?: boolean;
  /**
   * Text prompt for video generation. Reference video as @Video1.
   */
  prompt: string;
  /**
   * The type of multi-shot video generation. Default value: `"customize"`
   */
  shot_type?: string;
  /**
   * Reference video URL. Only .mp4/.mov formats, 3-10s duration, 720-2160px resolution, max 200MB.
   */
  video_url: string | Blob | File;
};
export type O3ProTextToVideoInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Video duration in seconds (3-15s). Default value: `"5"`
   */
  duration?:
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Whether to generate native audio for the video.
   */
  generate_audio?: boolean;
  /**
   * List of prompts for multi-shot video generation.
   */
  multi_prompt?: Array<KlingV3MultiPromptElement>;
  /**
   * Text prompt for video generation. Required unless multi_prompt is provided.
   */
  prompt?: string;
  /**
   * The type of multi-shot video generation. Default value: `"customize"`
   */
  shot_type?: string;
};
export type ObjectBoundingBox = {
  /**
   * Right boundary of detection box in normalized format (0 to 1)
   */
  x_max: number;
  /**
   * Left boundary of detection box in normalized format (0 to 1)
   */
  x_min: number;
  /**
   * Bottom boundary of detection box in normalized format (0 to 1)
   */
  y_max: number;
  /**
   * Top boundary of detection box in normalized format (0 to 1)
   */
  y_min: number;
};
export type ObjectOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
  /**
   * Generated textures for the 3D object.
   */
  textures: Array<Image>;
};
export type OCRBoundingBox = {
  /**
   * List of quadrilateral boxes
   */
  quad_boxes: Array<OCRBoundingBoxSingle>;
};
export type OmnigenV1Input = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The Image Guidance scale is a measure of how close you want
   * the model to stick to your input image when looking for a related image to show you. Default value: `1.6`
   */
  img_guidance_scale?: number;
  /**
   * URL of images to use while generating the image, Use <img><|image_1|></img> for the first image and so on.
   */
  input_image_urls?: Array<string>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type OmnigenV2Input = {
  /**
   * CFG range end value. Default value: `1`
   */
  cfg_range_end?: number;
  /**
   * CFG range start value.
   */
  cfg_range_start?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The Image Guidance scale controls how closely the model follows the input images.
   * For image editing: 1.3-2.0, for in-context generation: 2.0-3.0 Default value: `2`
   */
  image_guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URLs of input images to use for image editing or multi-image generation. Support up to 3 images.
   */
  input_image_urls?: Array<string>;
  /**
   * Negative prompt to guide what should not be in the image. Default value: `"(((deformed))), blurry, over saturation, bad anatomy, disfigured, poorly drawn face, mutation, mutated, (extra_limb), (ugly), (poorly drawn hands), fused fingers, messy drawing, broken legs censor, censored, censor_bar"`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate or edit an image. Use specific language like 'Add the bird from image 1 to the desk in image 2' for better results.
   */
  prompt: string;
  /**
   * The scheduler to use for the diffusion process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmsolver";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The Text Guidance scale controls how closely the model follows the text prompt.
   * Higher values make the model stick more closely to the prompt. Default value: `5`
   */
  text_guidance_scale?: number;
};
export type OmniHumanInput = {
  /**
   * The URL of the audio file to generate the video. Audio must be under 30s long.
   */
  audio_url: string | Blob | File;
  /**
   * The URL of the image used to generate the video
   */
  image_url: string | Blob | File;
};
export type OmniHumanv15Input = {
  /**
   * The URL of the audio file to generate the video. Audio must be under 30s long for 1080p generation and under 60s long for 720p generation.
   */
  audio_url: string | Blob | File;
  /**
   * The URL of the image used to generate the video
   */
  image_url: string | Blob | File;
  /**
   * The text prompt used to guide the video generation.
   */
  prompt?: string;
  /**
   * The resolution of the generated video. Defaults to 1080p. 720p generation is faster and higher in quality. 1080p generation is limited to 30s audio and 720p generation is limited to 60s audio. Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Generate a video at a faster rate with a slight quality trade-off.
   */
  turbo_mode?: boolean;
};
export type omnilottieInput = {
  /**
   * Maximum number of Lottie tokens to generate. Default value: `4096`
   */
  max_tokens?: number;
  /**
   * Text description of the Lottie animation to generate.
   */
  prompt: string;
  /**
   * Sampling temperature for generation. Default value: `0.9`
   */
  temperature?: number;
  /**
   * Top-k sampling parameter. Default value: `5`
   */
  top_k?: number;
  /**
   * Nucleus sampling probability threshold. Default value: `0.25`
   */
  top_p?: number;
};
export type omnilottieOutput = {
  /**
   * The generated Lottie animation as a JSON file.
   */
  lottie_file: File;
};
export type omnipartInput = {
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * Minimum segment size (pixels) for the model. Default value: `2000`
   */
  minimum_segment_size?: number;
  /**
   * Specify which segments to merge (e.g., '0,1;3,4' merges segments 0&1 together and 3&4 together) Default value: `""`
   */
  parts?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time. Default value: `765464`
   */
  seed?: number;
};
export type omnipartOutput = {
  /**
   * Generated 3D object file.
   */
  full_model_mesh: File;
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * All outputs file.
   */
  output_zip: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type OmniV2VEditInput = {
  /**
   * Elements (characters/objects) to include. Reference in prompt as @Element1, @Element2, etc. Maximum 4 total (elements + reference images) when using video.
   */
  elements?: Array<OmniVideoElementInput>;
  /**
   * Reference images for style/appearance. Reference in prompt as @Image1, @Image2, etc. Maximum 4 total (elements + reference images) when using video.
   */
  image_urls?: Array<string>;
  /**
   * Whether to keep the original audio from the video.
   */
  keep_audio?: boolean;
  /**
   * Use @Element1, @Element2 to reference elements and @Image1, @Image2 to reference images in order.
   */
  prompt: string;
  /**
   * Reference video URL. Only .mp4/.mov formats supported, 3-10 seconds duration, 720-2160px resolution, max 200MB.
   *
   * Max file size: 200.0MB, Min width: 720px, Min height: 720px, Max width: 2160px, Max height: 2160px, Min duration: 3.0s, Max duration: 10.05s, Min FPS: 24.0, Max FPS: 60.0, Timeout: 30.0s
   */
  video_url: string | Blob | File;
};
export type OmniV2VReferenceInput = {
  /**
   * The aspect ratio of the generated video frame. If 'auto', the aspect ratio will be determined automatically based on the input video, and the closest aspect ratio to the input video will be used. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * Video duration in seconds. Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10";
  /**
   * Elements (characters/objects) to include. Reference in prompt as @Element1, @Element2, etc. Maximum 4 total (elements + reference images) when using video.
   */
  elements?: Array<OmniVideoElementInput>;
  /**
   * Reference images for style/appearance. Reference in prompt as @Image1, @Image2, etc. Maximum 4 total (elements + reference images) when using video.
   */
  image_urls?: Array<string>;
  /**
   * Whether to keep the original audio from the video.
   */
  keep_audio?: boolean;
  /**
   * Use @Element1, @Element2 to reference elements and @Image1, @Image2 to reference images in order.
   */
  prompt: string;
  /**
   * Reference video URL. Only .mp4/.mov formats supported, 3-10 seconds duration, 720-2160px resolution, max 200MB.
   *
   * Max file size: 200.0MB, Min width: 720px, Min height: 720px, Max width: 2160px, Max height: 2160px, Min duration: 3.0s, Max duration: 10.05s, Min FPS: 24.0, Max FPS: 60.0, Timeout: 30.0s
   */
  video_url: string | Blob | File;
};
export type OmniVideoElementInput = {
  /**
   * The frontal image of the element (main view).
   *
   * Max file size: 10.0MB, Min width: 300px, Min height: 300px, Min aspect ratio: 0.40, Max aspect ratio: 2.50, Timeout: 20.0s
   */
  frontal_image_url: string | Blob | File;
  /**
   * Additional reference images from different angles. 1-3 images supported. At least one image is required.
   */
  reference_image_urls?: Array<string>;
};
export type OmniVideoImageToVideoInput = {
  /**
   * Video duration in seconds. Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10";
  /**
   * Image to use as the last frame of the video.
   */
  end_image_url?: string | Blob | File;
  /**
   * Use @Image1 to reference the start frame, @Image2 to reference the end frame.
   */
  prompt: string;
  /**
   * Image to use as the first frame of the video.
   *
   * Max file size: 10.0MB, Min width: 300px, Min height: 300px, Min aspect ratio: 0.40, Max aspect ratio: 2.50, Timeout: 20.0s
   */
  start_image_url: string | Blob | File;
};
export type OmniVideoReferenceToVideoInput = {
  /**
   * The aspect ratio of the generated video frame. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Video duration in seconds. Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10";
  /**
   * Elements (characters/objects) to include in the video. Reference in prompt as @Element1, @Element2, etc. Maximum 7 total (elements + reference images + start image).
   */
  elements?: Array<OmniVideoElementInput>;
  /**
   * Additional reference images for style/appearance. Reference in prompt as @Image1, @Image2, etc. Maximum 7 total (elements + reference images + start image).
   */
  image_urls?: Array<string>;
  /**
   * Take @Element1, @Element2 to reference elements and @Image1, @Image2 to reference images in order.
   */
  prompt: string;
};
export type OmniZeroInput = {
  /**
   * Composition image url.
   */
  composition_image_url: string | Blob | File;
  /**
   * Composition strength. Default value: `1`
   */
  composition_strength?: number;
  /**
   * Depth strength. Default value: `0.5`
   */
  depth_strength?: number;
  /**
   * Face strength. Default value: `1`
   */
  face_strength?: number;
  /**
   * Guidance scale. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Identity image url.
   */
  identity_image_url: string | Blob | File;
  /**
   * Identity strength. Default value: `1`
   */
  identity_strength?: number;
  /**
   * Image strength. Default value: `0.75`
   */
  image_strength?: number;
  /**
   * Input image url.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to guide the image generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images. Default value: `1`
   */
  number_of_images?: number;
  /**
   * Prompt to guide the image generation.
   */
  prompt: string;
  /**
   * Seed. Default value: `42`
   */
  seed?: number;
  /**
   * Style image url.
   */
  style_image_url: string | Blob | File;
  /**
   * Style strength. Default value: `1`
   */
  style_strength?: number;
};
export type onerewardInput = {
  /**
   * Acceleration level for image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If True, runs a safety checker on the output and filters NSFW content. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance scale for the base diffusion process. The model uses true_cfg for classifier-free guidance; this controls the distillation guidance. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * URL of the source image to edit.
   */
  image_url: string | Blob | File;
  /**
   * URL of the mask image. White pixels indicate the area to fill or modify; black pixels preserve the original content.
   */
  mask_url: string | Blob | File;
  /**
   * Text describing what to avoid in the generated output. Default value: `"nsfw"`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of denoising steps. More steps generally produce higher quality results. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Format of the output image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Text description of what to generate in the masked area.
   */
  prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is used.
   */
  seed?: number;
  /**
   * If True, returns the image as a data URI instead of uploading to CDN. The image will not be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * True classifier-free guidance scale. Controls how strongly the model follows the prompt. Values above 1.0 enable CFG. Default value: `4`
   */
  true_cfg?: number;
};
export type OrpheusTtsInput = {
  /**
   * Repetition penalty (>= 1.1 required for stable generations). Default value: `1.2`
   */
  repetition_penalty?: number;
  /**
   * Temperature for generation (higher = more creative). Default value: `0.7`
   */
  temperature?: number;
  /**
   * The text to be converted to speech. You can additionally add the following emotive tags: <laugh>, <chuckle>, <sigh>, <cough>, <sniffle>, <groan>, <yawn>, <gasp>
   */
  text: string;
  /**
   * Voice ID for the desired voice. Default value: `"tara"`
   */
  voice?: "tara" | "leah" | "jess" | "leo" | "dan" | "mia" | "zac" | "zoe";
};
export type OutpaintInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Number of pixels to add as black margin on the bottom side (0-700). Default value: `400`
   */
  expand_bottom?: number;
  /**
   * Number of pixels to add as black margin on the left side (0-700).
   */
  expand_left?: number;
  /**
   * Number of pixels to add as black margin on the right side (0-700).
   */
  expand_right?: number;
  /**
   * Number of pixels to add as black margin on the top side (0-700).
   */
  expand_top?: number;
  /**
   * Image URL to outpaint
   */
  image_url: string | Blob | File;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the output image. Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "jpg" | "webp";
  /**
   * Optional prompt to guide the outpainting. If provided, it will be appended to the base outpaint instruction. Example: 'with a beautiful sunset in the background' Default value: `""`
   */
  prompt?: string;
  /**
   * If True, the function will wait for the image to be generated and uploaded before returning the response. If False, the function will return immediately and the image will be generated asynchronously.
   */
  sync_mode?: boolean;
  /**
   * Percentage to zoom out the image. If set, the image will be scaled down by this percentage and black margins will be added to maintain original size. Example: 50 means the image will be 50% of original size with black margins filling the rest. Default value: `20`
   */
  zoom_out_percentage?: number;
};
export type Output = {
  /**
   * The prompt used to generate the audio.
   */
  prompt: string;
  /**
   * The generated video with audio.
   */
  video: File;
};
export type OutputFormat = {
  /**
   * Bit rate in bps. Only applicable for MP3 codec. Defaults to 128000 for MP3.
   */
  bit_rate?: "32000" | "64000" | "96000" | "128000" | "192000";
  /**
   * Audio codec. Supported: mp3, wav, pcm, mulaw, alaw. Default value: `"mp3"`
   */
  codec?: "mp3" | "wav" | "pcm" | "mulaw" | "alaw";
  /**
   * Sample rate in Hz. Default value: `"24000"`
   */
  sample_rate?: "8000" | "16000" | "22050" | "24000" | "44100" | "48000";
};
export type OverlayImageInput = {
  /**
   * The URL of the background image
   */
  background_image_url: string | Blob | File;
  /**
   * Opacity of overlay image (0.0-1.0) Default value: `1`
   */
  opacity?: number;
  /**
   * Output format for the result image Default value: `"png"`
   */
  output_format?: "png" | "jpg" | "jpeg" | "webp";
  /**
   * The URL of the overlay image
   */
  overlay_image_url: string | Blob | File;
  /**
   * Scale of overlay image as percentage (25-200) Default value: `100`
   */
  scale_percent?: number;
  /**
   * Color of stroke/border Default value: `"black"`
   */
  stroke_color?:
    | "black"
    | "white"
    | "red"
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "purple"
    | "pink"
    | "brown"
    | "gray"
    | "cyan"
    | "magenta";
  /**
   * Width of stroke/border around overlay in pixels (0 for no stroke)
   */
  stroke_width?: number;
  /**
   * X position of overlay center as percentage of background width (0-100) Default value: `50`
   */
  x_percent?: number;
  /**
   * Y position of overlay center as percentage of background height (0-100) Default value: `50`
   */
  y_percent?: number;
};
export type OverlayVideoInput = {
  /**
   * Which audio source to use. 'main' uses only the main video's audio, 'overlay' uses only the overlay video's audio, 'both' mixes audio from both videos together. When set, overrides include_overlay_audio.
   */
  audio_source?: "main" | "overlay" | "both";
  /**
   * Blend mode for compositing the overlay video. 'normal' places overlay on top, 'screen' makes dark areas transparent (good for overlays with black backgrounds), 'multiply' makes light areas transparent (good for overlays with white backgrounds). Default value: `"normal"`
   */
  blend_mode?: "normal" | "screen" | "multiply";
  /**
   * Blend amount for chroma key edges (0.0-1.0). Lower values create harder edges, higher values create softer, more gradual transparency. Default value: `0.1`
   */
  chroma_key_blend?: number;
  /**
   * Hex color to key out (remove) from overlay video. Use for green screen removal. Examples: '00FF00' for green, '0000FF' for blue, '000000' for black. Only used when enable_chroma_key is True.
   */
  chroma_key_color?: string;
  /**
   * Similarity threshold for chroma key color matching (0.01-1.0). Lower values match colors more precisely, higher values match a wider range of similar colors. Default value: `0.3`
   */
  chroma_key_similarity?: number;
  /**
   * Enable chroma key (color removal) on the overlay video. When disabled, all chroma key settings are ignored.
   */
  enable_chroma_key?: boolean;
  /**
   * Include audio from overlay video. When enabled, audio from both videos will be mixed together. When disabled, only main video audio is used. Ignored when audio_source is set.
   */
  include_overlay_audio?: boolean;
  /**
   * URL of the main/background video
   */
  main_video_url: string | Blob | File;
  /**
   * Opacity of overlay video (0.0-1.0) Default value: `1`
   */
  opacity?: number;
  /**
   * URL of the overlay video to place on top
   */
  overlay_video_url: string | Blob | File;
  /**
   * Scale of overlay video as percentage (10-200) Default value: `35`
   */
  scale_percent?: number;
  /**
   * End output when the shortest input ends Default value: `true`
   */
  shortest?: boolean;
  /**
   * X position of overlay center as percentage of background width (0-100) Default value: `14`
   */
  x_percent?: number;
  /**
   * Y position of overlay center as percentage of background height (0-100) Default value: `13`
   */
  y_percent?: number;
};
export type OvisImageInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the image generation. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ParabolizeInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Parabolize coefficient Default value: `1`
   */
  parabolize_coeff?: number;
  /**
   * Vertex X position Default value: `0.5`
   */
  vertex_x?: number;
  /**
   * Vertex Y position Default value: `0.5`
   */
  vertex_y?: number;
};
export type PartInput = {
  /**
   * URL of FBX file to split into parts. ONLY FBX format supported. Max size: 100MB, face count ≤30,000. Recommended: AIGC-generated models.
   */
  input_file_url: string | Blob | File;
};
export type PartOutput = {
  /**
   * List of generated part files in FBX format
   */
  result_files: Array<File>;
};
export type pasdInput = {
  /**
   * ControlNet conditioning scale (0.1-1.0) Default value: `0.8`
   */
  conditioning_scale?: number;
  /**
   * Guidance scale for diffusion (1.0-20.0) Default value: `7`
   */
  guidance_scale?: number;
  /**
   * Input image to super-resolve
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to avoid unwanted artifacts Default value: `"blurry, dirty, messy, frames, deformed, dotted, noise, raster lines, unclear, lowres, over-smoothed, painting, ai generated"`
   */
  negative_prompt?: string;
  /**
   * Additional prompt to guide super-resolution Default value: `""`
   */
  prompt?: string;
  /**
   * Upscaling factor (1-4x) Default value: `2`
   */
  scale?: number;
  /**
   * Number of inference steps (10-50) Default value: `25`
   */
  steps?: number;
};
export type pasdOutput = {
  /**
   * The generated super-resolved images
   */
  images: Array<Image>;
  /**
   * Timing information for different processing stages
   */
  timings?: unknown;
};
export type personaplexInput = {
  /**
   * URL to the input audio file (user's speech).
   */
  audio_url: string | Blob | File;
  /**
   * Text prompt describing the AI persona and conversation context. Default value: `"You are a wise and friendly teacher. Answer questions or provide advice in a clear and engaging way."`
   */
  prompt?: string;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * Audio sampling temperature. Higher values produce more diverse outputs. Default value: `0.8`
   */
  temperature_audio?: number;
  /**
   * Text sampling temperature. Higher values produce more diverse outputs. Default value: `0.7`
   */
  temperature_text?: number;
  /**
   * Top-K sampling for audio tokens. Default value: `250`
   */
  top_k_audio?: number;
  /**
   * Top-K sampling for text tokens. Default value: `25`
   */
  top_k_text?: number;
  /**
   * Voice ID for the AI response. NAT = natural, VAR = variety. F = female, M = male. Ignored when voice_audio_url is provided. Default value: `"NATF2"`
   */
  voice?:
    | "NATF0"
    | "NATF1"
    | "NATF2"
    | "NATF3"
    | "NATM0"
    | "NATM1"
    | "NATM2"
    | "NATM3"
    | "VARF0"
    | "VARF1"
    | "VARF2"
    | "VARF3"
    | "VARF4"
    | "VARM0"
    | "VARM1"
    | "VARM2"
    | "VARM3"
    | "VARM4";
  /**
   * URL to a voice sample audio for on-the-fly voice cloning. When provided, the AI responds in the cloned voice instead of the preset 'voice'. 10+ seconds of clear speech recommended. Billed at 2x rate.
   */
  voice_audio_url?: string | Blob | File;
};
export type personaplexOutput = {
  /**
   * The generated AI response audio (WAV, 24kHz).
   */
  audio: File;
  /**
   * Duration of the generated audio in seconds.
   */
  duration: number;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * Transcribed text of the AI's response.
   */
  text: string;
};
export type PersonaplexRealtimeInput = {
  /**
   * Input audio chunk (PCM s16le, 24kHz mono). Base64-encoded in JSON transport.
   */
  audio: string;
  /**
   * Text prompt describing the AI persona and conversation context. Default value: `"You are a wise and friendly teacher. Answer questions or provide advice in a clear and engaging way."`
   */
  prompt?: string;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * Audio sampling temperature. Higher values produce more diverse outputs. Default value: `0.8`
   */
  temperature_audio?: number;
  /**
   * Text sampling temperature. Higher values produce more diverse outputs. Default value: `0.7`
   */
  temperature_text?: number;
  /**
   * Top-K sampling for audio tokens. Default value: `250`
   */
  top_k_audio?: number;
  /**
   * Top-K sampling for text tokens. Default value: `25`
   */
  top_k_text?: number;
  /**
   * Voice ID for the AI response. NAT = natural, VAR = variety. F = female, M = male. Ignored when voice_audio_url is provided. Default value: `"NATF2"`
   */
  voice?:
    | "NATF0"
    | "NATF1"
    | "NATF2"
    | "NATF3"
    | "NATM0"
    | "NATM1"
    | "NATM2"
    | "NATM3"
    | "VARF0"
    | "VARF1"
    | "VARF2"
    | "VARF3"
    | "VARF4"
    | "VARM0"
    | "VARM1"
    | "VARM2"
    | "VARM3"
    | "VARM4";
  /**
   * URL to a voice sample audio for on-the-fly voice cloning. When provided, the AI responds in the cloned voice instead of the preset 'voice'. 10+ seconds of clear speech recommended. Billed at 2x rate. Default value: `"null"`
   */
  voice_audio_url?: string | Blob | File;
};
export type PersonaplexRealtimeOutput = {
  /**
   * Generated audio chunk (PCM s16le, 24kHz mono). Base64-encoded in JSON transport.
   */
  audio: string;
  /**
   * Generated text tokens for this chunk. Default value: `""`
   */
  text?: string;
};
export type PerspectiveInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   * Image URL for perspective change
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"front"`
   */
  target_perspective?:
    | "front"
    | "left_side"
    | "right_side"
    | "back"
    | "top_down"
    | "bottom_up"
    | "birds_eye"
    | "three_quarter_left"
    | "three_quarter_right";
};
export type PhotaCreateProfileInput = {
  /**
   * URL to a ZIP archive containing the profile images.
   */
  image_data_url: string | Blob | File;
};
export type PhotaEditInput = {
  /**
   * Aspect ratio of the generated image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "1:1" | "16:9" | "4:3" | "3:4" | "9:16";
  /**
   * List of URLs/ Base64 data URIs of the images to edit. A maximum of 10 images are supported, additional images will be ignored.
   */
  image_urls?: Array<string>;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * List of profile IDs to use for the image generation. Profiles may be tagged in the prompt as @Profile1, @Profile2, etc.
   */
  profile_ids?: Array<string>;
  /**
   * Text description of the desired image. To refer to specific profiles, use [[profile_id_1]], [[profile_id_2]], etc.
   */
  prompt: string;
  /**
   * Resolution of the generated image. Default value: `"1K"`
   */
  resolution?: "1K" | "4K";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type PhotaEnhanceInput = {
  /**
   * URL/Base64 data URI of the image to enhance.
   */
  image_url: string | Blob | File;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * List of profile IDs to use for the image enhancement. The profiles sent over will be used as candidates for identity preservation.
   */
  profile_ids?: Array<string>;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type photaInput = {
  /**
   * Aspect ratio of the generated image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "1:1" | "16:9" | "4:3" | "3:4" | "9:16";
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text description of the desired image. In case you wish to use specific profiles, refer to them as [[profile_id_1]], [[profile_id_2]], etc.
   */
  prompt: string;
  /**
   * Resolution of the generated image. Default value: `"1K"`
   */
  resolution?: "1K" | "4K";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type photaOutput = {
  /**
   * The URL of the generated image.
   */
  images: Array<ImageFile>;
};
export type PhotaProfileDeleteOutput = {
  /**
   * The deleted profile ID.
   */
  profile_id: string;
  /**
   * Whether the profile was deleted successfully.
   */
  success: boolean;
};
export type PhotaProfileOutput = {
  /**
   * The Photalabs profile ID.
   */
  profile_id: string;
};
export type PhotographicCharacteristics = {
  /**
   * The angle of the camera in the image to be generated.
   */
  camera_angle?: string;
  /**
   * The depth of field in the image to be generated.
   */
  depth_of_field?: string;
  /**
   * The focus in the image to be generated.
   */
  focus?: string;
  /**
   * The focal length of the lens in the image to be generated.
   */
  lens_focal_length?: string;
};
export type PhotographyEffectsInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   *  Default value: `"film"`
   */
  effect_type?:
    | "film"
    | "vintage_film"
    | "portrait_photography"
    | "fashion_photography"
    | "street_photography"
    | "sepia_tone"
    | "film_grain"
    | "light_leaks"
    | "vignette_effect"
    | "instant_camera"
    | "golden_hour"
    | "dramatic_lighting"
    | "soft_focus"
    | "bokeh_effect"
    | "high_contrast"
    | "double_exposure";
  /**
   * Image URL for photography effects
   */
  image_url: string | Blob | File;
};
export type PhotoLoraI2IInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * LoRA Scale of the photo lora model Default value: `0.75`
   */
  photo_lora_scale?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type PhotoLoraInpaintInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * LoRA Scale of the photo lora model Default value: `0.75`
   */
  photo_lora_scale?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type photomakerInput = {
  /**
   * The base pipeline to use for generating the image. Default value: `"photomaker"`
   */
  base_pipeline?: "photomaker" | "photomaker-style";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image archive containing the images you want to use.
   */
  image_archive_url: string | Blob | File;
  /**
   * How much noise to add to the latent image. O for no noise, 1 for maximum noise. Default value: `0.5`
   */
  initial_image_strength?: number;
  /**
   * Optional initial image for img2img
   */
  initial_image_url?: string | Blob | File;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate in one request. Note that the higher the batch size,
   * the longer it will take to generate the images. Default value: `1`
   */
  num_images?: number;
  /**
   * Increasing the amount of steps tells Stable Diffusion that it should take more steps
   * to generate your final result which can increase the amount of detail in your image. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   *  Default value: `"Photographic"`
   */
  style?:
    | "(No style)"
    | "Cinematic"
    | "Disney Character"
    | "Digital Art"
    | "Photographic"
    | "Fantasy art"
    | "Neonpunk"
    | "Enhance"
    | "Comic book"
    | "Lowpoly"
    | "Line art";
  /**
   *  Default value: `20`
   */
  style_strength?: number;
};
export type PhysicEditInput = {
  /**
   * Enable content safety checking on the output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Higher values follow the prompt more closely. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * URL of the image to edit with physics-aware transformations.
   */
  image_url: string | Blob | File;
  /**
   * Description of unwanted elements in the output. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of denoising steps. More steps generally produce higher quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The editing instruction describing the physical transformation to apply.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. Same seed and prompt produce identical output.
   */
  seed?: number;
  /**
   * If True, the image will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type PiDiInput = {
  /**
   * Whether to apply the filter to the image.
   */
  apply_filter?: boolean;
  /**
   * URL of the image to process
   */
  image_url: string | Blob | File;
  /**
   * Whether to use the safe version of the Pidi detector
   */
  safe?: boolean;
  /**
   * Whether to use the scribble version of the Pidi detector
   */
  scribble?: boolean;
};
export type piflowInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. You can choose between some presets or custom height and width
   * that **must be multiples of 8**. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation. If set to None, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type PikaImage = {
  /**
   *
   */
  image_url: string | Blob | File;
};
export type PikaV15PikaffectsInput = {
  /**
   * URL of the input image
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to guide the model
   */
  negative_prompt?: string;
  /**
   * The Pikaffect to apply
   */
  pikaffect:
    | "Cake-ify"
    | "Crumble"
    | "Crush"
    | "Decapitate"
    | "Deflate"
    | "Dissolve"
    | "Explode"
    | "Eye-pop"
    | "Inflate"
    | "Levitate"
    | "Melt"
    | "Peel"
    | "Poke"
    | "Squish"
    | "Ta-da"
    | "Tear";
  /**
   * Text prompt to guide the effect
   */
  prompt?: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
};
export type PikaV22ImageToVideoInput = {
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The seed for the random number generator
   */
  seed?: number;
};
export type PikaV22PikaframesInput = {
  /**
   * URLs of keyframe images (2-5 images) to create transitions between
   */
  image_urls: Array<string>;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Default prompt for all transitions. Individual transition prompts override this.
   */
  prompt?: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * Configuration for each transition. Length must be len(image_urls) - 1. Total duration of all transitions must not exceed 25 seconds. If not provided, uses default 5-second transitions with the global prompt.
   */
  transitions?: Array<KeyframeTransition>;
};
export type PikaV22TextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:5" | "5:4" | "3:2" | "2:3";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * A negative prompt to guide the model Default value: `"ugly, bad, terrible"`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "1080p" | "720p";
  /**
   * The seed for the random number generator
   */
  seed?: number;
};
export type PikaV2PikadditionsInput = {
  /**
   * URL of the image to add
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to guide the model
   */
  negative_prompt?: string;
  /**
   * Text prompt describing what to add
   */
  prompt?: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
};
export type PixartSigmaInput = {
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The scheduler to use for the model. Default value: `"DPM-SOLVER"`
   */
  scheduler?: "DPM-SOLVER" | "SA-SOLVER";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The style to apply to the image. Default value: `"(No style)"`
   */
  style?:
    | "(No style)"
    | "Cinematic"
    | "Photographic"
    | "Anime"
    | "Manga"
    | "Digital Art"
    | "Pixel art"
    | "Fantasy art"
    | "Neonpunk"
    | "3D Model";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type PixverseExtendFastInput = {
  /**
   * Enable audio generation (BGM, SFX, dialogue). Supported in v5.6+ models.
   */
  generate_audio_switch?: boolean;
  /**
   * The model version to use for generation Default value: `"v4.5"`
   */
  model?: "v3.5" | "v4" | "v4.5" | "v5" | "v5.5" | "v5.6" | "v6";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Prompt describing how to extend the video
   */
  prompt: string;
  /**
   * The resolution of the generated video. Fast mode doesn't support 1080p Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The style of the extended video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * URL of the input video to extend
   */
  video_url: string | Blob | File;
};
export type PixverseExtendInput = {
  /**
   * The duration of the generated video in seconds. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Enable audio generation (BGM, SFX, dialogue). Supported in v5.6+ models.
   */
  generate_audio_switch?: boolean;
  /**
   * The model version to use for generation Default value: `"v4.5"`
   */
  model?: "v3.5" | "v4" | "v4.5" | "v5" | "v5.5" | "v5.6" | "v6";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Prompt describing how to extend the video
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The style of the extended video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * URL of the input video to extend
   */
  video_url: string | Blob | File;
};
export type PixverseLipsyncInput = {
  /**
   * URL of the input audio. If not provided, TTS will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * Text content for TTS when audio_url is not provided
   */
  text?: string;
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
  /**
   * Voice to use for TTS when audio_url is not provided Default value: `"Auto"`
   */
  voice_id?:
    | "Emily"
    | "James"
    | "Isabella"
    | "Liam"
    | "Chloe"
    | "Adrian"
    | "Harper"
    | "Ava"
    | "Sophia"
    | "Julia"
    | "Mason"
    | "Jack"
    | "Oliver"
    | "Ethan"
    | "Auto";
};
export type PixverseSoundEffectsInput = {
  /**
   * Whether to keep the original audio from the video
   */
  original_sound_switch?: boolean;
  /**
   * Description of the sound effect to generate. If empty, a random sound effect will be generated Default value: `""`
   */
  prompt?: string;
  /**
   * URL of the input video to add sound effects to
   */
  video_url: string | Blob | File;
};
export type PixverseSwapInput = {
  /**
   * URL of the target image for swapping
   */
  image_url: string | Blob | File;
  /**
   * The keyframe ID to use for face/object mapping. The input video is normalized to 24 FPS before processing, so keyframe 1 = first frame, keyframe 24 = 1 second in, etc. Valid range: 1 to (duration_seconds * 24). Default value: `1`
   */
  keyframe_id?: number;
  /**
   * The swap mode to use Default value: `"person"`
   */
  mode?: "person" | "object" | "background";
  /**
   * Whether to keep the original audio Default value: `true`
   */
  original_sound_switch?: boolean;
  /**
   * The output resolution (1080p not supported) Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * URL of the external video to swap
   */
  video_url: string | Blob | File;
};
export type PixverseV4ImageToVideoFastInput = {
  /**
   * The type of camera movement to apply to the video
   */
  camera_movement?:
    | "horizontal_left"
    | "horizontal_right"
    | "vertical_up"
    | "vertical_down"
    | "zoom_in"
    | "zoom_out"
    | "crane_up"
    | "quickly_zoom_in"
    | "quickly_zoom_out"
    | "smooth_zoom_in"
    | "camera_rotation"
    | "robo_arm"
    | "super_dolly_out"
    | "whip_pan"
    | "hitchcock"
    | "left_follow"
    | "right_follow"
    | "pan_left"
    | "pan_right"
    | "fix_bg";
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
};
export type PixverseV4ImageToVideoInput = {
  /**
   * The type of camera movement to apply to the video
   */
  camera_movement?:
    | "horizontal_left"
    | "horizontal_right"
    | "vertical_up"
    | "vertical_down"
    | "zoom_in"
    | "zoom_out"
    | "crane_up"
    | "quickly_zoom_in"
    | "quickly_zoom_out"
    | "smooth_zoom_in"
    | "camera_rotation"
    | "robo_arm"
    | "super_dolly_out"
    | "whip_pan"
    | "hitchcock"
    | "left_follow"
    | "right_follow"
    | "pan_left"
    | "pan_right"
    | "fix_bg";
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
};
export type PixverseV4TextToVideoFastInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
};
export type PixverseV4TextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
};
export type PixverseV55EffectsInput = {
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8" | "10";
  /**
   * The effect to apply to the video
   */
  effect:
    | "Kiss Me AI"
    | "Kiss"
    | "Muscle Surge"
    | "Warmth of Jesus"
    | "Anything, Robot"
    | "The Tiger Touch"
    | "Hug"
    | "Holy Wings"
    | "Microwave"
    | "Zombie Mode"
    | "Squid Game"
    | "Baby Face"
    | "Black Myth: Wukong"
    | "Long Hair Magic"
    | "Leggy Run"
    | "Fin-tastic Mermaid"
    | "Punch Face"
    | "Creepy Devil Smile"
    | "Thunder God"
    | "Eye Zoom Challenge"
    | "Who's Arrested?"
    | "Baby Arrived"
    | "Werewolf Rage"
    | "Bald Swipe"
    | "BOOM DROP"
    | "Huge Cutie"
    | "Liquid Metal"
    | "Sharksnap!"
    | "Dust Me Away"
    | "3D Figurine Factor"
    | "Bikini Up"
    | "My Girlfriends"
    | "My Boyfriends"
    | "Subject 3 Fever"
    | "Earth Zoom"
    | "Pole Dance"
    | "Vroom Dance"
    | "GhostFace Terror"
    | "Dragon Evoker"
    | "Skeletal Bae"
    | "Summoning succubus"
    | "Halloween Voodoo Doll"
    | "3D Naked-Eye AD"
    | "Package Explosion"
    | "Dishes Served"
    | "Ocean ad"
    | "Supermarket AD"
    | "Tree doll"
    | "Come Feel My Abs"
    | "The Bicep Flex"
    | "London Elite Vibe"
    | "Flora Nymph Gown"
    | "Christmas Costume"
    | "It's Snowy"
    | "Reindeer Cruiser"
    | "Snow Globe Maker"
    | "Pet Christmas Outfit"
    | "Adopt a Polar Pal"
    | "Cat Christmas Box"
    | "Starlight Gift Box"
    | "Xmas Poster"
    | "Pet Christmas Tree"
    | "City Santa Hat"
    | "Stocking Sweetie"
    | "Christmas Night"
    | "Xmas Front Page Karma"
    | "Grinch's Xmas Hijack"
    | "Giant Product"
    | "Truck Fashion Shoot"
    | "Beach AD"
    | "Shoal Surround"
    | "Mechanical Assembly"
    | "Lighting AD"
    | "Billboard AD"
    | "Product close-up"
    | "Parachute Delivery"
    | "Dreamlike Cloud"
    | "Macaron Machine"
    | "Poster AD"
    | "Truck AD"
    | "Graffiti AD"
    | "3D Figurine Factory"
    | "The Exclusive First Class"
    | "Art Zoom Challenge"
    | "I Quit"
    | "Hitchcock Dolly Zoom"
    | "Smell the Lens"
    | "I believe I can fly"
    | "Strikout Dance"
    | "Pixel World"
    | "Mint in Box"
    | "Hands up, Hand"
    | "Flora Nymph Go"
    | "Somber Embrace"
    | "Beam me up"
    | "Suit Swagger";
  /**
   * Optional URL of the image to use as the first frame. If not provided, generates from text
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PixverseV55ImageToVideoInput = {
  /**
   * The duration of the generated video in seconds. Longer durations cost more. 1080p videos are limited to 5 or 8 seconds Default value: `"5"`
   */
  duration?: "5" | "8" | "10";
  /**
   * Enable audio generation (BGM, SFX, dialogue)
   */
  generate_audio_switch?: boolean;
  /**
   * Enable multi-clip generation with dynamic camera changes
   */
  generate_multi_clip_switch?: boolean;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PixverseV55TextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The duration of the generated video in seconds. Longer durations cost more. 1080p videos are limited to 5 or 8 seconds Default value: `"5"`
   */
  duration?: "5" | "8" | "10";
  /**
   * Enable audio generation (BGM, SFX, dialogue)
   */
  generate_audio_switch?: boolean;
  /**
   * Enable multi-clip generation with dynamic camera changes
   */
  generate_multi_clip_switch?: boolean;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PixverseV55TransitionInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The duration of the generated video in seconds. Longer durations cost more. 1080p videos are limited to 5 or 8 seconds Default value: `"5"`
   */
  duration?: "5" | "8" | "10";
  /**
   * URL of the image to use as the last frame
   */
  end_image_url?: string | Blob | File;
  /**
   * URL of the image to use as the first frame
   */
  first_image_url: string | Blob | File;
  /**
   * Enable audio generation (BGM, SFX, dialogue)
   */
  generate_audio_switch?: boolean;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The prompt for the transition
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PixverseV56ImageToVideoInput = {
  /**
   * The duration of the generated video in seconds. 1080p videos are limited to 5 or 8 seconds Default value: `"5"`
   */
  duration?: "5" | "8" | "10";
  /**
   * Enable audio generation (BGM, SFX, dialogue)
   */
  generate_audio_switch?: boolean;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PixverseV56TextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The duration of the generated video in seconds. 1080p videos are limited to 5 or 8 seconds Default value: `"5"`
   */
  duration?: "5" | "8" | "10";
  /**
   * Enable audio generation (BGM, SFX, dialogue)
   */
  generate_audio_switch?: boolean;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PixverseV5ImageToVideoInput = {
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
};
export type PixverseV5TransitionInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * URL of the image to use as the last frame
   */
  end_image_url?: string | Blob | File;
  /**
   * URL of the image to use as the first frame
   */
  first_image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The prompt for the transition
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
};
export type PixverseV6ExtendInput = {
  /**
   * The duration of the generated video in seconds. v6 supports values from 1 to 15 seconds Default value: `5`
   */
  duration?: number;
  /**
   * Enable audio generation (BGM, SFX, dialogue).
   */
  generate_audio_switch?: boolean;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Prompt describing how to extend the video
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The style of the extended video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * URL of the input video to extend
   */
  video_url: string | Blob | File;
};
export type PixverseV6ImageToVideoInput = {
  /**
   * The duration of the generated video in seconds. v6 supports values from 1 to 15 seconds Default value: `5`
   */
  duration?: number;
  /**
   * Enable audio generation (BGM, SFX, dialogue)
   */
  generate_audio_switch?: boolean;
  /**
   * Enable multi-clip generation with dynamic camera changes
   */
  generate_multi_clip_switch?: boolean;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PixverseV6TextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?:
    | "16:9"
    | "4:3"
    | "1:1"
    | "3:4"
    | "9:16"
    | "2:3"
    | "3:2"
    | "21:9";
  /**
   * The duration of the generated video in seconds. v6 supports values from 1 to 15 seconds Default value: `5`
   */
  duration?: number;
  /**
   * Enable audio generation (BGM, SFX, dialogue)
   */
  generate_audio_switch?: boolean;
  /**
   * Enable multi-clip generation with dynamic camera changes
   */
  generate_multi_clip_switch?: boolean;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PixverseV6TransitionInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?:
    | "16:9"
    | "4:3"
    | "1:1"
    | "3:4"
    | "9:16"
    | "2:3"
    | "3:2"
    | "21:9";
  /**
   * The duration of the generated video in seconds. v6 supports values from 1 to 15 seconds Default value: `5`
   */
  duration?: number;
  /**
   * URL of the image to use as the last frame
   */
  end_image_url?: string | Blob | File;
  /**
   * URL of the image to use as the first frame
   */
  first_image_url: string | Blob | File;
  /**
   * Enable audio generation (BGM, SFX, dialogue)
   */
  generate_audio_switch?: boolean;
  /**
   * Enable multi-clip generation with dynamic camera changes
   */
  generate_multi_clip_switch?: boolean;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The prompt for the transition
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * Prompt optimization mode: 'enabled' to optimize, 'disabled' to turn off, 'auto' for model decision
   */
  thinking_type?: "enabled" | "disabled" | "auto";
};
export type PlaygroundV25Input = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
};
export type plushifyInput = {
  /**
   * Whether to enable the safety checker Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance scale for the generation Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the image to apply cartoon style to
   */
  image_url: string | Blob | File;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Number of inference steps Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Prompt for the generation. Default is empty which is usually best, but sometimes it can help to add a description of the subject. Default value: `""`
   */
  prompt?: string;
  /**
   * Scale factor for the Cartoon effect Default value: `1`
   */
  scale?: number;
  /**
   * The seed for image generation. Same seed with same parameters will generate same image.
   */
  seed?: number;
  /**
   * Whether to use CFG zero
   */
  use_cfg_zero?: boolean;
};
export type Point = {
  /**
   * The x coordinate.
   */
  x: number;
  /**
   * The y coordinate.
   */
  y: number;
};
export type PointPrompt = {
  /**
   * The frame index to interact with.
   */
  frame_index?: number;
  /**
   * 1 for foreground, 0 for background
   */
  label?: "0" | "1";
  /**
   * Optional object identifier. Prompts sharing an object id refine the same object.
   */
  object_id?: number;
  /**
   * X Coordinate of the prompt
   */
  x?: number;
  /**
   * Y Coordinate of the prompt
   */
  y?: number;
};
export type PointPromptBase = {
  /**
   * 1 for foreground, 0 for background
   */
  label?: "0" | "1";
  /**
   * Optional object identifier. Prompts sharing an object id refine the same object.
   */
  object_id?: number;
  /**
   * X Coordinate of the prompt
   */
  x?: number;
  /**
   * Y Coordinate of the prompt
   */
  y?: number;
};
export type Polygon = {
  /**
   * Label of the polygon
   */
  label: string;
  /**
   * List of points
   */
  points: Array<unknown>;
};
export type PolygonOutput = {
  /**
   * List of polygons
   */
  polygons: Array<Polygon>;
};
export type PonyV7Input = {
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier free guidance scale Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The source of the noise to use for generating images.
   * If set to 'gpu', the noise will be generated on the GPU.
   * If set to 'cpu', the noise will be generated on the CPU. Default value: `"gpu"`
   */
  noise_source?: "gpu" | "cpu";
  /**
   * The number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to take Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate images from
   */
  prompt: string;
  /**
   * The seed to use for generating images
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type PortraitInput = {
  /**
   * Aspect ratio for 4K output (default: 3:4 for portraits)
   */
  aspect_ratio?: AspectRatio;
  /**
   * Portrait image URL to enhance
   */
  image_url: string | Blob | File;
};
export type PoseTransferInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your input when generating the image. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Url to the garment image.
   */
  person_image_url: string | Blob | File;
  /**
   * Url for the human image.
   */
  pose_image_url: string | Blob | File;
  /**
   * The same seed and the same input given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type PostProcessingInput = {
  /**
   * Blue channel shift direction Default value: `"horizontal"`
   */
  blue_direction?: "horizontal" | "vertical";
  /**
   * Blue channel shift amount
   */
  blue_shift?: number;
  /**
   * Blur radius Default value: `3`
   */
  blur_radius?: number;
  /**
   * Sigma for Gaussian blur Default value: `1`
   */
  blur_sigma?: number;
  /**
   * Type of blur to apply Default value: `"gaussian"`
   */
  blur_type?: "gaussian" | "kuwahara";
  /**
   * Brightness adjustment
   */
  brightness?: number;
  /**
   * CAS sharpening amount Default value: `0.8`
   */
  cas_amount?: number;
  /**
   * Contrast adjustment
   */
  contrast?: number;
  /**
   * Desaturation factor Default value: `1`
   */
  desaturate_factor?: number;
  /**
   * Desaturation method Default value: `"luminance (Rec.709)"`
   */
  desaturate_method?:
    | "luminance (Rec.709)"
    | "luminance (Rec.601)"
    | "average"
    | "lightness";
  /**
   * Dissolve blend factor Default value: `0.5`
   */
  dissolve_factor?: number;
  /**
   * URL of second image for dissolve Default value: `""`
   */
  dissolve_image_url?: string | Blob | File;
  /**
   * Dodge and burn intensity Default value: `0.5`
   */
  dodge_burn_intensity?: number;
  /**
   * Dodge and burn mode Default value: `"dodge"`
   */
  dodge_burn_mode?:
    | "dodge"
    | "burn"
    | "dodge_and_burn"
    | "burn_and_dodge"
    | "color_dodge"
    | "color_burn"
    | "linear_dodge"
    | "linear_burn";
  /**
   * Enable blur effect
   */
  enable_blur?: boolean;
  /**
   * Enable chromatic aberration
   */
  enable_chromatic?: boolean;
  /**
   * Enable color correction
   */
  enable_color_correction?: boolean;
  /**
   * Enable desaturation effect
   */
  enable_desaturate?: boolean;
  /**
   * Enable dissolve effect
   */
  enable_dissolve?: boolean;
  /**
   * Enable dodge and burn effect
   */
  enable_dodge_burn?: boolean;
  /**
   * Enable glow effect
   */
  enable_glow?: boolean;
  /**
   * Enable film grain effect
   */
  enable_grain?: boolean;
  /**
   * Enable parabolize effect
   */
  enable_parabolize?: boolean;
  /**
   * Enable sharpen effect
   */
  enable_sharpen?: boolean;
  /**
   * Enable solarize effect
   */
  enable_solarize?: boolean;
  /**
   * Enable color tint effect
   */
  enable_tint?: boolean;
  /**
   * Enable vignette effect
   */
  enable_vignette?: boolean;
  /**
   * Gamma adjustment Default value: `1`
   */
  gamma?: number;
  /**
   * Glow intensity Default value: `1`
   */
  glow_intensity?: number;
  /**
   * Glow blur radius Default value: `5`
   */
  glow_radius?: number;
  /**
   * Film grain intensity (when enabled) Default value: `0.4`
   */
  grain_intensity?: number;
  /**
   * Film grain scale (when enabled) Default value: `10`
   */
  grain_scale?: number;
  /**
   * Style of film grain to apply Default value: `"modern"`
   */
  grain_style?:
    | "modern"
    | "analog"
    | "kodak"
    | "fuji"
    | "cinematic"
    | "newspaper";
  /**
   * Green channel shift direction Default value: `"horizontal"`
   */
  green_direction?: "horizontal" | "vertical";
  /**
   * Green channel shift amount
   */
  green_shift?: number;
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Noise radius for smart sharpen Default value: `7`
   */
  noise_radius?: number;
  /**
   * Parabolize coefficient Default value: `1`
   */
  parabolize_coeff?: number;
  /**
   * Edge preservation factor Default value: `0.75`
   */
  preserve_edges?: number;
  /**
   * Red channel shift direction Default value: `"horizontal"`
   */
  red_direction?: "horizontal" | "vertical";
  /**
   * Red channel shift amount
   */
  red_shift?: number;
  /**
   * Saturation adjustment
   */
  saturation?: number;
  /**
   * Sharpen strength (for basic mode) Default value: `1`
   */
  sharpen_alpha?: number;
  /**
   * Type of sharpening to apply Default value: `"basic"`
   */
  sharpen_mode?: "basic" | "smart" | "cas";
  /**
   * Sharpen radius (for basic mode) Default value: `1`
   */
  sharpen_radius?: number;
  /**
   * Smart sharpen blend ratio Default value: `0.5`
   */
  smart_sharpen_ratio?: number;
  /**
   * Smart sharpen strength Default value: `5`
   */
  smart_sharpen_strength?: number;
  /**
   * Solarize threshold Default value: `0.5`
   */
  solarize_threshold?: number;
  /**
   * Color temperature adjustment
   */
  temperature?: number;
  /**
   * Tint color mode Default value: `"sepia"`
   */
  tint_mode?:
    | "sepia"
    | "red"
    | "green"
    | "blue"
    | "cyan"
    | "magenta"
    | "yellow"
    | "purple"
    | "orange"
    | "warm"
    | "cool"
    | "lime"
    | "navy"
    | "vintage"
    | "rose"
    | "teal"
    | "maroon"
    | "peach"
    | "lavender"
    | "olive";
  /**
   * Tint strength Default value: `1`
   */
  tint_strength?: number;
  /**
   * Vertex X position Default value: `0.5`
   */
  vertex_x?: number;
  /**
   * Vertex Y position Default value: `0.5`
   */
  vertex_y?: number;
  /**
   * Vignette strength (when enabled) Default value: `0.5`
   */
  vignette_strength?: number;
};
export type ProductHoldingInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   * Image URL of the person who will hold the product
   */
  person_image_url: string | Blob | File;
  /**
   * Image URL of the product to be held by the person
   */
  product_image_url: string | Blob | File;
};
export type ProductPhotographyInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   * Image URL of the product to create professional studio photography
   */
  product_image_url: string | Blob | File;
};
export type ProductShotInput = {
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * The URL of the product shot to be placed in a lifestyle shot. If both image_url and image_file are provided, image_url will be used. Accepted formats are jpeg, jpg, png, webp. Maximum file size 12MB.
   */
  image_url: string | Blob | File;
  /**
   * If you've selected placement_type=manual_placement, you should use this parameter to specify which placements/positions you would like to use from the list. You can select more than one placement in one request. Default value: `"bottom_center"`
   */
  manual_placement_selection?:
    | "upper_left"
    | "upper_right"
    | "bottom_left"
    | "bottom_right"
    | "right_center"
    | "left_center"
    | "upper_center"
    | "bottom_center"
    | "center_vertical"
    | "center_horizontal";
  /**
   * The number of lifestyle product shots you would like to generate. You will get num_results x 10 results when placement_type=automatic and according to the number of required placements x num_results if placement_type=manual_placement. Default value: `1`
   */
  num_results?: number;
  /**
   * Whether to optimize the scene description Default value: `true`
   */
  optimize_description?: boolean;
  /**
   * This flag is only relevant when placement_type=original. If true, the output image retains the original input image's size; otherwise, the image is scaled to 1 megapixel (1MP) while preserving its aspect ratio.
   */
  original_quality?: boolean;
  /**
   * The desired padding in pixels around the product, when using placement_type=manual_padding. The order of the values is [left, right, top, bottom]. For optimal results, the total number of pixels, including padding, should be around 1,000,000. It is recommended to first use the product cutout API, get the cutout and understand the size of the result, and then define the required padding and use the cutout as an input for this API.
   */
  padding_values?: Array<number>;
  /**
   * This parameter allows you to control the positioning of the product in the image. Choosing 'original' will preserve the original position of the product in the image. Choosing 'automatic' will generate results with the 10 recommended positions for the product. Choosing 'manual_placement' will allow you to select predefined positions (using the parameter 'manual_placement_selection'). Selecting 'manual_padding' will allow you to control the position and size of the image by defining the desired padding in pixels around the product. Default value: `"manual_placement"`
   */
  placement_type?:
    | "original"
    | "automatic"
    | "manual_placement"
    | "manual_padding";
  /**
   * The URL of the reference image to be used for generating the new scene or background for the product shot. Use "" to leave empty.Either ref_image_url or scene_description has to be provided but not both. If both ref_image_url and ref_image_file are provided, ref_image_url will be used. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  ref_image_url?: string | Blob | File;
  /**
   * Text description of the new scene or background for the provided product shot. Bria currently supports prompts in English only, excluding special characters.
   */
  scene_description?: string;
  /**
   * The desired size of the final product shot. For optimal results, the total number of pixels should be around 1,000,000. This parameter is only relevant when placement_type=automatic or placement_type=manual_placement.
   */
  shot_size?: Array<number>;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ProImageTo3DInput = {
  /**
   * Optional back/rear view image URL (JPG/PNG recommended).
   */
  back_image_url?: string | Blob | File;
  /**
   * Optional bottom view image URL (v3.1 exclusive, JPG/PNG recommended).
   */
  bottom_image_url?: string | Blob | File;
  /**
   * Enable PBR material generation (metallic, roughness, normal textures). Ignored when generate_type is Geometry.
   */
  enable_pbr?: boolean;
  /**
   * Target polygon face count. Range: 40,000-1,500,000. Default: 500,000. Default value: `500000`
   */
  face_count?: number;
  /**
   * Generation task type. Normal: textured model. Geometry: geometry-only white model (no textures). LowPoly/Sketch are not available in v3.1. Default value: `"Normal"`
   */
  generate_type?: "Normal" | "Geometry";
  /**
   * Front view image URL. Resolution: 128-5000px, max 8MB, formats: JPG/PNG/WEBP. Tips: simple background, single object, object >50% of frame.
   */
  input_image_url: string | Blob | File;
  /**
   * Optional left-front 45 degree angle view image URL (v3.1 exclusive, JPG/PNG recommended).
   */
  left_front_image_url?: string | Blob | File;
  /**
   * Optional left side view image URL (JPG/PNG recommended).
   */
  left_image_url?: string | Blob | File;
  /**
   * Optional right-front 45 degree angle view image URL (v3.1 exclusive, JPG/PNG recommended).
   */
  right_front_image_url?: string | Blob | File;
  /**
   * Optional right side view image URL (JPG/PNG recommended).
   */
  right_image_url?: string | Blob | File;
  /**
   * Optional top view image URL (v3.1 exclusive, JPG/PNG recommended).
   */
  top_image_url?: string | Blob | File;
};
export type ProImageToVideoHailuo02Input = {
  /**
   * Optional URL of the image to use as the last frame of the video
   */
  end_image_url?: string | Blob | File;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type ProImageToVideoHailuo23Input = {
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Text prompt for video generation
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type ProImageToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "9:16" | "16:9";
  /**
   * Up to two character IDs (from create-character) to use in the video. Refer to characters by name in the prompt. When set, only the OpenAI provider is used.
   */
  character_ids?: Array<string>;
  /**
   * Whether to delete the video after generation for privacy reasons. If True, the video cannot be used for remixing and will be permanently deleted. Default value: `true`
   */
  delete_video?: boolean;
  /**
   * If enabled, the prompt (and image for image-to-video) will be checked for known intellectual property references and the request will be blocked if any are detected.
   */
  detect_and_block_ip?: boolean;
  /**
   * Duration of the generated video in seconds Default value: `"4"`
   */
  duration?: "4" | "8" | "12" | "16" | "20";
  /**
   * The URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"auto"`
   */
  resolution?: "auto" | "720p" | "1080p" | "true_1080p";
};
export type PromptInput = {
  /**
   * The URL of the image to remove objects from.
   */
  image_url: string | Blob | File;
  /**
   * Amount of pixels to expand the mask by. Range: 0-50 Default value: `15`
   */
  mask_expansion?: number;
  /**
   *  Default value: `"best_quality"`
   */
  model?: "low_quality" | "medium_quality" | "high_quality" | "best_quality";
  /**
   * Text description of the object to remove.
   */
  prompt: string;
};
export type PromptObject = {
  /**
   * The action of the object in the image.
   */
  action?: string;
  /**
   * The appearance details of the object.
   */
  appearance_details?: string;
  /**
   * Bounding boxes defining the location of the object in the image.
   */
  bounding_boxes?: Array<BoundingBox>;
  /**
   * The clothing of the object in the image.
   */
  clothing?: string;
  /**
   * A list of colors associated with the object.
   */
  colors?: Array<Color>;
  /**
   * A description of the object to be generated.
   */
  description?: string;
  /**
   * The expression of the object in the image.
   */
  expression?: string;
  /**
   * The gender of the object in the image.
   */
  gender?: string;
  /**
   * The location of the object in the image.
   */
  location?: string;
  /**
   * The number of objects in the image.
   */
  number_of_objects?: number;
  /**
   * The orientation of the object in the image.
   */
  orientation?: string;
  /**
   * The pose of the object in the image.
   */
  pose?: string;
  /**
   * The relationship of the object to other objects in the image.
   */
  relationship?: string;
  /**
   * The relative distance of the object from the camera or viewer.
   */
  relative_distance?: number;
  /**
   * The relative size of the object in the image.
   */
  relative_size?: string;
  /**
   * The shape and color of the object.
   */
  shape_and_color?: string;
  /**
   * The RGB color values for the skin tone of the object.
   */
  skin_color?: Color;
  /**
   * The skin tone and texture of the object in the image.
   */
  skin_tone_and_texture?: string;
  /**
   * The texture of the object.
   */
  texture?: string;
};
export type PronunciationDict = {
  /**
   * List of pronunciation replacements in format ['text/(pronunciation)', ...]. For Chinese, tones are 1-5. Example: ['燕少飞/(yan4)(shao3)(fei1)']
   */
  tone_list?: Array<string>;
};
export type PronunciationDictionaryLocator = {
  /**
   * The ID of the pronunciation dictionary.
   */
  pronunciation_dictionary_id?: string;
  /**
   * The ID of the version of the pronunciation dictionary. If not provided, the latest version will be used.
   */
  version_id?: string;
};
export type ProTextTo3DInput = {
  /**
   * Enable PBR material generation (metallic, roughness, normal textures). Ignored when generate_type is Geometry.
   */
  enable_pbr?: boolean;
  /**
   * Target polygon face count. Range: 40,000-1,500,000. Default: 500,000. Default value: `500000`
   */
  face_count?: number;
  /**
   * Generation task type. Normal: textured model. Geometry: geometry-only white model (no textures). LowPoly/Sketch are not available in v3.1. Default value: `"Normal"`
   */
  generate_type?: "Normal" | "Geometry";
  /**
   * Text description of the 3D content to generate. Max 1024 UTF-8 characters.
   */
  prompt: string;
};
export type ProTextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Up to two character IDs (from create-character) to use in the video. Refer to characters by name in the prompt. When set, only the OpenAI provider is used.
   */
  character_ids?: Array<string>;
  /**
   * Whether to delete the video after generation for privacy reasons. If True, the video cannot be used for remixing and will be permanently deleted. Default value: `true`
   */
  delete_video?: boolean;
  /**
   * If enabled, the prompt (and image for image-to-video) will be checked for known intellectual property references and the request will be blocked if any are detected.
   */
  detect_and_block_ip?: boolean;
  /**
   * Duration of the generated video in seconds Default value: `"4"`
   */
  duration?: "4" | "8" | "12" | "16" | "20";
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p" | "true_1080p";
};
export type pshumanInput = {
  /**
   * Guidance scale for the diffusion process. Controls how much the output adheres to the generated views. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * A direct URL to the input image of a person.
   */
  image_url: string | Blob | File;
  /**
   * Seed for reproducibility. If None, a random seed will be used.
   */
  seed?: number;
};
export type pshumanOutput = {
  /**
   * The generated 3D model in OBJ format.
   */
  model_obj: File;
  /**
   * A preview image showing the input and the generated multi-view outputs.
   */
  preview_image: File;
};
export type pulidInput = {
  /**
   * Guidance scale Default value: `1.2`
   */
  guidance_scale?: number;
  /**
   * if you want to mix two ID image, please turn this on, otherwise, turn this off
   */
  id_mix?: boolean;
  /**
   * ID scale Default value: `0.8`
   */
  id_scale?: number;
  /**
   * Size of the generated image
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Mode of generation Default value: `"fidelity"`
   */
  mode?: "fidelity" | "extreme style";
  /**
   * Negative prompt to generate the face from Default value: `"flaws in the eyes, flaws in the face, flaws, lowres, non-HDRi, low quality, worst quality,artifacts noise, text, watermark, glitch, deformed, mutated, ugly, disfigured, hands, low resolution, partially rendered objects,  deformed or partially rendered eyes, deformed, deformed eyeballs, cross-eyed,blurry"`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Number of steps to take Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * Prompt to generate the face from
   */
  prompt: string;
  /**
   * List of reference faces, ideally 4 images.
   */
  reference_images: Array<ReferenceFace>;
  /**
   * Random seed for reproducibility
   */
  seed?: number;
};
export type pulidOutput = {
  /**
   * List of generated images
   */
  images: Array<Image>;
  /**
   * Random seed used for reproducibility
   */
  seed: number;
};
export type QueryInput = {
  /**
   * Image URL to be processed
   */
  image_url: string | Blob | File;
  /**
   * Maximum number of tokens to generate Default value: `64`
   */
  max_tokens?: number;
  /**
   * Prompt for query task
   */
  prompt: string;
  /**
   * Type of task to perform Default value: `"caption"`
   */
  task_type?: "caption" | "query";
};
export type Qwen3CloneVoiceInput = {
  /**
   * URL to the reference audio file used for voice cloning.
   */
  audio_url: string | Blob | File;
  /**
   * Optional reference text that was used when creating the speaker embedding. Providing this can improve synthesis quality when using a cloned voice.
   */
  reference_text?: string;
};
export type Qwen3CloneVoiceOutput = {
  /**
   * The generated speaker embedding file in safetensors format.
   */
  speaker_embedding: File;
};
export type Qwen3DesignVoiceInput = {
  /**
   * The language of the voice to be designed. Default value: `"Auto"`
   */
  language?:
    | "Auto"
    | "English"
    | "Chinese"
    | "Spanish"
    | "French"
    | "German"
    | "Italian"
    | "Japanese"
    | "Korean"
    | "Portuguese"
    | "Russian";
  /**
   * Maximum number of new codec tokens to generate. Default value: `200`
   */
  max_new_tokens?: number;
  /**
   * Optional prompt to guide the style of the generated speech.
   */
  prompt: string;
  /**
   * Penalty to reduce repeated tokens/codes. Default value: `1.05`
   */
  repetition_penalty?: number;
  /**
   * Sampling switch for the sub-talker. Default value: `true`
   */
  subtalker_dosample?: boolean;
  /**
   * Temperature for sub-talker sampling. Default value: `0.9`
   */
  subtalker_temperature?: number;
  /**
   * Top-k for sub-talker sampling. Default value: `50`
   */
  subtalker_top_k?: number;
  /**
   * Top-p for sub-talker sampling. Default value: `1`
   */
  subtalker_top_p?: number;
  /**
   * Sampling temperature; higher => more random. Default value: `0.9`
   */
  temperature?: number;
  /**
   * The text to be converted to speech.
   */
  text: string;
  /**
   * Top-k sampling parameter. Default value: `50`
   */
  top_k?: number;
  /**
   * Top-p sampling parameter. Default value: `1`
   */
  top_p?: number;
};
export type Qwen3GuardInput = {
  /**
   * The input text to be classified
   */
  prompt: string;
};
export type Qwen3GuardOutput = {
  /**
   * The confidence score of the classification
   */
  categories: Array<
    | "Violent"
    | "Non-violent Illegal Acts"
    | "Sexual Content or Sexual Acts"
    | "PII"
    | "Suicide & Self-Harm"
    | "Unethical Acts"
    | "Politically Sensitive Topics"
    | "Copyright Violation"
    | "Jailbreak"
    | "None"
  >;
  /**
   * The classification label
   */
  label: "Safe" | "Unsafe" | "Controversial";
};
export type Qwen3TTSInput = {
  /**
   * The language of the voice. Default value: `"Auto"`
   */
  language?:
    | "Auto"
    | "English"
    | "Chinese"
    | "Spanish"
    | "French"
    | "German"
    | "Italian"
    | "Japanese"
    | "Korean"
    | "Portuguese"
    | "Russian";
  /**
   * Maximum number of new codec tokens to generate. Default value: `200`
   */
  max_new_tokens?: number;
  /**
   * Optional prompt to guide the style of the generated speech. This prompt will be ignored if a speaker embedding is provided.
   */
  prompt?: string;
  /**
   * Optional reference text that was used when creating the speaker embedding. Providing this can improve synthesis quality when using a cloned voice.
   */
  reference_text?: string;
  /**
   * Penalty to reduce repeated tokens/codes. Default value: `1.05`
   */
  repetition_penalty?: number;
  /**
   * URL to a speaker embedding file in safetensors format, from `fal-ai/qwen-3-tts/clone-voice` endpoint. If provided, the TTS model will use the cloned voice for synthesis instead of the predefined voices.
   */
  speaker_voice_embedding_file_url?: string | Blob | File;
  /**
   * Sampling switch for the sub-talker. Default value: `true`
   */
  subtalker_dosample?: boolean;
  /**
   * Temperature for sub-talker sampling. Default value: `0.9`
   */
  subtalker_temperature?: number;
  /**
   * Top-k for sub-talker sampling. Default value: `50`
   */
  subtalker_top_k?: number;
  /**
   * Top-p for sub-talker sampling. Default value: `1`
   */
  subtalker_top_p?: number;
  /**
   * Sampling temperature; higher => more random. Default value: `0.9`
   */
  temperature?: number;
  /**
   * The text to be converted to speech.
   */
  text: string;
  /**
   * Top-k sampling parameter. Default value: `50`
   */
  top_k?: number;
  /**
   * Top-p sampling parameter. Default value: `1`
   */
  top_p?: number;
  /**
   * The voice to be used for speech synthesis, will be ignored if a speaker embedding is provided. Check out the **[documentation](https://github.com/QwenLM/Qwen3-TTS/tree/main?tab=readme-ov-file#custom-voice-generate)** for each voice's details and which language they primarily support.
   */
  voice?:
    | "Vivian"
    | "Serena"
    | "Uncle_Fu"
    | "Dylan"
    | "Eric"
    | "Ryan"
    | "Aiden"
    | "Ono_Anna"
    | "Sohee";
};
export type QwenImage2EditInput = {
  /**
   * Enable LLM prompt optimization for better results. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. If not provided, the size of the final input image will be used.  Total number of pixels must be between 512x512 and 2048x2048.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Reference images for editing (1-3 images required). Order matters: reference as 'image 1', 'image 2', 'image 3' in prompt. Resolution: 384-5000px each dimension. Max size: 10MB each. Formats: JPEG, JPG, PNG (no alpha), WEBP.
   */
  image_urls: Array<string>;
  /**
   * Content to avoid in the generated image. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text prompt describing the desired image. Supports Chinese and English. Max 800 characters.
   */
  prompt: string;
  /**
   * Random seed for reproducibility (0-2147483647).
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type QwenImage2TextToImageInput = {
  /**
   * Enable LLM prompt optimization for better results. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Total number of pixels must be between 512x512 and 2048x2048. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Content to avoid in the generated image. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text prompt describing the desired image. Supports Chinese and English.
   */
  prompt: string;
  /**
   * Random seed for reproducibility (0-2147483647).
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type QwenImageEdit2511MultipleAnglesInput = {
  /**
   * Acceleration level for image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Additional text to append to the automatically generated prompt.
   */
  additional_prompt?: string;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * Horizontal rotation angle around the object in degrees. 0°=front view, 90°=right side, 180°=back view, 270°=left side, 360°=front view again.
   */
  horizontal_angle?: number;
  /**
   * The size of the generated image. If not provided, the size of the input image will be used.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to adjust camera angle for.
   */
  image_urls: Array<string>;
  /**
   * The scale factor for the LoRA model. Controls the strength of the camera control effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The negative prompt for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
  /**
   * Vertical camera angle in degrees. -30°=low-angle shot (looking up), 0°=eye-level, 30°=elevated, 60°=high-angle, 90°=bird's-eye view (looking down).
   */
  vertical_angle?: number;
  /**
   * Camera zoom/distance. 0=wide shot (far away), 5=medium shot (normal), 10=close-up (very close). Default value: `5`
   */
  zoom?: number;
};
export type QwenImageEditInpaintInput = {
  /**
   * Acceleration level for image generation. Options: 'none', 'regular'. Higher acceleration increases speed. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the mask for inpainting
   */
  mask_url: string | Blob | File;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate the image with
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Strength of noising process for inpainting Default value: `0.93`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type QwenImageEditInput = {
  /**
   * Acceleration level for image generation. Options: 'none', 'regular'. Higher acceleration increases speed. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate the image with
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type QwenImageEditLoraInput = {
  /**
   * Acceleration level for image generation. Options: 'none', 'regular'. Higher acceleration increases speed. 'regular' balances speed and quality. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use up to 3 LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate the image with
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type QwenImageI2IInput = {
  /**
   * Acceleration level for image generation. Options: 'none', 'regular', 'high'. Higher acceleration increases speed. 'regular' balances speed and quality. 'high' is recommended for images without text. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. By default, we will use the provided image for determining the image_size.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The reference image to guide the generation.
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use up to 3 LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate the image with
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Denoising strength. 1.0 = fully remake; 0.0 = preserve original. Default value: `0.6`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Enable turbo mode for faster generation with high quality. When enabled, uses optimized settings (10 steps, CFG=1.2).
   */
  use_turbo?: boolean;
};
export type QwenImageInput = {
  /**
   * Acceleration level for image generation. Options: 'none', 'regular', 'high'. Higher acceleration increases speed. 'regular' balances speed and quality. 'high' is recommended for images without text. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The LoRAs to use for the image generation. You can use up to 3 LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate the image with
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Enable turbo mode for faster generation with high quality. When enabled, uses optimized settings (10 steps, CFG=1.2).
   */
  use_turbo?: boolean;
};
export type QwenImageLayeredInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the image generation. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The number of layers to generate. Default value: `4`
   */
  num_layers?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "png" | "webp";
  /**
   * A caption for the input image.
   */
  prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type QwenImageLayeredOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image files info.
   */
  images: Array<ImageFile>;
  /**
   * The prompt used to generate the image.
   */
  prompt?: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type QwenImageTrainerInput = {
  /**
   * URL to zip archive with images for training. The archive should contain images and corresponding text files with captions.
   * Each text file should have the same name as the image file it corresponds to (e.g., image1.jpg and image1.txt).
   * If text files are missing for some images, you can provide a trigger_phrase to automatically create them.
   * Supported image formats: PNG, JPG, JPEG, WEBP.
   * Try to use at least 10 images, although more is better.
   */
  image_data_url: string | Blob | File;
  /**
   * Learning rate for training. Default is 5e-4 Default value: `0.0005`
   */
  learning_rate?: number;
  /**
   * Total number of training steps to perform. Default is 4000. Default value: `1000`
   */
  steps?: number;
  /**
   * Default caption to use for images that don't have corresponding text files. If provided, missing .txt files will be created automatically. Default value: `""`
   */
  trigger_phrase?: string;
};
export type RapidImageTo3DInput = {
  /**
   * Generate geometry-only white model without textures. When enabled, enable_pbr is ignored and OBJ is not supported (default output is GLB).
   */
  enable_geometry?: boolean;
  /**
   * Enable PBR material generation (metallic, roughness, normal textures). Does not take effect when enable_geometry is True.
   */
  enable_pbr?: boolean;
  /**
   * Front view image URL. Resolution: 128-5000px, max 8MB (recommended ≤6MB for base64 encoding), formats: JPG/PNG/WEBP. Tips: simple background, single object, object >50% of frame.
   */
  input_image_url: string | Blob | File;
};
export type RapidImageTo3DOutput = {
  /**
   * MTL material file for the OBJ model.
   */
  material_mtl?: File;
  /**
   * Generated 3D model file. Contains GLB if available, otherwise OBJ.
   */
  model_glb?: File;
  /**
   * URLs for different 3D model formats.
   */
  model_urls: ModelUrls;
  /**
   * Texture image for the 3D model.
   */
  texture?: File;
  /**
   * Preview thumbnail of the generated model
   */
  thumbnail?: File;
};
export type RapidTextTo3DInput = {
  /**
   * Generate geometry-only white model without textures. When enabled, enable_pbr is ignored and OBJ is not supported (default output is GLB).
   */
  enable_geometry?: boolean;
  /**
   * Enable PBR material generation (metallic, roughness, normal textures). Does not take effect when enable_geometry is True.
   */
  enable_pbr?: boolean;
  /**
   * Text description of the 3D content to generate. Max 200 UTF-8 characters.
   */
  prompt: string;
};
export type RapidTextTo3DOutput = {
  /**
   * MTL material file for the OBJ model.
   */
  material_mtl?: File;
  /**
   * Generated 3D model in OBJ format.
   */
  model_obj?: File;
  /**
   * URLs for different 3D model formats.
   */
  model_urls: ModelUrls;
  /**
   * Texture image for the 3D model.
   */
  texture?: File;
  /**
   * Preview thumbnail of the generated model
   */
  thumbnail?: File;
};
export type RawImage = {
  /**
   *
   */
  content: string;
  /**
   *  Default value: `"image/jpeg"`
   */
  content_type?: string;
  /**
   *
   */
  height: number;
  /**
   *
   */
  width: number;
};
export type React1Input = {
  /**
   * URL to the input audio. Must be **15 seconds or shorter**.
   */
  audio_url: string | Blob | File;
  /**
   * Emotion prompt for the generation. Currently supports single-word emotions only.
   */
  emotion: "happy" | "angry" | "sad" | "neutral" | "disgusted" | "surprised";
  /**
   * Lipsync mode when audio and video durations are out of sync. Default value: `"bounce"`
   */
  lipsync_mode?: "cut_off" | "loop" | "bounce" | "silence" | "remap";
  /**
   * Controls the edit region and movement scope for the model. Available options:
   * - `lips`: Only lipsync using react-1 (minimal facial changes).
   * - `face`: Lipsync + facial expressions without head movements.
   * - `head`: Lipsync + facial expressions + natural talking head movements. Default value: `"face"`
   */
  model_mode?: "lips" | "face" | "head";
  /**
   * Controls the expresiveness of the lipsync. Default value: `0.5`
   */
  temperature?: number;
  /**
   * URL to the input video. Must be **15 seconds or shorter**.
   */
  video_url: string | Blob | File;
};
export type React1Output = {
  /**
   * The generated video with synchronized lip and facial movements.
   */
  video: VideoFile;
};
export type RealismInput = {
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the image to enhance with realism details.
   */
  image_url: string | Blob | File;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `0.6`
   */
  lora_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type RealisticVisionImageToImageInput = {
  /**
   * If set to true, the output cropped to the proper aspect ratio after generating.
   */
  crop_output?: boolean;
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The Realistic Vision model to use.
   */
  model_name?: string;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the aspect ratio of the generated image will be preserved even
   * if the image size is too large. However, if the image is not a multiple of 32
   * in width or height, it will be resized to the nearest multiple of 32. By default,
   * this snapping to the nearest multiple of 32 will not preserve the aspect ratio.
   * Set crop_output to True, to crop the output to the proper aspect ratio
   * after generating.
   */
  preserve_aspect_ratio?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type RealisticVisionInpaintingInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The URL of the mask to use for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The Realistic Vision model to use.
   */
  model_name?: string;
  /**
   * The negative prompt to use.Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `25`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * determines how much the generated image resembles the initial image Default value: `0.95`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type RealisticVisionInput = {
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The Realistic Vision model to use.
   */
  model_name?: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want in the image. Default value: `"(worst quality, low quality, normal quality, lowres, low details, oversaturated, undersaturated, overexposed, underexposed, grayscale, bw, bad photo, bad photography, bad art:1.4), (watermark, signature, text font, username, error, logo, words, letters, digits, autograph, trademark, name:1.2), (blur, blurry, grainy), morbid, ugly, asymmetrical, mutated malformed, mutilated, poorly lit, bad shadow, draft, cropped, out of frame, cut off, censored, jpeg artifacts, out of focus, glitch, duplicate, (airbrushed, cartoon, anime, semi-realistic, cgi, render, blender, digital art, manga, amateur:1.3), (3D ,3D Game, 3D Game Scene, 3D Character:1.1), (bad hands, bad anatomy, bad body, bad face, bad teeth, bad arms, bad legs, deformities:1.3)"`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type RealtimeEditInput = {
  /**
   * Enable RIFE frame interpolation between consecutive frames (doubles output frames).
   */
  enable_interpolation?: boolean;
  /**
   * The size of the generated image. square=768x768, square_hd=1024x1024. Default value: `"square"`
   */
  image_size?: "square" | "square_hd";
  /**
   * Base64-encoded image data URI for editing. CDN URLs are not supported for realtime. For optimal performance, use 704x704 JPEG images with 50% quality. Other sizes will be resized automatically.
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `3`
   */
  num_inference_steps?: number;
  /**
   * Output feedback loop. 1.0 = pure noise (no feedback), 0.9 = 90% noise + 10% previous output latent. Default value: `1`
   */
  output_feedback_strength?: number;
  /**
   * The prompt to guide image editing. Default value: `"Turn this into "Living oil painting, melting gold and sapphire""`
   */
  prompt?: string;
  /**
   * Schedule mu for time shift. 2.3=default, lower=more even denoising, 0.3=nearly linear. Default value: `2.3`
   */
  schedule_mu?: number;
  /**
   * Random seed for reproducibility. Default value: `35`
   */
  seed?: number;
};
export type RealtimeEditOutput = {
  /**
   * Generated images as raw bytes. When interpolation is enabled, returns [interpolated_frame, current_frame] in chronological order. Otherwise returns [current_frame].
   */
  images: Array<RawImage>;
  /**
   * Seed used for generation.
   */
  seed: number;
};
export type RealtimeImage = {
  /**
   *
   */
  content: string;
  /**
   *
   */
  content_type: string;
};
export type RealtimeInput = {
  /**
   * Background color to use in the output image Default value: `"white"`
   */
  background_color?: string;
  /**
   * Image bytes to remove background from
   */
  image_bytes: string;
  /**
   * Model to use for background removal Default value: `"General Use (Light)"`
   */
  model?:
    | "BiRefNet-DIS_ep580.pth"
    | "BiRefNet-portrait-TR_P3M_10k-epoch_120.pth"
    | "BiRefNet-massive-epoch_240.pth";
};
export type RealtimeLivePortraitImageInput = {
  /**
   * Amount to open mouth in 'aaa' shape
   */
  aaa?: number;
  /**
   * Amount to blink the eyes
   */
  blink?: number;
  /**
   * Size of the output image. Default value: `512`
   */
  dsize?: number;
  /**
   * Amount to shape mouth in 'eee' position
   */
  eee?: number;
  /**
   * Whether to enable the safety checker. If enabled, the model will check if the input image contains a face before processing it.
   * The safety checker will process the input image
   */
  enable_safety_checker?: boolean;
  /**
   * Amount to raise or lower eyebrows
   */
  eyebrow?: number;
  /**
   * Whether to crop the source portrait to the face-cropping space. Default value: `true`
   */
  flag_do_crop?: boolean;
  /**
   * Whether to conduct the rotation when flag_do_crop is True. Default value: `true`
   */
  flag_do_rot?: boolean;
  /**
   * Whether to set the lip to closed state before animation. Only takes effect when flag_eye_retargeting and flag_lip_retargeting are False. Default value: `true`
   */
  flag_lip_zero?: boolean;
  /**
   * Whether to paste-back/stitch the animated face cropping from the face-cropping space to the original image space. Default value: `true`
   */
  flag_pasteback?: boolean;
  /**
   * URL of the image to be animated
   */
  image_url: string | Blob | File;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Amount to move pupils horizontally
   */
  pupil_x?: number;
  /**
   * Amount to move pupils vertically
   */
  pupil_y?: number;
  /**
   * A unique identifier for the request. Default value: `"null"`
   */
  request_id?: string;
  /**
   * Amount to rotate the face in pitch
   */
  rotate_pitch?: number;
  /**
   * Amount to rotate the face in roll
   */
  rotate_roll?: number;
  /**
   * Amount to rotate the face in yaw
   */
  rotate_yaw?: number;
  /**
   * Scaling factor for the face crop. Default value: `2.3`
   */
  scale?: number;
  /**
   * Amount to smile
   */
  smile?: number;
  /**
   * Horizontal offset ratio for face crop.
   */
  vx_ratio?: number;
  /**
   * Vertical offset ratio for face crop. Positive values move up, negative values move down. Default value: `-0.125`
   */
  vy_ratio?: number;
  /**
   * Amount to wink
   */
  wink?: number;
  /**
   * Amount to shape mouth in 'woo' position
   */
  woo?: number;
};
export type RealtimeLivePortraitImageOutput = {
  /**
   * The generated image file.
   */
  image: RealtimeImage;
  /**
   *  Default value: `"null"`
   */
  request_id?: string;
};
export type RealtimeLivePortraitInput = {
  /**
   * Amount to open mouth in 'aaa' shape
   */
  aaa?: number;
  /**
   * Amount to blink the eyes
   */
  blink?: number;
  /**
   * Size of the output image. Default value: `512`
   */
  dsize?: number;
  /**
   * Amount to shape mouth in 'eee' position
   */
  eee?: number;
  /**
   * Amount to raise or lower eyebrows
   */
  eyebrow?: number;
  /**
   * Whether to crop the source portrait to the face-cropping space. Default value: `true`
   */
  flag_do_crop?: boolean;
  /**
   * Whether to conduct the rotation when flag_do_crop is True. Default value: `true`
   */
  flag_do_rot?: boolean;
  /**
   * Whether to enable eye retargeting.
   */
  flag_eye_retargeting?: boolean;
  /**
   * Whether to enable lip retargeting.
   */
  flag_lip_retargeting?: boolean;
  /**
   * Whether to set the lip to closed state before animation. Only takes effect when flag_eye_retargeting and flag_lip_retargeting are False. Default value: `true`
   */
  flag_lip_zero?: boolean;
  /**
   * Whether to paste-back/stitch the animated face cropping from the face-cropping space to the original image space. Default value: `true`
   */
  flag_pasteback?: boolean;
  /**
   * Whether to use relative motion. Default value: `true`
   */
  flag_relative?: boolean;
  /**
   * Whether to enable stitching. Recommended to set to True. Default value: `true`
   */
  flag_stitching?: boolean;
  /**
   *
   */
  image_bytes: string;
  /**
   *
   */
  initial_image_url: string | Blob | File;
  /**
   * Amount to move pupils horizontally
   */
  pupil_x?: number;
  /**
   * Amount to move pupils vertically
   */
  pupil_y?: number;
  /**
   *  Default value: `"null"`
   */
  r_d_0?: string;
  /**
   *  Default value: `"null"`
   */
  request_id?: string;
  /**
   *
   */
  return_relative_info?: boolean;
  /**
   * Amount to rotate the face in pitch
   */
  rotate_pitch?: number;
  /**
   * Amount to rotate the face in roll
   */
  rotate_roll?: number;
  /**
   * Amount to rotate the face in yaw
   */
  rotate_yaw?: number;
  /**
   * Scaling factor for the face crop. Default value: `2.3`
   */
  scale?: number;
  /**
   * Amount to smile
   */
  smile?: number;
  /**
   * Horizontal offset ratio for face crop.
   */
  vx_ratio?: number;
  /**
   * Vertical offset ratio for face crop. Positive values move up, negative values move down. Default value: `-0.125`
   */
  vy_ratio?: number;
  /**
   * Amount to wink
   */
  wink?: number;
  /**
   * Amount to shape mouth in 'woo' position
   */
  woo?: number;
  /**
   *  Default value: `"null"`
   */
  x_d_0_info?: string;
};
export type RealtimeLivePortraitOutput = {
  /**
   *
   */
  images: Array<RealtimeImage>;
  /**
   *  Default value: `"null"`
   */
  r_d_0?: string;
  /**
   *  Default value: `"null"`
   */
  request_id?: string;
  /**
   *  Default value: `"null"`
   */
  x_d_0_info?: string;
};
export type RealtimeOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image files info.
   */
  images: Array<RawImage>;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type Recraft20bInput = {
  /**
   * An array of preferable colors
   */
  colors?: Array<RGBColor>;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   *  Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   *
   */
  prompt: string;
  /**
   * The style of the generated images. Vector images cost 2X as much. Default value: `"realistic_image"`
   */
  style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/enterprise"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/motion_blur"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/3d"
    | "digital_illustration/80s"
    | "digital_illustration/engraving_color"
    | "digital_illustration/glow"
    | "digital_illustration/grain"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/kawaii"
    | "digital_illustration/pixel_art"
    | "digital_illustration/psychedelic"
    | "digital_illustration/seamless"
    | "digital_illustration/voxel"
    | "digital_illustration/watercolor"
    | "vector_illustration/cartoon"
    | "vector_illustration/doodle_line_art"
    | "vector_illustration/engraving"
    | "vector_illustration/flat_2"
    | "vector_illustration/kawaii"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut"
    | "vector_illustration/seamless";
  /**
   * The ID of the custom style reference (optional)
   */
  style_id?: string;
};
export type RecraftV3ImageToImageInput = {
  /**
   * An array of preferable colors
   */
  colors?: Array<RGBColor>;
  /**
   * The URL of the image to modify. Must be less than 5 MB in size, have resolution less than 16 MP and max dimension less than 4096 pixels.
   */
  image_url: string | Blob | File;
  /**
   * A text description of undesired elements on an image
   */
  negative_prompt?: string;
  /**
   * A text description of areas to change.
   */
  prompt: string;
  /**
   * Defines the difference with the original image, should lie in [0, 1], where 0 means almost identical, and 1 means miserable similarity Default value: `0.5`
   */
  strength?: number;
  /**
   * The style of the generated images. Vector images cost 2X as much. Default value: `"realistic_image"`
   */
  style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "realistic_image/enterprise"
    | "realistic_image/motion_blur"
    | "realistic_image/evening_light"
    | "realistic_image/faded_nostalgia"
    | "realistic_image/forest_life"
    | "realistic_image/mystic_naturalism"
    | "realistic_image/natural_tones"
    | "realistic_image/organic_calm"
    | "realistic_image/real_life_glow"
    | "realistic_image/retro_realism"
    | "realistic_image/retro_snapshot"
    | "realistic_image/urban_drama"
    | "realistic_image/village_realism"
    | "realistic_image/warm_folk"
    | "digital_illustration/pixel_art"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/grain"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/engraving_color"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/antiquarian"
    | "digital_illustration/bold_fantasy"
    | "digital_illustration/child_book"
    | "digital_illustration/child_books"
    | "digital_illustration/cover"
    | "digital_illustration/crosshatch"
    | "digital_illustration/digital_engraving"
    | "digital_illustration/expressionism"
    | "digital_illustration/freehand_details"
    | "digital_illustration/grain_20"
    | "digital_illustration/graphic_intensity"
    | "digital_illustration/hard_comics"
    | "digital_illustration/long_shadow"
    | "digital_illustration/modern_folk"
    | "digital_illustration/multicolor"
    | "digital_illustration/neon_calm"
    | "digital_illustration/noir"
    | "digital_illustration/nostalgic_pastel"
    | "digital_illustration/outline_details"
    | "digital_illustration/pastel_gradient"
    | "digital_illustration/pastel_sketch"
    | "digital_illustration/pop_art"
    | "digital_illustration/pop_renaissance"
    | "digital_illustration/street_art"
    | "digital_illustration/tablet_sketch"
    | "digital_illustration/urban_glow"
    | "digital_illustration/urban_sketching"
    | "digital_illustration/vanilla_dreams"
    | "digital_illustration/young_adult_book"
    | "digital_illustration/young_adult_book_2"
    | "vector_illustration/bold_stroke"
    | "vector_illustration/chemistry"
    | "vector_illustration/colored_stencil"
    | "vector_illustration/contour_pop_art"
    | "vector_illustration/cosmics"
    | "vector_illustration/cutout"
    | "vector_illustration/depressive"
    | "vector_illustration/editorial"
    | "vector_illustration/emotional_flat"
    | "vector_illustration/infographical"
    | "vector_illustration/marker_outline"
    | "vector_illustration/mosaic"
    | "vector_illustration/naivector"
    | "vector_illustration/roundish_flat"
    | "vector_illustration/segmented_colors"
    | "vector_illustration/sharp_contrast"
    | "vector_illustration/thin"
    | "vector_illustration/vector_photo"
    | "vector_illustration/vivid_shapes"
    | "vector_illustration/engraving"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut";
  /**
   * The ID of the custom style reference (optional)
   */
  style_id?: string;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type RecraftV4TextToImageInput = {
  /**
   * The preferable background color of the generated images.
   */
  background_color?: RGBColor;
  /**
   * An array of preferable colors
   */
  colors?: Array<RGBColor>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   *  Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   *
   */
  prompt: string;
};
export type ReferenceToVideoFlashInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:3" | "3:4";
  /**
   * Duration of the generated video in seconds. R2V Flash supports only 5 or 10 seconds. Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * Whether to generate a video with audio. Set to false for silent video generation. Silent videos are faster and cost 25% of the audio version price. Default value: `true`
   */
  enable_audio?: boolean;
  /**
   * Whether to enable prompt rewriting using LLM. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Reference images for subject consistency (0-5 images). Combined with video_urls, total references cannot exceed 5. Formats: JPEG, JPG, PNG (no alpha), BMP, WEBP. Resolution: 240-5000px. Max 10MB each. Reference order: image_urls continue numbering after video_urls.
   */
  image_urls?: Array<string>;
  /**
   * When true (default), enables intelligent multi-shot segmentation for coherent narrative videos with multiple shots. When false, generates single continuous shot. Only active when enable_prompt_expansion is True. Default value: `true`
   */
  multi_shots?: boolean;
  /**
   * Negative prompt to describe content to avoid. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Use Character1, Character2, etc. to reference subjects from your reference files. Works for people, animals, or objects. For multi-shot prompts: '[0-3s] Shot 1. [3-6s] Shot 2.' Max 1500 characters. Reference order: video_urls first, then image_urls.
   */
  prompt: string;
  /**
   * Video resolution tier. R2V Flash only supports 720p and 1080p. Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Reference videos for subject consistency (0-3 videos). Videos' FPS must be at least 16 FPS. Combined with image_urls, total references cannot exceed 5. Reference order: video_urls are numbered first (Character1, Character2...), then image_urls continue the sequence.
   */
  video_urls?: Array<string>;
};
export type ReferenceToVideoInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:3" | "3:4";
  /**
   * Duration of the generated video in seconds. R2V supports only 5 or 10 seconds (no 15s). Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * Whether to enable prompt rewriting using LLM. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * When true (default), enables intelligent multi-shot segmentation for coherent narrative videos with multiple shots. When false, generates single continuous shot. Only active when enable_prompt_expansion is True. Default value: `true`
   */
  multi_shots?: boolean;
  /**
   * Negative prompt to describe content to avoid. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Use @Video1, @Video2, @Video3 to reference subjects from your videos. Works for people, animals, or objects. For multi-shot prompts: '[0-3s] Shot 1. [3-6s] Shot 2.' Max 800 characters.
   */
  prompt: string;
  /**
   * Video resolution tier. R2V only supports 720p and 1080p (no 480p). Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Reference videos for subject consistency (1-3 videos). Videos' FPS must be at least 16 FPS.Reference in prompt as @Video1, @Video2, @Video3. Works for people, animals, or objects.
   */
  video_urls: Array<string>;
};
export type RefocusInput = {
  /**
   * Blur strength (K value). Higher values produce stronger bokeh/defocus. Set to 0 to only perform all-in-focus (deblur) estimation without bokeh. Default value: `8`
   */
  blur_strength?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The [x, y] coordinates of the focus point as fractions of image dimensions (0.0 to 1.0). For example, [0.5, 0.5] means center. If not provided, the center of the image is used.
   */
  focus_point?: Array<number>;
  /**
   * The URL of the image to refocus.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Lower values are faster but may reduce quality. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The same seed and the same input given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Resize the image so the longer side matches this value (in pixels). If not set, the original resolution is used (aligned to 16px). Recommended range: 512 to 2000. Default value: `512`
   */
  target_long_side?: number;
};
export type ReframeInput = {
  /**
   * The desired aspect ratio for the reframed image. Default value: `"16:9"`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the old or damaged photo to restore.
   */
  image_url: string | Blob | File;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Region = {
  /**
   * X-coordinate of the top-left corner
   */
  x1: number;
  /**
   * X-coordinate of the bottom-right corner
   */
  x2: number;
  /**
   * Y-coordinate of the top-left corner
   */
  y1: number;
  /**
   * Y-coordinate of the bottom-right corner
   */
  y2: number;
};
export type RegistryImageFastSdxlModelsImage = {
  /**
   *  Default value: `"image/jpeg"`
   */
  content_type?: string;
  /**
   *
   */
  height: number;
  /**
   *
   */
  url: string;
  /**
   *
   */
  width: number;
};
export type Reimagine32Input = {
  /**
   * Aspect ratio. Options: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9 Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * Canny edge control image (file or URL). Default value: `""`
   */
  canny_image_url?: string | Blob | File;
  /**
   * Canny image preprocess. Default value: `true`
   */
  canny_preprocess?: boolean;
  /**
   * Canny edge control strength (0.0 to 1.0). Default value: `0.5`
   */
  canny_scale?: number;
  /**
   * Depth control image (file or URL). Default value: `""`
   */
  depth_image_url?: string | Blob | File;
  /**
   * Depth image preprocess. Default value: `true`
   */
  depth_preprocess?: boolean;
  /**
   * Depth control strength (0.0 to 1.0). Default value: `0.5`
   */
  depth_scale?: number;
  /**
   * Guidance scale for text. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Negative prompt for image generation. Default value: `"Logo,Watermark,Ugly,Morbid,Extra fingers,Poorly drawn hands,Mutation,Blurry,Extra limbs,Gross proportions,Missing arms,Mutated hands,Long neck,Duplicate,Mutilated,Mutilated hands,Poorly drawn face,Deformed,Bad anatomy,Cloned face,Malformed limbs,Missing legs,Too many fingers"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Prompt for image generation.
   */
  prompt: string;
  /**
   * Whether to improve the prompt. Default value: `true`
   */
  prompt_enhancer?: boolean;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
  /**
   * Whether to truncate the prompt. Default value: `true`
   */
  truncate_prompt?: boolean;
};
export type ReimagineInput = {
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * The number of iterations the model goes through to refine the generated image. This parameter is optional. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * How many images you would like to generate. When using any Guidance Method, Value is set to 1. Default value: `1`
   */
  num_results?: number;
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The URL of the structure reference image. Use "" to leave empty. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  structure_image_url?: string | Blob | File;
  /**
   * The influence of the structure reference on the generated image. Default value: `0.75`
   */
  structure_ref_influence?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type RelightingInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   * Image URL for relighting
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"natural"`
   */
  lighting_style?:
    | "natural"
    | "studio"
    | "golden_hour"
    | "blue_hour"
    | "dramatic"
    | "soft"
    | "hard"
    | "backlight"
    | "side_light"
    | "front_light"
    | "rim_light"
    | "sunset"
    | "sunrise"
    | "neon"
    | "candlelight"
    | "moonlight"
    | "spotlight"
    | "ambient";
};
export type RelightInput = {
  /**
   * The source image.
   */
  image_url: string | Blob | File;
  /**
   * Where the light comes from.
   */
  light_direction: "front" | "side" | "bottom" | "top-down";
  /**
   * The quality/style/time of day.
   */
  light_type:
    | "midday"
    | "blue hour light"
    | "low-angle sunlight"
    | "sunrise light"
    | "spotlight on subject"
    | "overcast light"
    | "soft overcast daylight lighting"
    | "cloud-filtered lighting"
    | "fog-diffused lighting"
    | "moonlight lighting"
    | "starlight nighttime"
    | "soft bokeh lighting"
    | "harsh studio lighting";
};
export type RelightParameters = {
  /**
   * Direction of the light source (used for IC-light). Default value: `"Left"`
   */
  bg_source?: "Left" | "Right" | "Top" | "Bottom";
  /**
   * Classifier-free guidance scale for relighting. Default value: `2`
   */
  cfg?: number;
  /**
   * Text prompt describing the desired lighting condition.
   */
  relight_prompt: string;
  /**
   * Whether to use sky masking for outdoor scenes.
   */
  use_sky_mask?: boolean;
};
export type RemeshingInput = {
  /**
   * Number of faces for remesh Default value: `5000`
   */
  faces?: number;
  /**
   * Merge duplicate vertices before exporting Default value: `true`
   */
  merge?: boolean;
  /**
   * Path for the object file to be remeshed.
   */
  object_url: string | Blob | File;
  /**
   * Output format for the 3D model. Default value: `"glb"`
   */
  output_format?: "glb" | "fbx" | "obj" | "stl" | "usdc";
  /**
   * Preserve UVs during remeshing Default value: `true`
   */
  preserve_uvs?: boolean;
};
export type RemeshInput = {
  /**
   * URL or base64 data URI of a 3D model to remesh. Supports .glb, .gltf, .obj, .fbx, .stl formats. Can be a publicly accessible URL or data URI with MIME type application/octet-stream.
   */
  model_url: string | Blob | File;
  /**
   * Position of the origin. None means no effect.
   */
  origin_at?: "bottom" | "center";
  /**
   * Resize the model to a certain height measured in meters. Set to 0 for no resizing.
   */
  resize_height?: number;
  /**
   * List of target formats for the remeshed model.
   */
  target_formats?: Array<"glb" | "fbx" | "obj" | "usdz" | "blend" | "stl">;
  /**
   * Target number of polygons in the generated model. Actual count may vary based on geometry complexity. Default value: `30000`
   */
  target_polycount?: number;
  /**
   * Specify the topology of the generated model. Quad for smooth surfaces, Triangle for detailed geometry. Default value: `"triangle"`
   */
  topology?: "quad" | "triangle";
};
export type RemeshOutput = {
  /**
   * Remeshed 3D object in GLB format (if GLB was requested).
   */
  model_glb?: File;
  /**
   * URLs for different 3D model formats
   */
  model_urls: ModelUrls;
};
export type RemixImageInput = {
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type RemixInput = {
  /**
   * Whether to delete the video after generation for privacy reasons. If True, the video cannot be used for remixing and will be permanently deleted. Default value: `true`
   */
  delete_video?: boolean;
  /**
   * Updated text prompt that directs the remix generation
   */
  prompt: string;
  /**
   * The video_id from a previous Sora 2 generation. Note: You can only remix videos that were generated by Sora (via text-to-video or image-to-video endpoints), not arbitrary uploaded videos.
   */
  video_id: string;
};
export type RemixOutput = {
  /**
   * Spritesheet image for the video
   */
  spritesheet?: ImageFile;
  /**
   * Thumbnail image for the video
   */
  thumbnail?: ImageFile;
  /**
   * The generated video
   */
  video: VideoFile;
  /**
   * The ID of the generated video
   */
  video_id: string;
};
export type RemoveLightingInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale. Controls how closely the model follows the prompt. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If not provided, the size of the final input image will be used.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image with lighting/shadows to remove.
   */
  image_urls: Array<string>;
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `6`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * Random seed for reproducibility. Same seed with same prompt will produce same result.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and won't be saved in history.
   */
  sync_mode?: boolean;
};
export type ReplaceBackgroundInput = {
  /**
   * Reference image (file or URL). Default value: `"https://v3b.fal.media/files/b/0a8bea8c/Mztgx0NG3HPdby-4iPqwH_a_coffee_machine_standing_in_the_kitchen.png"`
   */
  image_url?: string | Blob | File;
  /**
   * Negative prompt for background replacement. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Prompt for background replacement.
   */
  prompt?: string;
  /**
   * Random seed for reproducibility. Default value: `4925634`
   */
  seed?: number;
  /**
   * Number of inference steps. Default value: `30`
   */
  steps_num?: number;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type ReplaceBackgroundOutput = {
  /**
   * Generated image.
   */
  image: Image;
  /**
   * Generated images.
   */
  images?: Array<unknown>;
};
export type ReseasonInput = {
  /**
   * The source image.
   */
  image_url: string | Blob | File;
  /**
   * The desired season.
   */
  season: "spring" | "summer" | "autumn" | "winter";
};
export type Resolution = {
  /**
   * Display aspect ratio (e.g., '16:9')
   */
  aspect_ratio: string;
  /**
   * Height of the video in pixels
   */
  height: number;
  /**
   * Width of the video in pixels
   */
  width: number;
};
export type RestyletInput = {
  /**
   * The source image.
   */
  image_url: string | Blob | File;
  /**
   * Select the desired artistic style for the output image.
   */
  style:
    | "3D Render"
    | "Cubism"
    | "Oil Painting"
    | "Anime"
    | "Cartoon"
    | "Coloring Book"
    | "Retro Ad"
    | "Pop Art Halftone"
    | "Vector Art"
    | "Story Board"
    | "Art Nouveau"
    | "Cross Etching"
    | "Wood Cut";
};
export type RetextureInput = {
  /**
   * Use the original UV mapping of the model instead of generating new UVs. If the model has no original UV, output quality may be reduced. Default value: `true`
   */
  enable_original_uv?: boolean;
  /**
   * Generate PBR Maps (metallic, roughness, normal) in addition to base color.
   */
  enable_pbr?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * 2D image to guide the texturing process. Supports .jpg, .jpeg, and .png formats. Required if text_style_prompt is not provided. If both are provided, image_style_url takes precedence.
   */
  image_style_url?: string | Blob | File;
  /**
   * URL or base64 data URI of a 3D model to texture. Supports .glb, .gltf, .obj, .fbx, .stl formats. Can be a publicly accessible URL or data URI with MIME type application/octet-stream.
   */
  model_url: string | Blob | File;
  /**
   * Describe your desired texture style using text. Maximum 600 characters. Required if image_style_url is not provided.
   */
  text_style_prompt?: string;
};
export type RetextureOutput = {
  /**
   * The image URL used for texturing (if provided)
   */
  image_style_url?: string | Blob | File;
  /**
   * Retextured 3D object in GLB format.
   */
  model_glb: File;
  /**
   * URLs for different 3D model formats
   */
  model_urls: ModelUrls;
  /**
   * The text prompt used for texturing (if provided)
   */
  text_style_prompt?: string;
  /**
   * Array of texture file objects
   */
  texture_urls?: Array<TextureFiles>;
  /**
   * Preview thumbnail of the retextured model
   */
  thumbnail?: File;
};
export type RewriteTextInput = {
  /**
   * The source image.
   */
  image_url: string | Blob | File;
  /**
   * The new text string to appear in the image.
   */
  new_text: string;
};
export type RFInversionInput = {
  /**
   * Base shift for the scheduled timesteps Default value: `0.5`
   */
  base_shift?: number;
  /**
   * The LoRAs to use for the image generation which use a control image. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  control_loras?: Array<ControlLoraWeight>;
  /**
   * The controller guidance (gamma) used in the creation of structured noise. Default value: `0.6`
   */
  controller_guidance_forward?: number;
  /**
   * The controller guidance (eta) used in the denoising process.Using values closer to 1 will result in an image closer to input. Default value: `0.75`
   */
  controller_guidance_reverse?: number;
  /**
   * The controlnet unions to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnet_unions?: Array<ControlNetUnion>;
  /**
   * The controlnets to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnets?: Array<ControlNet>;
  /**
   * EasyControl Inputs to use for image generation.
   */
  easycontrols?: Array<EasyControlWeight>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Use an image input to influence the generation. Can be used to fill images in masked areas.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of image to be edited
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * Max shift for the scheduled timesteps Default value: `1.15`
   */
  max_shift?: number;
  /**
   * The alpha value for NAG. This value is used as a final weighting
   * factor for steering the normalized guidance (positive and negative prompts)
   * in the direction of the positive prompt. Higher values will result in less
   * steering on the normalized guidance where lower values will result in
   * considering the positive prompt guidance more. Default value: `0.25`
   */
  nag_alpha?: number;
  /**
   * The proportion of steps to apply NAG. After the specified proportion
   * of steps has been iterated, the remaining steps will use original
   * attention processors in FLUX. Default value: `0.25`
   */
  nag_end?: number;
  /**
   * The scale for NAG. Higher values will result in a image that is more distant
   * to the negative prompt. Default value: `3`
   */
  nag_scale?: number;
  /**
   * The tau for NAG. Controls the normalization of the hidden state.
   * Higher values will result in a less aggressive normalization,
   * but may also lead to unexpected changes with respect to the original image.
   * Not recommended to change this value. Default value: `2.5`
   */
  nag_tau?: number;
  /**
   * Negative prompt to steer the image generation away from unwanted features.
   * By default, we will be using NAG for processing the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to edit the image with
   */
  prompt: string;
  /**
   * The percentage of the total timesteps when the reference guidance is to be ended. Default value: `1`
   */
  reference_end?: number;
  /**
   * URL of Image for Reference-Only
   */
  reference_image_url?: string | Blob | File;
  /**
   * The percentage of the total timesteps when the reference guidance is to bestarted.
   */
  reference_start?: number;
  /**
   * Strength of reference_only generation. Only used if a reference image is provided. Default value: `0.65`
   */
  reference_strength?: number;
  /**
   * Timestep to stop guidance during reverse process. Default value: `8`
   */
  reverse_guidance_end?: number;
  /**
   * Scheduler for applying reverse guidance. Default value: `"constant"`
   */
  reverse_guidance_schedule?:
    | "constant"
    | "linear_increase"
    | "linear_decrease";
  /**
   * Timestep to start guidance during reverse process.
   */
  reverse_guidance_start?: number;
  /**
   * Scheduler for the denoising process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmpp_2m";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Sigmas schedule for the denoising process.
   */
  sigma_schedule?: string;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Specifies whether beta sigmas ought to be used.
   */
  use_beta_schedule?: boolean;
  /**
   * Uses CFG-zero init sampling as in https://arxiv.org/abs/2503.18886.
   */
  use_cfg_zero?: boolean;
};
export type RGBColor = {
  /**
   * Blue color value
   */
  b?: number;
  /**
   * Green color value
   */
  g?: number;
  /**
   * Red color value
   */
  r?: number;
};
export type rifeInput = {
  /**
   * The URL of the second image to use as the ending point for interpolation.
   */
  end_image_url: string | Blob | File;
  /**
   * Frames per second for the output video. Only applicable if output_type is 'video'. Default value: `8`
   */
  fps?: number;
  /**
   * Whether to include the end image in the output.
   */
  include_end?: boolean;
  /**
   * Whether to include the start image in the output.
   */
  include_start?: boolean;
  /**
   * The number of frames to generate between the input images. Default value: `1`
   */
  num_frames?: number;
  /**
   * The format of the output images. Only applicable if output_type is 'images'. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg";
  /**
   * The type of output to generate; either individual images or a video. Default value: `"images"`
   */
  output_type?: "images" | "video";
  /**
   * The URL of the first image to use as the starting point for interpolation.
   */
  start_image_url: string | Blob | File;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type rifeOutput = {
  /**
   * The generated frames as individual images.
   */
  images?: Array<Image>;
  /**
   * The generated video file, if output_type is 'video'.
   */
  video?: File;
};
export type RifeVideoInput = {
  /**
   * Frames per second for the output video. Only applicable if use_calculated_fps is False. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the final frame will be looped back to the first frame to create a seamless loop. If False, the final frame will not loop back.
   */
  loop?: boolean;
  /**
   * The number of frames to generate between the input video frames. Default value: `1`
   */
  num_frames?: number;
  /**
   * If True, the function will use the calculated FPS of the input video multiplied by the number of frames to determine the output FPS. If False, the passed FPS will be used. Default value: `true`
   */
  use_calculated_fps?: boolean;
  /**
   * If True, the input video will be split into scenes before interpolation. This removes smear frames between scenes, but can result in false positives if the scene detection is not accurate. If False, the entire video will be treated as a single scene.
   */
  use_scene_detection?: boolean;
  /**
   * The URL of the video to use for interpolation.
   */
  video_url: string | Blob | File;
};
export type Rodin3DInput = {
  /**
   * Generation add-on features. Default is []. Possible values are HighPack. The HighPack option will provide 4K resolution textures instead of the default 1K, as well as models with high-poly. It will cost triple the billable units.
   */
  addons?: string;
  /**
   * An array that specifies the dimensions and scaling factor of the bounding box. Typically, this array contains 3 elements, Length(X-axis), Width(Y-axis) and Height(Z-axis).
   */
  bbox_condition?: Array<number>;
  /**
   * For fuse mode, One or more images are required.It will generate a model by extracting and fusing features of objects from multiple images.For concat mode, need to upload multiple multi-view images of the same object and generate the model. (You can upload multi-view images in any order, regardless of the order of view.) Default value: `"concat"`
   */
  condition_mode?: "fuse" | "concat";
  /**
   * Format of the geometry file. Possible values: glb, usdz, fbx, obj, stl. Default is glb. Default value: `"glb"`
   */
  geometry_file_format?: "glb" | "usdz" | "fbx" | "obj" | "stl";
  /**
   * URL of images to use while generating the 3D model. Required for Image-to-3D mode. Optional for Text-to-3D mode.
   */
  input_image_urls?: Array<string>;
  /**
   * Material type. Possible values: PBR, Shaded. Default is PBR. Default value: `"PBR"`
   */
  material?: "PBR" | "Shaded";
  /**
   * A textual prompt to guide model generation. Required for Text-to-3D mode. Optional for Image-to-3D mode. Default value: `""`
   */
  prompt?: string;
  /**
   * Generation quality. Possible values: high, medium, low, extra-low. Default is medium. Default value: `"medium"`
   */
  quality?: "high" | "medium" | "low" | "extra-low";
  /**
   * Seed value for randomization, ranging from 0 to 65535. Optional.
   */
  seed?: number;
  /**
   * When generating the human-like model, this parameter control the generation result to T/A Pose.
   */
  TAPose?: boolean;
  /**
   * Tier of generation. For Rodin Sketch, set to Sketch. For Rodin Regular, set to Regular. Default value: `"Regular"`
   */
  tier?: "Regular" | "Sketch";
  /**
   * Whether to export the model using hyper mode. Default is false.
   */
  use_hyper?: boolean;
};
export type RodinGen2Input = {
  /**
   * The HighPack option will provide 4K resolution textures instead of the default 1K, as well as models with high-poly. It will cost **triple the billable units**.
   */
  addons?: string;
  /**
   * An array that specifies the bounding box dimensions [width, height, length].
   */
  bbox_condition?: Array<number>;
  /**
   * Format of the geometry file. Possible values: glb, usdz, fbx, obj, stl. Default is glb. Default value: `"glb"`
   */
  geometry_file_format?: "glb" | "usdz" | "fbx" | "obj" | "stl";
  /**
   * URL of images to use while generating the 3D model. Required for Image-to-3D mode. Up to 5 images allowed.
   */
  input_image_urls?: Array<string>;
  /**
   * Material type. PBR: Physically-based materials with realistic lighting. Shaded: Simple materials with baked lighting. All: Both types included. Default value: `"All"`
   */
  material?: "PBR" | "Shaded" | "All";
  /**
   * Generate a preview render image of the 3D model along with the model files.
   */
  preview_render?: boolean;
  /**
   * A textual prompt to guide model generation. Optional for Image-to-3D mode - if empty, AI will generate a prompt based on your images. Default value: `""`
   */
  prompt?: string;
  /**
   * Combined quality and mesh type selection. Quad = smooth surfaces, Triangle = detailed geometry. These corresponds to `mesh_mode` (if the option contains 'Triangle', mesh_mode is 'Raw', otherwise 'Quad') and `quality_override` (the numeric part of the option) parameters in Hyper3D API. Default value: `"500K Triangle"`
   */
  quality_mesh_option?:
    | "4K Quad"
    | "8K Quad"
    | "18K Quad"
    | "50K Quad"
    | "2K Triangle"
    | "20K Triangle"
    | "150K Triangle"
    | "500K Triangle";
  /**
   * Seed value for randomization, ranging from 0 to 65535. Optional.
   */
  seed?: number;
  /**
   * Generate characters in T-pose or A-pose format, making them easier to rig and animate in 3D software.
   */
  TAPose?: boolean;
  /**
   * When enabled, preserves the transparency channel from input images during 3D generation.
   */
  use_original_alpha?: boolean;
};
export type RodinGen2TextTo3DInput = {
  /**
   * The HighPack option will provide 4K resolution textures instead of the default 1K, as well as models with high-poly. It will cost **triple the billable units**.
   */
  addons?: string;
  /**
   * An array that specifies the bounding box dimensions [width, height, length].
   */
  bbox_condition?: Array<number>;
  /**
   * Format of the geometry file. Possible values: glb, usdz, fbx, obj, stl. Default is glb. Default value: `"glb"`
   */
  geometry_file_format?: "glb" | "usdz" | "fbx" | "obj" | "stl";
  /**
   * Material type. PBR: Physically-based materials with realistic lighting. Shaded: Simple materials with baked lighting. All: Both types included. Default value: `"All"`
   */
  material?: "PBR" | "Shaded" | "All";
  /**
   * A textual prompt to guide model generation. Required for Text-to-3D mode.
   */
  prompt: string;
  /**
   * Combined quality and mesh type selection. Quad = smooth surfaces, Triangle = detailed geometry. Default value: `"18K Quad"`
   */
  quality_mesh_option?:
    | "4K Quad"
    | "8K Quad"
    | "18K Quad"
    | "50K Quad"
    | "2K Triangle"
    | "20K Triangle"
    | "150K Triangle"
    | "500K Triangle";
  /**
   * Seed value for randomization, ranging from 0 to 65535. Optional.
   */
  seed?: number;
  /**
   * Generate characters in T-pose or A-pose format, making them easier to rig and animate in 3D software.
   */
  TAPose?: boolean;
};
export type routerOutput = {
  /**
   * Error message if an error occurred
   */
  error?: string;
  /**
   * Generated output
   */
  output: string;
  /**
   * Whether the output is partial
   */
  partial?: boolean;
  /**
   * Generated reasoning for the final answer
   */
  reasoning?: string;
  /**
   * Token usage information
   */
  usage?: UsageInfo;
};
export type RouterVideoInput = {
  /**
   * This sets the upper limit for the number of tokens the model can generate in response. It won't produce more than this limit. The maximum value is the context length minus the prompt length.
   */
  max_tokens?: number;
  /**
   * Name of the model to use. Charged based on actual token usage.
   */
  model: string;
  /**
   * Prompt to be used for the video processing
   */
  prompt: string;
  /**
   * Should reasoning be the part of the final answer.
   */
  reasoning?: boolean;
  /**
   * System prompt to provide context or instructions to the model
   */
  system_prompt?: string;
  /**
   * This setting influences the variety in the model's responses. Lower values lead to more predictable and typical responses, while higher values encourage more diverse and less common responses. At 0, the model always gives the same response for a given input. Default value: `1`
   */
  temperature?: number;
  /**
   * List of URLs or data URIs of video files to process. Supported formats: mp4, mpeg, mov, webm. For Google Gemini on AI Studio, YouTube links are also supported. Mutually exclusive with video_url.
   */
  video_urls?: Array<string>;
};
export type RouterVisionInput = {
  /**
   * List of image URLs to be processed
   */
  image_urls: Array<string>;
  /**
   * This sets the upper limit for the number of tokens the model can generate in response. It won't produce more than this limit. The maximum value is the context length minus the prompt length.
   */
  max_tokens?: number;
  /**
   * Name of the model to use. Charged based on actual token usage.
   */
  model: string;
  /**
   * Prompt to be used for the image
   */
  prompt: string;
  /**
   * Should reasoning be the part of the final answer.
   */
  reasoning?: boolean;
  /**
   * System prompt to provide context or instructions to the model
   */
  system_prompt?: string;
  /**
   * This setting influences the variety in the model's responses. Lower values lead to more predictable and typical responses, while higher values encourage more diverse and less common responses. At 0, the model always gives the same response for a given input. Default value: `1`
   */
  temperature?: number;
};
export type RundiffusionPhotoFluxInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * LoRA Scale of the photo lora model Default value: `0.75`
   */
  photo_lora_scale?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type Sa2va4bVideoInput = {
  /**
   * Number of frames to sample from the video. If not provided, all frames are sampled.
   */
  num_frames_to_sample?: number;
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * The URL of the input video.
   */
  video_url: string | Blob | File;
};
export type sadtalkerInput = {
  /**
   * URL of the driven audio
   */
  driven_audio_url: string | Blob | File;
  /**
   * The scale of the expression Default value: `1`
   */
  expression_scale?: number;
  /**
   * The type of face enhancer to use
   */
  face_enhancer?: "gfpgan";
  /**
   * The resolution of the face model Default value: `"256"`
   */
  face_model_resolution?: "256" | "512";
  /**
   * The style of the pose
   */
  pose_style?: number;
  /**
   * The type of preprocessing to use Default value: `"crop"`
   */
  preprocess?: "crop" | "extcrop" | "resize" | "full" | "extfull";
  /**
   * URL of the source image
   */
  source_image_url: string | Blob | File;
  /**
   * Whether to use still mode. Fewer head motion, works with preprocess `full`.
   */
  still_mode?: boolean;
};
export type SadTalkerRefVideoInput = {
  /**
   * URL of the driven audio
   */
  driven_audio_url: string | Blob | File;
  /**
   * The scale of the expression Default value: `1`
   */
  expression_scale?: number;
  /**
   * The type of face enhancer to use
   */
  face_enhancer?: "gfpgan";
  /**
   * The resolution of the face model Default value: `"256"`
   */
  face_model_resolution?: "256" | "512";
  /**
   * The style of the pose
   */
  pose_style?: number;
  /**
   * The type of preprocessing to use Default value: `"crop"`
   */
  preprocess?: "crop" | "extcrop" | "resize" | "full" | "extfull";
  /**
   * URL of the reference video
   */
  reference_pose_video_url: string | Blob | File;
  /**
   * URL of the source image
   */
  source_image_url: string | Blob | File;
  /**
   * Whether to use still mode. Fewer head motion, works with preprocess `full`.
   */
  still_mode?: boolean;
};
export type Sam2AutoSegmentInput = {
  /**
   * URL of the image to be automatically segmented
   */
  image_url: string | Blob | File;
  /**
   * Minimum area of a mask region. Default value: `100`
   */
  min_mask_region_area?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Number of points to sample along each side of the image. Default value: `32`
   */
  points_per_side?: number;
  /**
   * Threshold for predicted IOU score. Default value: `0.88`
   */
  pred_iou_thresh?: number;
  /**
   * Threshold for stability score. Default value: `0.95`
   */
  stability_score_thresh?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type Sam2AutoSegmentOutput = {
  /**
   * Combined segmentation mask.
   */
  combined_mask: Image;
  /**
   * Individual segmentation masks.
   */
  individual_masks: Array<Image>;
};
export type SAM2EmbeddingOutput = {
  /**
   * Embedding of the image
   */
  embedding_b64: string;
};
export type Sam2ImageInput = {
  /**
   * Apply the mask on the image.
   */
  apply_mask?: boolean;
  /**
   * Coordinates for boxes
   */
  box_prompts?: Array<BoxPrompt>;
  /**
   * URL of the image to be segmented
   */
  image_url: string | Blob | File;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * List of prompts to segment the image
   */
  prompts?: Array<PointPrompt>;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SAM2RLEFileOutput = {
  /**
   * Zip file containing per-frame bounding box overlays.
   */
  boundingbox_frames_zip?: File;
  /**
   * Run Length Encoding of the mask.
   */
  rle: File;
};
export type SAM2RLEOutput = {
  /**
   * Zip file containing per-frame bounding box overlays.
   */
  boundingbox_frames_zip?: File;
  /**
   * Run Length Encoding of the mask.
   */
  rle: string | Array<string>;
};
export type Sam2VideoInput = {
  /**
   * Apply the mask on the video.
   */
  apply_mask?: boolean;
  /**
   * Return per-frame bounding box overlays as a zip archive.
   */
  boundingbox_zip?: boolean;
  /**
   * Coordinates for boxes
   */
  box_prompts?: Array<BoxPrompt>;
  /**
   * The URL of the mask to be applied initially.
   */
  mask_url?: string | Blob | File;
  /**
   * List of prompts to segment the video
   */
  prompts?: Array<PointPrompt>;
  /**
   * The URL of the video to be segmented.
   */
  video_url: string | Blob | File;
};
export type Sam2VideoOutput = {
  /**
   * Zip file containing per-frame bounding box overlays.
   */
  boundingbox_frames_zip?: File;
  /**
   * The segmented video.
   */
  video: File;
};
export type Sam31VideoInput = {
  /**
   * Apply the mask on the video. Default value: `true`
   */
  apply_mask?: boolean;
  /**
   * List of box prompt coordinates (x_min, y_min, x_max, y_max).
   */
  box_prompts?: Array<BoxPromptBase>;
  /**
   * Detection confidence threshold (0.0-1.0). Lower = more detections but less precise. Default value: `0.5`
   */
  detection_threshold?: number;
  /**
   * Maximum number of objects to track in the video. Default value: `16`
   */
  max_num_objects?: number;
  /**
   * List of point prompts
   */
  point_prompts?: Array<PointPromptBase>;
  /**
   * Text prompt for segmentation. Use commas to track multiple objects (e.g., 'person, cloth'). Default value: `""`
   */
  prompt?: string;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?: "X264 (.mp4)" | "VP9 (.webm)";
  /**
   * The URL of the video to be segmented.
   */
  video_url: string | Blob | File;
};
export type Sam31VideoRleInput = {
  /**
   * Apply the mask on the video.
   */
  apply_mask?: boolean;
  /**
   * Return per-frame bounding box overlays as a zip archive.
   */
  boundingbox_zip?: boolean;
  /**
   * List of box prompts with optional frame_index.
   */
  box_prompts?: Array<BoxPrompt>;
  /**
   * Detection confidence threshold (0.0-1.0). Lower = more detections but less precise. Defaults: 0.5 for existing, 0.7 for new objects. Try 0.2-0.3 if text prompts fail. Default value: `0.5`
   */
  detection_threshold?: number;
  /**
   * Frame index used for initial interaction when mask_url is provided.
   */
  frame_index?: number;
  /**
   * The URL of the mask to be applied initially.
   */
  mask_url?: string | Blob | File;
  /**
   * Maximum number of objects to track in the video. Default value: `16`
   */
  max_num_objects?: number;
  /**
   * List of point prompts with frame indices.
   */
  point_prompts?: Array<PointPrompt>;
  /**
   * Text prompt for segmentation. Use commas to track multiple objects (e.g., 'person, cloth'). Default value: `""`
   */
  prompt?: string;
  /**
   * The URL of the video to be segmented.
   */
  video_url: string | Blob | File;
};
export type Sam33dAlignInput = {
  /**
   * URL of the human mask image. If not provided, uses full image.
   */
  body_mask_url?: string | Blob | File;
  /**
   * URL of the SAM-3D Body mesh file (.ply or .glb) to align
   */
  body_mesh_url: string | Blob | File;
  /**
   * Focal length from SAM-3D Body metadata. If not provided, estimated from MoGe.
   */
  focal_length?: number;
  /**
   * URL of the original image used for MoGe depth estimation
   */
  image_url: string | Blob | File;
  /**
   * Optional URL of SAM-3D Object mesh (.glb) to create combined scene
   */
  object_mesh_url?: string | Blob | File;
};
export type Sam33dAlignOutput = {
  /**
   * Aligned body mesh in PLY format
   */
  body_mesh_ply: File;
  /**
   * Alignment info (scale, translation, etc.)
   */
  metadata: SAM3DBodyAlignmentInfo;
  /**
   * Aligned body mesh in GLB format (for 3D preview)
   */
  model_glb: File;
  /**
   * Combined scene with body + object meshes in GLB format (only when object_mesh_url provided)
   */
  scene_glb?: File;
  /**
   * Visualization of aligned mesh overlaid on input image
   */
  visualization: File;
};
export type SAM3DBodyAlignmentInfo = {
  /**
   * Number of cropped vertices
   */
  cropped_vertices_count: number;
  /**
   * Focal length used
   */
  focal_length: number;
  /**
   * Index of the person
   */
  person_id: number;
  /**
   * Scale factor applied for alignment
   */
  scale_factor: number;
  /**
   * Number of target points for alignment
   */
  target_points_count: number;
  /**
   * Translation [tx, ty, tz]
   */
  translation: Array<number>;
};
export type SAM3DBodyInput = {
  /**
   * Export individual mesh files (.ply) per person Default value: `true`
   */
  export_meshes?: boolean;
  /**
   * URL of the image containing humans
   */
  image_url: string | Blob | File;
  /**
   * Include 3D keypoint markers (spheres) in the GLB mesh for visualization Default value: `true`
   */
  include_3d_keypoints?: boolean;
  /**
   * Optional URL of a binary mask image (white=person, black=background). When provided, skips auto human detection and uses this mask instead. Bbox is auto-computed from the mask.
   */
  mask_url?: string | Blob | File;
};
export type SAM3DBodyMetadata = {
  /**
   * Number of people detected
   */
  num_people: number;
  /**
   * Per-person metadata
   */
  people: Array<SAM3DBodyPersonMetadata>;
};
export type SAM3DBodyOutput = {
  /**
   * Individual mesh files (.ply), one per detected person (when export_meshes=True)
   */
  meshes?: Array<File>;
  /**
   * Structured metadata including keypoints and camera parameters
   */
  metadata: SAM3DBodyMetadata;
  /**
   * 3D body mesh in GLB format with optional 3D keypoint markers
   */
  model_glb: File;
  /**
   * Combined visualization image (original + keypoints + mesh + side view)
   */
  visualization: File;
};
export type SAM3DBodyPersonMetadata = {
  /**
   * Bounding box [x_min, y_min, x_max, y_max]
   */
  bbox: Array<number>;
  /**
   * Estimated focal length
   */
  focal_length: number;
  /**
   * 2D keypoints [[x, y], ...] - 70 body keypoints
   */
  keypoints_2d: Array<Array<number>>;
  /**
   * 3D keypoints [[x, y, z], ...] - 70 body keypoints in camera space
   */
  keypoints_3d?: Array<Array<number>>;
  /**
   * Index of the person in the scene
   */
  person_id: number;
  /**
   * Predicted camera translation [tx, ty, tz]
   */
  pred_cam_t: Array<number>;
};
export type SAM3DObjectInput = {
  /**
   * Box prompts for auto-segmentation when no masks provided. Multiple boxes supported - each produces a separate object mask for 3D reconstruction.
   */
  box_prompts?: Array<BoxPromptBase>;
  /**
   * Detection confidence threshold (0.1-1.0). Lower = more detections but less precise. If not set, uses the model's default.
   */
  detection_threshold?: number;
  /**
   * If True, exports GLB with baked texture and UVs instead of vertex colors.
   */
  export_textured_glb?: boolean;
  /**
   * URL of the image to reconstruct in 3D
   */
  image_url: string | Blob | File;
  /**
   * Optional list of mask URLs (one per object). If not provided, use prompt/point_prompts/box_prompts to auto-segment, or entire image will be used.
   */
  mask_urls?: Array<string>;
  /**
   * Point prompts for auto-segmentation when no masks provided
   */
  point_prompts?: Array<PointPromptBase>;
  /**
   * Optional URL to external pointmap/depth data (NPY or NPZ format) for improved 3D reconstruction depth estimation
   */
  pointmap_url?: string | Blob | File;
  /**
   * Text prompt for auto-segmentation when no masks provided (e.g., 'chair', 'lamp') Default value: `"car"`
   */
  prompt?: string;
  /**
   * Random seed for reproducibility
   */
  seed?: number;
};
export type SAM3DObjectMetadata = {
  /**
   * Camera pose matrix
   */
  camera_pose?: Array<Array<number>>;
  /**
   * Index of the object in the scene
   */
  object_index: number;
  /**
   * Rotation quaternion [x, y, z, w]
   */
  rotation?: Array<Array<number>>;
  /**
   * Scale factors [sx, sy, sz]
   */
  scale?: Array<Array<number>>;
  /**
   * Translation [tx, ty, tz]
   */
  translation?: Array<Array<number>>;
};
export type SAM3DObjectOutput = {
  /**
   * Zip bundle containing all artifacts and metadata
   */
  artifacts_zip?: File;
  /**
   * Gaussian splat file (.ply) - combined scene splat for multi-object, single splat otherwise
   */
  gaussian_splat: File;
  /**
   * Individual GLB mesh files per object (only for multi-object scenes)
   */
  individual_glbs?: Array<File>;
  /**
   * Individual Gaussian splat files per object (only for multi-object scenes)
   */
  individual_splats?: Array<File>;
  /**
   * Per-object metadata (rotation/translation/scale)
   */
  metadata: Array<SAM3DObjectMetadata>;
  /**
   * 3D mesh in GLB format - combined scene for multi-object, single mesh otherwise
   */
  model_glb?: File;
};
export type Sam3ImageInput = {
  /**
   * Apply the mask on the image. Default value: `true`
   */
  apply_mask?: boolean;
  /**
   * Box prompt coordinates (x_min, y_min, x_max, y_max). Multiple boxes supported - use object_id to group boxes for the same object or leave empty for separate objects.
   */
  box_prompts?: Array<BoxPrompt>;
  /**
   * URL of the image to be segmented
   */
  image_url: string | Blob | File;
  /**
   * Whether to include bounding boxes for each mask (when available).
   */
  include_boxes?: boolean;
  /**
   * Whether to include mask confidence scores.
   */
  include_scores?: boolean;
  /**
   * Maximum number of masks to return when `return_multiple_masks` is enabled. Default value: `3`
   */
  max_masks?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * List of point prompts
   */
  point_prompts?: Array<PointPrompt>;
  /**
   * Text prompt for segmentation Default value: `"wheel"`
   */
  prompt?: string;
  /**
   * If True, upload and return multiple generated masks as defined by `max_masks`.
   */
  return_multiple_masks?: boolean;
  /**
   * If True, the media will be returned as a data URI.
   */
  sync_mode?: boolean;
};
export type Sam3ImageOutput = {
  /**
   * Per-mask normalized bounding boxes [cx, cy, w, h] when requested.
   */
  boxes?: Array<Array<number>>;
  /**
   * Primary segmented mask preview.
   */
  image?: Image;
  /**
   * Segmented mask images.
   */
  masks: Array<Image>;
  /**
   * Per-mask metadata including scores and boxes.
   */
  metadata?: Array<MaskMetadata>;
  /**
   * Per-mask confidence scores when requested.
   */
  scores?: Array<number>;
};
export type SAM3RLEFileOutput = {
  /**
   * Zip file containing per-frame bounding box overlays.
   */
  boundingbox_frames_zip?: File;
  /**
   * Per-mask normalized bounding boxes [cx, cy, w, h] when requested.
   */
  boxes?: Array<Array<number>>;
  /**
   * Per-mask metadata when multiple RLEs are returned.
   */
  metadata?: Array<MaskMetadata>;
  /**
   * Run Length Encoding of the mask.
   */
  rle: File;
  /**
   * Per-mask confidence scores when requested.
   */
  scores?: Array<number>;
};
export type SAM3RLEOutput = {
  /**
   * Zip file containing per-frame bounding box overlays.
   */
  boundingbox_frames_zip?: File;
  /**
   * Per-mask normalized bounding boxes [cx, cy, w, h] when requested.
   */
  boxes?: Array<Array<number>>;
  /**
   * Per-mask metadata when multiple RLEs are returned.
   */
  metadata?: Array<MaskMetadata>;
  /**
   * Run Length Encoding of the mask.
   */
  rle: string | Array<string>;
  /**
   * Per-mask confidence scores when requested.
   */
  scores?: Array<number>;
};
export type Sam3VideoInput = {
  /**
   * Apply the mask on the video. Default value: `true`
   */
  apply_mask?: boolean;
  /**
   * List of box prompt coordinates (x_min, y_min, x_max, y_max).
   */
  box_prompts?: Array<BoxPromptBase>;
  /**
   * Detection confidence threshold (0.0-1.0). Lower = more detections but less precise. Default value: `0.5`
   */
  detection_threshold?: number;
  /**
   * List of point prompts
   */
  point_prompts?: Array<PointPromptBase>;
  /**
   * Text prompt for segmentation. Use commas to track multiple objects (e.g., 'person, cloth'). Default value: `""`
   */
  prompt?: string;
  /**
   * The output type of the generated video. Default value: `"X264 (.mp4)"`
   */
  video_output_type?: "X264 (.mp4)" | "VP9 (.webm)";
  /**
   * The URL of the video to be segmented.
   */
  video_url: string | Blob | File;
};
export type Sam3VideoRleInput = {
  /**
   * Apply the mask on the video.
   */
  apply_mask?: boolean;
  /**
   * Return per-frame bounding box overlays as a zip archive.
   */
  boundingbox_zip?: boolean;
  /**
   * List of box prompts with optional frame_index.
   */
  box_prompts?: Array<BoxPrompt>;
  /**
   * Detection confidence threshold (0.0-1.0). Lower = more detections but less precise. Defaults: 0.5 for existing, 0.7 for new objects. Try 0.2-0.3 if text prompts fail. Default value: `0.5`
   */
  detection_threshold?: number;
  /**
   * Frame index used for initial interaction when mask_url is provided.
   */
  frame_index?: number;
  /**
   * The URL of the mask to be applied initially.
   */
  mask_url?: string | Blob | File;
  /**
   * List of point prompts with frame indices.
   */
  point_prompts?: Array<PointPrompt>;
  /**
   * Text prompt for segmentation. Use commas to track multiple objects (e.g., 'person, cloth'). Default value: `""`
   */
  prompt?: string;
  /**
   * The URL of the video to be segmented.
   */
  video_url: string | Blob | File;
};
export type SAMAudioInput = {
  /**
   * The acceleration level to use. Default value: `"balanced"`
   */
  acceleration?: "fast" | "balanced" | "quality";
  /**
   * URL of the audio file to process (WAV, MP3, FLAC supported)
   */
  audio_url: string | Blob | File;
  /**
   * Overlap duration (in seconds) between chunks for crossfade blending. Default value: `5`
   */
  chunk_overlap?: number;
  /**
   * Maximum audio duration (in seconds) to process in a single pass. Longer audio will be chunked with overlap and blended. Default value: `60`
   */
  max_chunk_duration?: number;
  /**
   * Output audio format. Default value: `"wav"`
   */
  output_format?: "wav" | "mp3";
  /**
   * Automatically predict temporal spans where the target sound occurs.
   */
  predict_spans?: boolean;
  /**
   * Text prompt describing the sound to isolate.
   */
  prompt: string;
  /**
   * Number of candidates to generate and rank. Higher improves quality but increases latency and cost. Default value: `1`
   */
  reranking_candidates?: number;
};
export type SAMAudioJudgeInput = {
  /**
   * URL of the original audio file.
   */
  original_audio_url: string | Blob | File;
  /**
   * Text prompt of the target sound that was separated.
   */
  prompt: string;
  /**
   * URL of the separated audio file to evaluate.
   */
  separated_audio_url: string | Blob | File;
};
export type SAMAudioJudgeOutput = {
  /**
   * Faithfulness score - overall separation quality (1-5 scale)
   */
  faithfulness: number;
  /**
   * Overall separation quality score (1-5 scale)
   */
  overall: number;
  /**
   * Precision score - how much of the output is the target sound (1-5 scale)
   */
  precision: number;
  /**
   * Recall score - how much of the target sound was recovered (1-5 scale)
   */
  recall: number;
};
export type SamAudioSeparateOutput = {
  /**
   * Duration of the output audio in seconds.
   */
  duration: number;
  /**
   * Everything else in the audio.
   */
  residual: File;
  /**
   * Sample rate of the output audio in Hz. Default value: `48000`
   */
  sample_rate?: number;
  /**
   * The isolated target sound.
   */
  target: File;
};
export type SAMAudioSpanInput = {
  /**
   * The acceleration level to use. Default value: `"balanced"`
   */
  acceleration?: "fast" | "balanced" | "quality";
  /**
   * URL of the audio file to process.
   */
  audio_url: string | Blob | File;
  /**
   * Overlap duration (in seconds) between chunks for crossfade blending. Default value: `5`
   */
  chunk_overlap?: number;
  /**
   * Maximum audio duration (in seconds) to process in a single pass. Longer audio will be chunked with overlap and blended. Default value: `60`
   */
  max_chunk_duration?: number;
  /**
   * Output audio format. Default value: `"wav"`
   */
  output_format?: "wav" | "mp3";
  /**
   * Text prompt describing the sound to isolate. Optional but recommended - helps the model identify what type of sound to extract from the span.
   */
  prompt?: string;
  /**
   * Number of candidates to generate and rank. Higher improves quality but increases latency and cost. Requires text prompt; ignored for span-only separation. Default value: `1`
   */
  reranking_candidates?: number;
  /**
   * Time spans where the target sound occurs which should be isolated.
   */
  spans: Array<AudioTimeSpan>;
  /**
   * Trim output audio to only include the specified span time range. If False, returns the full audio length with the target sound isolated throughout.
   */
  trim_to_span?: boolean;
  /**
   * Use sound activity detection to rank reranking candidates based on how well each candidate's non-silent regions match the provided spans. Enables effective reranking even without a text prompt (span-only separation). Requires reranking_candidates > 1.
   */
  use_sound_activity_ranking?: boolean;
};
export type SAMAudioVisualInput = {
  /**
   * The acceleration level to use. Default value: `"balanced"`
   */
  acceleration?: "fast" | "balanced" | "quality";
  /**
   * Overlap duration (in seconds) between chunks for crossfade blending. Default value: `5`
   */
  chunk_overlap?: number;
  /**
   * URL of the mask video (binary mask indicating target object). Black=target, White=background.
   */
  mask_video_url?: string | Blob | File;
  /**
   * Maximum audio duration (in seconds) to process in a single pass. Longer audio will be chunked with overlap and blended. Default value: `60`
   */
  max_chunk_duration?: number;
  /**
   * Output audio format. Default value: `"wav"`
   */
  output_format?: "wav" | "mp3";
  /**
   * Text prompt to assist with separation. Use natural language to describe the target sound. Default value: `""`
   */
  prompt?: string;
  /**
   * Number of candidates to generate and rank. Higher improves quality but increases latency and cost. Default value: `1`
   */
  reranking_candidates?: number;
  /**
   * URL of the video file to process (MP4, MOV, etc.)
   */
  video_url: string | Blob | File;
};
export type SamInput = {
  /**
   * Attempt better quality output using morphologyEx
   */
  better_quality?: boolean;
  /**
   * Output black and white, multiple masks will be combined into one mask
   */
  black_white?: boolean;
  /**
   * Coordinates for multiple boxes, e.g. [[x,y,w,h],[x2,y2,w2,h2]]
   */
  box_prompt?: Array<Array<unknown>>;
  /**
   * Object confidence threshold Default value: `0.4`
   */
  confidence?: number;
  /**
   * Url to input image
   */
  image_url: string | Blob | File;
  /**
   * Invert mask colors
   */
  invert?: boolean;
  /**
   * IOU threshold for filtering the annotations Default value: `0.9`
   */
  iou?: number;
  /**
   * Label for point, [1,0], 0 = background, 1 = foreground
   */
  point_label?: Array<number>;
  /**
   * Coordinates for multiple points [[x1,y1],[x2,y2]]
   */
  point_prompt?: Array<Array<unknown>>;
  /**
   * Draw high-resolution segmentation masks Default value: `true`
   */
  retina?: boolean;
  /**
   * Image size Default value: `1024`
   */
  size?: number;
  /**
   * The prompt to use when generating masks
   */
  text_prompt?: string;
  /**
   * Draw the edges of the masks
   */
  with_contours?: boolean;
};
export type SamOutput = {
  /**
   * Combined image of all detected masks
   */
  image?: Image;
};
export type SanaImageToVideoInput = {
  /**
   * The aspect ratio of the output video. Only used when resolution is '720p'. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "1:1";
  /**
   * Enable safety checking of the generated video. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second for the output video. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Guidance scale for generation (higher = more prompt adherence). Default value: `6`
   */
  guidance_scale?: number;
  /**
   * The input image URL to animate.
   */
  image_url: string | Blob | File;
  /**
   * Motion intensity score (higher = more motion). Default value: `30`
   */
  motion_score?: number;
  /**
   * The negative prompt describing what to avoid in the generation. Default value: `"A chaotic sequence with misshapen, deformed limbs in heavy motion blur, sudden disappearance, jump cuts, jerky movements, rapid shot changes, frames out of sync, inconsistent character shapes, temporal artifacts, jitter, and ghosting effects, creating a disorienting visual experience."`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of denoising steps. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The text prompt describing how the image should be animated.
   */
  prompt: string;
  /**
   * The resolution of the output video. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducible generation. If not provided, a random seed will be used.
   */
  seed?: number;
};
export type sanaInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `18`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The style to generate the image in. Default value: `"(No style)"`
   */
  style_name?:
    | "(No style)"
    | "Cinematic"
    | "Photographic"
    | "Anime"
    | "Manga"
    | "Digital Art"
    | "Pixel art"
    | "Fantasy art"
    | "Neonpunk"
    | "3D Model";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SanaVideoInput = {
  /**
   * The aspect ratio of the output video. Only used when resolution is '720p'. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "1:1";
  /**
   * Enable safety checking of the generated video. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second for the output video. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Guidance scale for generation (higher = more prompt adherence). Default value: `6`
   */
  guidance_scale?: number;
  /**
   * Motion intensity score (higher = more motion). Default value: `30`
   */
  motion_score?: number;
  /**
   * The negative prompt describing what to avoid in the generation. Default value: `"A chaotic sequence with misshapen, deformed limbs in heavy motion blur, sudden disappearance, jump cuts, jerky movements, rapid shot changes, frames out of sync, inconsistent character shapes, temporal artifacts, jitter, and ghosting effects, creating a disorienting visual experience."`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of denoising steps. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The text prompt describing the video to generate.
   */
  prompt: string;
  /**
   * The resolution of the output video. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducible generation. If not provided, a random seed will be used.
   */
  seed?: number;
};
export type SanaVideoOutput = {
  /**
   * The random seed used for the generation process.
   */
  seed: number;
  /**
   * Performance timing breakdown.
   */
  timings: unknown;
  /**
   * Generated video file.
   */
  video: File;
};
export type scailInput = {
  /**
   * The URL of the image to use as a reference for the video generation.
   */
  image_url: string | Blob | File;
  /**
   * Enable multi-character mode. Use when driving video has multiple people.
   */
  multi_character?: boolean;
  /**
   * The number of inference steps to use for the video generation. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The prompt to guide video generation.
   */
  prompt: string;
  /**
   * Output resolution. Outputs 896x512 (landscape) or 512x896 (portrait) based on the input image aspect ratio. Default value: `"512p"`
   */
  resolution?: string;
  /**
   * The URL of the video to use as a reference for the video generation.
   */
  video_url: string | Blob | File;
};
export type ScaleVideoInput = {
  /**
   * Video codec to use for encoding. libx264 (H.264) is widely compatible, libx265 (H.265/HEVC) offers better compression. Default value: `"libx264"`
   */
  codec?: "libx264" | "libx265";
  /**
   * Constant Rate Factor for quality (0-51). Lower values mean better quality and larger files. 18 is visually lossless for most content. Default value: `18`
   */
  crf?: number;
  /**
   * Target height in pixels. If only height is provided, width is auto-calculated to preserve aspect ratio. At least one of width or height must be provided.
   */
  height?: number;
  /**
   * Scaling mode. 'stretch' scales the video to the exact target dimensions (may distort aspect ratio). 'pad' scales to fit within the target dimensions while preserving aspect ratio, then pads with the chosen color to fill the remaining space (letterbox/pillarbox). 'crop' scales to cover the target dimensions while preserving aspect ratio, then center-crops to the exact target size. Default value: `"stretch"`
   */
  mode?: "stretch" | "pad" | "crop";
  /**
   * Padding color when mode is 'pad'. Ignored for other modes. Default value: `"black"`
   */
  pad_color?: "black" | "white" | "red" | "green" | "blue" | "gray";
  /**
   * Encoding speed preset. Slower presets give better compression but take longer. Default value: `"fast"`
   */
  preset?: "ultrafast" | "fast" | "medium" | "slow";
  /**
   * URL of the video file to scale/resize. Height and Width of the video must be even numbers for compatibility with video codecs.
   */
  video_url: string | Blob | File;
  /**
   * Target width in pixels. If only width is provided, height is auto-calculated to preserve aspect ratio. At least one of width or height must be provided.
   */
  width?: number;
};
export type ScaleVideoOutput = {
  /**
   * Height of the original video in pixels
   */
  original_height: number;
  /**
   * Width of the original video in pixels
   */
  original_width: number;
  /**
   * Height of the output video in pixels
   */
  scaled_height: number;
  /**
   * Width of the output video in pixels
   */
  scaled_width: number;
  /**
   * The scaled/resized video
   */
  video: File;
};
export type SceneChangeInfo = {
  /**
   * Frame number of the scene change
   */
  frame_number: number;
  /**
   * Presentation timestamp in stream time base units
   */
  pts: number;
  /**
   * Scene change score (0-1). Higher values indicate a bigger visual change between frames.
   */
  score: number;
  /**
   * Timestamp of the scene change in seconds
   */
  timestamp: number;
};
export type SceneDetectionInput = {
  /**
   * Whether to extract thumbnail images at scene change points. Default value: `true`
   */
  extract_thumbnails?: boolean;
  /**
   * Whether to always include the first frame as a scene change. Default value: `true`
   */
  include_first_frame?: boolean;
  /**
   * Maximum number of scene changes to return. Default value: `100`
   */
  max_scene_changes?: number;
  /**
   * Scene change threshold (0.0-1.0). Lower values detect more scene changes, higher values only detect major changes. Default value: `0.1`
   */
  threshold?: number;
  /**
   * URL of the video file to analyze for scene changes
   */
  video_url: string | Blob | File;
};
export type SceneDetectionOutput = {
  /**
   * Thumbnail images at scene change points (populated when extract_thumbnails=True)
   */
  images?: Array<Image>;
  /**
   * List of detected scene changes with timing and score information
   */
  scene_changes: Array<SceneChangeInfo>;
  /**
   * Total number of scene changes detected
   */
  scene_count: number;
};
export type SchnellReduxInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ScribbleInput = {
  /**
   * URL of the image to process
   */
  image_url: string | Blob | File;
  /**
   * The model to use for the Scribble detector Default value: `"HED"`
   */
  model?: "HED" | "PiDi";
  /**
   * Whether to use the safe version of the Scribble detector
   */
  safe?: boolean;
};
export type Sd15DepthControlnetInput = {
  /**
   * The URL of the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * The scale of the controlnet conditioning. Default value: `0.5`
   */
  controlnet_conditioning_scale?: number;
  /**
   * If set to true, DeepCache will be enabled. TBD
   */
  enable_deep_cache?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Leave it none to automatically infer from the control image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type SdxlControlnetUnionInput = {
  /**
   * The URL of the control image.
   */
  canny_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the canny image. Default value: `true`
   */
  canny_preprocess?: boolean;
  /**
   * The scale of the controlnet conditioning. Default value: `0.5`
   */
  controlnet_conditioning_scale?: number;
  /**
   * The URL of the control image.
   */
  depth_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the depth image. Default value: `true`
   */
  depth_preprocess?: boolean;
  /**
   * The list of embeddings to use.
   */
  embeddings?: Array<Embedding>;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Leave it none to automatically infer from the control image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The list of LoRA weights to use.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The URL of the control image.
   */
  normal_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the normal image. Default value: `true`
   */
  normal_preprocess?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `35`
   */
  num_inference_steps?: number;
  /**
   * The URL of the control image.
   */
  openpose_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the openpose image. Default value: `true`
   */
  openpose_preprocess?: boolean;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The URL of the control image.
   */
  segmentation_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the segmentation image. Default value: `true`
   */
  segmentation_preprocess?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The URL of the control image.
   */
  teed_image_url?: string | Blob | File;
  /**
   * Whether to preprocess the teed image. Default value: `true`
   */
  teed_preprocess?: boolean;
};
export type Seed2MiniInput = {
  /**
   * URLs of images for visual understanding. Supported formats: JPEG, PNG, WebP. A maximum of 6 images is supported. Any additional images will be ignored.
   */
  image_urls?: Array<string>;
  /**
   * Controls the maximum length of the model's output, including both the model's response and its chain-of-thought content, measured in tokens. Default value: `4096`
   */
  max_completion_tokens?: number;
  /**
   * Optional prior conversation history for multi-turn conversations. Pass back the `messages` field from a previous response to provide context. The current `prompt`, `image_urls`, `video_urls`, and `system_prompt` are always appended as the latest user turn.
   */
  messages?: Array<Seed2MiniMessage>;
  /**
   * The text prompt or question for the model.
   */
  prompt: string;
  /**
   * Controls the depth of reasoning before the model responds. Only applicable when `thinking` is `enabled` or `auto`. `minimal` for immediate response, `low` for faster response with light reasoning, `medium` for balanced speed and depth, `high` for deep analysis of complex issues.
   */
  reasoning_effort?: "minimal" | "low" | "medium" | "high";
  /**
   * Optional system prompt to guide the model's behavior.
   */
  system_prompt?: string;
  /**
   * Controls randomness in the response. Lower values make output more focused and deterministic, higher values make it more creative. Default value: `1`
   */
  temperature?: number;
  /**
   * Controls the model's chain-of-thought reasoning. `enabled` always includes reasoning, `disabled` never includes reasoning, `auto` lets the model decide based on the query. Default value: `"enabled"`
   */
  thinking?: "enabled" | "disabled" | "auto";
  /**
   * Nucleus sampling parameter. The model considers tokens with top_p cumulative probability mass. Lower values narrow the token selection. Default value: `0.7`
   */
  top_p?: number;
  /**
   * URLs of videos for video understanding. Supported formats: MP4, MOV. Audio comprehension is not supported. A maximum of 3 videos is supported. Any additional videos will be ignored.
   */
  video_urls?: Array<string>;
};
export type Seed2MiniMessage = {
  /**
   * The content of the message. Can be a string for text-only messages, or a list of content parts for multimodal messages (e.g. with images).
   */
  content: string | Array<unknown>;
  /**
   * The role of the message author.
   */
  role: "system" | "user" | "assistant";
};
export type Seed2MiniOutput = {
  /**
   * The full conversation history including the model's response. Pass this back as the `messages` input field to continue the conversation.
   */
  messages: Array<Seed2MiniMessage>;
  /**
   * The model's text response.
   */
  output: string;
  /**
   * The model's chain-of-thought reasoning content. Only present when `thinking` is `enabled` or `auto`.
   */
  reasoning_content?: string;
};
export type Seed3DImageTo3DOutput = {
  /**
   * The generated 3D model files
   */
  model: File;
  /**
   * The number of tokens used for the 3D model generation
   */
  usage_tokens: number;
};
export type Seedance2I2VInput = {
  /**
   * The aspect ratio of the generated video. Use 16:9 for landscape, 9:16 for portrait/vertical, 1:1 for square, 21:9 for ultrawide cinematic, or auto to infer from the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * Duration of the video in seconds. Supports 4 to 15 seconds, or auto to let the model decide based on the prompt. Default value: `"auto"`
   */
  duration?:
    | "auto"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * The URL of the image to use as the last frame of the video. When provided, the generated video will transition from the starting image to this ending image. Supported formats: JPEG, PNG, WebP. Max 30 MB.
   */
  end_image_url?: string | Blob | File;
  /**
   * Whether to generate synchronized audio for the video, including sound effects, ambient sounds, and lip-synced speech. When enabled, the cost of the request is doubled. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The URL of the starting frame image to animate. Supported formats: JPEG, PNG, WebP. Max 30 MB.
   */
  image_url: string | Blob | File;
  /**
   * The text prompt describing the desired motion and action for the video.
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for balance. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. Note that results may still vary slightly even with the same seed.
   */
  seed?: number;
};
export type Seedance2R2VInput = {
  /**
   * The aspect ratio of the generated video. Use 16:9 for landscape, 9:16 for portrait/vertical, 1:1 for square, 21:9 for ultrawide cinematic, or auto to let the model decide. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * Reference audio to guide video generation. Refer to them in the prompt as @Audio1, @Audio2, etc. Supported formats: MP3, WAV. Up to 3 files, combined duration must not exceed 15 seconds. Max 15 MB per file.
   */
  audio_urls?: Array<string>;
  /**
   * Duration of the video in seconds. Supports 4 to 15 seconds, or auto to let the model decide based on the prompt. Default value: `"auto"`
   */
  duration?:
    | "auto"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Whether to generate synchronized audio for the video, including sound effects, ambient sounds, and lip-synced speech. When enabled, the cost of the request is doubled. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * Reference images to guide video generation. Refer to them in the prompt as @Image1, @Image2, etc. Supported formats: JPEG, PNG, WebP. Max 30 MB per image. Up to 9 images. Total files across all modalities must not exceed 12.
   */
  image_urls?: Array<string>;
  /**
   * The text prompt used to generate the video. Use @Image1, @Video1, @Audio1 etc. in the prompt to refer to the corresponding reference media.
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for balance. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. Note that results may still vary slightly even with the same seed.
   */
  seed?: number;
  /**
   * Reference videos to guide video generation. Refer to them in the prompt as @Video1, @Video2, etc. Supported formats: MP4, MOV. Up to 3 videos, combined duration must be between 2 and 15 seconds, total size under 50 MB. Each video must be between ~480p (640x640) and ~720p (834x1112) in resolution.
   */
  video_urls?: Array<string>;
};
export type Seedance2T2VInput = {
  /**
   * The aspect ratio of the generated video. Use 16:9 for landscape, 9:16 for portrait/vertical, 1:1 for square, 21:9 for ultrawide cinematic, or auto to let the model decide. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * Duration of the video in seconds. Supports 4 to 15 seconds, or auto to let the model decide based on the prompt. Default value: `"auto"`
   */
  duration?:
    | "auto"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Whether to generate synchronized audio for the video, including sound effects, ambient sounds, and lip-synced speech. When enabled, the cost of the request is doubled. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for balance. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. Note that results may still vary slightly even with the same seed.
   */
  seed?: number;
};
export type SeedanceImageToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"auto"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "auto";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image the video ends with. Defaults to None.
   */
  end_image_url?: string | Blob | File;
  /**
   * The URL of the image used to generate video
   */
  image_url: string | Blob | File;
  /**
   * The number of frames to generate. If provided, will override duration.
   */
  num_frames?: number;
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type SeedanceProFastImageToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"auto"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "auto";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image used to generate video
   */
  image_url: string | Blob | File;
  /**
   * The number of frames to generate. If provided, will override duration.
   */
  num_frames?: number;
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for balance, 1080p for higher quality Default value: `"1080p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type SeedanceProv15ImageToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "auto";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image the video ends with. Defaults to None.
   */
  end_image_url?: string | Blob | File;
  /**
   * Whether to generate audio for the video Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The URL of the image used to generate video
   */
  image_url: string | Blob | File;
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for balance, 1080p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type SeedanceProv15TextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "auto";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to generate audio for the video Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for balance, 1080p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type SeedanceReferenceToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"auto"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "auto";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The number of frames to generate. If provided, will override duration.
   */
  num_frames?: number;
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Reference images to generate the video with.
   */
  reference_image_urls: Array<string>;
  /**
   * Video resolution - 480p for faster generation, 720p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type SeedanceTextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "9:21";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The number of frames to generate. If provided, will override duration.
   */
  num_frames?: number;
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type SeedDream45EditInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Width and height must be between 1920 and 4096, or total number of pixels must be between 2560*1440 and 4096*4096.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto_2K"
    | "auto_4K";
  /**
   * List of URLs of input images for editing. Presently, up to 10 image inputs are allowed. If over 10 images are sent, only the last 10 will be used.
   */
  image_urls: Array<string>;
  /**
   * If set to a number greater than one, enables multi-image generation. The model will potentially return up to `max_images` images every generation, and in total, `num_images` generations will be carried out. In total, the number of images generated will be between `num_images` and `max_images*num_images`. The total number of images (image inputs + image outputs) must not exceed 15 Default value: `1`
   */
  max_images?: number;
  /**
   * Number of separate model generations to be run with the prompt. Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt used to edit the image
   */
  prompt: string;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SeedDream45T2IInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Width and height must be between 1920 and 4096, or total number of pixels must be between 2560*1440 and 4096*4096.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto_2K"
    | "auto_4K";
  /**
   * If set to a number greater than one, enables multi-image generation. The model will potentially return up to `max_images` images every generation, and in total, `num_images` generations will be carried out. In total, the number of images generated will be between `num_images` and `max_images*num_images`. Default value: `1`
   */
  max_images?: number;
  /**
   * Number of separate model generations to be run with the prompt. Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SeedDream4EditInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The mode to use for enhancing prompt enhancement. Standard mode provides higher quality results but takes longer to generate. Fast mode provides average quality results but takes less time to generate. Default value: `"standard"`
   */
  enhance_prompt_mode?: "standard" | "fast";
  /**
   * The size of the generated image. The minimum total image area is 921600 pixels. Failing this, the image size will be adjusted to by scaling it up, while maintaining the aspect ratio.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto"
    | "auto_2K"
    | "auto_4K";
  /**
   * List of URLs of input images for editing. Presently, up to 10 image inputs are allowed. If over 10 images are sent, only the last 10 will be used.
   */
  image_urls: Array<string>;
  /**
   * If set to a number greater than one, enables multi-image generation. The model will potentially return up to `max_images` images every generation, and in total, `num_images` generations will be carried out. In total, the number of images generated will be between `num_images` and `max_images*num_images`. The total number of images (image inputs + image outputs) must not exceed 15 Default value: `1`
   */
  max_images?: number;
  /**
   * Number of separate model generations to be run with the prompt. Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt used to edit the image
   */
  prompt: string;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SeedDream4T2IInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The mode to use for enhancing prompt enhancement. Standard mode provides higher quality results but takes longer to generate. Fast mode provides average quality results but takes less time to generate. Default value: `"standard"`
   */
  enhance_prompt_mode?: "standard" | "fast";
  /**
   * The size of the generated image. Total pixels must be between 960x960 and 4096x4096.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto"
    | "auto_2K"
    | "auto_4K";
  /**
   * If set to a number greater than one, enables multi-image generation. The model will potentially return up to `max_images` images every generation, and in total, `num_images` generations will be carried out. In total, the number of images generated will be between `num_images` and `max_images*num_images`. Default value: `1`
   */
  max_images?: number;
  /**
   * Number of separate model generations to be run with the prompt. Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SeedDream50LiteEditInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Total pixels must be between 2560x1440 and 3072x3072. In case the image size does not fall within these parameters, the image size will be adjusted to by scaling. Default value: `auto_2K`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto_2K"
    | "auto_3K";
  /**
   * List of URLs of input images for editing. Presently, up to 10 image inputs are allowed. If over 10 images are sent, only the last 10 will be used.
   */
  image_urls: Array<string>;
  /**
   * If set to a number greater than one, enables multi-image generation. The model will potentially return up to `max_images` images every generation, and in total, `num_images` generations will be carried out. In total, the number of images generated will be between `num_images` and `max_images*num_images`. Default value: `1`
   */
  max_images?: number;
  /**
   * Number of separate model generations to be run with the prompt. Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt used to edit the image
   */
  prompt: string;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SeedDream50LiteT2IInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Total pixels must be between 2560x1440 and 3072x3072. In case the image size does not fall within these parameters, the image size will be adjusted to by scaling. Default value: `auto_2K`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto_2K"
    | "auto_3K";
  /**
   * If set to a number greater than one, enables multi-image generation. The model will potentially return up to `max_images` images every generation, and in total, `num_images` generations will be carried out. In total, the number of images generated will be between `num_images` and `max_images*num_images`. Default value: `1`
   */
  max_images?: number;
  /**
   * Number of separate model generations to be run with the prompt. Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SeedDreamInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Controls how closely the output image aligns with the input prompt. Higher values mean stronger prompt correlation. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * Use for finer control over the output image size. Will be used over aspect_ratio, if both are provided. Width and height must be between 512 and 2048.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SeedEditInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Controls how closely the output image aligns with the input prompt. Higher values mean stronger prompt correlation. Default value: `0.5`
   */
  guidance_scale?: number;
  /**
   * URL of the image to be edited.
   */
  image_url: string | Blob | File;
  /**
   * The text prompt used to edit the image
   */
  prompt: string;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type SeedVRImageInput = {
  /**
   * The input image to be processed
   */
  image_url: string | Blob | File;
  /**
   * The noise scale to use for the generation process. Default value: `0.1`
   */
  noise_scale?: number;
  /**
   * The format of the output image. Default value: `"jpg"`
   */
  output_format?: "png" | "jpg" | "webp";
  /**
   * The random seed used for the generation process.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The target resolution to upscale to when `upscale_mode` is `target`. Default value: `"1080p"`
   */
  target_resolution?: "720p" | "1080p" | "1440p" | "2160p";
  /**
   * Upscaling factor to be used. Will multiply the dimensions with this factor when `upscale_mode` is `factor`. Default value: `2`
   */
  upscale_factor?: number;
  /**
   * The mode to use for the upscale. If 'target', the upscale factor will be calculated based on the target resolution. If 'factor', the upscale factor will be used directly. Default value: `"factor"`
   */
  upscale_mode?: "target" | "factor";
};
export type SeedVRImageOutput = {
  /**
   * Upscaled image file after processing
   */
  image: ImageFile;
  /**
   * The random seed used for the generation process.
   */
  seed: number;
};
export type SeedVRSeamlessImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The input image to be processed
   */
  image_url: string | Blob | File;
  /**
   * The noise scale to use for the generation process. Default value: `0.1`
   */
  noise_scale?: number;
  /**
   * The format of the output image. Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * The random seed used for the generation process.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The target resolution to upscale to when `upscale_mode` is `target`. Default value: `"1080p"`
   */
  target_resolution?: "720p" | "1080p" | "1440p" | "2160p";
  /**
   * Upscaling factor to be used. Will multiply the dimensions with this factor when `upscale_mode` is `factor`. Default value: `2`
   */
  upscale_factor?: number;
  /**
   * The mode to use for the upscale. If 'target', the upscale factor will be calculated based on the target resolution. If 'factor', the upscale factor will be used directly. Default value: `"factor"`
   */
  upscale_mode?: "target" | "factor";
};
export type SeedVRVideoInput = {
  /**
   * The noise scale to use for the generation process. Default value: `0.1`
   */
  noise_scale?: number;
  /**
   * The format of the output video. Default value: `"X264 (.mp4)"`
   */
  output_format?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the output video. Default value: `"high"`
   */
  output_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Default value: `"balanced"`
   */
  output_write_mode?: "fast" | "balanced" | "small";
  /**
   * The random seed used for the generation process.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The target resolution to upscale to when `upscale_mode` is `target`. Default value: `"1080p"`
   */
  target_resolution?: "720p" | "1080p" | "1440p" | "2160p";
  /**
   * Upscaling factor to be used. Will multiply the dimensions with this factor when `upscale_mode` is `factor`. Default value: `2`
   */
  upscale_factor?: number;
  /**
   * The mode to use for the upscale. If 'target', the upscale factor will be calculated based on the target resolution. If 'factor', the upscale factor will be used directly. Default value: `"factor"`
   */
  upscale_mode?: "target" | "factor";
  /**
   * The input video to be processed
   */
  video_url: string | Blob | File;
};
export type SegmentSamplingSettings = {
  /**
   * Maximum number of tokens to generate.
   */
  max_tokens?: number;
  /**
   * Sampling temperature to use. Higher values will make the output more random, while lower values will make it more focused and deterministic. Default value: `1`
   */
  temperature?: number;
  /**
   * Nucleus sampling probability mass to use, between 0 and 1. Default value: `1`
   */
  top_p?: number;
};
export type SemanticImageInput = {
  /**
   * The hypothesis image to use for the measurement.
   */
  hypothesis: string;
  /**
   * The text reference to use for the measurement.
   */
  reference: string;
};
export type SetptsVideoInput = {
  /**
   * Speed multiplier (0.25-4.0). Values > 1.0 speed up, < 1.0 slow down. E.g., 2.0 = 2x faster, 0.5 = half speed Default value: `4`
   */
  speed_factor?: number;
  /**
   * URL of the video file to change speed
   */
  video_url: string | Blob | File;
};
export type SfxV15VideoToAudioInput = {
  /**
   * The duration of the generated audio in seconds Default value: `10`
   */
  duration?: number;
  /**
   * The number of samples to generate from the model Default value: `2`
   */
  num_samples?: number;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used Default value: `8069`
   */
  seed?: number;
  /**
   * The start offset in seconds to start the audio generation from
   */
  start_offset?: number;
  /**
   * Additional description to guide the model
   */
  text_prompt?: string;
  /**
   * A video url that can accessed from the API to process and add sound effects
   */
  video_url: string | Blob | File;
};
export type SfxV15VideoToAudioOutput = {
  /**
   * The generated sound effects audio
   */
  audio: Array<AudioOutput>;
};
export type SfxV15VideoToVideoOutput = {
  /**
   * The processed video with sound effects
   */
  video: Array<VideoOutput>;
};
export type SfxV1VideoToAudioInput = {
  /**
   * The duration of the generated audio in seconds Default value: `10`
   */
  duration?: number;
  /**
   * The number of samples to generate from the model Default value: `2`
   */
  num_samples?: number;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used Default value: `2105`
   */
  seed?: number;
  /**
   * Additional description to guide the model
   */
  text_prompt?: string;
  /**
   * A video url that can accessed from the API to process and add sound effects
   */
  video_url: string | Blob | File;
};
export type SfxV1VideoToAudioOutput = {
  /**
   * The generated sound effects audio
   */
  audio: Array<Audio>;
};
export type SfxV1VideoToVideoOutput = {
  /**
   * The processed video with sound effects
   */
  video: Array<Video>;
};
export type SharpenInput = {
  /**
   * CAS sharpening amount Default value: `0.8`
   */
  cas_amount?: number;
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Noise radius for smart sharpen Default value: `7`
   */
  noise_radius?: number;
  /**
   * Edge preservation factor Default value: `0.75`
   */
  preserve_edges?: number;
  /**
   * Sharpen strength (for basic mode) Default value: `1`
   */
  sharpen_alpha?: number;
  /**
   * Type of sharpening to apply Default value: `"basic"`
   */
  sharpen_mode?: "basic" | "smart" | "cas";
  /**
   * Sharpen radius (for basic mode) Default value: `1`
   */
  sharpen_radius?: number;
  /**
   * Smart sharpen blend ratio Default value: `0.5`
   */
  smart_sharpen_ratio?: number;
  /**
   * Smart sharpen strength Default value: `5`
   */
  smart_sharpen_strength?: number;
};
export type Shot = {
  /**
   * Number of frames for this shot. Each shot must have between 17 and 100 frames. Default value: `65`
   */
  num_frames?: number;
  /**
   * Caption describing what happens in this shot, including subject actions, camera angles, and scene details.
   */
  prompt: string;
};
export type SigmasInput = {
  /**
   * Sigmas schedule to be used if 'custom' method is selected.
   */
  array?: Array<number>;
  /**
   * The method to use for the sigmas. If set to 'custom', the sigmas will be set based
   * on the provided sigmas schedule in the `array` field.
   * Defaults to 'default' which means the scheduler will use the sigmas of the scheduler. Default value: `"default"`
   */
  method?: "default" | "array";
};
export type SileroVadInput = {
  /**
   * The URL of the audio to get speech timestamps from.
   */
  audio_url: string | Blob | File;
};
export type SileroVadOutput = {
  /**
   * Whether the audio has speech.
   */
  has_speech: boolean;
  /**
   * The speech timestamps.
   */
  timestamps: Array<SpeechTimestamp>;
};
export type SimaVideoUpscalerLiteInput = {
  /**
   * CRF quality (lower = better quality, 0-51) Default value: `18`
   */
  crf?: number;
  /**
   * URL of the video to upscale
   */
  video_url: string | Blob | File;
};
export type SimaVideoUpscalerLiteOutput = {
  /**
   * Video frame rate
   */
  frame_rate: string;
  /**
   * Original video dimensions (width, height)
   */
  original_size: Array<unknown>;
  /**
   * Upscaled video dimensions (width, height)
   */
  upscaled_size: Array<unknown>;
  /**
   * The upscaled video
   */
  video: VideoOutput;
};
export type SketchTo3DInput = {
  /**
   * Whether to enable PBR material generation.
   */
  enable_pbr?: boolean;
  /**
   * Target face count. Range: 40000-1500000 Default value: `500000`
   */
  face_count?: number;
  /**
   * URL of sketch or line art image to transform into a 3D model. Image resolution must be between 128x128 and 5000x5000 pixels.
   */
  input_image_url: string | Blob | File;
  /**
   * Text prompt describing the 3D content attributes such as color, category, and material.
   */
  prompt: string;
};
export type SketchTo3DOutput = {
  /**
   * Generated 3D object in GLB format.
   */
  model_glb: File;
  /**
   * URLs for different 3D model formats
   */
  model_urls: ModelUrls;
  /**
   * The seed used for generation
   */
  seed?: number;
  /**
   * Preview thumbnail of the generated model
   */
  thumbnail?: File;
};
export type SkyreelsI2vInput = {
  /**
   * Aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Guidance scale for generation (between 1.0 and 20.0) Default value: `6`
   */
  guidance_scale?: number;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to guide generation away from certain attributes.
   */
  negative_prompt?: string;
  /**
   * Number of denoising steps (between 1 and 50). Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * Random seed for generation. If not provided, a random seed will be used.
   */
  seed?: number;
};
export type SmartTopologyInput = {
  /**
   * Target polygon density. high: more detail/polygons, medium: balanced, low: fewer polygons. Default value: `"medium"`
   */
  face_level?: "high" | "medium" | "low";
  /**
   * Input 3D file format. Default value: `"glb"`
   */
  input_file_type?: "glb" | "obj";
  /**
   * URL of GLB or OBJ file to optimize topology. Max size: 200MB. Default value: `"https://v3b.fal.media/files/b/0a8c09c0/VYDiCTcDGK55qY2-idGbX_model.glb"`
   */
  input_file_url?: string | Blob | File;
  /**
   * Output polygon type. triangle: triangular faces only. quadrilateral: mixed quad and triangle faces. Default value: `"triangle"`
   */
  polygon_type?: "triangle" | "quadrilateral";
};
export type SmartTopologyOutput = {
  /**
   * Processed 3D model with optimized topology (primary file).
   */
  model_glb: File;
  /**
   * URLs for different 3D model formats
   */
  model_urls: ModelUrls;
};
export type SmartTurnOutput = {
  /**
   * The metrics of the inference.
   */
  metrics: unknown;
  /**
   * The predicted turn type. 1 for Complete, 0 for Incomplete.
   */
  prediction: number;
  /**
   * The probability of the predicted turn type.
   */
  probability: number;
};
export type SolarizeInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Solarize threshold Default value: `0.5`
   */
  solarize_threshold?: number;
};
export type Sora2ImageToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "9:16" | "16:9";
  /**
   * Up to two character IDs (from create-character) to use in the video. Refer to characters by name in the prompt. When set, only the OpenAI provider is used.
   */
  character_ids?: Array<string>;
  /**
   * Whether to delete the video after generation for privacy reasons. If True, the video cannot be used for remixing and will be permanently deleted. Default value: `true`
   */
  delete_video?: boolean;
  /**
   * If enabled, the prompt (and image for image-to-video) will be checked for known intellectual property references and the request will be blocked if any are detected.
   */
  detect_and_block_ip?: boolean;
  /**
   * Duration of the generated video in seconds Default value: `"4"`
   */
  duration?: "4" | "8" | "12" | "16" | "20";
  /**
   * The URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * The model to use for the generation. When the default model is selected, the latest snapshot of the model will be used - otherwise, select a specific snapshot of the model. Default value: `"sora-2"`
   */
  model?: "sora-2" | "sora-2-2025-12-08" | "sora-2-2025-10-06";
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"auto"`
   */
  resolution?: "auto" | "720p";
};
export type Sora2TextToVideoInput = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Up to two character IDs (from create-character) to use in the video. Refer to characters by name in the prompt. When set, only the OpenAI provider is used.
   */
  character_ids?: Array<string>;
  /**
   * Whether to delete the video after generation for privacy reasons. If True, the video cannot be used for remixing and will be permanently deleted. Default value: `true`
   */
  delete_video?: boolean;
  /**
   * If enabled, the prompt (and image for image-to-video) will be checked for known intellectual property references and the request will be blocked if any are detected.
   */
  detect_and_block_ip?: boolean;
  /**
   * Duration of the generated video in seconds Default value: `"4"`
   */
  duration?: "4" | "8" | "12" | "16" | "20";
  /**
   * The model to use for the generation. When the default model is selected, the latest snapshot of the model will be used - otherwise, select a specific snapshot of the model. Default value: `"sora-2"`
   */
  model?: "sora-2" | "sora-2-2025-12-08" | "sora-2-2025-10-06";
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: string;
};
export type SoteDiffusionInput = {
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Number of steps to run the first stage for. Default value: `25`
   */
  first_stage_steps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `8`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2`
   */
  second_stage_guidance_scale?: number;
  /**
   * Number of steps to run the second stage for. Default value: `10`
   */
  second_stage_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Cascade
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the image will be returned as base64 encoded string.
   */
  sync_mode?: boolean;
};
export type Speaker = {
  /**
   *
   */
  audio_url: string | Blob | File;
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  speaker_id: number;
};
export type SpeakerConfig = {
  /**
   * Alias used to identify this speaker in the prompt. Use this alias as a prefix in the prompt field, e.g. 'Alice: Hello! Bob: Hi there!'. Must be alphanumeric with no whitespace.
   */
  speaker_id: string;
  /**
   * Voice preset for this speaker.
   */
  voice:
    | "Achernar"
    | "Achird"
    | "Algenib"
    | "Algieba"
    | "Alnilam"
    | "Aoede"
    | "Autonoe"
    | "Callirrhoe"
    | "Charon"
    | "Despina"
    | "Enceladus"
    | "Erinome"
    | "Fenrir"
    | "Gacrux"
    | "Iapetus"
    | "Kore"
    | "Laomedeia"
    | "Leda"
    | "Orus"
    | "Pulcherrima"
    | "Puck"
    | "Rasalgethi"
    | "Sadachbia"
    | "Sadaltager"
    | "Schedar"
    | "Sulafat"
    | "Umbriel"
    | "Vindemiatrix"
    | "Zephyr"
    | "Zubenelgenubi";
};
export type SpeechOutput = {
  /**
   * The partial or final transcription output from Canary
   */
  output: string;
  /**
   * Indicates if this is a partial (in-progress) transcript
   */
  partial?: boolean;
};
export type SpeechToTextInput = {
  /**
   * Local filesystem path (or remote URL) to a long audio file
   */
  audio_url: string | Blob | File;
  /**
   * Whether to use Canary's built-in punctuation & capitalization Default value: `true`
   */
  use_pnc?: boolean;
};
export type SplitAudioInput = {
  /**
   * URL of the audio file to split
   */
  audio_url: string | Blob | File;
  /**
   * List of timestamps in seconds where to split the audio
   */
  split_points: Array<number>;
};
export type SplitAudioOutput = {
  /**
   * List of split audio segments
   */
  audio: Array<AudioFile>;
};
export type SplitTextInput = {
  /**
   * Maximum number of parts to return (max 6) Default value: `6`
   */
  max_parts?: number;
  /**
   * Separator to use for splitting the text Default value: `"|"`
   */
  separator?: string;
  /**
   * Text to split into parts
   */
  text: string;
};
export type SplitTextOutput = {
  /**
   * First text part
   */
  text1?: string;
  /**
   * Second text part
   */
  text2?: string;
  /**
   * Third text part
   */
  text3?: string;
  /**
   * Fourth text part
   */
  text4?: string;
  /**
   * Fifth text part
   */
  text5?: string;
  /**
   * Sixth text part
   */
  text6?: string;
};
export type StableAudio25InpaintInput = {
  /**
   * The audio clip to inpaint
   */
  audio_url: string | Blob | File;
  /**
   * How strictly the diffusion process adheres to the prompt text (higher values make your audio closer to your prompt). Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The end point of the audio mask Default value: `190`
   */
  mask_end?: number;
  /**
   * The start point of the audio mask Default value: `30`
   */
  mask_start?: number;
  /**
   * The number of steps to denoise the audio for Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The prompt to guide the audio generation
   */
  prompt: string;
  /**
   * The duration of the audio clip to generate. If not provided, it will be set to the duration of the input audio. Default value: `190`
   */
  seconds_total?: number;
  /**
   *
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StableAudioInput = {
  /**
   * The prompt to generate audio from
   */
  prompt: string;
  /**
   * The start point of the audio clip to generate
   */
  seconds_start?: number;
  /**
   * The duration of the audio clip to generate Default value: `30`
   */
  seconds_total?: number;
  /**
   * The number of steps to denoise the audio for Default value: `100`
   */
  steps?: number;
};
export type StableAudioOutput = {
  /**
   * The generated audio clip
   */
  audio_file: File;
};
export type StableAvatarInput = {
  /**
   * The aspect ratio of the video to generate. If 'auto', the aspect ratio will be determined by the reference image. Default value: `"auto"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16" | "auto";
  /**
   * The audio guidance scale to use for the video generation. Default value: `4`
   */
  audio_guidance_scale?: number;
  /**
   * The URL of the audio to use as a reference for the video generation.
   */
  audio_url: string | Blob | File;
  /**
   * The guidance scale to use for the video generation. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to use as a reference for the video generation.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to use for the video generation. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The amount of perturbation to use for the video generation. 0.0 means no perturbation, 1.0 means full perturbation. Default value: `0.1`
   */
  perturbation?: number;
  /**
   * The prompt to use for the video generation.
   */
  prompt: string;
  /**
   * The seed to use for the video generation.
   */
  seed?: number;
};
export type StableDiffusionV35LargeInput = {
  /**
   * ControlNet for inference.
   */
  controlnet?: ControlNet;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Defaults to landscape_4_3 if no controlnet has been passed, otherwise defaults to the size of the controlnet conditioning image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * IP-Adapter to use during inference.
   */
  ip_adapter?: IPAdapter;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StableDiffusionV3MediumImageToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Defaults to the conditioning image's size.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The image URL to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * If set to true, prompt will be upsampled with more details.
   */
  prompt_expansion?: boolean;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the image-to-image transformation. Default value: `0.9`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StableDiffusionV3MediumInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * If set to true, prompt will be upsampled with more details.
   */
  prompt_expansion?: boolean;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StableDiffusionV3MediumOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   * The number of images generated.
   */
  num_images: number;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type StableVideoInput = {
  /**
   * The conditoning augmentation determines the amount of noise that will be
   * added to the conditioning frame. The higher the number, the more noise
   * there will be, and the less the video will look like the initial image.
   * Increase it for more motion. Default value: `0.02`
   */
  cond_aug?: number;
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * The URL of the image to use as a starting point for the generation.
   */
  image_url: string | Blob | File;
  /**
   * The motion bucket id determines the motion of the generated video. The
   * higher the number, the more motion there will be. Default value: `127`
   */
  motion_bucket_id?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
};
export type StandardImageToVideoHailuo02Input = {
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   * Optional URL of the image to use as the last frame of the video
   */
  end_image_url?: string | Blob | File;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
  /**
   * The resolution of the generated video. Default value: `"768P"`
   */
  resolution?: "512P" | "768P";
};
export type StandardTextToVideoHailuo02Input = {
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type StarVectorInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * Maximum number of tokens for SVG generation. Increase for complex images. Default value: `4000`
   */
  max_length?: number;
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type StarVectorOutput = {
  /**
   * The generated image file info.
   */
  image: File;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
};
export type SteadyDancerInput = {
  /**
   * Acceleration levels. Default value: `"aggressive"`
   */
  acceleration?: "light" | "moderate" | "aggressive";
  /**
   * Aspect ratio of the generated video. If 'auto', will be determined from the reference image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. If not specified, uses the FPS from the input video.
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale for prompt adherence. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * URL of the reference image to animate. This is the person/character whose appearance will be preserved. Default value: `"https://v3b.fal.media/files/b/0a85edaa/GDUCMPrdvOMcI5JpEcU7f.png"`
   */
  image_url?: string | Blob | File;
  /**
   * Negative prompt for video generation. Default value: `"blurred, distorted face, bad anatomy, extra limbs, poorly drawn hands, poorly drawn feet, disfigured, out of frame, duplicate, watermark, signature, text"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. If not specified, uses the frame count from the input video (capped at 241). Will be adjusted to nearest valid value (must satisfy 4k+1 pattern).
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `6`
   */
  num_inference_steps?: number;
  /**
   * End ratio for pose guidance. Controls when pose guidance ends. Default value: `0.4`
   */
  pose_guidance_end?: number;
  /**
   * Pose guidance scale for pose control strength. Default value: `1`
   */
  pose_guidance_scale?: number;
  /**
   * Start ratio for pose guidance. Controls when pose guidance begins. Default value: `0.1`
   */
  pose_guidance_start?: number;
  /**
   * If enabled, copies audio from the input driving video to the output video. Default value: `true`
   */
  preserve_audio?: boolean;
  /**
   * Text prompt describing the desired animation. Default value: `"A person dancing with smooth and natural movements."`
   */
  prompt?: string;
  /**
   * Resolution of the generated video. 576p is default, 720p for higher quality. 480p is lower quality. Default value: `"576p"`
   */
  resolution?: "480p" | "576p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If true, applies quality enhancement for faster generation with improved quality. When enabled, parameters are automatically optimized (num_inference_steps=6, guidance_scale=1.0) and uses the LightX2V distillation LoRA.
   */
  use_turbo?: boolean;
  /**
   * URL of the driving pose video. The motion from this video will be transferred to the reference image. Default value: `"https://v3b.fal.media/files/b/0a84de68/jXDWywjhagRfR-GuZjoRs_video.mp4"`
   */
  video_url?: string | Blob | File;
};
export type SteadyDancerOutput = {
  /**
   * The actual number of frames generated (aligned to 4k+1 pattern).
   */
  num_frames: number;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated dance animation video.
   */
  video: File;
};
export type StepxEdit2Input = {
  /**
   * Enable reflection mode. Reviews outputs, corrects unintended changes, and determines when editing is complete. Default value: `true`
   */
  enable_reflection_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Enable thinking mode. Uses multimodal language model knowledge to interpret abstract editing instructions. Default value: `true`
   */
  enable_thinking_mode?: boolean;
  /**
   * The true CFG scale. Controls how closely the model follows the prompt. Default value: `6`
   */
  guidance_scale?: number;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Recommended: 50. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StepxEdit2Output = {
  /**
   * Reflection analysis (only available when reflection mode is enabled).
   */
  best_info?: Array<unknown>;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
  /**
   * The model's interpretation of your instruction (only available when thinking mode is enabled).
   */
  reformat_prompt?: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Reasoning process details (only available when thinking mode is enabled).
   */
  think_info?: Array<string>;
  /**
   *
   */
  timings: unknown;
};
export type StreamingDevTextToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StreamingFastTextToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `16`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StreamingFlux2EditImageInput = {
  /**
   * The acceleration level to use for the image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images for editing. A maximum of 4 images are allowed, if more are provided, only the first 4 will be used.
   */
  image_urls: Array<string>;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StreamingFlux2EditImageLoRAInput = {
  /**
   * The acceleration level to use for the image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URsL of the images for editing. A maximum of 3 images are allowed, if more are provided, only the first 3 will be used.
   */
  image_urls: Array<string>;
  /**
   * List of LoRA weights to apply (maximum 3). Each LoRA can be a URL, HuggingFace repo ID, or local path.
   */
  loras?: Array<LoRAInput>;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StreamingFlux2TextToImageLoRAInput = {
  /**
   * The acceleration level to use for the image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance Scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. The width and height must be between 512 and 2048 pixels. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of LoRA weights to apply (maximum 3). Each LoRA can be a URL, HuggingFace repo ID, or local path.
   */
  loras?: Array<LoRAInput>;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StreamingFullTextToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * A list of LoRAs to apply to the model. Each LoRA specifies its path, scale, and optional weight name.
   */
  loras?: Array<LoraWeight>;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StreamingInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your initial image when looking for a related image to show you. Default value: `2`
   */
  image_guidance_scale?: number;
  /**
   * URL of an input image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"low resolution, blur"`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The description of the target image after your edits have been made. Leave this blank to allow the model to use its own imagination.
   */
  target_image_description?: string;
};
export type StreamingKleinBaseInput = {
  /**
   * The acceleration level to use for image generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance scale for classifier-free guidance. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The size of the image to generate. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Negative prompt for classifier-free guidance. Describes what to avoid in the image. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI. Output is not stored when this is True.
   */
  sync_mode?: boolean;
};
export type StreamingKontextEditInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * Determines how the output resolution is set for image editing.
   * - `auto`: The model selects an optimal resolution from a predefined set that best matches the input image's aspect ratio. This is the recommended setting for most use cases as it's what the model was trained on.
   * - `match_input`: The model will attempt to use the same resolution as the input image. The resolution will be adjusted to be compatible with the model's requirements (e.g. dimensions must be multiples of 16 and within supported limits).
   * Apart from these, a few aspect ratios are also supported. Default value: `"match_input"`
   */
  resolution_mode?:
    | "auto"
    | "match_input"
    | "1:1"
    | "16:9"
    | "21:9"
    | "3:2"
    | "2:3"
    | "4:5"
    | "5:4"
    | "3:4"
    | "4:3"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
};
export type StreamingKontextImg2ImgInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image for image-to-image.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
};
export type StreamingKontextInpaintInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The URL of the image to be inpainted.
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The URL of the mask for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The URL of the reference image for inpainting.
   */
  reference_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StreamingKontextInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enhance the prompt for better results.
   */
  enhance_prompt?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
};
export type StreamingKreaInput = {
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type StructuredInstruction = {
  /**
   * The aesthetics of the image to be generated.
   */
  aesthetics?: Aesthetics;
  /**
   * The artistic style of the image to be generated.
   */
  artistic_style?: string;
  /**
   * The background setting of the image to be generated.
   */
  background_setting?: string;
  /**
   * The context of the image to be generated.
   */
  context?: string;
  /**
   * The edit instruction for the image.
   */
  edit_instruction?: string;
  /**
   * The lighting of the image to be generated.
   */
  lighting?: Lighting;
  /**
   * A list of objects in the image to be generated, along with their attributes and relationships to other objects in the image.
   */
  objects?: Array<PromptObject>;
  /**
   * The photographic characteristics of the image to be generated.
   */
  photographic_characteristics?: PhotographicCharacteristics;
  /**
   * A short description of the image to be generated.
   */
  short_description?: string;
  /**
   * The style medium of the image to be generated.
   */
  style_medium?: string;
  /**
   * A list of text to be rendered in the image.
   */
  text_render?: Array<void>;
};
export type StructuredPrompt = {
  /**
   * The aesthetics of the image to be generated.
   */
  aesthetics?: Aesthetics;
  /**
   * The artistic style of the image to be generated.
   */
  artistic_style?: string;
  /**
   * The background setting of the image to be generated.
   */
  background_setting?: string;
  /**
   * A list of colors that define the overall color palette of the image.
   */
  color_palette?: Array<Color>;
  /**
   * The context of the image to be generated.
   */
  context?: string;
  /**
   * The lighting of the image to be generated.
   */
  lighting?: Lighting;
  /**
   * A list of objects in the image to be generated, along with their attributes and relationships to other objects in the image.
   */
  objects?: Array<PromptObject>;
  /**
   * The photographic characteristics of the image to be generated.
   */
  photographic_characteristics?: PhotographicCharacteristics;
  /**
   * A short description of the image to be generated.
   */
  short_description?: string;
  /**
   * The style medium of the image to be generated.
   */
  style_medium?: string;
  /**
   * A list of text to be rendered in the image.
   */
  text_render?: Array<void>;
};
export type StructuredPromptInput = {
  /**
   * Input image URL
   */
  image_url?: string | Blob | File;
  /**
   * The prompt to generate.
   */
  prompt?: string;
  /**
   * Seed for the random number generator. Default value: `7`
   */
  seed?: number;
  /**
   * The structured prompt to generate.
   */
  structured_prompt?: StructuredPrompt;
};
export type STSInput = {
  /**
   * If True, the generated audio will be upscaled to 48kHz. The generation of the audio will take longer, but the quality will be higher. If False, the generated audio will be 24kHz.
   */
  high_quality_audio?: boolean;
  /**
   * URL to the source audio file to be voice-converted.
   */
  source_audio_url: string | Blob | File;
  /**
   * The voice to use for the speech-to-speech request. If neither target_voice nor target_voice_audio_url are provided, a random target voice will be used.
   */
  target_voice?:
    | "Aurora"
    | "Blade"
    | "Britney"
    | "Carl"
    | "Cliff"
    | "Richard"
    | "Rico"
    | "Siobhan"
    | "Vicky";
  /**
   * URL to the audio file which represents the voice of the output audio. If provided, this will override the target_voice setting. If neither target_voice nor target_voice_audio_url are provided, the default target voice will be used.
   */
  target_voice_audio_url?: string | Blob | File;
};
export type STSOutput = {
  /**
   * The generated voice-converted audio file.
   */
  audio: Audio;
};
export type StyleReferenceInput = {
  /**
   * The base style of the generated images, this topic is covered above. Default value: `"digital_illustration"`
   */
  base_style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "realistic_image/enterprise"
    | "realistic_image/motion_blur"
    | "realistic_image/evening_light"
    | "realistic_image/faded_nostalgia"
    | "realistic_image/forest_life"
    | "realistic_image/mystic_naturalism"
    | "realistic_image/natural_tones"
    | "realistic_image/organic_calm"
    | "realistic_image/real_life_glow"
    | "realistic_image/retro_realism"
    | "realistic_image/retro_snapshot"
    | "realistic_image/urban_drama"
    | "realistic_image/village_realism"
    | "realistic_image/warm_folk"
    | "digital_illustration/pixel_art"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/grain"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/engraving_color"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/antiquarian"
    | "digital_illustration/bold_fantasy"
    | "digital_illustration/child_book"
    | "digital_illustration/child_books"
    | "digital_illustration/cover"
    | "digital_illustration/crosshatch"
    | "digital_illustration/digital_engraving"
    | "digital_illustration/expressionism"
    | "digital_illustration/freehand_details"
    | "digital_illustration/grain_20"
    | "digital_illustration/graphic_intensity"
    | "digital_illustration/hard_comics"
    | "digital_illustration/long_shadow"
    | "digital_illustration/modern_folk"
    | "digital_illustration/multicolor"
    | "digital_illustration/neon_calm"
    | "digital_illustration/noir"
    | "digital_illustration/nostalgic_pastel"
    | "digital_illustration/outline_details"
    | "digital_illustration/pastel_gradient"
    | "digital_illustration/pastel_sketch"
    | "digital_illustration/pop_art"
    | "digital_illustration/pop_renaissance"
    | "digital_illustration/street_art"
    | "digital_illustration/tablet_sketch"
    | "digital_illustration/urban_glow"
    | "digital_illustration/urban_sketching"
    | "digital_illustration/vanilla_dreams"
    | "digital_illustration/young_adult_book"
    | "digital_illustration/young_adult_book_2"
    | "vector_illustration/bold_stroke"
    | "vector_illustration/chemistry"
    | "vector_illustration/colored_stencil"
    | "vector_illustration/contour_pop_art"
    | "vector_illustration/cosmics"
    | "vector_illustration/cutout"
    | "vector_illustration/depressive"
    | "vector_illustration/editorial"
    | "vector_illustration/emotional_flat"
    | "vector_illustration/infographical"
    | "vector_illustration/marker_outline"
    | "vector_illustration/mosaic"
    | "vector_illustration/naivector"
    | "vector_illustration/roundish_flat"
    | "vector_illustration/segmented_colors"
    | "vector_illustration/sharp_contrast"
    | "vector_illustration/thin"
    | "vector_illustration/vector_photo"
    | "vector_illustration/vivid_shapes"
    | "vector_illustration/engraving"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut";
  /**
   * URL to zip archive with images, use PNG format. Maximum 5 images are allowed.
   */
  images_data_url: string | Blob | File;
};
export type StyleReferenceOutput = {
  /**
   * The ID of the created style, this ID can be used to reference the style in the future.
   */
  style_id: string;
};
export type StylizeInput = {
  /**
   * URL of the image to make the stylized video from.
   */
  image_url: string | Blob | File;
  /**
   * The style for your character in the video. Please use a short description.
   */
  style: string;
};
export type SubjectCustomizeInput = {
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt describing what you want to see, using [1] to reference the subject
   */
  prompt: string;
  /**
   * 1-4 reference images of the subject to customize
   */
  reference_images: Array<ReferenceImage>;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
  /**
   * Optional description of the subject in the reference images
   */
  subject_description?: string;
  /**
   * Type of subject in the reference images
   */
  subject_type: "product" | "animal";
};
export type SubtitleSegment = {
  /**
   * End time in seconds (must be greater than start time)
   */
  end: number;
  /**
   * Start time in seconds (e.g., 0.0 for beginning, 5.5 for 5.5 seconds)
   */
  start: number;
  /**
   * Subtitle text to display (supports multiple lines with
   * )
   */
  text: string;
};
export type swin2srInput = {
  /**
   * URL of image to be used for image enhancement
   */
  image_url: string | Blob | File;
  /**
   * seed to be used for generation
   */
  seed?: number;
  /**
   * Task to perform Default value: `"classical_sr"`
   */
  task?: "classical_sr" | "compressed_sr" | "real_sr";
};
export type swittiInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `6`
   */
  guidance_scale?: number;
  /**
   * Temperature after disabling CFG Default value: `0.1`
   */
  last_scale_temp?: number;
  /**
   * More diverse sampling
   */
  more_diverse?: boolean;
  /**
   * Smoothing with Gumbel softmax sampling Default value: `true`
   */
  more_smooth?: boolean;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The number of top-k tokens to sample from. Default value: `400`
   */
  sampling_top_k?: number;
  /**
   * The top-p probability to sample from. Default value: `0.95`
   */
  sampling_top_p?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Smoothing starting scale Default value: `2`
   */
  smooth_start_si?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Disable CFG starting scale Default value: `8`
   */
  turn_off_cfg_start_si?: number;
};
export type Sync3Input = {
  /**
   * URL of the input audio
   */
  audio_url: string | Blob | File;
  /**
   * Lipsync mode when audio and video durations are out of sync. Default value: `"cut_off"`
   */
  sync_mode?: "cut_off" | "loop" | "bounce" | "silence" | "remap";
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
};
export type T2vTurboInput = {
  /**
   * The FPS of the exported video Default value: `8`
   */
  export_fps?: number;
  /**
   * The guidance scale Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * The number of frames to generate Default value: `16`
   */
  num_frames?: number;
  /**
   * The number of steps to sample Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate images from
   */
  prompt: string;
  /**
   * The seed to use for the random number generator
   */
  seed?: number;
};
export type Tada1bTextToSpeechInput = {
  /**
   * Classifier-free guidance scale for acoustic feature generation. Default value: `1.6`
   */
  acoustic_cfg_scale?: number;
  /**
   * URL of the reference audio file for voice cloning. The model will replicate this speaker's voice characteristics.
   */
  audio_url: string | Blob | File;
  /**
   * Language for text alignment. Use the appropriate code for non-English synthesis. Default value: `"en"`
   */
  language?:
    | "en"
    | "ar"
    | "ch"
    | "de"
    | "es"
    | "fr"
    | "it"
    | "ja"
    | "pl"
    | "pt";
  /**
   * Temperature for noise in the flow matching diffusion process. Default value: `0.9`
   */
  noise_temperature?: number;
  /**
   * Number of extra autoregressive steps for speech continuation beyond the input text. Useful for generating trailing prosody or silence.
   */
  num_extra_steps?: number;
  /**
   * Number of ODE solver steps for flow matching acoustic generation. More steps improve quality at the cost of speed. Default value: `20`
   */
  num_inference_steps?: number;
  /**
   * The format of the output audio file. Default value: `"wav"`
   */
  output_format?: "wav" | "mp3";
  /**
   * The text to synthesize into speech using the reference speaker's voice.
   */
  prompt: string;
  /**
   * Penalty applied to repeated tokens during generation. Default value: `1.1`
   */
  repetition_penalty?: number;
  /**
   * Factor to speed up or slow down the generated speech. Values > 1.0 speed up, < 1.0 slow down. Default value: `1`
   */
  speed_up_factor?: number;
  /**
   * Sampling temperature for text token generation. Higher values produce more varied output. Default value: `0.6`
   */
  temperature?: number;
  /**
   * Top-p (nucleus) sampling parameter for text generation. Default value: `0.9`
   */
  top_p?: number;
  /**
   * Transcript of the reference audio. For non-English audio, providing a transcript is required since the built-in ASR is English-only. Default value: `""`
   */
  transcript?: string;
};
export type TestClientSelectionInput = {
  /**
   * Whether to simulate a request that requires web search.
   */
  enable_web_search?: boolean;
  /**
   * Whether to simulate a retry (passes a fake exception list to get_client_for_request).
   */
  is_retry?: boolean;
  /**
   * Number of times to call the client selection algorithm. Default value: `100`
   */
  num_trials?: number;
  /**
   * Resolution to simulate (e.g. '1K', '2K', '4K'). Default value: `"1K"`
   */
  resolution?: "1K" | "2K" | "4K";
  /**
   * Safety tolerance to simulate (1-6). Values <= 3 exclude Poe/OpenRouter. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * Seed value to simulate (non-None excludes Poe/OpenRouter).
   */
  seed?: number;
  /**
   * Optional user ID to simulate. Affects routing for favored users.
   */
  user_id?: string;
};
export type TestClientSelectionOutput = {
  /**
   * Current balancer state summary.
   */
  balancer_state: string;
  /**
   * Map of client description to selection count.
   */
  counts: unknown;
  /**
   * Map of client description to selection percentage.
   */
  percentages: unknown;
  /**
   *
   */
  total_trials: number;
};
export type Text2VideoInput = {
  /**
   * The avatar to use for the video
   */
  avatar_id:
    | "emily_vertical_primary"
    | "emily_vertical_secondary"
    | "marcus_vertical_primary"
    | "marcus_vertical_secondary"
    | "mira_vertical_primary"
    | "mira_vertical_secondary"
    | "jasmine_vertical_primary"
    | "jasmine_vertical_secondary"
    | "jasmine_vertical_walking"
    | "aisha_vertical_walking"
    | "elena_vertical_primary"
    | "elena_vertical_secondary"
    | "any_male_vertical_primary"
    | "any_female_vertical_primary"
    | "any_male_vertical_secondary"
    | "any_female_vertical_secondary"
    | "any_female_vertical_walking"
    | "emily_primary"
    | "emily_side"
    | "marcus_primary"
    | "marcus_side"
    | "aisha_walking"
    | "elena_primary"
    | "elena_side"
    | "any_male_primary"
    | "any_female_primary"
    | "any_male_side"
    | "any_female_side";
  /**
   *
   */
  text: string;
};
export type TextInput = {
  /**
   * The conditoning augmentation determines the amount of noise that will be
   * added to the conditioning frame. The higher the number, the more noise
   * there will be, and the less the video will look like the initial image.
   * Increase it for more motion. Default value: `0.02`
   */
  cond_aug?: number;
  /**
   * The motion bucket id determines the motion of the generated video. The
   * higher the number, the more motion there will be. Default value: `127`
   */
  motion_bucket_id?: number;
  /**
   * The negative prompt to use as a starting point for the generation. Default value: `"unrealistic, saturated, high contrast, big nose, painting, drawing, sketch, cartoon, anime, manga, render, CG, 3d, watermark, signature, label"`
   */
  negative_prompt?: string;
  /**
   * The prompt to use as a starting point for the generation.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The size of the generated video. Default value: `landscape_16_9`
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type TextOutput = {
  /**
   * Results from the model
   */
  results: string;
};
export type TextReferenceMeasurementInput = {
  /**
   * The inputs to use for the measurement.
   */
  inputs: Array<TextReferenceInput>;
  /**
   * The measurements to use for the measurement.
   */
  measurements: Array<string>;
};
export type TextTo3dInput = {
  /**
   * Automatically scale the model to real-world dimensions, with the unit in meters. The default value is False.
   */
  auto_size?: boolean;
  /**
   * Limits the number of faces on the output model. If this option is not set, the face limit will be adaptively determined.
   */
  face_limit?: number;
  /**
   * This is the random seed used for the process based on the prompt. This parameter is an integer and is randomly chosen if not set.
   */
  image_seed?: number;
  /**
   * Unlike prompt, it provides a reverse direction to assist in generating content contrasting with the original prompt. The maximum length is 255 characters.
   */
  negative_prompt?: string;
  /**
   * A boolean option to enable pbr. The default value is True, set False to get a model without pbr. If this option is set to True, texture will be ignored and used as True.
   */
  pbr?: boolean;
  /**
   * Text input that directs the model generation. The maximum prompt length is 1024 characters, equivalent to approximately 100 words. The API supports multiple languages. However, emojis and certain special Unicode characters are not supported.
   */
  prompt: string;
  /**
   * Set True to enable quad mesh output (extra $0.05 per generation). If quad=True and face_limit is not set, the default face_limit will be 10000. Note: Enabling this option will force the output to be an FBX model.
   */
  quad?: boolean;
  /**
   * This is the random seed for model generation. The seed controls the geometry generation process, ensuring identical models when the same seed is used. This parameter is an integer and is randomly chosen if not set.
   */
  seed?: number;
  /**
   * An option to enable texturing. Default is 'standard', set 'no' to get a model without any textures, and set 'HD' to get a model with hd quality textures. Default value: `"standard"`
   */
  texture?: "no" | "standard" | "HD";
  /**
   * This is the random seed for texture generation. Using the same seed will produce identical textures. This parameter is an integer and is randomly chosen if not set. If you want a model with different textures, please use same seed and different texture_seed.
   */
  texture_seed?: number;
};
export type TextTo3DInput = {
  /**
   * Animation preset ID from Meshy's library (500+ presets). Only used when enable_animation is true. See https://docs.meshy.ai/en/api/animation-library for available action IDs. Default value: `1001`
   */
  animation_action_id?: number;
  /**
   * Desired art style of the object. Note: enable_pbr should be false for sculpture style. Default value: `"realistic"`
   */
  art_style?: "realistic" | "sculpture";
  /**
   * Apply an animation preset to the rigged model. Requires enable_rigging to be true.
   */
  enable_animation?: boolean;
  /**
   * Generate PBR Maps (metallic, roughness, normal) in addition to base color. Should be false for sculpture style.
   */
  enable_pbr?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Automatically rig the generated model as a humanoid character. Includes basic walking and running animations. Best results with humanoid characters that have clearly defined limbs.
   */
  enable_rigging?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Generation mode. 'preview' returns untextured geometry only, 'full' returns textured model (preview + refine). Default value: `"full"`
   */
  mode?: "preview" | "full";
  /**
   * Pose mode for the generated model. 'a-pose' generates an A-pose, 't-pose' generates a T-pose, empty string for no specific pose. Default value: `""`
   */
  pose_mode?: "a-pose" | "t-pose" | "";
  /**
   * Describe what kind of object the 3D model is. Maximum 600 characters.
   */
  prompt: string;
  /**
   * Approximate height of the character in meters. Only used when enable_rigging is true. Default value: `1.7`
   */
  rigging_height_meters?: number;
  /**
   * Seed for reproducible results. Same prompt and seed usually generate the same result.
   */
  seed?: number;
  /**
   * Whether to enable the remesh phase. When false, returns unprocessed triangular mesh. Default value: `true`
   */
  should_remesh?: boolean;
  /**
   * Controls symmetry behavior during model generation. Default value: `"auto"`
   */
  symmetry_mode?: "off" | "auto" | "on";
  /**
   * Target number of polygons in the generated model Default value: `30000`
   */
  target_polycount?: number;
  /**
   * 2D image to guide the texturing process (only used in 'full' mode)
   */
  texture_image_url?: string | Blob | File;
  /**
   * Additional text prompt to guide the texturing process (only used in 'full' mode)
   */
  texture_prompt?: string;
  /**
   * Specify the topology of the generated model. Quad for smooth surfaces, Triangle for detailed geometry. Default value: `"triangle"`
   */
  topology?: "quad" | "triangle";
};
export type TextTo3DOutput = {
  /**
   * The actual prompt used if prompt expansion was enabled
   */
  actual_prompt?: string;
  /**
   * Animated 3D model in FBX format. Only present when enable_animation is true.
   */
  animation_fbx?: File;
  /**
   * Animated 3D model in GLB format. Only present when enable_animation is true.
   */
  animation_glb?: File;
  /**
   * Basic walking and running animations. Only present when enable_rigging is true.
   */
  basic_animations?: BasicAnimations;
  /**
   * Generated 3D object in GLB format.
   */
  model_glb: File;
  /**
   * URLs for different 3D model formats
   */
  model_urls: ModelUrls;
  /**
   * The text prompt used for generation
   */
  prompt: string;
  /**
   * Rigging task ID. Only present when enable_rigging is true.
   */
  rig_task_id?: string;
  /**
   * Rigged character in FBX format. Only present when enable_rigging is true.
   */
  rigged_character_fbx?: File;
  /**
   * Rigged character in GLB format. Only present when enable_rigging is true.
   */
  rigged_character_glb?: File;
  /**
   * The seed used for generation
   */
  seed?: number;
  /**
   * Array of texture file objects
   */
  texture_urls?: Array<TextureFiles>;
  /**
   * Preview thumbnail of the generated model
   */
  thumbnail?: File;
};
export type TextToAudioInput = {
  /**
   * How strictly the diffusion process adheres to the prompt text (higher values make your audio closer to your prompt). Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The number of steps to denoise the audio for Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate audio from
   */
  prompt: string;
  /**
   * The duration of the audio clip to generate Default value: `190`
   */
  seconds_total?: number;
  /**
   *
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type TextToAudioOutput = {
  /**
   * The generated audio clip
   */
  audio: File;
  /**
   * The random seed used for generation
   */
  seed: number;
};
export type TextToImage32Input = {
  /**
   * Aspect ratio. Options: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9 Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * Guidance scale for text. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Negative prompt for image generation. Default value: `"Logo,Watermark,Ugly,Morbid,Extra fingers,Poorly drawn hands,Mutation,Blurry,Extra limbs,Gross proportions,Missing arms,Mutated hands,Long neck,Duplicate,Mutilated,Mutilated hands,Poorly drawn face,Deformed,Bad anatomy,Cloned face,Malformed limbs,Missing legs,Too many fingers"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Prompt for image generation.
   */
  prompt: string;
  /**
   * Whether to improve the prompt. Default value: `true`
   */
  prompt_enhancer?: boolean;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
  /**
   * Whether to truncate the prompt. Default value: `true`
   */
  truncate_prompt?: boolean;
};
export type TextToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type TextToImageLCMInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the prompt will be expanded with additional prompts.
   */
  expand_prompt?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  format?: "jpeg" | "png";
  /**
   * The rescale factor for the CFG.
   */
  guidance_rescale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `1.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The name of the model to use. Default value: `"stabilityai/stable-diffusion-xl-base-1.0"`
   */
  model_name?:
    | "stabilityai/stable-diffusion-xl-base-1.0"
    | "runwayml/stable-diffusion-v1-5";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `6`
   */
  num_inference_steps?: number;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results.
   */
  prompt: string;
  /**
   * An id bound to a request, can be used with response to identify the request
   * itself. Default value: `""`
   */
  request_id?: string;
  /**
   * The version of the safety checker to use. v1 is the default CompVis safety checker. v2 uses a custom ViT model. Default value: `"v1"`
   */
  safety_checker_version?: "v1" | "v2";
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history. Default value: `true`
   */
  sync_mode?: boolean;
};
export type TextToImageLoRAInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the image generation. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<LoRAInput>;
  /**
   * The negative prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The number of layers to generate. Default value: `4`
   */
  num_layers?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "png" | "webp";
  /**
   * A caption for the input image.
   */
  prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type TextToImageOutput = {
  /**
   * The actual prompt used if prompt rewriting was enabled
   */
  actual_prompt?: string;
  /**
   * The generated images
   */
  images: Array<ImageFile>;
  /**
   * The seeds used for each generated image
   */
  seeds: Array<number>;
};
export type TextToImageWanInput = {
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output image size. If not set: matches input image size (up to 1280*1280). Use presets like 'square_hd', 'landscape_16_9', or specify exact dimensions.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Optional reference image (0 or 1). When provided, can be used for style guidance. Resolution: 384-5000px each dimension. Max size: 10MB. Formats: JPEG, JPG, PNG (no alpha), BMP, WEBP.
   */
  image_url?: string | Blob | File;
  /**
   * Maximum number of images to generate (1-5). Actual count may be less depending on model inference. Default value: `1`
   */
  max_images?: number;
  /**
   * Content to avoid in the generated image. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Text prompt describing the desired image. Supports Chinese and English. Max 2000 characters.
   */
  prompt: string;
  /**
   * Random seed for reproducibility (0-2147483647).
   */
  seed?: number;
};
export type TextToImageWanOutput = {
  /**
   * Generated text content (in mixed text-and-image mode). May be None if only images were generated.
   */
  generated_text?: string;
  /**
   * Generated images in PNG format
   */
  images: Array<File>;
  /**
   * The seed used for generation
   */
  seed: number;
};
export type TextToSpeechOutput = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Duration of the audio in milliseconds
   */
  duration_ms: number;
};
export type TextToSVGInput = {
  /**
   * Enable input safety checking. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Fill color for the generated glyphs. Accepts any valid SVG/CSS color value. Default value: `"black"`
   */
  fill_color?: string;
  /**
   * Maximum tokens to generate. Increase for longer text. Default value: `8192`
   */
  max_tokens?: number;
  /**
   * Maximum dimension (width or height) of the output SVG in pixels. The aspect ratio is preserved. Default value: `512`
   */
  output_size?: number;
  /**
   * The target text to generate as vector glyphs. Each character is rendered as a separate SVG path element.
   */
  prompt: string;
  /**
   * Repetition penalty to reduce repeated SVG path segments. Default value: `1`
   */
  repetition_penalty?: number;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * Optional stroke (outline) color for the generated glyphs. When set, adds an outline around each glyph path.
   */
  stroke_color?: string;
  /**
   * Stroke width in SVG units. Only applies when stroke_color is set. Default value: `1`
   */
  stroke_width?: number;
  /**
   * Font style description using typography terms such as weight (100-900), style (italic, oblique), category (serif, sans-serif, display, handwriting, monospace), and characteristics (geometric, humanist, condensed, rounded). Default value: `"italic style, 400 weight, serif, text, elegant"`
   */
  style_description?: string;
  /**
   * Sampling temperature. Lower values produce more deterministic output. Default value: `0.1`
   */
  temperature?: number;
  /**
   * Top-k sampling parameter. Default value: `5`
   */
  top_k?: number;
  /**
   * Top-p (nucleus) sampling parameter. Default value: `0.95`
   */
  top_p?: number;
};
export type TextToVideoInput = {
  /**
   * The aspect ratio of the generated video. Wan 2.6 supports additional ratios. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:3" | "3:4";
  /**
   * URL of the audio to use as the background music. Must be publicly accessible.
   * Limit handling: If the audio duration exceeds the duration value (5, 10, or 15 seconds),
   * the audio is truncated to the first N seconds, and the rest is discarded. If
   * the audio is shorter than the video, the remaining part of the video will be silent.
   * For example, if the audio is 3 seconds long and the video duration is 5 seconds, the
   * first 3 seconds of the output video will have sound, and the last 2 seconds will be silent.
   * - Format: WAV, MP3.
   * - Duration: 3 to 30 s.
   * - File size: Up to 15 MB.
   */
  audio_url?: string | Blob | File;
  /**
   * Duration of the generated video in seconds. Choose between 5, 10, or 15 seconds. Default value: `"5"`
   */
  duration?: "5" | "10" | "15";
  /**
   * Whether to enable prompt rewriting using LLM. Improves results for short prompts but increases processing time. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * When true, enables intelligent multi-shot segmentation for coherent narrative videos. Only active when enable_prompt_expansion is True. Set to false for single-shot generation. Default value: `true`
   */
  multi_shots?: boolean;
  /**
   * Negative prompt to describe content to avoid. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The text prompt for video generation. Supports Chinese and English, max 800 characters. For multi-shot videos, use format: 'Overall description. First shot [0-3s] content. Second shot [3-5s] content.'
   */
  prompt: string;
  /**
   * Video resolution tier. Wan 2.6 T2V only supports 720p and 1080p (no 480p). Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type TextToVideoOutput = {
  /**
   * The actual prompt used if prompt rewriting was enabled
   */
  actual_prompt?: string;
  /**
   * The seed used for generation
   */
  seed: number;
  /**
   * The generated video file
   */
  video: VideoFile;
};
export type TextToVideov21Input = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:5" | "5:4" | "3:2" | "2:3";
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The seed for the random number generator
   */
  seed?: number;
};
export type TextToVoiceCreateOutput = {
  /**
   * Preview of the created voice.
   */
  audio?: File;
  /**
   * The ID of the voice.
   */
  voice_id: string;
};
export type TextToVoiceRemixOutput = {
  /**
   * A list of audio previews generated with the created voice.
   */
  previews: Array<VoicePreview>;
  /**
   * Random seed for reproducibility.
   */
  seed: number;
  /**
   * The text voiced by the created voice.
   */
  text: string;
};
export type TextureFiles = {
  /**
   * Base color texture
   */
  base_color: File;
  /**
   * Metallic texture (PBR)
   */
  metallic?: File;
  /**
   * Normal texture (PBR)
   */
  normal?: File;
  /**
   * Roughness texture (PBR)
   */
  roughness?: File;
};
export type TextureTransformInput = {
  /**
   * Aspect ratio for 4K output
   */
  aspect_ratio?: AspectRatio;
  /**
   * Image URL for texture transformation
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"marble"`
   */
  target_texture?:
    | "cotton"
    | "denim"
    | "wool"
    | "felt"
    | "wood"
    | "leather"
    | "velvet"
    | "stone"
    | "marble"
    | "ceramic"
    | "concrete"
    | "brick"
    | "clay"
    | "foam"
    | "glass"
    | "metal"
    | "silk"
    | "fabric"
    | "crystal"
    | "rubber"
    | "plastic"
    | "lace";
};
export type TextVoice = {
  /**
   * The text that the avatar will speak in the video
   */
  prompt?: string;
  /**
   * Speed of the speech (0.5 to 2.0) Default value: `1`
   */
  speed?: number;
  /**
   * Name of the voice to use for the avatar
   */
  voice?: string;
  /**
   * Direct voice ID. When provided, this is used instead of looking up the voice by name.
   */
  voice_id?: string;
};
export type theraInput = {
  /**
   * Backbone to use for upscaling
   */
  backbone: "edsr" | "rdn";
  /**
   * URL of image to be used for upscaling
   */
  image_url: string | Blob | File;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * The upscaling factor for the image. Default value: `2`
   */
  upscale_factor?: number;
};
export type ThinksoundAudioOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The prompt used to generate the audio.
   */
  prompt: string;
};
export type thinksoundInput = {
  /**
   * The classifier-free guidance scale for audio generation. Default value: `5`
   */
  cfg_scale?: number;
  /**
   * The number of inference steps for audio generation. Default value: `24`
   */
  num_inference_steps?: number;
  /**
   * A prompt to guide the audio generation. If not provided, it will be extracted from the video. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * The URL of the video to generate the audio for.
   */
  video_url: string | Blob | File;
};
export type TimeOfDayInput = {
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The time of day to transform the scene to. Default value: `"golden hour"`
   */
  prompt?: string;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type TimestepsInput = {
  /**
   * Timesteps schedule to be used if 'custom' method is selected.
   */
  array?: Array<number>;
  /**
   * The method to use for the timesteps. If set to 'array', the timesteps will be set based
   * on the provided timesteps schedule in the `array` field.
   * Defaults to 'default' which means the scheduler will use the `num_inference_steps` parameter. Default value: `"default"`
   */
  method?: "default" | "array";
};
export type TopazUpscaleImageInput = {
  /**
   * Enable automatic prompt generation for generative upscaling. Applies to Redefine model only.
   */
  autoprompt?: boolean;
  /**
   * Creativity level for generative upscaling (1-6). Higher values produce more creative/hallucinated details. Applies to Redefine model only.
   */
  creativity?: number;
  /**
   *
   */
  crop_to_fill?: boolean;
  /**
   * Denoising level (0.0-1.0). Applies to Standard V2, Low Resolution V2, CGI, High Fidelity V2, Text Refine, and Redefine models.
   */
  denoise?: number;
  /**
   * Detail recovery level (0.0-1.0). Applies to Recovery V2 model only.
   */
  detail?: number;
  /**
   * Whether to apply face enhancement to the image. Applies to standard enhance and Recovery V2 models. Default value: `true`
   */
  face_enhancement?: boolean;
  /**
   * Creativity level for face enhancement. 0.0 means no creativity, 1.0 means maximum creativity. Ignored if face enhancement is disabled.
   */
  face_enhancement_creativity?: number;
  /**
   * Strength of the face enhancement. 0.0 means no enhancement, 1.0 means maximum enhancement. Ignored if face enhancement is disabled. Default value: `0.8`
   */
  face_enhancement_strength?: number;
  /**
   * Compression artifact removal level (0.0-1.0). Applies to Standard V2, Low Resolution V2, High Fidelity V2, and Text Refine models.
   */
  fix_compression?: number;
  /**
   * Url of the image to be upscaled
   */
  image_url: string | Blob | File;
  /**
   * Model to use for image enhancement. Default value: `"Standard V2"`
   */
  model?:
    | "Low Resolution V2"
    | "Standard V2"
    | "CGI"
    | "High Fidelity V2"
    | "Text Refine"
    | "Recovery"
    | "Redefine"
    | "Recovery V2"
    | "Standard MAX"
    | "Wonder";
  /**
   * Output format of the upscaled image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Text prompt to guide generative upscaling (max 1024 chars). Applies to Redefine model only.
   */
  prompt?: string;
  /**
   * Sharpening level (0.0-1.0). Applies to Standard V2, Low Resolution V2, CGI, High Fidelity V2, Text Refine, and Redefine models.
   */
  sharpen?: number;
  /**
   * Enhancement strength (0.01-1.0). Applies to Text Refine model only.
   */
  strength?: number;
  /**
   * Subject detection mode for the image enhancement. Applies to standard enhance and Recovery V2 models. Default value: `"All"`
   */
  subject_detection?: "All" | "Foreground" | "Background";
  /**
   * Texture detail level for generative upscaling (1-5). Applies to Redefine model only.
   */
  texture?: number;
  /**
   * Factor to upscale the image by (e.g. 2.0 doubles width and height) Default value: `2`
   */
  upscale_factor?: number;
};
export type TopazUpscaleVideoInput = {
  /**
   * Compression artifact removal level (0.0-1.0). Default varies by model.
   */
  compression?: number;
  /**
   * Film grain amount (0.0-1.0). Default varies by model.
   */
  grain?: number;
  /**
   * Whether to use H264 codec for output video. Default is H265.
   */
  H264_output?: boolean;
  /**
   * Halo reduction level (0.0-1.0). Default varies by model.
   */
  halo?: number;
  /**
   * Video enhancement model. Proteus is best for most videos, Artemis for denoise+sharpen, Nyx for dedicated denoising, Gaia for AI-generated/CG/animation content, Starlight for generative diffusion-based upscaling and enhancement. Default value: `"Proteus"`
   */
  model?:
    | "Proteus"
    | "Artemis HQ"
    | "Artemis MQ"
    | "Artemis LQ"
    | "Nyx"
    | "Nyx Fast"
    | "Nyx XL"
    | "Nyx HF"
    | "Gaia HQ"
    | "Gaia CG"
    | "Starlight Precise 1"
    | "Starlight Precise 2"
    | "Starlight Precise 2.5"
    | "Starlight HQ"
    | "Starlight Mini"
    | "Starlight Sharp"
    | "Starlight Fast 1"
    | "Starlight Fast 2";
  /**
   * Noise reduction level (0.0-1.0). Default varies by model.
   */
  noise?: number;
  /**
   * Recover original detail level (0.0-1.0). Higher values preserve more original detail.
   */
  recover_detail?: number;
  /**
   * Target FPS for frame interpolation. If set, frame interpolation will be enabled.
   */
  target_fps?: number;
  /**
   * Factor to upscale the video by (e.g. 2.0 doubles width and height) Default value: `2`
   */
  upscale_factor?: number;
  /**
   * URL of the video to upscale
   */
  video_url: string | Blob | File;
};
export type Track = {
  /**
   * Unique identifier for the track
   */
  id: string;
  /**
   * List of keyframes that make up this track
   */
  keyframes: Array<Keyframe>;
  /**
   * Type of track ('video', 'audio', or 'image')
   */
  type: string;
};
export type TrackPoint = {
  /**
   * X coordinate of the point
   */
  x: number;
  /**
   * Y coordinate of the point
   */
  y: number;
};
export type TrainingStage = {
  /**
   * Aspect ratio for this training stage. Default value: `"3:4"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "9:21";
  /**
   * Batch size for this training stage. Default value: `1`
   */
  batch_size?: number;
  /**
   * Learning rate for this training stage. Default value: `0.0001`
   */
  learning_rate?: number;
  /**
   * Learning rate scheduler. Default value: `"linear"`
   */
  learning_rate_scheduler?:
    | "linear"
    | "cosine"
    | "cosine_with_restarts"
    | "polynomial"
    | "constant"
    | "constant_with_warmup"
    | "piecewise_constant";
  /**
   * Number of training steps for this stage. Default value: `50`
   */
  num_steps?: number;
  /**
   * Number of warmup steps for this stage. Default value: `10`
   */
  num_warmup_steps?: number;
  /**
   * Resolution for this training stage. Default value: `"1024"`
   */
  resolution?: "64" | "128" | "256" | "512" | "768" | "1024" | "1280" | "1536";
  /**
   * The timestep to split the training into two parts. Only applicable when transformer_trainer_style is 'split'. Default value: `0.875`
   */
  split_train_timestep?: number;
  /**
   * The style of the transformer trainer. Either 'both', 'split', 'transformer_1', or 'transformer_2'. Default value: `"in_sequence"`
   */
  transformer_trainer_style?:
    | "both"
    | "split"
    | "transformer_1_only"
    | "transformer_2_only"
    | "in_sequence";
};
export type TrajectoryParameters = {
  /**
   * Vertical rotation angles (degrees) for each keyframe.
   */
  phi: Array<number>;
  /**
   * Camera distance scaling factors for each keyframe.
   */
  radius: Array<number>;
  /**
   * Horizontal rotation angles (degrees) for each keyframe.
   */
  theta: Array<number>;
};
export type TrajectoryPoint = {
  /**
   * Amount of proportional speed that will affect the movement to the next trajectory point. Higher values mean faster movement. Can't be zero. Default value: `1`
   */
  speed?: number;
  /**
   * X coordinate of the trajectory point in pixels.
   */
  x: number;
  /**
   * Y coordinate of the trajectory point in pixels.
   */
  y: number;
};
export type TranscriptionOutput = {
  /**
   * Detected or specified language code
   */
  language_code: string;
  /**
   * Confidence in language detection
   */
  language_probability: number;
  /**
   * The full transcribed text
   */
  text: string;
  /**
   * Word-level transcription details
   */
  words: Array<TranscriptionWord>;
};
export type TranscriptionWord = {
  /**
   * End time in seconds
   */
  end?: number;
  /**
   * Speaker identifier if diarization was enabled
   */
  speaker_id?: string;
  /**
   * Start time in seconds
   */
  start?: number;
  /**
   * The transcribed word or audio event
   */
  text: string;
  /**
   * Type of element (word, spacing, or audio_event)
   */
  type: string;
};
export type transpixarInput = {
  /**
   * The target FPS of the video Default value: `8`
   */
  export_fps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related video to show you. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * The negative prompt to generate video from Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `24`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
};
export type transpixarOutput = {
  /**
   * The prompt used for generating the video.
   */
  prompt: string;
  /**
   * Seed of the generated video. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
  /**
   * The URL to the generated video
   */
  videos: Array<File>;
};
export type Trellis2Input = {
  /**
   * Target number of vertices in the final mesh. Lower values produce smaller files but less detail. 500k is good for most uses, reduce to 20k-50k for web/mobile. Default value: `500000`
   */
  decimation_target?: number;
  /**
   * URL of the input image to convert to 3D
   */
  image_url: string | Blob | File;
  /**
   * Rebuild the mesh topology for cleaner triangles. Slower but usually produces better results for downstream use (animation, 3D printing, etc). Default value: `true`
   */
  remesh?: boolean;
  /**
   * Controls how far remeshing can move vertices from the original surface. Higher values allow more smoothing but may lose fine details. Default value: `1`
   */
  remesh_band?: number;
  /**
   * How much to project remeshed vertices back onto the original surface. 0 = no projection (smoother), 1 = full projection (preserves detail).
   */
  remesh_project?: number;
  /**
   * Output resolution; higher is slower but more detailed Default value: `"1024"`
   */
  resolution?: "512" | "1024" | "1536";
  /**
   * Random seed for reproducibility
   */
  seed?: number;
  /**
   * Dampens artifacts from high guidance in the shape stage. Increase if you see noisy geometry. Default value: `0.5`
   */
  shape_slat_guidance_rescale?: number;
  /**
   * How closely the detailed geometry follows the input image. Higher values add more detail but may introduce noise. Default value: `7.5`
   */
  shape_slat_guidance_strength?: number;
  /**
   * Controls noise schedule sharpness for shape refinement. Higher values produce sharper geometric details. Default value: `3`
   */
  shape_slat_rescale_t?: number;
  /**
   * Number of denoising steps for shape refinement. More steps = slower but potentially smoother geometry. Default value: `12`
   */
  shape_slat_sampling_steps?: number;
  /**
   * Dampens artifacts from high guidance in stage 1. Lower values allow stronger guidance effects, higher values stabilize the output. Default value: `0.7`
   */
  ss_guidance_rescale?: number;
  /**
   * How closely the initial 3D structure follows the input image. Higher values produce more faithful but potentially noisier results. Default value: `7.5`
   */
  ss_guidance_strength?: number;
  /**
   * Controls noise schedule sharpness for structure generation. Higher values produce sharper transitions. Default value: `5`
   */
  ss_rescale_t?: number;
  /**
   * Number of denoising steps for the initial structure. More steps = slower but potentially higher quality. Default value: `12`
   */
  ss_sampling_steps?: number;
  /**
   * Dampens artifacts from high guidance in the texture stage. Increase if textures look noisy or have color banding.
   */
  tex_slat_guidance_rescale?: number;
  /**
   * How closely the texture follows the input image colors. Higher values produce more vivid but potentially oversaturated textures. Default value: `1`
   */
  tex_slat_guidance_strength?: number;
  /**
   * Controls noise schedule sharpness for texture generation. Higher values produce sharper texture details. Default value: `3`
   */
  tex_slat_rescale_t?: number;
  /**
   * Number of denoising steps for texture generation. More steps = slower but potentially cleaner textures. Default value: `12`
   */
  tex_slat_sampling_steps?: number;
  /**
   * Resolution of the texture image baked onto the mesh. Higher values capture finer surface details but produce larger files. Default value: `"2048"`
   */
  texture_size?: "1024" | "2048" | "4096";
};
export type Trellis2Output = {
  /**
   * Generated 3D GLB file
   */
  model_glb: File;
};
export type Trellis2RetextureInput = {
  /**
   * URL of the reference image for texturing
   */
  image_url: string | Blob | File;
  /**
   * URL of the untextured 3D mesh to retexture. Supports GLB, OBJ, PLY, and STL formats.
   */
  mesh_url: string | Blob | File;
  /**
   * Internal resolution for texture generation. Higher produces finer texture details but is slower. Default value: `"1024"`
   */
  resolution?: "512" | "1024";
  /**
   * Random seed for reproducibility
   */
  seed?: number;
  /**
   * Dampens artifacts from high guidance in the texture stage. Increase if textures look noisy or have color banding.
   */
  tex_slat_guidance_rescale?: number;
  /**
   * How closely the texture follows the input image colors. Higher values produce more vivid but potentially oversaturated textures. Default value: `1`
   */
  tex_slat_guidance_strength?: number;
  /**
   * Controls noise schedule sharpness for texture generation. Higher values produce sharper texture details. Default value: `3`
   */
  tex_slat_rescale_t?: number;
  /**
   * Number of denoising steps for texture generation. More steps = slower but potentially cleaner textures. Default value: `12`
   */
  tex_slat_sampling_steps?: number;
  /**
   * Resolution of the texture image baked onto the mesh. Higher values capture finer surface details but produce larger files. Default value: `"2048"`
   */
  texture_size?: "1024" | "2048" | "4096";
};
export type trellisInput = {
  /**
   * URL of the input image to convert to 3D
   */
  image_url: string | Blob | File;
  /**
   * Mesh simplification factor Default value: `0.95`
   */
  mesh_simplify?: number;
  /**
   * Random seed for reproducibility
   */
  seed?: number;
  /**
   * Guidance strength for structured latent generation Default value: `3`
   */
  slat_guidance_strength?: number;
  /**
   * Sampling steps for structured latent generation Default value: `12`
   */
  slat_sampling_steps?: number;
  /**
   * Guidance strength for sparse structure generation Default value: `7.5`
   */
  ss_guidance_strength?: number;
  /**
   * Sampling steps for sparse structure generation Default value: `12`
   */
  ss_sampling_steps?: number;
  /**
   * Texture resolution Default value: `"1024"`
   */
  texture_size?: "512" | "1024" | "2048";
};
export type TrellisMultiInput = {
  /**
   * List of URLs of input images to convert to 3D
   */
  image_urls: Array<string>;
  /**
   * Mesh simplification factor Default value: `0.95`
   */
  mesh_simplify?: number;
  /**
   * Algorithm for multi-image generation Default value: `"stochastic"`
   */
  multiimage_algo?: "stochastic" | "multidiffusion";
  /**
   * Random seed for reproducibility
   */
  seed?: number;
  /**
   * Guidance strength for structured latent generation Default value: `3`
   */
  slat_guidance_strength?: number;
  /**
   * Sampling steps for structured latent generation Default value: `12`
   */
  slat_sampling_steps?: number;
  /**
   * Guidance strength for sparse structure generation Default value: `7.5`
   */
  ss_guidance_strength?: number;
  /**
   * Sampling steps for sparse structure generation Default value: `12`
   */
  ss_sampling_steps?: number;
  /**
   * Texture resolution Default value: `"1024"`
   */
  texture_size?: "512" | "1024" | "2048";
};
export type trellisOutput = {
  /**
   * Generated 3D mesh file
   */
  model_mesh: File;
  /**
   * Processing timings
   */
  timings: unknown;
};
export type TrimVideoInput = {
  /**
   * Duration in seconds from start_time. Ignored if end_time is provided. Default value: `2`
   */
  duration?: number;
  /**
   * End time in seconds. If not provided, uses duration instead.
   */
  end_time?: number;
  /**
   * Start time in seconds Default value: `1`
   */
  start_time?: number;
  /**
   * URL of the video file to trim
   */
  video_url: string | Blob | File;
};
export type TrimVideoOutput = {
  /**
   * Duration of the original video in seconds
   */
  original_duration: number;
  /**
   * Duration of the trimmed video in seconds
   */
  trimmed_duration: number;
  /**
   * The trimmed video
   */
  video: File;
};
export type triposrInput = {
  /**
   * Whether to remove the background from the input image. Default value: `true`
   */
  do_remove_background?: boolean;
  /**
   * Ratio of the foreground image to the original image. Default value: `0.9`
   */
  foreground_ratio?: number;
  /**
   * Path for the image file to be processed.
   */
  image_url: string | Blob | File;
  /**
   * Resolution of the marching cubes. Above 512 is not recommended. Default value: `256`
   */
  mc_resolution?: number;
  /**
   * Output format for the 3D model. Default value: `"glb"`
   */
  output_format?: "glb" | "obj";
};
export type triposrOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Directory containing textures for the remeshed model.
   */
  remeshing_dir?: File;
  /**
   * Inference timings.
   */
  timings: unknown;
};
export type TripoV25ImageTo3dOutput = {
  /**
   * Base model
   */
  base_model?: File;
  /**
   * Model
   */
  model_mesh?: File;
  /**
   * Pbr model
   */
  pbr_model?: File;
  /**
   * A preview image of the model
   */
  rendered_image?: File;
  /**
   * The task id of the 3D model generation.
   */
  task_id: string;
};
export type TTSInput = {
  /**
   * URL to the audio sample to use as a voice prompt for zero-shot TTS voice cloning. Providing a audio sample will override the voice setting. If neither voice nor audio_url are provided, a random voice will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * Classifier-free guidance scale (CFG) controls the conditioning factor. Range typically 0.2 to 1.0. For expressive or dramatic speech, try lower cfg values (e.g. ~0.3) and increase exaggeration to around 0.7 or higher. If the reference speaker has a fast speaking style, lowering cfg to around 0.3 can improve pacing. Default value: `0.5`
   */
  cfg?: number;
  /**
   * Controls emotion exaggeration. Range typically 0.25 to 2.0. Default value: `0.5`
   */
  exaggeration?: number;
  /**
   * If True, the generated audio will be upscaled to 48kHz. The generation of the audio will take longer, but the quality will be higher. If False, the generated audio will be 24kHz.
   */
  high_quality_audio?: boolean;
  /**
   * Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file. Set to 0 for random seed.
   */
  seed?: number;
  /**
   * Controls the randomness of generation. Range typically 0.05 to 5. Default value: `0.8`
   */
  temperature?: number;
  /**
   * Text to synthesize into speech. Default value: `"My name is Maximus Decimus Meridius, commander of the Armies of the North, General of the Felix Legions and loyal servant to the true emperor, Marcus Aurelius. Father to a murdered son, husband to a murdered wife. And I will have my vengeance, in this life or the next."`
   */
  text?: string;
  /**
   * The voice to use for the TTS request. If neither voice nor audio are provided, a random voice will be used.
   */
  voice?:
    | "Aurora"
    | "Blade"
    | "Britney"
    | "Carl"
    | "Cliff"
    | "Richard"
    | "Rico"
    | "Siobhan"
    | "Vicky";
};
export type TtsV1Input = {
  /**
   * BCP-47 language code or 'auto' for automatic detection. Supported: en, zh, fr, de, hi, id, it, ja, ko, pt-BR, pt-PT, ru, es-MX, es-ES, tr, vi, bn, ar-EG, ar-SA, ar-AE. Default value: `"auto"`
   */
  language?:
    | "auto"
    | "en"
    | "ar-EG"
    | "ar-SA"
    | "ar-AE"
    | "bn"
    | "zh"
    | "fr"
    | "de"
    | "hi"
    | "id"
    | "it"
    | "ja"
    | "ko"
    | "pt-BR"
    | "pt-PT"
    | "ru"
    | "es-MX"
    | "es-ES"
    | "tr"
    | "vi";
  /**
   * Output format configuration. Defaults to MP3 at 24 kHz / 128 kbps.
   */
  output_format?: OutputFormat;
  /**
   * The text to convert to speech. Maximum 15,000 characters. Supports speech tags for expressive delivery: inline tags like [laugh], [pause], [sigh] and wrapping tags like <whisper>text</whisper>, <slow>text</slow>.
   */
  text: string;
  /**
   * Voice to use for synthesis. eve: energetic, upbeat. ara: warm, friendly. rex: confident, clear. sal: smooth, balanced. leo: authoritative, strong. Default value: `"eve"`
   */
  voice?: "eve" | "ara" | "rex" | "sal" | "leo";
};
export type TurboFluxTrainerInput = {
  /**
   * Whether to try to detect the face and crop the images to the face. Default value: `true`
   */
  face_crop?: boolean;
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images, although more is better.
   */
  images_data_url: string | Blob | File;
  /**
   * Learning rate for the training. Default value: `0.00115`
   */
  learning_rate?: number;
  /**
   * Number of steps to train the LoRA on. Default value: `1000`
   */
  steps?: number;
  /**
   * Training style to use. Default value: `subject`
   */
  training_style?: "subject" | "style";
  /**
   * Trigger phrase to be used in the captions. If None, a trigger word will not be used.
   * If no captions are provide the trigger_work will be used instead of captions. If captions are provided, the trigger word will replace the `[trigger]` string in the captions. Default value: `"ohwx"`
   */
  trigger_phrase?: string;
};
export type Turn = {
  /**
   *
   */
  speaker_id: number;
  /**
   *
   */
  text: string;
};
export type ultrashapeInput = {
  /**
   * URL of the reference image for mesh refinement.
   */
  image_url: string | Blob | File;
  /**
   * URL of the coarse mesh (.glb or .obj) to refine.
   */
  model_url: string | Blob | File;
  /**
   * Diffusion steps. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Marching cubes resolution. Default value: `1024`
   */
  octree_resolution?: number;
  /**
   * Remove image background. Default value: `true`
   */
  remove_background?: boolean;
  /**
   * Random seed. Default value: `42`
   */
  seed?: number;
};
export type unoInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. You can choose between some presets or custom height and width
   * that **must be multiples of 8**. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of images to use while generating the image.
   */
  input_image_urls: Array<string>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation. If set none, a random seed will be used.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type unoOutput = {
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The URLs of the generated images.
   */
  images: Array<Image>;
  /**
   * The prompt used to generate the image.
   */
  prompt: string;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   *
   */
  timings: unknown;
};
export type UpdatePathConfigInput = {
  /**
   * Per-path circuit breaker threshold (0.0-1.0). Use -1 to reset to global default.
   */
  breaker_threshold?: number;
  /**
   * New concurrency limit. Use -1 to reset to unlimited.
   */
  concurrency_limit?: number;
  /**
   * New cost value.
   */
  cost?: number;
  /**
   * Enable/disable the path.
   */
  enabled?: boolean;
  /**
   * Path ID to update.
   */
  path_id: string;
  /**
   * Enable/disable priority routing.
   */
  priority_enabled?: boolean;
  /**
   * New RPS limit.
   */
  rps_limit?: number;
};
export type UpscaleCreativeInput = {
  /**
   *  Default value: `"https://bria-datasets.s3.us-east-1.amazonaws.com/upscale/wild-west.png"`
   */
  image_url?: string | Blob | File;
  /**
   * Preserve alpha channel from input image. Default value: `true`
   */
  preserve_alpha?: boolean;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type UpscaleImageInput = {
  /**
   * The detail of the upscaled image Default value: `50`
   */
  detail?: number;
  /**
   * Whether to expand the prompt with MagicPrompt functionality.
   */
  expand_prompt?: boolean;
  /**
   * The image URL to upscale
   */
  image_url: string | Blob | File;
  /**
   * The prompt to upscale the image with Default value: `""`
   */
  prompt?: string;
  /**
   * The resemblance of the upscaled image to the original image Default value: `50`
   */
  resemblance?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type UpscaleInput = {
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image to be upscaled. Must be in PNG format.
   */
  image_url: string | Blob | File;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type UsageInfo = {
  /**
   * Time taken for decoding in milliseconds
   */
  decode_time_ms: number;
  /**
   * Number of input tokens processed
   */
  input_tokens: number;
  /**
   * Number of output tokens generated
   */
  output_tokens: number;
  /**
   * Time taken for prefill in milliseconds
   */
  prefill_time_ms: number;
  /**
   * Time to first token in milliseconds
   */
  ttft_ms: number;
};
export type usoInput = {
  /**
   * Enable NSFW content detection and filtering. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * How closely to follow the prompt. Higher values stick closer to the prompt. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of image URLs in order: [content_image, style_image, extra_style_image].
   */
  input_image_urls: Array<string>;
  /**
   * Preserve the layout and dimensions of the input content image. Useful for style transfer.
   */
  keep_size?: boolean;
  /**
   * What you don't want in the image. Use it to exclude unwanted elements, styles, or artifacts. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate in parallel. Default value: `1`
   */
  num_images?: number;
  /**
   * Number of denoising steps. More steps can improve quality but increase generation time. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Output image format. PNG preserves transparency, JPEG is smaller. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Text prompt for generation. Can be empty for pure style transfer. Default value: `""`
   */
  prompt?: string;
  /**
   * Random seed for reproducible generation. Use same seed for consistent results.
   */
  seed?: number;
  /**
   * If true, wait for generation and upload before returning. Increases latency but provides immediate access to images.
   */
  sync_mode?: boolean;
};
export type V16Input = {
  /**
   * Category of the garment to try-on. 'auto' will attempt to automatically detect the category of the garment. Default value: `"auto"`
   */
  category?: "tops" | "bottoms" | "one-pieces" | "auto";
  /**
   * URL or base64 of the garment image
   */
  garment_image: string;
  /**
   * Specifies the type of garment photo to optimize internal parameters for better performance. 'model' is for photos of garments on a model, 'flat-lay' is for flat-lay or ghost mannequin images, and 'auto' attempts to automatically detect the photo type. Default value: `"auto"`
   */
  garment_photo_type?: "auto" | "model" | "flat-lay";
  /**
   * Specifies the mode of operation. 'performance' mode is faster but may sacrifice quality, 'balanced' mode is a balance between speed and quality, and 'quality' mode is slower but produces higher quality results. Default value: `"balanced"`
   */
  mode?: "performance" | "balanced" | "quality";
  /**
   * URL or base64 of the model image
   */
  model_image: string;
  /**
   * Content moderation level for garment images. 'none' disables moderation, 'permissive' blocks only explicit content, 'conservative' also blocks underwear and swimwear. Default value: `"permissive"`
   */
  moderation_level?: "none" | "permissive" | "conservative";
  /**
   * Number of images to generate in a single run. Image generation has a random element in it, so trying multiple images at once increases the chances of getting a good result. Default value: `1`
   */
  num_samples?: number;
  /**
   * Output format of the generated images. 'png' is highest quality, while 'jpeg' is faster Default value: `"png"`
   */
  output_format?: "png" | "jpeg";
  /**
   * Sets random operations to a fixed state. Use the same seed to reproduce results with the same inputs, or different seed to force different results.
   */
  seed?: number;
  /**
   * Disables human parsing on the model image. Default value: `true`
   */
  segmentation_free?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type V16Output = {
  /**
   *
   */
  images: Array<File>;
};
export type Validation = {
  /**
   * An image to use for image-to-video validation. If provided for one validation, _all_ validation inputs must have an image.
   */
  image_url?: string | Blob | File;
  /**
   * The prompt to use for validation.
   */
  prompt: string;
};
export type vecglypherOutput = {
  /**
   * The generated SVG file containing vector glyphs.
   */
  image: File;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * Raw SVG content as a string.
   */
  svg_content: string;
  /**
   * Timing breakdown of the generation process.
   */
  timings: unknown;
};
export type Veo2ImageToVideoInput = {
  /**
   * The duration of the generated video in seconds Default value: `"5s"`
   */
  duration?: "5s" | "6s" | "7s" | "8s";
  /**
   * URL of the input image to animate. Should be 720p or higher resolution in 16:9 or 9:16 aspect ratio. If the image is not in 16:9 or 9:16 aspect ratio, it will be cropped to fit.
   */
  image_url: string | Blob | File;
  /**
   * The text prompt describing how the image should be animated
   */
  prompt: string;
};
export type veo2Input = {
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9" | "1:1";
  /**
   * Whether to automatically attempt to fix prompts that fail content policy or other validation checks by rewriting them Default value: `true`
   */
  auto_fix?: boolean;
  /**
   * The duration of the generated video in seconds Default value: `"5s"`
   */
  duration?: "5s" | "6s" | "7s" | "8s";
  /**
   * Whether to enhance the video generation Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * A negative prompt to guide the video generation
   */
  negative_prompt?: string;
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * A seed to use for the video generation
   */
  seed?: number;
};
export type Veo31ExtendVideoInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16";
  /**
   * Whether to automatically attempt to fix prompts that fail content policy or other validation checks by rewriting them.
   */
  auto_fix?: boolean;
  /**
   * The duration of the generated video. Default value: `"7s"`
   */
  duration?: string;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * A negative prompt to guide the video generation.
   */
  negative_prompt?: string;
  /**
   * The text prompt describing how the video should be extended
   */
  prompt: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: string;
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * URL of the video to extend. The video should be 720p or 1080p resolution in 16:9 or 9:16 aspect ratio.
   */
  video_url: string | Blob | File;
};
export type Veo31FirstLastFrameToVideoInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16";
  /**
   * Whether to automatically attempt to fix prompts that fail content policy or other validation checks by rewriting them.
   */
  auto_fix?: boolean;
  /**
   * The duration of the generated video. Default value: `"8s"`
   */
  duration?: "4s" | "6s" | "8s";
  /**
   * URL of the first frame of the video
   */
  first_frame_url: string | Blob | File;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * URL of the last frame of the video
   */
  last_frame_url: string | Blob | File;
  /**
   * A negative prompt to guide the video generation.
   */
  negative_prompt?: string;
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "720p" | "1080p" | "4k";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
};
export type Veo31LiteFirstLastFrameToVideoInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16";
  /**
   * Whether to automatically attempt to fix prompts that fail content policy or other validation checks by rewriting them.
   */
  auto_fix?: boolean;
  /**
   * The duration of the generated video. Default value: `"8s"`
   */
  duration?: string;
  /**
   * URL of the first frame of the video
   */
  first_frame_url: string | Blob | File;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * URL of the last frame of the video
   */
  last_frame_url: string | Blob | File;
  /**
   * A negative prompt to guide the video generation.
   */
  negative_prompt?: string;
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
};
export type Veo31ReferenceToVideoInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Whether to automatically attempt to fix prompts that fail content policy or other validation checks by rewriting them.
   */
  auto_fix?: boolean;
  /**
   * The duration of the generated video. Default value: `"8s"`
   */
  duration?: string;
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * URLs of the reference images to use for consistent subject appearance
   */
  image_urls: Array<string>;
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "720p" | "1080p" | "4k";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
};
export type Veo3ImageToVideoInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16";
  /**
   * Whether to automatically attempt to fix prompts that fail content policy or other validation checks by rewriting them.
   */
  auto_fix?: boolean;
  /**
   * The duration of the generated video. Default value: `"8s"`
   */
  duration?: "4s" | "6s" | "8s";
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * URL of the input image to animate. Should be 720p or higher resolution in 16:9 or 9:16 aspect ratio. If the image is not in 16:9 or 9:16 aspect ratio, it will be cropped to fit.
   */
  image_url: string | Blob | File;
  /**
   * A negative prompt to guide the video generation.
   */
  negative_prompt?: string;
  /**
   * The text prompt describing how the image should be animated
   */
  prompt: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
};
export type veo3Input = {
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * Whether to automatically attempt to fix prompts that fail content policy or other validation checks by rewriting them. Default value: `true`
   */
  auto_fix?: boolean;
  /**
   * The duration of the generated video. Default value: `"8s"`
   */
  duration?: "4s" | "6s" | "8s";
  /**
   * Whether to generate audio for the video. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * A negative prompt to guide the video generation.
   */
  negative_prompt?: string;
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The safety tolerance level for content moderation. 1 is the most strict (blocks most content), 6 is the least strict. Default value: `"4"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
};
export type Vibevoice05bInput = {
  /**
   * CFG (Classifier-Free Guidance) scale for generation. Higher values increase adherence to text. Default value: `1.3`
   */
  cfg_scale?: number;
  /**
   * The script to convert to speech.
   */
  script: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * Voice to use for speaking.
   */
  speaker: "Frank" | "Wayne" | "Carter" | "Emma" | "Grace" | "Mike";
};
export type vibevoiceInput = {
  /**
   * CFG (Classifier-Free Guidance) scale for generation. Higher values increase adherence to text. Default value: `1.3`
   */
  cfg_scale?: number;
  /**
   * The script to convert to speech. Can be formatted with 'Speaker X:' prefixes for multi-speaker dialogues.
   */
  script: string;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * List of speakers to use for the script. If not provided, will be inferred from the script or voice samples.
   */
  speakers: Array<VibeVoiceSpeaker>;
};
export type vibevoiceOutput = {
  /**
   * The generated audio file containing the speech
   */
  audio: File;
  /**
   * Duration of the generated audio in seconds
   */
  duration: number;
  /**
   * Time taken to generate the audio in seconds
   */
  generation_time: number;
  /**
   * Real-time factor (generation_time / audio_duration). Lower is better.
   */
  rtf: number;
  /**
   * Sample rate of the generated audio
   */
  sample_rate: number;
};
export type VibeVoiceSpeaker = {
  /**
   * URL to a voice sample audio file. If provided, `preset` will be ignored.
   */
  audio_url?: string | Blob | File;
  /**
   * Default voice preset to use for the speaker. Not used if `audio_url` is provided. Default value: `"Alice [EN]"`
   */
  preset?:
    | "Alice [EN]"
    | "Carter [EN]"
    | "Frank [EN]"
    | "Mary [EN] (Background Music)"
    | "Maya [EN]"
    | "Anchen [ZH] (Background Music)"
    | "Bowen [ZH]"
    | "Xinran [ZH]";
};
export type Video = {
  /**
   * Audio track information if video has audio
   */
  audio?: AudioTrack;
  /**
   * Overall bitrate of the media in bits per second
   */
  bitrate: number;
  /**
   * Codec used to encode the media
   */
  codec: string;
  /**
   * Container format of the media file (e.g., 'mp4', 'mov')
   */
  container: string;
  /**
   * MIME type of the media file
   */
  content_type: string;
  /**
   * Duration of the media in seconds
   */
  duration: number;
  /**
   * URL of the extracted last frame
   */
  end_frame_url?: string | Blob | File;
  /**
   * Original filename of the media
   */
  file_name: string;
  /**
   * Size of the file in bytes
   */
  file_size: number;
  /**
   * Detailed video format information
   */
  format: VideoFormat;
  /**
   * Frames per second
   */
  fps: number;
  /**
   * Total number of frames in the video
   */
  frame_count: number;
  /**
   * Type of media (always 'video') Default value: `"video"`
   */
  media_type?: string;
  /**
   * Video resolution information
   */
  resolution: Resolution;
  /**
   * URL of the extracted first frame
   */
  start_frame_url?: string | Blob | File;
  /**
   * Time base used for frame timestamps
   */
  timebase: string;
  /**
   * URL where the media file can be accessed
   */
  url: string;
};
export type Video2PixelInput = {
  /**
   * Alpha binarization threshold (0-255). Default value: `128`
   */
  alpha_threshold?: number;
  /**
   * Enable automatic detection of optimal number of colors.
   */
  auto_color_detect?: boolean;
  /**
   * Controls where to flood-fill from when removing the background. Default value: `"corners"`
   */
  background_mode?: "edges" | "corners" | "midpoints";
  /**
   * Background tolerance (0-255).
   */
  background_tolerance?: number;
  /**
   * Remove isolated diagonal pixels (jaggy edge cleanup).
   */
  cleanup_jaggy?: boolean;
  /**
   * Apply morphological operations to remove noise.
   */
  cleanup_morph?: boolean;
  /**
   * Scale detection method to use. Default value: `"auto"`
   */
  detect_method?: "auto" | "runs" | "edge";
  /**
   * Dominant color threshold (0.0-1.0). Default value: `0.05`
   */
  dominant_color_threshold?: number;
  /**
   * Downscaling method to produce the pixel-art output. Default value: `"dominant"`
   */
  downscale_method?:
    | "dominant"
    | "median"
    | "mode"
    | "mean"
    | "content-adaptive";
  /**
   * Optional fixed color palette as hex strings (e.g., ['#000000', '#ffffff']).
   */
  fixed_palette?: Array<string>;
  /**
   * Maximum number of colors in the output palette. Set None to disable limit. Default value: `32`
   */
  max_colors?: number;
  /**
   * Resample the input video to a different FPS.
   */
  resample_fps?: boolean;
  /**
   * Force a specific pixel scale. If None, auto-detect.
   */
  scale?: number;
  /**
   * Align output to the pixel grid. Default value: `true`
   */
  snap_grid?: boolean;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The target FPS to resample the input video to. Only relevant if `resample_fps` is True. Default value: `24`
   */
  target_fps?: number;
  /**
   * Remove background of the image. This will check for contiguous color regions from the edges after correction and make them transparent.
   */
  transparent_background?: boolean;
  /**
   * Trim borders of the image.
   */
  trim_borders?: boolean;
  /**
   * Whether to loop the output video. Only applicable if output_type is 'gif'.
   */
  video_loop?: boolean;
  /**
   * The quality of the output video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The type of output video to generate. Default value: `"X264 (.mp4)"`
   */
  video_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The video URL to process into improved pixel art
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the output video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type Video2PixelOutput = {
  /**
   * The number of colors in the processed media.
   */
  num_colors: number;
  /**
   * The palette of the processed media.
   */
  palette: Array<string>;
  /**
   * The detected pixel scale of the input.
   */
  pixel_scale: number;
  /**
   * The processed pixel-art videos.
   */
  videos: Array<VideoFile>;
};
export type VideoAgentConfig = {
  /**
   * Optional avatar name to use Default value: `"Adriana SuitSofa Front"`
   */
  avatar?:
    | "Abigail (Upper Body)"
    | "Abigail Office Front"
    | "Abigail Office Side"
    | "Abigail Sofa Front"
    | "Abigail Sofa Side"
    | "Aditya in Brown blazer"
    | "Aditya in Blue blazer"
    | "Aditya in Blue shirt"
    | "Aditya in Blue t-shirt"
    | "Aditya in Beige blazer"
    | "Adrian in Blue Shirt"
    | "Adrian in Blue Suit"
    | "Adrian in Blue Sweater"
    | "Adriana BizTalk Front"
    | "Adriana BizTalk Side"
    | "Adriana Business Front 2"
    | "Adriana Business Front "
    | "Adriana Business Side"
    | "Adriana Nurse Front 2"
    | "Adriana Nurse Front"
    | "Adriana Nurse Side 2"
    | "Adriana Nurse Side"
    | "Adriana Nurse Sitting Side"
    | "Adriana SuitSofa Front"
    | "Aiko"
    | "Albert in Blue Polo shirt"
    | "Albert in Blue blazer"
    | "Albert in Blue shirt"
    | "Albert in Blue suit"
    | "Albert in Khaki blazer"
    | "Albert in White shirt"
    | "Amanda in Blue Shirt (Front)"
    | "Amanda in Blue Shirt (Left)"
    | "Amanda in Blue Shirt (Right)"
    | "Amanda in Grey Shirt (Front)"
    | "Amanda in Grey Shirt (Left)"
    | "Amanda in Grey Shirt (Right)"
    | "Amanda in Maintenance (Front)"
    | "Amanda in Maintenance (Left)"
    | "Amanda in Maintenance (Right)"
    | "Amelia Business Training Front 2"
    | "Amelia Business Training Front"
    | "Amelia Business Training Side 2"
    | "Amelia Business Training Side"
    | "Amelia Lounge Front 2"
    | "Amelia Lounge Front"
    | "Amelia Lounge Side 2"
    | "Amelia Lounge Side"
    | "Amelia Yoga Front 2"
    | "Amelia Yoga Front"
    | "Amelia Yoga Side 2"
    | "Amelia Yoga Side"
    | "Alex in Black Suit"
    | "Alex in Jacket"
    | "Alex in White Coat"
    | "Alex in Yellow Sweater"
    | "Anja Office Front"
    | "Anja Office Side "
    | "Anja Sofa Front"
    | "Anja Sofa Side "
    | "Ann Business Front"
    | "Ann Business Sitting"
    | "Ann Casual Front"
    | "Ann Casual Lying"
    | "Ann Casual Sitting 2"
    | "Ann Casual Sitting"
    | "Ann Doctor Sitting"
    | "Ann Doctor Standing"
    | "Ann Sweater Front"
    | "Ann Sweater Side"
    | "Ann Therapist"
    | "Anna in White T-shirt"
    | "Anna in Turtleneck T-shirt"
    | "Anna in Brown T-shirt"
    | "Annelise in Dark blue dress"
    | "Annelise in Off white dress"
    | "Annelise in Sky blue dress"
    | "Annelise in White dress"
    | "Annelore in Blue blazer"
    | "Annelore in Blue dress"
    | "Annelore in Red blazer"
    | "Annelore in Red sweater"
    | "Annelore in White shirt"
    | "Annie in Grey Jacket"
    | "Annie in White Shirt"
    | "Annie in Tan Jacket"
    | "Annie in Blue Casual"
    | "Annie in Light Blue"
    | "Annie in Brown Shirt"
    | "Annie in Black Shirt"
    | "Annie in Pink Suit"
    | "Annie in Grey Dress"
    | "Annie in Blue Vest"
    | "Annie in Black V-neck Shirt"
    | "Annie in Blue Suit"
    | "Annie Bar Sitting Front"
    | "Annie Bar Sitting Side"
    | "Annie Bar Standing Front 2"
    | "Annie Bar Standing Front 3"
    | "Annie Bar Standing Front"
    | "Annie Bar Standing Side 2"
    | "Annie Bar Standing Side 3"
    | "Annie Bar Standing Side"
    | "Annie Business Casual Standing Front 2"
    | "Annie Business Casual Standing Front"
    | "Annie Business Casual Standing Side 2"
    | "Annie Business Casual Standing Side"
    | "Annie Casual Sitting Front 2"
    | "Annie Casual Sitting Front"
    | "Annie Casual Sitting Side 2"
    | "Annie Casual Sitting Side"
    | "Annie Casual Standing Front 2"
    | "Annie Casual Standing Front"
    | "Annie Casual Standing Side 2"
    | "Annie Casual Standing Side"
    | "Annie Desk Sitting Front 2"
    | "Annie Desk Sitting Front"
    | "Annie Desk Sitting Side 2"
    | "Annie Desk Sitting Side"
    | "Annie Lounge Standing Front"
    | "Annie Lounge Standing Side"
    | "Annie Office Sitting Front 2"
    | "Annie Office Sitting Front"
    | "Annie Office Sitting Side 2"
    | "Annie Office Sitting Side"
    | "Annie Office Standing Front"
    | "Annie Office Standing Side"
    | "Annie Sofa Sitting Front 3"
    | "Annie Sofa Sitting Front"
    | "Annie Sofa Sitting Side 2"
    | "Annie Sofa Sitting Side 3"
    | "Annie Sofa Sitting Side"
    | "Annie Sofa Sitting Front 2"
    | "Annie Studio Pink Sitting Front"
    | "Annie Studio Pink Sitting Side"
    | "Annie Studio Pink Standing Front"
    | "Annie Studio Pink Standing Side"
    | "Armando Casual Front"
    | "Armando Casual Side"
    | "Armando Suit Front"
    | "Armando Suit Side"
    | "Armando Sweater Front 2"
    | "Armando Sweater Front"
    | "Armando Sweater Side 2"
    | "Armando Sweater Side"
    | "Artur Office Front 2"
    | "Artur Office Front"
    | "Artur Office Side 2"
    | "Artur Office Side"
    | "Artur Sofa Casual Front 2"
    | "Artur Sofa Casual Front"
    | "Artur Sofa Casual Side 2"
    | "Artur Sofa Causal Side"
    | "Aubrey Sofa Side"
    | "Aubrey (Upper Body)"
    | "Aubrey Night Scene Front"
    | "Aubrey Outdoor Sport Front"
    | "Aubrey Outdoor Sport Side"
    | "Aubrey Sofa Front"
    | "August Casual Front 2"
    | "August Casual Front"
    | "August Cool Sitting"
    | "August Cool Style"
    | "August Employee Front"
    | "August Hoodies Front 2"
    | "August Hoodies Front"
    | "Bahar (Upper Body)"
    | "Bahar Business Front"
    | "Bahar Business Side"
    | "Bahar Business Sitting Front"
    | "Bahar Business Sitting Side"
    | "Bahar Casual Sitting Front 2"
    | "Bahar Casual Sitting Front"
    | "Bahar Casual Sitting Side 2"
    | "Bahar Casual Sitting Side"
    | "Bahar Denim Casual Front"
    | "Bahar Denim Casual Side"
    | "Bahar Denim Front"
    | "Bahar Denim Side"
    | "Bahar Jacket Casual Front"
    | "Bahar Jacket Casual Side"
    | "Bahar Jacket Front"
    | "Bahar Jacket Side"
    | "Bahar Suit Front"
    | "Bahar Suit Side"
    | "Bastien in Blue T-shirt"
    | "Bastien in Blue blazer"
    | "Bastien in Blue shirt"
    | "Bastien in White shirt"
    | "Blanka Lounge Front"
    | "Blanka Lounge Side"
    | "Blanka Outdoor Business Front"
    | "Blanka Outdoor Business Side"
    | "Blanka Outdoor Reading Front"
    | "Blanka Outdoor Reading Side"
    | "Blanka Picnic Front"
    | "Blanka Picnic Side"
    | "Bojan Business Training Front 2"
    | "Bojan Business Training Front"
    | "Bojan Business Training Side 2"
    | "Bojan Business Training Side"
    | "Bojan Lounge Front 2"
    | "Bojan Lounge Front"
    | "Bojan Lounge Side 2"
    | "Bojan Lounge Side"
    | "Bojan Sport Front 2"
    | "Bojan Sport Front"
    | "Bojan Sport Side 2"
    | "Bojan Sport Side"
    | "Bradley in Blue Polo (Front)"
    | "Bradley in Blue Polo (Left)"
    | "Bradley in Blue Polo (Right)"
    | "Bradley in Blue Shirt (Front)"
    | "Bradley in Blue Shirt (Left)"
    | "Bradley in Blue Shirt (Right)"
    | "Bradley in Doctor (Front)"
    | "Bradley in Doctor (Left)"
    | "Bradley in Doctor (Right)"
    | "Brandon in Grey Suit"
    | "Brandon in Blue Sweater"
    | "Brandon in White Shirt"
    | "Brandon Business Sitting Front"
    | "Brandon Business Sitting Side"
    | "Brandon Business Standing Front"
    | "Brandon Business Standing Side"
    | "Brandon Casual Sitting Front"
    | "Brandon Casual Sitting Side"
    | "Brandon Kitchen Standing Front"
    | "Brandon Kitchen Standing Side"
    | "Brandon Lobby Sitting Front"
    | "Brandon Lobby Sitting Side"
    | "Brandon Lobby Standing Front"
    | "Brandon Lobby Standing Side"
    | "Brandon Office Sitting Front"
    | "Brandon Office Sitting Side"
    | "Brandon Office Standing Front"
    | "Brandon Office Standing Side"
    | "Brandon Sofa Sitting Front"
    | "Brandon Sofa Sitting Side"
    | "Brent Office Front 2"
    | "Brent Office Front"
    | "Brent Office Side 2"
    | "Brent Office Side"
    | "Brent Sofa Front 2"
    | "Brent Sofa Front"
    | "Brent Sofa Side 2"
    | "Brent Sofa Side"
    | "Briana in White shirt"
    | "Briana in Striped T-shirt"
    | "Briana in Brown suit"
    | "Bruce"
    | "Bryan Casual Front"
    | "Bryan Casual Side"
    | "Bryan Fitness Coach"
    | "Bryan Tech Expert"
    | "Bryan Plaid Shirt Front"
    | "Bryan Plaid Shirt Side"
    | "Bryan Suit Front"
    | "Bryan Suit Side"
    | "Bryce in Black t-shirt"
    | "Bryce in Blue blazer"
    | "Bryce in Blue shirt"
    | "Bryce in Grey blazer"
    | "Bryce in White shirt"
    | "Byron Business Front 2"
    | "Byron Business Front"
    | "Byron Business Side 2"
    | "Byron Business Side"
    | "Byron Business Sitting Front"
    | "Byron Business Sitting Size"
    | "Byron Casual Front 2"
    | "Byron Casual Front"
    | "Byron Casual Side 2"
    | "Byron Casual Side"
    | "Byron Casual Sitting Front"
    | "Byron Casual Sitting Side"
    | "Byron Jacket Front"
    | "Byron Jacket Side"
    | "Byron Sitting Front"
    | "Byron Sitting Side"
    | "Byron Suit Front"
    | "Byron Suit Side"
    | "Candace in Beige Dress (Front)"
    | "Candace in Beige Dress (Left)"
    | "Candace in Beige Dress (Right)"
    | "Candace in Doctor (Front)"
    | "Candace in Doctor (Left)"
    | "Candace in Doctor (Right)"
    | "Candace in Pink Blazer (Front)"
    | "Candace in Pink Blazer (Right)"
    | "Candace in Pink Blazer  (Left)"
    | "Carla in Doctor (Front)"
    | "Carla in Doctor (Left)"
    | "Carla in Doctor (Right)"
    | "Carla in Dress  (Left)"
    | "Carla in Shirt (Front)"
    | "Carla in Shirt (Left)"
    | "Carla in Shirt (Right)"
    | "Carlotta BizTalk Front"
    | "Carlotta BizTalk Side"
    | "Carlotta Business Front"
    | "Carlotta Business Side"
    | "Carlotta Casual Front"
    | "Carlotta Casual Side"
    | "Carlotta Casual Sitting Front"
    | "Carlotta Casual Sitting Side"
    | "Carlotta Half Front"
    | "Carlotta Pink Jumpsuit Front 2"
    | "Carlotta Pink Jumpsuit Front"
    | "Carlotta Pink Jumpsuit Side 2"
    | "Carlotta Pink Jumpsuit Side"
    | "Carlotta Pink Jumpsuit Sitting Front"
    | "Carlotta Pink Jumpsuit Sitting Side"
    | "Caroline Business Sitting Front"
    | "Caroline Business Sitting Side"
    | "Caroline Business Standing Front"
    | "Caroline Business Standing Side"
    | "Caroline in Blue Suit"
    | "Caroline in White Shirt"
    | "Caroline in Yellow Casual"
    | "Caroline in Yellow Skirt"
    | "Caroline Casual Sitting Front"
    | "Caroline Casual Sitting Side"
    | "Caroline Kitchen Standing Front"
    | "Caroline Kitchen Standing Side"
    | "Caroline Lobby Sitting Front"
    | "Caroline Lobby Sitting Side"
    | "Caroline Lobby Standing Front"
    | "Caroline Lobby Standing Side"
    | "Caroline Office Sitting Front"
    | "Caroline Office Sitting Side"
    | "Caroline Office Standing Front"
    | "Caroline Office Standing Side"
    | "Caroline Sofa Sitting Front"
    | "Caroline Sofa Sitting Side"
    | "Chad in Blue Shirt (Front)"
    | "Chad in Blue Shirt (Left)"
    | "Chad in Blue Shirt (Right)"
    | "Chad in Grey Shirt (Front)"
    | "Chad in Grey Shirt (Left)"
    | "Chad in Grey Shirt (Right)"
    | "Chad in Maintenance (Front)"
    | "Chad in Maintenance (Left)"
    | "Chad in Maintenance (Right)"
    | "Chakir in Headscarf (Front)"
    | "Chakir in Headscarf (Left)"
    | "Chakir in Headscarf (Right)"
    | "Chakir in White Shirt (Front)"
    | "Chakir in White Shirt (Right)"
    | "Chakir in white Shirt (Left)"
    | "Chloe (Upper Body)"
    | "Chloe Lounge Front"
    | "Chloe Lounge Side"
    | "Chloe Outdoor Side"
    | "Colin Business Front 2"
    | "Colin Business Front"
    | "Colin Jacket Front"
    | "Colin Sitting Cool Style 2"
    | "Colin Sitting Cool Style"
    | "Colin Suit Front"
    | "Colin Sweater Front"
    | "Colin Sweater Sitting Front"
    | "Connie Business Front"
    | "Connie Business Sitting Front"
    | "Connie Casual Sitting Front"
    | "Connie Casual Sitting Side"
    | "Connie Education Front"
    | "Connie Education Side 2"
    | "Connie Education Side"
    | "Connie Skirt Front"
    | "Connie Skirt Side"
    | "Connie Skirt Sitting Front"
    | "Connie Skirt Sitting Side"
    | "Connie Suit Front"
    | "Conrad House Front"
    | "Conrad House Side"
    | "Conrad Sofa Front"
    | "Conrad Sofa Side"
    | "Crisanto Business Front"
    | "Crisanto Business Side"
    | "Crisanto Chef Front 2"
    | "Crisanto Chef Front"
    | "Crisanto Chef Side 2"
    | "Crisanto Chef Side"
    | "Crisanto Chef Sitting "
    | "Crisanto Education Front"
    | "Crisanto Education Side"
    | "Crisanto Nurse Front 2"
    | "Crisanto Nurse Front"
    | "Crisanto Nurse Side 2"
    | "Crisanto Nurse Side"
    | "Crisanto Nurse Sitting Front"
    | "Crisanto Nurse Sitting Side"
    | "Crisanto Suit Front"
    | "Crisanto Suit Side"
    | "Daphne in Blue blazer"
    | "Daphne in Blue shirt"
    | "Daphne in Grey blazer"
    | "Daphne in Grey suit"
    | "Daphne in Pink hoodie"
    | "Daphne in White t-shirt"
    | "Darnell in Blue Shirt (Front)"
    | "Darnell in Blue Shirt (Left)"
    | "Darnell in Blue Shirt (Right)"
    | "Darnell in Bordeaux Polo (Front)"
    | "Darnell in Bordeaux Polo (Left)"
    | "Darnell in Bordeaux Polo (Right)"
    | "Darnell in Doctor (Front)"
    | "Darnell in Doctor (Left)"
    | "Darnell in Doctor (Right)"
    | "Derya Indoor Front 2"
    | "Derya Indoor Front"
    | "Derya Indoor Side 2"
    | "Derya Indoor Side"
    | "Derya Office Front 2"
    | "Derya Office Front"
    | "Derya Office Side 2"
    | "Derya Office Side"
    | "Dexter Casual Front"
    | "Dexter Casuat Side"
    | "Dexter Doctor Sitting"
    | "Dexter Doctor Standing"
    | "Dexter Lawyer"
    | "Dexter Suit Front"
    | "Dexter Suit Side"
    | "Dexter Winter Coat Front"
    | "Dexter Winter Coat Side"
    | "Diana in Black Camisole Top"
    | "Diana in Striped Shirt"
    | "Diana in White Shirt"
    | "Diora in Blue blazer"
    | "Diora in Green blazer"
    | "Diora in Pink shirt"
    | "Diora in White shirt"
    | "Diora in White t-shirt"
    | "Diran Casual Front"
    | "Diran Casual Side"
    | "Diran Jacket Front"
    | "Diran Jacket Side"
    | "Diran Macbook Business Front"
    | "Diran Macbook Business Side"
    | "Diran Macbook Casual Front"
    | "Diran Macbook Casual Side"
    | "Diran Macbook Sitting Front 2"
    | "Diran Macbook Sitting Front"
    | "Diran Macbook Sitting Side 2"
    | "Diran Macbook Sitting Side"
    | "Diran Suit Front"
    | "Diran Suit Side"
    | "Diran iPad Front"
    | "Diran iPad Side"
    | "Diran iPad Sitting Front"
    | "Diran iPad Sitting Side"
    | "Elenora Casual Front"
    | "Elenora Casual Side"
    | "Elenora Fitness Coach"
    | "Elenora Fitness Coach 2"
    | "Elenora Tech Expert"
    | "Elenora Suit Front"
    | "Elenora Suit Side"
    | "Elenora YellowDress Front"
    | "Elenora YellowDress Side"
    | "Emanuel Office Front"
    | "Emanuel Office Side"
    | "Emanuel Sofa Front"
    | "Emanuel Sofa Side"
    | "Emery in Blue blazer"
    | "Emery in Blue suit"
    | "Emery in Green shirt"
    | "Emery in Khaki blazer"
    | "Emery in Red blazer"
    | "Emery in White t-shirt"
    | "Emilia Outdoor Business Front"
    | "Emilia Outdoor Business Side"
    | "Emilia Outdoor Yoga Front 2"
    | "Emilia Outdoor Yoga Front"
    | "Emilia Outdoor Yoga Side"
    | "Emilia Picnic Front"
    | "Emilia Picnic Side"
    | "Edward in Black Suit"
    | "Edward"
    | "Edward in Blue Shirt"
    | "Esmond in Black coat"
    | "Esmond in Black shirt"
    | "Esmond in Blue blazer"
    | "Esmond in Blue suit"
    | "Esmond in Grey sweater"
    | "Fernando Business Indoor Front"
    | "Fernando Business Indoor Side"
    | "Fernando OutdoorChair Front 2"
    | "Fernando Outdoor Chair Front"
    | "Fernando Outdoor Chair Side"
    | "Fernando Outdoor Front"
    | "Fernando Outdoor Side"
    | "Fernando Outdoor Table Front"
    | "Fernando Outdoor Table Side"
    | "Fina Business Sitting Front"
    | "Fina Business Sitting Side"
    | "Fina Casual Front 2"
    | "Fina Casual Front"
    | "Fina Casual Side 2"
    | "Fina Casual Side"
    | "Fina Casual Sitting Front"
    | "Fina Casual Sitting Side"
    | "Fina Denim Front"
    | "Fina Denim Side"
    | "Fina Denim Sitting Front"
    | "Fina Employee Front"
    | "Fina Employee Side"
    | "Fina Suit Front"
    | "Fina Suit Side"
    | "Fina Support Front"
    | "Fina Support Side"
    | "Florin Business Sitting Front"
    | "Florin Business Sitting Side"
    | "Florin Maintain Front 2"
    | "Florin Maintain Front"
    | "Florin Maintain Side 2"
    | "Florin Maintain Side"
    | "Florin Maintain Siiting Front"
    | "Florin Maintain Siiting Side"
    | "Florin Suit Front 2"
    | "Florin Suit Front"
    | "Florin Suit Side 2"
    | "Florin Suit Side"
    | "Francis in Blazer (Front)"
    | "Francis in Blazer (Left)"
    | "Francis in Blazer (Right)"
    | "Francis in Doctor (Front)"
    | "Francis in Doctor (Left)"
    | "Francis in Doctor (Right)"
    | "Francis in Shirt (Front)"
    | "Francis in Shirt (Left)"
    | "Francis in Shirt (Right)"
    | "Fred in Blue Long Shirt (Front)"
    | "Fred in Blue Long Shirt (Left)"
    | "Fred in Blue Long Shirt (Right)"
    | "Fred in Blue Short Shirt (Front)"
    | "Fred in Blue Short Shirt (Left)"
    | "Fred in Blue Short Shirt (Right)"
    | "Freja in Blue blazer"
    | "Freja in Grey blazer"
    | "Freja in White blazer"
    | "Freja in White polo shirt"
    | "Freja in White shirt"
    | "Gabriel in Black Sweatshirt"
    | "Gabriel in Blue Suit"
    | "Gabriel in Gray Shirt"
    | "Gala Bedroom Front"
    | "Gala Business Sofa Front 2"
    | "Gala Business Sofa Front 3"
    | "Gala Business Sofa Front"
    | "Gala Business Sofa Side 2"
    | "Gala Business Sofa Side 3"
    | "Gala Business Sofa Side"
    | "Gala Casual Sofa with iPad Front"
    | "Gala Casual Sofa with iPad Side 2"
    | "Gala Casual Sofa with iPad Side"
    | "Gala Office Front"
    | "Gala Office Side"
    | "Gala Sofa Front 2"
    | "Gala Sofa Front 3"
    | "Gala Sofa Front"
    | "Gala Sofa Side 2"
    | "Gala Sofa Side 3"
    | "Gala Sofa Side"
    | "Georgia (Upper Body)"
    | "Georgia Casual Front"
    | "Georgia Casual Side"
    | "Georgia Office Front"
    | "Georgia Office Side"
    | "Gerardo Sofa Side"
    | "Gerardo Indoor Front"
    | "Gerardo Indoor Side"
    | "Gerardo Night Scene Front 2"
    | "Gerardo Night Scene Front"
    | "Gerardo Outdoor Sport Front"
    | "Gerardo Outdoor Sport Side"
    | "Gerardo Sofa Front"
    | "Giulia Office Front 2"
    | "Giulia Office Front"
    | "Giulia Office Side 2"
    | "Giulia Office Side"
    | "Giulia Sofa Front"
    | "Giulia Sofa Side"
    | "Giulia Sofa  Front 2"
    | "Giulia Sofa  Side 3"
    | "Hada Casual Cup Front"
    | "Hada Casual Cup Side"
    | "Hada Casual Front"
    | "Hada Casual Side"
    | "Hada Casual Sitting Front 2"
    | "Hada Casual Sitting Front"
    | "Hada Casual Sitting Side 2"
    | "Hada Casual Sitting Side"
    | "Hada LivelyGestures Front"
    | "Hada LivelyGestures Side"
    | "Hada LivelyGestures Sitting Front"
    | "Hada LivelyGestures Sitting Side"
    | "Hada Suit Front 2"
    | "Hada Suit Front"
    | "Hada Suit Side 2"
    | "Hada Suit Side"
    | "Hada Suit Sitting Front 2"
    | "Hada Suit Sitting Front"
    | "Hada Suit Sitting Side"
    | "Harrison in Black Suit"
    | "Harrison in Gray Suit"
    | "Harrison in White Shirt"
    | "Ian in Beige Shirt"
    | "Ian in Black Jacket"
    | "Ian in Gray Suit"
    | "Ida Lounge Front 2"
    | "Ida Lounge Front"
    | "Ida Lounge Side 2"
    | "Ida Lounge Side"
    | "Ida Sofa Front 2"
    | "Ida Sofa Front"
    | "Ida Sofa Side 2"
    | "Ida Sofa Side"
    | "Iker in Almond sweater"
    | "Iker in Black blazer"
    | "Iker in Blue shirt"
    | "Iker in Grey blazer"
    | "Iker in White shirt"
    | "Imelda BizTalk Front"
    | "Imelda BizTalk Side"
    | "Imelda Business Front"
    | "Imelda Business Side"
    | "Imelda Business Sitting Front 2"
    | "Imelda Business Sitting Front"
    | "Imelda Business Sitting Side 2"
    | "Imelda Business Sitting Side"
    | "Imelda Casual Front"
    | "Imelda Casual Side"
    | "Imelda Coat Front 2"
    | "Imelda Coat Front"
    | "Imelda Coat Side"
    | "Imelda Customer Support Front"
    | "Imelda Customer Support Side"
    | "Imelda Full Side "
    | "Imelda Suit Front"
    | "Imelda Suit Side"
    | "Ivan in Black Suit"
    | "Ivan in Gary Suit"
    | "Ivan in Suit"
    | "Ivan in Sweater"
    | "Jason in Black Jacket"
    | "Jason in Blue Suit"
    | "Jason in Gray Shirt"
    | "Javi Intense Sitting Speaking Front"
    | "Javi Intense Sitting Speaking Side"
    | "Javi Intense Sitting Speaking "
    | "Javi Intense Speaking Front 2"
    | "Javi Intense Speaking Front 3"
    | "Javi Intense Speaking Front"
    | "Javi Intense Speaking Side 3"
    | "Javi Intense Speaking Side"
    | "Javi in Passionate Gestures 2"
    | "Javi in Passionate Gestures 3"
    | "Javi in Passionate Gestures"
    | "Jin (Upper Body)"
    | "Jin Blue Casual Front"
    | "Jin Blue Casual Side"
    | "Jin Business Café Mode Front"
    | "Jin Business Café Mode Side"
    | "Jin Business Sitting Front"
    | "Jin Business Sitting Side"
    | "Jin Casual Café Mode Front"
    | "Jin Casual Café Mode Side"
    | "Jin Casual Sitting Front"
    | "Jin Casual Sitting Side"
    | "Jin Suit Front"
    | "Jin Suit Side"
    | "Jin Vest Front"
    | "Jin Vest Side"
    | "Jin Vest Sitting Front"
    | "Jin Vest Sitting Side"
    | "Jin in Education Front"
    | "Jin in Education Side"
    | "Jinwoo in Black vest"
    | "Jinwoo in Blue suit"
    | "Jinwoo in White T-shirt"
    | "Jinwoo in White shirt"
    | "Jinwoo in White suit"
    | "Jocelyn Office Front 2"
    | "Jocelyn Office Front"
    | "Jocelyn Office Side 2"
    | "Jocelyn Office Side"
    | "Jocelyn Sofa Front 2"
    | "Jocelyn Sofa Front"
    | "Jocelyn Sofa Side 2"
    | "Jocelyn Sofa Side"
    | "Joel Couch Front"
    | "Joel Couch Side"
    | "Joel Gym Front"
    | "Joel Gym Side"
    | "Joel Mountain Front"
    | "Joel Mountain Side"
    | "Jonas (Upper Body)"
    | "Jonas Gym Front 2"
    | "Jonas Gym Front"
    | "Jonas Gym Side 2"
    | "Jonas Gym Side "
    | "June Office Front 2"
    | "Juan Office Front"
    | "Juan Office Side 2"
    | "Juan Office Side"
    | "Juan Sofa Front 2"
    | "Juan Sofa Front"
    | "Juan Sofa Side 2"
    | "Juan Sofa Side"
    | "Judita (Upper Body)"
    | "Judita Yoga Front 2"
    | "Judita Yoga Front"
    | "Judita Yoga Side 2"
    | "Judita Yoga Side"
    | "Judith (Upper Body)"
    | "Judith Business Front 2"
    | "Judith Business Front"
    | "Judith Business Sitting Front"
    | "Judith Casual Front"
    | "Judith Casual Sitting Front"
    | "Judith Suit Front"
    | "Judy Business Front"
    | "Judy Business Side"
    | "Judy Business Sitting Front"
    | "Judy Casual Front"
    | "Judy Casual Side"
    | "Judy Casual Sitting"
    | "Judy ConfidentSpeaking Front"
    | "Judy ConfidentSpeaking Side"
    | "Judy Doctor Sitting"
    | "Judy Doctor Standing"
    | "Judy Lawyer"
    | "Judy NurseSitting Front 2"
    | "Judy NurseSitting Front"
    | "Judy NurseSitting Side 2"
    | "Judy NurseSitting Side"
    | "Judy Nurse Front 2"
    | "Judy Nurse Front"
    | "Judy Nurse OnDuty Front"
    | "Judy Nurse OnDuty Side"
    | "Judy Nurse Side 2"
    | "Judy Nurse Side"
    | "Judy Suit Front"
    | "Judy Suit Side"
    | "Judy Teacher Sitting"
    | "Judy HR"
    | "Judy Teacher Standing"
    | "June (Upper Body)"
    | "June HR"
    | "June Office Front"
    | "June Office Side 2"
    | "June Office Side"
    | "June Sofa Casual Front 2"
    | "June Sofa Casual Front"
    | "June Sofa Casual Side 2"
    | "June Sofa Casual Side"
    | "Justin in Black Shirt"
    | "Justin in Black Suit"
    | "Justin in White Shirt"
    | "Justo Business Front 2"
    | "Justo Business Front"
    | "Justo Business Side 2"
    | "Justo Business Side"
    | "Justo Casual Sitting"
    | "Justo CustomerService Front 2"
    | "Justo CustomerService Front"
    | "Justo CustomerService Side"
    | "Justo CustomerService Sitting Front"
    | "Justo CustomerService Sitting Side"
    | "Justo EmployeeTraining Front"
    | "Justo Suit Casual Front "
    | "Justo Suit Casual Side "
    | "Karolin in Gray Suit"
    | "Karolin in Sweatshirt"
    | "Karolin in Black Suit"
    | "Kavya Indoor Front"
    | "Kavya Indoor Side"
    | "Kavya Outdoor Side"
    | "Kavya Outdoor Sport Front"
    | "Kavya Outdoor Sport Side"
    | "Kavya Sofa Front"
    | "Kavya Sofa Side"
    | "Kelly in Blue Shirt  (Front)"
    | "Kelly in Blue Shirt (Left)"
    | "Kelly in Blue Shirt (Right)"
    | "Kelly in Doctor (Front)"
    | "Kelly in Doctor (Left)"
    | "Kelly in Doctor (Right)"
    | "Kelly in Pink Shirt (Front)"
    | "Kelly in Pink Shirt (Left)"
    | "Kelly in Pink Shirt (Right)"
    | "Klara in Black blazer"
    | "Klara in Blue blazer"
    | "Klara in Blue dress"
    | "Klara in Pink shirt"
    | "Klara in White blazer"
    | "Klara in White shirt"
    | "Kristin in Lace Dress"
    | "Kristin in V-neck Shirt"
    | "Kristin in Black Suit"
    | "Leos Office Front 2"
    | "Leos Office Front"
    | "Leos Office Side 2"
    | "Leos Office Side"
    | "Leos Sofa Front 2"
    | "Leos Sofa Front"
    | "Leos Sofa Side 2"
    | "Leos Sofa Side"
    | "Leszek Lounge Front"
    | "Leszek Lounge Side"
    | "Leszek Outdoor Business Front"
    | "Leszek Outdoor Business Side"
    | "Leszek Outdoor Casual Front"
    | "Leszek Outdoor Casual Side"
    | "Leszek Sofa Front"
    | "Leszek Sofa Side"
    | "Leah"
    | "Leah in Black Suit"
    | "Lina Casual Front 2"
    | "Lina Casual Front"
    | "Lina Casual Side 2"
    | "Lina Casual Side"
    | "Lina Casual Sitting Front"
    | "Lina Casual Sitting Side"
    | "Lina Dress Front"
    | "Lina Dress Side 2"
    | "Lina Dress Side"
    | "Lina Dress Sitting Front"
    | "Lina Dress Sitting Side"
    | "Lina Sweater Front 2"
    | "Lina Sweater Front"
    | "Lina Sweater Side 2"
    | "Lina Sweater Side"
    | "Lina Sweater Sitting Front 2"
    | "Lina Sweater Sitting Front"
    | "Lina Sweater Sitting Side 2"
    | "Lina Sweater Sitting Side"
    | "Lisa"
    | "Luca"
    | "Lucien in Black coat"
    | "Lucien in Black polo-shirt"
    | "Lucien in Blue blazer"
    | "Lucien in Blue shirt"
    | "Lucien in Grey blazer"
    | "Lucien in White shirt"
    | "Luke in Brown Suit"
    | "Luke in Yellow Jacket"
    | "Luke in Blue Suit"
    | "Marcus (Upper Body)"
    | "Marcus Café Front 2"
    | "Marcus Café Front"
    | "Marcus Café Side 2"
    | "Marcus Café Side"
    | "Marcus Casual Front"
    | "Marcus Casual Side"
    | "Marcus Casual Sitting Front 2"
    | "Marcus Casual Sitting Front"
    | "Marcus Casual Sitting Side 2"
    | "Marcus Casual Sitting Side"
    | "Marcus Denim Jacket Front"
    | "Marcus Denim Jacket Side"
    | "Marcus Education Front"
    | "Marcus Education Side"
    | "Marcus Sitting Front"
    | "Marcus Sitting Side"
    | "Marcus Suit Front"
    | "Marcus Suit Side"
    | "Maria in Suit"
    | "Maria in Sweater"
    | "Maria in Black Suit"
    | "Martina Office Front 2"
    | "Martina Office Front"
    | "Martina Office Side 2"
    | "Martina Office Side"
    | "Martina Sofa Front 2"
    | "Martina Sofa Front"
    | "Martina Sofa Side 2"
    | "Martina Sofa Side"
    | "Masha Office Front 2"
    | "Masha Office Front"
    | "Masha Office Side 2"
    | "Masha Office Side"
    | "Masha Sofa Casual Front 2"
    | "Masha Sofa Casual Front"
    | "Masha Sofa Casual Side 2"
    | "Masha Sofa Casual Side"
    | "Mason in Blue Suit"
    | "Mason in White Shirt"
    | "Mason in Blue Sweater"
    | "Matteo Office Front 2"
    | "Matteo Office Front"
    | "Matteo Office Side 2"
    | "Matteo Office Side"
    | "Matteo Sofa Front 2"
    | "Matteo Sofa Front"
    | "Matteo Sofa Side 2"
    | "Matteo Sofa Side"
    | "Max (Upper Body)"
    | "Max Indoor Front"
    | "Max Indoor Side"
    | "Max Outdoor Sport Front"
    | "Max Outdoor Sport Side"
    | "Milena Office Front 2"
    | "Milena Office Front"
    | "Milena Office Side 2"
    | "Milena Office Side"
    | "Milena Sofa Front 2"
    | "Milena Sofa Front"
    | "Milena Sofa Side 2"
    | "Milena Sofa Side"
    | "Miles (Upper Body)"
    | "Miles Outdoor Front"
    | "Miles Outdoor Side"
    | "Miles Sofa Front 2"
    | "Miles Sofa Front"
    | "Miles Sofa Side 2"
    | "Miles Sofa Side"
    | "Minho in Blue blazer"
    | "Minho in Blue shirt"
    | "Minho in Green polo-shirt"
    | "Minho in Khaki jacket"
    | "Minho in White shirt"
    | "Minho in White t-shirt"
    | "Mireia Business Indoor Front"
    | "Mireia Business Indoor Side"
    | "Mireia Outdoor Chair Front"
    | "Mireia Outdoor Front"
    | "Mireia Outdoor Side"
    | "Mireia Outdoor Table Front"
    | "Mireia Outdoor Table Side"
    | "Miyu Office Front 2"
    | "Miyu Office Front"
    | "Miyu Office Side 2"
    | "Miyu Office Side"
    | "Miyu Sofa Business Front"
    | "Miyu Sofa Business Side"
    | "Miyu Sofa Casual Front 2"
    | "Miyu Sofa Casual Front"
    | "Miyu Sofa Casual Side 2 "
    | "Miyu Sofa Casual Side"
    | "Nadim in Black blazer"
    | "Nadim in Blue blazer"
    | "Nadim in Blue jacket"
    | "Nadim in Puffer vest"
    | "Nadim in White shirt"
    | "Neil in Black Shirt"
    | "Neil in Black Suit "
    | "Neil in Yellow Jacket"
    | "Nico"
    | "Noah Lobby Front 2"
    | "Noah Lobby Front"
    | "Noah Lobby Side 2"
    | "Noah Lobby Side"
    | "Noah Office Front 2"
    | "Noah Office Front"
    | "Noah Office Side 2"
    | "Noah Office Side"
    | "Noah Sofa Front 2"
    | "Noah Sofa Front "
    | "Noah Sofa Side 2"
    | "Noah Sofa Side"
    | "Nour in Black blazer"
    | "Nour in Brown dress"
    | "Nour in Grey hoodie"
    | "Nour in Sporty vest"
    | "Nour in White shirt"
    | "Odelia in Blue Suit"
    | "Odelia in Red Dress "
    | "Odelia in Yellow Suit"
    | "Onat (Upper Body)"
    | "Onat Casual Front"
    | "Onat Casual Side"
    | "Onat Casual Sitting Front"
    | "Onat Casual Sitting Side"
    | "Onat Macbook Front 2"
    | "Onat Macbook Front"
    | "Onat Macbook Side 2"
    | "Onat Macbook Side"
    | "Onat Suit Front"
    | "Onat Suit Side"
    | "Onat Suit Sitting Front"
    | "Onat Suit Sitting Side"
    | "Onat iPad Front"
    | "Onat iPad Side"
    | "Onat iPad Sitting Front"
    | "Onat iPad Sitting Side"
    | "Oxana (Upper Body)"
    | "Oxana Gym Front 2"
    | "Oxana Gym Front"
    | "Oxana Gym Side 2"
    | "Oxana Gym Side"
    | "Oxana Office Front 2"
    | "Oxana Office Front"
    | "Oxana Office Side 2"
    | "Oxana Office Side"
    | "Oxana Sofa Front 2"
    | "Oxana Sofa Front"
    | "Oxana Sofa Side 2"
    | "Oxana Sofa Side"
    | "Oxana Yoga Front 2"
    | "Oxana Yoga Front"
    | "Oxana Yoga Side 2"
    | "Oxana Yoga Side"
    | "Patrizio Business Training Front"
    | "Patrizio Business Training Side"
    | "Patrizio Office Front"
    | "Patrizio Office Side 2"
    | "Patrizio Office Side"
    | "Patrizio Sofa Front"
    | "Patrizio Sofa Side"
    | "Piper Business Sofa Front"
    | "Piper Business Sofa Side"
    | "Piper Education Front"
    | "Piper Education Side"
    | "Rasmus Lounge Front 2"
    | "Rasmus Lounge Front"
    | "Rasmus Lounge Side 2"
    | "Rasmus Lounge Side"
    | "Rasmus Sofa Front 2"
    | "Rasmus Sofa Front"
    | "Rasmus Sofa Side 2"
    | "Rasmus Sofa Side"
    | "Raul (Upper Body)"
    | "Raul Business Sofa Front 2"
    | "Raul Business Sofa Front"
    | "Raul Business Sofa Side 2"
    | "Raul Business Sofa Side"
    | "Raul Casual Sofa Front 2"
    | "Raul Casual Sofa Front"
    | "Raul Casual Sofa Side 2"
    | "Raul Casual Sofa Side"
    | "Raul Casual Sofa no iPad Front"
    | "Raul Casual Sofa no iPad Side"
    | "Raul Casual Sofa with iPad Front"
    | "Raul Casual Sofa with iPad Side"
    | "Raul Office Front 2"
    | "Raul Office Front"
    | "Raul Office Side"
    | "Raul Sofa Front 2"
    | "Raul Sofa Front"
    | "Raul Sofa Side 2"
    | "Raul Sofa Side"
    | "Rebecca"
    | "Ren (Upper Body)"
    | "Ren Office Front 2"
    | "Ren Office Front"
    | "Ren Office Side 2"
    | "Ren Office Side"
    | "Ren Sofa Business Front"
    | "Ren Sofa Business Side"
    | "Ren Sofa Casual Front 2"
    | "Ren Sofa Casual Front"
    | "Ren Sofa Casual Side 2 "
    | "Ren Sofa Casual Side"
    | "Riley (Upper Body)"
    | "Riley Casual Front"
    | "Riley Casual Side"
    | "Riley Office Front"
    | "Riley Office Side"
    | "Roman Outdoor Sport Front"
    | "Roman Outdoor Sport Side"
    | "Sabine Office Front 2"
    | "Sabine Office Front"
    | "Sabine Office Side"
    | "Sabine Sofa Side"
    | "Salma in headscarf (Front)"
    | "Salma in headscarf (Left)"
    | "Salma in headscarf (Right)"
    | "Santa Avatar Present Standing "
    | "Santa Avatar Sitting 2"
    | "Santa Avatar Sitting"
    | "Santa Avatar Sitting Side"
    | "Santa Avatar Standing"
    | "Santa Fireplace Front"
    | "Santa Fireplace Side"
    | "Saskia in Blue blazer"
    | "Saskia in Blue shirt"
    | "Saskia in Green blazer"
    | "Saskia in Grey vest"
    | "Saskia in White blazer"
    | "Scarlett Couch Front 2"
    | "Scarlett Couch Front"
    | "Scarlett Couch Side 2"
    | "Scarlett Couch Side"
    | "Scarlett Fireplace Front"
    | "Scarlett Fireplace Side"
    | "Scarlett Hall Front"
    | "Scarlett Hall Side"
    | "Scarlett Yoga Front"
    | "Scarlett Yoga Side"
    | "Seema Business Front"
    | "Seema Business Side"
    | "Seema Business Sitting Side"
    | "Seema Casual Front"
    | "Seema Casual Side"
    | "Seema Casual Sitting Front"
    | "Seema Casual Sitting Side"
    | "Seema Nurse Front 2"
    | "Seema Nurse Front"
    | "Seema Nurse Side 2"
    | "Seema Nurse Side"
    | "Shawn Business Front"
    | "Shawn Business Side"
    | "Shawn Casual Sitting Front"
    | "Shawn Casual Sitting Side"
    | "Shawn Sitting Front"
    | "Shawn Sitting Side"
    | "Shawn Suit Front"
    | "Shawn Suit Side"
    | "Shawn Therapist"
    | "Shirley Business Front"
    | "Shirley Business Side"
    | "Shirley Casual Front 2"
    | "Shirley Casual Front"
    | "Shirley Casual Side"
    | "Shirley Casual Sitting Front 2"
    | "Shirley Casual Sitting Front"
    | "Shirley Casual Sitting Side 2"
    | "Shirley Casual Sitting Side"
    | "Shirley Education Front"
    | "Shirley Education Side 2"
    | "Shirley Education Side"
    | "Shirley Sitting Front"
    | "Shirley Sitting Side"
    | "Shirley Skirt Front"
    | "Shirley Skirt Side"
    | "Shirley Suit Front"
    | "Shirley Suit Side"
    | "Silas (Upper Body)"
    | "Silas Customer Support"
    | "Silas HR"
    | "Silas Lounge Front"
    | "Silas Lounge Side"
    | "Silas Sofa Side 2"
    | "Silas Sofa Side"
    | "Sloane in Blue dress"
    | "Sloane in Grey suit"
    | "Sloane in Pink sweater"
    | "Sophia in Black Shirt"
    | "Sophia in Suit "
    | "Sophia in White Suit"
    | "Sophie"
    | "Stacy in Doctor (Front)"
    | "Stacy in Doctor (Left)"
    | "Stacy in Doctor (Right)"
    | "Stacy in Dress (Front)"
    | "Stacy in Dress (Left)"
    | "Stacy in Dress (Right)"
    | "Stacy in Shirt (Front)"
    | "Stacy in Shirt (Left)"
    | "Stacy in Shirt (Right)"
    | "Susan in Black Shirt"
    | "Susan in Black Suit"
    | "Susan in Suit"
    | "Tahlia in Blue dress"
    | "Tahlia in Blue suit"
    | "Tahlia in Dark blue suit"
    | "Tahlia in Red suit"
    | "Tahlia in White shirt"
    | "Teodor (Upper Body)"
    | "Teodor Office Front 2"
    | "Teodor Office Front"
    | "Teodor Office Side 2"
    | "Teodor Office Side"
    | "Teodor Sofa Front 2"
    | "Teodor Sofa Front"
    | "Teodor Sofa Side 2"
    | "Teodor Sofa Side"
    | "Timothy (Upper Body)"
    | "Timothy Casual Front"
    | "Timothy Casual Side"
    | "Timothy Office Front"
    | "Timothy Office Side"
    | "Tito Casual Front 2"
    | "Tito Casual Front"
    | "Tito Casual Side 2"
    | "Tito Casual Side"
    | "Tito Casual Sitting Front"
    | "Tito Casual Sitting Side"
    | "Tito Coat Front 2"
    | "Tito Coat Front"
    | "Tito Coat Full Side"
    | "Tito Coat Side 2"
    | "Tito Coat Side"
    | "Tito Painter Front"
    | "Tito Painter Full Side"
    | "Tito Painter Side"
    | "Tito Painter Sitting Front"
    | "Tito Painter Sitting Side"
    | "Travis in Polo Shirt"
    | "Travis in Gray Suit"
    | "Travis in Black Suit"
    | "Trevor  in Plaid Shirt"
    | "Trevor in Blue Shirt"
    | "Trevor in Blue Suit"
    | "Trevor in Suit"
    | "Tuba Business Chair Front"
    | "Tuba Business Chair Side"
    | "Tuba Business Front"
    | "Tuba Business Side"
    | "Tuba Business Sofa Front"
    | "Tuba Business Sofa Side"
    | "Tuba Casual Front"
    | "Tuba Casual Side"
    | "Tuba Casual Sitting Front"
    | "Tuba Casual Sitting Side"
    | "Tuba Macbook Front"
    | "Tuba Macbook Side"
    | "Veit Office Front"
    | "Veit Office Side"
    | "Veit Sofa Front"
    | "Veit Sofa Side"
    | "Vernon Office Front 2"
    | "Vernon Office Front"
    | "Vernon Office Side 2"
    | "Vernon Office Side"
    | "Verena Office Front"
    | "Verena Office Side"
    | "Verena Sofa Front"
    | "Verena Sofa Side"
    | "Vernon (Upper Body)"
    | "Vernon Lounge Front 2"
    | "Vernon Lounge Side 2"
    | "Vernon Lounge Side"
    | "Vince (Upper Body)"
    | "Vince Business Sofa Front"
    | "Vince Business Training Front"
    | "Vince Business Training Side 2"
    | "Vince Business Training Side"
    | "Vince Sofa Casual Front 2"
    | "Vince Sofa Casual Front"
    | "Vince Sofa Casual Side 2"
    | "Vince Sofa Casual Side"
    | "Violante Brown Suit Front 2"
    | "Violante Brown Suit Front "
    | "Violante Business Sitting Front"
    | "Violante Business Sitting Side"
    | "Violante Casual Sitting Front "
    | "Violante Casual Sitting Side"
    | "Violante Sport Front 2"
    | "Violante Sport Front"
    | "Violante Sport Side"
    | "Violante Sport Sitting Side"
    | "Violante Suit Front"
    | "Vivianna in Black shirt"
    | "Wade in Black Suit"
    | "Wade in Black Jacket"
    | "Wade in Gray Jacket"
    | "Yola Active Speaking Front"
    | "Yola Active Speaking Side"
    | "Yola Business Front"
    | "Yola Business Side"
    | "Yola Calm Speaking Front"
    | "Yola Calm Speaking Side"
    | "Yola Casual Front"
    | "Yola Casual Side"
    | "Yola Employee Badge Front"
    | "Yola Employee Badge Side"
    | "Zosia in Blue blazer"
    | "Zosia in Green dress"
    | "Zosia in Khaki blazer"
    | "Zosia in White dress"
    | "Zosia in Yellow shirt"
    | "Austin in Suit"
    | "Austin in Blue Casual Suit"
    | "Austin in Black Jacket"
    | "Austin in Blue Suit"
    | "Candace in Pink Blazer (Upper Body)"
    | "Chad in Blue Shirt (Upper Body)"
    | "Daisy in Suit"
    | "Daisy in Shirt"
    | "Daisy in Dress"
    | "Daisy in T-shirt"
    | "Francis in Blazer (Upper Body)"
    | "Matthew"
    | "Matthew in Suit"
    | "Matthew in Flowery Shirt"
    | "Matthew in Grey Sweater"
    | "Nik in Black Shirt"
    | "Nik in Blue Sweater"
    | "Tyler in Casual Suit"
    | "Tyler in Shirt"
    | "Tyler in Suit";
  /**
   * Approximate duration in seconds (minimum 5). Suggested presets: 30, 60, or 90 seconds.
   */
  duration?: number;
  /**
   * Video orientation Default value: `"portrait"`
   */
  orientation?: "portrait" | "landscape";
};
export type VideoAsPromptInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"9:16"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second for the output video. Only applicable if output_type is 'video'. Default value: `16`
   */
  fps?: number;
  /**
   * Guidance scale for generation. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Input image to generate the effect video for.
   */
  image_url: string | Blob | File;
  /**
   * The number of frames to generate. Default value: `49`
   */
  num_frames?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Resolution of the generated video. Default value: `"480p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducible generation. If set none, a random seed will be used.
   */
  seed?: number;
  /**
   * A brief description of the input video content.
   */
  video_description: string;
  /**
   * reference video to generate effect video from.
   */
  video_url: string | Blob | File;
};
export type VideoChatOutput = {
  /**
   * Dictionary of label: mask video
   */
  masks: Array<File>;
  /**
   * Generated output
   */
  output: string;
};
export type VideoCondition = {
  /**
   * The frame number to start the condition on.
   */
  start_frame_number?: number;
  /**
   * The strength of the condition. Default value: `1`
   */
  strength?: number;
  /**
   * The URL of the video to use as input.
   */
  video_url: string | Blob | File;
};
export type VideoConditioningInput = {
  /**
   * Type of conditioning this video provides. This is relevant to ensure in-context LoRA weights are applied correctly, as well as selecting the correct preprocessing pipeline, when enabled. Default value: `rgb`
   */
  conditioning_type?: "rgb" | "depth" | "pose" | "canny";
  /**
   * Whether to limit the number of frames used from the video. If True, the `max_num_frames` parameter will be used to limit the number of frames.
   */
  limit_num_frames?: boolean;
  /**
   * Maximum number of frames to use from the video. If None, all frames will be used. Default value: `1441`
   */
  max_num_frames?: number;
  /**
   * Whether to preprocess the video. If True, the video will be preprocessed to match the conditioning type. This is a no-op for RGB conditioning.
   */
  preprocess?: boolean;
  /**
   * Whether to resample the video to a specific FPS. If True, the `target_fps` parameter will be used to resample the video.
   */
  resample_fps?: boolean;
  /**
   * Whether to reverse the video. This is useful for tasks where the video conditioning should be applied in reverse order.
   */
  reverse_video?: boolean;
  /**
   * Frame number of the video from which the conditioning starts. Must be a multiple of 8.
   */
  start_frame_num?: number;
  /**
   * Strength of the conditioning. 0.0 means no conditioning, 1.0 means full conditioning. Default value: `1`
   */
  strength?: number;
  /**
   * Target FPS to resample the video to. Only relevant if `resample_fps` is True. Default value: `24`
   */
  target_fps?: number;
  /**
   * URL of video to use as conditioning
   */
  video_url: string | Blob | File;
};
export type VideoEffectInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second for the output video. Only applicable if output_type is 'video'. Default value: `16`
   */
  fps?: number;
  /**
   * Input image to generate the effect video for.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation. If set none, a random seed will be used.
   */
  seed?: number;
  /**
   * A brief description of the input video content.
   */
  video_description: string;
  /**
   * reference video to generate effect video from.
   */
  video_url: string | Blob | File;
};
export type VideoEraseKeypointsInput = {
  /**
   * auto trim the video, to working duration ( 5s ) Default value: `true`
   */
  auto_trim?: boolean;
  /**
   * Input keypoints [x,y] to erase or keep from the video. Format like so: {'x':100, 'y':100, 'type':'positive/negative'}
   */
  keypoints: Array<string>;
  /**
   * Output container and codec. Options: mp4_h265, mp4_h264, webm_vp9, gif, mov_h264, mov_h265, mov_proresks, mkv_h264, mkv_h265, mkv_vp9, mkv_mpeg4. Default value: `"mp4_h264"`
   */
  output_container_and_codec?:
    | "mp4_h265"
    | "mp4_h264"
    | "webm_vp9"
    | "gif"
    | "mov_h264"
    | "mov_h265"
    | "mov_proresks"
    | "mkv_h264"
    | "mkv_h265"
    | "mkv_vp9"
    | "mkv_mpeg4";
  /**
   * If true, audio will be preserved in the output video. Default value: `true`
   */
  preserve_audio?: boolean;
  /**
   * Input video to erase object from. duration must be less than 5s.
   */
  video_url: string | Blob | File;
};
export type VideoEraseMaskInput = {
  /**
   * auto trim the video, to working duration ( 5s ) Default value: `true`
   */
  auto_trim?: boolean;
  /**
   * Input video to mask erase object from. duration must be less than 5s.
   */
  mask_video_url: string | Blob | File;
  /**
   * Output container and codec. Options: mp4_h265, mp4_h264, webm_vp9, gif, mov_h264, mov_h265, mov_proresks, mkv_h264, mkv_h265, mkv_vp9, mkv_mpeg4. Default value: `"mp4_h264"`
   */
  output_container_and_codec?:
    | "mp4_h265"
    | "mp4_h264"
    | "webm_vp9"
    | "gif"
    | "mov_h264"
    | "mov_h265"
    | "mov_proresks"
    | "mkv_h264"
    | "mkv_h265"
    | "mkv_vp9"
    | "mkv_mpeg4";
  /**
   * If true, audio will be preserved in the output video. Default value: `true`
   */
  preserve_audio?: boolean;
  /**
   * Input video to erase object from. duration must be less than 5s.
   */
  video_url: string | Blob | File;
};
export type VideoErasePromptInput = {
  /**
   * auto trim the video, to working duration ( 5s ) Default value: `true`
   */
  auto_trim?: boolean;
  /**
   * Output container and codec. Options: mp4_h265, mp4_h264, webm_vp9, gif, mov_h264, mov_h265, mov_proresks, mkv_h264, mkv_h265, mkv_vp9, mkv_mpeg4. Default value: `"mp4_h264"`
   */
  output_container_and_codec?:
    | "mp4_h265"
    | "mp4_h264"
    | "webm_vp9"
    | "gif"
    | "mov_h264"
    | "mov_h265"
    | "mov_proresks"
    | "mkv_h264"
    | "mkv_h265"
    | "mkv_vp9"
    | "mkv_mpeg4";
  /**
   * If true, audio will be preserved in the output video. Default value: `true`
   */
  preserve_audio?: boolean;
  /**
   * Input prompt to detect object to erase
   */
  prompt: string;
  /**
   * Input video to erase object from. duration must be less than 5s.
   */
  video_url: string | Blob | File;
};
export type VideoFile = {
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * The duration of the video
   */
  duration?: number;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The FPS of the video
   */
  fps?: number;
  /**
   * The height of the video
   */
  height?: number;
  /**
   * The number of frames in the video
   */
  num_frames?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
  /**
   * The width of the video
   */
  width?: number;
};
export type VideoFormat = {
  /**
   * Video bitrate in bits per second
   */
  bitrate: number;
  /**
   * Container format of the video
   */
  container: string;
  /**
   * Codec level (e.g., 4.1)
   */
  level: number;
  /**
   * Pixel format used (e.g., 'yuv420p')
   */
  pixel_format: string;
  /**
   * Codec profile (e.g., 'main', 'high')
   */
  profile: string;
  /**
   * Video codec used (e.g., 'h264')
   */
  video_codec: string;
};
export type VideoIncreaseResolutionInput = {
  /**
   * desired_increase factor. Options: 2x, 4x. Default value: `"2"`
   */
  desired_increase?: "2" | "4";
  /**
   * Output container and codec. Options: mp4_h265, mp4_h264, webm_vp9, mov_h265, mov_proresks, mkv_h265, mkv_h264, mkv_vp9, gif. Default value: `"webm_vp9"`
   */
  output_container_and_codec?:
    | "mp4_h265"
    | "mp4_h264"
    | "webm_vp9"
    | "mov_h265"
    | "mov_proresks"
    | "mkv_h265"
    | "mkv_h264"
    | "mkv_vp9"
    | "gif";
  /**
   * Input video to increase resolution. Size should be less than 7680,4320 and duration less than 30s.
   */
  video_url: string | Blob | File;
};
export type VideoIncreaseResolutionOutput = {
  /**
   * Video with removed background and audio.
   */
  video: Video | File;
};
export type VideoInput = {
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * File data
   */
  file_data?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
};
export type VideoOutput = {
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
};
export type VideoPromptGeneratorInput = {
  /**
   * Camera direction Default value: `"None"`
   */
  camera_direction?:
    | "None"
    | "Zoom in"
    | "Zoom out"
    | "Pan left"
    | "Pan right"
    | "Tilt up"
    | "Tilt down"
    | "Orbital rotation"
    | "Push in"
    | "Pull out"
    | "Track forward"
    | "Track backward"
    | "Spiral in"
    | "Spiral out"
    | "Arc movement"
    | "Diagonal traverse"
    | "Vertical rise"
    | "Vertical descent";
  /**
   * Camera movement style Default value: `"None"`
   */
  camera_style?:
    | "None"
    | "Steadicam flow"
    | "Drone aerials"
    | "Handheld urgency"
    | "Crane elegance"
    | "Dolly precision"
    | "VR 360"
    | "Multi-angle rig"
    | "Static tripod"
    | "Gimbal smoothness"
    | "Slider motion"
    | "Jib sweep"
    | "POV immersion"
    | "Time-slice array"
    | "Macro extreme"
    | "Tilt-shift miniature"
    | "Snorricam character"
    | "Whip pan dynamics"
    | "Dutch angle tension"
    | "Underwater housing"
    | "Periscope lens";
  /**
   * Custom technical elements (optional) Default value: `""`
   */
  custom_elements?: string;
  /**
   * URL of an image to analyze and incorporate into the video prompt (optional)
   */
  image_url?: string | Blob | File;
  /**
   * Core concept or thematic input for the video prompt
   */
  input_concept: string;
  /**
   * Model to use Default value: `"google/gemini-2.0-flash-001"`
   */
  model?:
    | "anthropic/claude-3.5-sonnet"
    | "anthropic/claude-3-5-haiku"
    | "anthropic/claude-3-haiku"
    | "google/gemini-2.5-flash-lite"
    | "google/gemini-2.0-flash-001"
    | "meta-llama/llama-3.2-1b-instruct"
    | "meta-llama/llama-3.2-3b-instruct"
    | "meta-llama/llama-3.1-8b-instruct"
    | "meta-llama/llama-3.1-70b-instruct"
    | "openai/gpt-4o-mini"
    | "openai/gpt-4o"
    | "deepseek/deepseek-r1";
  /**
   * Pacing rhythm Default value: `"None"`
   */
  pacing?:
    | "None"
    | "Slow burn"
    | "Rhythmic pulse"
    | "Frantic energy"
    | "Ebb and flow"
    | "Hypnotic drift"
    | "Time-lapse rush"
    | "Stop-motion staccato"
    | "Gradual build"
    | "Quick cut rhythm"
    | "Long take meditation"
    | "Jump cut energy"
    | "Match cut flow"
    | "Cross-dissolve dreamscape"
    | "Parallel action"
    | "Slow motion impact"
    | "Ramping dynamics"
    | "Montage tempo"
    | "Continuous flow"
    | "Episodic breaks";
  /**
   * Length of the prompt Default value: `"Medium"`
   */
  prompt_length?: "Short" | "Medium" | "Long";
  /**
   * Special effects approach Default value: `"None"`
   */
  special_effects?:
    | "None"
    | "Practical effects"
    | "CGI enhancement"
    | "Analog glitches"
    | "Light painting"
    | "Projection mapping"
    | "Nanosecond exposures"
    | "Double exposure"
    | "Smoke diffusion"
    | "Lens flare artistry"
    | "Particle systems"
    | "Holographic overlay"
    | "Chromatic aberration"
    | "Digital distortion"
    | "Wire removal"
    | "Motion capture"
    | "Miniature integration"
    | "Weather simulation"
    | "Color grading"
    | "Mixed media composite"
    | "Neural style transfer";
  /**
   * Style of the video prompt Default value: `"Simple"`
   */
  style?:
    | "Minimalist"
    | "Simple"
    | "Detailed"
    | "Descriptive"
    | "Dynamic"
    | "Cinematic"
    | "Documentary"
    | "Animation"
    | "Action"
    | "Experimental";
};
export type VideoSoundEffectsGeneratorInput = {
  /**
   * A video file to analyze & re-sound with generated SFX.
   */
  video_url: Video;
};
export type VideoToAudioInput = {
  /**
   * Enable ASMR mode. This mode enhances detailed sound effects and is suitable for highly immersive content scenarios.
   */
  asmr_mode?: boolean;
  /**
   * Background music prompt. Cannot exceed 200 characters. Default value: `"intense car race"`
   */
  background_music_prompt?: string;
  /**
   * Sound effect prompt. Cannot exceed 200 characters. Default value: `"Car tires screech as they accelerate in a drag race"`
   */
  sound_effect_prompt?: string;
  /**
   * The video URL to extract audio from. Only .mp4/.mov formats are supported. File size does not exceed 100MB. Video duration between 3.0s and 20.0s.
   */
  video_url: string | Blob | File;
};
export type VideoToAudioOutput = {
  /**
   * The extracted/generated audio from the video in MP3 format
   */
  audio: File;
  /**
   * The original video with dubbed audio applied
   */
  video: File;
};
export type VideoToGifInput = {
  /**
   * Duration in seconds (max 15s to avoid memory issues, if not set uses 10s from start_time)
   */
  duration?: number;
  /**
   * Frames per second for the GIF Default value: `10`
   */
  fps?: number;
  /**
   * Quality level (affects color palette) Default value: `"medium"`
   */
  quality?: "low" | "medium" | "high";
  /**
   * Start time in seconds
   */
  start_time?: number;
  /**
   * URL of the video file to convert to GIF
   */
  video_url: string | Blob | File;
  /**
   * Output width in pixels (height auto-calculated, if not set defaults to max 800px to prevent large file sizes)
   */
  width?: number;
};
export type VideoToLottieInput = {
  /**
   * Maximum number of Lottie tokens to generate. Default value: `4096`
   */
  max_tokens?: number;
  /**
   * Optional text description guiding the conversion.
   */
  prompt?: string;
  /**
   * Sampling temperature for generation. Default value: `0.9`
   */
  temperature?: number;
  /**
   * Top-k sampling parameter. Default value: `5`
   */
  top_k?: number;
  /**
   * Nucleus sampling probability threshold. Default value: `0.25`
   */
  top_p?: number;
  /**
   * URL of the video to convert into a Lottie animation.
   */
  video_url: string | Blob | File;
};
export type VideoToVideoInput = {
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Prompt for the video-to-video generation.
   */
  prompt: string;
  /**
   * Seed for the video-to-video generation.
   */
  seed?: number;
  /**
   * Denoising strength for the video-to-video generation. 0.0 preserves the original, 1.0 completely remakes the video. Default value: `0.85`
   */
  strength?: number;
  /**
   * URL of the input video. Currently, only outputs of 16:9 aspect ratio and 480p resolution are supported. Video duration should be less than 1000 frames at 16fps, and output frames will be 6 plus a multiple of 12, for example 18, 30, 42, etc.
   */
  video_url: string | Blob | File;
};
export type VideoUnderstandingInput = {
  /**
   * Whether to request a more detailed analysis of the video
   */
  detailed_analysis?: boolean;
  /**
   * The question or prompt about the video content.
   */
  prompt: string;
  /**
   * URL of the video to analyze
   */
  video_url: string | Blob | File;
};
export type VideoUpscalerInput = {
  /**
   * The scale factor Default value: `2`
   */
  scale?: number;
  /**
   * The URL of the video to upscale
   */
  video_url: string | Blob | File;
};
export type ViduImageToVideoInput = {
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type ViduQ1ReferenceToVideoInput = {
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Whether to add background music to the generated video
   */
  bgm?: boolean;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URLs of the reference images to use for consistent subject appearance. Q1 model supports up to 7 reference images.
   */
  reference_image_urls: Array<string>;
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type ViduQ1TextToVideoInput = {
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of output video Default value: `"general"`
   */
  style?: "general" | "anime";
};
export type ViduQ2ImageToVideoProInput = {
  /**
   * Whether to add background music to the video (only for 4-second videos)
   */
  bgm?: boolean;
  /**
   * Duration of the video in seconds Default value: `"4"`
   */
  duration?: "2" | "3" | "4" | "5" | "6" | "7" | "8";
  /**
   * URL of the image to use as the ending frame. When provided, generates a transition video between start and end frames.
   */
  end_image_url?: string | Blob | File;
  /**
   * URL of the image to use as the starting frame
   */
  image_url: string | Blob | File;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Text prompt for video generation, max 3000 characters
   */
  prompt: string;
  /**
   * Output video resolution Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type ViduQ2ReferenceToVideoProInput = {
  /**
   * Aspect ratio of the output video (e.g., auto, 16:9, 9:16, 1:1, or any W:H) Default value: `"16:9"`
   */
  aspect_ratio?: string;
  /**
   * Whether to add background music to the generated video
   */
  bgm?: boolean;
  /**
   * Duration of the video in seconds (0 for automatic duration) Default value: `4`
   */
  duration?: number;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Text prompt for video generation, max 2000 characters
   */
  prompt: string;
  /**
   * URLs of the reference images for subject appearance. If videos are provided, up to 4 images are allowed; otherwise up to 7 images.
   */
  reference_image_urls?: Array<string>;
  /**
   * URLs of the reference videos for video editing or motion reference. Supports up to 2 videos.
   */
  reference_video_urls?: Array<string>;
  /**
   * Output video resolution Default value: `"720p"`
   */
  resolution?: "540p" | "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type ViduQ2TextToImageInput = {
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type ViduQ2TextToVideoInput = {
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Whether to add background music to the video (only for 4-second videos)
   */
  bgm?: boolean;
  /**
   * Duration of the video in seconds Default value: `"4"`
   */
  duration?: "2" | "3" | "4" | "5" | "6" | "7" | "8";
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Text prompt for video generation, max 3000 characters
   */
  prompt: string;
  /**
   * Output video resolution Default value: `"720p"`
   */
  resolution?: "360p" | "520p" | "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type ViduQ2VideoExtensionProInput = {
  /**
   * Duration of the extension in seconds Default value: `"4"`
   */
  duration?: "2" | "3" | "4" | "5" | "6" | "7";
  /**
   * text prompt to guide the video extension
   */
  prompt?: string;
  /**
   * Output video resolution Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * URL of the video to extend
   */
  video_url: string | Blob | File;
};
export type ViduQ3ImageToVideoInput = {
  /**
   * Whether to use direct audio-video generation. When true, outputs video with sound (including dialogue and sound effects). Default value: `true`
   */
  audio?: boolean;
  /**
   * Duration of the video in seconds (1-16 for Q3 models) Default value: `5`
   */
  duration?: number;
  /**
   * URL of the image to use as the ending frame. When provided, generates a transition video between start and end frames.
   */
  end_image_url?: string | Blob | File;
  /**
   * URL or base64 image to use as the starting frame
   */
  image_url: string | Blob | File;
  /**
   * Text prompt for video generation, max 2000 characters Default value: `""`
   */
  prompt?: string;
  /**
   * Output video resolution. Note: 360p is not available when end_image_url is provided. Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type ViduQ3TextToVideoInput = {
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "1:1";
  /**
   * Whether to use direct audio-video generation. When true, outputs video with sound. Default value: `true`
   */
  audio?: boolean;
  /**
   * Duration of the video in seconds Default value: `5`
   */
  duration?: number;
  /**
   * Text prompt for video generation, max 2000 characters
   */
  prompt: string;
  /**
   * Output video resolution Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type ViduReferenceToImageInput = {
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URLs of the reference images to use for consistent subject appearance
   */
  reference_image_urls: Array<string>;
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type ViduReferenceToVideoInput = {
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URLs of the reference images to use for consistent subject appearance
   */
  reference_image_urls: Array<string>;
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type ViduStartEndToVideoInput = {
  /**
   * URL of the image to use as the last frame
   */
  end_image_url: string | Blob | File;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  start_image_url: string | Blob | File;
};
export type ViduTemplateToVideoInput = {
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * URLs of the images to use with the template. Number of images required varies by template: 'dynasty_dress' and 'shop_frame' accept 1-2 images, 'wish_sender' requires exactly 3 images, all other templates accept only 1 image.
   */
  input_image_urls: Array<string>;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * AI video template to use. Pricing varies by template: Standard templates (hug, kiss, love_pose, etc.) cost 4 credits ($0.20), Premium templates (lunar_newyear, dynasty_dress, dreamy_wedding, etc.) cost 6 credits ($0.30), and Advanced templates (live_photo) cost 10 credits ($0.50). Default value: `"hug"`
   */
  template?:
    | "dreamy_wedding"
    | "romantic_lift"
    | "sweet_proposal"
    | "couple_arrival"
    | "cupid_arrow"
    | "pet_lovers"
    | "lunar_newyear"
    | "hug"
    | "kiss"
    | "dynasty_dress"
    | "wish_sender"
    | "love_pose"
    | "hair_swap"
    | "youth_rewind"
    | "morphlab"
    | "live_photo"
    | "emotionlab"
    | "live_memory"
    | "interaction"
    | "christmas"
    | "pet_finger"
    | "eat_mushrooms"
    | "beast_chase_library"
    | "beast_chase_supermarket"
    | "petal_scattered"
    | "emoji_figure"
    | "hair_color_change"
    | "multiple_people_kissing"
    | "beast_chase_amazon"
    | "beast_chase_mountain"
    | "balloonman_explodes_pro"
    | "get_thinner"
    | "jump2pool"
    | "bodyshake"
    | "jiggle_up"
    | "shake_it_dance"
    | "subject_3"
    | "pubg_winner_hit"
    | "shake_it_down"
    | "blueprint_supreme"
    | "hip_twist"
    | "motor_dance"
    | "rat_dance"
    | "kwok_dance"
    | "leg_sweep_dance"
    | "heeseung_march"
    | "shake_to_max"
    | "dame_un_grrr"
    | "i_know"
    | "lit_bounce"
    | "wave_dance"
    | "chill_dance"
    | "hip_flicking"
    | "sakura_season"
    | "zongzi_wrap"
    | "zongzi_drop"
    | "dragonboat_shot"
    | "rain_kiss"
    | "child_memory"
    | "couple_drop"
    | "couple_walk"
    | "flower_receive"
    | "love_drop"
    | "cheek_kiss"
    | "carry_me"
    | "blow_kiss"
    | "love_fall"
    | "french_kiss_8s"
    | "workday_feels"
    | "love_story"
    | "bloom_magic"
    | "ghibli"
    | "minecraft"
    | "box_me"
    | "claw_me"
    | "clayshot"
    | "manga_meme"
    | "quad_meme"
    | "pixel_me"
    | "clayshot_duo"
    | "irasutoya"
    | "american_comic"
    | "simpsons_comic"
    | "yayoi_kusama_style"
    | "pop_art"
    | "jojo_style"
    | "slice_therapy"
    | "balloon_flyaway"
    | "flying"
    | "paperman"
    | "pinch"
    | "bloom_doorobear"
    | "gender_swap"
    | "nap_me"
    | "sexy_me"
    | "spin360"
    | "smooth_shift"
    | "paper_fall"
    | "jump_to_cloud"
    | "pilot"
    | "sweet_dreams"
    | "soul_depart"
    | "punch_hit"
    | "watermelon_hit"
    | "split_stance_pet"
    | "make_face"
    | "break_glass"
    | "split_stance_human"
    | "covered_liquid_metal"
    | "fluffy_plunge"
    | "pet_belly_dance"
    | "water_float"
    | "relax_cut"
    | "head_to_balloon"
    | "cloning"
    | "across_the_universe_jungle"
    | "clothes_spinning_remnant"
    | "across_the_universe_jurassic"
    | "across_the_universe_moon"
    | "fisheye_pet"
    | "hitchcock_zoom"
    | "cute_bangs"
    | "earth_zoom_out"
    | "fisheye_human"
    | "drive_yacht"
    | "virtual_singer"
    | "earth_zoom_in"
    | "aliens_coming"
    | "drive_ferrari"
    | "bjd_style"
    | "virtual_fitting"
    | "orbit"
    | "zoom_in"
    | "ai_outfit"
    | "spin180"
    | "orbit_dolly"
    | "orbit_dolly_fast"
    | "auto_spin"
    | "walk_forward"
    | "outfit_show"
    | "zoom_in_fast"
    | "zoom_out_image"
    | "zoom_out_startend"
    | "muscling"
    | "captain_america"
    | "hulk"
    | "cap_walk"
    | "hulk_dive"
    | "exotic_princess"
    | "beast_companion"
    | "cartoon_doll"
    | "golden_epoch"
    | "oscar_gala"
    | "fashion_stride"
    | "star_carpet"
    | "flame_carpet"
    | "frost_carpet"
    | "mecha_x"
    | "style_me"
    | "tap_me"
    | "saber_warrior"
    | "pet2human"
    | "graduation"
    | "fishermen"
    | "happy_birthday"
    | "fairy_me"
    | "ladudu_me"
    | "ladudu_me_random"
    | "squid_game"
    | "superman"
    | "grow_wings"
    | "clevage"
    | "fly_with_doraemon"
    | "creatice_product_down"
    | "pole_dance"
    | "hug_from_behind"
    | "creatice_product_up_cybercity"
    | "creatice_product_up_bluecircuit"
    | "creatice_product_up"
    | "run_fast"
    | "background_explosion";
};
export type VignetteInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Vignette strength Default value: `0.5`
   */
  vignette_strength?: number;
};
export type VirtualTryonInput = {
  /**
   * Acceleration level for image generation. 'regular' balances speed and quality. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale. Controls how closely the model follows the prompt. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. If not provided, the size of the input image will be used.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The URLs of the images for virtual try-on. Provide person image and clothing image.
   */
  image_urls: Array<string>;
  /**
   * The strength of the virtual try-on effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The format of the output image Default value: `"png"`
   */
  output_format?: "png" | "jpeg" | "webp";
  /**
   * The prompt to generate a virtual try-on image.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. Same seed with same prompt will produce same result.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and won't be saved in history.
   */
  sync_mode?: boolean;
};
export type VirtualTryOnInput = {
  /**
   * Aspect ratio for 4K output (default: 3:4 for fashion)
   */
  aspect_ratio?: AspectRatio;
  /**
   * Clothing photo URL
   */
  clothing_image_url: string | Blob | File;
  /**
   * Person photo URL
   */
  person_image_url: string | Blob | File;
  /**
   *  Default value: `true`
   */
  preserve_pose?: boolean;
};
export type VisionInput = {
  /**
   * Image URL to be processed
   */
  image_url: string | Blob | File;
  /**
   * Prompt to be used for the image
   */
  prompt: string;
  /**
   * Response style to be used for the image.
   *
   * - text: Model will output text. Good for descriptions and captioning.
   * - box: Model will output a combination of text and bounding boxes. Good for
   * localization.
   * - point: Model will output a combination of text and points. Good for counting many
   * objects.
   * - polygon: Model will output a combination of text and polygons. Good for granular
   * segmentation. Default value: `"text"`
   */
  response_style?: "text" | "box" | "point" | "polygon";
};
export type VoiceCloneOutput = {
  /**
   * Preview audio generated with the cloned voice (if requested)
   */
  audio?: File;
  /**
   * The cloned voice ID for use with TTS
   */
  custom_voice_id: string;
};
export type VoiceDesignOutput = {
  /**
   * The preview audio using the generated voice
   */
  audio: File;
  /**
   * The voice_id of the generated voice
   */
  custom_voice_id: string;
};
export type VoiceModify = {
  /**
   * Intensity/energy of the voice. Range: -100 to 100. Higher values create more energetic speech.
   */
  intensity?: number;
  /**
   * Pitch adjustment in semitones. Range: -100 to 100. Positive values raise pitch, negative values lower it.
   */
  pitch?: number;
  /**
   * Timbre adjustment. Range: -100 to 100. Affects the tonal quality of the voice.
   */
  timbre?: number;
};
export type VoicePreview = {
  /**
   * The generated voice
   */
  audio: File;
  /**
   * Duration of the generated audio in seconds
   */
  duration_seconds: number;
  /**
   * The ID of the generated voice
   */
  generated_voice_id: string;
  /**
   * The language of the preview
   */
  language?: string;
  /**
   * The media type of the generated audio
   */
  media_type: string;
};
export type VoiceSetting = {
  /**
   * Emotion of the generated speech
   */
  emotion?:
    | "happy"
    | "sad"
    | "angry"
    | "fearful"
    | "disgusted"
    | "surprised"
    | "neutral";
  /**
   * Enables English text normalization to improve number reading performance, with a slight increase in latency
   */
  english_normalization?: boolean;
  /**
   * Voice pitch (-12 to 12)
   */
  pitch?: number;
  /**
   * Speech speed (0.5-2.0) Default value: `1`
   */
  speed?: number;
  /**
   * Predefined voice ID to use for synthesis Default value: `"Wise_Woman"`
   */
  voice_id?: string;
  /**
   * Volume (0-10) Default value: `1`
   */
  vol?: number;
};
export type VTONInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Url to the garment image.
   */
  garment_image_url: string | Blob | File;
  /**
   * The type of the garment used for virtual try-on.
   */
  garment_type: "upper_body" | "lower_body" | "dresses";
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your input when generating the image. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * Url for the human image.
   */
  human_image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The same seed and the same input given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type VTONOutput = {
  /**
   * Whether the image contains NSFW concepts.
   */
  has_nsfw_concepts: boolean;
  /**
   * The output image.
   */
  image: Image;
  /**
   * The seed for the inference.
   */
  seed: number;
};
export type Wan22ImageTrainerInput = {
  /**
   * Whether to include synthetic captions.
   */
  include_synthetic_captions?: boolean;
  /**
   * Whether the training data is style data. If true, face specific options like masking and face detection will be disabled.
   */
  is_style?: boolean;
  /**
   * Learning rate for training. Default value: `0.0007`
   */
  learning_rate?: number;
  /**
   * Number of training steps. Default value: `1000`
   */
  steps?: number;
  /**
   * URL to the training data.
   */
  training_data_url: string | Blob | File;
  /**
   * Trigger phrase for the model.
   */
  trigger_phrase: string;
  /**
   * Whether to use face cropping for the training data. When enabled, images will be cropped to the face before resizing.
   */
  use_face_cropping?: boolean;
  /**
   * Whether to use face detection for the training data. When enabled, images will use the center of the face as the center of the image when resizing. Default value: `true`
   */
  use_face_detection?: boolean;
  /**
   * Whether to use masks for the training data. Default value: `true`
   */
  use_masks?: boolean;
};
export type Wan22ImageTrainerOutput = {
  /**
   * Config file helping inference endpoints after training.
   */
  config_file: File;
  /**
   * Low noise LoRA file.
   */
  diffusers_lora_file: File;
  /**
   * High noise LoRA file.
   */
  high_noise_lora: File;
};
export type Wan25PreviewImageToImageInput = {
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Width and height must be between 384 and 1440 pixels. Default value: `square`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URLs of images to edit. For single-image editing, provide 1 URL. For multi-reference generation, provide up to 2 URLs. If more than 2 URLs are provided, only the first 2 will be used.
   */
  image_urls: Array<string>;
  /**
   * Negative prompt to describe content to avoid. Max 500 characters.
   */
  negative_prompt?: string;
  /**
   * Number of images to generate. Values from 1 to 4. Default value: `1`
   */
  num_images?: number;
  /**
   * The text prompt describing how to edit the image. Max 2000 characters.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type Wan25PreviewImageToVideoInput = {
  /**
   * URL of the audio to use as the background music. Must be publicly accessible.
   * Limit handling: If the audio duration exceeds the duration value (5 or 10 seconds),
   * the audio is truncated to the first 5 or 10 seconds, and the rest is discarded. If
   * the audio is shorter than the video, the remaining part of the video will be silent.
   * For example, if the audio is 3 seconds long and the video duration is 5 seconds, the
   * first 3 seconds of the output video will have sound, and the last 2 seconds will be silent.
   * - Format: WAV, MP3.
   * - Duration: 3 to 30 s.
   * - File size: Up to 15 MB.
   */
  audio_url?: string | Blob | File;
  /**
   * Duration of the generated video in seconds. Choose between 5 or 10 seconds. Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * Whether to enable prompt rewriting using LLM. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the image to use as the first frame. Must be publicly accessible or base64 data URI.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to describe content to avoid. Max 500 characters.
   */
  negative_prompt?: string;
  /**
   * The text prompt describing the desired video motion. Max 800 characters.
   */
  prompt: string;
  /**
   * Video resolution. Valid values: 480p, 720p, 1080p Default value: `"1080p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type Wan25PreviewTextToImageInput = {
  /**
   * Whether to enable prompt rewriting using LLM. Improves results for short prompts but increases processing time. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Can use preset names like 'square', 'landscape_16_9', etc., or specific dimensions. Total pixels must be between 768×768 and 1440×1440, with aspect ratio between [1:4, 4:1]. Default value: `square`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Negative prompt to describe content to avoid. Max 500 characters.
   */
  negative_prompt?: string;
  /**
   * Number of images to generate. Values from 1 to 4. Default value: `1`
   */
  num_images?: number;
  /**
   * The prompt for image generation. Supports Chinese and English, max 2000 characters.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type Wan27ImageToVideoInput = {
  /**
   * URL of driving audio. Supports WAV and MP3. Duration: 2-30s. Max 15 MB.
   */
  audio_url?: string | Blob | File;
  /**
   * Output video duration in seconds (2-15). Default value: `"5"`
   */
  duration?:
    | "2"
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Enable intelligent prompt rewriting. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the last frame image for first-and-last-frame-to-video. Same constraints as image_url.
   */
  end_image_url?: string | Blob | File;
  /**
   * URL of the first frame image. Formats: JPEG, JPG, PNG, BMP, WEBP. Max 20 MB.
   */
  image_url?: string | Blob | File;
  /**
   * Content to avoid in the video. Max 500 characters.
   */
  negative_prompt?: string;
  /**
   * Text prompt describing the desired video. Max 5000 characters.
   */
  prompt?: string;
  /**
   * Output video resolution tier. Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility (0-2147483647).
   */
  seed?: number;
  /**
   * URL of a video clip to continue from. Format: MP4, MOV. Duration: 2-10s. Max 100 MB. Cannot be combined with image_url.
   */
  video_url?: string | Blob | File;
};
export type Wan27ReferenceToVideoInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:3" | "3:4";
  /**
   * Output video duration in seconds (2-10). Default value: `"5"`
   */
  duration?: "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10";
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * When true, enables intelligent multi-shot segmentation. When false (default), generates a single continuous shot.
   */
  multi_shots?: boolean;
  /**
   * Content to avoid in the video. Max 500 characters.
   */
  negative_prompt?: string;
  /**
   * Text prompt describing the desired video. Max 5000 characters.
   */
  prompt: string;
  /**
   * Reference image URLs for character/object appearance. Pass multiple images for multi-subject generation. Max 20 MB each.
   */
  reference_image_urls?: Array<string>;
  /**
   * Reference video URLs for character/object appearance and motion. Pass multiple videos for multi-subject generation. Max 100 MB each.
   */
  reference_video_urls?: Array<string>;
  /**
   * Output video resolution tier. Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility (0-2147483647).
   */
  seed?: number;
};
export type Wan27TextToImageInput = {
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output image size. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Maximum number of images to generate (1-5). Default value: `1`
   */
  max_images?: number;
  /**
   * Content to avoid in the generated image. Max 500 characters. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Text prompt describing the desired output. Supports Chinese and English.
   */
  prompt: string;
  /**
   * Random seed for reproducibility (0-2147483647).
   */
  seed?: number;
};
export type Wan27TextToVideoInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:3" | "3:4";
  /**
   * URL of driving audio. Supports WAV and MP3. Duration: 3-30s. Max 15 MB. If not provided, the model auto-generates matching background music.
   */
  audio_url?: string | Blob | File;
  /**
   * Output video duration in seconds (2-15). Default value: `"5"`
   */
  duration?:
    | "2"
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "11"
    | "12"
    | "13"
    | "14"
    | "15";
  /**
   * Enable intelligent prompt rewriting. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Content to avoid in the video. Max 500 characters.
   */
  negative_prompt?: string;
  /**
   * Text prompt describing the desired video. Max 5000 characters.
   */
  prompt: string;
  /**
   * Output video resolution tier. Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility (0-2147483647).
   */
  seed?: number;
};
export type Wan27VideoEditInput = {
  /**
   * Aspect ratio of the generated video. If not provided, uses the aspect ratio of the input video.
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:3" | "3:4";
  /**
   * Audio handling. 'auto': model decides whether to regenerate audio. 'origin': preserve original audio from input video. Default value: `"auto"`
   */
  audio_setting?: "auto" | "origin";
  /**
   * Output video duration in seconds. Default 0 means match input video duration. When set (2-10), truncates from the start. Default value: `"0"`
   */
  duration?: "0" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10";
  /**
   * Enable content moderation for input and output. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Editing instruction or style transfer description.
   */
  prompt: string;
  /**
   * Reference image URL for reference-based editing.
   */
  reference_image_url?: string | Blob | File;
  /**
   * Output video resolution tier. Default value: `"1080p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Random seed for reproducibility (0-2147483647).
   */
  seed?: number;
  /**
   * URL of the input video to edit. Format: MP4, MOV. Duration: 2-10s. Max 100 MB.
   *
   * Max file size: 100.0MB, Min duration: 2s, Max duration: 10s, Timeout: 30.0s
   */
  video_url: string | Blob | File;
};
export type WanAlphaInput = {
  /**
   * The aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * Whether to binarize the mask.
   */
  binarize_mask?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to enable safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The frame rate of the generated video. Default value: `16`
   */
  fps?: number;
  /**
   * The threshold for mask binarization. When binarize_mask is True, this threshold will be used to binarize the mask. This will also be used for transparency when the output type is `.webm`. Default value: `0.8`
   */
  mask_binarization_threshold?: number;
  /**
   * The lower bound of the mask clamping. Default value: `0.1`
   */
  mask_clamp_lower?: number;
  /**
   * The upper bound of the mask clamping. Default value: `0.75`
   */
  mask_clamp_upper?: number;
  /**
   * The number of frames to generate. Default value: `81`
   */
  num_frames?: number;
  /**
   * The number of inference steps to use. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The prompt to guide the video generation.
   */
  prompt: string;
  /**
   * The resolution of the generated video. Default value: `"480p"`
   */
  resolution?: "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * The sampler to use. Default value: `"euler"`
   */
  sampler?: "unipc" | "dpm++" | "euler";
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * The shift of the generated video. Default value: `10.5`
   */
  shift?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The output type of the generated video. Default value: `"VP9 (.webm)"`
   */
  video_output_type?:
    | "X264 (.mp4)"
    | "VP9 (.webm)"
    | "PRORES4444 (.mov)"
    | "GIF (.gif)";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanAlphaOutput = {
  /**
   * The generated image file.
   */
  image?: VideoFile;
  /**
   * The generated mask file.
   */
  mask?: VideoFile;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video file.
   */
  video?: VideoFile;
};
export type WanAtiInput = {
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, 720p). Default value: `"480p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Motion tracks to guide video generation. Each track is a sequence of points defining a motion trajectory. Multiple tracks can control different elements or objects in the video. Expected format: array of tracks, where each track is an array of points with 'x' and 'y' coordinates (up to 121 points per track). Points will be automatically padded to 121 if fewer are provided. Coordinates should be within the image dimensions.
   */
  track: Array<Array<TrackPoint>>;
};
export type WanEffectsInput = {
  /**
   * Aspect ratio of the output video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The type of effect to apply to the video. Default value: `"cakeify"`
   */
  effect_type?:
    | "squish"
    | "muscle"
    | "inflate"
    | "crush"
    | "rotate"
    | "gun-shooting"
    | "deflate"
    | "cakeify"
    | "hulk"
    | "baby"
    | "bride"
    | "classy"
    | "puppy"
    | "snow-white"
    | "disney-princess"
    | "mona-lisa"
    | "painting"
    | "pirate-captain"
    | "princess"
    | "jungle"
    | "samurai"
    | "vip"
    | "warrior"
    | "zen"
    | "assassin"
    | "timelapse"
    | "tsunami"
    | "fire"
    | "zoom-call"
    | "doom-fps"
    | "fus-ro-dah"
    | "hug-jesus"
    | "robot-face-reveal"
    | "super-saiyan"
    | "jumpscare"
    | "laughing"
    | "cartoon-jaw-drop"
    | "crying"
    | "kissing"
    | "angry-face"
    | "selfie-younger-self"
    | "animeify"
    | "blast";
  /**
   * Frames per second of the generated video. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * The scale of the LoRA weight. Used to adjust effect intensity. Default value: `1`
   */
  lora_scale?: number;
  /**
   * Number of frames to generate. Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * The subject to insert into the predefined prompt template for the selected effect.
   */
  subject: string;
  /**
   * Whether to use turbo mode. If True, the video will be generated faster but with lower quality.
   */
  turbo_mode?: boolean;
};
export type WanFlf2vInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the ending image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  end_image_url: string | Blob | File;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `5`
   */
  guide_scale?: number;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * URL of the starting image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  start_image_url: string | Blob | File;
};
export type WanFunControlInput = {
  /**
   * The URL of the control video to use as a reference for the video generation.
   */
  control_video_url: string | Blob | File;
  /**
   * The fps to generate. Only used when match_input_fps is False. Default value: `16`
   */
  fps?: number;
  /**
   * The guidance scale. Default value: `6`
   */
  guidance_scale?: number;
  /**
   * Whether to match the fps in the input video. Default value: `true`
   */
  match_input_fps?: boolean;
  /**
   * Whether to match the number of frames in the input video. Default value: `true`
   */
  match_input_num_frames?: boolean;
  /**
   * The negative prompt to generate the video. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of frames to generate. Only used when match_input_num_frames is False. Default value: `81`
   */
  num_frames?: number;
  /**
   * The number of inference steps. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * The type of preprocess to apply to the video. Only used when preprocess_video is True. Default value: `"depth"`
   */
  preprocess_type?: "depth" | "pose";
  /**
   * Whether to preprocess the video. If True, the video will be preprocessed to depth or pose.
   */
  preprocess_video?: boolean;
  /**
   * The prompt to generate the video.
   */
  prompt: string;
  /**
   * The URL of the reference image to use as a reference for the video generation.
   */
  reference_image_url?: string | Blob | File;
  /**
   * The seed for the random number generator.
   */
  seed?: number;
  /**
   * The shift for the scheduler. Default value: `5`
   */
  shift?: number;
};
export type WanI2vInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `5`
   */
  guide_scale?: number;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
};
export type WanI2vLoraInput = {
  /**
   * Aspect ratio of the output video. Default value: `"16:9"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `5`
   */
  guide_scale?: number;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoraWeight>;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality. Default value: `true`
   */
  turbo_mode?: boolean;
};
export type WanMotionInput = {
  /**
   * Acceleration level to use. 'regular' enables caching for faster generation, 'none' disables it. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Adapts the driving video's motion to match the reference image's body proportions. Recommended when the driving video subject and reference image have different body shapes or sizes. Default value: `true`
   */
  adapt_motion?: boolean;
  /**
   * If set to true, input and output will be checked for safety. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Enhances identity preservation by preprocessing the reference image with Flux Kontext Edit before animation. Produces more faithful face and appearance transfer at the cost of slightly longer processing time.
   */
  enhance_identity?: boolean;
  /**
   * URL of the reference image (provides the character appearance).
   */
  image_url: string | Blob | File;
  /**
   * Optional text prompt to guide the generation. Default value: `""`
   */
  prompt?: string;
  /**
   * Random seed for reproducibility.
   */
  seed?: number;
  /**
   * URL of the driving video (provides the motion).
   */
  video_url: string | Blob | File;
};
export type WanMotionOutput = {
  /**
   * The prompt used for generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video file.
   */
  video: File;
};
export type WanMoveInput = {
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt to guide the video generation. Default value: `"色调艳丽，过曝，静态，细节模糊不清，字幕，风格，作品，画作，画面，静止，整体发灰，最差质量，低质量，JPEG压缩残留，丑陋的，残缺的，多余的手指，画得不好的手部，画得不好的脸部，畸形的，毁容的，形态畸形的肢体，手指融合，静止不动的画面，杂乱的背景，三条腿，背景人很多，倒着走"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Text prompt to guide the video generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * A list of trajectories. Each trajectory list means the movement of one object.
   */
  trajectories: Array<Array<TrajectoryPoint>>;
};
export type WanProImageToVideoInput = {
  /**
   * Whether to enable the safety checker Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image to generate the video from
   */
  image_url: string | Blob | File;
  /**
   * The prompt to generate the video
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type WanProTextToVideoInput = {
  /**
   * Whether to enable the safety checker Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The prompt to generate the video
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
};
export type WanT2vInput = {
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality.
   */
  turbo_mode?: boolean;
};
export type WanT2vLoraInput = {
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoraWeight>;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"480p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality. Default value: `true`
   */
  turbo_mode?: boolean;
};
export type WanTrainerInput = {
  /**
   * If true, the input will be automatically scale the video to 81 frames at 16fps.
   */
  auto_scale_input?: boolean;
  /**
   * The rate at which the model learns. Higher values can lead to faster training, but over-fitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * The number of steps to train for. Default value: `400`
   */
  number_of_steps?: number;
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images and/or videos, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image/video file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * The phrase that will trigger the model to generate an image. Default value: `""`
   */
  trigger_phrase?: string;
};
export type WanTrainerOutput = {
  /**
   * Configuration used for setting up the inference endpoints.
   */
  config_file: File;
  /**
   * URL to the trained LoRA weights.
   */
  lora_file: File;
};
export type WanV2214bAnimateMoveInput = {
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `20`
   */
  num_inference_steps?: number;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"480p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * If true, also return a ZIP archive containing per-frame images generated on GPU (lossless).
   */
  return_frames_zip?: boolean;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * If true, applies quality enhancement for faster generation with improved quality. When enabled, parameters are automatically optimized for best results.
   */
  use_turbo?: boolean;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL of the input video.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV2214bAnimateMoveOutput = {
  /**
   * ZIP archive of generated frames (if requested).
   */
  frames_zip?: File;
  /**
   * The prompt used for generation (auto-generated by the model)
   */
  prompt: string;
  /**
   * The seed used for generation
   */
  seed: number;
  /**
   * The generated video file.
   */
  video: File;
};
export type WanV2214bSpeechToVideoInput = {
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 40 to 120, (must be multiple of 4). Default value: `80`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * The text prompt used for video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"480p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV225bImageToVideoInput = {
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `24`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 17 to 161 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4.
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (580p or 720p). Default value: `"720p"`
   */
  resolution?: "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV225bTextToImageInput = {
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The format of the output image. Default value: `"jpeg"`
   */
  image_format?: "png" | "jpeg";
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide image generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the image. Must be between 1.0 and 10.0. Default value: `2`
   */
  shift?: number;
};
export type WanV225bTextToVideoDistillInput = {
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `24`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to generate. Must be between 17 to 161 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4.
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (580p or 720p). Default value: `"720p"`
   */
  resolution?: "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV225bTextToVideoFastWanInput = {
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `24`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 17 to 161 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4.
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (580p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV225bTextToVideoInput = {
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `24`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 17 to 161 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4.
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (580p or 720p). Default value: `"720p"`
   */
  resolution?: "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV22A14bImageToImageInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Aspect ratio of the generated image. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * The format of the output image. Default value: `"jpeg"`
   */
  image_format?: "png" | "jpeg";
  /**
   *
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide image generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   *  Default value: `2`
   */
  shift?: number;
  /**
   * Denoising strength. 1.0 = fully remake; 0.0 = preserve original. Default value: `0.5`
   */
  strength?: number;
};
export type WanV22A14bImageToImageOutput = {
  /**
   * The generated image file.
   */
  image: File;
  /**
   * The text prompt used for image generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bImageToVideoLoraInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the end image.
   */
  end_image_url?: string | Blob | File;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoRAWeight>;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 17 to 161 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4. Default value: `1`
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV22A14bImageToVideoTurboInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the end image.
   */
  end_image_url?: string | Blob | File;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV22A14bTextToImageInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide image generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the image. Must be between 1.0 and 10.0. Default value: `2`
   */
  shift?: number;
};
export type WanV22A14bTextToImageLoraInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * The format of the output image. Default value: `"jpeg"`
   */
  image_format?: "png" | "jpeg";
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoRAWeight>;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * The text prompt to guide image generation.
   */
  prompt: string;
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the image. Must be between 1.0 and 10.0. Default value: `2`
   */
  shift?: number;
};
export type WanV22A14bTextToVideoInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 17 to 161 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4. Default value: `1`
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV22A14bTextToVideoLoraInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoRAWeight>;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 17 to 161 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4. Default value: `1`
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV22A14bTextToVideoTurboInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanV22A14bVideoToVideoInput = {
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, output video will be checked for safety after generation.
   */
  enable_output_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 17 to 161 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4. Default value: `1`
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * If true, the video will be resampled to the passed frames per second. If false, the video will not be resampled.
   */
  resample_fps?: boolean;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * Strength of the video transformation. A value of 1.0 means the output will be completely based on the prompt, while a value of 0.0 means the output will be identical to the input video. Default value: `0.9`
   */
  strength?: number;
  /**
   * The quality of the output video. Higher quality means better visual quality but larger file size. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL of the input video.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the output video. Faster write mode means faster results but larger file size, balanced write mode is a good compromise between speed and quality, and small write mode is the slowest but produces the smallest file size. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanVace14bInpaintingInput = {
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster. Default value: `regular`
   */
  acceleration?: "none" | "low" | "regular";
  /**
   * Aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * The minimum frames per second to downsample the video to. This is used to help determine the auto downsample factor to try and find the lowest detail-preserving downsample factor. The default value is appropriate for most videos, if you are using a video with very fast motion, you may need to increase this value. If your video has a very low amount of motion, you could decrease this value to allow for higher downsampling and thus longer sequences. Default value: `15`
   */
  auto_downsample_min_fps?: number;
  /**
   * If true, the model will automatically temporally downsample the video to an appropriate frame length for the model, then will interpolate it back to the original frame length.
   */
  enable_auto_downsample?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * URL to the first frame of the video. If provided, the model will use this frame as a reference.
   */
  first_frame_url?: string | Blob | File;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. Options are 'rife' or 'film'. Default value: `"film"`
   */
  interpolator_model?: "rife" | "film";
  /**
   * URL to the last frame of the video. If provided, the model will use this frame as a reference.
   */
  last_frame_url?: string | Blob | File;
  /**
   * URL to the guiding mask file. If provided, the model will use this mask as a reference to create masked video using salient mask tracking. Will be ignored if mask_video_url is provided.
   */
  mask_image_url?: string | Blob | File;
  /**
   * URL to the source mask file. Required for inpainting.
   */
  mask_video_url?: string | Blob | File;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between the original frames. A value of 0 means no interpolation.
   */
  num_interpolated_frames?: number;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Urls to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * Resolution of the generated video. Default value: `"auto"`
   */
  resolution?: "auto" | "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * If true, also return a ZIP file containing all generated frames.
   */
  return_frames_zip?: boolean;
  /**
   * Sampler to use for video generation. Default value: `"unipc"`
   */
  sampler?: "unipc" | "dpm++" | "euler";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Temporal downsample factor for the video. This is an integer value that determines how many frames to skip in the video. A value of 0 means no downsampling. For each downsample factor, one upsample factor will automatically be applied.
   */
  temporal_downsample_factor?: number;
  /**
   * The transparency mode to apply to the first and last frames. This controls how the transparent areas of the first and last frames are filled. Default value: `"content_aware"`
   */
  transparency_mode?: "content_aware" | "white" | "black";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL to the source video file. Required for inpainting.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanVace14bInput = {
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster. Default value: `regular`
   */
  acceleration?: "none" | "low" | "regular";
  /**
   * Aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * The minimum frames per second to downsample the video to. This is used to help determine the auto downsample factor to try and find the lowest detail-preserving downsample factor. The default value is appropriate for most videos, if you are using a video with very fast motion, you may need to increase this value. If your video has a very low amount of motion, you could decrease this value to allow for higher downsampling and thus longer sequences. Default value: `15`
   */
  auto_downsample_min_fps?: number;
  /**
   * If true, the model will automatically temporally downsample the video to an appropriate frame length for the model, then will interpolate it back to the original frame length.
   */
  enable_auto_downsample?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * URL to the first frame of the video. If provided, the model will use this frame as a reference.
   */
  first_frame_url?: string | Blob | File;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. Options are 'rife' or 'film'. Default value: `"film"`
   */
  interpolator_model?: "rife" | "film";
  /**
   * URL to the last frame of the video. If provided, the model will use this frame as a reference.
   */
  last_frame_url?: string | Blob | File;
  /**
   * URL to the guiding mask file. If provided, the model will use this mask as a reference to create masked video. If provided mask video url will be ignored.
   */
  mask_image_url?: string | Blob | File;
  /**
   * URL to the source mask file. If provided, the model will use this mask as a reference.
   */
  mask_video_url?: string | Blob | File;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between the original frames. A value of 0 means no interpolation.
   */
  num_interpolated_frames?: number;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * URLs to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * Resolution of the generated video. Default value: `"auto"`
   */
  resolution?: "auto" | "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * If true, also return a ZIP file containing all generated frames.
   */
  return_frames_zip?: boolean;
  /**
   * Sampler to use for video generation. Default value: `"unipc"`
   */
  sampler?: "unipc" | "dpm++" | "euler";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Task type for the model. Default value: `"depth"`
   */
  task?: "depth" | "pose" | "inpainting" | "outpainting" | "reframe";
  /**
   * Temporal downsample factor for the video. This is an integer value that determines how many frames to skip in the video. A value of 0 means no downsampling. For each downsample factor, one upsample factor will automatically be applied.
   */
  temporal_downsample_factor?: number;
  /**
   * The transparency mode to apply to the first and last frames. This controls how the transparent areas of the first and last frames are filled. Default value: `"content_aware"`
   */
  transparency_mode?: "content_aware" | "white" | "black";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL to the source video file. If provided, the model will use this video as a reference.
   */
  video_url?: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanVace14bOutpaintingInput = {
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster. Default value: `regular`
   */
  acceleration?: "none" | "low" | "regular";
  /**
   * Aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * The minimum frames per second to downsample the video to. This is used to help determine the auto downsample factor to try and find the lowest detail-preserving downsample factor. The default value is appropriate for most videos, if you are using a video with very fast motion, you may need to increase this value. If your video has a very low amount of motion, you could decrease this value to allow for higher downsampling and thus longer sequences. Default value: `15`
   */
  auto_downsample_min_fps?: number;
  /**
   * If true, the model will automatically temporally downsample the video to an appropriate frame length for the model, then will interpolate it back to the original frame length.
   */
  enable_auto_downsample?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to expand the video to the bottom.
   */
  expand_bottom?: boolean;
  /**
   * Whether to expand the video to the left.
   */
  expand_left?: boolean;
  /**
   * Amount of expansion. This is a float value between 0 and 1, where 0.25 adds 25% to the original video size on the specified sides. Default value: `0.25`
   */
  expand_ratio?: number;
  /**
   * Whether to expand the video to the right.
   */
  expand_right?: boolean;
  /**
   * Whether to expand the video to the top.
   */
  expand_top?: boolean;
  /**
   * URL to the first frame of the video. If provided, the model will use this frame as a reference.
   */
  first_frame_url?: string | Blob | File;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. Options are 'rife' or 'film'. Default value: `"film"`
   */
  interpolator_model?: "rife" | "film";
  /**
   * URL to the last frame of the video. If provided, the model will use this frame as a reference.
   */
  last_frame_url?: string | Blob | File;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between the original frames. A value of 0 means no interpolation.
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * URLs to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * Resolution of the generated video. Default value: `"auto"`
   */
  resolution?: "auto" | "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * If true, also return a ZIP file containing all generated frames.
   */
  return_frames_zip?: boolean;
  /**
   * Sampler to use for video generation. Default value: `"unipc"`
   */
  sampler?: "unipc" | "dpm++" | "euler";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Temporal downsample factor for the video. This is an integer value that determines how many frames to skip in the video. A value of 0 means no downsampling. For each downsample factor, one upsample factor will automatically be applied.
   */
  temporal_downsample_factor?: number;
  /**
   * The transparency mode to apply to the first and last frames. This controls how the transparent areas of the first and last frames are filled. Default value: `"content_aware"`
   */
  transparency_mode?: "content_aware" | "white" | "black";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL to the source video file. Required for outpainting.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanVace14bPoseInput = {
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster. Default value: `regular`
   */
  acceleration?: "none" | "low" | "regular";
  /**
   * Aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * The minimum frames per second to downsample the video to. This is used to help determine the auto downsample factor to try and find the lowest detail-preserving downsample factor. The default value is appropriate for most videos, if you are using a video with very fast motion, you may need to increase this value. If your video has a very low amount of motion, you could decrease this value to allow for higher downsampling and thus longer sequences. Default value: `15`
   */
  auto_downsample_min_fps?: number;
  /**
   * If true, the model will automatically temporally downsample the video to an appropriate frame length for the model, then will interpolate it back to the original frame length.
   */
  enable_auto_downsample?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * URL to the first frame of the video. If provided, the model will use this frame as a reference.
   */
  first_frame_url?: string | Blob | File;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. Options are 'rife' or 'film'. Default value: `"film"`
   */
  interpolator_model?: "rife" | "film";
  /**
   * URL to the last frame of the video. If provided, the model will use this frame as a reference.
   */
  last_frame_url?: string | Blob | File;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between the original frames. A value of 0 means no interpolation.
   */
  num_interpolated_frames?: number;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
  /**
   * The text prompt to guide video generation. For pose task, the prompt should describe the desired pose and action of the subject in the video.
   */
  prompt: string;
  /**
   * URLs to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * Resolution of the generated video. Default value: `"auto"`
   */
  resolution?: "auto" | "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * If true, also return a ZIP file containing all generated frames.
   */
  return_frames_zip?: boolean;
  /**
   * Sampler to use for video generation. Default value: `"unipc"`
   */
  sampler?: "unipc" | "dpm++" | "euler";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Temporal downsample factor for the video. This is an integer value that determines how many frames to skip in the video. A value of 0 means no downsampling. For each downsample factor, one upsample factor will automatically be applied.
   */
  temporal_downsample_factor?: number;
  /**
   * The transparency mode to apply to the first and last frames. This controls how the transparent areas of the first and last frames are filled. Default value: `"content_aware"`
   */
  transparency_mode?: "content_aware" | "white" | "black";
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL to the source video file. Required for pose task.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
};
export type WanVace14bReframeInput = {
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster. Default value: `regular`
   */
  acceleration?: "none" | "low" | "regular";
  /**
   * Aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * The minimum frames per second to downsample the video to. This is used to help determine the auto downsample factor to try and find the lowest detail-preserving downsample factor. The default value is appropriate for most videos, if you are using a video with very fast motion, you may need to increase this value. If your video has a very low amount of motion, you could decrease this value to allow for higher downsampling and thus longer sequences. Default value: `15`
   */
  auto_downsample_min_fps?: number;
  /**
   * If true, the model will automatically temporally downsample the video to an appropriate frame length for the model, then will interpolate it back to the original frame length.
   */
  enable_auto_downsample?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * URL to the first frame of the video. If provided, the model will use this frame as a reference.
   */
  first_frame_url?: string | Blob | File;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. Options are 'rife' or 'film'. Default value: `"film"`
   */
  interpolator_model?: "rife" | "film";
  /**
   * URL to the last frame of the video. If provided, the model will use this frame as a reference.
   */
  last_frame_url?: string | Blob | File;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter. Default value: `true`
   */
  match_input_frames_per_second?: boolean;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter. Default value: `true`
   */
  match_input_num_frames?: boolean;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Number of frames to interpolate between the original frames. A value of 0 means no interpolation.
   */
  num_interpolated_frames?: number;
  /**
   * The text prompt to guide video generation. Optional for reframing. Default value: `""`
   */
  prompt?: string;
  /**
   * Resolution of the generated video. Default value: `"auto"`
   */
  resolution?: "auto" | "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * If true, also return a ZIP file containing all generated frames.
   */
  return_frames_zip?: boolean;
  /**
   * Sampler to use for video generation. Default value: `"unipc"`
   */
  sampler?: "unipc" | "dpm++" | "euler";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Temporal downsample factor for the video. This is an integer value that determines how many frames to skip in the video. A value of 0 means no downsampling. For each downsample factor, one upsample factor will automatically be applied.
   */
  temporal_downsample_factor?: number;
  /**
   * The transparency mode to apply to the first and last frames. This controls how the transparent areas of the first and last frames are filled. Default value: `"content_aware"`
   */
  transparency_mode?: "content_aware" | "white" | "black";
  /**
   * Whether to trim borders from the video. Default value: `true`
   */
  trim_borders?: boolean;
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL to the source video file. This video will be used as a reference for the reframe task.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
  /**
   * Zoom factor for the video. When this value is greater than 0, the video will be zoomed in by this factor (in relation to the canvas size,) cutting off the edges of the video. A value of 0 means no zoom.
   */
  zoom_factor?: number;
};
export type WanVaceAppsLongReframeInput = {
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster. Default value: `regular`
   */
  acceleration?: "none" | "low" | "regular";
  /**
   * Aspect ratio of the generated video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * Minimum FPS for auto downsample. Default value: `6`
   */
  auto_downsample_min_fps?: number;
  /**
   * Whether to enable auto downsample. Default value: `true`
   */
  enable_auto_downsample?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. Options are 'rife' or 'film'. Default value: `"film"`
   */
  interpolator_model?: "rife" | "film";
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to paste back the reframed scene to the original video. Default value: `true`
   */
  paste_back?: boolean;
  /**
   * The text prompt to guide video generation. Optional for reframing. Default value: `""`
   */
  prompt?: string;
  /**
   * Resolution of the generated video. Default value: `"auto"`
   */
  resolution?: "auto" | "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * If true, also return a ZIP file containing all generated frames.
   */
  return_frames_zip?: boolean;
  /**
   * Sampler to use for video generation. Default value: `"unipc"`
   */
  sampler?: "unipc" | "dpm++" | "euler";
  /**
   * Threshold for scene detection sensitivity (0-100). Lower values detect more scenes. Default value: `30`
   */
  scene_threshold?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * The transparency mode to apply to the first and last frames. This controls how the transparent areas of the first and last frames are filled. Default value: `"content_aware"`
   */
  transparency_mode?: "content_aware" | "white" | "black";
  /**
   * Whether to trim borders from the video. Default value: `true`
   */
  trim_borders?: boolean;
  /**
   * The quality of the generated video. Default value: `"high"`
   */
  video_quality?: "low" | "medium" | "high" | "maximum";
  /**
   * URL to the source video file. This video will be used as a reference for the reframe task.
   */
  video_url: string | Blob | File;
  /**
   * The write mode of the generated video. Default value: `"balanced"`
   */
  video_write_mode?: "fast" | "balanced" | "small";
  /**
   * Zoom factor for the video. When this value is greater than 0, the video will be zoomed in by this factor (in relation to the canvas size,) cutting off the edges of the video. A value of 0 means no zoom.
   */
  zoom_factor?: number;
};
export type WanVaceAppsVideoEditInput = {
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster. Default value: `regular`
   */
  acceleration?: "none" | "low" | "regular";
  /**
   * Aspect ratio of the edited video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * The minimum frames per second to downsample the video to. Default value: `15`
   */
  auto_downsample_min_fps?: number;
  /**
   * Whether to enable automatic downsampling. If your video has a high frame rate or is long, enabling longer sequences to be generated. The video will be interpolated back to the original frame rate after generation. Default value: `true`
   */
  enable_auto_downsample?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URLs of the input images to use as a reference for the generation.
   */
  image_urls?: Array<string>;
  /**
   * Prompt to edit the video.
   */
  prompt: string;
  /**
   * Resolution of the edited video. Default value: `"auto"`
   */
  resolution?: "auto" | "240p" | "360p" | "480p" | "580p" | "720p";
  /**
   * Whether to include a ZIP archive containing all generated frames.
   */
  return_frames_zip?: boolean;
  /**
   * The type of video you're editing. Use 'general' for most videos, and 'human' for videos emphasizing human subjects and motions. The default value 'auto' means the model will guess based on the first frame of the video. Default value: `"auto"`
   */
  video_type?: "auto" | "general" | "human";
  /**
   * URL of the input video.
   */
  video_url: string | Blob | File;
};
export type WanVaceAppsVideoEditOutput = {
  /**
   * ZIP archive of generated frames if requested.
   */
  frames_zip?: File;
  /**
   * The edited video.
   */
  video: VideoFile;
};
export type WanVaceInput = {
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "auto" | "9:16" | "16:9";
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * URL to the guiding mask file. If provided, the model will use this mask as a reference to create masked video. If provided mask video url will be ignored.
   */
  mask_image_url?: string | Blob | File;
  /**
   * URL to the source mask file. If provided, the model will use this mask as a reference.
   */
  mask_video_url?: string | Blob | File;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). Works only with only reference images as input if source video or mask video is provided output len would be same as source up to 241 frames Default value: `81`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Urls to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * Task type for the model. Default value: `"depth"`
   */
  task?: "depth" | "inpainting";
  /**
   * URL to the source video file. If provided, the model will use this video as a reference.
   */
  video_url?: string | Blob | File;
};
export type WanVisionEnhancerInput = {
  /**
   * Controls how much the model enhances/changes the video. 0 = Minimal change (preserves original), 1 = Subtle enhancement (default), 2 = Medium enhancement, 3 = Strong enhancement, 4 = Maximum enhancement. Default value: `1`
   */
  creativity?: number;
  /**
   * Negative prompt to avoid unwanted features. Default value: `"oversaturated, overexposed, static, blurry details, subtitles, stylized, artwork, painting, still frame, overall gray, worst quality, low quality, JPEG artifacts, ugly, mutated, extra fingers, poorly drawn hands, poorly drawn face, deformed, disfigured, malformed limbs, fused fingers, static motion, cluttered background, three legs, crowded background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Optional prompt to prepend to the VLM-generated description. Leave empty to use only the auto-generated description from the video.
   */
  prompt?: string;
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Target output resolution for the enhanced video. 720p (native, fast) or 1080p (upscaled, slower). Processing is always done at 720p, then upscaled if 1080p selected. Default value: `"720p"`
   */
  target_resolution?: "720p" | "1080p";
  /**
   * The URL of the video to enhance with Wan Video. Maximum 200MB file size. Videos longer than 500 frames will have only the first 500 frames processed (~8-21 seconds depending on fps).
   */
  video_url: string | Blob | File;
};
export type WaveformInput = {
  /**
   * URL of the audio file to analyze
   */
  media_url: string | Blob | File;
  /**
   * Controls how many points are sampled per second of audio. Lower values (e.g. 1-2) create a coarser waveform, higher values (e.g. 4-10) create a more detailed one. Default value: `4`
   */
  points_per_second?: number;
  /**
   * Number of decimal places for the waveform values. Higher values provide more precision but increase payload size. Default value: `2`
   */
  precision?: number;
  /**
   * Size of the smoothing window. Higher values create a smoother waveform. Must be an odd number. Default value: `3`
   */
  smoothing_window?: number;
};
export type WaveformOutput = {
  /**
   * Duration of the audio in seconds
   */
  duration: number;
  /**
   * Number of points in the waveform data
   */
  points: number;
  /**
   * Number of decimal places used in the waveform values
   */
  precision: number;
  /**
   * Normalized waveform data as an array of values between -1 and 1. The number of points is determined by audio duration × points_per_second.
   */
  waveform: Array<number>;
};
export type WhisperChunk = {
  /**
   * Speaker ID of the chunk. Only present if diarization is enabled.
   */
  speaker?: string;
  /**
   * Transcription of the chunk
   */
  text: string;
  /**
   * Start and end timestamp of the chunk
   */
  timestamp: Array<unknown>;
};
export type whisperInput = {
  /**
   * URL of the audio file to transcribe. Supported formats: mp3, mp4, mpeg, mpga, m4a, wav or webm.
   */
  audio_url: string | Blob | File;
  /**
   *  Default value: `64`
   */
  batch_size?: number;
  /**
   * Level of the chunks to return. Either none, segment or word. `none` would imply that all of the audio will be transcribed without the timestamp tokens, we suggest to switch to `none` if you are not satisfied with the transcription quality, since it will usually improve the quality of the results. Switching to `none` will also provide minor speed ups in the transcription due to less amount of generated tokens. Notice that setting to none will produce **a single chunk with the whole transcription**. Default value: `"segment"`
   */
  chunk_level?: "none" | "segment" | "word";
  /**
   * Whether to diarize the audio file. Defaults to false. Setting to true will add costs proportional to diarization inference time.
   */
  diarize?: boolean;
  /**
   * Language of the audio file. If set to null, the language will be
   * automatically detected. Defaults to null.
   *
   * If translate is selected as the task, the audio will be translated to
   * English, regardless of the language selected.
   */
  language?:
    | "af"
    | "am"
    | "ar"
    | "as"
    | "az"
    | "ba"
    | "be"
    | "bg"
    | "bn"
    | "bo"
    | "br"
    | "bs"
    | "ca"
    | "cs"
    | "cy"
    | "da"
    | "de"
    | "el"
    | "en"
    | "es"
    | "et"
    | "eu"
    | "fa"
    | "fi"
    | "fo"
    | "fr"
    | "gl"
    | "gu"
    | "ha"
    | "haw"
    | "he"
    | "hi"
    | "hr"
    | "ht"
    | "hu"
    | "hy"
    | "id"
    | "is"
    | "it"
    | "ja"
    | "jw"
    | "ka"
    | "kk"
    | "km"
    | "kn"
    | "ko"
    | "la"
    | "lb"
    | "ln"
    | "lo"
    | "lt"
    | "lv"
    | "mg"
    | "mi"
    | "mk"
    | "ml"
    | "mn"
    | "mr"
    | "ms"
    | "mt"
    | "my"
    | "ne"
    | "nl"
    | "nn"
    | "no"
    | "oc"
    | "pa"
    | "pl"
    | "ps"
    | "pt"
    | "ro"
    | "ru"
    | "sa"
    | "sd"
    | "si"
    | "sk"
    | "sl"
    | "sn"
    | "so"
    | "sq"
    | "sr"
    | "su"
    | "sv"
    | "sw"
    | "ta"
    | "te"
    | "tg"
    | "th"
    | "tk"
    | "tl"
    | "tr"
    | "tt"
    | "uk"
    | "ur"
    | "uz"
    | "vi"
    | "yi"
    | "yo"
    | "zh";
  /**
   * Number of speakers in the audio file. Defaults to null.
   * If not provided, the number of speakers will be automatically
   * detected.
   */
  num_speakers?: number;
  /**
   * Prompt to use for generation. Defaults to an empty string. Default value: `""`
   */
  prompt?: string;
  /**
   * Task to perform on the audio file. Either transcribe or translate. Default value: `"transcribe"`
   */
  task?: "transcribe" | "translate";
};
export type whisperOutput = {
  /**
   * Timestamp chunks of the audio file
   */
  chunks?: Array<WhisperChunk>;
  /**
   * Speaker diarization segments of the audio file. Only present if diarization is enabled.
   */
  diarization_segments: Array<DiarizationSegment>;
  /**
   * List of languages that the audio file is inferred to be. Defaults to null.
   */
  inferred_languages: Array<
    | "af"
    | "am"
    | "ar"
    | "as"
    | "az"
    | "ba"
    | "be"
    | "bg"
    | "bn"
    | "bo"
    | "br"
    | "bs"
    | "ca"
    | "cs"
    | "cy"
    | "da"
    | "de"
    | "el"
    | "en"
    | "es"
    | "et"
    | "eu"
    | "fa"
    | "fi"
    | "fo"
    | "fr"
    | "gl"
    | "gu"
    | "ha"
    | "haw"
    | "he"
    | "hi"
    | "hr"
    | "ht"
    | "hu"
    | "hy"
    | "id"
    | "is"
    | "it"
    | "ja"
    | "jw"
    | "ka"
    | "kk"
    | "km"
    | "kn"
    | "ko"
    | "la"
    | "lb"
    | "ln"
    | "lo"
    | "lt"
    | "lv"
    | "mg"
    | "mi"
    | "mk"
    | "ml"
    | "mn"
    | "mr"
    | "ms"
    | "mt"
    | "my"
    | "ne"
    | "nl"
    | "nn"
    | "no"
    | "oc"
    | "pa"
    | "pl"
    | "ps"
    | "pt"
    | "ro"
    | "ru"
    | "sa"
    | "sd"
    | "si"
    | "sk"
    | "sl"
    | "sn"
    | "so"
    | "sq"
    | "sr"
    | "su"
    | "sv"
    | "sw"
    | "ta"
    | "te"
    | "tg"
    | "th"
    | "tk"
    | "tl"
    | "tr"
    | "tt"
    | "uk"
    | "ur"
    | "uz"
    | "vi"
    | "yi"
    | "yo"
    | "zh"
  >;
  /**
   * Transcription of the audio file
   */
  text: string;
};
export type wizperInput = {
  /**
   * URL of the audio file to transcribe. Supported formats: mp3, mp4, mpeg, mpga, m4a, wav or webm.
   */
  audio_url: string | Blob | File;
  /**
   * Level of the chunks to return. Default value: `"segment"`
   */
  chunk_level?: string;
  /**
   * Language of the audio file.
   * If translate is selected as the task, the audio will be translated to
   * English, regardless of the language selected. If `None` is passed,
   * the language will be automatically detected. This will also increase
   * the inference time. Default value: `en`
   */
  language?:
    | "af"
    | "am"
    | "ar"
    | "as"
    | "az"
    | "ba"
    | "be"
    | "bg"
    | "bn"
    | "bo"
    | "br"
    | "bs"
    | "ca"
    | "cs"
    | "cy"
    | "da"
    | "de"
    | "el"
    | "en"
    | "es"
    | "et"
    | "eu"
    | "fa"
    | "fi"
    | "fo"
    | "fr"
    | "gl"
    | "gu"
    | "ha"
    | "haw"
    | "he"
    | "hi"
    | "hr"
    | "ht"
    | "hu"
    | "hy"
    | "id"
    | "is"
    | "it"
    | "ja"
    | "jw"
    | "ka"
    | "kk"
    | "km"
    | "kn"
    | "ko"
    | "la"
    | "lb"
    | "ln"
    | "lo"
    | "lt"
    | "lv"
    | "mg"
    | "mi"
    | "mk"
    | "ml"
    | "mn"
    | "mr"
    | "ms"
    | "mt"
    | "my"
    | "ne"
    | "nl"
    | "nn"
    | "no"
    | "oc"
    | "pa"
    | "pl"
    | "ps"
    | "pt"
    | "ro"
    | "ru"
    | "sa"
    | "sd"
    | "si"
    | "sk"
    | "sl"
    | "sn"
    | "so"
    | "sq"
    | "sr"
    | "su"
    | "sv"
    | "sw"
    | "ta"
    | "te"
    | "tg"
    | "th"
    | "tk"
    | "tl"
    | "tr"
    | "tt"
    | "uk"
    | "ur"
    | "uz"
    | "vi"
    | "yi"
    | "yo"
    | "zh";
  /**
   * Maximum speech segment duration in seconds before splitting. Default value: `29`
   */
  max_segment_len?: number;
  /**
   * Whether to merge consecutive chunks. When enabled, chunks are merged if their combined duration does not exceed max_segment_len. Default value: `true`
   */
  merge_chunks?: boolean;
  /**
   * Task to perform on the audio file. Either transcribe or translate. Default value: `"transcribe"`
   */
  task?: "transcribe" | "translate";
  /**
   * Version of the model to use. All of the models are the Whisper large variant. Default value: `"3"`
   */
  version?: string;
};
export type wizperOutput = {
  /**
   * Timestamp chunks of the audio file
   */
  chunks: Array<WhisperChunk>;
  /**
   * List of languages that the audio file is inferred to be. Defaults to null.
   */
  languages: Array<
    | "af"
    | "am"
    | "ar"
    | "as"
    | "az"
    | "ba"
    | "be"
    | "bg"
    | "bn"
    | "bo"
    | "br"
    | "bs"
    | "ca"
    | "cs"
    | "cy"
    | "da"
    | "de"
    | "el"
    | "en"
    | "es"
    | "et"
    | "eu"
    | "fa"
    | "fi"
    | "fo"
    | "fr"
    | "gl"
    | "gu"
    | "ha"
    | "haw"
    | "he"
    | "hi"
    | "hr"
    | "ht"
    | "hu"
    | "hy"
    | "id"
    | "is"
    | "it"
    | "ja"
    | "jw"
    | "ka"
    | "kk"
    | "km"
    | "kn"
    | "ko"
    | "la"
    | "lb"
    | "ln"
    | "lo"
    | "lt"
    | "lv"
    | "mg"
    | "mi"
    | "mk"
    | "ml"
    | "mn"
    | "mr"
    | "ms"
    | "mt"
    | "my"
    | "ne"
    | "nl"
    | "nn"
    | "no"
    | "oc"
    | "pa"
    | "pl"
    | "ps"
    | "pt"
    | "ro"
    | "ru"
    | "sa"
    | "sd"
    | "si"
    | "sk"
    | "sl"
    | "sn"
    | "so"
    | "sq"
    | "sr"
    | "su"
    | "sv"
    | "sw"
    | "ta"
    | "te"
    | "tg"
    | "th"
    | "tk"
    | "tl"
    | "tr"
    | "tt"
    | "uk"
    | "ur"
    | "uz"
    | "vi"
    | "yi"
    | "yo"
    | "zh"
  >;
  /**
   * Transcription of the audio file
   */
  text: string;
};
export type XAIImageEditInput = {
  /**
   * List of URLs of the images to edit. A maximum of 3 images are supported.
   */
  image_urls?: Array<string>;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * Text description of the desired image.
   */
  prompt: string;
  /**
   * Resolution of the generated image. `1k` for standard resolution, `2k` for high resolution. Default value: `"1k"`
   */
  resolution?: "1k" | "2k";
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type XAIImageOutput = {
  /**
   * The URL of the generated image.
   */
  images: Array<ImageFile>;
  /**
   * The enhanced prompt that was used to generate the image.
   */
  revised_prompt: string;
};
export type XAIImageToVideoInput = {
  /**
   * Aspect ratio of the generated video. Default value: `auto`
   */
  aspect_ratio?:
    | "auto"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16";
  /**
   * Video duration in seconds. Default value: `6`
   */
  duration?: number;
  /**
   * URL of the input image for video generation.
   */
  image_url: string | Blob | File;
  /**
   * Text description of desired changes or motion in the video.
   */
  prompt: string;
  /**
   * Resolution of the output video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
};
export type XAilabNsfwInput = {
  /**
   * List of image URLs to check. If more than 10 images are provided, only the first 10 will be checked.
   */
  image_urls: Array<string>;
};
export type XAilabNsfwOutput = {
  /**
   * List of booleans indicating if the image has an NSFW concept
   */
  has_nsfw_concepts: Array<boolean>;
};
export type XAIReferenceToVideoInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "3:2" | "1:1" | "2:3" | "3:4" | "9:16";
  /**
   * Video duration in seconds. Default value: `8`
   */
  duration?: number;
  /**
   * Text prompt describing the video to generate. Use @Image1, @Image2, etc. to reference specific images from reference_image_urls in order.
   */
  prompt: string;
  /**
   * One or more reference image URLs to guide the video generation as style and content references. Reference in prompt as @Image1, @Image2, etc. Maximum 7 images.
   */
  reference_image_urls: Array<string>;
  /**
   * Resolution of the output video. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
};
export type XAITextToVideoInput = {
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "3:2" | "1:1" | "2:3" | "3:4" | "9:16";
  /**
   * Video duration in seconds. Default value: `6`
   */
  duration?: number;
  /**
   * Text description of the desired video.
   */
  prompt: string;
  /**
   * Resolution of the output video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
};
export type XAIVideoEditInput = {
  /**
   * Text description of the desired edit.
   */
  prompt: string;
  /**
   * Resolution of the output video. Default value: `"auto"`
   */
  resolution?: "auto" | "480p" | "720p";
  /**
   * URL of the input video to edit. The video will be resized to a maximum area of 854x480 pixels and truncated to 8 seconds.
   */
  video_url: string | Blob | File;
};
export type XAIVideoExtensionInput = {
  /**
   * Length of the extension in seconds. Default value: `6`
   */
  duration?: number;
  /**
   * Text description of what should happen next in the video.
   */
  prompt: string;
  /**
   * URL of the source video to extend. Must be MP4 format (H.264, H.265, or AV1 codec), 2-15 seconds long.
   */
  video_url: string | Blob | File;
};
export type YouTubeThumbnailInput = {
  /**
   * Aspect ratio for 4K output (default: 16:9 for YouTube)
   */
  aspect_ratio?: AspectRatio;
  /**
   * Image URL for YouTube thumbnail
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `"shocked"`
   */
  reaction_type?:
    | "shocked"
    | "surprised"
    | "excited"
    | "happy"
    | "mind_blown"
    | "jaw_dropped"
    | "clickbait_face"
    | "thumbnail_face"
    | "pointing_excited"
    | "hands_up_amazed"
    | "covering_mouth"
    | "facepalm"
    | "poggers"
    | "sus_face"
    | "fire_eyes"
    | "slay_reaction";
  /**
   *  Default value: `"bold_3d"`
   */
  text_style?:
    | "bold_3d"
    | "stroke_outline"
    | "drop_shadow"
    | "neon_glow"
    | "metallic_chrome"
    | "gradient_fill"
    | "glowing_outline"
    | "fire_effect"
    | "rainbow_gradient"
    | "comic_book"
    | "retro_80s"
    | "cyberpunk_neon"
    | "modern_clean"
    | "explosion_burst"
    | "holographic";
  /**
   *  Default value: `""`
   */
  thumbnail_text?: string;
  /**
   * Creative prompt
   */
  user_prompt: string;
};
export type YouTubeThumbnailsInput = {
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * URL of the image to convert to YouTube thumbnail style.
   */
  image_url: string | Blob | File;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `0.5`
   */
  lora_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The text to include in the YouTube thumbnail. Default value: `"Generate youtube thumbnails"`
   */
  prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type yueInput = {
  /**
   * The genres (separated by a space ' ') to guide the music generation.
   */
  genres: string;
  /**
   * The prompt to generate an image from. Must have two sections. Sections start with either [chorus] or a [verse].
   */
  lyrics: string;
};
export type ZImageBaseLoraInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The guidance scale to use for the image generation. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<LoRAInput>;
  /**
   * The negative prompt to use for the image generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageBaseTrainerInput = {
  /**
   * Default caption to use when caption files are missing. If None, missing captions will cause an error.
   */
  default_caption?: string;
  /**
   * URL to the input data zip archive.
   *
   * The zip should contain pairs of images and corresponding captions.
   *
   * The images should be named: ROOT.EXT. For example: 001.jpg
   *
   * The corresponding captions should be named: ROOT.txt. For example: 001.txt
   *
   * If no text file is provided for an image, the default_caption will be used.
   */
  image_data_url: string | Blob | File;
  /**
   * Learning rate. Default value: `0.0005`
   */
  learning_rate?: number;
  /**
   * Number of steps to train for Default value: `2000`
   */
  steps?: number;
};
export type ZImageTrainerInput = {
  /**
   * Default caption to use when caption files are missing. If None, missing captions will cause an error.
   */
  default_caption?: string;
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images, although more is better.
   *
   * The zip can also contain a text file for each image. The text file should be named:
   * ROOT.txt
   * For example:
   * photo.txt
   *
   * This text file can be used to specify the edit instructions for the image pair.
   *
   * If no text file is provided, the default_caption will be used.
   *
   * If no default_caption is provided, the training will fail.
   */
  image_data_url: string | Blob | File;
  /**
   * Learning rate applied to trainable parameters. Default value: `0.0001`
   */
  learning_rate?: number;
  /**
   * Total number of training steps. Default value: `1000`
   */
  steps?: number;
  /**
   * Type of training to perform. Use 'content' to focus on the content of the images, 'style' to focus on the style of the images, and 'balanced' to focus on a combination of both. Default value: `"balanced"`
   */
  training_type?: "content" | "style" | "balanced";
};
export type ZImageTurboControlnetInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The end of the controlnet conditioning. Default value: `0.8`
   */
  control_end?: number;
  /**
   * The scale of the controlnet conditioning. Default value: `0.75`
   */
  control_scale?: number;
  /**
   * The start of the controlnet conditioning.
   */
  control_start?: number;
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto";
  /**
   * URL of Image for ControlNet generation.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * What kind of preprocessing to apply to the image, if any. Default value: `none`
   */
  preprocess?: "none" | "canny" | "depth" | "pose";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageTurboControlnetLoraInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The end of the controlnet conditioning. Default value: `0.8`
   */
  control_end?: number;
  /**
   * The scale of the controlnet conditioning. Default value: `0.75`
   */
  control_scale?: number;
  /**
   * The start of the controlnet conditioning.
   */
  control_start?: number;
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto";
  /**
   * URL of Image for ControlNet generation.
   */
  image_url: string | Blob | File;
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<LoRAInput>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * What kind of preprocessing to apply to the image, if any. Default value: `none`
   */
  preprocess?: "none" | "canny" | "depth" | "pose";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageTurboImageToImageInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto";
  /**
   * URL of Image for Image-to-Image generation.
   */
  image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the image-to-image conditioning. Default value: `0.6`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageTurboImageToImageLoraInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto";
  /**
   * URL of Image for Image-to-Image generation.
   */
  image_url: string | Blob | File;
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<LoRAInput>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the image-to-image conditioning. Default value: `0.6`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageTurboInpaintInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The end of the controlnet conditioning. Default value: `0.8`
   */
  control_end?: number;
  /**
   * The scale of the controlnet conditioning. Default value: `0.75`
   */
  control_scale?: number;
  /**
   * The start of the controlnet conditioning.
   */
  control_start?: number;
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto";
  /**
   * URL of Image for Inpaint generation.
   */
  image_url: string | Blob | File;
  /**
   * URL of Mask for Inpaint generation.
   */
  mask_image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the inpaint conditioning. Default value: `1`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageTurboInpaintLoraInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The end of the controlnet conditioning. Default value: `0.8`
   */
  control_end?: number;
  /**
   * The scale of the controlnet conditioning. Default value: `0.75`
   */
  control_scale?: number;
  /**
   * The start of the controlnet conditioning.
   */
  control_start?: number;
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `auto`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto";
  /**
   * URL of Image for Inpaint generation.
   */
  image_url: string | Blob | File;
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<LoRAInput>;
  /**
   * URL of Mask for Inpaint generation.
   */
  mask_image_url: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The strength of the inpaint conditioning. Default value: `1`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageTurboInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageTurboLoraInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<LoRAInput>;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
};
export type ZImageTurboTilingInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Use 'auto' to match the input image size (or 1024x1024 if no image). Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto";
  /**
   * URL of an image for image-to-image or inpainting. When provided without mask_image_url, performs image-to-image; with mask_image_url, performs inpainting.
   */
  image_url?: string | Blob | File;
  /**
   * URL of a mask image for inpainting. White regions are regenerated, black regions are preserved. Requires image_url.
   */
  mask_image_url?: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * How much to transform the input image. Only used when image_url is provided. Default value: `0.6`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Tile size in latent space (64 = 512px, 128 = 1024px, 256 = 2048px). Default value: `128`
   */
  tile_size?: number;
  /**
   * Tile stride in latent space. (32 = 256px, 64 = 512px, 128 = 1024px). Default value: `64`
   */
  tile_stride?: number;
  /**
   * Tiling direction: 'both' (omnidirectional), 'horizontal' (left-right only), 'vertical' (top-bottom only). Default value: `"both"`
   */
  tiling_mode?: "both" | "horizontal" | "vertical";
};
export type ZImageTurboTilingLoraInput = {
  /**
   * The acceleration level to use. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Whether to enable prompt expansion. Note: this will increase the price by 0.0025 credits per request.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The size of the generated image. Use 'auto' to match the input image size (or 1024x1024 if no image). Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9"
    | "auto";
  /**
   * URL of an image for image-to-image or inpainting. When provided without mask_image_url, performs image-to-image; with mask_image_url, performs inpainting.
   */
  image_url?: string | Blob | File;
  /**
   * List of LoRA weights to apply (maximum 3).
   */
  loras?: Array<LoRAInput>;
  /**
   * URL of a mask image for inpainting. White regions are regenerated, black regions are preserved. Requires image_url.
   */
  mask_image_url?: string | Blob | File;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png" | "webp";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * How much to transform the input image. Only used when image_url is provided. Default value: `0.6`
   */
  strength?: number;
  /**
   * If `True`, the media will be returned as a data URI and the output data won't be available in the request history.
   */
  sync_mode?: boolean;
  /**
   * Tile size in latent space (64 = 512px, 128 = 1024px, 256 = 2048px). Default value: `128`
   */
  tile_size?: number;
  /**
   * Tile stride in latent space. (32 = 256px, 64 = 512px, 128 = 1024px). Default value: `64`
   */
  tile_stride?: number;
  /**
   * Tiling direction: 'both' (omnidirectional), 'horizontal' (left-right only), 'vertical' (top-bottom only). Default value: `"both"`
   */
  tiling_mode?: "both" | "horizontal" | "vertical";
};
export type ZoeInput = {
  /**
   * URL of the image to process
   */
  image_url: string | Blob | File;
};
export type zonosInput = {
  /**
   * The content generated using cloned voice.
   */
  prompt: string;
  /**
   * The reference audio.
   */
  reference_audio_url: string | Blob | File;
};
export type AceStepAudioInpaintOutput = AceStepOutput;
export type AceStepAudioOutpaintOutput = AceStepOutput;
export type AceStepAudioToAudioOutput = AceStepOutput;
export type AceStepPromptToAudioOutput = AceStepOutput;
export type AddBackgroundInput = NextSceneInput;
export type AddBackgroundOutput = pulidOutput;
export type AddObjectByTextInput = BlendingInput;
export type AddSubtitlesToVideoOutput = I2VOutput;
export type AddTextToImageOutput = HEDOutput;
export type AgeModifyOutput = BlurOutput;
export type AgeProgressionInput = TimeOfDayInput;
export type AgeProgressionOutput = pulidOutput;
export type AiAvatarMultiOutput = magiOutput;
export type AiAvatarMultiTextOutput = magiOutput;
export type AiAvatarSingleTextOutput = magiOutput;
export type AmEngOutput = DiaOutput;
export type AmixAudioOutput = MiniOutput;
export type AmtInterpolationFrameInterpolationInput =
  AMTFrameInterpolationInput;
export type AmtInterpolationFrameInterpolationOutput = I2VOutput;
export type AMTInterpolationInput = AmtInterpolationInput;
export type AmtInterpolationOutput = I2VOutput;
export type AnimatediffSparsectrlLcmOutput = magiOutput;
export type AnimateDiffT2VOutput = magiOutput;
export type AnimateDiffV2VOutput = magiOutput;
export type ApartmentStagingInput = VirtualTryonInput;
export type ApartmentStagingOutput = AuraFlowOutput;
export type ArbiterImageImageOutput = ArbiterImageOutput;
export type ArbiterImageTextOutput = ArbiterImageOutput;
export type AudioCompressorOutput = MiniOutput;
export type AudioEnterpriseInput = AudioInput;
export type AudioEqualizerOutput = MiniOutput;
export type AudioToAudioOutput = TextToAudioOutput;
export type AudioUnderstandingOutput = MoondreamOutput;
export type AvatarIVOutput = I2VOutput;
export type AvatarsAudioToVideoInput = Audio2VideoInput;
export type AvatarsAudioToVideoOutput = I2VOutput;
export type AvatarsTextToVideoInput = Text2VideoInput;
export type AvatarsTextToVideoOutput = I2VOutput;
export type BabyVersionOutput = pulidOutput;
export type BackgroundChangeInput = TimeOfDayInput;
export type BackgroundChangeOutput = pulidOutput;
export type BagelEditOutput = unoOutput;
export type bagelOutput = unoOutput;
export type BallpointPenSketchInput = HdrStyleInput;
export type BallpointPenSketchOutput = AuraFlowOutput;
export type BaseFlux1ImageToInput = BaseImageToInput;
export type BaseFlux1Input = FluxDevInput;
export type BaseFlux1ReduxInput = BaseReduxInput;
export type BaseKreaFlux1ImageToInput = BaseImageToInput;
export type BaseKreaFlux1Input = FluxDevInput;
export type BaseKreaFlux1ReduxInput = BaseReduxInput;
export type BaseKreaImageToInput = BaseImageToInput;
export type BaseKreaInput = FluxDevInput;
export type BaseKreaReduxInput = BaseReduxInput;
export type BaseQwenEditImageInput = QwenImageEditInput;
export type BaseQwenEditInpaintImageInput = QwenImageEditInpaintInput;
export type BaseQwenImageInput = QwenImageInput;
export type BaseSRPOFlux1ImageToInput = BaseImageToInput;
export type BaseSRPOFlux1Input = FluxDevInput;
export type BaseSRPOImageToInput = BaseImageToInput;
export type BaseSRPOInput = FluxDevInput;
export type BaseTextToImageInput = IdeogramV2aInput;
export type BenV2ImageInput = NafnetInput;
export type BenV2ImageOutput = ccsrOutput;
export type BenV2VideoOutput = magiOutput;
export type BGRemoveBatchedOutput = BlurOutput;
export type BGRemoveOutput = HEDOutput;
export type BGReplaceOutput = pulidOutput;
export type BirefnetV2Output = birefnetOutput;
export type BlendVideoOutput = I2VOutput;
export type BrEngOutput = DiaOutput;
export type BriaBackgroundRemoveInput = BGRemoveInput;
export type BriaBackgroundRemoveOutput = HEDOutput;
export type BriaBackgroundReplaceInput = BGReplaceInput;
export type BriaBackgroundReplaceOutput = pulidOutput;
export type BriaEraserInput = EraserInput;
export type BriaEraserOutput = HEDOutput;
export type BriaExpandOutput = ccsrOutput;
export type BriaGenfillInput = GenFillInput;
export type BriaGenfillOutput = pulidOutput;
export type BriaProductShotInput = ProductShotInput;
export type BriaProductShotOutput = BlurOutput;
export type BriaReimagineInput = ReimagineInput;
export type BriaReimagineOutput = pulidOutput;
export type BriaTextToImageBaseInput = BriaTextToImageHdInput;
export type BriaTextToImageBaseOutput = pulidOutput;
export type BriaTextToImageFastInput = BriaTextToImageHdInput;
export type BriaTextToImageFastOutput = pulidOutput;
export type BriaTextToImageHdOutput = pulidOutput;
export type BriaVideoEraserEraseKeypointsInput = VideoEraseKeypointsInput;
export type BriaVideoEraserEraseMaskInput = VideoEraseMaskInput;
export type BriaVideoEraserErasePromptInput = VideoErasePromptInput;
export type BroccoliHaircutInput = RealismInput;
export type BroccoliHaircutOutput = pulidOutput;
export type BrPortugeseOutput = DiaOutput;
export type BytedanceDreamactorV2Input = DreamActor2Input;
export type BytedanceDreamactorV2Output = I2VOutput;
export type BytedanceDreaminaV31TextToImageInput = DreaminaInput;
export type BytedanceDreaminaV31TextToImageOutput = pulidOutput;
export type BytedanceOmnihumanInput = OmniHumanInput;
export type BytedanceOmnihumanOutput = AIAvatarOutput;
export type BytedanceOmnihumanV15Input = OmniHumanv15Input;
export type BytedanceOmnihumanV15Output = AIAvatarOutput;
export type BytedanceSeedanceV15ProImageToVideoInput =
  SeedanceProv15ImageToVideoInput;
export type BytedanceSeedanceV15ProImageToVideoOutput = magiOutput;
export type BytedanceSeedanceV15ProTextToVideoInput =
  SeedanceProv15TextToVideoInput;
export type BytedanceSeedanceV15ProTextToVideoOutput = magiOutput;
export type BytedanceSeedanceV1LiteImageToVideoInput =
  SeedanceImageToVideoInput;
export type BytedanceSeedanceV1LiteImageToVideoOutput = magiOutput;
export type BytedanceSeedanceV1LiteReferenceToVideoInput =
  SeedanceReferenceToVideoInput;
export type BytedanceSeedanceV1LiteReferenceToVideoOutput = magiOutput;
export type BytedanceSeedanceV1LiteTextToVideoInput = SeedanceTextToVideoInput;
export type BytedanceSeedanceV1LiteTextToVideoOutput = magiOutput;
export type BytedanceSeedanceV1ProFastImageToVideoInput =
  SeedanceProFastImageToVideoInput;
export type BytedanceSeedanceV1ProFastImageToVideoOutput = magiOutput;
export type BytedanceSeedanceV1ProFastTextToVideoInput =
  SeedanceTextToVideoInput;
export type BytedanceSeedanceV1ProFastTextToVideoOutput = magiOutput;
export type BytedanceSeedanceV1ProImageToVideoInput = SeedanceImageToVideoInput;
export type BytedanceSeedanceV1ProImageToVideoOutput = magiOutput;
export type BytedanceSeedanceV1ProTextToVideoInput = SeedanceTextToVideoInput;
export type BytedanceSeedanceV1ProTextToVideoOutput = magiOutput;
export type BytedanceSeedreamV3TextToImageInput = SeedDreamInput;
export type BytedanceSeedreamV3TextToImageOutput = pulidOutput;
export type BytedanceSeedreamV45EditInput = SeedDream45EditInput;
export type BytedanceSeedreamV45EditOutput = BlurOutput;
export type BytedanceSeedreamV45TextToImageInput = SeedDream45T2IInput;
export type BytedanceSeedreamV45TextToImageOutput = pulidOutput;
export type BytedanceSeedreamV4EditInput = SeedDream4EditInput;
export type BytedanceSeedreamV4EditOutput = pulidOutput;
export type BytedanceSeedreamV4TextToImageInput = SeedDream4T2IInput;
export type BytedanceSeedreamV4TextToImageOutput = pulidOutput;
export type BytedanceSeedreamV5LiteEditInput = SeedDream50LiteEditInput;
export type BytedanceSeedreamV5LiteEditOutput = pulidOutput;
export type BytedanceSeedreamV5LiteTextToImageInput = SeedDream50LiteT2IInput;
export type BytedanceSeedreamV5LiteTextToImageOutput = pulidOutput;
export type BytedanceSeedV2MiniInput = Seed2MiniInput;
export type BytedanceSeedV2MiniOutput = Seed2MiniOutput;
export type BytedanceUpscalerUpscaleVideoOutput = AIAvatarOutput;
export type BytedanceVideoStylizeInput = StylizeInput;
export type CannyOutput = HEDOutput;
export type cartoonifyOutput = unoOutput;
export type CartoonifyOutput = pulidOutput;
export type CatVtonOutput = HEDOutput;
export type ChatEnterpriseInput = ChatInput;
export type ChatterboxhdSpeechToSpeechInput = STSInput;
export type ChatterboxhdSpeechToSpeechOutput = STSOutput;
export type ChatterboxhdTextToSpeechInput = TTSInput;
export type ChatterboxhdTextToSpeechOutput = STSOutput;
export type ChatterboxMultilingualOutput = DiaOutput;
export type ChatterboxOutput = DiaOutput;
export type ChatterboxSpeechToSpeechOutput = DiaOutput;
export type ChatterboxTextToSpeechMultilingualOutput = DiaOutput;
export type ChatterboxTextToSpeechOutput = DiaOutput;
export type ChatterboxTurboOutput = DiaOutput;
export type ChromaticAberrationOutput = BlurOutput;
export type ChronoEditLoraGalleryPaintbrushInput = ChronoEditPaintBrushInput;
export type ChronoEditLoraGalleryPaintbrushOutput = ChronoEditOutput;
export type ChronoEditLoraGalleryUpscalerInput = ChronoEditUpscalerInput;
export type ChronoEditLoraGalleryUpscalerOutput = ChronoEditOutput;
export type ChronoEditLoraOutput = ChronoEditOutput;
export type CityTeleportOutput = BlurOutput;
export type codeformerOutput = ccsrOutput;
export type Cogvideox5bImageToVideoOutput = Cogvideox5bOutput;
export type Cogvideox5bVideoToVideoOutput = Cogvideox5bOutput;
export type cogview4Output = unoOutput;
export type CollectionToVideoOutput = I2VOutput;
export type ColorCorrectionOutput = BlurOutput;
export type ColorTintOutput = BlurOutput;
export type CombineOutput = I2VOutput;
export type ConcatImageOutput = HEDOutput;
export type controlnetsdxlOutput = pulidOutput;
export type controlnextOutput = I2VOutput;
export type ConversationInput = personaplexInput;
export type ConversationOutput = personaplexOutput;
export type CosmosPredict25DistilledTextToVideoOutput = lynxOutput;
export type CosmosPredict25ImageToVideoOutput = lynxOutput;
export type CosmosPredict25TextToVideoOutput = lynxOutput;
export type CosmosPredict25VideoToVideoOutput = lynxOutput;
export type CreatifyAuroraOutput = React1Output;
export type CreativeUpscalerOutput = ccsrOutput;
export type CropImageOutput = HEDOutput;
export type CrystalUpscaleOutput = BlurOutput;
export type CrystalUpscalerInput = CrystalUpscaleInput;
export type CrystalUpscalerOutput = BlurOutput;
export type CrystalVideoUpscaleOutput = React1Output;
export type CrystalVideoUpscalerInput = CrystalVideoUpscaleInput;
export type CrystalVideoUpscalerOutput = React1Output;
export type ddcolorInput = NafnetInput;
export type ddcolorOutput = HEDOutput;
export type deepfilternet3Input = NovaSrInput;
export type DeepFilterNetTimings = LavaSRTimings;
export type DepthAnythingV2Input = ZoeInput;
export type DepthAnythingV2Output = HEDOutput;
export type DepthMapOutput = HEDOutput;
export type DesaturateOutput = BlurOutput;
export type DevTextToImageInput = bitdanceInput;
export type DiaCloneOutput = DiaOutput;
export type DiaTtsOutput = DiaOutput;
export type DiaTtsVoiceCloneInput = DiaTtsInput;
export type DiaTtsVoiceCloneOutput = DiaOutput;
export type diffrhythmOutput = DiaOutput;
export type DiffusionEdgeInput = ZoeInput;
export type DiffusionEdgeOutput = HEDOutput;
export type DigitalComicArtInput = HdrStyleInput;
export type DigitalComicArtOutput = AuraFlowOutput;
export type DissolveOutput = BlurOutput;
export type DistilledTextToVideoInput = LtxVideo13bDevInput;
export type DocresDewarpInput = NafnetInput;
export type DocresDewarpOutput = HEDOutput;
export type DocResInput = docresInput;
export type docresOutput = HEDOutput;
export type DodgeBurnOutput = BlurOutput;
export type DrctSuperResolutionOutput = HEDOutput;
export type DreamActor2Output = I2VOutput;
export type DreaminaOutput = pulidOutput;
export type Dreamomni2EditOutput = HEDOutput;
export type dreamoOutput = unoOutput;
export type dreamshaperOutput = unoOutput;
export type dubbingOutput = I2VOutput;
export type DWPoseInput = dwposeInput;
export type dwposeOutput = HEDOutput;
export type DWPoseOutput = HEDOutput;
export type DWPoseVideoInput = DwposeVideoInput;
export type DwposeVideoOutput = I2VOutput;
export type DWPoseVideoOutput = I2VOutput;
export type EchomimicV3Output = I2VOutput;
export type EffectOutput = I2VOutput;
export type ElementsOutput = I2VOutput;
export type ElevenlabsAudioIsolationOutput = ElevenlabsTtsElevenV3Output;
export type ElevenlabsDubbingOutput = DubbingVideoOutput;
export type ElevenlabsMusicOutput = DiaOutput;
export type ElevenlabsSoundEffectsV2Output = DiaOutput;
export type ElevenlabsSpeechToTextOutput = TranscriptionOutput;
export type ElevenlabsSpeechToTextScribeV2Output = TranscriptionOutput;
export type ElevenlabsTextToDialogueElevenV3Output = TextToAudioOutput;
export type ElevenlabsTtsMultilingualV2Input = ElevenlabsTtsTurboV25Input;
export type ElevenlabsTtsMultilingualV2Output = ElevenlabsTtsElevenV3Output;
export type ElevenlabsTtsTurboV25Output = ElevenlabsTtsElevenV3Output;
export type ElevenlabsVoiceChangerOutput = TextToAudioOutput;
export type EmbedProductOutput = ccsrOutput;
export type Emu35EditOutput = Emu35Output;
export type Emu35ImageEditImageInput = Emu35ImageEditInput;
export type Emu35ImageEditImageOutput = Emu35Output;
export type Emu35ImageTextToImageInput = Emu35ImageInput;
export type Emu35ImageTextToImageOutput = Emu35Output;
export type EraserOutput = HEDOutput;
export type esrganOutput = HEDOutput;
export type ExpressionChangeInput = TimeOfDayInput;
export type ExpressionChangeOutput = pulidOutput;
export type ExtractAudioOutput = MiniOutput;
export type Fabric10FastInput = Fabric10Input;
export type Fabric10FastOutput = I2VOutput;
export type Fabric10Output = I2VOutput;
export type Fabric10TextOutput = I2VOutput;
export type FabricOneLipsyncInput = Fabric10Input;
export type FabricOneOutput = I2VOutput;
export type FabricOneTextInput = Fabric10TextInput;
export type FabricOneTextOutput = I2VOutput;
export type FaceEnhancementOutput = pulidOutput;
export type FaceToFullPortraitInput = NextSceneInput;
export type FaceToFullPortraitOutput = pulidOutput;
export type FalAiFlux2KleinLorainput = ChronoLoraWeight;
export type FalToolkitImageImageImage = ImageFile;
export type FashnTryonV15Input = V16Input;
export type FashnTryonV15Output = V16Output;
export type FashnTryonV16Input = V16Input;
export type FashnTryonV16Output = V16Output;
export type FastAnimatediffTextToVideoInput = AnimateDiffT2VInput;
export type FastAnimatediffTextToVideoOutput = magiOutput;
export type FastAnimatediffTurboVideoToVideoInput = AnimateDiffV2VTurboInput;
export type FastAnimatediffTurboVideoToVideoOutput = magiOutput;
export type FastAnimatediffVideoToVideoInput = AnimateDiffV2VInput;
export type FastAnimatediffVideoToVideoOutput = magiOutput;
export type FastFooocusSdxlImageToImageInput = ImageToImageFooocusInput;
export type FastFooocusSdxlImageToImageOutput = unoOutput;
export type FastFooocusSdxlOutput = unoOutput;
export type FastGeneralRembgInput = GeneralRembgInput;
export type FastGeneralRembgOutput = GeneralRembgOutput;
export type FastLcmDiffusionImageToImageInput = ImageToImageLCMInput;
export type FastLcmDiffusionImageToImageOutput = unoOutput;
export type FastLcmDiffusionInpaintingInput = InpaintingLCMInput;
export type FastLcmDiffusionInpaintingOutput = unoOutput;
export type FastLcmDiffusionInput = TextToImageLCMInput;
export type FastLcmDiffusionOutput = unoOutput;
export type FastLightningSdxlImageToImageInput = ImageToImageLightningInput;
export type FastLightningSdxlImageToImageOutput = unoOutput;
export type FastLightningSdxlInpaintingInput = InpaintingLightningInput;
export type FastLightningSdxlInpaintingOutput = unoOutput;
export type FastLightningSdxlOutput = unoOutput;
export type FastSdxlControlnetCannyImageToImageInput =
  ImageToImageControlNetInput;
export type FastSdxlControlnetCannyImageToImageOutput =
  FastSdxlControlnetCannyOutput;
export type FastSdxlControlnetCannyInpaintingInput = InpaintingControlNetInput;
export type FastSdxlControlnetCannyInpaintingOutput =
  FastSdxlControlnetCannyOutput;
export type FastSdxlControlnetCannyInput = Sd15DepthControlnetInput;
export type FastSdxlImageToImageInput = ImageToImageSD15Input;
export type FastSdxlImageToImageOutput = unoOutput;
export type FastSdxlInpaintingInput = InpaintingInput;
export type FastSdxlInpaintingOutput = unoOutput;
export type FastSdxlOutput = unoOutput;
export type FastSVDImageInput = FastSvdLcmInput;
export type FastSvdLcmOutput = magiOutput;
export type FastSvdLcmTextToVideoInput = FastSVDTextInput;
export type FastSvdLcmTextToVideoOutput = magiOutput;
export type FastSvdTextToVideoOutput = magiOutput;
export type FfmpegApiComposeInput = Input;
export type FfmpegApiComposeOutput = ComposeOutput;
export type FfmpegApiExtractFrameInput = FrameInput;
export type FfmpegApiExtractFrameOutput = BlurOutput;
export type FfmpegApiLoudnormInput = LoudnormInput;
export type FfmpegApiLoudnormOutput = LoudnormOutput;
export type FfmpegApiMergeAudiosInput = MergeAudiosInput;
export type FfmpegApiMergeAudiosOutput = DiaOutput;
export type FfmpegApiMergeVideosInput = MergeVideosInput;
export type FfmpegApiMergeVideosOutput = MergeVideosOutput;
export type FfmpegApiMetadataInput = MetadataInput;
export type FfmpegApiMetadataOutput = MetadataOutput;
export type FfmpegApiWaveformInput = WaveformInput;
export type FfmpegApiWaveformOutput = WaveformOutput;
export type FiboBbqPreviewGenerateOutput = FiboGenerateOutput;
export type FiboEditAddObjectByTextInput = BlendingInput;
export type FiboEditAddObjectByTextOutput = FiboEditEditOutput;
export type FiboEditBlendInput = BlendingInput;
export type FiboEditBlendOutput = FiboEditEditOutput;
export type FiboEditColorizeInput = ColorizeInput;
export type FiboEditColorizeOutput = FiboEditEditOutput;
export type FiboEditEraseByTextInput = EraseByTextInput;
export type FiboEditEraseByTextOutput = FiboEditEditOutput;
export type FiboEditRelightInput = RelightInput;
export type FiboEditRelightOutput = FiboEditEditOutput;
export type FiboEditReplaceObjectByTextInput = BlendingInput;
export type FiboEditReplaceObjectByTextOutput = FiboEditEditOutput;
export type FiboEditReseasonInput = ReseasonInput;
export type FiboEditReseasonOutput = FiboEditEditOutput;
export type FiboEditRestoreInput = ZoeInput;
export type FiboEditRestoreOutput = FiboEditEditOutput;
export type FiboEditRestyleInput = RestyletInput;
export type FiboEditRestyleOutput = FiboEditEditOutput;
export type FiboEditRewriteTextInput = RewriteTextInput;
export type FiboEditRewriteTextOutput = FiboEditEditOutput;
export type FiboEditSketchToColoredImageInput = ZoeInput;
export type FiboEditSketchToColoredImageOutput = FiboEditEditOutput;
export type FiboGenerateStructuredPromptInput = StructuredPromptInput;
export type FiboLiteGenerateStructuredPromptInput = StructuredPromptInput;
export type FILMImageInput = filmInput;
export type FILMImageOutput = filmOutput;
export type FILMVideoInput = FilmVideoInput;
export type FilmVideoOutput = React1Output;
export type FILMVideoOutput = React1Output;
export type FinegrainEraserBboxOutput = FinegrainEraserOutput;
export type FinegrainEraserMaskOutput = FinegrainEraserOutput;
export type FireredImageEditInput = EditImageInput;
export type FireredImageEditOutput = unoOutput;
export type FireredImageEditV11Input = EditImageInput;
export type FireredImageEditV11Output = unoOutput;
export type FlashvsrUpscaleVideoOutput = magiOutput;
export type FLiteStandardInput = FLiteTextureInput;
export type FLiteStandardOutput = unoOutput;
export type FLiteTextureOutput = unoOutput;
export type Florence2LargeCaptionInput = ZoeInput;
export type Florence2LargeCaptionOutput = TextOutput;
export type Florence2LargeCaptionToPhraseGroundingInput = ImageWithTextInput;
export type Florence2LargeCaptionToPhraseGroundingOutput =
  Florence2LargeRegionProposalOutput;
export type Florence2LargeDenseRegionCaptionInput = ZoeInput;
export type Florence2LargeDenseRegionCaptionOutput =
  Florence2LargeRegionProposalOutput;
export type Florence2LargeDetailedCaptionInput = ZoeInput;
export type Florence2LargeDetailedCaptionOutput = TextOutput;
export type Florence2LargeMoreDetailedCaptionInput = ZoeInput;
export type Florence2LargeMoreDetailedCaptionOutput = TextOutput;
export type Florence2LargeObjectDetectionInput = ZoeInput;
export type Florence2LargeObjectDetectionOutput =
  Florence2LargeRegionProposalOutput;
export type Florence2LargeOcrInput = ZoeInput;
export type Florence2LargeOcrOutput = TextOutput;
export type Florence2LargeOcrWithRegionInput = ZoeInput;
export type Florence2LargeOpenVocabularyDetectionInput = ImageWithTextInput;
export type Florence2LargeOpenVocabularyDetectionOutput =
  Florence2LargeRegionProposalOutput;
export type Florence2LargeReferringExpressionSegmentationInput =
  ImageWithTextInput;
export type Florence2LargeReferringExpressionSegmentationOutput =
  Florence2LargeRegionToSegmentationOutput;
export type Florence2LargeRegionProposalInput = ZoeInput;
export type Florence2LargeRegionToCategoryInput = ImageWithUserCoordinatesInput;
export type Florence2LargeRegionToCategoryOutput = TextOutput;
export type Florence2LargeRegionToDescriptionInput =
  ImageWithUserCoordinatesInput;
export type Florence2LargeRegionToDescriptionOutput = TextOutput;
export type Florence2LargeRegionToSegmentationInput =
  ImageWithUserCoordinatesInput;
export type floweditOutput = ccsrOutput;
export type Flux1DevImageToImageInput = BaseImageToInput;
export type Flux1DevImageToImageOutput = unoOutput;
export type Flux1DevInput = FluxDevInput;
export type Flux1DevOutput = unoOutput;
export type Flux1DevReduxInput = BaseReduxInput;
export type Flux1DevReduxOutput = unoOutput;
export type Flux1KreaImageToImageInput = BaseImageToInput;
export type Flux1KreaImageToImageOutput = unoOutput;
export type Flux1KreaInput = FluxDevInput;
export type Flux1KreaOutput = unoOutput;
export type Flux1KreaReduxInput = BaseReduxInput;
export type Flux1KreaReduxOutput = unoOutput;
export type Flux1SchnellInput = FluxDevInput;
export type Flux1SchnellOutput = unoOutput;
export type Flux1SchnellReduxInput = FluxSchnellReduxInput;
export type Flux1SchnellReduxOutput = unoOutput;
export type Flux1SrpoImageToImageInput = BaseImageToInput;
export type Flux1SrpoImageToImageOutput = unoOutput;
export type Flux1SrpoInput = FluxDevInput;
export type Flux1SrpoOutput = unoOutput;
export type Flux2EditImageInput = Flux2EditInput;
export type Flux2EditImageLoRAInput = Flux2LoraEditInput;
export type Flux2EditImageLoRAOutput = Flux2Output;
export type Flux2EditImageOutput = Flux2Output;
export type Flux2EditOutput = Flux2Output;
export type Flux2FlashEditImageInput = Flux2FlashEditInput;
export type Flux2FlashEditImageOutput = Flux2Output;
export type Flux2FlashEditOutput = Flux2Output;
export type Flux2FlashOutput = Flux2Output;
export type Flux2FlashT2IOutput = Flux2Output;
export type Flux2FlashTextToImageInput = Flux2FlashInput;
export type Flux2FlexEditOutput = Emu35Output;
export type Flux2FlexImageEditInput = Flux2FlexEditInput;
export type Flux2FlexOutput = Emu35Output;
export type Flux2FlexTextToImageInput = Flux2FlexInput;
export type Flux2Klein4bBaseEditInput = EditImageInput;
export type Flux2Klein4bBaseEditOutput = Flux2Output;
export type Flux2Klein4bBaseInput = OvisImageInput;
export type Flux2Klein4bBaseOutput = Flux2Output;
export type Flux2Klein4bBaseTrainerEditInput = Flux2TrainerInput;
export type Flux2Klein4bBaseTrainerEditOutput = Flux2TrainerOutput;
export type Flux2Klein4bBaseTrainerInput = Flux2TrainerInput;
export type Flux2Klein4bBaseTrainerOutput = Flux2TrainerOutput;
export type Flux2Klein4bEditLoraOutput = Flux2Output;
export type Flux2Klein4bEditOutput = Flux2Output;
export type Flux2Klein4bLoraInput = KleinLoRAInput;
export type Flux2Klein4bLoraOutput = Flux2Output;
export type Flux2Klein9bBaseEditInput = EditImageInput;
export type Flux2Klein9bBaseEditLoraInput = KleinBaseEditLoRAInput;
export type Flux2Klein9bBaseEditLoraOutput = Flux2Output;
export type Flux2Klein9bBaseEditOutput = Flux2Output;
export type Flux2Klein9bBaseInput = OvisImageInput;
export type Flux2Klein9bBaseLoraInput = KleinBaseLoRAInput;
export type Flux2Klein9bBaseLoraOutput = Flux2Output;
export type Flux2Klein9bBaseOutput = Flux2Output;
export type Flux2Klein9bBaseTrainerEditInput = Flux2TrainerInput;
export type Flux2Klein9bBaseTrainerEditOutput = Flux2TrainerOutput;
export type Flux2Klein9bBaseTrainerInput = Flux2TrainerInput;
export type Flux2Klein9bBaseTrainerOutput = Flux2TrainerOutput;
export type Flux2Klein9bEditInput = Flux2Klein4bEditInput;
export type Flux2Klein9bEditLoraInput = Flux2Klein4bEditLoraInput;
export type Flux2Klein9bEditLoraOutput = Flux2Output;
export type Flux2Klein9bEditOutput = Flux2Output;
export type Flux2Klein9bInput = piflowInput;
export type Flux2Klein9bLoraInput = KleinLoRAInput;
export type Flux2Klein9bLoraOutput = Flux2Output;
export type Flux2Klein9bOutput = Flux2Output;
export type Flux2KleinRealtimeInput = RealtimeEditInput;
export type Flux2KleinRealtimeOutput = RealtimeEditOutput;
export type Flux2LoraEditOutput = Flux2Output;
export type Flux2LoraGalleryAddBackgroundOutput = AuraFlowOutput;
export type Flux2LoraGalleryApartmentStagingInput = VirtualTryonInput;
export type Flux2LoraGalleryApartmentStagingOutput = AuraFlowOutput;
export type Flux2LoraGalleryBallpointPenSketchInput = HdrStyleInput;
export type Flux2LoraGalleryBallpointPenSketchOutput = AuraFlowOutput;
export type Flux2LoraGalleryDigitalComicArtInput = HdrStyleInput;
export type Flux2LoraGalleryDigitalComicArtOutput = AuraFlowOutput;
export type Flux2LoraGalleryFaceToFullPortraitInput =
  Flux2LoraGalleryAddBackgroundInput;
export type Flux2LoraGalleryFaceToFullPortraitOutput = AuraFlowOutput;
export type Flux2LoraGalleryHdrStyleInput = HdrStyleInput;
export type Flux2LoraGalleryHdrStyleOutput = AuraFlowOutput;
export type Flux2LoraGalleryMultipleAnglesOutput = AuraFlowOutput;
export type Flux2LoraGalleryRealismInput = HdrStyleInput;
export type Flux2LoraGalleryRealismOutput = AuraFlowOutput;
export type Flux2LoraGallerySatelliteViewStyleInput = HdrStyleInput;
export type Flux2LoraGallerySatelliteViewStyleOutput = AuraFlowOutput;
export type Flux2LoraGallerySepiaVintageInput = HdrStyleInput;
export type Flux2LoraGallerySepiaVintageOutput = AuraFlowOutput;
export type Flux2LoraGalleryVirtualTryonInput = VirtualTryonInput;
export type Flux2LoraGalleryVirtualTryonOutput = AuraFlowOutput;
export type Flux2LoraOutput = Flux2Output;
export type Flux2MaxEditOutput = Emu35Output;
export type Flux2MaxImageEditInput = Flux2MaxEditInput;
export type Flux2MaxOutput = Emu35Output;
export type Flux2MaxTextToImageInput = Flux2MaxInput;
export type Flux2ProEditInput = Flux2MaxEditInput;
export type Flux2ProEditOutput = Emu35Output;
export type Flux2ProImageEditInput = Flux2MaxEditInput;
export type Flux2ProInput = Flux2MaxInput;
export type Flux2ProOutput = Emu35Output;
export type Flux2ProTextToImageInput = Flux2MaxInput;
export type Flux2T2ILoRAOutput = Flux2Output;
export type Flux2T2IOutput = Flux2Output;
export type Flux2TextToImageInput = Flux2Input;
export type Flux2TextToImageLoRAInput = Flux2LoraInput;
export type Flux2TrainerEditInput = Flux2TrainerInput;
export type Flux2TrainerEditOutput = Flux2TrainerOutput;
export type Flux2TrainerV2EditInput = Flux2TrainerInput;
export type Flux2TrainerV2EditOutput = Flux2TrainerOutput;
export type Flux2TrainerV2Input = Flux2TrainerInput;
export type Flux2TrainerV2Output = Flux2TrainerOutput;
export type Flux2TurboEditImageInput = Flux2FlashEditInput;
export type Flux2TurboEditImageOutput = Flux2Output;
export type Flux2TurboEditInput = Flux2FlashEditInput;
export type Flux2TurboEditOutput = Flux2Output;
export type Flux2TurboInput = Flux2FlashInput;
export type Flux2TurboOutput = Flux2Output;
export type Flux2TurboT2IOutput = Flux2Output;
export type Flux2TurboTextToImageInput = Flux2FlashInput;
export type FluxControlLoraCannyImageToImageOutput = unoOutput;
export type FluxControlLoraCannyInput = DepthLoraInput;
export type FluxControlLoraCannyOutput = unoOutput;
export type FluxControlLoraDepthImageToImageInput =
  FluxControlLoraCannyImageToImageInput;
export type FluxControlLoraDepthImageToImageOutput = unoOutput;
export type FluxControlLoraDepthInput = DepthLoraInput;
export type FluxControlLoraDepthOutput = unoOutput;
export type FluxDevImageToImageInput = BaseImageToInput;
export type FluxDevImageToImageOutput = unoOutput;
export type FluxDevOutput = unoOutput;
export type FluxDifferentialDiffusionOutput = unoOutput;
export type FluxGeneralDifferentialDiffusionInput = DifferentialDiffusionInput;
export type FluxGeneralDifferentialDiffusionOutput = unoOutput;
export type FluxGeneralImageToImageOutput = unoOutput;
export type FluxGeneralInpaintingOutput = unoOutput;
export type FluxGeneralOutput = unoOutput;
export type FluxGeneralRfInversionInput = RFInversionInput;
export type FluxGeneralRfInversionOutput = unoOutput;
export type FluxKontextDevOutput = unoOutput;
export type FluxKontextLoraInput = BaseKontextEditInput;
export type FluxKontextLoraOutput = unoOutput;
export type FluxKontextLoraTextToImageInput = FluxLoraInput;
export type FluxKontextLoraTextToImageOutput = unoOutput;
export type FluxKontextTrainerInput = Flux2TrainerInput;
export type FluxKontextTrainerOutput = Flux2TrainerOutput;
export type FluxKreaImageToImageInput = BaseImageToInput;
export type FluxKreaImageToImageOutput = unoOutput;
export type FluxKreaInput = FluxDevInput;
export type FluxKreaLoraImageToImageInput = FluxLoraImageToImageInput;
export type FluxKreaLoraImageToImageOutput = unoOutput;
export type FluxKreaLoraInpaintingOutput = unoOutput;
export type FluxKreaLoraInput = FluxLoraInput;
export type FluxKreaLoraOutput = unoOutput;
export type FluxKreaLoraStreamInput = FluxLoraInput;
export type FluxKreaLoraStreamOutput = unoOutput;
export type FluxKreaOutput = unoOutput;
export type FluxKreaReduxInput = BaseReduxInput;
export type FluxKreaReduxOutput = unoOutput;
export type FluxLoraCannyOutput = unoOutput;
export type FluxLoraDepthOutput = unoOutput;
export type FluxLoraFastTrainingInput = FluxKreaTrainerInput;
export type FluxLoraFastTrainingOutput = FluxKreaTrainerOutput;
export type FluxLoraFillOutput = unoOutput;
export type FluxLoraImageToImageOutput = unoOutput;
export type FluxLoraOutput = unoOutput;
export type FluxLoraPortraitTrainerOutput = Flux2TrainerOutput;
export type FluxLoraStreamInput = FluxLoraInput;
export type FluxLoraStreamOutput = unoOutput;
export type FluxProDepthControlFinetunedInput =
  FluxProCannyControlFinetunedInput;
export type FluxProDepthControlInput = FluxProCannyControlInput;
export type FluxProKontextInput = FluxKontextInput;
export type FluxProKontextMaxInput = FluxKontextInput;
export type FluxProKontextMaxMultiInput = FluxKontextMultiInput;
export type FluxProKontextMaxMultiOutput = FluxProNewOutput;
export type FluxProKontextMaxOutput = FluxKontextOutput;
export type FluxProKontextMaxTextToImageInput = FluxProKontextTextToImageInput;
export type FluxProKontextMaxTextToImageOutput = FluxProNewOutput;
export type FluxProKontextMultiInput = FluxKontextMultiInput;
export type FluxProKontextMultiOutput = FluxProNewOutput;
export type FluxProKontextOutput = FluxKontextOutput;
export type FluxProKontextTextToImageOutput = FluxProNewOutput;
export type FluxProPlusTextToImageInput = FluxProV11Input;
export type FluxProTextToImageInput = FluxProNewInput;
export type FluxProUltraTextToImageFinetunedInput =
  FluxProV11UltraFinetunedInput;
export type FluxProUltraTextToImageInput = FluxProV11UltraInput;
export type FluxProV11Output = FluxProNewOutput;
export type FluxProV11ReduxInput = FluxProV1ReduxInput;
export type FluxProV11ReduxOutput = FluxProNewOutput;
export type FluxProV11UltraFinetunedOutput = FluxProNewOutput;
export type FluxProV11UltraOutput = FluxProNewOutput;
export type FluxProV11UltraReduxOutput = FluxProNewOutput;
export type FluxProV1FillFinetunedInput = FluxProFillFinetunedInput;
export type FluxProV1FillFinetunedOutput = FluxProNewOutput;
export type FluxProV1FillInput = FluxProFillInput;
export type FluxProV1FillOutput = FluxProNewOutput;
export type FluxProV1ReduxOutput = FluxProNewOutput;
export type FluxPulidOutput = unoOutput;
export type FluxSchnellInput = FluxDevInput;
export type FluxSchnellOutput = unoOutput;
export type FluxSchnellReduxOutput = unoOutput;
export type FluxSrpoImageToImageInput = BaseImageToInput;
export type FluxSrpoImageToImageOutput = unoOutput;
export type FluxSrpoInput = FluxDevInput;
export type FluxSrpoOutput = unoOutput;
export type FluxSubjectOutput = unoOutput;
export type FooocusImagePromptOutput = fooocusOutput;
export type FooocusInpaintOutput = fooocusOutput;
export type FooocusLegacyInput = fooocusInput;
export type FooocusUpscaleOrVaryOutput = fooocusOutput;
export type FrameOutput = BlurOutput;
export type FramepackF1Input = framepackInput;
export type FramepackF1Output = magiOutput;
export type FramepackFlf2vOutput = magiOutput;
export type framepackOutput = magiOutput;
export type FrenchOutput = DiaOutput;
export type Gemini25FlashImageEditInput = NanoBananaEditInput;
export type Gemini25FlashImageEditOutput = NanoBananaOutput;
export type Gemini25FlashImageInput = NanoBananaInput;
export type Gemini25FlashImageOutput = NanoBananaOutput;
export type Gemini31FlashImagePreviewEditInput = NanoBanana2EditInput;
export type Gemini31FlashImagePreviewEditOutput = NanoBananaOutput;
export type Gemini31FlashImagePreviewInput = NanoBanana2Input;
export type Gemini31FlashImagePreviewOutput = NanoBananaOutput;
export type Gemini3ProImagePreviewEditInput = NanoBananaProEditInput;
export type Gemini3ProImagePreviewEditOutput = NanoBananaOutput;
export type Gemini3ProImagePreviewInput = NanoBananaProInput;
export type Gemini3ProImagePreviewOutput = NanoBananaOutput;
export type GeminiFlashEditInput = HunyuanWorldInput;
export type GeminiFlashEditMultiOutput = GeminiFlashEditOutput;
export type GeminiTtsOutput = DiaOutput;
export type GenFillOutput = pulidOutput;
export type GenfocusAllInFocusInput = AllInFocusInput;
export type GenfocusAllInFocusOutput = unoOutput;
export type genfocusInput = RefocusInput;
export type genfocusOutput = unoOutput;
export type ghiblifyOutput = HEDOutput;
export type GifToVideoOutput = I2VOutput;
export type GlmImageImageToImageInput = GlmImageToImageInput;
export type GlmImageImageToImageOutput = unoOutput;
export type GlmImageOutput = unoOutput;
export type GlmImageToImageOutput = unoOutput;
export type GlowOutput = BlurOutput;
export type GptImage15EditOutput = photaOutput;
export type GptImage15Output = photaOutput;
export type GptImage1EditImageOutput = photaOutput;
export type GptImage1MiniEditOutput = photaOutput;
export type GptImage1MiniInput = GptImage15Input;
export type GptImage1MiniOutput = photaOutput;
export type GptImage1TextToImageInput = GptImage15Input;
export type GptImage1TextToImageOutput = photaOutput;
export type GrainOutput = BlurOutput;
export type GreenScreenRembgOutput = GeneralRembgOutput;
export type GrokImagineImageEditInput = XAIImageEditInput;
export type GrokImagineImageEditOutput = XAIImageOutput;
export type GrokImagineImageInput = photaInput;
export type GrokImagineImageOutput = XAIImageOutput;
export type GrokImagineVideoEditVideoInput = XAIVideoEditInput;
export type GrokImagineVideoEditVideoOutput = React1Output;
export type GrokImagineVideoExtendVideoInput = XAIVideoExtensionInput;
export type GrokImagineVideoExtendVideoOutput = React1Output;
export type GrokImagineVideoImageToVideoInput = XAIImageToVideoInput;
export type GrokImagineVideoImageToVideoOutput = React1Output;
export type GrokImagineVideoReferenceToVideoInput = XAIReferenceToVideoInput;
export type GrokImagineVideoReferenceToVideoOutput = React1Output;
export type GrokImagineVideoTextToVideoInput = XAITextToVideoInput;
export type GrokImagineVideoTextToVideoOutput = React1Output;
export type GroupPhotoInput = NextSceneInput;
export type GroupPhotoOutput = pulidOutput;
export type HairChangeInput = TimeOfDayInput;
export type HairChangeOutput = pulidOutput;
export type HdrStyleOutput = AuraFlowOutput;
export type HeadshotOutput = BlurOutput;
export type HeygenAvatar3DigitalTwinOutput = I2VOutput;
export type HeygenAvatar4DigitalTwinInput = HeygenAvatar3DigitalTwinInput;
export type HeygenAvatar4DigitalTwinOutput = I2VOutput;
export type HeygenAvatar4ImageToVideoOutput = I2VOutput;
export type HeygenV2TranslatePrecisionInput = HeygenV2TranslateSpeedInput;
export type HeygenV2TranslatePrecisionOutput = I2VOutput;
export type HeygenV2TranslateSpeedOutput = I2VOutput;
export type HeygenV2VideoAgentOutput = I2VOutput;
export type HidreamE11Output = unoOutput;
export type HidreamE1FullInput = HidreamE11Input;
export type HidreamE1FullOutput = unoOutput;
export type HidreamI1DevOutput = unoOutput;
export type HidreamI1FastInput = HidreamI1DevInput;
export type HidreamI1FastOutput = unoOutput;
export type HidreamI1FullImageToImageOutput = unoOutput;
export type HidreamI1FullOutput = unoOutput;
export type HindiOutput = DiaOutput;
export type Hunyuan3dV21Input = Hunyuan3DInput;
export type Hunyuan3dV2Input = Hunyuan3DInput;
export type Hunyuan3dV2MiniInput = Hunyuan3DInput;
export type Hunyuan3dV2MiniOutput = Hunyuan3dV2Output;
export type Hunyuan3dV2MiniTurboInput = Hunyuan3DInput;
export type Hunyuan3dV2MiniTurboOutput = Hunyuan3dV2Output;
export type Hunyuan3dV2MultiViewOutput = Hunyuan3dV2Output;
export type Hunyuan3dV2MultiViewTurboInput = Hunyuan3dV2MultiViewInput;
export type Hunyuan3dV2MultiViewTurboOutput = Hunyuan3dV2Output;
export type Hunyuan3dV2TurboInput = Hunyuan3DInput;
export type Hunyuan3dV2TurboOutput = Hunyuan3dV2Output;
export type Hunyuan3dV31PartInput = PartInput;
export type Hunyuan3dV31PartOutput = PartOutput;
export type Hunyuan3dV31ProImageTo3dInput = ProImageTo3DInput;
export type Hunyuan3dV31ProImageTo3dOutput = SketchTo3DOutput;
export type Hunyuan3dV31ProTextTo3dInput = ProTextTo3DInput;
export type Hunyuan3dV31ProTextTo3dOutput = SketchTo3DOutput;
export type Hunyuan3dV31RapidImageTo3dInput = RapidImageTo3DInput;
export type Hunyuan3dV31RapidImageTo3dOutput = RapidImageTo3DOutput;
export type Hunyuan3dV31RapidTextTo3dInput = RapidTextTo3DInput;
export type Hunyuan3dV31RapidTextTo3dOutput = RapidTextTo3DOutput;
export type Hunyuan3dV31SmartTopologyInput = SmartTopologyInput;
export type Hunyuan3dV31SmartTopologyOutput = SmartTopologyOutput;
export type Hunyuan3dV3ImageTo3dOutput = SketchTo3DOutput;
export type Hunyuan3dV3SketchTo3dInput = SketchTo3DInput;
export type Hunyuan3dV3SketchTo3dOutput = SketchTo3DOutput;
export type Hunyuan3dV3TextTo3dOutput = SketchTo3DOutput;
export type HunyuanAvatarOutput = I2VOutput;
export type HunyuanCustomOutput = magiOutput;
export type HunyuanImageToImageOutput = pulidOutput;
export type HunyuanImageV21TextToImageInput = HunyuanTextToImageInput;
export type HunyuanImageV21TextToImageOutput = pulidOutput;
export type HunyuanImageV3InstructEditOutput = pulidOutput;
export type HunyuanImageV3InstructTextToImageOutput = pulidOutput;
export type HunyuanImageV3TextToImageOutput = pulidOutput;
export type HunyuanPortraitOutput = I2VOutput;
export type HunyuanTextToImageOutput = pulidOutput;
export type HunyuanTextToImageV3Output = pulidOutput;
export type HunyuanVideoFoleyOutput = I2VOutput;
export type HunyuanVideoImageToVideoOutput = magiOutput;
export type HunyuanVideoImg2vidLoraInput = BagelUnderstandInput;
export type HunyuanVideoImg2vidLoraOutput = magiOutput;
export type HunyuanVideoLoraOutput = magiOutput;
export type HunyuanVideoLoraTrainingOutput = Flux2TrainerOutput;
export type HunyuanVideoLoraVideoToVideoOutput = magiOutput;
export type HunyuanVideoV15ImageToVideoOutput = magiOutput;
export type HunyuanVideoV15TextToVideoOutput = magiOutput;
export type HunyuanVideoVideoToVideoOutput = magiOutput;
export type HunyuanWorldOutput = HEDOutput;
export type Hyper3dRodinInput = Rodin3DInput;
export type Hyper3dRodinOutput = ObjectOutput;
export type Hyper3dRodinV2Input = RodinGen2Input;
export type Hyper3dRodinV2Output = ObjectOutput;
export type I2IO3ImageOutput = BlurOutput;
export type I2IV3ImageOutput = BlurOutput;
export type I2VDirectorOutput = I2VOutput;
export type I2VLiveOutput = I2VOutput;
export type IclightV2Output = unoOutput;
export type IdentifyFaceInput = AutoCaptionOutput;
export type IdeogramCharacterEditOutput = imagen3Output;
export type IdeogramCharacterOutput = imagen3Output;
export type IdeogramCharacterRemixOutput = imagen3Output;
export type IdeogramUpscaleInput = UpscaleImageInput;
export type IdeogramUpscaleOutput = imagen3Output;
export type IdeogramV2aOutput = imagen3Output;
export type IdeogramV2aRemixInput = RemixImageInput;
export type IdeogramV2aRemixOutput = imagen3Output;
export type IdeogramV2aTurboInput = IdeogramV2aInput;
export type IdeogramV2aTurboOutput = imagen3Output;
export type IdeogramV2aTurboRemixInput = RemixImageInput;
export type IdeogramV2aTurboRemixOutput = imagen3Output;
export type IdeogramV2EditOutput = imagen3Output;
export type IdeogramV2Output = imagen3Output;
export type IdeogramV2RemixInput = RemixImageInput;
export type IdeogramV2RemixOutput = imagen3Output;
export type IdeogramV2TurboEditInput = IdeogramV2EditInput;
export type IdeogramV2TurboEditOutput = imagen3Output;
export type IdeogramV2TurboInput = IdeogramV2Input;
export type IdeogramV2TurboOutput = imagen3Output;
export type IdeogramV2TurboRemixInput = RemixImageInput;
export type IdeogramV2TurboRemixOutput = imagen3Output;
export type IdeogramV3EditOutput = imagen3Output;
export type IdeogramV3Output = imagen3Output;
export type IdeogramV3ReframeOutput = imagen3Output;
export type IdeogramV3RemixOutput = imagen3Output;
export type IdeogramV3ReplaceBackgroundOutput = imagen3Output;
export type IllusionDiffusionOutput = ccsrOutput;
export type image2svgOutput = V16Output;
export type ImageAppsV2AgeModifyInput = AgeModifyInput;
export type ImageAppsV2AgeModifyOutput = BlurOutput;
export type ImageAppsV2CityTeleportInput = CityTeleportInput;
export type ImageAppsV2CityTeleportOutput = BlurOutput;
export type ImageAppsV2ExpressionChangeOutput = BlurOutput;
export type ImageAppsV2HairChangeOutput = BlurOutput;
export type ImageAppsV2HeadshotPhotoInput = HeadshotInput;
export type ImageAppsV2HeadshotPhotoOutput = BlurOutput;
export type ImageAppsV2MakeupApplicationInput = MakeupApplicationInput;
export type ImageAppsV2MakeupApplicationOutput = BlurOutput;
export type ImageAppsV2ObjectRemovalOutput = BlurOutput;
export type ImageAppsV2OutpaintInput = OutpaintInput;
export type ImageAppsV2OutpaintOutput = BlurOutput;
export type ImageAppsV2PerspectiveInput = PerspectiveInput;
export type ImageAppsV2PerspectiveOutput = BlurOutput;
export type ImageAppsV2PhotographyEffectsInput = PhotographyEffectsInput;
export type ImageAppsV2PhotographyEffectsOutput = BlurOutput;
export type ImageAppsV2PhotoRestorationOutput = BlurOutput;
export type ImageAppsV2PortraitEnhanceInput = PortraitInput;
export type ImageAppsV2PortraitEnhanceOutput = BlurOutput;
export type ImageAppsV2ProductHoldingInput = ProductHoldingInput;
export type ImageAppsV2ProductHoldingOutput = BlurOutput;
export type ImageAppsV2ProductPhotographyInput = ProductPhotographyInput;
export type ImageAppsV2ProductPhotographyOutput = BlurOutput;
export type ImageAppsV2RelightingInput = RelightingInput;
export type ImageAppsV2RelightingOutput = BlurOutput;
export type ImageAppsV2StyleTransferOutput = BlurOutput;
export type ImageAppsV2TextureTransformInput = TextureTransformInput;
export type ImageAppsV2TextureTransformOutput = BlurOutput;
export type ImageAppsV2VirtualTryOnInput = VirtualTryOnInput;
export type ImageAppsV2VirtualTryOnOutput = BlurOutput;
export type ImageEditingAgeProgressionInput = TimeOfDayInput;
export type ImageEditingAgeProgressionOutput = pulidOutput;
export type ImageEditingBabyVersionInput = BabyVersionInput;
export type ImageEditingBabyVersionOutput = pulidOutput;
export type ImageEditingBackgroundChangeInput = TimeOfDayInput;
export type ImageEditingBackgroundChangeOutput = pulidOutput;
export type ImageEditingBroccoliHaircutInput = RealismInput;
export type ImageEditingBroccoliHaircutOutput = pulidOutput;
export type ImageEditingCartoonifyInput = BabyVersionInput;
export type ImageEditingCartoonifyOutput = pulidOutput;
export type ImageEditingColorCorrectionInput = BabyVersionInput;
export type ImageEditingColorCorrectionOutput = pulidOutput;
export type ImageEditingExpressionChangeInput = TimeOfDayInput;
export type ImageEditingExpressionChangeOutput = pulidOutput;
export type ImageEditingFaceEnhancementInput = BabyVersionInput;
export type ImageEditingFaceEnhancementOutput = pulidOutput;
export type ImageEditingHairChangeInput = TimeOfDayInput;
export type ImageEditingHairChangeOutput = pulidOutput;
export type ImageEditingObjectRemovalInput = TimeOfDayInput;
export type ImageEditingObjectRemovalOutput = pulidOutput;
export type ImageEditingPhotoRestorationInput = BabyVersionInput;
export type ImageEditingPhotoRestorationOutput = pulidOutput;
export type ImageEditingPlushieStyleInput = RealismInput;
export type ImageEditingPlushieStyleOutput = pulidOutput;
export type ImageEditingProfessionalPhotoInput = BabyVersionInput;
export type ImageEditingProfessionalPhotoOutput = pulidOutput;
export type ImageEditingRealismInput = RealismInput;
export type ImageEditingRealismOutput = pulidOutput;
export type ImageEditingReframeInput = ReframeInput;
export type ImageEditingReframeOutput = pulidOutput;
export type ImageEditingRetouchInput = RealismInput;
export type ImageEditingRetouchOutput = pulidOutput;
export type ImageEditingSceneCompositionInput = TimeOfDayInput;
export type ImageEditingSceneCompositionOutput = pulidOutput;
export type ImageEditingStyleTransferInput = TimeOfDayInput;
export type ImageEditingStyleTransferOutput = pulidOutput;
export type ImageEditingTextRemovalInput = BabyVersionInput;
export type ImageEditingTextRemovalOutput = pulidOutput;
export type ImageEditingTimeOfDayInput = TimeOfDayInput;
export type ImageEditingTimeOfDayOutput = pulidOutput;
export type ImageEditingWeatherEffectInput = TimeOfDayInput;
export type ImageEditingWeatherEffectOutput = pulidOutput;
export type ImageEditingWojakStyleInput = RealismInput;
export type ImageEditingWojakStyleOutput = pulidOutput;
export type ImageEditingYoutubeThumbnailsInput = YouTubeThumbnailsInput;
export type ImageEditingYoutubeThumbnailsOutput = pulidOutput;
export type ImageEditOutput = imagen3Output;
export type ImageExpansionInput = BriaExpandInput;
export type ImageExpansionOutput = ccsrOutput;
export type ImageGenInput = bagelInput;
export type ImageGridOutput = HEDOutput;
export type ImageMultiMeasurementInput = ArbiterImageInput;
export type Imagen3FastInput = imagen3Input;
export type Imagen3FastOutput = imagen3Output;
export type Imagen4PreviewFastOutput = NanoBananaOutput;
export type Imagen4PreviewOutput = NanoBananaOutput;
export type Imagen4PreviewUltraInput = Imagen4PreviewInput;
export type Imagen4PreviewUltraOutput = NanoBananaOutput;
export type Imagen4TextToImageFastInput = Imagen4PreviewFastInput;
export type Imagen4TextToImageFastOutput = NanoBananaOutput;
export type Imagen4TextToImageInput = Imagen4PreviewInput;
export type Imagen4TextToImageOutput = NanoBananaOutput;
export type Imagen4TextToImageUltraInput = Imagen4PreviewInput;
export type Imagen4TextToImageUltraOutput = NanoBananaOutput;
export type ImagePreprocessorsDepthAnythingV2Input = ZoeInput;
export type ImagePreprocessorsDepthAnythingV2Output = HEDOutput;
export type ImagePreprocessorsHedInput = HEDInput;
export type ImagePreprocessorsHedOutput = HEDOutput;
export type ImagePreprocessorsLineartInput = LineartInput;
export type ImagePreprocessorsLineartOutput = HEDOutput;
export type ImagePreprocessorsMidasInput = MiDaSInput;
export type ImagePreprocessorsMidasOutput = MiDaSOutput;
export type ImagePreprocessorsMlsdInput = MLSDInput;
export type ImagePreprocessorsMlsdOutput = HEDOutput;
export type ImagePreprocessorsPidiInput = PiDiInput;
export type ImagePreprocessorsPidiOutput = HEDOutput;
export type ImagePreprocessorsSamInput = ZoeInput;
export type ImagePreprocessorsSamOutput = HEDOutput;
export type ImagePreprocessorsScribbleInput = ScribbleInput;
export type ImagePreprocessorsScribbleOutput = HEDOutput;
export type ImagePreprocessorsTeedInput = ZoeInput;
export type ImagePreprocessorsTeedOutput = HEDOutput;
export type ImagePreprocessorsZoeInput = ZoeInput;
export type ImagePreprocessorsZoeOutput = HEDOutput;
export type ImageProcessingInput = PostProcessingInput;
export type ImageReferenceMeasurementInput = ArbiterImageImageInput;
export type ImageToImageOutput = TextToImageOutput;
export type ImageToVideoHailuo02FastOutput = I2VOutput;
export type ImageToVideoHailuo02Output = I2VOutput;
export type ImageToVideoOutput = TextToVideoOutput;
export type ImageToVideoTurboInput = ImageToVideov21Input;
export type ImageToVideoV21MasterOutput = I2VOutput;
export type ImageToVideoV21Output = I2VOutput;
export type ImageToVideoV21ProOutput = I2VOutput;
export type ImageToVideoV21StandardOutput = I2VOutput;
export type ImageToVideoV25ProOutput = I2VOutput;
export type ImageToVideoV25StandardOutput = I2VOutput;
export type ImageToVideoV26ProOutput = I2VOutput;
export type ImageToVideoV2MasterOutput = I2VOutput;
export type ImageToVideoV3ProOutput = I2VOutput;
export type ImageToVideoV3StandardOutput = I2VOutput;
export type ImageUnderstandingInput = BagelUnderstandInput;
export type ImageUpscaleOutput = EvfSamOutput;
export type ImageutilsDepthInput = DepthMapInput;
export type ImageutilsDepthOutput = HEDOutput;
export type ImageutilsMarigoldDepthInput = MarigoldDepthMapInput;
export type ImageutilsMarigoldDepthOutput = HEDOutput;
export type ImageutilsNsfwInput = ZoeInput;
export type ImageutilsRembgOutput = HEDOutput;
export type ImageutilsSamInput = SamInput;
export type ImageutilsSamOutput = SamOutput;
export type Imagineart15PreviewTextToImageInput = ViduQ2TextToImageInput;
export type Imagineart15ProPreviewTextToImageInput = ViduQ2TextToImageInput;
export type Imagineart15ProPreviewTextToImageOutput = BlurOutput;
export type Img2ImgOutput = unoOutput;
export type ImpulseResponseOutput = MiniOutput;
export type IndexTts2TextToSpeechOutput = DiaOutput;
export type infinitalkOutput = magiOutput;
export type InfinitalkSingleTextInput = AiAvatarSingleTextInput;
export type InfinitalkSingleTextOutput = magiOutput;
export type InfinitalkVideoToVideoOutput = magiOutput;
export type InfinityStarTextToVideoOutput = I2VOutput;
export type InpaintingSD15Input = InpaintingInput;
export type inpaintOutput = ccsrOutput;
export type InstantCharacterOutput = unoOutput;
export type IntegrateProductInput = NextSceneInput;
export type IntegrateProductOutput = pulidOutput;
export type InterleaveVideoOutput = I2VOutput;
export type InworldTtsOutput = DiaOutput;
export type IpAdapterFaceIdOutput = ccsrOutput;
export type Isaac01Input = VisionInput;
export type Isaac01Output = ChatOutput;
export type ItalianOutput = DiaOutput;
export type janusOutput = unoOutput;
export type JapaneseOutput = DiaOutput;
export type JuggernautFluxBaseImageToImageInput = DevImageToImageInput;
export type JuggernautFluxBaseImageToImageOutput = unoOutput;
export type JuggernautFluxBaseInput = bitdanceInput;
export type JuggernautFluxBaseOutput = unoOutput;
export type JuggernautFluxLightningInput = piflowInput;
export type JuggernautFluxLightningOutput = unoOutput;
export type JuggernautFluxLoraInpaintingOutput = unoOutput;
export type JuggernautFluxLoraInput = TextToImageInput;
export type JuggernautFluxLoraOutput = unoOutput;
export type JuggernautFluxProImageToImageInput = DevImageToImageInput;
export type JuggernautFluxProImageToImageOutput = unoOutput;
export type JuggernautFluxProInput = bitdanceInput;
export type JuggernautFluxProOutput = unoOutput;
export type Kandinsky5ProImageToVideoOutput = Kandinsky5TextToVideoOutput;
export type Kandinsky5ProTextToVideoOutput = Kandinsky5TextToVideoOutput;
export type Kandinsky5TextToVideoDistillOutput = Kandinsky5TextToVideoOutput;
export type Klein4BBaseEditInput = EditImageInput;
export type Klein4BBaseEditOutput = Flux2Output;
export type Klein4BBaseInput = OvisImageInput;
export type Klein4BDistilledEditOutput = Flux2Output;
export type Klein4BDistilledT2IOutput = Flux2Output;
export type Klein4BT2IOutput = Flux2Output;
export type Klein9BBaseEditOutput = Flux2Output;
export type Klein9BBaseInput = OvisImageInput;
export type Klein9BDistilledInput = piflowInput;
export type Klein9BDistilledLoRAInput = KleinLoRAInput;
export type Klein9BDistilledT2IOutput = Flux2Output;
export type Klein9BEditImageInput = EditImageInput;
export type Klein9BT2IOutput = Flux2Output;
export type KleinDistilledEditInput = Flux2Klein4bEditInput;
export type KleinDistilledEditLoRAInput = Flux2Klein4bEditLoraInput;
export type KleinDistilledInput = piflowInput;
export type KleinT2IOutput = Flux2Output;
export type KlingImageO1Output = BlurOutput;
export type KlingImageO3ImageToImageOutput = BlurOutput;
export type KlingImageO3TextToImageOutput = BlurOutput;
export type KlingImageV3ImageToImageOutput = BlurOutput;
export type KlingImageV3TextToImageOutput = BlurOutput;
export type KlingV15KolorsVirtualTryOnOutput = HEDOutput;
export type KlingV1I2VOutput = I2VOutput;
export type KlingV3ImageElementInput = ElementInput;
export type KlingVideoAiAvatarV2ProInput = AIAvatarInput;
export type KlingVideoAiAvatarV2ProOutput = AIAvatarOutput;
export type KlingVideoAiAvatarV2StandardInput = AIAvatarInput;
export type KlingVideoAiAvatarV2StandardOutput = AIAvatarOutput;
export type KlingVideoCreateVoiceInput = CreateVoiceInput;
export type KlingVideoCreateVoiceOutput = CreateVoiceOutput;
export type KlingVideoLipsyncAudioToVideoInput = lipsyncInput;
export type KlingVideoLipsyncAudioToVideoOutput = I2VOutput;
export type KlingVideoLipsyncTextToVideoOutput = I2VOutput;
export type KlingVideoO1ImageToVideoInput = OmniVideoImageToVideoInput;
export type KlingVideoO1ImageToVideoOutput = I2VOutput;
export type KlingVideoO1ReferenceToVideoInput = OmniVideoReferenceToVideoInput;
export type KlingVideoO1ReferenceToVideoOutput = I2VOutput;
export type KlingVideoO1StandardImageToVideoInput = OmniVideoImageToVideoInput;
export type KlingVideoO1StandardImageToVideoOutput = I2VOutput;
export type KlingVideoO1StandardReferenceToVideoInput =
  OmniVideoReferenceToVideoInput;
export type KlingVideoO1StandardReferenceToVideoOutput = I2VOutput;
export type KlingVideoO1StandardVideoToVideoEditInput = OmniV2VEditInput;
export type KlingVideoO1StandardVideoToVideoEditOutput = I2VOutput;
export type KlingVideoO1StandardVideoToVideoReferenceInput =
  OmniV2VReferenceInput;
export type KlingVideoO1StandardVideoToVideoReferenceOutput = I2VOutput;
export type KlingVideoO1VideoToVideoEditInput = OmniV2VEditInput;
export type KlingVideoO1VideoToVideoEditOutput = I2VOutput;
export type KlingVideoO1VideoToVideoReferenceInput = OmniV2VReferenceInput;
export type KlingVideoO1VideoToVideoReferenceOutput = I2VOutput;
export type KlingVideoO3ProImageToVideoInput = O3ProImageToVideoInput;
export type KlingVideoO3ProImageToVideoOutput = I2VOutput;
export type KlingVideoO3ProReferenceToVideoInput = O3ProReferenceVideoI2VInput;
export type KlingVideoO3ProReferenceToVideoOutput = I2VOutput;
export type KlingVideoO3ProTextToVideoInput = O3ProTextToVideoInput;
export type KlingVideoO3ProTextToVideoOutput = I2VOutput;
export type KlingVideoO3ProVideoToVideoEditInput = O3ProEditVideoV2VInput;
export type KlingVideoO3ProVideoToVideoEditOutput = I2VOutput;
export type KlingVideoO3ProVideoToVideoReferenceInput =
  O3ProReferenceVideoV2VInput;
export type KlingVideoO3ProVideoToVideoReferenceOutput = I2VOutput;
export type KlingVideoO3StandardImageToVideoInput = O3ProImageToVideoInput;
export type KlingVideoO3StandardImageToVideoOutput = I2VOutput;
export type KlingVideoO3StandardReferenceToVideoInput =
  O3ProReferenceVideoI2VInput;
export type KlingVideoO3StandardReferenceToVideoOutput = I2VOutput;
export type KlingVideoO3StandardTextToVideoInput = O3ProTextToVideoInput;
export type KlingVideoO3StandardTextToVideoOutput = I2VOutput;
export type KlingVideoO3StandardVideoToVideoEditInput = O3ProEditVideoV2VInput;
export type KlingVideoO3StandardVideoToVideoEditOutput = I2VOutput;
export type KlingVideoO3StandardVideoToVideoReferenceInput =
  O3ProReferenceVideoV2VInput;
export type KlingVideoO3StandardVideoToVideoReferenceOutput = I2VOutput;
export type KlingVideoV15ProEffectsOutput = I2VOutput;
export type KlingVideoV15ProImageToVideoOutput = I2VOutput;
export type KlingVideoV15ProTextToVideoOutput = I2VOutput;
export type KlingVideoV16ProEffectsInput = KlingVideoV15ProEffectsInput;
export type KlingVideoV16ProEffectsOutput = I2VOutput;
export type KlingVideoV16ProElementsOutput = I2VOutput;
export type KlingVideoV16ProImageToVideoOutput = I2VOutput;
export type KlingVideoV16ProTextToVideoInput = KlingVideoV15ProTextToVideoInput;
export type KlingVideoV16ProTextToVideoOutput = I2VOutput;
export type KlingVideoV16StandardEffectsInput = KlingVideoV15ProEffectsInput;
export type KlingVideoV16StandardEffectsOutput = I2VOutput;
export type KlingVideoV16StandardElementsInput = KlingVideoV16ProElementsInput;
export type KlingVideoV16StandardElementsOutput = I2VOutput;
export type KlingVideoV16StandardImageToVideoInput =
  KlingVideoV2MasterImageToVideoInput;
export type KlingVideoV16StandardImageToVideoOutput = I2VOutput;
export type KlingVideoV16StandardTextToVideoInput =
  KlingVideoV15ProTextToVideoInput;
export type KlingVideoV16StandardTextToVideoOutput = I2VOutput;
export type KlingVideoV1ProAiAvatarInput = AIAvatarInput;
export type KlingVideoV1ProAiAvatarOutput = AIAvatarOutput;
export type KlingVideoV1StandardAiAvatarInput = AIAvatarInput;
export type KlingVideoV1StandardAiAvatarOutput = AIAvatarOutput;
export type KlingVideoV1StandardEffectsInput = KlingVideoV15ProEffectsInput;
export type KlingVideoV1StandardEffectsOutput = I2VOutput;
export type KlingVideoV1StandardImageToVideoOutput = I2VOutput;
export type KlingVideoV1StandardTextToVideoOutput = I2VOutput;
export type KlingVideoV1TtsOutput = DiaOutput;
export type KlingVideoV21MasterImageToVideoInput =
  KlingVideoV2MasterImageToVideoInput;
export type KlingVideoV21MasterImageToVideoOutput = I2VOutput;
export type KlingVideoV21MasterTextToVideoInput =
  KlingVideoV15ProTextToVideoInput;
export type KlingVideoV21MasterTextToVideoOutput = I2VOutput;
export type KlingVideoV21StandardImageToVideoInput =
  KlingVideoV2MasterImageToVideoInput;
export type KlingVideoV21StandardImageToVideoOutput = I2VOutput;
export type KlingVideoV25TurboProImageToVideoOutput = I2VOutput;
export type KlingVideoV25TurboProTextToVideoInput =
  KlingVideoV15ProTextToVideoInput;
export type KlingVideoV25TurboProTextToVideoOutput = I2VOutput;
export type KlingVideoV25TurboStandardImageToVideoInput =
  KlingVideoV2MasterImageToVideoInput;
export type KlingVideoV25TurboStandardImageToVideoOutput = I2VOutput;
export type KlingVideoV26ProImageToVideoOutput = I2VOutput;
export type KlingVideoV26ProMotionControlOutput = I2VOutput;
export type KlingVideoV26ProTextToVideoOutput = I2VOutput;
export type KlingVideoV26StandardMotionControlInput =
  KlingVideoV26ProMotionControlInput;
export type KlingVideoV26StandardMotionControlOutput = I2VOutput;
export type KlingVideoV2MasterImageToVideoOutput = I2VOutput;
export type KlingVideoV2MasterTextToVideoInput =
  KlingVideoV15ProTextToVideoInput;
export type KlingVideoV2MasterTextToVideoOutput = I2VOutput;
export type KlingVideoV3ProImageToVideoOutput = I2VOutput;
export type KlingVideoV3ProMotionControlOutput = I2VOutput;
export type KlingVideoV3ProTextToVideoOutput = I2VOutput;
export type KlingVideoV3StandardImageToVideoInput =
  KlingVideoV3ProImageToVideoInput;
export type KlingVideoV3StandardImageToVideoOutput = I2VOutput;
export type KlingVideoV3StandardMotionControlInput =
  KlingVideoV3ProMotionControlInput;
export type KlingVideoV3StandardMotionControlOutput = I2VOutput;
export type KlingVideoV3StandardTextToVideoInput =
  KlingVideoV3ProTextToVideoInput;
export type KlingVideoV3StandardTextToVideoOutput = I2VOutput;
export type KlingVideoVideoToAudioInput = VideoToAudioInput;
export type KlingVideoVideoToAudioOutput = VideoToAudioOutput;
export type KokoroAmericanEnglishOutput = DiaOutput;
export type KokoroBrazilianPortugueseInput = KokoroHindiInput;
export type KokoroBrazilianPortugueseOutput = DiaOutput;
export type KokoroBritishEnglishInput = KokoroHindiInput;
export type KokoroBritishEnglishOutput = DiaOutput;
export type KokoroFrenchOutput = DiaOutput;
export type KokoroHindiOutput = DiaOutput;
export type KokoroItalianInput = KokoroHindiInput;
export type KokoroItalianOutput = DiaOutput;
export type KokoroJapaneseInput = KokoroHindiInput;
export type KokoroJapaneseOutput = DiaOutput;
export type KokoroMandarinChineseInput = KokoroHindiInput;
export type KokoroMandarinChineseOutput = DiaOutput;
export type KokoroSpanishInput = KokoroHindiInput;
export type KokoroSpanishOutput = DiaOutput;
export type KolorsImageToImageInput = KolorsImg2ImgInput;
export type KolorsImageToImageOutput = unoOutput;
export type KolorsInput = kolorsInput;
export type kolorsOutput = unoOutput;
export type KontextEditOutput = unoOutput;
export type KontextImg2ImgOutput = unoOutput;
export type KontextInpaintOutput = unoOutput;
export type KontextT2IOutput = unoOutput;
export type KontextText2ImageOutput = unoOutput;
export type KreaOutput = unoOutput;
export type KreaReduxOutput = unoOutput;
export type KreaWan14bTextToVideoOutput = I2VOutput;
export type KreaWan14bVideoToVideoInput = VideoToVideoInput;
export type KreaWan14bVideoToVideoOutput = I2VOutput;
export type latentsyncOutput = I2VOutput;
export type LayerDiffusionOutput = ccsrOutput;
export type LcmSd15I2iOutput = lcmOutput;
export type LeffaPoseTransferInput = PoseTransferInput;
export type LeffaPoseTransferOutput = VTONOutput;
export type LeffaVirtualTryonInput = VTONInput;
export type LeffaVirtualTryonOutput = VTONOutput;
export type LightingRestorationInput = RemoveLightingInput;
export type LightingRestorationOutput = pulidOutput;
export type LightningModelsOutput = unoOutput;
export type LightxRecameraOutput = LightxRelightOutput;
export type LineartOutput = HEDOutput;
export type LipsyncA2VOutput = I2VOutput;
export type lipsyncOutput = I2VOutput;
export type LipsyncOutput = I2VOutput;
export type LipSyncOutput = I2VOutput;
export type LipSyncV2Input = LipSyncInput;
export type LipSyncV2Output = I2VOutput;
export type LipSyncV2ProInput = Sync3Input;
export type LipSyncV2ProOutput = I2VOutput;
export type LiveAvatarOutput = lynxOutput;
export type LivePortraitImageOutput = HEDOutput;
export type LivePortraitOutput = I2VOutput;
export type LlavaNextOutput = SpeechOutput;
export type Llavav1513bInput = LlavaNextInput;
export type Llavav1513bOutput = SpeechOutput;
export type LongcatImageEditOutput = unoOutput;
export type LongcatImageInput = FluxDevInput;
export type LongcatImageOutput = unoOutput;
export type LongcatMultiAvatarImageAudioToVideoOutput = magiOutput;
export type LongcatSingleAvatarAudioToVideoOutput = magiOutput;
export type LongcatSingleAvatarImageAudioToVideoOutput = magiOutput;
export type LongcatVideoDistilledImageToVideo480pOutput = ExtendVideoOutput;
export type LongcatVideoDistilledImageToVideo720pOutput = ExtendVideoOutput;
export type LongcatVideoDistilledTextToVideo480pOutput = ExtendVideoOutput;
export type LongcatVideoDistilledTextToVideo720pOutput = ExtendVideoOutput;
export type LongcatVideoImageToVideo480pOutput = ExtendVideoOutput;
export type LongcatVideoImageToVideo720pOutput = ExtendVideoOutput;
export type LongcatVideoTextToVideo480pOutput = ExtendVideoOutput;
export type LongcatVideoTextToVideo720pOutput = ExtendVideoOutput;
export type LoraImageToImageOutput = loraOutput;
export type LoraInpaintOutput = loraOutput;
export type Ltx219bAudioToVideoInput = LTX2AudioToVideoInput;
export type Ltx219bAudioToVideoLoraInput = LTX2LoRAAudioToVideoInput;
export type Ltx219bAudioToVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bAudioToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledAudioToVideoInput = LTX2DistilledAudioToVideoInput;
export type Ltx219bDistilledAudioToVideoLoraInput =
  LTX2LoRADistilledAudioToVideoInput;
export type Ltx219bDistilledAudioToVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledAudioToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledExtendVideoInput = LTX2DistilledExtendVideoInput;
export type Ltx219bDistilledExtendVideoLoraInput =
  LTX2LoRADistilledExtendVideoInput;
export type Ltx219bDistilledExtendVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledExtendVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledImageToVideoInput = LTX2DistilledImageToVideoInput;
export type Ltx219bDistilledImageToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledTextToVideoInput = LTX2DistilledTextToVideoInput;
export type Ltx219bDistilledTextToVideoLoraInput =
  LTX2LoRADistilledTextToVideoInput;
export type Ltx219bDistilledTextToVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledTextToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledVideoToVideoInput = LTX2DistilledVideoToVideoInput;
export type Ltx219bDistilledVideoToVideoLoraInput =
  LTX2LoRADistilledVideoToVideoInput;
export type Ltx219bDistilledVideoToVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bDistilledVideoToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bExtendVideoInput = LTX2ExtendVideoInput;
export type Ltx219bExtendVideoLoraInput = LTX2LoRAExtendVideoInput;
export type Ltx219bExtendVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bExtendVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bImageToVideoInput = LTX2ImageToVideoInput;
export type Ltx219bImageToVideoLoraInput = LTX2LoRAImageToVideoInput;
export type Ltx219bImageToVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bImageToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bTextToVideoInput = LTX2TextToVideoInput;
export type Ltx219bTextToVideoLoraInput = LTX2LoRATextToVideoInput;
export type Ltx219bTextToVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bTextToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx219bVideoToVideoInput = LTX2VideoToVideoInput;
export type Ltx219bVideoToVideoLoraInput = LTX2LoRAVideoToVideoInput;
export type Ltx219bVideoToVideoLoraOutput = LTX2ExtendVideoOutput;
export type Ltx219bVideoToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx23AudioToVideoOutput = React1Output;
export type Ltx23ExtendVideoInput = Ltx2ExtendVideoInput;
export type Ltx23ExtendVideoOutput = React1Output;
export type Ltx23ImageToVideoFastInput = Ltx23ImageToVideoInput;
export type Ltx23ImageToVideoFastOutput = React1Output;
export type Ltx23ImageToVideoOutput = React1Output;
export type Ltx23RetakeVideoInput = Ltx2RetakeVideoInput;
export type Ltx23RetakeVideoOutput = React1Output;
export type Ltx23TextToVideoFastInput = Ltx23TextToVideoInput;
export type Ltx23TextToVideoFastOutput = React1Output;
export type Ltx23TextToVideoOutput = React1Output;
export type Ltx2AudioToVideoOutput = React1Output;
export type LTX2AudioToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx2ExtendVideoOutput = React1Output;
export type Ltx2ImageToVideoFastInput = Ltx2ImageToVideoInput;
export type Ltx2ImageToVideoFastOutput = React1Output;
export type Ltx2ImageToVideoOutput = React1Output;
export type LTX2ImageToVideoOutput = LTX2ExtendVideoOutput;
export type Ltx2RetakeVideoOutput = React1Output;
export type Ltx2TextToVideoFastInput = Ltx2TextToVideoInput;
export type Ltx2TextToVideoFastOutput = React1Output;
export type Ltx2TextToVideoOutput = React1Output;
export type LTX2TextToVideoOutput = LTX2ExtendVideoOutput;
export type LTX2VideoToVideoOutput = LTX2ExtendVideoOutput;
export type Ltxv13b098DistilledExtendInput = DistilledExtendVideoInput;
export type Ltxv13b098DistilledExtendOutput = ExtendVideoOutput;
export type Ltxv13b098DistilledImageToVideoInput = DistilledImageToVideoInput;
export type Ltxv13b098DistilledImageToVideoOutput = ExtendVideoOutput;
export type Ltxv13b098DistilledInput = LtxVideo13bDevInput;
export type Ltxv13b098DistilledMulticonditioningInput =
  DistilledMultiConditioningVideoInput;
export type Ltxv13b098DistilledMulticonditioningOutput = ExtendVideoOutput;
export type Ltxv13b098DistilledOutput = ExtendVideoOutput;
export type LtxVideo13bDevExtendInput = DistilledExtendVideoInput;
export type LtxVideo13bDevExtendOutput = ExtendVideoOutput;
export type LtxVideo13bDevImageToVideoInput = DistilledImageToVideoInput;
export type LtxVideo13bDevImageToVideoOutput = ExtendVideoOutput;
export type LtxVideo13bDevMulticonditioningInput =
  DistilledMultiConditioningVideoInput;
export type LtxVideo13bDevMulticonditioningOutput = ExtendVideoOutput;
export type LtxVideo13bDevOutput = ExtendVideoOutput;
export type LtxVideo13bDistilledExtendInput = DistilledExtendVideoInput;
export type LtxVideo13bDistilledExtendOutput = ExtendVideoOutput;
export type LtxVideo13bDistilledImageToVideoInput = DistilledImageToVideoInput;
export type LtxVideo13bDistilledImageToVideoOutput = ExtendVideoOutput;
export type LtxVideo13bDistilledInput = LtxVideo13bDevInput;
export type LtxVideo13bDistilledMulticonditioningInput =
  DistilledMultiConditioningVideoInput;
export type LtxVideo13bDistilledMulticonditioningOutput = ExtendVideoOutput;
export type LtxVideo13bDistilledOutput = ExtendVideoOutput;
export type LtxVideoImageToVideoOutput = magiOutput;
export type LtxVideoLoraImageToVideoOutput = ExtendVideoOutput;
export type LtxVideoLoraMulticonditioningInput = MulticonditioningVideoInput;
export type LtxVideoLoraMulticonditioningOutput = ExtendVideoOutput;
export type LtxVideoLoraOutput = ExtendVideoOutput;
export type LtxVideoOutput = magiOutput;
export type LtxVideoV095ExtendInput = ExtendVideoInput;
export type LtxVideoV095ExtendOutput = magiOutput;
export type LtxVideoV095ImageToVideoOutput = magiOutput;
export type LtxVideoV095MulticonditioningInput = MultiConditioningVideoInput;
export type LtxVideoV095MulticonditioningOutput = magiOutput;
export type LtxVideoV095Output = magiOutput;
export type LtxVideoV097ExtendInput = ExtendVideoInput;
export type LtxVideoV097ExtendOutput = magiOutput;
export type LtxVideoV097ImageToVideoInput = LtxVideoV095ImageToVideoInput;
export type LtxVideoV097ImageToVideoOutput = magiOutput;
export type LtxVideoV097Input = LtxVideoV095Input;
export type LtxVideoV097MulticonditioningInput = MultiConditioningVideoInput;
export type LtxVideoV097MulticonditioningOutput = magiOutput;
export type LtxVideoV097Output = magiOutput;
export type lucidfluxOutput = ccsrOutput;
export type LucyEditDevOutput = I2VOutput;
export type LucyEditFastInput = LucyEditDevInput;
export type LucyEditFastOutput = I2VOutput;
export type LucyEditProOutput = I2VOutput;
export type LucyI2vOutput = I2VOutput;
export type LucyRestyleOutput = I2VOutput;
export type LumaDreamMachineRay2FlashImageToVideoInput =
  LumaDreamMachineRay2ImageToVideoInput;
export type LumaDreamMachineRay2FlashImageToVideoOutput = I2VOutput;
export type LumaDreamMachineRay2FlashInput = LumaDreamMachineRay2Input;
export type LumaDreamMachineRay2FlashModifyOutput = I2VOutput;
export type LumaDreamMachineRay2FlashOutput = I2VOutput;
export type LumaDreamMachineRay2FlashReframeInput =
  LumaDreamMachineRay2ReframeInput;
export type LumaDreamMachineRay2FlashReframeOutput = I2VOutput;
export type LumaDreamMachineRay2ImageToVideoOutput = I2VOutput;
export type LumaDreamMachineRay2Output = I2VOutput;
export type LumaDreamMachineRay2ReframeOutput = I2VOutput;
export type LumaPhotonFlashInput = LumaPhotonInput;
export type LumaPhotonFlashModifyInput = LumaPhotonModifyInput;
export type LumaPhotonFlashModifyOutput = V16Output;
export type LumaPhotonFlashOutput = V16Output;
export type LumaPhotonFlashReframeInput = LumaPhotonReframeInput;
export type LumaPhotonFlashReframeOutput = V16Output;
export type LumaPhotonModifyOutput = V16Output;
export type LumaPhotonOutput = V16Output;
export type LumaPhotonReframeOutput = V16Output;
export type LuminaImageV2Output = unoOutput;
export type lyria2Output = DiaOutput;
export type MagiDistilledExtendVideoInput = MagiExtendVideoInput;
export type MagiDistilledExtendVideoOutput = magiOutput;
export type MagiDistilledImageToVideoInput = MagiImageToVideoInput;
export type MagiDistilledImageToVideoOutput = magiOutput;
export type MagiDistilledInput = magiInput;
export type MagiDistilledOutput = magiOutput;
export type MagiExtendVideoOutput = magiOutput;
export type MagiImageToVideoOutput = magiOutput;
export type MakeupApplicationOutput = BlurOutput;
export type MandarinOutput = DiaOutput;
export type MareyI2vOutput = I2VOutput;
export type MareyMotionTransferInput = MareyPoseTransferInput;
export type MareyMotionTransferOutput = I2VOutput;
export type MareyPoseTransferOutput = I2VOutput;
export type MareyT2vOutput = I2VOutput;
export type MarigoldDepthMapOutput = HEDOutput;
export type MayaStreamInput = mayaInput;
export type MayaVoiceBatchInput = MayaBatchInput;
export type MayaVoiceBatchOutput = MayaBatchOutput;
export type MayaVoiceInput = mayaInput;
export type MayaVoiceOutput = mayaOutput;
export type MayaVoiceStreamingInput = mayaInput;
export type MergeAudioIntoVideoOutput = I2VOutput;
export type MergeAudioOutput = MiniOutput;
export type MergeAudiosOutput = DiaOutput;
export type MergeImagesOutput = BlurOutput;
export type MergeTextOutput = DiaTtsInput;
export type MeshyV5MultiImageTo3dInput = MultiImageTo3DInput;
export type MeshyV5MultiImageTo3dOutput = ImageTo3DOutput;
export type MeshyV5RetextureInput = RetextureInput;
export type MeshyV5RetextureOutput = RetextureOutput;
export type MeshyV6ImageTo3dInput = ImageTo3DInput;
export type MeshyV6ImageTo3dOutput = ImageTo3DOutput;
export type MeshyV6PreviewTextTo3dInput = TextTo3DInput;
export type MeshyV6PreviewTextTo3dOutput = TextTo3DOutput;
export type MeshyV6TextTo3dInput = TextTo3DInput;
export type MeshyV6TextTo3dOutput = TextTo3DOutput;
export type MinimaxHailuo02FastImageToVideoInput =
  FastImageToVideoHailuo02Input;
export type MinimaxHailuo02FastImageToVideoOutput = I2VOutput;
export type MinimaxHailuo02ProImageToVideoInput = ProImageToVideoHailuo02Input;
export type MinimaxHailuo02ProImageToVideoOutput = I2VOutput;
export type MinimaxHailuo02ProTextToVideoInput = MinimaxVideo01Input;
export type MinimaxHailuo02ProTextToVideoOutput = I2VOutput;
export type MinimaxHailuo02StandardImageToVideoInput =
  StandardImageToVideoHailuo02Input;
export type MinimaxHailuo02StandardImageToVideoOutput = I2VOutput;
export type MinimaxHailuo02StandardTextToVideoInput =
  StandardTextToVideoHailuo02Input;
export type MinimaxHailuo02StandardTextToVideoOutput = I2VOutput;
export type MinimaxHailuo23FastProImageToVideoInput =
  ProImageToVideoHailuo23Input;
export type MinimaxHailuo23FastProImageToVideoOutput = I2VOutput;
export type MinimaxHailuo23FastStandardImageToVideoInput =
  FastImageToVideoHailuo02Input;
export type MinimaxHailuo23FastStandardImageToVideoOutput = I2VOutput;
export type MinimaxHailuo23ProImageToVideoInput = ProImageToVideoHailuo23Input;
export type MinimaxHailuo23ProImageToVideoOutput = I2VOutput;
export type MinimaxHailuo23ProTextToVideoInput = MinimaxVideo01Input;
export type MinimaxHailuo23ProTextToVideoOutput = I2VOutput;
export type MinimaxHailuo23StandardImageToVideoInput =
  FastImageToVideoHailuo02Input;
export type MinimaxHailuo23StandardImageToVideoOutput = I2VOutput;
export type MinimaxHailuo23StandardTextToVideoInput =
  StandardTextToVideoHailuo02Input;
export type MinimaxHailuo23StandardTextToVideoOutput = I2VOutput;
export type MinimaxImage01Output = V16Output;
export type MinimaxImage01SubjectReferenceOutput = V16Output;
export type MinimaxMusicInput = zonosInput;
export type MinimaxMusicOutput = DiaOutput;
export type MinimaxMusicV15Input = MinimaxMusicV2Input;
export type MinimaxMusicV15Output = DiaOutput;
export type MinimaxMusicV2Output = DiaOutput;
export type MinimaxPreviewSpeech25TurboInput = MinimaxSpeech02HdInput;
export type MinimaxPreviewSpeech25TurboOutput = TextToSpeechOutput;
export type MinimaxSpeech02HdOutput = TextToSpeechOutput;
export type MinimaxSpeech02TurboInput = MinimaxSpeech02HdInput;
export type MinimaxSpeech02TurboOutput = TextToSpeechOutput;
export type MinimaxSpeech26HdOutput = TextToSpeechOutput;
export type MinimaxSpeech26TurboInput = MinimaxSpeech26HdInput;
export type MinimaxSpeech26TurboOutput = TextToSpeechOutput;
export type MinimaxSpeech28HdOutput = TextToSpeechOutput;
export type MinimaxSpeech28TurboInput = MinimaxSpeech28HdInput;
export type MinimaxSpeech28TurboOutput = TextToSpeechOutput;
export type MiniMaxTextToImageOutput = V16Output;
export type MiniMaxTextToImageWithReferenceOutput = V16Output;
export type MinimaxVideo01DirectorImageToVideoInput =
  ProImageToVideoHailuo23Input;
export type MinimaxVideo01DirectorImageToVideoOutput = I2VOutput;
export type MinimaxVideo01DirectorInput = MinimaxVideo01Input;
export type MinimaxVideo01DirectorOutput = I2VOutput;
export type MinimaxVideo01ImageToVideoInput = ProImageToVideoHailuo23Input;
export type MinimaxVideo01ImageToVideoOutput = I2VOutput;
export type MinimaxVideo01LiveImageToVideoInput = ProImageToVideoHailuo23Input;
export type MinimaxVideo01LiveImageToVideoOutput = I2VOutput;
export type MinimaxVideo01Output = I2VOutput;
export type MinimaxVideo01SubjectReferenceOutput = I2VOutput;
export type MinimaxVoiceCloneOutput = VoiceCloneOutput;
export type MinimaxVoiceDesignOutput = VoiceDesignOutput;
export type MixDehazeNetOutput = HEDOutput;
export type MLSDOutput = HEDOutput;
export type MmaudioV2TextToAudioOutput = DiaOutput;
export type MochiV1Output = I2VOutput;
export type ModifyOutput = I2VOutput;
export type moondream2Input = ZoeInput;
export type Moondream2ObjectDetectionInput = MoondreamObjectInput;
export type Moondream2ObjectDetectionOutput = MoondreamObjectOutput;
export type moondream2Output = MoondreamOutput;
export type Moondream2PointObjectDetectionInput = MoondreamObjectInput;
export type Moondream2PointObjectDetectionOutput = MoondreamObjectOutput;
export type Moondream2VisualQueryInput = HunyuanWorldInput;
export type Moondream2VisualQueryOutput = MoondreamOutput;
export type Moondream3PreviewCaptionInput = MoondreamCaptionInput;
export type Moondream3PreviewCaptionOutput = MoondreamCaptionOutput;
export type Moondream3PreviewDetectInput = MoondreamPointInput;
export type Moondream3PreviewDetectOutput = MoondreamDetectOutput;
export type Moondream3PreviewPointInput = MoondreamPointInput;
export type Moondream3PreviewPointOutput = MoondreamPointOutput;
export type Moondream3PreviewQueryInput = MoondreamQueryInput;
export type Moondream3PreviewQueryOutput = MoondreamQueryOutput;
export type Moondream3PreviewSegmentInput = MoondreamSegementationInput;
export type Moondream3PreviewSegmentOutput = MoondreamSegementationOutput;
export type MoondreamDetectInput = MoondreamPointInput;
export type MoondreamInput = ZoeInput;
export type MoondreamNextBatchInput = BatchQueryInput;
export type MoondreamNextBatchOutput = BatchMoonDreamOutput;
export type MoondreamNextDetectionInput = DetectionInput;
export type MoondreamNextDetectionOutput = DetectionOutput;
export type MoondreamNextInput = QueryInput;
export type MoondreamNextOutput = MoondreamOutput;
export type MoonDreamOutput = MoondreamOutput;
export type MotionControlOutput = I2VOutput;
export type MotionControlV3ProOutput = I2VOutput;
export type MotionControlV3StandardOutput = I2VOutput;
export type MulticonditioningVideoOutput = magiOutput;
export type MultiConditioningVideoOutput = ExtendVideoOutput;
export type MultiImageTo3DOutput = ImageTo3DOutput;
export type MultipleAnglesOutput = pulidOutput;
export type MultishotMasterOutput = SanaVideoOutput;
export type MultiViewObjectOutput = Hunyuan3dV2Output;
export type MusePoseOutput = I2VOutput;
export type musetalkOutput = I2VOutput;
export type MusicGeneratorOutput = StableAudioOutput;
export type MusicOutput = DiaOutput;
export type MusicV15Output = DiaOutput;
export type NafnetDeblurInput = NafnetInput;
export type NafnetDeblurOutput = HEDOutput;
export type NafnetDenoiseInput = NafnetInput;
export type NafnetDenoiseOutput = HEDOutput;
export type NafnetOutput = HEDOutput;
export type NanoBanana2EditOutput = NanoBananaOutput;
export type NanoBanana2ImageToImageInput = NanoBanana2EditInput;
export type NanoBanana2ImageToImageOutput = NanoBananaOutput;
export type NanoBanana2Output = NanoBananaOutput;
export type NanoBanana2TextToImageInput = NanoBanana2Input;
export type NanoBanana2TextToImageOutput = NanoBananaOutput;
export type NanoBananaEditOutput = NanoBananaOutput;
export type NanoBananaImageToImageInput = NanoBananaProEditInput;
export type NanoBananaImageToImageOutput = NanoBananaOutput;
export type NanoBananaProEditOutput = NanoBananaOutput;
export type NanoBananaProOutput = NanoBananaOutput;
export type NanoBananaTextToImageInput = NanoBananaInput;
export type NanoBananaTextToImageOutput = NanoBananaOutput;
export type NemotronAsrOutput = SpeechOutput;
export type NemotronAsrStreamInput = NemotronAsrInput;
export type NextSceneOutput = pulidOutput;
export type Nextstep1Output = ccsrOutput;
export type NovaSRTimings = LavaSRTimings;
export type NSFWImageDetectionInput = ZoeInput;
export type NSFWImageDetectionOutput = ImageutilsNsfwOutput;
export type O3ProEditV2VOutput = I2VOutput;
export type O3ProImageR2VOutput = I2VOutput;
export type O3ProImageToVideoOutput = I2VOutput;
export type O3ProReferenceV2VOutput = I2VOutput;
export type O3ProTextToVideoOutput = I2VOutput;
export type O3StandardEditV2VOutput = I2VOutput;
export type O3StandardEditVideoV2VInput = O3ProEditVideoV2VInput;
export type O3StandardImageR2VOutput = I2VOutput;
export type O3StandardImageToVideoInput = O3ProImageToVideoInput;
export type O3StandardImageToVideoOutput = I2VOutput;
export type O3StandardReferenceV2VOutput = I2VOutput;
export type O3StandardReferenceVideoI2VInput = O3ProReferenceVideoI2VInput;
export type O3StandardReferenceVideoV2VInput = O3ProReferenceVideoV2VInput;
export type O3StandardTextToVideoInput = O3ProTextToVideoInput;
export type O3StandardTextToVideoOutput = I2VOutput;
export type ObjectRemovalInput = PromptInput;
export type ObjectRemovalMaskInput = MaskInput;
export type ObjectRemovalMaskOutput = BlurOutput;
export type ObjectRemovalOutput = BlurOutput;
export type OCRBoundingBoxSingle = BoundingBox;
export type OmnigenV1Output = unoOutput;
export type OmnigenV2Output = unoOutput;
export type OmniHumanOutput = AIAvatarOutput;
export type OmniHumanv15Output = AIAvatarOutput;
export type OmniImageOutput = BlurOutput;
export type OmnilottieImageToLottieInput = ImageToLottieInput;
export type OmnilottieImageToLottieOutput = omnilottieOutput;
export type OmnilottieVideoToLottieInput = VideoToLottieInput;
export type OmnilottieVideoToLottieOutput = omnilottieOutput;
export type OmniV2VEditOutput = I2VOutput;
export type OmniV2VReferenceOutput = I2VOutput;
export type OmniVideoImageToVideoOutput = I2VOutput;
export type OmniVideoReferenceToVideoOutput = I2VOutput;
export type OmniZeroOutput = HEDOutput;
export type onerewardOutput = unoOutput;
export type OrpheusTtsOutput = DiaOutput;
export type OutpaintOutput = BlurOutput;
export type OverlayImageOutput = HEDOutput;
export type OverlayVideoOutput = I2VOutput;
export type OvisImageOutput = unoOutput;
export type ParabolizeOutput = BlurOutput;
export type PerspectiveOutput = BlurOutput;
export type PhotaCreateProfileOutput = PhotaProfileOutput;
export type PhotaEditOutput = photaOutput;
export type PhotaEnhanceOutput = photaOutput;
export type PhotaImageEditInput = PhotaEditInput;
export type PhotaImageEditOutput = photaOutput;
export type PhotaImageEnhanceInput = PhotaEnhanceInput;
export type PhotaImageEnhanceOutput = photaOutput;
export type PhotaImageGenerateInput = photaInput;
export type PhotaImageOutput = photaOutput;
export type PhotaProfileCreateInput = PhotaCreateProfileInput;
export type PhotaProfileDeleteInput = PhotaProfileOutput;
export type PhotographyEffectsOutput = BlurOutput;
export type photomakerOutput = pulidOutput;
export type PhotoRestorationInput = BabyVersionInput;
export type PhotoRestorationOutput = pulidOutput;
export type PhysicEditOutput = unoOutput;
export type PiDiOutput = HEDOutput;
export type piflowOutput = pulidOutput;
export type Pika22ImageToVideoOutput = I2VOutput;
export type Pika22KeyframesToVideoOutput = I2VOutput;
export type Pika22PikascenesOutput = I2VOutput;
export type Pika22TextToVideoOutput = I2VOutput;
export type Pika25ImageToVideoOutput = I2VOutput;
export type Pika25KeyframesToVideoOutput = I2VOutput;
export type Pika25TextToVideoOutput = I2VOutput;
export type PikadditionsOutput = I2VOutput;
export type PikaffectsOutput = I2VOutput;
export type PikaswapsOutput = I2VOutput;
export type PikaV15PikaffectsOutput = I2VOutput;
export type PikaV21ImageToVideoInput = ImageToVideov21Input;
export type PikaV21ImageToVideoOutput = I2VOutput;
export type PikaV21TextToVideoInput = TextToVideov21Input;
export type PikaV21TextToVideoOutput = I2VOutput;
export type PikaV22ImageToVideoOutput = I2VOutput;
export type PikaV22PikaframesOutput = I2VOutput;
export type PikaV22TextToVideoOutput = I2VOutput;
export type PikaV2PikadditionsOutput = I2VOutput;
export type PikaV2TurboImageToVideoInput = ImageToVideov21Input;
export type PikaV2TurboImageToVideoOutput = I2VOutput;
export type PikaV2TurboTextToVideoInput = TextToVideov21Input;
export type PikaV2TurboTextToVideoOutput = I2VOutput;
export type PixartSigmaOutput = unoOutput;
export type PixverseExtendFastOutput = I2VOutput;
export type PixverseExtendOutput = I2VOutput;
export type PixverseLipsyncOutput = I2VOutput;
export type PixverseSoundEffectsOutput = I2VOutput;
export type PixverseSwapOutput = I2VOutput;
export type PixverseV35ImageToVideoInput = PixverseV5ImageToVideoInput;
export type PixverseV35ImageToVideoOutput = I2VOutput;
export type PixverseV35TextToVideoFastInput = PixverseV4TextToVideoFastInput;
export type PixverseV35TextToVideoFastOutput = I2VOutput;
export type PixverseV35TextToVideoInput = PixverseV4TextToVideoInput;
export type PixverseV35TextToVideoOutput = I2VOutput;
export type PixverseV45EffectsInput = EffectInput;
export type PixverseV45EffectsOutput = I2VOutput;
export type PixverseV45ImageToVideoFastInput = PixverseV4ImageToVideoFastInput;
export type PixverseV45ImageToVideoFastOutput = I2VOutput;
export type PixverseV45ImageToVideoInput = PixverseV4ImageToVideoInput;
export type PixverseV45ImageToVideoOutput = I2VOutput;
export type PixverseV45TextToVideoFastInput = PixverseV4TextToVideoFastInput;
export type PixverseV45TextToVideoFastOutput = I2VOutput;
export type PixverseV45TextToVideoInput = PixverseV4TextToVideoInput;
export type PixverseV45TextToVideoOutput = I2VOutput;
export type PixverseV45TransitionInput = PixverseV5TransitionInput;
export type PixverseV45TransitionOutput = I2VOutput;
export type PixverseV4EffectsInput = EffectInput;
export type PixverseV4EffectsOutput = I2VOutput;
export type PixverseV4ImageToVideoFastOutput = I2VOutput;
export type PixverseV4ImageToVideoOutput = I2VOutput;
export type PixverseV4TextToVideoFastOutput = I2VOutput;
export type PixverseV4TextToVideoOutput = I2VOutput;
export type PixverseV55EffectsOutput = I2VOutput;
export type PixverseV55ImageToVideoOutput = I2VOutput;
export type PixverseV55TextToVideoOutput = I2VOutput;
export type PixverseV55TransitionOutput = I2VOutput;
export type PixverseV56ImageToVideoOutput = I2VOutput;
export type PixverseV56TextToVideoOutput = I2VOutput;
export type PixverseV56TransitionInput = PixverseV55TransitionInput;
export type PixverseV56TransitionOutput = I2VOutput;
export type PixverseV5EffectsInput = EffectInput;
export type PixverseV5EffectsOutput = I2VOutput;
export type PixverseV5ImageToVideoOutput = I2VOutput;
export type PixverseV5TextToVideoInput = PixverseV4TextToVideoInput;
export type PixverseV5TextToVideoOutput = I2VOutput;
export type PixverseV5TransitionOutput = I2VOutput;
export type PixverseV6ExtendOutput = I2VOutput;
export type PixverseV6ImageToVideoOutput = I2VOutput;
export type PixverseV6TextToVideoOutput = I2VOutput;
export type PixverseV6TransitionOutput = I2VOutput;
export type PlaygroundV25ImageToImageInput = ImageToImagePlaygroundv25Input;
export type PlaygroundV25ImageToImageOutput = unoOutput;
export type PlaygroundV25InpaintingInput = InpaintingPlaygroundv25Input;
export type PlaygroundV25InpaintingOutput = unoOutput;
export type PlaygroundV25Output = unoOutput;
export type PlushieStyleInput = RealismInput;
export type PlushieStyleOutput = pulidOutput;
export type plushifyOutput = unoOutput;
export type PonyV7Output = unoOutput;
export type PortraitOutput = BlurOutput;
export type PoseTransferOutput = VTONOutput;
export type PostProcessingChromaticAberrationInput = ChromaticAberrationInput;
export type PostProcessingChromaticAberrationOutput = BlurOutput;
export type PostProcessingColorCorrectionInput = ColorCorrectionInput;
export type PostProcessingColorCorrectionOutput = BlurOutput;
export type PostProcessingColorTintInput = ColorTintInput;
export type PostProcessingColorTintOutput = BlurOutput;
export type PostProcessingDesaturateInput = DesaturateInput;
export type PostProcessingDesaturateOutput = BlurOutput;
export type PostProcessingDissolveInput = DissolveInput;
export type PostProcessingDissolveOutput = BlurOutput;
export type PostProcessingDodgeBurnInput = DodgeBurnInput;
export type PostProcessingDodgeBurnOutput = BlurOutput;
export type PostProcessingGrainInput = GrainInput;
export type PostProcessingGrainOutput = BlurOutput;
export type PostProcessingOutput = BlurOutput;
export type PostProcessingParabolizeInput = ParabolizeInput;
export type PostProcessingParabolizeOutput = BlurOutput;
export type PostProcessingSharpenInput = SharpenInput;
export type PostProcessingSharpenOutput = BlurOutput;
export type PostProcessingSolarizeInput = SolarizeInput;
export type PostProcessingSolarizeOutput = BlurOutput;
export type PostProcessingVignetteInput = VignetteInput;
export type PostProcessingVignetteOutput = BlurOutput;
export type ProcessedOutput = BlurOutput;
export type ProductHoldingOutput = BlurOutput;
export type ProductPhotographyOutput = BlurOutput;
export type ProductShotOutput = BlurOutput;
export type ProFastImageToVideoHailuo23Input = ProImageToVideoHailuo23Input;
export type ProFastImageToVideoHailuo23Output = I2VOutput;
export type ProfessionalPhotoOutput = pulidOutput;
export type ProImageTo3DOutput = SketchTo3DOutput;
export type ProImageToVideoHailuo23Output = I2VOutput;
export type ProImageToVideoOutput = RemixOutput;
export type ProSketchTo3DInput = SketchTo3DInput;
export type ProSketchTo3DOutput = SketchTo3DOutput;
export type ProTextTo3DOutput = SketchTo3DOutput;
export type ProTextToVideoHailuo02Input = MinimaxVideo01Input;
export type ProTextToVideoHailuo23Input = MinimaxVideo01Input;
export type ProTextToVideoHailuo23Output = I2VOutput;
export type ProTextToVideoOutput = RemixOutput;
export type Q1ImageToVideoOutput = I2VOutput;
export type Q1ReferenceToVideoOutput = I2VOutput;
export type Q1StartEndToVideoOutput = I2VOutput;
export type Q1TextToVideoOutput = I2VOutput;
export type Q2ImageToVideoOutput = I2VOutput;
export type Q2ProReferenceToVideoOutput = I2VOutput;
export type Q2ReferenceToVideoOutput = I2VOutput;
export type Q2TextToVideoOutput = I2VOutput;
export type Q2VideoExtensionOutput = I2VOutput;
export type Q3ImageToVideoOutput = I2VOutput;
export type Q3TextToVideoOutput = I2VOutput;
export type Qwen3DesignVoiceOutput = MiniOutput;
export type Qwen3TtsCloneVoice06bInput = Qwen3CloneVoiceInput;
export type Qwen3TtsCloneVoice06bOutput = Qwen3CloneVoiceOutput;
export type Qwen3TtsCloneVoice17bInput = Qwen3CloneVoiceInput;
export type Qwen3TtsCloneVoice17bOutput = Qwen3CloneVoiceOutput;
export type Qwen3TTSOutput = MiniOutput;
export type Qwen3TtsTextToSpeech06bInput = Qwen3TTSInput;
export type Qwen3TtsTextToSpeech06bOutput = MiniOutput;
export type Qwen3TtsTextToSpeech17bInput = Qwen3TTSInput;
export type Qwen3TtsTextToSpeech17bOutput = MiniOutput;
export type Qwen3TtsVoiceDesign17bInput = Qwen3DesignVoiceInput;
export type Qwen3TtsVoiceDesign17bOutput = MiniOutput;
export type QwenImage2512Input = OvisImageInput;
export type QwenImage2512LoraInput = LoraInput;
export type QwenImage2512LoraOutput = unoOutput;
export type QwenImage2512Output = unoOutput;
export type QwenImage2512TrainerInput = ZImageBaseTrainerInput;
export type QwenImage2512TrainerOutput = Flux2TrainerOutput;
export type QwenImage2512TrainerV2Input = ZImageBaseTrainerInput;
export type QwenImage2512TrainerV2Output = Flux2TrainerOutput;
export type QwenImage2EditOutput = imagen3Output;
export type QwenImage2ProEditInput = QwenImage2EditInput;
export type QwenImage2ProEditOutput = imagen3Output;
export type QwenImage2ProTextToImageInput = QwenImage2TextToImageInput;
export type QwenImage2ProTextToImageOutput = imagen3Output;
export type QwenImage2TextToImageOutput = imagen3Output;
export type QwenImageEdit2509Input = EditImageInput;
export type QwenImageEdit2509LoraGalleryAddBackgroundInput = NextSceneInput;
export type QwenImageEdit2509LoraGalleryAddBackgroundOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryFaceToFullPortraitInput =
  NextSceneInput;
export type QwenImageEdit2509LoraGalleryFaceToFullPortraitOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryGroupPhotoInput = NextSceneInput;
export type QwenImageEdit2509LoraGalleryGroupPhotoOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryIntegrateProductInput = NextSceneInput;
export type QwenImageEdit2509LoraGalleryIntegrateProductOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryLightingRestorationInput =
  RemoveLightingInput;
export type QwenImageEdit2509LoraGalleryLightingRestorationOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryMultipleAnglesInput =
  MultipleAnglesInput;
export type QwenImageEdit2509LoraGalleryMultipleAnglesOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryNextSceneInput = NextSceneInput;
export type QwenImageEdit2509LoraGalleryNextSceneOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryRemoveElementInput = NextSceneInput;
export type QwenImageEdit2509LoraGalleryRemoveElementOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryRemoveLightingInput =
  RemoveLightingInput;
export type QwenImageEdit2509LoraGalleryRemoveLightingOutput = pulidOutput;
export type QwenImageEdit2509LoraGalleryShirtDesignInput = NextSceneInput;
export type QwenImageEdit2509LoraGalleryShirtDesignOutput = pulidOutput;
export type QwenImageEdit2509LoraInput = EditImageLoraInput;
export type QwenImageEdit2509LoraOutput = unoOutput;
export type QwenImageEdit2509Output = unoOutput;
export type QwenImageEdit2509TrainerInput = ZImageBaseTrainerInput;
export type QwenImageEdit2509TrainerOutput = Flux2TrainerOutput;
export type QwenImageEdit2511Input = EditImageInput;
export type QwenImageEdit2511LoraInput = EditImageLoraInput;
export type QwenImageEdit2511LoraOutput = unoOutput;
export type QwenImageEdit2511MultipleAnglesOutput = AuraFlowOutput;
export type QwenImageEdit2511Output = unoOutput;
export type QwenImageEdit2511TrainerInput = ZImageBaseTrainerInput;
export type QwenImageEdit2511TrainerOutput = Flux2TrainerOutput;
export type QwenImageEditImageToImageInput = BaseQwenEditImg2ImgInput;
export type QwenImageEditImageToImageOutput = unoOutput;
export type QwenImageEditInpaintOutput = unoOutput;
export type QwenImageEditLoraOutput = unoOutput;
export type QwenImageEditOutput = unoOutput;
export type QwenImageEditPlusInput = EditImageInput;
export type QwenImageEditPlusLoraGalleryAddBackgroundInput = NextSceneInput;
export type QwenImageEditPlusLoraGalleryAddBackgroundOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryFaceToFullPortraitInput =
  NextSceneInput;
export type QwenImageEditPlusLoraGalleryFaceToFullPortraitOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryGroupPhotoInput = NextSceneInput;
export type QwenImageEditPlusLoraGalleryGroupPhotoOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryIntegrateProductInput = NextSceneInput;
export type QwenImageEditPlusLoraGalleryIntegrateProductOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryLightingRestorationInput =
  RemoveLightingInput;
export type QwenImageEditPlusLoraGalleryLightingRestorationOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryMultipleAnglesInput =
  MultipleAnglesInput;
export type QwenImageEditPlusLoraGalleryMultipleAnglesOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryNextSceneInput = NextSceneInput;
export type QwenImageEditPlusLoraGalleryNextSceneOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryRemoveElementInput = NextSceneInput;
export type QwenImageEditPlusLoraGalleryRemoveElementOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryRemoveLightingInput =
  RemoveLightingInput;
export type QwenImageEditPlusLoraGalleryRemoveLightingOutput = pulidOutput;
export type QwenImageEditPlusLoraGalleryShirtDesignInput = NextSceneInput;
export type QwenImageEditPlusLoraGalleryShirtDesignOutput = pulidOutput;
export type QwenImageEditPlusLoraInput = EditImageLoraInput;
export type QwenImageEditPlusLoraOutput = unoOutput;
export type QwenImageEditPlusOutput = unoOutput;
export type QwenImageEditPlusTrainerInput = ZImageBaseTrainerInput;
export type QwenImageEditPlusTrainerOutput = Flux2TrainerOutput;
export type QwenImageEditTrainerInput = ZImageBaseTrainerInput;
export type QwenImageEditTrainerOutput = Flux2TrainerOutput;
export type QwenImageI2IOutput = unoOutput;
export type QwenImageImageToImageInput = QwenImageI2IInput;
export type QwenImageImageToImageOutput = unoOutput;
export type QwenImageInpaintOutput = unoOutput;
export type QwenImageLayeredLoraInput = TextToImageLoRAInput;
export type QwenImageLayeredLoraOutput = QwenImageLayeredOutput;
export type QwenImageLayeredTrainerInput = ZImageBaseTrainerInput;
export type QwenImageLayeredTrainerOutput = Flux2TrainerOutput;
export type QwenImageMaxEditInput = QwenImage2EditInput;
export type QwenImageMaxEditOutput = imagen3Output;
export type QwenImageMaxTextToImageInput = QwenImage2TextToImageInput;
export type QwenImageMaxTextToImageOutput = imagen3Output;
export type QwenImageOutput = unoOutput;
export type QwenImageTrainerOutput = WanTrainerOutput;
export type QwenImageTrainerV2Input = ZImageBaseTrainerInput;
export type QwenImageTrainerV2Output = Flux2TrainerOutput;
export type Ray2I2VOutput = I2VOutput;
export type Ray2T2VOutput = I2VOutput;
export type RealismOutput = pulidOutput;
export type RealisticVisionOutput = unoOutput;
export type RealtimeConversationInput = PersonaplexRealtimeInput;
export type RealtimeConversationOutput = PersonaplexRealtimeOutput;
export type Recraft20bOutput = V16Output;
export type Recraft20BTextToImageInput = Recraft20bInput;
export type RecraftUpscaleCreativeInput = UpscaleInput;
export type RecraftUpscaleCreativeOutput = EvfSamOutput;
export type RecraftUpscaleCrispInput = UpscaleInput;
export type RecraftUpscaleCrispOutput = EvfSamOutput;
export type RecraftV3CreateStyleInput = StyleReferenceInput;
export type RecraftV3CreateStyleOutput = StyleReferenceOutput;
export type RecraftV3ImageToImageOutput = V16Output;
export type RecraftV3TextToImageInput = Recraft20bInput;
export type RecraftV3TextToImageOutput = V16Output;
export type RecraftV4ProTextToImageInput = RecraftV4TextToImageInput;
export type RecraftV4ProTextToImageOutput = V16Output;
export type RecraftV4ProTextToVectorInput = RecraftV4TextToImageInput;
export type RecraftV4ProTextToVectorOutput = V16Output;
export type RecraftV4TextToImageOutput = V16Output;
export type RecraftV4TextToVectorInput = RecraftV4TextToImageInput;
export type RecraftV4TextToVectorOutput = V16Output;
export type RecraftV4VectorProTextToImageInput = RecraftV4TextToImageInput;
export type RecraftV4VectorProTextToImageOutput = V16Output;
export type RecraftV4VectorTextToImageInput = RecraftV4TextToImageInput;
export type RecraftV4VectorTextToImageOutput = V16Output;
export type RecraftVectorizeInput = ZoeInput;
export type RecraftVectorizeOutput = EvfSamOutput;
export type ReferenceFace = PikaImage;
export type ReferenceImage = PikaImage;
export type ReferenceImageInput = SemanticImageInput;
export type ReferenceToImageOutput = HEDOutput;
export type ReferenceToVideoOutput = TextToVideoOutput;
export type ReframeOutput = I2VOutput;
export type Reimagine32Output = HEDOutput;
export type ReimagineOutput = pulidOutput;
export type RelightingOutput = BlurOutput;
export type RembgEnhanceInput = ZoeInput;
export type RembgEnhanceOutput = EvfSamOutput;
export type RemoveBackgroundInput = ImageutilsRembgInput;
export type RemoveBackgroundOutput = HEDOutput;
export type RemoveElementInput = NextSceneInput;
export type RemoveElementOutput = pulidOutput;
export type RemoveLightingOutput = pulidOutput;
export type ReplaceObjectInput = BlendingInput;
export type RestoreInput = ZoeInput;
export type retoucherInput = NafnetInput;
export type retoucherOutput = ccsrOutput;
export type RetouchInput = RealismInput;
export type RetouchOutput = pulidOutput;
export type ReverseVideoInput = AutoCaptionOutput;
export type ReverseVideoOutput = I2VOutput;
export type RIFEImageInput = rifeInput;
export type RIFEImageOutput = rifeOutput;
export type RIFEVideoInput = RifeVideoInput;
export type RifeVideoOutput = I2VOutput;
export type RIFEVideoOutput = I2VOutput;
export type RouterAudioInput = AudioInput;
export type RouterAudioOutput = AudioOutput;
export type routerInput = ChatInput;
export type RouterVideoEnterpriseInput = RouterVideoInput;
export type RouterVideoEnterpriseOutput = AudioOutput;
export type RouterVideoOutput = AudioOutput;
export type RouterVisionOutput = AudioOutput;
export type RundiffusionPhotoFluxOutput = unoOutput;
export type Sa2va4bImageInput = HunyuanWorldInput;
export type Sa2va4bImageOutput = ImageChatOutput;
export type Sa2va4bVideoOutput = VideoChatOutput;
export type Sa2va8bImageInput = HunyuanWorldInput;
export type Sa2va8bImageOutput = ImageChatOutput;
export type Sa2va8bVideoInput = Sa2va4bVideoInput;
export type Sa2va8bVideoOutput = VideoChatOutput;
export type SadTalkerInput = sadtalkerInput;
export type sadtalkerOutput = I2VOutput;
export type SadtalkerReferenceInput = SadTalkerRefVideoInput;
export type SadtalkerReferenceOutput = I2VOutput;
export type SAM2AutomaticSegmentationInput = Sam2AutoSegmentInput;
export type SAM2AutomaticSegmentationOutput = Sam2AutoSegmentOutput;
export type SAM2ImageInput = Sam2ImageInput;
export type Sam2ImageOutput = HEDOutput;
export type SAM2ImageOutput = HEDOutput;
export type SAM2VideoOutput = Sam2VideoOutput;
export type SAM2VideoRLEInput = Sam2VideoInput;
export type SAM31EmbeddingInput = ZoeInput;
export type SAM31EmbeddingOutput = SAM2EmbeddingOutput;
export type Sam31ImageInput = Sam3ImageInput;
export type SAM31ImageInput = Sam3ImageInput;
export type Sam31ImageOutput = Sam3ImageOutput;
export type SAM31ImageOutput = Sam3ImageOutput;
export type Sam31ImageRleInput = Sam3ImageInput;
export type Sam31ImageRleOutput = SAM3RLEOutput;
export type SAM31RLEFileOutput = SAM3RLEFileOutput;
export type SAM31RLEOutput = SAM3RLEOutput;
export type SAM31VideoInput = Sam31VideoInput;
export type Sam31VideoOutput = Sam2VideoOutput;
export type SAM31VideoOutput = Sam2VideoOutput;
export type SAM31VideoRLEInput = Sam31VideoRleInput;
export type Sam31VideoRleOutput = Sam2VideoOutput;
export type Sam33dBodyInput = SAM3DBodyInput;
export type Sam33dBodyOutput = SAM3DBodyOutput;
export type Sam33dObjectsInput = SAM3DObjectInput;
export type Sam33dObjectsOutput = SAM3DObjectOutput;
export type SAM3DAlignmentInput = Sam33dAlignInput;
export type SAM3DAlignmentOutput = Sam33dAlignOutput;
export type SAM3EmbeddingInput = ZoeInput;
export type SAM3EmbeddingOutput = SAM2EmbeddingOutput;
export type Sam3ImageEmbedInput = ZoeInput;
export type Sam3ImageEmbedOutput = SAM2EmbeddingOutput;
export type SAM3ImageInput = Sam3ImageInput;
export type SAM3ImageOutput = Sam3ImageOutput;
export type Sam3ImageRleInput = Sam3ImageInput;
export type Sam3ImageRleOutput = SAM3RLEOutput;
export type SAM3VideoInput = Sam3VideoInput;
export type Sam3VideoOutput = Sam2VideoOutput;
export type SAM3VideoOutput = Sam2VideoOutput;
export type SAM3VideoRLEInput = Sam3VideoRleInput;
export type Sam3VideoRleOutput = Sam2VideoOutput;
export type SamAudioSeparateInput = SAMAudioInput;
export type SAMAudioSeparateOutput = SamAudioSeparateOutput;
export type SamAudioSpanSeparateInput = SAMAudioSpanInput;
export type SamAudioSpanSeparateOutput = SamAudioSeparateOutput;
export type SAMAudioSpanSeparateOutput = SamAudioSeparateOutput;
export type SamAudioVisualSeparateInput = SAMAudioVisualInput;
export type SamAudioVisualSeparateOutput = SamAudioSeparateOutput;
export type SAMAudioVisualSeparateOutput = SamAudioSeparateOutput;
export type SanaI2VVideoOutput = SanaVideoOutput;
export type sanaOutput = unoOutput;
export type SanaSprintInput = sanaInput;
export type SanaSprintOutput = unoOutput;
export type SanaV1516bInput = sanaInput;
export type SanaV1516bOutput = unoOutput;
export type SanaV1548bInput = sanaInput;
export type SanaV1548bOutput = unoOutput;
export type SatelliteViewStyleInput = HdrStyleInput;
export type SatelliteViewStyleOutput = AuraFlowOutput;
export type scailOutput = I2VOutput;
export type SceneCompositionInput = TimeOfDayInput;
export type SceneCompositionOutput = pulidOutput;
export type SchnellFlux1ReduxInput = FluxSchnellReduxInput;
export type SchnellFlux1TextToImageInput = FluxDevInput;
export type SchnellTextToImageInput = piflowInput;
export type ScribbleOutput = HEDOutput;
export type Sd15DepthControlnetOutput = unoOutput;
export type SdxlControlnetUnionImageToImageInput =
  ImageToImageControlNetUnionInput;
export type SdxlControlnetUnionImageToImageOutput = unoOutput;
export type SdxlControlnetUnionInpaintingInput = InpaintingControlNetUnionInput;
export type SdxlControlnetUnionInpaintingOutput = unoOutput;
export type SdxlControlnetUnionOutput = unoOutput;
export type Seed3DImageTo3DInput = ZoeInput;
export type Seedance2VideoOutput = magiOutput;
export type SeedanceFastI2VVideoOutput = magiOutput;
export type SeedanceFastT2VVideoOutput = magiOutput;
export type SeedanceProFastTextToVideoInput = SeedanceTextToVideoInput;
export type SeedanceProI2VVideoOutput = magiOutput;
export type SeedanceProImageToVideoInput = SeedanceImageToVideoInput;
export type SeedanceProT2VVideoOutput = magiOutput;
export type SeedanceProTextToVideoInput = SeedanceTextToVideoInput;
export type SeedanceProv15I2VVideoOutput = magiOutput;
export type SeedanceProv15T2VVideoOutput = magiOutput;
export type SeedanceReferenceToVideoOutput = magiOutput;
export type SeedanceVideoOutput = magiOutput;
export type SeedDream45EditOutput = BlurOutput;
export type SeedDream45T2IOutput = pulidOutput;
export type SeedDream4EditOutput = pulidOutput;
export type SeedDream4T2IOutput = pulidOutput;
export type SeedDream50LiteEditOutput = pulidOutput;
export type SeedDream50LiteT2IOutput = pulidOutput;
export type SeedDreamOutput = pulidOutput;
export type SeedEditOutput = ccsrOutput;
export type SeedvrUpscaleImageInput = SeedVRImageInput;
export type SeedvrUpscaleImageOutput = SeedVRImageOutput;
export type SeedvrUpscaleImageSeamlessInput = SeedVRSeamlessImageInput;
export type SeedvrUpscaleImageSeamlessOutput = SeedVRImageOutput;
export type SeedvrUpscaleVideoInput = SeedVRVideoInput;
export type SeedvrUpscaleVideoOutput = magiOutput;
export type SeedVRVideoOutput = magiOutput;
export type SemanticImageMeasurementInput = ArbiterImageTextInput;
export type SepiaVintageInput = HdrStyleInput;
export type SepiaVintageOutput = AuraFlowOutput;
export type SetptsVideoOutput = I2VOutput;
export type SfxV15VideoToVideoInput = SfxV15VideoToAudioInput;
export type SfxV1VideoToVideoInput = SfxV1VideoToAudioInput;
export type SharpenOutput = BlurOutput;
export type ShirtDesignInput = NextSceneInput;
export type ShirtDesignOutput = pulidOutput;
export type SketchColoredImageInput = ZoeInput;
export type SkyreelsI2vOutput = magiOutput;
export type SmartTurnInput = SileroVadInput;
export type SolarizeOutput = BlurOutput;
export type Sora2CharactersInput = CreateCharacterInput;
export type Sora2CharactersOutput = CreateCharacterOutput;
export type Sora2ImageToVideoOutput = RemixOutput;
export type Sora2ImageToVideoProInput = ProImageToVideoInput;
export type Sora2ImageToVideoProOutput = RemixOutput;
export type Sora2TextToVideoOutput = RemixOutput;
export type Sora2TextToVideoProInput = ProTextToVideoInput;
export type Sora2TextToVideoProOutput = RemixOutput;
export type Sora2VideoToVideoRemixInput = RemixInput;
export type Sora2VideoToVideoRemixOutput = RemixOutput;
export type SoundEffectOutput = I2VOutput;
export type SoundEffectsGeneratorInput = MusicGeneratorInput;
export type SoundEffectsGeneratorOutput = StableAudioOutput;
export type SpanishOutput = DiaOutput;
export type SpeechTimestamp = InpaintSection;
export type SpeechToTextOutput = SpeechOutput;
export type SpeechToTextStreamInput = SpeechToTextInput;
export type SpeechToTextTurboInput = SpeechToTextInput;
export type SpeechToTextTurboOutput = SpeechOutput;
export type SpeechToTextTurboStreamInput = SpeechToTextInput;
export type SplitImagesInput = MergeImagesInput;
export type SplitImagesOutput = BlurOutput;
export type SprintInput = sanaInput;
export type SRPOOutput = unoOutput;
export type StableAudio25AudioToAudioInput = AudioToAudioInput;
export type StableAudio25AudioToAudioOutput = TextToAudioOutput;
export type StableAudio25InpaintOutput = TextToAudioOutput;
export type StableAudio25TextToAudioInput = TextToAudioInput;
export type StableAudio25TextToAudioOutput = TextToAudioOutput;
export type StableAvatarOutput = I2VOutput;
export type StableCascadeInput = SoteDiffusionInput;
export type StableCascadeOutput = unoOutput;
export type StableCascadeSoteDiffusionInput = SoteDiffusionInput;
export type StableCascadeSoteDiffusionOutput = unoOutput;
export type StableDiffusionV15Input = FastSdxlInput;
export type StableDiffusionV15Output = unoOutput;
export type StableDiffusionV35LargeOutput = unoOutput;
export type StableDiffusionV35MediumInput = cogview4Input;
export type StableDiffusionV35MediumOutput = unoOutput;
export type StableDiffusionV3MediumImageToImageOutput =
  StableDiffusionV3MediumOutput;
export type StableVideoOutput = magiOutput;
export type StandardFastImageToVideoHailuo23Input =
  FastImageToVideoHailuo02Input;
export type StandardFastImageToVideoHailuo23Output = I2VOutput;
export type StandardImageToVideoHailuo23Input = FastImageToVideoHailuo02Input;
export type StandardImageToVideoHailuo23Output = I2VOutput;
export type StandardTextToVideoHailuo23Input = StandardTextToVideoHailuo02Input;
export type StandardTextToVideoHailuo23Output = I2VOutput;
export type StartEndToVideoOutput = I2VOutput;
export type Step1xEditInput = PhysicEditInput;
export type Step1xEditOutput = unoOutput;
export type StreamingFlux1Input = StreamingKreaInput;
export type StreamingFlux2TextToImageInput = StreamingKreaInput;
export type StreamingKreaFlux1Input = StreamingKreaInput;
export type StreamingSRPOInput = StreamingKreaInput;
export type StreamingTextToImageInput = PonyV7Input;
export type StyleTransferInput = TimeOfDayInput;
export type StyleTransferOutput = pulidOutput;
export type SubjectReferenceOutput = I2VOutput;
export type SwapOutput = I2VOutput;
export type swin2srOutput = HEDOutput;
export type Switti512Input = swittiInput;
export type Switti512Output = unoOutput;
export type swittiOutput = unoOutput;
export type Sync3Output = I2VOutput;
export type SyncLipsyncInput = LipSyncInput;
export type SyncLipsyncOutput = I2VOutput;
export type SyncLipsyncReact1Input = React1Input;
export type SyncLipsyncReact1Output = React1Output;
export type SyncLipsyncV2Input = LipSyncInput;
export type SyncLipsyncV2Output = I2VOutput;
export type SyncLipsyncV2ProInput = Sync3Input;
export type SyncLipsyncV2ProOutput = I2VOutput;
export type T2IO3ImageOutput = BlurOutput;
export type T2IV3ImageOutput = BlurOutput;
export type T2VDirectorOutput = I2VOutput;
export type T2VLiveOutput = I2VOutput;
export type T2VOutput = I2VOutput;
export type T2vTurboOutput = I2VOutput;
export type Tada1bTextToSpeechOutput = MiniOutput;
export type Tada3bTextToSpeechInput = Tada1bTextToSpeechInput;
export type Tada3bTextToSpeechOutput = MiniOutput;
export type TeeDInput = ZoeInput;
export type TeeDOutput = HEDOutput;
export type TemplateToVideoOutput = I2VOutput;
export type TextReferenceInput = SemanticImageInput;
export type TextRemovalInput = BabyVersionInput;
export type TextRemovalOutput = pulidOutput;
export type TextToDialogueOutput = TextToAudioOutput;
export type TextToImage32Output = HEDOutput;
export type TextToImageControlNetInput = Sd15DepthControlnetInput;
export type TextToImageControlNetUnionInput = SdxlControlnetUnionInput;
export type TextToImageFooocusInput = FastFooocusSdxlInput;
export type TextToImageLightningInput = FastLightningSdxlInput;
export type TextToImagePlaygroundv25Input = PlaygroundV25Input;
export type TextToImageTurboInput = cogview4Input;
export type TextToLottieInput = omnilottieInput;
export type TextToSpeechHD26Output = TextToSpeechOutput;
export type TextToSpeechHD28Output = TextToSpeechOutput;
export type TextToSpeechTurbo26Output = TextToSpeechOutput;
export type TextToSpeechTurbo28Output = TextToSpeechOutput;
export type TextToVideoHailuo02Output = I2VOutput;
export type TextToVideoTurboInput = TextToVideov21Input;
export type TextToVideoV21MasterOutput = I2VOutput;
export type TextToVideoV21Output = I2VOutput;
export type TextToVideoV25ProOutput = I2VOutput;
export type TextToVideoV26ProOutput = I2VOutput;
export type TextToVideoV2MasterOutput = I2VOutput;
export type TextToVideoV3ProOutput = I2VOutput;
export type TextToVideoV3StandardOutput = I2VOutput;
export type TextToVoiceDesignOutput = TextToVoiceRemixOutput;
export type TextureTransformOutput = BlurOutput;
export type theraOutput = ccsrOutput;
export type ThinksoundAudioInput = thinksoundInput;
export type thinksoundOutput = Output;
export type TimeOfDayOutput = pulidOutput;
export type TopazUpscaleImageOutput = EvfSamOutput;
export type TopazUpscaleVideoOutput = I2VOutput;
export type Trajectory = TrackPoint;
export type TransitionOutput = I2VOutput;
export type Trellis2RetextureOutput = Trellis2Output;
export type TrellisMultiOutput = trellisOutput;
export type TripoV25ImageTo3dInput = ImageTo3dInput;
export type TripoV25MultiviewTo3dInput = MultiviewTo3dInput;
export type TripoV25MultiviewTo3dOutput = TripoV25ImageTo3dOutput;
export type TTSOutput = STSOutput;
export type TtsV1Output = DiaOutput;
export type TurboFluxTrainerOutput = Flux2TrainerOutput;
export type TurboImageToVideoOutput = I2VOutput;
export type TurboTextToVideoOutput = I2VOutput;
export type ultrashapeOutput = Trellis2Output;
export type UpscaleCreativeOutput = HEDOutput;
export type UpscaleOutput = EvfSamOutput;
export type usoOutput = unoOutput;
export type V26ImageToImageInput = ImageEditInput;
export type V26ImageToImageOutput = imagen3Output;
export type V26ImageToVideoFlashInput = ImageToVideoInput;
export type V26ImageToVideoFlashOutput = TextToVideoOutput;
export type V26ImageToVideoInput = ImageToVideoInput;
export type V26ImageToVideoOutput = TextToVideoOutput;
export type V26ReferenceToVideoFlashInput = ReferenceToVideoFlashInput;
export type V26ReferenceToVideoFlashOutput = TextToVideoOutput;
export type V26ReferenceToVideoInput = ReferenceToVideoInput;
export type V26ReferenceToVideoOutput = TextToVideoOutput;
export type V26TextToImageInput = TextToImageWanInput;
export type V26TextToImageOutput = TextToImageWanOutput;
export type V26TextToVideoInput = TextToVideoInput;
export type V26TextToVideoOutput = TextToVideoOutput;
export type V2ExtendInput = ExtendInput;
export type V2ExtendOutput = ExtendOutput;
export type V2InpaintInput = InpaintInput;
export type V2InpaintOutput = InpaintOutput;
export type V2TextToMusicInput = GenerateInput;
export type V2TextToMusicOutput = GenerateOutput;
export type VecglypherImageToSvgInput = ImageToSVGInput;
export type VecglypherImageToSvgOutput = vecglypherOutput;
export type vecglypherInput = TextToSVGInput;
export type VectorizeInput = ZoeInput;
export type VectorizeOutput = EvfSamOutput;
export type Veo2ImageToVideoOutput = I2VOutput;
export type veo2Output = I2VOutput;
export type Veo31ExtendVideoOutput = I2VOutput;
export type Veo31FastExtendVideoInput = Veo31ExtendVideoInput;
export type Veo31FastExtendVideoOutput = I2VOutput;
export type Veo31FastFirstLastFrameToVideoInput =
  Veo31FirstLastFrameToVideoInput;
export type Veo31FastFirstLastFrameToVideoOutput = I2VOutput;
export type Veo31FastImageToVideoInput = Veo3ImageToVideoInput;
export type Veo31FastImageToVideoOutput = I2VOutput;
export type Veo31FastInput = veo3Input;
export type Veo31FastOutput = I2VOutput;
export type Veo31FirstLastFrameToVideoOutput = I2VOutput;
export type Veo31ImageToVideoInput = Veo3ImageToVideoInput;
export type Veo31ImageToVideoOutput = I2VOutput;
export type Veo31Input = veo3Input;
export type Veo31LiteFirstLastFrameToVideoOutput = I2VOutput;
export type Veo31LiteImageToVideoInput = Veo3ImageToVideoInput;
export type Veo31LiteImageToVideoOutput = I2VOutput;
export type Veo31LiteInput = veo3Input;
export type Veo31LiteOutput = I2VOutput;
export type Veo31LiteReferenceToVideoInput = Veo31ReferenceToVideoInput;
export type Veo31LiteReferenceToVideoOutput = I2VOutput;
export type Veo31LiteTextToVideoInput = veo3Input;
export type Veo31LiteTextToVideoOutput = I2VOutput;
export type Veo31Output = I2VOutput;
export type Veo31ReferenceToVideoOutput = I2VOutput;
export type Veo31TextToVideoInput = veo3Input;
export type Veo31TextToVideoOutput = I2VOutput;
export type Veo31VideoToVideoInput = Veo31ExtendVideoInput;
export type Veo31VideoToVideoOutput = I2VOutput;
export type Veo3FastImageToVideoInput = Veo3ImageToVideoInput;
export type Veo3FastImageToVideoOutput = I2VOutput;
export type Veo3FastInput = veo3Input;
export type Veo3FastOutput = I2VOutput;
export type Veo3ImageToVideoOutput = I2VOutput;
export type veo3Output = I2VOutput;
export type Veo3TextToVideoInput = veo3Input;
export type Veo3TextToVideoOutput = I2VOutput;
export type Vibevoice05bOutput = vibevoiceOutput;
export type Vibevoice7bInput = vibevoiceInput;
export type VibeVoice7bInput = vibevoiceInput;
export type Vibevoice7bOutput = vibevoiceOutput;
export type VibeVoiceInput = vibevoiceInput;
export type VibeVoiceOutput = vibevoiceOutput;
export type VideoAgentOutput = I2VOutput;
export type VideoAsPromptOutput = I2VOutput;
export type VideoBackgroundRemovalFastInput = GeneralRembgInput;
export type VideoBackgroundRemovalFastOutput = GeneralRembgOutput;
export type VideoBackgroundRemovalGreenScreenInput = GreenScreenRembgInput;
export type VideoBackgroundRemovalGreenScreenOutput = GeneralRembgOutput;
export type VideoBackgroundRemovalInput = GeneralRembgInput;
export type VideoBackgroundRemovalOutput = GeneralRembgOutput;
export type VideoEffectsOutput = I2VOutput;
export type VideoEnterpriseInput = RouterVideoInput;
export type VideoPromptGeneratorOutput = Qwen3GuardInput;
export type VideoSoundEffectsGeneratorOutput = I2VOutput;
export type VideoToGifOutput = EvfSamOutput;
export type VideoTranslateOutput = I2VOutput;
export type VideoUnderstandingOutput = MoondreamOutput;
export type VideoUpscaleOutput = I2VOutput;
export type VideoUpscalerOutput = I2VOutput;
export type ViduImageToVideoOutput = I2VOutput;
export type ViduQ1ImageToVideoInput = ViduImageToVideoInput;
export type ViduQ1ImageToVideoOutput = I2VOutput;
export type ViduQ1ReferenceToVideoOutput = I2VOutput;
export type ViduQ1StartEndToVideoInput = ViduStartEndToVideoInput;
export type ViduQ1StartEndToVideoOutput = I2VOutput;
export type ViduQ1TextToVideoOutput = I2VOutput;
export type ViduQ2ImageToVideoProOutput = I2VOutput;
export type ViduQ2ImageToVideoTurboInput = ViduQ2ImageToVideoProInput;
export type ViduQ2ImageToVideoTurboOutput = I2VOutput;
export type ViduQ2ReferenceToImageInput = ViduReferenceToImageInput;
export type ViduQ2ReferenceToImageOutput = HEDOutput;
export type ViduQ2ReferenceToVideoProOutput = I2VOutput;
export type ViduQ2TextToImageOutput = HEDOutput;
export type ViduQ2TextToVideoOutput = I2VOutput;
export type ViduQ2VideoExtensionProOutput = I2VOutput;
export type ViduQ3ImageToVideoOutput = I2VOutput;
export type ViduQ3ImageToVideoTurboInput = ViduQ3ImageToVideoInput;
export type ViduQ3ImageToVideoTurboOutput = I2VOutput;
export type ViduQ3TextToVideoOutput = I2VOutput;
export type ViduQ3TextToVideoTurboInput = ViduQ3TextToVideoInput;
export type ViduQ3TextToVideoTurboOutput = I2VOutput;
export type ViduReferenceToImageOutput = HEDOutput;
export type ViduReferenceToVideoOutput = I2VOutput;
export type ViduStartEndToVideoOutput = I2VOutput;
export type ViduTemplateToVideoOutput = I2VOutput;
export type VignetteOutput = BlurOutput;
export type VirtualTryonOutput = AuraFlowOutput;
export type VirtualTryOnOutput = BlurOutput;
export type VisionEnterpriseInput = RouterVisionInput;
export type VisionOutput = AudioOutput;
export type VoiceChangerOutput = TextToAudioOutput;
export type VoiceCloningOutput = CreateVoiceOutput;
export type VoiceDeleteOutput = CreateVoiceOutput;
export type Wan22TrainerI2vA14bInput = WanTrainerInput;
export type Wan22TrainerI2vA14bOutput = WanTrainerOutput;
export type Wan22TrainerT2vA14bInput = WanTrainerInput;
export type Wan22TrainerT2vA14bOutput = WanTrainerOutput;
export type Wan22VaceFunA14bDepthInput = WanVace14bPoseInput;
export type Wan22VaceFunA14bDepthOutput = edittoOutput;
export type Wan22VaceFunA14bInpaintingInput = WanVace14bInpaintingInput;
export type Wan22VaceFunA14bInpaintingOutput = edittoOutput;
export type Wan22VaceFunA14bOutpaintingInput = WanVace14bOutpaintingInput;
export type Wan22VaceFunA14bOutpaintingOutput = edittoOutput;
export type Wan22VaceFunA14bPoseInput = WanVace14bPoseInput;
export type Wan22VaceFunA14bPoseOutput = edittoOutput;
export type Wan22VaceFunA14bReframeInput = WanVace14bReframeInput;
export type Wan22VaceFunA14bReframeOutput = edittoOutput;
export type Wan25PreviewImageToImageOutput = TextToImageOutput;
export type Wan25PreviewImageToVideoOutput = TextToVideoOutput;
export type Wan25PreviewTextToImageOutput = TextToImageOutput;
export type Wan25PreviewTextToVideoInput = Wan27TextToVideoInput;
export type Wan25PreviewTextToVideoOutput = TextToVideoOutput;
export type Wan27ImageEditInput = ImageEditInput;
export type Wan27ImageEditOutput = imagen3Output;
export type Wan27ImageToVideoOutput = TextToVideoOutput;
export type Wan27ReferenceToVideoOutput = TextToVideoOutput;
export type Wan27TextToImageOutput = TextToImageWanOutput;
export type Wan27TextToVideoOutput = TextToVideoOutput;
export type Wan27VideoEditOutput = TextToVideoOutput;
export type WanAnimateSimpleInput = WanMotionInput;
export type WanAnimateSimpleOutput = WanMotionOutput;
export type WanAtiOutput = I2VOutput;
export type WanEffectsOutput = magiOutput;
export type WanFlf2vOutput = magiOutput;
export type WanFunControlOutput = I2VOutput;
export type WanI2vLoraOutput = magiOutput;
export type WanI2vOutput = magiOutput;
export type WanMoveOutput = lynxOutput;
export type WanProImageToVideoOutput = I2VOutput;
export type WanProTextToVideoOutput = I2VOutput;
export type WanT2vLoraOutput = magiOutput;
export type WanT2vOutput = magiOutput;
export type WanTrainerFlf2v720pInput = WanTrainerInput;
export type WanTrainerFlf2v720pOutput = WanTrainerOutput;
export type WanTrainerI2v720pInput = WanTrainerInput;
export type WanTrainerI2v720pOutput = WanTrainerOutput;
export type WanTrainerT2v14bInput = WanTrainerInput;
export type WanTrainerT2v14bOutput = WanTrainerOutput;
export type WanTrainerT2vInput = WanTrainerInput;
export type WanTrainerT2vOutput = WanTrainerOutput;
export type WanV2214bAnimateReplaceInput = WanV2214bAnimateMoveInput;
export type WanV2214bAnimateReplaceOutput = WanV2214bAnimateMoveOutput;
export type WanV2214bSpeechToVideoOutput = I2VOutput;
export type WanV225bImageToVideoOutput = WanMotionOutput;
export type WanV225bTextToImageOutput = StarVectorOutput;
export type WanV225bTextToVideoDistillOutput = WanMotionOutput;
export type WanV225bTextToVideoFastWanOutput = WanMotionOutput;
export type WanV225bTextToVideoOutput = WanMotionOutput;
export type WanV22A14bImageToVideoLoraOutput = WanMotionOutput;
export type WanV22A14bImageToVideoTurboOutput = WanMotionOutput;
export type WanV22A14bTextToImageLoraOutput = StarVectorOutput;
export type WanV22A14bTextToImageOutput = StarVectorOutput;
export type WanV22A14bTextToVideoLoraOutput = WanMotionOutput;
export type WanV22A14bTextToVideoOutput = WanMotionOutput;
export type WanV22A14bTextToVideoTurboOutput = WanMotionOutput;
export type WanV22A14bVideoToVideoOutput = WanMotionOutput;
export type WanV27EditInput = ImageEditInput;
export type WanV27EditOutput = imagen3Output;
export type WanV27EditVideoInput = Wan27VideoEditInput;
export type WanV27EditVideoOutput = TextToVideoOutput;
export type WanV27ImageToVideoInput = Wan27ImageToVideoInput;
export type WanV27ImageToVideoOutput = TextToVideoOutput;
export type WanV27ProEditInput = ImageEditInput;
export type WanV27ProEditOutput = imagen3Output;
export type WanV27ProTextToImageInput = Wan27TextToImageInput;
export type WanV27ProTextToImageOutput = TextToImageWanOutput;
export type WanV27ReferenceToVideoInput = Wan27ReferenceToVideoInput;
export type WanV27ReferenceToVideoOutput = TextToVideoOutput;
export type WanV27TextToImageInput = Wan27TextToImageInput;
export type WanV27TextToImageOutput = TextToImageWanOutput;
export type WanV27TextToVideoInput = Wan27TextToVideoInput;
export type WanV27TextToVideoOutput = TextToVideoOutput;
export type WanVace13bInput = WanVaceInput;
export type WanVace13bOutput = magiOutput;
export type WanVace14bDepthInput = WanVace14bPoseInput;
export type WanVace14bDepthOutput = edittoOutput;
export type WanVace14bInpaintingOutput = edittoOutput;
export type WanVace14bOutpaintingOutput = edittoOutput;
export type WanVace14bOutput = edittoOutput;
export type WanVace14bPoseOutput = edittoOutput;
export type WanVace14bReframeOutput = edittoOutput;
export type WanVaceAppsLongReframeOutput = React1Output;
export type WanVaceOutput = magiOutput;
export type WanVisionEnhancerOutput = SanaVideoOutput;
export type WeatherEffectInput = TimeOfDayInput;
export type WeatherEffectOutput = pulidOutput;
export type WojakStyleInput = RealismInput;
export type WojakStyleOutput = pulidOutput;
export type WorkflowUtilitiesAudioCompressorInput = AudioCompressorInput;
export type WorkflowUtilitiesAudioCompressorOutput = MiniOutput;
export type WorkflowUtilitiesAutoSubtitleInput = AutoSubtitleInput;
export type WorkflowUtilitiesAutoSubtitleOutput = AutoSubtitleOutput;
export type WorkflowUtilitiesBlendVideoInput = BlendVideoInput;
export type WorkflowUtilitiesBlendVideoOutput = I2VOutput;
export type WorkflowUtilitiesExtractNthFrameInput = ExtractNthFrameInput;
export type WorkflowUtilitiesExtractNthFrameOutput = ExtractNthFrameOutput;
export type WorkflowUtilitiesImpulseResponseInput = ImpulseResponseInput;
export type WorkflowUtilitiesImpulseResponseOutput = MiniOutput;
export type WorkflowUtilitiesReverseVideoInput = AutoCaptionOutput;
export type WorkflowUtilitiesReverseVideoOutput = I2VOutput;
export type WorkflowUtilitiesScaleVideoInput = ScaleVideoInput;
export type WorkflowUtilitiesScaleVideoOutput = ScaleVideoOutput;
export type WorkflowUtilitiesTrimVideoInput = TrimVideoInput;
export type WorkflowUtilitiesTrimVideoOutput = TrimVideoOutput;
export type XAIImageEditOutput = XAIImageOutput;
export type XAIImageInput = photaInput;
export type XAIImageToVideoOutput = React1Output;
export type XAIReferenceToVideoOutput = React1Output;
export type XAITextToVideoOutput = React1Output;
export type XAITTSStreamInput = TtsV1Input;
export type XAIVideoEditOutput = React1Output;
export type XAIVideoExtensionOutput = React1Output;
export type YouTubeThumbnailOutput = BlurOutput;
export type YouTubeThumbnailsOutput = pulidOutput;
export type yueOutput = DiaOutput;
export type ZImageBaseInput = OvisImageInput;
export type ZImageBaseLoraOutput = Flux2Output;
export type ZImageBaseOutput = Flux2Output;
export type ZImageBaseTextToImageInput = OvisImageInput;
export type ZImageBaseTextToImageLoRAInput = ZImageBaseLoraInput;
export type ZImageBaseTrainerOutput = Flux2TrainerOutput;
export type ZImageTrainerOutput = Flux2TrainerOutput;
export type ZImageTurboControlNetInput = ZImageTurboControlnetInput;
export type ZImageTurboControlNetLoRAInput = ZImageTurboControlnetLoraInput;
export type ZImageTurboControlnetLoraOutput = Flux2Output;
export type ZImageTurboControlnetOutput = Flux2Output;
export type ZImageTurboControlNetOutput = Flux2Output;
export type ZImageTurboImageToImageLoRAInput = ZImageTurboImageToImageLoraInput;
export type ZImageTurboImageToImageLoraOutput = Flux2Output;
export type ZImageTurboImageToImageOutput = Flux2Output;
export type ZImageTurboInpaintLoRAInput = ZImageTurboInpaintLoraInput;
export type ZImageTurboInpaintLoraOutput = Flux2Output;
export type ZImageTurboInpaintOutput = Flux2Output;
export type ZImageTurboLoraOutput = Flux2Output;
export type ZImageTurboOutput = Flux2Output;
export type ZImageTurboTextToImageInput = ZImageTurboInput;
export type ZImageTurboTextToImageLoRAInput = ZImageTurboLoraInput;
export type ZImageTurboTilingLoRAInput = ZImageTurboTilingLoraInput;
export type ZImageTurboTilingLoraOutput = Flux2Output;
export type ZImageTurboTilingOutput = Flux2Output;
export type ZImageTurboTrainerV2Input = ZImageBaseTrainerInput;
export type ZImageTurboTrainerV2Output = Flux2TrainerOutput;
export type ZoeOutput = HEDOutput;
export type zonosOutput = DiaOutput;
export type EndpointTypeMap = {
  "argil/avatars/audio-to-video": {
    input: AvatarsAudioToVideoInput;
    output: AvatarsAudioToVideoOutput;
  };
  "argil/avatars/text-to-video": {
    input: AvatarsTextToVideoInput;
    output: AvatarsTextToVideoOutput;
  };
  "bria/embed-product": {
    input: EmbedProductInput;
    output: EmbedProductOutput;
  };
  "bria/fibo-bbq-preview/generate": {
    input: FiboBbqPreviewGenerateInput;
    output: FiboBbqPreviewGenerateOutput;
  };
  "bria/fibo-edit/add_object_by_text": {
    input: FiboEditAddObjectByTextInput;
    output: FiboEditAddObjectByTextOutput;
  };
  "bria/fibo-edit/blend": {
    input: FiboEditBlendInput;
    output: FiboEditBlendOutput;
  };
  "bria/fibo-edit/colorize": {
    input: FiboEditColorizeInput;
    output: FiboEditColorizeOutput;
  };
  "bria/fibo-edit/edit": {
    input: FiboEditEditInput;
    output: FiboEditEditOutput;
  };
  "bria/fibo-edit/erase_by_text": {
    input: FiboEditEraseByTextInput;
    output: FiboEditEraseByTextOutput;
  };
  "bria/fibo-edit/relight": {
    input: FiboEditRelightInput;
    output: FiboEditRelightOutput;
  };
  "bria/fibo-edit/replace_object_by_text": {
    input: FiboEditReplaceObjectByTextInput;
    output: FiboEditReplaceObjectByTextOutput;
  };
  "bria/fibo-edit/reseason": {
    input: FiboEditReseasonInput;
    output: FiboEditReseasonOutput;
  };
  "bria/fibo-edit/restore": {
    input: FiboEditRestoreInput;
    output: FiboEditRestoreOutput;
  };
  "bria/fibo-edit/restyle": {
    input: FiboEditRestyleInput;
    output: FiboEditRestyleOutput;
  };
  "bria/fibo-edit/rewrite_text": {
    input: FiboEditRewriteTextInput;
    output: FiboEditRewriteTextOutput;
  };
  "bria/fibo-edit/sketch_to_colored_image": {
    input: FiboEditSketchToColoredImageInput;
    output: FiboEditSketchToColoredImageOutput;
  };
  "bria/fibo/generate": {
    input: FiboGenerateInput;
    output: FiboGenerateOutput;
  };
  "bria/reimagine/3.2": {
    input: Reimagine32Input;
    output: Reimagine32Output;
  };
  "bria/replace-background": {
    input: ReplaceBackgroundInput;
    output: ReplaceBackgroundOutput;
  };
  "bria/text-to-image/3.2": {
    input: TextToImage32Input;
    output: TextToImage32Output;
  };
  "bria/upscale/creative": {
    input: UpscaleCreativeInput;
    output: UpscaleCreativeOutput;
  };
  "bria/video/background-removal": {
    input: VideoBackgroundRemovalInput;
    output: VideoBackgroundRemovalOutput;
  };
  "bria/video/increase-resolution": {
    input: VideoIncreaseResolutionInput;
    output: VideoIncreaseResolutionOutput;
  };
  "bytedance/lynx": {
    input: lynxInput;
    output: lynxOutput;
  };
  "CassetteAI/music-generator": {
    input: MusicGeneratorInput;
    output: MusicGeneratorOutput;
  };
  "cassetteai/sound-effects-generator": {
    input: SoundEffectsGeneratorInput;
    output: SoundEffectsGeneratorOutput;
  };
  "cassetteai/video-sound-effects-generator": {
    input: VideoSoundEffectsGeneratorInput;
    output: VideoSoundEffectsGeneratorOutput;
  };
  "clarityai/crystal-upscaler": {
    input: CrystalUpscalerInput;
    output: CrystalUpscalerOutput;
  };
  "clarityai/crystal-video-upscaler": {
    input: CrystalVideoUpscalerInput;
    output: CrystalVideoUpscalerOutput;
  };
  "decart/lucy-edit/dev": {
    input: LucyEditDevInput;
    output: LucyEditDevOutput;
  };
  "decart/lucy-edit/fast": {
    input: LucyEditFastInput;
    output: LucyEditFastOutput;
  };
  "decart/lucy-edit/pro": {
    input: LucyEditProInput;
    output: LucyEditProOutput;
  };
  "decart/lucy-i2v": {
    input: LucyI2vInput;
    output: LucyI2vOutput;
  };
  "decart/lucy-restyle": {
    input: LucyRestyleInput;
    output: LucyRestyleOutput;
  };
  "fal-ai/ace-step": {
    input: AceStepInput;
    output: AceStepOutput;
  };
  "fal-ai/ace-step/audio-inpaint": {
    input: AceStepAudioInpaintInput;
    output: AceStepAudioInpaintOutput;
  };
  "fal-ai/ace-step/audio-outpaint": {
    input: AceStepAudioOutpaintInput;
    output: AceStepAudioOutpaintOutput;
  };
  "fal-ai/ace-step/audio-to-audio": {
    input: AceStepAudioToAudioInput;
    output: AceStepAudioToAudioOutput;
  };
  "fal-ai/ace-step/prompt-to-audio": {
    input: AceStepPromptToAudioInput;
    output: AceStepPromptToAudioOutput;
  };
  "fal-ai/ai-avatar/multi": {
    input: AiAvatarMultiInput;
    output: AiAvatarMultiOutput;
  };
  "fal-ai/ai-avatar/multi-text": {
    input: AiAvatarMultiTextInput;
    output: AiAvatarMultiTextOutput;
  };
  "fal-ai/ai-avatar/single-text": {
    input: AiAvatarSingleTextInput;
    output: AiAvatarSingleTextOutput;
  };
  "fal-ai/amt-interpolation": {
    input: AmtInterpolationInput;
    output: AmtInterpolationOutput;
  };
  "fal-ai/amt-interpolation/frame-interpolation": {
    input: AmtInterpolationFrameInterpolationInput;
    output: AmtInterpolationFrameInterpolationOutput;
  };
  "fal-ai/animatediff-sparsectrl-lcm": {
    input: AnimatediffSparsectrlLcmInput;
    output: AnimatediffSparsectrlLcmOutput;
  };
  "fal-ai/arbiter/image": {
    input: ArbiterImageInput;
    output: ArbiterImageOutput;
  };
  "fal-ai/arbiter/image/image": {
    input: ArbiterImageImageInput;
    output: ArbiterImageImageOutput;
  };
  "fal-ai/arbiter/image/text": {
    input: ArbiterImageTextInput;
    output: ArbiterImageTextOutput;
  };
  "fal-ai/audio-understanding": {
    input: AudioUnderstandingInput;
    output: AudioUnderstandingOutput;
  };
  "fal-ai/aura-flow": {
    input: AuraFlowInput;
    output: AuraFlowOutput;
  };
  "fal-ai/aura-sr": {
    input: AuraSrInput;
    output: AuraSrOutput;
  };
  "fal-ai/auto-caption": {
    input: AutoCaptionInput;
    output: AutoCaptionOutput;
  };
  "fal-ai/bagel": {
    input: bagelInput;
    output: bagelOutput;
  };
  "fal-ai/bagel/edit": {
    input: BagelEditInput;
    output: BagelEditOutput;
  };
  "fal-ai/bagel/understand": {
    input: BagelUnderstandInput;
    output: BagelUnderstandOutput;
  };
  "fal-ai/ben/v2/image": {
    input: BenV2ImageInput;
    output: BenV2ImageOutput;
  };
  "fal-ai/ben/v2/video": {
    input: BenV2VideoInput;
    output: BenV2VideoOutput;
  };
  "fal-ai/birefnet": {
    input: birefnetInput;
    output: birefnetOutput;
  };
  "fal-ai/birefnet/v2": {
    input: BirefnetV2Input;
    output: BirefnetV2Output;
  };
  "fal-ai/birefnet/v2/video": {
    input: BirefnetV2VideoInput;
    output: BirefnetV2VideoOutput;
  };
  "fal-ai/bitdance": {
    input: bitdanceInput;
    output: bitdanceOutput;
  };
  "fal-ai/bria/background/remove": {
    input: BriaBackgroundRemoveInput;
    output: BriaBackgroundRemoveOutput;
  };
  "fal-ai/bria/background/replace": {
    input: BriaBackgroundReplaceInput;
    output: BriaBackgroundReplaceOutput;
  };
  "fal-ai/bria/eraser": {
    input: BriaEraserInput;
    output: BriaEraserOutput;
  };
  "fal-ai/bria/expand": {
    input: BriaExpandInput;
    output: BriaExpandOutput;
  };
  "fal-ai/bria/genfill": {
    input: BriaGenfillInput;
    output: BriaGenfillOutput;
  };
  "fal-ai/bria/product-shot": {
    input: BriaProductShotInput;
    output: BriaProductShotOutput;
  };
  "fal-ai/bria/reimagine": {
    input: BriaReimagineInput;
    output: BriaReimagineOutput;
  };
  "fal-ai/bria/text-to-image/base": {
    input: BriaTextToImageBaseInput;
    output: BriaTextToImageBaseOutput;
  };
  "fal-ai/bria/text-to-image/fast": {
    input: BriaTextToImageFastInput;
    output: BriaTextToImageFastOutput;
  };
  "fal-ai/bria/text-to-image/hd": {
    input: BriaTextToImageHdInput;
    output: BriaTextToImageHdOutput;
  };
  "fal-ai/bytedance-upscaler/upscale/video": {
    input: BytedanceUpscalerUpscaleVideoInput;
    output: BytedanceUpscalerUpscaleVideoOutput;
  };
  "fal-ai/bytedance/dreamactor/v2": {
    input: BytedanceDreamactorV2Input;
    output: BytedanceDreamactorV2Output;
  };
  "fal-ai/bytedance/dreamina/v3.1/text-to-image": {
    input: BytedanceDreaminaV31TextToImageInput;
    output: BytedanceDreaminaV31TextToImageOutput;
  };
  "fal-ai/bytedance/omnihuman": {
    input: BytedanceOmnihumanInput;
    output: BytedanceOmnihumanOutput;
  };
  "fal-ai/bytedance/omnihuman/v1.5": {
    input: BytedanceOmnihumanV15Input;
    output: BytedanceOmnihumanV15Output;
  };
  "fal-ai/bytedance/seed/v2/mini": {
    input: BytedanceSeedV2MiniInput;
    output: BytedanceSeedV2MiniOutput;
  };
  "fal-ai/bytedance/seedance/v1.5/pro/image-to-video": {
    input: BytedanceSeedanceV15ProImageToVideoInput;
    output: BytedanceSeedanceV15ProImageToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1.5/pro/text-to-video": {
    input: BytedanceSeedanceV15ProTextToVideoInput;
    output: BytedanceSeedanceV15ProTextToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1/lite/image-to-video": {
    input: BytedanceSeedanceV1LiteImageToVideoInput;
    output: BytedanceSeedanceV1LiteImageToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1/lite/reference-to-video": {
    input: BytedanceSeedanceV1LiteReferenceToVideoInput;
    output: BytedanceSeedanceV1LiteReferenceToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1/lite/text-to-video": {
    input: BytedanceSeedanceV1LiteTextToVideoInput;
    output: BytedanceSeedanceV1LiteTextToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1/pro/fast/image-to-video": {
    input: BytedanceSeedanceV1ProFastImageToVideoInput;
    output: BytedanceSeedanceV1ProFastImageToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1/pro/fast/text-to-video": {
    input: BytedanceSeedanceV1ProFastTextToVideoInput;
    output: BytedanceSeedanceV1ProFastTextToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1/pro/image-to-video": {
    input: BytedanceSeedanceV1ProImageToVideoInput;
    output: BytedanceSeedanceV1ProImageToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1/pro/text-to-video": {
    input: BytedanceSeedanceV1ProTextToVideoInput;
    output: BytedanceSeedanceV1ProTextToVideoOutput;
  };
  "fal-ai/bytedance/seedream/v3/text-to-image": {
    input: BytedanceSeedreamV3TextToImageInput;
    output: BytedanceSeedreamV3TextToImageOutput;
  };
  "fal-ai/bytedance/seedream/v4.5/edit": {
    input: BytedanceSeedreamV45EditInput;
    output: BytedanceSeedreamV45EditOutput;
  };
  "fal-ai/bytedance/seedream/v4.5/text-to-image": {
    input: BytedanceSeedreamV45TextToImageInput;
    output: BytedanceSeedreamV45TextToImageOutput;
  };
  "fal-ai/bytedance/seedream/v4/edit": {
    input: BytedanceSeedreamV4EditInput;
    output: BytedanceSeedreamV4EditOutput;
  };
  "fal-ai/bytedance/seedream/v4/text-to-image": {
    input: BytedanceSeedreamV4TextToImageInput;
    output: BytedanceSeedreamV4TextToImageOutput;
  };
  "fal-ai/bytedance/seedream/v5/lite/edit": {
    input: BytedanceSeedreamV5LiteEditInput;
    output: BytedanceSeedreamV5LiteEditOutput;
  };
  "fal-ai/bytedance/seedream/v5/lite/text-to-image": {
    input: BytedanceSeedreamV5LiteTextToImageInput;
    output: BytedanceSeedreamV5LiteTextToImageOutput;
  };
  "fal-ai/cartoonify": {
    input: cartoonifyInput;
    output: cartoonifyOutput;
  };
  "fal-ai/cat-vton": {
    input: CatVtonInput;
    output: CatVtonOutput;
  };
  "fal-ai/ccsr": {
    input: ccsrInput;
    output: ccsrOutput;
  };
  "fal-ai/chain-of-zoom": {
    input: ChainOfZoomInput;
    output: ChainOfZoomOutput;
  };
  "fal-ai/chatterbox/speech-to-speech": {
    input: ChatterboxSpeechToSpeechInput;
    output: ChatterboxSpeechToSpeechOutput;
  };
  "fal-ai/chatterbox/text-to-speech": {
    input: ChatterboxTextToSpeechInput;
    output: ChatterboxTextToSpeechOutput;
  };
  "fal-ai/chatterbox/text-to-speech/multilingual": {
    input: ChatterboxTextToSpeechMultilingualInput;
    output: ChatterboxTextToSpeechMultilingualOutput;
  };
  "fal-ai/chrono-edit": {
    input: ChronoEditInput;
    output: ChronoEditOutput;
  };
  "fal-ai/chrono-edit-lora": {
    input: ChronoEditLoraInput;
    output: ChronoEditLoraOutput;
  };
  "fal-ai/chrono-edit-lora-gallery/paintbrush": {
    input: ChronoEditLoraGalleryPaintbrushInput;
    output: ChronoEditLoraGalleryPaintbrushOutput;
  };
  "fal-ai/chrono-edit-lora-gallery/upscaler": {
    input: ChronoEditLoraGalleryUpscalerInput;
    output: ChronoEditLoraGalleryUpscalerOutput;
  };
  "fal-ai/clarity-upscaler": {
    input: ClarityUpscalerInput;
    output: ClarityUpscalerOutput;
  };
  "fal-ai/codeformer": {
    input: codeformerInput;
    output: codeformerOutput;
  };
  "fal-ai/cogvideox-5b": {
    input: Cogvideox5bInput;
    output: Cogvideox5bOutput;
  };
  "fal-ai/cogvideox-5b/image-to-video": {
    input: Cogvideox5bImageToVideoInput;
    output: Cogvideox5bImageToVideoOutput;
  };
  "fal-ai/cogvideox-5b/video-to-video": {
    input: Cogvideox5bVideoToVideoInput;
    output: Cogvideox5bVideoToVideoOutput;
  };
  "fal-ai/cogview4": {
    input: cogview4Input;
    output: cogview4Output;
  };
  "fal-ai/controlnetsdxl": {
    input: controlnetsdxlInput;
    output: controlnetsdxlOutput;
  };
  "fal-ai/controlnext": {
    input: controlnextInput;
    output: controlnextOutput;
  };
  "fal-ai/cosmos-predict-2.5/distilled/text-to-video": {
    input: CosmosPredict25DistilledTextToVideoInput;
    output: CosmosPredict25DistilledTextToVideoOutput;
  };
  "fal-ai/cosmos-predict-2.5/image-to-video": {
    input: CosmosPredict25ImageToVideoInput;
    output: CosmosPredict25ImageToVideoOutput;
  };
  "fal-ai/cosmos-predict-2.5/text-to-video": {
    input: CosmosPredict25TextToVideoInput;
    output: CosmosPredict25TextToVideoOutput;
  };
  "fal-ai/cosmos-predict-2.5/video-to-video": {
    input: CosmosPredict25VideoToVideoInput;
    output: CosmosPredict25VideoToVideoOutput;
  };
  "fal-ai/creatify/aurora": {
    input: CreatifyAuroraInput;
    output: CreatifyAuroraOutput;
  };
  "fal-ai/creative-upscaler": {
    input: CreativeUpscalerInput;
    output: CreativeUpscalerOutput;
  };
  "fal-ai/csm-1b": {
    input: Csm1bInput;
    output: Csm1bOutput;
  };
  "fal-ai/davinci-magihuman": {
    input: DavinciMagihumanInput;
    output: DavinciMagihumanOutput;
  };
  "fal-ai/ddcolor": {
    input: ddcolorInput;
    output: ddcolorOutput;
  };
  "fal-ai/deepfilternet3": {
    input: deepfilternet3Input;
    output: deepfilternet3Output;
  };
  "fal-ai/demucs": {
    input: demucsInput;
    output: demucsOutput;
  };
  "fal-ai/depth-anything-video": {
    input: DepthAnythingVideoInput;
    output: DepthAnythingVideoOutput;
  };
  "fal-ai/dia-tts": {
    input: DiaTtsInput;
    output: DiaTtsOutput;
  };
  "fal-ai/dia-tts/voice-clone": {
    input: DiaTtsVoiceCloneInput;
    output: DiaTtsVoiceCloneOutput;
  };
  "fal-ai/diffrhythm": {
    input: diffrhythmInput;
    output: diffrhythmOutput;
  };
  "fal-ai/diffusion-edge": {
    input: DiffusionEdgeInput;
    output: DiffusionEdgeOutput;
  };
  "fal-ai/docres": {
    input: docresInput;
    output: docresOutput;
  };
  "fal-ai/docres/dewarp": {
    input: DocresDewarpInput;
    output: DocresDewarpOutput;
  };
  "fal-ai/drct-super-resolution": {
    input: DrctSuperResolutionInput;
    output: DrctSuperResolutionOutput;
  };
  "fal-ai/dreamo": {
    input: dreamoInput;
    output: dreamoOutput;
  };
  "fal-ai/dreamomni2/edit": {
    input: Dreamomni2EditInput;
    output: Dreamomni2EditOutput;
  };
  "fal-ai/dreamshaper": {
    input: dreamshaperInput;
    output: dreamshaperOutput;
  };
  "fal-ai/dubbing": {
    input: dubbingInput;
    output: dubbingOutput;
  };
  "fal-ai/dwpose": {
    input: dwposeInput;
    output: dwposeOutput;
  };
  "fal-ai/dwpose/video": {
    input: DwposeVideoInput;
    output: DwposeVideoOutput;
  };
  "fal-ai/echomimic-v3": {
    input: EchomimicV3Input;
    output: EchomimicV3Output;
  };
  "fal-ai/editto": {
    input: edittoInput;
    output: edittoOutput;
  };
  "fal-ai/elevenlabs/audio-isolation": {
    input: ElevenlabsAudioIsolationInput;
    output: ElevenlabsAudioIsolationOutput;
  };
  "fal-ai/elevenlabs/dubbing": {
    input: ElevenlabsDubbingInput;
    output: ElevenlabsDubbingOutput;
  };
  "fal-ai/elevenlabs/music": {
    input: ElevenlabsMusicInput;
    output: ElevenlabsMusicOutput;
  };
  "fal-ai/elevenlabs/sound-effects/v2": {
    input: ElevenlabsSoundEffectsV2Input;
    output: ElevenlabsSoundEffectsV2Output;
  };
  "fal-ai/elevenlabs/speech-to-text": {
    input: ElevenlabsSpeechToTextInput;
    output: ElevenlabsSpeechToTextOutput;
  };
  "fal-ai/elevenlabs/speech-to-text/scribe-v2": {
    input: ElevenlabsSpeechToTextScribeV2Input;
    output: ElevenlabsSpeechToTextScribeV2Output;
  };
  "fal-ai/elevenlabs/text-to-dialogue/eleven-v3": {
    input: ElevenlabsTextToDialogueElevenV3Input;
    output: ElevenlabsTextToDialogueElevenV3Output;
  };
  "fal-ai/elevenlabs/tts/eleven-v3": {
    input: ElevenlabsTtsElevenV3Input;
    output: ElevenlabsTtsElevenV3Output;
  };
  "fal-ai/elevenlabs/tts/multilingual-v2": {
    input: ElevenlabsTtsMultilingualV2Input;
    output: ElevenlabsTtsMultilingualV2Output;
  };
  "fal-ai/elevenlabs/tts/turbo-v2.5": {
    input: ElevenlabsTtsTurboV25Input;
    output: ElevenlabsTtsTurboV25Output;
  };
  "fal-ai/elevenlabs/voice-changer": {
    input: ElevenlabsVoiceChangerInput;
    output: ElevenlabsVoiceChangerOutput;
  };
  "fal-ai/emu-3.5-image/edit-image": {
    input: Emu35ImageEditImageInput;
    output: Emu35ImageEditImageOutput;
  };
  "fal-ai/emu-3.5-image/text-to-image": {
    input: Emu35ImageTextToImageInput;
    output: Emu35ImageTextToImageOutput;
  };
  "fal-ai/era-3d": {
    input: Era3dInput;
    output: Era3dOutput;
  };
  "fal-ai/esrgan": {
    input: esrganInput;
    output: esrganOutput;
  };
  "fal-ai/evf-sam": {
    input: EvfSamInput;
    output: EvfSamOutput;
  };
  "fal-ai/f-lite/standard": {
    input: FLiteStandardInput;
    output: FLiteStandardOutput;
  };
  "fal-ai/f-lite/texture": {
    input: FLiteTextureInput;
    output: FLiteTextureOutput;
  };
  "fal-ai/f5-tts": {
    input: F5TtsInput;
    output: F5TtsOutput;
  };
  "fal-ai/face-to-sticker": {
    input: FaceToStickerInput;
    output: FaceToStickerOutput;
  };
  "fal-ai/fashn/tryon/v1.5": {
    input: FashnTryonV15Input;
    output: FashnTryonV15Output;
  };
  "fal-ai/fashn/tryon/v1.6": {
    input: FashnTryonV16Input;
    output: FashnTryonV16Output;
  };
  "fal-ai/fast-animatediff/text-to-video": {
    input: FastAnimatediffTextToVideoInput;
    output: FastAnimatediffTextToVideoOutput;
  };
  "fal-ai/fast-animatediff/turbo/video-to-video": {
    input: FastAnimatediffTurboVideoToVideoInput;
    output: FastAnimatediffTurboVideoToVideoOutput;
  };
  "fal-ai/fast-animatediff/video-to-video": {
    input: FastAnimatediffVideoToVideoInput;
    output: FastAnimatediffVideoToVideoOutput;
  };
  "fal-ai/fast-fooocus-sdxl": {
    input: FastFooocusSdxlInput;
    output: FastFooocusSdxlOutput;
  };
  "fal-ai/fast-fooocus-sdxl/image-to-image": {
    input: FastFooocusSdxlImageToImageInput;
    output: FastFooocusSdxlImageToImageOutput;
  };
  "fal-ai/fast-lcm-diffusion": {
    input: FastLcmDiffusionInput;
    output: FastLcmDiffusionOutput;
  };
  "fal-ai/fast-lcm-diffusion/image-to-image": {
    input: FastLcmDiffusionImageToImageInput;
    output: FastLcmDiffusionImageToImageOutput;
  };
  "fal-ai/fast-lcm-diffusion/inpainting": {
    input: FastLcmDiffusionInpaintingInput;
    output: FastLcmDiffusionInpaintingOutput;
  };
  "fal-ai/fast-lightning-sdxl": {
    input: FastLightningSdxlInput;
    output: FastLightningSdxlOutput;
  };
  "fal-ai/fast-lightning-sdxl/image-to-image": {
    input: FastLightningSdxlImageToImageInput;
    output: FastLightningSdxlImageToImageOutput;
  };
  "fal-ai/fast-lightning-sdxl/inpainting": {
    input: FastLightningSdxlInpaintingInput;
    output: FastLightningSdxlInpaintingOutput;
  };
  "fal-ai/fast-sdxl": {
    input: FastSdxlInput;
    output: FastSdxlOutput;
  };
  "fal-ai/fast-sdxl-controlnet-canny": {
    input: FastSdxlControlnetCannyInput;
    output: FastSdxlControlnetCannyOutput;
  };
  "fal-ai/fast-sdxl-controlnet-canny/image-to-image": {
    input: FastSdxlControlnetCannyImageToImageInput;
    output: FastSdxlControlnetCannyImageToImageOutput;
  };
  "fal-ai/fast-sdxl-controlnet-canny/inpainting": {
    input: FastSdxlControlnetCannyInpaintingInput;
    output: FastSdxlControlnetCannyInpaintingOutput;
  };
  "fal-ai/fast-sdxl/image-to-image": {
    input: FastSdxlImageToImageInput;
    output: FastSdxlImageToImageOutput;
  };
  "fal-ai/fast-sdxl/inpainting": {
    input: FastSdxlInpaintingInput;
    output: FastSdxlInpaintingOutput;
  };
  "fal-ai/fast-svd-lcm": {
    input: FastSvdLcmInput;
    output: FastSvdLcmOutput;
  };
  "fal-ai/fast-svd-lcm/text-to-video": {
    input: FastSvdLcmTextToVideoInput;
    output: FastSvdLcmTextToVideoOutput;
  };
  "fal-ai/fast-svd/text-to-video": {
    input: FastSvdTextToVideoInput;
    output: FastSvdTextToVideoOutput;
  };
  "fal-ai/ffmpeg-api/compose": {
    input: FfmpegApiComposeInput;
    output: FfmpegApiComposeOutput;
  };
  "fal-ai/ffmpeg-api/extract-frame": {
    input: FfmpegApiExtractFrameInput;
    output: FfmpegApiExtractFrameOutput;
  };
  "fal-ai/ffmpeg-api/loudnorm": {
    input: FfmpegApiLoudnormInput;
    output: FfmpegApiLoudnormOutput;
  };
  "fal-ai/ffmpeg-api/merge-audios": {
    input: FfmpegApiMergeAudiosInput;
    output: FfmpegApiMergeAudiosOutput;
  };
  "fal-ai/ffmpeg-api/merge-videos": {
    input: FfmpegApiMergeVideosInput;
    output: FfmpegApiMergeVideosOutput;
  };
  "fal-ai/ffmpeg-api/metadata": {
    input: FfmpegApiMetadataInput;
    output: FfmpegApiMetadataOutput;
  };
  "fal-ai/ffmpeg-api/waveform": {
    input: FfmpegApiWaveformInput;
    output: FfmpegApiWaveformOutput;
  };
  "fal-ai/film": {
    input: filmInput;
    output: filmOutput;
  };
  "fal-ai/film/video": {
    input: FilmVideoInput;
    output: FilmVideoOutput;
  };
  "fal-ai/finegrain-eraser": {
    input: FinegrainEraserInput;
    output: FinegrainEraserOutput;
  };
  "fal-ai/finegrain-eraser/bbox": {
    input: FinegrainEraserBboxInput;
    output: FinegrainEraserBboxOutput;
  };
  "fal-ai/finegrain-eraser/mask": {
    input: FinegrainEraserMaskInput;
    output: FinegrainEraserMaskOutput;
  };
  "fal-ai/firered-image-edit": {
    input: FireredImageEditInput;
    output: FireredImageEditOutput;
  };
  "fal-ai/firered-image-edit-v1.1": {
    input: FireredImageEditV11Input;
    output: FireredImageEditV11Output;
  };
  "fal-ai/flashvsr/upscale/video": {
    input: FlashvsrUpscaleVideoInput;
    output: FlashvsrUpscaleVideoOutput;
  };
  "fal-ai/florence-2-large/caption": {
    input: Florence2LargeCaptionInput;
    output: Florence2LargeCaptionOutput;
  };
  "fal-ai/florence-2-large/caption-to-phrase-grounding": {
    input: Florence2LargeCaptionToPhraseGroundingInput;
    output: Florence2LargeCaptionToPhraseGroundingOutput;
  };
  "fal-ai/florence-2-large/dense-region-caption": {
    input: Florence2LargeDenseRegionCaptionInput;
    output: Florence2LargeDenseRegionCaptionOutput;
  };
  "fal-ai/florence-2-large/detailed-caption": {
    input: Florence2LargeDetailedCaptionInput;
    output: Florence2LargeDetailedCaptionOutput;
  };
  "fal-ai/florence-2-large/more-detailed-caption": {
    input: Florence2LargeMoreDetailedCaptionInput;
    output: Florence2LargeMoreDetailedCaptionOutput;
  };
  "fal-ai/florence-2-large/object-detection": {
    input: Florence2LargeObjectDetectionInput;
    output: Florence2LargeObjectDetectionOutput;
  };
  "fal-ai/florence-2-large/ocr": {
    input: Florence2LargeOcrInput;
    output: Florence2LargeOcrOutput;
  };
  "fal-ai/florence-2-large/ocr-with-region": {
    input: Florence2LargeOcrWithRegionInput;
    output: Florence2LargeOcrWithRegionOutput;
  };
  "fal-ai/florence-2-large/open-vocabulary-detection": {
    input: Florence2LargeOpenVocabularyDetectionInput;
    output: Florence2LargeOpenVocabularyDetectionOutput;
  };
  "fal-ai/florence-2-large/referring-expression-segmentation": {
    input: Florence2LargeReferringExpressionSegmentationInput;
    output: Florence2LargeReferringExpressionSegmentationOutput;
  };
  "fal-ai/florence-2-large/region-proposal": {
    input: Florence2LargeRegionProposalInput;
    output: Florence2LargeRegionProposalOutput;
  };
  "fal-ai/florence-2-large/region-to-category": {
    input: Florence2LargeRegionToCategoryInput;
    output: Florence2LargeRegionToCategoryOutput;
  };
  "fal-ai/florence-2-large/region-to-description": {
    input: Florence2LargeRegionToDescriptionInput;
    output: Florence2LargeRegionToDescriptionOutput;
  };
  "fal-ai/florence-2-large/region-to-segmentation": {
    input: Florence2LargeRegionToSegmentationInput;
    output: Florence2LargeRegionToSegmentationOutput;
  };
  "fal-ai/flowedit": {
    input: floweditInput;
    output: floweditOutput;
  };
  "fal-ai/flux-1/dev": {
    input: Flux1DevInput;
    output: Flux1DevOutput;
  };
  "fal-ai/flux-1/dev/image-to-image": {
    input: Flux1DevImageToImageInput;
    output: Flux1DevImageToImageOutput;
  };
  "fal-ai/flux-1/dev/redux": {
    input: Flux1DevReduxInput;
    output: Flux1DevReduxOutput;
  };
  "fal-ai/flux-1/krea": {
    input: Flux1KreaInput;
    output: Flux1KreaOutput;
  };
  "fal-ai/flux-1/krea/image-to-image": {
    input: Flux1KreaImageToImageInput;
    output: Flux1KreaImageToImageOutput;
  };
  "fal-ai/flux-1/krea/redux": {
    input: Flux1KreaReduxInput;
    output: Flux1KreaReduxOutput;
  };
  "fal-ai/flux-1/schnell": {
    input: Flux1SchnellInput;
    output: Flux1SchnellOutput;
  };
  "fal-ai/flux-1/schnell/redux": {
    input: Flux1SchnellReduxInput;
    output: Flux1SchnellReduxOutput;
  };
  "fal-ai/flux-1/srpo": {
    input: Flux1SrpoInput;
    output: Flux1SrpoOutput;
  };
  "fal-ai/flux-1/srpo/image-to-image": {
    input: Flux1SrpoImageToImageInput;
    output: Flux1SrpoImageToImageOutput;
  };
  "fal-ai/flux-2": {
    input: Flux2Input;
    output: Flux2Output;
  };
  "fal-ai/flux-2-flex": {
    input: Flux2FlexInput;
    output: Flux2FlexOutput;
  };
  "fal-ai/flux-2-flex/edit": {
    input: Flux2FlexEditInput;
    output: Flux2FlexEditOutput;
  };
  "fal-ai/flux-2-klein-4b-base-trainer": {
    input: Flux2Klein4bBaseTrainerInput;
    output: Flux2Klein4bBaseTrainerOutput;
  };
  "fal-ai/flux-2-klein-4b-base-trainer/edit": {
    input: Flux2Klein4bBaseTrainerEditInput;
    output: Flux2Klein4bBaseTrainerEditOutput;
  };
  "fal-ai/flux-2-klein-9b-base-trainer": {
    input: Flux2Klein9bBaseTrainerInput;
    output: Flux2Klein9bBaseTrainerOutput;
  };
  "fal-ai/flux-2-klein-9b-base-trainer/edit": {
    input: Flux2Klein9bBaseTrainerEditInput;
    output: Flux2Klein9bBaseTrainerEditOutput;
  };
  "fal-ai/flux-2-lora-gallery/add-background": {
    input: Flux2LoraGalleryAddBackgroundInput;
    output: Flux2LoraGalleryAddBackgroundOutput;
  };
  "fal-ai/flux-2-lora-gallery/apartment-staging": {
    input: Flux2LoraGalleryApartmentStagingInput;
    output: Flux2LoraGalleryApartmentStagingOutput;
  };
  "fal-ai/flux-2-lora-gallery/ballpoint-pen-sketch": {
    input: Flux2LoraGalleryBallpointPenSketchInput;
    output: Flux2LoraGalleryBallpointPenSketchOutput;
  };
  "fal-ai/flux-2-lora-gallery/digital-comic-art": {
    input: Flux2LoraGalleryDigitalComicArtInput;
    output: Flux2LoraGalleryDigitalComicArtOutput;
  };
  "fal-ai/flux-2-lora-gallery/face-to-full-portrait": {
    input: Flux2LoraGalleryFaceToFullPortraitInput;
    output: Flux2LoraGalleryFaceToFullPortraitOutput;
  };
  "fal-ai/flux-2-lora-gallery/hdr-style": {
    input: Flux2LoraGalleryHdrStyleInput;
    output: Flux2LoraGalleryHdrStyleOutput;
  };
  "fal-ai/flux-2-lora-gallery/multiple-angles": {
    input: Flux2LoraGalleryMultipleAnglesInput;
    output: Flux2LoraGalleryMultipleAnglesOutput;
  };
  "fal-ai/flux-2-lora-gallery/realism": {
    input: Flux2LoraGalleryRealismInput;
    output: Flux2LoraGalleryRealismOutput;
  };
  "fal-ai/flux-2-lora-gallery/satellite-view-style": {
    input: Flux2LoraGallerySatelliteViewStyleInput;
    output: Flux2LoraGallerySatelliteViewStyleOutput;
  };
  "fal-ai/flux-2-lora-gallery/sepia-vintage": {
    input: Flux2LoraGallerySepiaVintageInput;
    output: Flux2LoraGallerySepiaVintageOutput;
  };
  "fal-ai/flux-2-lora-gallery/virtual-tryon": {
    input: Flux2LoraGalleryVirtualTryonInput;
    output: Flux2LoraGalleryVirtualTryonOutput;
  };
  "fal-ai/flux-2-max": {
    input: Flux2MaxInput;
    output: Flux2MaxOutput;
  };
  "fal-ai/flux-2-max/edit": {
    input: Flux2MaxEditInput;
    output: Flux2MaxEditOutput;
  };
  "fal-ai/flux-2-pro": {
    input: Flux2ProInput;
    output: Flux2ProOutput;
  };
  "fal-ai/flux-2-pro/edit": {
    input: Flux2ProEditInput;
    output: Flux2ProEditOutput;
  };
  "fal-ai/flux-2-trainer": {
    input: Flux2TrainerInput;
    output: Flux2TrainerOutput;
  };
  "fal-ai/flux-2-trainer-v2": {
    input: Flux2TrainerV2Input;
    output: Flux2TrainerV2Output;
  };
  "fal-ai/flux-2-trainer-v2/edit": {
    input: Flux2TrainerV2EditInput;
    output: Flux2TrainerV2EditOutput;
  };
  "fal-ai/flux-2-trainer/edit": {
    input: Flux2TrainerEditInput;
    output: Flux2TrainerEditOutput;
  };
  "fal-ai/flux-2/edit": {
    input: Flux2EditInput;
    output: Flux2EditOutput;
  };
  "fal-ai/flux-2/flash": {
    input: Flux2FlashInput;
    output: Flux2FlashOutput;
  };
  "fal-ai/flux-2/flash/edit": {
    input: Flux2FlashEditInput;
    output: Flux2FlashEditOutput;
  };
  "fal-ai/flux-2/klein/4b/base": {
    input: Flux2Klein4bBaseInput;
    output: Flux2Klein4bBaseOutput;
  };
  "fal-ai/flux-2/klein/4b/base/edit": {
    input: Flux2Klein4bBaseEditInput;
    output: Flux2Klein4bBaseEditOutput;
  };
  "fal-ai/flux-2/klein/4b/edit": {
    input: Flux2Klein4bEditInput;
    output: Flux2Klein4bEditOutput;
  };
  "fal-ai/flux-2/klein/4b/edit/lora": {
    input: Flux2Klein4bEditLoraInput;
    output: Flux2Klein4bEditLoraOutput;
  };
  "fal-ai/flux-2/klein/4b/lora": {
    input: Flux2Klein4bLoraInput;
    output: Flux2Klein4bLoraOutput;
  };
  "fal-ai/flux-2/klein/9b": {
    input: Flux2Klein9bInput;
    output: Flux2Klein9bOutput;
  };
  "fal-ai/flux-2/klein/9b/base": {
    input: Flux2Klein9bBaseInput;
    output: Flux2Klein9bBaseOutput;
  };
  "fal-ai/flux-2/klein/9b/base/edit": {
    input: Flux2Klein9bBaseEditInput;
    output: Flux2Klein9bBaseEditOutput;
  };
  "fal-ai/flux-2/klein/9b/base/edit/lora": {
    input: Flux2Klein9bBaseEditLoraInput;
    output: Flux2Klein9bBaseEditLoraOutput;
  };
  "fal-ai/flux-2/klein/9b/base/lora": {
    input: Flux2Klein9bBaseLoraInput;
    output: Flux2Klein9bBaseLoraOutput;
  };
  "fal-ai/flux-2/klein/9b/edit": {
    input: Flux2Klein9bEditInput;
    output: Flux2Klein9bEditOutput;
  };
  "fal-ai/flux-2/klein/9b/edit/lora": {
    input: Flux2Klein9bEditLoraInput;
    output: Flux2Klein9bEditLoraOutput;
  };
  "fal-ai/flux-2/klein/9b/lora": {
    input: Flux2Klein9bLoraInput;
    output: Flux2Klein9bLoraOutput;
  };
  "fal-ai/flux-2/klein/realtime": {
    input: Flux2KleinRealtimeInput;
    output: Flux2KleinRealtimeOutput;
  };
  "fal-ai/flux-2/lora": {
    input: Flux2LoraInput;
    output: Flux2LoraOutput;
  };
  "fal-ai/flux-2/lora/edit": {
    input: Flux2LoraEditInput;
    output: Flux2LoraEditOutput;
  };
  "fal-ai/flux-2/turbo": {
    input: Flux2TurboInput;
    output: Flux2TurboOutput;
  };
  "fal-ai/flux-2/turbo/edit": {
    input: Flux2TurboEditInput;
    output: Flux2TurboEditOutput;
  };
  "fal-ai/flux-control-lora-canny": {
    input: FluxControlLoraCannyInput;
    output: FluxControlLoraCannyOutput;
  };
  "fal-ai/flux-control-lora-canny/image-to-image": {
    input: FluxControlLoraCannyImageToImageInput;
    output: FluxControlLoraCannyImageToImageOutput;
  };
  "fal-ai/flux-control-lora-depth": {
    input: FluxControlLoraDepthInput;
    output: FluxControlLoraDepthOutput;
  };
  "fal-ai/flux-control-lora-depth/image-to-image": {
    input: FluxControlLoraDepthImageToImageInput;
    output: FluxControlLoraDepthImageToImageOutput;
  };
  "fal-ai/flux-differential-diffusion": {
    input: FluxDifferentialDiffusionInput;
    output: FluxDifferentialDiffusionOutput;
  };
  "fal-ai/flux-general": {
    input: FluxGeneralInput;
    output: FluxGeneralOutput;
  };
  "fal-ai/flux-general/differential-diffusion": {
    input: FluxGeneralDifferentialDiffusionInput;
    output: FluxGeneralDifferentialDiffusionOutput;
  };
  "fal-ai/flux-general/image-to-image": {
    input: FluxGeneralImageToImageInput;
    output: FluxGeneralImageToImageOutput;
  };
  "fal-ai/flux-general/inpainting": {
    input: FluxGeneralInpaintingInput;
    output: FluxGeneralInpaintingOutput;
  };
  "fal-ai/flux-general/rf-inversion": {
    input: FluxGeneralRfInversionInput;
    output: FluxGeneralRfInversionOutput;
  };
  "fal-ai/flux-kontext-lora": {
    input: FluxKontextLoraInput;
    output: FluxKontextLoraOutput;
  };
  "fal-ai/flux-kontext-lora/text-to-image": {
    input: FluxKontextLoraTextToImageInput;
    output: FluxKontextLoraTextToImageOutput;
  };
  "fal-ai/flux-kontext-trainer": {
    input: FluxKontextTrainerInput;
    output: FluxKontextTrainerOutput;
  };
  "fal-ai/flux-kontext/dev": {
    input: FluxKontextDevInput;
    output: FluxKontextDevOutput;
  };
  "fal-ai/flux-krea-lora": {
    input: FluxKreaLoraInput;
    output: FluxKreaLoraOutput;
  };
  "fal-ai/flux-krea-lora/image-to-image": {
    input: FluxKreaLoraImageToImageInput;
    output: FluxKreaLoraImageToImageOutput;
  };
  "fal-ai/flux-krea-lora/inpainting": {
    input: FluxKreaLoraInpaintingInput;
    output: FluxKreaLoraInpaintingOutput;
  };
  "fal-ai/flux-krea-lora/stream": {
    input: FluxKreaLoraStreamInput;
    output: FluxKreaLoraStreamOutput;
  };
  "fal-ai/flux-krea-trainer": {
    input: FluxKreaTrainerInput;
    output: FluxKreaTrainerOutput;
  };
  "fal-ai/flux-lora": {
    input: FluxLoraInput;
    output: FluxLoraOutput;
  };
  "fal-ai/flux-lora-canny": {
    input: FluxLoraCannyInput;
    output: FluxLoraCannyOutput;
  };
  "fal-ai/flux-lora-depth": {
    input: FluxLoraDepthInput;
    output: FluxLoraDepthOutput;
  };
  "fal-ai/flux-lora-fast-training": {
    input: FluxLoraFastTrainingInput;
    output: FluxLoraFastTrainingOutput;
  };
  "fal-ai/flux-lora-fill": {
    input: FluxLoraFillInput;
    output: FluxLoraFillOutput;
  };
  "fal-ai/flux-lora-portrait-trainer": {
    input: FluxLoraPortraitTrainerInput;
    output: FluxLoraPortraitTrainerOutput;
  };
  "fal-ai/flux-lora/image-to-image": {
    input: FluxLoraImageToImageInput;
    output: FluxLoraImageToImageOutput;
  };
  "fal-ai/flux-lora/stream": {
    input: FluxLoraStreamInput;
    output: FluxLoraStreamOutput;
  };
  "fal-ai/flux-pro/kontext": {
    input: FluxProKontextInput;
    output: FluxProKontextOutput;
  };
  "fal-ai/flux-pro/kontext/max": {
    input: FluxProKontextMaxInput;
    output: FluxProKontextMaxOutput;
  };
  "fal-ai/flux-pro/kontext/max/multi": {
    input: FluxProKontextMaxMultiInput;
    output: FluxProKontextMaxMultiOutput;
  };
  "fal-ai/flux-pro/kontext/max/text-to-image": {
    input: FluxProKontextMaxTextToImageInput;
    output: FluxProKontextMaxTextToImageOutput;
  };
  "fal-ai/flux-pro/kontext/multi": {
    input: FluxProKontextMultiInput;
    output: FluxProKontextMultiOutput;
  };
  "fal-ai/flux-pro/kontext/text-to-image": {
    input: FluxProKontextTextToImageInput;
    output: FluxProKontextTextToImageOutput;
  };
  "fal-ai/flux-pro/new": {
    input: FluxProNewInput;
    output: FluxProNewOutput;
  };
  "fal-ai/flux-pro/v1.1": {
    input: FluxProV11Input;
    output: FluxProV11Output;
  };
  "fal-ai/flux-pro/v1.1-ultra": {
    input: FluxProV11UltraInput;
    output: FluxProV11UltraOutput;
  };
  "fal-ai/flux-pro/v1.1-ultra-finetuned": {
    input: FluxProV11UltraFinetunedInput;
    output: FluxProV11UltraFinetunedOutput;
  };
  "fal-ai/flux-pro/v1.1-ultra/redux": {
    input: FluxProV11UltraReduxInput;
    output: FluxProV11UltraReduxOutput;
  };
  "fal-ai/flux-pro/v1.1/redux": {
    input: FluxProV11ReduxInput;
    output: FluxProV11ReduxOutput;
  };
  "fal-ai/flux-pro/v1/fill": {
    input: FluxProV1FillInput;
    output: FluxProV1FillOutput;
  };
  "fal-ai/flux-pro/v1/fill-finetuned": {
    input: FluxProV1FillFinetunedInput;
    output: FluxProV1FillFinetunedOutput;
  };
  "fal-ai/flux-pro/v1/redux": {
    input: FluxProV1ReduxInput;
    output: FluxProV1ReduxOutput;
  };
  "fal-ai/flux-pulid": {
    input: FluxPulidInput;
    output: FluxPulidOutput;
  };
  "fal-ai/flux-subject": {
    input: FluxSubjectInput;
    output: FluxSubjectOutput;
  };
  "fal-ai/flux-vision-upscaler": {
    input: FluxVisionUpscalerInput;
    output: FluxVisionUpscalerOutput;
  };
  "fal-ai/flux/dev": {
    input: FluxDevInput;
    output: FluxDevOutput;
  };
  "fal-ai/flux/dev/image-to-image": {
    input: FluxDevImageToImageInput;
    output: FluxDevImageToImageOutput;
  };
  "fal-ai/flux/krea": {
    input: FluxKreaInput;
    output: FluxKreaOutput;
  };
  "fal-ai/flux/krea/image-to-image": {
    input: FluxKreaImageToImageInput;
    output: FluxKreaImageToImageOutput;
  };
  "fal-ai/flux/krea/redux": {
    input: FluxKreaReduxInput;
    output: FluxKreaReduxOutput;
  };
  "fal-ai/flux/schnell": {
    input: FluxSchnellInput;
    output: FluxSchnellOutput;
  };
  "fal-ai/flux/schnell/redux": {
    input: FluxSchnellReduxInput;
    output: FluxSchnellReduxOutput;
  };
  "fal-ai/flux/srpo": {
    input: FluxSrpoInput;
    output: FluxSrpoOutput;
  };
  "fal-ai/flux/srpo/image-to-image": {
    input: FluxSrpoImageToImageInput;
    output: FluxSrpoImageToImageOutput;
  };
  "fal-ai/fooocus": {
    input: fooocusInput;
    output: fooocusOutput;
  };
  "fal-ai/fooocus/image-prompt": {
    input: FooocusImagePromptInput;
    output: FooocusImagePromptOutput;
  };
  "fal-ai/fooocus/inpaint": {
    input: FooocusInpaintInput;
    output: FooocusInpaintOutput;
  };
  "fal-ai/fooocus/upscale-or-vary": {
    input: FooocusUpscaleOrVaryInput;
    output: FooocusUpscaleOrVaryOutput;
  };
  "fal-ai/framepack": {
    input: framepackInput;
    output: framepackOutput;
  };
  "fal-ai/framepack/f1": {
    input: FramepackF1Input;
    output: FramepackF1Output;
  };
  "fal-ai/framepack/flf2v": {
    input: FramepackFlf2vInput;
    output: FramepackFlf2vOutput;
  };
  "fal-ai/gemini-25-flash-image": {
    input: Gemini25FlashImageInput;
    output: Gemini25FlashImageOutput;
  };
  "fal-ai/gemini-25-flash-image/edit": {
    input: Gemini25FlashImageEditInput;
    output: Gemini25FlashImageEditOutput;
  };
  "fal-ai/gemini-3-pro-image-preview": {
    input: Gemini3ProImagePreviewInput;
    output: Gemini3ProImagePreviewOutput;
  };
  "fal-ai/gemini-3-pro-image-preview/edit": {
    input: Gemini3ProImagePreviewEditInput;
    output: Gemini3ProImagePreviewEditOutput;
  };
  "fal-ai/gemini-3.1-flash-image-preview": {
    input: Gemini31FlashImagePreviewInput;
    output: Gemini31FlashImagePreviewOutput;
  };
  "fal-ai/gemini-3.1-flash-image-preview/edit": {
    input: Gemini31FlashImagePreviewEditInput;
    output: Gemini31FlashImagePreviewEditOutput;
  };
  "fal-ai/gemini-flash-edit": {
    input: GeminiFlashEditInput;
    output: GeminiFlashEditOutput;
  };
  "fal-ai/gemini-flash-edit/multi": {
    input: GeminiFlashEditMultiInput;
    output: GeminiFlashEditMultiOutput;
  };
  "fal-ai/gemini-tts": {
    input: GeminiTtsInput;
    output: GeminiTtsOutput;
  };
  "fal-ai/genfocus": {
    input: genfocusInput;
    output: genfocusOutput;
  };
  "fal-ai/genfocus/all-in-focus": {
    input: GenfocusAllInFocusInput;
    output: GenfocusAllInFocusOutput;
  };
  "fal-ai/ghiblify": {
    input: ghiblifyInput;
    output: ghiblifyOutput;
  };
  "fal-ai/glm-image": {
    input: GlmImageInput;
    output: GlmImageOutput;
  };
  "fal-ai/glm-image/image-to-image": {
    input: GlmImageImageToImageInput;
    output: GlmImageImageToImageOutput;
  };
  "fal-ai/goal-force": {
    input: GoalForceInput;
    output: GoalForceOutput;
  };
  "fal-ai/got-ocr/v2": {
    input: GotOcrV2Input;
    output: GotOcrV2Output;
  };
  "fal-ai/gpt-image-1-mini": {
    input: GptImage1MiniInput;
    output: GptImage1MiniOutput;
  };
  "fal-ai/gpt-image-1-mini/edit": {
    input: GptImage1MiniEditInput;
    output: GptImage1MiniEditOutput;
  };
  "fal-ai/gpt-image-1.5": {
    input: GptImage15Input;
    output: GptImage15Output;
  };
  "fal-ai/gpt-image-1.5/edit": {
    input: GptImage15EditInput;
    output: GptImage15EditOutput;
  };
  "fal-ai/gpt-image-1/edit-image": {
    input: GptImage1EditImageInput;
    output: GptImage1EditImageOutput;
  };
  "fal-ai/gpt-image-1/text-to-image": {
    input: GptImage1TextToImageInput;
    output: GptImage1TextToImageOutput;
  };
  "fal-ai/heygen/avatar3/digital-twin": {
    input: HeygenAvatar3DigitalTwinInput;
    output: HeygenAvatar3DigitalTwinOutput;
  };
  "fal-ai/heygen/avatar4/digital-twin": {
    input: HeygenAvatar4DigitalTwinInput;
    output: HeygenAvatar4DigitalTwinOutput;
  };
  "fal-ai/heygen/avatar4/image-to-video": {
    input: HeygenAvatar4ImageToVideoInput;
    output: HeygenAvatar4ImageToVideoOutput;
  };
  "fal-ai/heygen/v2/translate/precision": {
    input: HeygenV2TranslatePrecisionInput;
    output: HeygenV2TranslatePrecisionOutput;
  };
  "fal-ai/heygen/v2/translate/speed": {
    input: HeygenV2TranslateSpeedInput;
    output: HeygenV2TranslateSpeedOutput;
  };
  "fal-ai/heygen/v2/video-agent": {
    input: HeygenV2VideoAgentInput;
    output: HeygenV2VideoAgentOutput;
  };
  "fal-ai/hidream-e1-1": {
    input: HidreamE11Input;
    output: HidreamE11Output;
  };
  "fal-ai/hidream-e1-full": {
    input: HidreamE1FullInput;
    output: HidreamE1FullOutput;
  };
  "fal-ai/hidream-i1-dev": {
    input: HidreamI1DevInput;
    output: HidreamI1DevOutput;
  };
  "fal-ai/hidream-i1-fast": {
    input: HidreamI1FastInput;
    output: HidreamI1FastOutput;
  };
  "fal-ai/hidream-i1-full": {
    input: HidreamI1FullInput;
    output: HidreamI1FullOutput;
  };
  "fal-ai/hidream-i1-full/image-to-image": {
    input: HidreamI1FullImageToImageInput;
    output: HidreamI1FullImageToImageOutput;
  };
  "fal-ai/hunyuan_world": {
    input: HunyuanWorldInput;
    output: HunyuanWorldOutput;
  };
  "fal-ai/hunyuan_world/image-to-world": {
    input: HunyuanWorldImageToWorldInput;
    output: HunyuanWorldImageToWorldOutput;
  };
  "fal-ai/hunyuan-3d/v3.1/part": {
    input: Hunyuan3dV31PartInput;
    output: Hunyuan3dV31PartOutput;
  };
  "fal-ai/hunyuan-3d/v3.1/pro/image-to-3d": {
    input: Hunyuan3dV31ProImageTo3dInput;
    output: Hunyuan3dV31ProImageTo3dOutput;
  };
  "fal-ai/hunyuan-3d/v3.1/pro/text-to-3d": {
    input: Hunyuan3dV31ProTextTo3dInput;
    output: Hunyuan3dV31ProTextTo3dOutput;
  };
  "fal-ai/hunyuan-3d/v3.1/rapid/image-to-3d": {
    input: Hunyuan3dV31RapidImageTo3dInput;
    output: Hunyuan3dV31RapidImageTo3dOutput;
  };
  "fal-ai/hunyuan-3d/v3.1/rapid/text-to-3d": {
    input: Hunyuan3dV31RapidTextTo3dInput;
    output: Hunyuan3dV31RapidTextTo3dOutput;
  };
  "fal-ai/hunyuan-3d/v3.1/smart-topology": {
    input: Hunyuan3dV31SmartTopologyInput;
    output: Hunyuan3dV31SmartTopologyOutput;
  };
  "fal-ai/hunyuan-avatar": {
    input: HunyuanAvatarInput;
    output: HunyuanAvatarOutput;
  };
  "fal-ai/hunyuan-custom": {
    input: HunyuanCustomInput;
    output: HunyuanCustomOutput;
  };
  "fal-ai/hunyuan-image/v2.1/text-to-image": {
    input: HunyuanImageV21TextToImageInput;
    output: HunyuanImageV21TextToImageOutput;
  };
  "fal-ai/hunyuan-image/v3/instruct/edit": {
    input: HunyuanImageV3InstructEditInput;
    output: HunyuanImageV3InstructEditOutput;
  };
  "fal-ai/hunyuan-image/v3/instruct/text-to-image": {
    input: HunyuanImageV3InstructTextToImageInput;
    output: HunyuanImageV3InstructTextToImageOutput;
  };
  "fal-ai/hunyuan-image/v3/text-to-image": {
    input: HunyuanImageV3TextToImageInput;
    output: HunyuanImageV3TextToImageOutput;
  };
  "fal-ai/hunyuan-motion": {
    input: HunyuanMotionInput;
    output: HunyuanMotionOutput;
  };
  "fal-ai/hunyuan-portrait": {
    input: HunyuanPortraitInput;
    output: HunyuanPortraitOutput;
  };
  "fal-ai/hunyuan-video-foley": {
    input: HunyuanVideoFoleyInput;
    output: HunyuanVideoFoleyOutput;
  };
  "fal-ai/hunyuan-video-image-to-video": {
    input: HunyuanVideoImageToVideoInput;
    output: HunyuanVideoImageToVideoOutput;
  };
  "fal-ai/hunyuan-video-img2vid-lora": {
    input: HunyuanVideoImg2vidLoraInput;
    output: HunyuanVideoImg2vidLoraOutput;
  };
  "fal-ai/hunyuan-video-lora": {
    input: HunyuanVideoLoraInput;
    output: HunyuanVideoLoraOutput;
  };
  "fal-ai/hunyuan-video-lora-training": {
    input: HunyuanVideoLoraTrainingInput;
    output: HunyuanVideoLoraTrainingOutput;
  };
  "fal-ai/hunyuan-video-lora/video-to-video": {
    input: HunyuanVideoLoraVideoToVideoInput;
    output: HunyuanVideoLoraVideoToVideoOutput;
  };
  "fal-ai/hunyuan-video-v1.5/image-to-video": {
    input: HunyuanVideoV15ImageToVideoInput;
    output: HunyuanVideoV15ImageToVideoOutput;
  };
  "fal-ai/hunyuan-video-v1.5/text-to-video": {
    input: HunyuanVideoV15TextToVideoInput;
    output: HunyuanVideoV15TextToVideoOutput;
  };
  "fal-ai/hunyuan-video/video-to-video": {
    input: HunyuanVideoVideoToVideoInput;
    output: HunyuanVideoVideoToVideoOutput;
  };
  "fal-ai/hunyuan3d-v21": {
    input: Hunyuan3dV21Input;
    output: Hunyuan3dV21Output;
  };
  "fal-ai/hunyuan3d-v3/image-to-3d": {
    input: Hunyuan3dV3ImageTo3dInput;
    output: Hunyuan3dV3ImageTo3dOutput;
  };
  "fal-ai/hunyuan3d-v3/sketch-to-3d": {
    input: Hunyuan3dV3SketchTo3dInput;
    output: Hunyuan3dV3SketchTo3dOutput;
  };
  "fal-ai/hunyuan3d-v3/text-to-3d": {
    input: Hunyuan3dV3TextTo3dInput;
    output: Hunyuan3dV3TextTo3dOutput;
  };
  "fal-ai/hunyuan3d/v2": {
    input: Hunyuan3dV2Input;
    output: Hunyuan3dV2Output;
  };
  "fal-ai/hunyuan3d/v2/mini": {
    input: Hunyuan3dV2MiniInput;
    output: Hunyuan3dV2MiniOutput;
  };
  "fal-ai/hunyuan3d/v2/mini/turbo": {
    input: Hunyuan3dV2MiniTurboInput;
    output: Hunyuan3dV2MiniTurboOutput;
  };
  "fal-ai/hunyuan3d/v2/multi-view": {
    input: Hunyuan3dV2MultiViewInput;
    output: Hunyuan3dV2MultiViewOutput;
  };
  "fal-ai/hunyuan3d/v2/multi-view/turbo": {
    input: Hunyuan3dV2MultiViewTurboInput;
    output: Hunyuan3dV2MultiViewTurboOutput;
  };
  "fal-ai/hunyuan3d/v2/turbo": {
    input: Hunyuan3dV2TurboInput;
    output: Hunyuan3dV2TurboOutput;
  };
  "fal-ai/hy-wu-edit": {
    input: HyWuEditInput;
    output: HyWuEditOutput;
  };
  "fal-ai/hyper3d/rodin": {
    input: Hyper3dRodinInput;
    output: Hyper3dRodinOutput;
  };
  "fal-ai/hyper3d/rodin/v2": {
    input: Hyper3dRodinV2Input;
    output: Hyper3dRodinV2Output;
  };
  "fal-ai/iclight-v2": {
    input: IclightV2Input;
    output: IclightV2Output;
  };
  "fal-ai/ideogram/character": {
    input: IdeogramCharacterInput;
    output: IdeogramCharacterOutput;
  };
  "fal-ai/ideogram/character/edit": {
    input: IdeogramCharacterEditInput;
    output: IdeogramCharacterEditOutput;
  };
  "fal-ai/ideogram/character/remix": {
    input: IdeogramCharacterRemixInput;
    output: IdeogramCharacterRemixOutput;
  };
  "fal-ai/ideogram/upscale": {
    input: IdeogramUpscaleInput;
    output: IdeogramUpscaleOutput;
  };
  "fal-ai/ideogram/v2": {
    input: IdeogramV2Input;
    output: IdeogramV2Output;
  };
  "fal-ai/ideogram/v2/edit": {
    input: IdeogramV2EditInput;
    output: IdeogramV2EditOutput;
  };
  "fal-ai/ideogram/v2/remix": {
    input: IdeogramV2RemixInput;
    output: IdeogramV2RemixOutput;
  };
  "fal-ai/ideogram/v2/turbo": {
    input: IdeogramV2TurboInput;
    output: IdeogramV2TurboOutput;
  };
  "fal-ai/ideogram/v2/turbo/edit": {
    input: IdeogramV2TurboEditInput;
    output: IdeogramV2TurboEditOutput;
  };
  "fal-ai/ideogram/v2/turbo/remix": {
    input: IdeogramV2TurboRemixInput;
    output: IdeogramV2TurboRemixOutput;
  };
  "fal-ai/ideogram/v2a": {
    input: IdeogramV2aInput;
    output: IdeogramV2aOutput;
  };
  "fal-ai/ideogram/v2a/remix": {
    input: IdeogramV2aRemixInput;
    output: IdeogramV2aRemixOutput;
  };
  "fal-ai/ideogram/v2a/turbo": {
    input: IdeogramV2aTurboInput;
    output: IdeogramV2aTurboOutput;
  };
  "fal-ai/ideogram/v2a/turbo/remix": {
    input: IdeogramV2aTurboRemixInput;
    output: IdeogramV2aTurboRemixOutput;
  };
  "fal-ai/ideogram/v3": {
    input: IdeogramV3Input;
    output: IdeogramV3Output;
  };
  "fal-ai/ideogram/v3/edit": {
    input: IdeogramV3EditInput;
    output: IdeogramV3EditOutput;
  };
  "fal-ai/ideogram/v3/reframe": {
    input: IdeogramV3ReframeInput;
    output: IdeogramV3ReframeOutput;
  };
  "fal-ai/ideogram/v3/remix": {
    input: IdeogramV3RemixInput;
    output: IdeogramV3RemixOutput;
  };
  "fal-ai/ideogram/v3/replace-background": {
    input: IdeogramV3ReplaceBackgroundInput;
    output: IdeogramV3ReplaceBackgroundOutput;
  };
  "fal-ai/illusion-diffusion": {
    input: IllusionDiffusionInput;
    output: IllusionDiffusionOutput;
  };
  "fal-ai/image-apps-v2/age-modify": {
    input: ImageAppsV2AgeModifyInput;
    output: ImageAppsV2AgeModifyOutput;
  };
  "fal-ai/image-apps-v2/city-teleport": {
    input: ImageAppsV2CityTeleportInput;
    output: ImageAppsV2CityTeleportOutput;
  };
  "fal-ai/image-apps-v2/expression-change": {
    input: ImageAppsV2ExpressionChangeInput;
    output: ImageAppsV2ExpressionChangeOutput;
  };
  "fal-ai/image-apps-v2/hair-change": {
    input: ImageAppsV2HairChangeInput;
    output: ImageAppsV2HairChangeOutput;
  };
  "fal-ai/image-apps-v2/headshot-photo": {
    input: ImageAppsV2HeadshotPhotoInput;
    output: ImageAppsV2HeadshotPhotoOutput;
  };
  "fal-ai/image-apps-v2/makeup-application": {
    input: ImageAppsV2MakeupApplicationInput;
    output: ImageAppsV2MakeupApplicationOutput;
  };
  "fal-ai/image-apps-v2/object-removal": {
    input: ImageAppsV2ObjectRemovalInput;
    output: ImageAppsV2ObjectRemovalOutput;
  };
  "fal-ai/image-apps-v2/outpaint": {
    input: ImageAppsV2OutpaintInput;
    output: ImageAppsV2OutpaintOutput;
  };
  "fal-ai/image-apps-v2/perspective": {
    input: ImageAppsV2PerspectiveInput;
    output: ImageAppsV2PerspectiveOutput;
  };
  "fal-ai/image-apps-v2/photo-restoration": {
    input: ImageAppsV2PhotoRestorationInput;
    output: ImageAppsV2PhotoRestorationOutput;
  };
  "fal-ai/image-apps-v2/photography-effects": {
    input: ImageAppsV2PhotographyEffectsInput;
    output: ImageAppsV2PhotographyEffectsOutput;
  };
  "fal-ai/image-apps-v2/portrait-enhance": {
    input: ImageAppsV2PortraitEnhanceInput;
    output: ImageAppsV2PortraitEnhanceOutput;
  };
  "fal-ai/image-apps-v2/product-holding": {
    input: ImageAppsV2ProductHoldingInput;
    output: ImageAppsV2ProductHoldingOutput;
  };
  "fal-ai/image-apps-v2/product-photography": {
    input: ImageAppsV2ProductPhotographyInput;
    output: ImageAppsV2ProductPhotographyOutput;
  };
  "fal-ai/image-apps-v2/relighting": {
    input: ImageAppsV2RelightingInput;
    output: ImageAppsV2RelightingOutput;
  };
  "fal-ai/image-apps-v2/style-transfer": {
    input: ImageAppsV2StyleTransferInput;
    output: ImageAppsV2StyleTransferOutput;
  };
  "fal-ai/image-apps-v2/texture-transform": {
    input: ImageAppsV2TextureTransformInput;
    output: ImageAppsV2TextureTransformOutput;
  };
  "fal-ai/image-apps-v2/virtual-try-on": {
    input: ImageAppsV2VirtualTryOnInput;
    output: ImageAppsV2VirtualTryOnOutput;
  };
  "fal-ai/image-editing/age-progression": {
    input: ImageEditingAgeProgressionInput;
    output: ImageEditingAgeProgressionOutput;
  };
  "fal-ai/image-editing/baby-version": {
    input: ImageEditingBabyVersionInput;
    output: ImageEditingBabyVersionOutput;
  };
  "fal-ai/image-editing/background-change": {
    input: ImageEditingBackgroundChangeInput;
    output: ImageEditingBackgroundChangeOutput;
  };
  "fal-ai/image-editing/broccoli-haircut": {
    input: ImageEditingBroccoliHaircutInput;
    output: ImageEditingBroccoliHaircutOutput;
  };
  "fal-ai/image-editing/cartoonify": {
    input: ImageEditingCartoonifyInput;
    output: ImageEditingCartoonifyOutput;
  };
  "fal-ai/image-editing/color-correction": {
    input: ImageEditingColorCorrectionInput;
    output: ImageEditingColorCorrectionOutput;
  };
  "fal-ai/image-editing/expression-change": {
    input: ImageEditingExpressionChangeInput;
    output: ImageEditingExpressionChangeOutput;
  };
  "fal-ai/image-editing/face-enhancement": {
    input: ImageEditingFaceEnhancementInput;
    output: ImageEditingFaceEnhancementOutput;
  };
  "fal-ai/image-editing/hair-change": {
    input: ImageEditingHairChangeInput;
    output: ImageEditingHairChangeOutput;
  };
  "fal-ai/image-editing/object-removal": {
    input: ImageEditingObjectRemovalInput;
    output: ImageEditingObjectRemovalOutput;
  };
  "fal-ai/image-editing/photo-restoration": {
    input: ImageEditingPhotoRestorationInput;
    output: ImageEditingPhotoRestorationOutput;
  };
  "fal-ai/image-editing/plushie-style": {
    input: ImageEditingPlushieStyleInput;
    output: ImageEditingPlushieStyleOutput;
  };
  "fal-ai/image-editing/professional-photo": {
    input: ImageEditingProfessionalPhotoInput;
    output: ImageEditingProfessionalPhotoOutput;
  };
  "fal-ai/image-editing/realism": {
    input: ImageEditingRealismInput;
    output: ImageEditingRealismOutput;
  };
  "fal-ai/image-editing/reframe": {
    input: ImageEditingReframeInput;
    output: ImageEditingReframeOutput;
  };
  "fal-ai/image-editing/retouch": {
    input: ImageEditingRetouchInput;
    output: ImageEditingRetouchOutput;
  };
  "fal-ai/image-editing/scene-composition": {
    input: ImageEditingSceneCompositionInput;
    output: ImageEditingSceneCompositionOutput;
  };
  "fal-ai/image-editing/style-transfer": {
    input: ImageEditingStyleTransferInput;
    output: ImageEditingStyleTransferOutput;
  };
  "fal-ai/image-editing/text-removal": {
    input: ImageEditingTextRemovalInput;
    output: ImageEditingTextRemovalOutput;
  };
  "fal-ai/image-editing/time-of-day": {
    input: ImageEditingTimeOfDayInput;
    output: ImageEditingTimeOfDayOutput;
  };
  "fal-ai/image-editing/weather-effect": {
    input: ImageEditingWeatherEffectInput;
    output: ImageEditingWeatherEffectOutput;
  };
  "fal-ai/image-editing/wojak-style": {
    input: ImageEditingWojakStyleInput;
    output: ImageEditingWojakStyleOutput;
  };
  "fal-ai/image-editing/youtube-thumbnails": {
    input: ImageEditingYoutubeThumbnailsInput;
    output: ImageEditingYoutubeThumbnailsOutput;
  };
  "fal-ai/image-preprocessors/depth-anything/v2": {
    input: ImagePreprocessorsDepthAnythingV2Input;
    output: ImagePreprocessorsDepthAnythingV2Output;
  };
  "fal-ai/image-preprocessors/hed": {
    input: ImagePreprocessorsHedInput;
    output: ImagePreprocessorsHedOutput;
  };
  "fal-ai/image-preprocessors/lineart": {
    input: ImagePreprocessorsLineartInput;
    output: ImagePreprocessorsLineartOutput;
  };
  "fal-ai/image-preprocessors/midas": {
    input: ImagePreprocessorsMidasInput;
    output: ImagePreprocessorsMidasOutput;
  };
  "fal-ai/image-preprocessors/mlsd": {
    input: ImagePreprocessorsMlsdInput;
    output: ImagePreprocessorsMlsdOutput;
  };
  "fal-ai/image-preprocessors/pidi": {
    input: ImagePreprocessorsPidiInput;
    output: ImagePreprocessorsPidiOutput;
  };
  "fal-ai/image-preprocessors/sam": {
    input: ImagePreprocessorsSamInput;
    output: ImagePreprocessorsSamOutput;
  };
  "fal-ai/image-preprocessors/scribble": {
    input: ImagePreprocessorsScribbleInput;
    output: ImagePreprocessorsScribbleOutput;
  };
  "fal-ai/image-preprocessors/teed": {
    input: ImagePreprocessorsTeedInput;
    output: ImagePreprocessorsTeedOutput;
  };
  "fal-ai/image-preprocessors/zoe": {
    input: ImagePreprocessorsZoeInput;
    output: ImagePreprocessorsZoeOutput;
  };
  "fal-ai/image2pixel": {
    input: image2pixelInput;
    output: image2pixelOutput;
  };
  "fal-ai/image2svg": {
    input: image2svgInput;
    output: image2svgOutput;
  };
  "fal-ai/imagen3": {
    input: imagen3Input;
    output: imagen3Output;
  };
  "fal-ai/imagen3/fast": {
    input: Imagen3FastInput;
    output: Imagen3FastOutput;
  };
  "fal-ai/imagen4/preview": {
    input: Imagen4PreviewInput;
    output: Imagen4PreviewOutput;
  };
  "fal-ai/imagen4/preview/fast": {
    input: Imagen4PreviewFastInput;
    output: Imagen4PreviewFastOutput;
  };
  "fal-ai/imagen4/preview/ultra": {
    input: Imagen4PreviewUltraInput;
    output: Imagen4PreviewUltraOutput;
  };
  "fal-ai/imageutils/depth": {
    input: ImageutilsDepthInput;
    output: ImageutilsDepthOutput;
  };
  "fal-ai/imageutils/marigold-depth": {
    input: ImageutilsMarigoldDepthInput;
    output: ImageutilsMarigoldDepthOutput;
  };
  "fal-ai/imageutils/nsfw": {
    input: ImageutilsNsfwInput;
    output: ImageutilsNsfwOutput;
  };
  "fal-ai/imageutils/rembg": {
    input: ImageutilsRembgInput;
    output: ImageutilsRembgOutput;
  };
  "fal-ai/imageutils/sam": {
    input: ImageutilsSamInput;
    output: ImageutilsSamOutput;
  };
  "fal-ai/index-tts-2/text-to-speech": {
    input: IndexTts2TextToSpeechInput;
    output: IndexTts2TextToSpeechOutput;
  };
  "fal-ai/infinitalk": {
    input: infinitalkInput;
    output: infinitalkOutput;
  };
  "fal-ai/infinitalk/single-text": {
    input: InfinitalkSingleTextInput;
    output: InfinitalkSingleTextOutput;
  };
  "fal-ai/infinitalk/video-to-video": {
    input: InfinitalkVideoToVideoInput;
    output: InfinitalkVideoToVideoOutput;
  };
  "fal-ai/infinity-star/text-to-video": {
    input: InfinityStarTextToVideoInput;
    output: InfinityStarTextToVideoOutput;
  };
  "fal-ai/inpaint": {
    input: inpaintInput;
    output: inpaintOutput;
  };
  "fal-ai/instant-character": {
    input: InstantCharacterInput;
    output: InstantCharacterOutput;
  };
  "fal-ai/invisible-watermark": {
    input: InvisibleWatermarkInput;
    output: InvisibleWatermarkOutput;
  };
  "fal-ai/inworld-tts": {
    input: InworldTtsInput;
    output: InworldTtsOutput;
  };
  "fal-ai/ip-adapter-face-id": {
    input: IpAdapterFaceIdInput;
    output: IpAdapterFaceIdOutput;
  };
  "fal-ai/janus": {
    input: janusInput;
    output: janusOutput;
  };
  "fal-ai/kandinsky5-pro/image-to-video": {
    input: Kandinsky5ProImageToVideoInput;
    output: Kandinsky5ProImageToVideoOutput;
  };
  "fal-ai/kandinsky5-pro/text-to-video": {
    input: Kandinsky5ProTextToVideoInput;
    output: Kandinsky5ProTextToVideoOutput;
  };
  "fal-ai/kandinsky5/text-to-video": {
    input: Kandinsky5TextToVideoInput;
    output: Kandinsky5TextToVideoOutput;
  };
  "fal-ai/kandinsky5/text-to-video/distill": {
    input: Kandinsky5TextToVideoDistillInput;
    output: Kandinsky5TextToVideoDistillOutput;
  };
  "fal-ai/kling-image/o1": {
    input: KlingImageO1Input;
    output: KlingImageO1Output;
  };
  "fal-ai/kling-image/o3/image-to-image": {
    input: KlingImageO3ImageToImageInput;
    output: KlingImageO3ImageToImageOutput;
  };
  "fal-ai/kling-image/o3/text-to-image": {
    input: KlingImageO3TextToImageInput;
    output: KlingImageO3TextToImageOutput;
  };
  "fal-ai/kling-image/v3/image-to-image": {
    input: KlingImageV3ImageToImageInput;
    output: KlingImageV3ImageToImageOutput;
  };
  "fal-ai/kling-image/v3/text-to-image": {
    input: KlingImageV3TextToImageInput;
    output: KlingImageV3TextToImageOutput;
  };
  "fal-ai/kling-video/ai-avatar/v2/pro": {
    input: KlingVideoAiAvatarV2ProInput;
    output: KlingVideoAiAvatarV2ProOutput;
  };
  "fal-ai/kling-video/ai-avatar/v2/standard": {
    input: KlingVideoAiAvatarV2StandardInput;
    output: KlingVideoAiAvatarV2StandardOutput;
  };
  "fal-ai/kling-video/create-voice": {
    input: KlingVideoCreateVoiceInput;
    output: KlingVideoCreateVoiceOutput;
  };
  "fal-ai/kling-video/lipsync/audio-to-video": {
    input: KlingVideoLipsyncAudioToVideoInput;
    output: KlingVideoLipsyncAudioToVideoOutput;
  };
  "fal-ai/kling-video/lipsync/text-to-video": {
    input: KlingVideoLipsyncTextToVideoInput;
    output: KlingVideoLipsyncTextToVideoOutput;
  };
  "fal-ai/kling-video/o1/image-to-video": {
    input: KlingVideoO1ImageToVideoInput;
    output: KlingVideoO1ImageToVideoOutput;
  };
  "fal-ai/kling-video/o1/reference-to-video": {
    input: KlingVideoO1ReferenceToVideoInput;
    output: KlingVideoO1ReferenceToVideoOutput;
  };
  "fal-ai/kling-video/o1/standard/image-to-video": {
    input: KlingVideoO1StandardImageToVideoInput;
    output: KlingVideoO1StandardImageToVideoOutput;
  };
  "fal-ai/kling-video/o1/standard/reference-to-video": {
    input: KlingVideoO1StandardReferenceToVideoInput;
    output: KlingVideoO1StandardReferenceToVideoOutput;
  };
  "fal-ai/kling-video/o1/standard/video-to-video/edit": {
    input: KlingVideoO1StandardVideoToVideoEditInput;
    output: KlingVideoO1StandardVideoToVideoEditOutput;
  };
  "fal-ai/kling-video/o1/standard/video-to-video/reference": {
    input: KlingVideoO1StandardVideoToVideoReferenceInput;
    output: KlingVideoO1StandardVideoToVideoReferenceOutput;
  };
  "fal-ai/kling-video/o1/video-to-video/edit": {
    input: KlingVideoO1VideoToVideoEditInput;
    output: KlingVideoO1VideoToVideoEditOutput;
  };
  "fal-ai/kling-video/o1/video-to-video/reference": {
    input: KlingVideoO1VideoToVideoReferenceInput;
    output: KlingVideoO1VideoToVideoReferenceOutput;
  };
  "fal-ai/kling-video/o3/pro/image-to-video": {
    input: KlingVideoO3ProImageToVideoInput;
    output: KlingVideoO3ProImageToVideoOutput;
  };
  "fal-ai/kling-video/o3/pro/reference-to-video": {
    input: KlingVideoO3ProReferenceToVideoInput;
    output: KlingVideoO3ProReferenceToVideoOutput;
  };
  "fal-ai/kling-video/o3/pro/text-to-video": {
    input: KlingVideoO3ProTextToVideoInput;
    output: KlingVideoO3ProTextToVideoOutput;
  };
  "fal-ai/kling-video/o3/pro/video-to-video/edit": {
    input: KlingVideoO3ProVideoToVideoEditInput;
    output: KlingVideoO3ProVideoToVideoEditOutput;
  };
  "fal-ai/kling-video/o3/pro/video-to-video/reference": {
    input: KlingVideoO3ProVideoToVideoReferenceInput;
    output: KlingVideoO3ProVideoToVideoReferenceOutput;
  };
  "fal-ai/kling-video/o3/standard/image-to-video": {
    input: KlingVideoO3StandardImageToVideoInput;
    output: KlingVideoO3StandardImageToVideoOutput;
  };
  "fal-ai/kling-video/o3/standard/reference-to-video": {
    input: KlingVideoO3StandardReferenceToVideoInput;
    output: KlingVideoO3StandardReferenceToVideoOutput;
  };
  "fal-ai/kling-video/o3/standard/text-to-video": {
    input: KlingVideoO3StandardTextToVideoInput;
    output: KlingVideoO3StandardTextToVideoOutput;
  };
  "fal-ai/kling-video/o3/standard/video-to-video/edit": {
    input: KlingVideoO3StandardVideoToVideoEditInput;
    output: KlingVideoO3StandardVideoToVideoEditOutput;
  };
  "fal-ai/kling-video/o3/standard/video-to-video/reference": {
    input: KlingVideoO3StandardVideoToVideoReferenceInput;
    output: KlingVideoO3StandardVideoToVideoReferenceOutput;
  };
  "fal-ai/kling-video/v1.5/pro/effects": {
    input: KlingVideoV15ProEffectsInput;
    output: KlingVideoV15ProEffectsOutput;
  };
  "fal-ai/kling-video/v1.5/pro/image-to-video": {
    input: KlingVideoV15ProImageToVideoInput;
    output: KlingVideoV15ProImageToVideoOutput;
  };
  "fal-ai/kling-video/v1.5/pro/text-to-video": {
    input: KlingVideoV15ProTextToVideoInput;
    output: KlingVideoV15ProTextToVideoOutput;
  };
  "fal-ai/kling-video/v1.6/pro/effects": {
    input: KlingVideoV16ProEffectsInput;
    output: KlingVideoV16ProEffectsOutput;
  };
  "fal-ai/kling-video/v1.6/pro/elements": {
    input: KlingVideoV16ProElementsInput;
    output: KlingVideoV16ProElementsOutput;
  };
  "fal-ai/kling-video/v1.6/pro/image-to-video": {
    input: KlingVideoV16ProImageToVideoInput;
    output: KlingVideoV16ProImageToVideoOutput;
  };
  "fal-ai/kling-video/v1.6/pro/text-to-video": {
    input: KlingVideoV16ProTextToVideoInput;
    output: KlingVideoV16ProTextToVideoOutput;
  };
  "fal-ai/kling-video/v1.6/standard/effects": {
    input: KlingVideoV16StandardEffectsInput;
    output: KlingVideoV16StandardEffectsOutput;
  };
  "fal-ai/kling-video/v1.6/standard/elements": {
    input: KlingVideoV16StandardElementsInput;
    output: KlingVideoV16StandardElementsOutput;
  };
  "fal-ai/kling-video/v1.6/standard/image-to-video": {
    input: KlingVideoV16StandardImageToVideoInput;
    output: KlingVideoV16StandardImageToVideoOutput;
  };
  "fal-ai/kling-video/v1.6/standard/text-to-video": {
    input: KlingVideoV16StandardTextToVideoInput;
    output: KlingVideoV16StandardTextToVideoOutput;
  };
  "fal-ai/kling-video/v1/pro/ai-avatar": {
    input: KlingVideoV1ProAiAvatarInput;
    output: KlingVideoV1ProAiAvatarOutput;
  };
  "fal-ai/kling-video/v1/standard/ai-avatar": {
    input: KlingVideoV1StandardAiAvatarInput;
    output: KlingVideoV1StandardAiAvatarOutput;
  };
  "fal-ai/kling-video/v1/standard/effects": {
    input: KlingVideoV1StandardEffectsInput;
    output: KlingVideoV1StandardEffectsOutput;
  };
  "fal-ai/kling-video/v1/standard/image-to-video": {
    input: KlingVideoV1StandardImageToVideoInput;
    output: KlingVideoV1StandardImageToVideoOutput;
  };
  "fal-ai/kling-video/v1/standard/text-to-video": {
    input: KlingVideoV1StandardTextToVideoInput;
    output: KlingVideoV1StandardTextToVideoOutput;
  };
  "fal-ai/kling-video/v1/tts": {
    input: KlingVideoV1TtsInput;
    output: KlingVideoV1TtsOutput;
  };
  "fal-ai/kling-video/v2.1/master/image-to-video": {
    input: KlingVideoV21MasterImageToVideoInput;
    output: KlingVideoV21MasterImageToVideoOutput;
  };
  "fal-ai/kling-video/v2.1/master/text-to-video": {
    input: KlingVideoV21MasterTextToVideoInput;
    output: KlingVideoV21MasterTextToVideoOutput;
  };
  "fal-ai/kling-video/v2.1/standard/image-to-video": {
    input: KlingVideoV21StandardImageToVideoInput;
    output: KlingVideoV21StandardImageToVideoOutput;
  };
  "fal-ai/kling-video/v2.5-turbo/pro/image-to-video": {
    input: KlingVideoV25TurboProImageToVideoInput;
    output: KlingVideoV25TurboProImageToVideoOutput;
  };
  "fal-ai/kling-video/v2.5-turbo/pro/text-to-video": {
    input: KlingVideoV25TurboProTextToVideoInput;
    output: KlingVideoV25TurboProTextToVideoOutput;
  };
  "fal-ai/kling-video/v2.5-turbo/standard/image-to-video": {
    input: KlingVideoV25TurboStandardImageToVideoInput;
    output: KlingVideoV25TurboStandardImageToVideoOutput;
  };
  "fal-ai/kling-video/v2.6/pro/image-to-video": {
    input: KlingVideoV26ProImageToVideoInput;
    output: KlingVideoV26ProImageToVideoOutput;
  };
  "fal-ai/kling-video/v2.6/pro/motion-control": {
    input: KlingVideoV26ProMotionControlInput;
    output: KlingVideoV26ProMotionControlOutput;
  };
  "fal-ai/kling-video/v2.6/pro/text-to-video": {
    input: KlingVideoV26ProTextToVideoInput;
    output: KlingVideoV26ProTextToVideoOutput;
  };
  "fal-ai/kling-video/v2.6/standard/motion-control": {
    input: KlingVideoV26StandardMotionControlInput;
    output: KlingVideoV26StandardMotionControlOutput;
  };
  "fal-ai/kling-video/v2/master/image-to-video": {
    input: KlingVideoV2MasterImageToVideoInput;
    output: KlingVideoV2MasterImageToVideoOutput;
  };
  "fal-ai/kling-video/v2/master/text-to-video": {
    input: KlingVideoV2MasterTextToVideoInput;
    output: KlingVideoV2MasterTextToVideoOutput;
  };
  "fal-ai/kling-video/v3/pro/image-to-video": {
    input: KlingVideoV3ProImageToVideoInput;
    output: KlingVideoV3ProImageToVideoOutput;
  };
  "fal-ai/kling-video/v3/pro/motion-control": {
    input: KlingVideoV3ProMotionControlInput;
    output: KlingVideoV3ProMotionControlOutput;
  };
  "fal-ai/kling-video/v3/pro/text-to-video": {
    input: KlingVideoV3ProTextToVideoInput;
    output: KlingVideoV3ProTextToVideoOutput;
  };
  "fal-ai/kling-video/v3/standard/image-to-video": {
    input: KlingVideoV3StandardImageToVideoInput;
    output: KlingVideoV3StandardImageToVideoOutput;
  };
  "fal-ai/kling-video/v3/standard/motion-control": {
    input: KlingVideoV3StandardMotionControlInput;
    output: KlingVideoV3StandardMotionControlOutput;
  };
  "fal-ai/kling-video/v3/standard/text-to-video": {
    input: KlingVideoV3StandardTextToVideoInput;
    output: KlingVideoV3StandardTextToVideoOutput;
  };
  "fal-ai/kling-video/video-to-audio": {
    input: KlingVideoVideoToAudioInput;
    output: KlingVideoVideoToAudioOutput;
  };
  "fal-ai/kling/v1-5/kolors-virtual-try-on": {
    input: KlingV15KolorsVirtualTryOnInput;
    output: KlingV15KolorsVirtualTryOnOutput;
  };
  "fal-ai/kokoro/american-english": {
    input: KokoroAmericanEnglishInput;
    output: KokoroAmericanEnglishOutput;
  };
  "fal-ai/kokoro/brazilian-portuguese": {
    input: KokoroBrazilianPortugueseInput;
    output: KokoroBrazilianPortugueseOutput;
  };
  "fal-ai/kokoro/british-english": {
    input: KokoroBritishEnglishInput;
    output: KokoroBritishEnglishOutput;
  };
  "fal-ai/kokoro/french": {
    input: KokoroFrenchInput;
    output: KokoroFrenchOutput;
  };
  "fal-ai/kokoro/hindi": {
    input: KokoroHindiInput;
    output: KokoroHindiOutput;
  };
  "fal-ai/kokoro/italian": {
    input: KokoroItalianInput;
    output: KokoroItalianOutput;
  };
  "fal-ai/kokoro/japanese": {
    input: KokoroJapaneseInput;
    output: KokoroJapaneseOutput;
  };
  "fal-ai/kokoro/mandarin-chinese": {
    input: KokoroMandarinChineseInput;
    output: KokoroMandarinChineseOutput;
  };
  "fal-ai/kokoro/spanish": {
    input: KokoroSpanishInput;
    output: KokoroSpanishOutput;
  };
  "fal-ai/kolors": {
    input: kolorsInput;
    output: kolorsOutput;
  };
  "fal-ai/kolors/image-to-image": {
    input: KolorsImageToImageInput;
    output: KolorsImageToImageOutput;
  };
  "fal-ai/krea-wan-14b/text-to-video": {
    input: KreaWan14bTextToVideoInput;
    output: KreaWan14bTextToVideoOutput;
  };
  "fal-ai/krea-wan-14b/video-to-video": {
    input: KreaWan14bVideoToVideoInput;
    output: KreaWan14bVideoToVideoOutput;
  };
  "fal-ai/latentsync": {
    input: latentsyncInput;
    output: latentsyncOutput;
  };
  "fal-ai/lava-sr": {
    input: LavaSrInput;
    output: LavaSrOutput;
  };
  "fal-ai/layer-diffusion": {
    input: LayerDiffusionInput;
    output: LayerDiffusionOutput;
  };
  "fal-ai/lcm": {
    input: lcmInput;
    output: lcmOutput;
  };
  "fal-ai/lcm-sd15-i2i": {
    input: LcmSd15I2iInput;
    output: LcmSd15I2iOutput;
  };
  "fal-ai/leffa/pose-transfer": {
    input: LeffaPoseTransferInput;
    output: LeffaPoseTransferOutput;
  };
  "fal-ai/leffa/virtual-tryon": {
    input: LeffaVirtualTryonInput;
    output: LeffaVirtualTryonOutput;
  };
  "fal-ai/lightning-models": {
    input: LightningModelsInput;
    output: LightningModelsOutput;
  };
  "fal-ai/lightx/recamera": {
    input: LightxRecameraInput;
    output: LightxRecameraOutput;
  };
  "fal-ai/lightx/relight": {
    input: LightxRelightInput;
    output: LightxRelightOutput;
  };
  "fal-ai/live-avatar": {
    input: LiveAvatarInput;
    output: LiveAvatarOutput;
  };
  "fal-ai/live-portrait": {
    input: LivePortraitInput;
    output: LivePortraitOutput;
  };
  "fal-ai/live-portrait/image": {
    input: LivePortraitImageInput;
    output: LivePortraitImageOutput;
  };
  "fal-ai/llava-next": {
    input: LlavaNextInput;
    output: LlavaNextOutput;
  };
  "fal-ai/llavav15-13b": {
    input: Llavav1513bInput;
    output: Llavav1513bOutput;
  };
  "fal-ai/longcat-image": {
    input: LongcatImageInput;
    output: LongcatImageOutput;
  };
  "fal-ai/longcat-image/edit": {
    input: LongcatImageEditInput;
    output: LongcatImageEditOutput;
  };
  "fal-ai/longcat-multi-avatar/image-audio-to-video": {
    input: LongcatMultiAvatarImageAudioToVideoInput;
    output: LongcatMultiAvatarImageAudioToVideoOutput;
  };
  "fal-ai/longcat-single-avatar/audio-to-video": {
    input: LongcatSingleAvatarAudioToVideoInput;
    output: LongcatSingleAvatarAudioToVideoOutput;
  };
  "fal-ai/longcat-single-avatar/image-audio-to-video": {
    input: LongcatSingleAvatarImageAudioToVideoInput;
    output: LongcatSingleAvatarImageAudioToVideoOutput;
  };
  "fal-ai/longcat-video/distilled/image-to-video/480p": {
    input: LongcatVideoDistilledImageToVideo480pInput;
    output: LongcatVideoDistilledImageToVideo480pOutput;
  };
  "fal-ai/longcat-video/distilled/image-to-video/720p": {
    input: LongcatVideoDistilledImageToVideo720pInput;
    output: LongcatVideoDistilledImageToVideo720pOutput;
  };
  "fal-ai/longcat-video/distilled/text-to-video/480p": {
    input: LongcatVideoDistilledTextToVideo480pInput;
    output: LongcatVideoDistilledTextToVideo480pOutput;
  };
  "fal-ai/longcat-video/distilled/text-to-video/720p": {
    input: LongcatVideoDistilledTextToVideo720pInput;
    output: LongcatVideoDistilledTextToVideo720pOutput;
  };
  "fal-ai/longcat-video/image-to-video/480p": {
    input: LongcatVideoImageToVideo480pInput;
    output: LongcatVideoImageToVideo480pOutput;
  };
  "fal-ai/longcat-video/image-to-video/720p": {
    input: LongcatVideoImageToVideo720pInput;
    output: LongcatVideoImageToVideo720pOutput;
  };
  "fal-ai/longcat-video/text-to-video/480p": {
    input: LongcatVideoTextToVideo480pInput;
    output: LongcatVideoTextToVideo480pOutput;
  };
  "fal-ai/longcat-video/text-to-video/720p": {
    input: LongcatVideoTextToVideo720pInput;
    output: LongcatVideoTextToVideo720pOutput;
  };
  "fal-ai/lora": {
    input: loraInput;
    output: loraOutput;
  };
  "fal-ai/lora/image-to-image": {
    input: LoraImageToImageInput;
    output: LoraImageToImageOutput;
  };
  "fal-ai/lora/inpaint": {
    input: LoraInpaintInput;
    output: LoraInpaintOutput;
  };
  "fal-ai/ltx-2-19b/audio-to-video": {
    input: Ltx219bAudioToVideoInput;
    output: Ltx219bAudioToVideoOutput;
  };
  "fal-ai/ltx-2-19b/audio-to-video/lora": {
    input: Ltx219bAudioToVideoLoraInput;
    output: Ltx219bAudioToVideoLoraOutput;
  };
  "fal-ai/ltx-2-19b/distilled/audio-to-video": {
    input: Ltx219bDistilledAudioToVideoInput;
    output: Ltx219bDistilledAudioToVideoOutput;
  };
  "fal-ai/ltx-2-19b/distilled/audio-to-video/lora": {
    input: Ltx219bDistilledAudioToVideoLoraInput;
    output: Ltx219bDistilledAudioToVideoLoraOutput;
  };
  "fal-ai/ltx-2-19b/distilled/extend-video": {
    input: Ltx219bDistilledExtendVideoInput;
    output: Ltx219bDistilledExtendVideoOutput;
  };
  "fal-ai/ltx-2-19b/distilled/extend-video/lora": {
    input: Ltx219bDistilledExtendVideoLoraInput;
    output: Ltx219bDistilledExtendVideoLoraOutput;
  };
  "fal-ai/ltx-2-19b/distilled/image-to-video": {
    input: Ltx219bDistilledImageToVideoInput;
    output: Ltx219bDistilledImageToVideoOutput;
  };
  "fal-ai/ltx-2-19b/distilled/text-to-video": {
    input: Ltx219bDistilledTextToVideoInput;
    output: Ltx219bDistilledTextToVideoOutput;
  };
  "fal-ai/ltx-2-19b/distilled/text-to-video/lora": {
    input: Ltx219bDistilledTextToVideoLoraInput;
    output: Ltx219bDistilledTextToVideoLoraOutput;
  };
  "fal-ai/ltx-2-19b/distilled/video-to-video": {
    input: Ltx219bDistilledVideoToVideoInput;
    output: Ltx219bDistilledVideoToVideoOutput;
  };
  "fal-ai/ltx-2-19b/distilled/video-to-video/lora": {
    input: Ltx219bDistilledVideoToVideoLoraInput;
    output: Ltx219bDistilledVideoToVideoLoraOutput;
  };
  "fal-ai/ltx-2-19b/extend-video": {
    input: Ltx219bExtendVideoInput;
    output: Ltx219bExtendVideoOutput;
  };
  "fal-ai/ltx-2-19b/extend-video/lora": {
    input: Ltx219bExtendVideoLoraInput;
    output: Ltx219bExtendVideoLoraOutput;
  };
  "fal-ai/ltx-2-19b/image-to-video": {
    input: Ltx219bImageToVideoInput;
    output: Ltx219bImageToVideoOutput;
  };
  "fal-ai/ltx-2-19b/image-to-video/lora": {
    input: Ltx219bImageToVideoLoraInput;
    output: Ltx219bImageToVideoLoraOutput;
  };
  "fal-ai/ltx-2-19b/text-to-video": {
    input: Ltx219bTextToVideoInput;
    output: Ltx219bTextToVideoOutput;
  };
  "fal-ai/ltx-2-19b/text-to-video/lora": {
    input: Ltx219bTextToVideoLoraInput;
    output: Ltx219bTextToVideoLoraOutput;
  };
  "fal-ai/ltx-2-19b/video-to-video": {
    input: Ltx219bVideoToVideoInput;
    output: Ltx219bVideoToVideoOutput;
  };
  "fal-ai/ltx-2-19b/video-to-video/lora": {
    input: Ltx219bVideoToVideoLoraInput;
    output: Ltx219bVideoToVideoLoraOutput;
  };
  "fal-ai/ltx-2.3/audio-to-video": {
    input: Ltx23AudioToVideoInput;
    output: Ltx23AudioToVideoOutput;
  };
  "fal-ai/ltx-2.3/extend-video": {
    input: Ltx23ExtendVideoInput;
    output: Ltx23ExtendVideoOutput;
  };
  "fal-ai/ltx-2.3/image-to-video": {
    input: Ltx23ImageToVideoInput;
    output: Ltx23ImageToVideoOutput;
  };
  "fal-ai/ltx-2.3/image-to-video/fast": {
    input: Ltx23ImageToVideoFastInput;
    output: Ltx23ImageToVideoFastOutput;
  };
  "fal-ai/ltx-2.3/retake-video": {
    input: Ltx23RetakeVideoInput;
    output: Ltx23RetakeVideoOutput;
  };
  "fal-ai/ltx-2.3/text-to-video": {
    input: Ltx23TextToVideoInput;
    output: Ltx23TextToVideoOutput;
  };
  "fal-ai/ltx-2.3/text-to-video/fast": {
    input: Ltx23TextToVideoFastInput;
    output: Ltx23TextToVideoFastOutput;
  };
  "fal-ai/ltx-2/audio-to-video": {
    input: Ltx2AudioToVideoInput;
    output: Ltx2AudioToVideoOutput;
  };
  "fal-ai/ltx-2/extend-video": {
    input: Ltx2ExtendVideoInput;
    output: Ltx2ExtendVideoOutput;
  };
  "fal-ai/ltx-2/image-to-video": {
    input: Ltx2ImageToVideoInput;
    output: Ltx2ImageToVideoOutput;
  };
  "fal-ai/ltx-2/image-to-video/fast": {
    input: Ltx2ImageToVideoFastInput;
    output: Ltx2ImageToVideoFastOutput;
  };
  "fal-ai/ltx-2/retake-video": {
    input: Ltx2RetakeVideoInput;
    output: Ltx2RetakeVideoOutput;
  };
  "fal-ai/ltx-2/text-to-video": {
    input: Ltx2TextToVideoInput;
    output: Ltx2TextToVideoOutput;
  };
  "fal-ai/ltx-2/text-to-video/fast": {
    input: Ltx2TextToVideoFastInput;
    output: Ltx2TextToVideoFastOutput;
  };
  "fal-ai/ltx-video": {
    input: LtxVideoInput;
    output: LtxVideoOutput;
  };
  "fal-ai/ltx-video-13b-dev": {
    input: LtxVideo13bDevInput;
    output: LtxVideo13bDevOutput;
  };
  "fal-ai/ltx-video-13b-dev/extend": {
    input: LtxVideo13bDevExtendInput;
    output: LtxVideo13bDevExtendOutput;
  };
  "fal-ai/ltx-video-13b-dev/image-to-video": {
    input: LtxVideo13bDevImageToVideoInput;
    output: LtxVideo13bDevImageToVideoOutput;
  };
  "fal-ai/ltx-video-13b-dev/multiconditioning": {
    input: LtxVideo13bDevMulticonditioningInput;
    output: LtxVideo13bDevMulticonditioningOutput;
  };
  "fal-ai/ltx-video-13b-distilled": {
    input: LtxVideo13bDistilledInput;
    output: LtxVideo13bDistilledOutput;
  };
  "fal-ai/ltx-video-13b-distilled/extend": {
    input: LtxVideo13bDistilledExtendInput;
    output: LtxVideo13bDistilledExtendOutput;
  };
  "fal-ai/ltx-video-13b-distilled/image-to-video": {
    input: LtxVideo13bDistilledImageToVideoInput;
    output: LtxVideo13bDistilledImageToVideoOutput;
  };
  "fal-ai/ltx-video-13b-distilled/multiconditioning": {
    input: LtxVideo13bDistilledMulticonditioningInput;
    output: LtxVideo13bDistilledMulticonditioningOutput;
  };
  "fal-ai/ltx-video-lora": {
    input: LtxVideoLoraInput;
    output: LtxVideoLoraOutput;
  };
  "fal-ai/ltx-video-lora/image-to-video": {
    input: LtxVideoLoraImageToVideoInput;
    output: LtxVideoLoraImageToVideoOutput;
  };
  "fal-ai/ltx-video-lora/multiconditioning": {
    input: LtxVideoLoraMulticonditioningInput;
    output: LtxVideoLoraMulticonditioningOutput;
  };
  "fal-ai/ltx-video-trainer": {
    input: LtxVideoTrainerInput;
    output: LtxVideoTrainerOutput;
  };
  "fal-ai/ltx-video-v095": {
    input: LtxVideoV095Input;
    output: LtxVideoV095Output;
  };
  "fal-ai/ltx-video-v095/extend": {
    input: LtxVideoV095ExtendInput;
    output: LtxVideoV095ExtendOutput;
  };
  "fal-ai/ltx-video-v095/image-to-video": {
    input: LtxVideoV095ImageToVideoInput;
    output: LtxVideoV095ImageToVideoOutput;
  };
  "fal-ai/ltx-video-v095/multiconditioning": {
    input: LtxVideoV095MulticonditioningInput;
    output: LtxVideoV095MulticonditioningOutput;
  };
  "fal-ai/ltx-video-v097": {
    input: LtxVideoV097Input;
    output: LtxVideoV097Output;
  };
  "fal-ai/ltx-video-v097/extend": {
    input: LtxVideoV097ExtendInput;
    output: LtxVideoV097ExtendOutput;
  };
  "fal-ai/ltx-video-v097/image-to-video": {
    input: LtxVideoV097ImageToVideoInput;
    output: LtxVideoV097ImageToVideoOutput;
  };
  "fal-ai/ltx-video-v097/multiconditioning": {
    input: LtxVideoV097MulticonditioningInput;
    output: LtxVideoV097MulticonditioningOutput;
  };
  "fal-ai/ltx-video/image-to-video": {
    input: LtxVideoImageToVideoInput;
    output: LtxVideoImageToVideoOutput;
  };
  "fal-ai/ltx2-video-trainer": {
    input: Ltx2VideoTrainerInput;
    output: Ltx2VideoTrainerOutput;
  };
  "fal-ai/ltxv-13b-098-distilled": {
    input: Ltxv13b098DistilledInput;
    output: Ltxv13b098DistilledOutput;
  };
  "fal-ai/ltxv-13b-098-distilled/extend": {
    input: Ltxv13b098DistilledExtendInput;
    output: Ltxv13b098DistilledExtendOutput;
  };
  "fal-ai/ltxv-13b-098-distilled/image-to-video": {
    input: Ltxv13b098DistilledImageToVideoInput;
    output: Ltxv13b098DistilledImageToVideoOutput;
  };
  "fal-ai/ltxv-13b-098-distilled/multiconditioning": {
    input: Ltxv13b098DistilledMulticonditioningInput;
    output: Ltxv13b098DistilledMulticonditioningOutput;
  };
  "fal-ai/lucidflux": {
    input: lucidfluxInput;
    output: lucidfluxOutput;
  };
  "fal-ai/luma-dream-machine/ray-2": {
    input: LumaDreamMachineRay2Input;
    output: LumaDreamMachineRay2Output;
  };
  "fal-ai/luma-dream-machine/ray-2-flash": {
    input: LumaDreamMachineRay2FlashInput;
    output: LumaDreamMachineRay2FlashOutput;
  };
  "fal-ai/luma-dream-machine/ray-2-flash/image-to-video": {
    input: LumaDreamMachineRay2FlashImageToVideoInput;
    output: LumaDreamMachineRay2FlashImageToVideoOutput;
  };
  "fal-ai/luma-dream-machine/ray-2-flash/modify": {
    input: LumaDreamMachineRay2FlashModifyInput;
    output: LumaDreamMachineRay2FlashModifyOutput;
  };
  "fal-ai/luma-dream-machine/ray-2-flash/reframe": {
    input: LumaDreamMachineRay2FlashReframeInput;
    output: LumaDreamMachineRay2FlashReframeOutput;
  };
  "fal-ai/luma-dream-machine/ray-2/image-to-video": {
    input: LumaDreamMachineRay2ImageToVideoInput;
    output: LumaDreamMachineRay2ImageToVideoOutput;
  };
  "fal-ai/luma-dream-machine/ray-2/reframe": {
    input: LumaDreamMachineRay2ReframeInput;
    output: LumaDreamMachineRay2ReframeOutput;
  };
  "fal-ai/luma-photon": {
    input: LumaPhotonInput;
    output: LumaPhotonOutput;
  };
  "fal-ai/luma-photon/flash": {
    input: LumaPhotonFlashInput;
    output: LumaPhotonFlashOutput;
  };
  "fal-ai/luma-photon/flash/modify": {
    input: LumaPhotonFlashModifyInput;
    output: LumaPhotonFlashModifyOutput;
  };
  "fal-ai/luma-photon/flash/reframe": {
    input: LumaPhotonFlashReframeInput;
    output: LumaPhotonFlashReframeOutput;
  };
  "fal-ai/luma-photon/modify": {
    input: LumaPhotonModifyInput;
    output: LumaPhotonModifyOutput;
  };
  "fal-ai/luma-photon/reframe": {
    input: LumaPhotonReframeInput;
    output: LumaPhotonReframeOutput;
  };
  "fal-ai/lumina-image/v2": {
    input: LuminaImageV2Input;
    output: LuminaImageV2Output;
  };
  "fal-ai/lux-tts": {
    input: LuxTtsInput;
    output: LuxTtsOutput;
  };
  "fal-ai/lyria2": {
    input: lyria2Input;
    output: lyria2Output;
  };
  "fal-ai/magi": {
    input: magiInput;
    output: magiOutput;
  };
  "fal-ai/magi-distilled": {
    input: MagiDistilledInput;
    output: MagiDistilledOutput;
  };
  "fal-ai/magi-distilled/extend-video": {
    input: MagiDistilledExtendVideoInput;
    output: MagiDistilledExtendVideoOutput;
  };
  "fal-ai/magi-distilled/image-to-video": {
    input: MagiDistilledImageToVideoInput;
    output: MagiDistilledImageToVideoOutput;
  };
  "fal-ai/magi/extend-video": {
    input: MagiExtendVideoInput;
    output: MagiExtendVideoOutput;
  };
  "fal-ai/magi/image-to-video": {
    input: MagiImageToVideoInput;
    output: MagiImageToVideoOutput;
  };
  "fal-ai/maya": {
    input: mayaInput;
    output: mayaOutput;
  };
  "fal-ai/maya/batch": {
    input: MayaBatchInput;
    output: MayaBatchOutput;
  };
  "fal-ai/meshy/v5/multi-image-to-3d": {
    input: MeshyV5MultiImageTo3dInput;
    output: MeshyV5MultiImageTo3dOutput;
  };
  "fal-ai/meshy/v5/retexture": {
    input: MeshyV5RetextureInput;
    output: MeshyV5RetextureOutput;
  };
  "fal-ai/meshy/v6-preview/text-to-3d": {
    input: MeshyV6PreviewTextTo3dInput;
    output: MeshyV6PreviewTextTo3dOutput;
  };
  "fal-ai/meshy/v6/image-to-3d": {
    input: MeshyV6ImageTo3dInput;
    output: MeshyV6ImageTo3dOutput;
  };
  "fal-ai/meshy/v6/text-to-3d": {
    input: MeshyV6TextTo3dInput;
    output: MeshyV6TextTo3dOutput;
  };
  "fal-ai/minimax-music": {
    input: MinimaxMusicInput;
    output: MinimaxMusicOutput;
  };
  "fal-ai/minimax-music/v1.5": {
    input: MinimaxMusicV15Input;
    output: MinimaxMusicV15Output;
  };
  "fal-ai/minimax-music/v2": {
    input: MinimaxMusicV2Input;
    output: MinimaxMusicV2Output;
  };
  "fal-ai/minimax/hailuo-02-fast/image-to-video": {
    input: MinimaxHailuo02FastImageToVideoInput;
    output: MinimaxHailuo02FastImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-02/pro/image-to-video": {
    input: MinimaxHailuo02ProImageToVideoInput;
    output: MinimaxHailuo02ProImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-02/pro/text-to-video": {
    input: MinimaxHailuo02ProTextToVideoInput;
    output: MinimaxHailuo02ProTextToVideoOutput;
  };
  "fal-ai/minimax/hailuo-02/standard/image-to-video": {
    input: MinimaxHailuo02StandardImageToVideoInput;
    output: MinimaxHailuo02StandardImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-02/standard/text-to-video": {
    input: MinimaxHailuo02StandardTextToVideoInput;
    output: MinimaxHailuo02StandardTextToVideoOutput;
  };
  "fal-ai/minimax/hailuo-2.3-fast/pro/image-to-video": {
    input: MinimaxHailuo23FastProImageToVideoInput;
    output: MinimaxHailuo23FastProImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-2.3-fast/standard/image-to-video": {
    input: MinimaxHailuo23FastStandardImageToVideoInput;
    output: MinimaxHailuo23FastStandardImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-2.3/pro/image-to-video": {
    input: MinimaxHailuo23ProImageToVideoInput;
    output: MinimaxHailuo23ProImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-2.3/pro/text-to-video": {
    input: MinimaxHailuo23ProTextToVideoInput;
    output: MinimaxHailuo23ProTextToVideoOutput;
  };
  "fal-ai/minimax/hailuo-2.3/standard/image-to-video": {
    input: MinimaxHailuo23StandardImageToVideoInput;
    output: MinimaxHailuo23StandardImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-2.3/standard/text-to-video": {
    input: MinimaxHailuo23StandardTextToVideoInput;
    output: MinimaxHailuo23StandardTextToVideoOutput;
  };
  "fal-ai/minimax/image-01": {
    input: MinimaxImage01Input;
    output: MinimaxImage01Output;
  };
  "fal-ai/minimax/image-01/subject-reference": {
    input: MinimaxImage01SubjectReferenceInput;
    output: MinimaxImage01SubjectReferenceOutput;
  };
  "fal-ai/minimax/preview/speech-2.5-turbo": {
    input: MinimaxPreviewSpeech25TurboInput;
    output: MinimaxPreviewSpeech25TurboOutput;
  };
  "fal-ai/minimax/speech-02-hd": {
    input: MinimaxSpeech02HdInput;
    output: MinimaxSpeech02HdOutput;
  };
  "fal-ai/minimax/speech-02-turbo": {
    input: MinimaxSpeech02TurboInput;
    output: MinimaxSpeech02TurboOutput;
  };
  "fal-ai/minimax/speech-2.6-hd": {
    input: MinimaxSpeech26HdInput;
    output: MinimaxSpeech26HdOutput;
  };
  "fal-ai/minimax/speech-2.6-turbo": {
    input: MinimaxSpeech26TurboInput;
    output: MinimaxSpeech26TurboOutput;
  };
  "fal-ai/minimax/speech-2.8-hd": {
    input: MinimaxSpeech28HdInput;
    output: MinimaxSpeech28HdOutput;
  };
  "fal-ai/minimax/speech-2.8-turbo": {
    input: MinimaxSpeech28TurboInput;
    output: MinimaxSpeech28TurboOutput;
  };
  "fal-ai/minimax/video-01": {
    input: MinimaxVideo01Input;
    output: MinimaxVideo01Output;
  };
  "fal-ai/minimax/video-01-director": {
    input: MinimaxVideo01DirectorInput;
    output: MinimaxVideo01DirectorOutput;
  };
  "fal-ai/minimax/video-01-director/image-to-video": {
    input: MinimaxVideo01DirectorImageToVideoInput;
    output: MinimaxVideo01DirectorImageToVideoOutput;
  };
  "fal-ai/minimax/video-01-live/image-to-video": {
    input: MinimaxVideo01LiveImageToVideoInput;
    output: MinimaxVideo01LiveImageToVideoOutput;
  };
  "fal-ai/minimax/video-01-subject-reference": {
    input: MinimaxVideo01SubjectReferenceInput;
    output: MinimaxVideo01SubjectReferenceOutput;
  };
  "fal-ai/minimax/video-01/image-to-video": {
    input: MinimaxVideo01ImageToVideoInput;
    output: MinimaxVideo01ImageToVideoOutput;
  };
  "fal-ai/minimax/voice-clone": {
    input: MinimaxVoiceCloneInput;
    output: MinimaxVoiceCloneOutput;
  };
  "fal-ai/minimax/voice-design": {
    input: MinimaxVoiceDesignInput;
    output: MinimaxVoiceDesignOutput;
  };
  "fal-ai/mix-dehaze-net": {
    input: MixDehazeNetInput;
    output: MixDehazeNetOutput;
  };
  "fal-ai/mmaudio-v2/text-to-audio": {
    input: MmaudioV2TextToAudioInput;
    output: MmaudioV2TextToAudioOutput;
  };
  "fal-ai/mochi-v1": {
    input: MochiV1Input;
    output: MochiV1Output;
  };
  "fal-ai/moondream-next": {
    input: MoondreamNextInput;
    output: MoondreamNextOutput;
  };
  "fal-ai/moondream-next/batch": {
    input: MoondreamNextBatchInput;
    output: MoondreamNextBatchOutput;
  };
  "fal-ai/moondream-next/detection": {
    input: MoondreamNextDetectionInput;
    output: MoondreamNextDetectionOutput;
  };
  "fal-ai/moondream/batched": {
    input: MoondreamBatchedInput;
    output: MoondreamBatchedOutput;
  };
  "fal-ai/moondream2": {
    input: moondream2Input;
    output: moondream2Output;
  };
  "fal-ai/moondream2/object-detection": {
    input: Moondream2ObjectDetectionInput;
    output: Moondream2ObjectDetectionOutput;
  };
  "fal-ai/moondream2/point-object-detection": {
    input: Moondream2PointObjectDetectionInput;
    output: Moondream2PointObjectDetectionOutput;
  };
  "fal-ai/moondream2/visual-query": {
    input: Moondream2VisualQueryInput;
    output: Moondream2VisualQueryOutput;
  };
  "fal-ai/moondream3-preview/caption": {
    input: Moondream3PreviewCaptionInput;
    output: Moondream3PreviewCaptionOutput;
  };
  "fal-ai/moondream3-preview/detect": {
    input: Moondream3PreviewDetectInput;
    output: Moondream3PreviewDetectOutput;
  };
  "fal-ai/moondream3-preview/point": {
    input: Moondream3PreviewPointInput;
    output: Moondream3PreviewPointOutput;
  };
  "fal-ai/moondream3-preview/query": {
    input: Moondream3PreviewQueryInput;
    output: Moondream3PreviewQueryOutput;
  };
  "fal-ai/moondream3-preview/segment": {
    input: Moondream3PreviewSegmentInput;
    output: Moondream3PreviewSegmentOutput;
  };
  "fal-ai/multishot-master": {
    input: MultishotMasterInput;
    output: MultishotMasterOutput;
  };
  "fal-ai/muse-pose": {
    input: MusePoseInput;
    output: MusePoseOutput;
  };
  "fal-ai/musetalk": {
    input: musetalkInput;
    output: musetalkOutput;
  };
  "fal-ai/nafnet/deblur": {
    input: NafnetDeblurInput;
    output: NafnetDeblurOutput;
  };
  "fal-ai/nafnet/denoise": {
    input: NafnetDenoiseInput;
    output: NafnetDenoiseOutput;
  };
  "fal-ai/nano-banana": {
    input: NanoBananaInput;
    output: NanoBananaOutput;
  };
  "fal-ai/nano-banana-2": {
    input: NanoBanana2Input;
    output: NanoBanana2Output;
  };
  "fal-ai/nano-banana-2/edit": {
    input: NanoBanana2EditInput;
    output: NanoBanana2EditOutput;
  };
  "fal-ai/nano-banana-pro": {
    input: NanoBananaProInput;
    output: NanoBananaProOutput;
  };
  "fal-ai/nano-banana-pro/edit": {
    input: NanoBananaProEditInput;
    output: NanoBananaProEditOutput;
  };
  "fal-ai/nano-banana/edit": {
    input: NanoBananaEditInput;
    output: NanoBananaEditOutput;
  };
  "fal-ai/nemotron/asr": {
    input: NemotronAsrInput;
    output: NemotronAsrOutput;
  };
  "fal-ai/nextstep-1": {
    input: Nextstep1Input;
    output: Nextstep1Output;
  };
  "fal-ai/nova-sr": {
    input: NovaSrInput;
    output: NovaSrOutput;
  };
  "fal-ai/object-removal": {
    input: ObjectRemovalInput;
    output: ObjectRemovalOutput;
  };
  "fal-ai/object-removal/mask": {
    input: ObjectRemovalMaskInput;
    output: ObjectRemovalMaskOutput;
  };
  "fal-ai/omni-zero": {
    input: OmniZeroInput;
    output: OmniZeroOutput;
  };
  "fal-ai/omnigen-v1": {
    input: OmnigenV1Input;
    output: OmnigenV1Output;
  };
  "fal-ai/omnigen-v2": {
    input: OmnigenV2Input;
    output: OmnigenV2Output;
  };
  "fal-ai/omnilottie": {
    input: omnilottieInput;
    output: omnilottieOutput;
  };
  "fal-ai/omnilottie/image-to-lottie": {
    input: OmnilottieImageToLottieInput;
    output: OmnilottieImageToLottieOutput;
  };
  "fal-ai/omnilottie/video-to-lottie": {
    input: OmnilottieVideoToLottieInput;
    output: OmnilottieVideoToLottieOutput;
  };
  "fal-ai/omnipart": {
    input: omnipartInput;
    output: omnipartOutput;
  };
  "fal-ai/onereward": {
    input: onerewardInput;
    output: onerewardOutput;
  };
  "fal-ai/orpheus-tts": {
    input: OrpheusTtsInput;
    output: OrpheusTtsOutput;
  };
  "fal-ai/ovis-image": {
    input: OvisImageInput;
    output: OvisImageOutput;
  };
  "fal-ai/pasd": {
    input: pasdInput;
    output: pasdOutput;
  };
  "fal-ai/personaplex": {
    input: personaplexInput;
    output: personaplexOutput;
  };
  "fal-ai/personaplex/realtime": {
    input: PersonaplexRealtimeInput;
    output: PersonaplexRealtimeOutput;
  };
  "fal-ai/phota": {
    input: photaInput;
    output: photaOutput;
  };
  "fal-ai/phota/create-profile": {
    input: PhotaCreateProfileInput;
    output: PhotaCreateProfileOutput;
  };
  "fal-ai/phota/edit": {
    input: PhotaEditInput;
    output: PhotaEditOutput;
  };
  "fal-ai/phota/enhance": {
    input: PhotaEnhanceInput;
    output: PhotaEnhanceOutput;
  };
  "fal-ai/photomaker": {
    input: photomakerInput;
    output: photomakerOutput;
  };
  "fal-ai/physic-edit": {
    input: PhysicEditInput;
    output: PhysicEditOutput;
  };
  "fal-ai/piflow": {
    input: piflowInput;
    output: piflowOutput;
  };
  "fal-ai/pika/v1.5/pikaffects": {
    input: PikaV15PikaffectsInput;
    output: PikaV15PikaffectsOutput;
  };
  "fal-ai/pika/v2.1/image-to-video": {
    input: PikaV21ImageToVideoInput;
    output: PikaV21ImageToVideoOutput;
  };
  "fal-ai/pika/v2.1/text-to-video": {
    input: PikaV21TextToVideoInput;
    output: PikaV21TextToVideoOutput;
  };
  "fal-ai/pika/v2.2/image-to-video": {
    input: PikaV22ImageToVideoInput;
    output: PikaV22ImageToVideoOutput;
  };
  "fal-ai/pika/v2.2/pikaframes": {
    input: PikaV22PikaframesInput;
    output: PikaV22PikaframesOutput;
  };
  "fal-ai/pika/v2.2/text-to-video": {
    input: PikaV22TextToVideoInput;
    output: PikaV22TextToVideoOutput;
  };
  "fal-ai/pika/v2/pikadditions": {
    input: PikaV2PikadditionsInput;
    output: PikaV2PikadditionsOutput;
  };
  "fal-ai/pika/v2/turbo/image-to-video": {
    input: PikaV2TurboImageToVideoInput;
    output: PikaV2TurboImageToVideoOutput;
  };
  "fal-ai/pika/v2/turbo/text-to-video": {
    input: PikaV2TurboTextToVideoInput;
    output: PikaV2TurboTextToVideoOutput;
  };
  "fal-ai/pixart-sigma": {
    input: PixartSigmaInput;
    output: PixartSigmaOutput;
  };
  "fal-ai/pixverse/extend": {
    input: PixverseExtendInput;
    output: PixverseExtendOutput;
  };
  "fal-ai/pixverse/extend/fast": {
    input: PixverseExtendFastInput;
    output: PixverseExtendFastOutput;
  };
  "fal-ai/pixverse/lipsync": {
    input: PixverseLipsyncInput;
    output: PixverseLipsyncOutput;
  };
  "fal-ai/pixverse/sound-effects": {
    input: PixverseSoundEffectsInput;
    output: PixverseSoundEffectsOutput;
  };
  "fal-ai/pixverse/swap": {
    input: PixverseSwapInput;
    output: PixverseSwapOutput;
  };
  "fal-ai/pixverse/v3.5/image-to-video": {
    input: PixverseV35ImageToVideoInput;
    output: PixverseV35ImageToVideoOutput;
  };
  "fal-ai/pixverse/v3.5/text-to-video": {
    input: PixverseV35TextToVideoInput;
    output: PixverseV35TextToVideoOutput;
  };
  "fal-ai/pixverse/v3.5/text-to-video/fast": {
    input: PixverseV35TextToVideoFastInput;
    output: PixverseV35TextToVideoFastOutput;
  };
  "fal-ai/pixverse/v4.5/effects": {
    input: PixverseV45EffectsInput;
    output: PixverseV45EffectsOutput;
  };
  "fal-ai/pixverse/v4.5/image-to-video": {
    input: PixverseV45ImageToVideoInput;
    output: PixverseV45ImageToVideoOutput;
  };
  "fal-ai/pixverse/v4.5/image-to-video/fast": {
    input: PixverseV45ImageToVideoFastInput;
    output: PixverseV45ImageToVideoFastOutput;
  };
  "fal-ai/pixverse/v4.5/text-to-video": {
    input: PixverseV45TextToVideoInput;
    output: PixverseV45TextToVideoOutput;
  };
  "fal-ai/pixverse/v4.5/text-to-video/fast": {
    input: PixverseV45TextToVideoFastInput;
    output: PixverseV45TextToVideoFastOutput;
  };
  "fal-ai/pixverse/v4.5/transition": {
    input: PixverseV45TransitionInput;
    output: PixverseV45TransitionOutput;
  };
  "fal-ai/pixverse/v4/effects": {
    input: PixverseV4EffectsInput;
    output: PixverseV4EffectsOutput;
  };
  "fal-ai/pixverse/v4/image-to-video": {
    input: PixverseV4ImageToVideoInput;
    output: PixverseV4ImageToVideoOutput;
  };
  "fal-ai/pixverse/v4/image-to-video/fast": {
    input: PixverseV4ImageToVideoFastInput;
    output: PixverseV4ImageToVideoFastOutput;
  };
  "fal-ai/pixverse/v4/text-to-video": {
    input: PixverseV4TextToVideoInput;
    output: PixverseV4TextToVideoOutput;
  };
  "fal-ai/pixverse/v4/text-to-video/fast": {
    input: PixverseV4TextToVideoFastInput;
    output: PixverseV4TextToVideoFastOutput;
  };
  "fal-ai/pixverse/v5.5/effects": {
    input: PixverseV55EffectsInput;
    output: PixverseV55EffectsOutput;
  };
  "fal-ai/pixverse/v5.5/image-to-video": {
    input: PixverseV55ImageToVideoInput;
    output: PixverseV55ImageToVideoOutput;
  };
  "fal-ai/pixverse/v5.5/text-to-video": {
    input: PixverseV55TextToVideoInput;
    output: PixverseV55TextToVideoOutput;
  };
  "fal-ai/pixverse/v5.5/transition": {
    input: PixverseV55TransitionInput;
    output: PixverseV55TransitionOutput;
  };
  "fal-ai/pixverse/v5.6/image-to-video": {
    input: PixverseV56ImageToVideoInput;
    output: PixverseV56ImageToVideoOutput;
  };
  "fal-ai/pixverse/v5.6/text-to-video": {
    input: PixverseV56TextToVideoInput;
    output: PixverseV56TextToVideoOutput;
  };
  "fal-ai/pixverse/v5.6/transition": {
    input: PixverseV56TransitionInput;
    output: PixverseV56TransitionOutput;
  };
  "fal-ai/pixverse/v5/effects": {
    input: PixverseV5EffectsInput;
    output: PixverseV5EffectsOutput;
  };
  "fal-ai/pixverse/v5/image-to-video": {
    input: PixverseV5ImageToVideoInput;
    output: PixverseV5ImageToVideoOutput;
  };
  "fal-ai/pixverse/v5/text-to-video": {
    input: PixverseV5TextToVideoInput;
    output: PixverseV5TextToVideoOutput;
  };
  "fal-ai/pixverse/v5/transition": {
    input: PixverseV5TransitionInput;
    output: PixverseV5TransitionOutput;
  };
  "fal-ai/pixverse/v6/extend": {
    input: PixverseV6ExtendInput;
    output: PixverseV6ExtendOutput;
  };
  "fal-ai/pixverse/v6/image-to-video": {
    input: PixverseV6ImageToVideoInput;
    output: PixverseV6ImageToVideoOutput;
  };
  "fal-ai/pixverse/v6/text-to-video": {
    input: PixverseV6TextToVideoInput;
    output: PixverseV6TextToVideoOutput;
  };
  "fal-ai/pixverse/v6/transition": {
    input: PixverseV6TransitionInput;
    output: PixverseV6TransitionOutput;
  };
  "fal-ai/playground-v25": {
    input: PlaygroundV25Input;
    output: PlaygroundV25Output;
  };
  "fal-ai/playground-v25/image-to-image": {
    input: PlaygroundV25ImageToImageInput;
    output: PlaygroundV25ImageToImageOutput;
  };
  "fal-ai/playground-v25/inpainting": {
    input: PlaygroundV25InpaintingInput;
    output: PlaygroundV25InpaintingOutput;
  };
  "fal-ai/plushify": {
    input: plushifyInput;
    output: plushifyOutput;
  };
  "fal-ai/pony-v7": {
    input: PonyV7Input;
    output: PonyV7Output;
  };
  "fal-ai/post-processing": {
    input: PostProcessingInput;
    output: PostProcessingOutput;
  };
  "fal-ai/post-processing/chromatic-aberration": {
    input: PostProcessingChromaticAberrationInput;
    output: PostProcessingChromaticAberrationOutput;
  };
  "fal-ai/post-processing/color-correction": {
    input: PostProcessingColorCorrectionInput;
    output: PostProcessingColorCorrectionOutput;
  };
  "fal-ai/post-processing/color-tint": {
    input: PostProcessingColorTintInput;
    output: PostProcessingColorTintOutput;
  };
  "fal-ai/post-processing/desaturate": {
    input: PostProcessingDesaturateInput;
    output: PostProcessingDesaturateOutput;
  };
  "fal-ai/post-processing/dissolve": {
    input: PostProcessingDissolveInput;
    output: PostProcessingDissolveOutput;
  };
  "fal-ai/post-processing/dodge-burn": {
    input: PostProcessingDodgeBurnInput;
    output: PostProcessingDodgeBurnOutput;
  };
  "fal-ai/post-processing/grain": {
    input: PostProcessingGrainInput;
    output: PostProcessingGrainOutput;
  };
  "fal-ai/post-processing/parabolize": {
    input: PostProcessingParabolizeInput;
    output: PostProcessingParabolizeOutput;
  };
  "fal-ai/post-processing/sharpen": {
    input: PostProcessingSharpenInput;
    output: PostProcessingSharpenOutput;
  };
  "fal-ai/post-processing/solarize": {
    input: PostProcessingSolarizeInput;
    output: PostProcessingSolarizeOutput;
  };
  "fal-ai/post-processing/vignette": {
    input: PostProcessingVignetteInput;
    output: PostProcessingVignetteOutput;
  };
  "fal-ai/pshuman": {
    input: pshumanInput;
    output: pshumanOutput;
  };
  "fal-ai/pulid": {
    input: pulidInput;
    output: pulidOutput;
  };
  "fal-ai/qwen-3-guard": {
    input: Qwen3GuardInput;
    output: Qwen3GuardOutput;
  };
  "fal-ai/qwen-3-tts/clone-voice/0.6b": {
    input: Qwen3TtsCloneVoice06bInput;
    output: Qwen3TtsCloneVoice06bOutput;
  };
  "fal-ai/qwen-3-tts/clone-voice/1.7b": {
    input: Qwen3TtsCloneVoice17bInput;
    output: Qwen3TtsCloneVoice17bOutput;
  };
  "fal-ai/qwen-3-tts/text-to-speech/0.6b": {
    input: Qwen3TtsTextToSpeech06bInput;
    output: Qwen3TtsTextToSpeech06bOutput;
  };
  "fal-ai/qwen-3-tts/text-to-speech/1.7b": {
    input: Qwen3TtsTextToSpeech17bInput;
    output: Qwen3TtsTextToSpeech17bOutput;
  };
  "fal-ai/qwen-3-tts/voice-design/1.7b": {
    input: Qwen3TtsVoiceDesign17bInput;
    output: Qwen3TtsVoiceDesign17bOutput;
  };
  "fal-ai/qwen-image": {
    input: QwenImageInput;
    output: QwenImageOutput;
  };
  "fal-ai/qwen-image-2/edit": {
    input: QwenImage2EditInput;
    output: QwenImage2EditOutput;
  };
  "fal-ai/qwen-image-2/pro/edit": {
    input: QwenImage2ProEditInput;
    output: QwenImage2ProEditOutput;
  };
  "fal-ai/qwen-image-2/pro/text-to-image": {
    input: QwenImage2ProTextToImageInput;
    output: QwenImage2ProTextToImageOutput;
  };
  "fal-ai/qwen-image-2/text-to-image": {
    input: QwenImage2TextToImageInput;
    output: QwenImage2TextToImageOutput;
  };
  "fal-ai/qwen-image-2512": {
    input: QwenImage2512Input;
    output: QwenImage2512Output;
  };
  "fal-ai/qwen-image-2512-trainer": {
    input: QwenImage2512TrainerInput;
    output: QwenImage2512TrainerOutput;
  };
  "fal-ai/qwen-image-2512-trainer-v2": {
    input: QwenImage2512TrainerV2Input;
    output: QwenImage2512TrainerV2Output;
  };
  "fal-ai/qwen-image-2512/lora": {
    input: QwenImage2512LoraInput;
    output: QwenImage2512LoraOutput;
  };
  "fal-ai/qwen-image-edit": {
    input: QwenImageEditInput;
    output: QwenImageEditOutput;
  };
  "fal-ai/qwen-image-edit-2509": {
    input: QwenImageEdit2509Input;
    output: QwenImageEdit2509Output;
  };
  "fal-ai/qwen-image-edit-2509-lora": {
    input: QwenImageEdit2509LoraInput;
    output: QwenImageEdit2509LoraOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/add-background": {
    input: QwenImageEdit2509LoraGalleryAddBackgroundInput;
    output: QwenImageEdit2509LoraGalleryAddBackgroundOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/face-to-full-portrait": {
    input: QwenImageEdit2509LoraGalleryFaceToFullPortraitInput;
    output: QwenImageEdit2509LoraGalleryFaceToFullPortraitOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/group-photo": {
    input: QwenImageEdit2509LoraGalleryGroupPhotoInput;
    output: QwenImageEdit2509LoraGalleryGroupPhotoOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/integrate-product": {
    input: QwenImageEdit2509LoraGalleryIntegrateProductInput;
    output: QwenImageEdit2509LoraGalleryIntegrateProductOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/lighting-restoration": {
    input: QwenImageEdit2509LoraGalleryLightingRestorationInput;
    output: QwenImageEdit2509LoraGalleryLightingRestorationOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/multiple-angles": {
    input: QwenImageEdit2509LoraGalleryMultipleAnglesInput;
    output: QwenImageEdit2509LoraGalleryMultipleAnglesOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/next-scene": {
    input: QwenImageEdit2509LoraGalleryNextSceneInput;
    output: QwenImageEdit2509LoraGalleryNextSceneOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/remove-element": {
    input: QwenImageEdit2509LoraGalleryRemoveElementInput;
    output: QwenImageEdit2509LoraGalleryRemoveElementOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/remove-lighting": {
    input: QwenImageEdit2509LoraGalleryRemoveLightingInput;
    output: QwenImageEdit2509LoraGalleryRemoveLightingOutput;
  };
  "fal-ai/qwen-image-edit-2509-lora-gallery/shirt-design": {
    input: QwenImageEdit2509LoraGalleryShirtDesignInput;
    output: QwenImageEdit2509LoraGalleryShirtDesignOutput;
  };
  "fal-ai/qwen-image-edit-2509-trainer": {
    input: QwenImageEdit2509TrainerInput;
    output: QwenImageEdit2509TrainerOutput;
  };
  "fal-ai/qwen-image-edit-2511": {
    input: QwenImageEdit2511Input;
    output: QwenImageEdit2511Output;
  };
  "fal-ai/qwen-image-edit-2511-multiple-angles": {
    input: QwenImageEdit2511MultipleAnglesInput;
    output: QwenImageEdit2511MultipleAnglesOutput;
  };
  "fal-ai/qwen-image-edit-2511-trainer": {
    input: QwenImageEdit2511TrainerInput;
    output: QwenImageEdit2511TrainerOutput;
  };
  "fal-ai/qwen-image-edit-2511/lora": {
    input: QwenImageEdit2511LoraInput;
    output: QwenImageEdit2511LoraOutput;
  };
  "fal-ai/qwen-image-edit-lora": {
    input: QwenImageEditLoraInput;
    output: QwenImageEditLoraOutput;
  };
  "fal-ai/qwen-image-edit-plus": {
    input: QwenImageEditPlusInput;
    output: QwenImageEditPlusOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora": {
    input: QwenImageEditPlusLoraInput;
    output: QwenImageEditPlusLoraOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/add-background": {
    input: QwenImageEditPlusLoraGalleryAddBackgroundInput;
    output: QwenImageEditPlusLoraGalleryAddBackgroundOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/face-to-full-portrait": {
    input: QwenImageEditPlusLoraGalleryFaceToFullPortraitInput;
    output: QwenImageEditPlusLoraGalleryFaceToFullPortraitOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/group-photo": {
    input: QwenImageEditPlusLoraGalleryGroupPhotoInput;
    output: QwenImageEditPlusLoraGalleryGroupPhotoOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/integrate-product": {
    input: QwenImageEditPlusLoraGalleryIntegrateProductInput;
    output: QwenImageEditPlusLoraGalleryIntegrateProductOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/lighting-restoration": {
    input: QwenImageEditPlusLoraGalleryLightingRestorationInput;
    output: QwenImageEditPlusLoraGalleryLightingRestorationOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/multiple-angles": {
    input: QwenImageEditPlusLoraGalleryMultipleAnglesInput;
    output: QwenImageEditPlusLoraGalleryMultipleAnglesOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/next-scene": {
    input: QwenImageEditPlusLoraGalleryNextSceneInput;
    output: QwenImageEditPlusLoraGalleryNextSceneOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/remove-element": {
    input: QwenImageEditPlusLoraGalleryRemoveElementInput;
    output: QwenImageEditPlusLoraGalleryRemoveElementOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/remove-lighting": {
    input: QwenImageEditPlusLoraGalleryRemoveLightingInput;
    output: QwenImageEditPlusLoraGalleryRemoveLightingOutput;
  };
  "fal-ai/qwen-image-edit-plus-lora-gallery/shirt-design": {
    input: QwenImageEditPlusLoraGalleryShirtDesignInput;
    output: QwenImageEditPlusLoraGalleryShirtDesignOutput;
  };
  "fal-ai/qwen-image-edit-plus-trainer": {
    input: QwenImageEditPlusTrainerInput;
    output: QwenImageEditPlusTrainerOutput;
  };
  "fal-ai/qwen-image-edit-trainer": {
    input: QwenImageEditTrainerInput;
    output: QwenImageEditTrainerOutput;
  };
  "fal-ai/qwen-image-edit/image-to-image": {
    input: QwenImageEditImageToImageInput;
    output: QwenImageEditImageToImageOutput;
  };
  "fal-ai/qwen-image-edit/inpaint": {
    input: QwenImageEditInpaintInput;
    output: QwenImageEditInpaintOutput;
  };
  "fal-ai/qwen-image-layered": {
    input: QwenImageLayeredInput;
    output: QwenImageLayeredOutput;
  };
  "fal-ai/qwen-image-layered-trainer": {
    input: QwenImageLayeredTrainerInput;
    output: QwenImageLayeredTrainerOutput;
  };
  "fal-ai/qwen-image-layered/lora": {
    input: QwenImageLayeredLoraInput;
    output: QwenImageLayeredLoraOutput;
  };
  "fal-ai/qwen-image-max/edit": {
    input: QwenImageMaxEditInput;
    output: QwenImageMaxEditOutput;
  };
  "fal-ai/qwen-image-max/text-to-image": {
    input: QwenImageMaxTextToImageInput;
    output: QwenImageMaxTextToImageOutput;
  };
  "fal-ai/qwen-image-trainer": {
    input: QwenImageTrainerInput;
    output: QwenImageTrainerOutput;
  };
  "fal-ai/qwen-image-trainer-v2": {
    input: QwenImageTrainerV2Input;
    output: QwenImageTrainerV2Output;
  };
  "fal-ai/qwen-image/image-to-image": {
    input: QwenImageImageToImageInput;
    output: QwenImageImageToImageOutput;
  };
  "fal-ai/realistic-vision": {
    input: RealisticVisionInput;
    output: RealisticVisionOutput;
  };
  "fal-ai/recraft-20b": {
    input: Recraft20bInput;
    output: Recraft20bOutput;
  };
  "fal-ai/recraft/upscale/creative": {
    input: RecraftUpscaleCreativeInput;
    output: RecraftUpscaleCreativeOutput;
  };
  "fal-ai/recraft/upscale/crisp": {
    input: RecraftUpscaleCrispInput;
    output: RecraftUpscaleCrispOutput;
  };
  "fal-ai/recraft/v3/create-style": {
    input: RecraftV3CreateStyleInput;
    output: RecraftV3CreateStyleOutput;
  };
  "fal-ai/recraft/v3/image-to-image": {
    input: RecraftV3ImageToImageInput;
    output: RecraftV3ImageToImageOutput;
  };
  "fal-ai/recraft/v3/text-to-image": {
    input: RecraftV3TextToImageInput;
    output: RecraftV3TextToImageOutput;
  };
  "fal-ai/recraft/v4/pro/text-to-image": {
    input: RecraftV4ProTextToImageInput;
    output: RecraftV4ProTextToImageOutput;
  };
  "fal-ai/recraft/v4/pro/text-to-vector": {
    input: RecraftV4ProTextToVectorInput;
    output: RecraftV4ProTextToVectorOutput;
  };
  "fal-ai/recraft/v4/text-to-image": {
    input: RecraftV4TextToImageInput;
    output: RecraftV4TextToImageOutput;
  };
  "fal-ai/recraft/v4/text-to-vector": {
    input: RecraftV4TextToVectorInput;
    output: RecraftV4TextToVectorOutput;
  };
  "fal-ai/recraft/vectorize": {
    input: RecraftVectorizeInput;
    output: RecraftVectorizeOutput;
  };
  "fal-ai/retoucher": {
    input: retoucherInput;
    output: retoucherOutput;
  };
  "fal-ai/rife": {
    input: rifeInput;
    output: rifeOutput;
  };
  "fal-ai/rife/video": {
    input: RifeVideoInput;
    output: RifeVideoOutput;
  };
  "fal-ai/sa2va/4b/image": {
    input: Sa2va4bImageInput;
    output: Sa2va4bImageOutput;
  };
  "fal-ai/sa2va/4b/video": {
    input: Sa2va4bVideoInput;
    output: Sa2va4bVideoOutput;
  };
  "fal-ai/sa2va/8b/image": {
    input: Sa2va8bImageInput;
    output: Sa2va8bImageOutput;
  };
  "fal-ai/sa2va/8b/video": {
    input: Sa2va8bVideoInput;
    output: Sa2va8bVideoOutput;
  };
  "fal-ai/sadtalker": {
    input: sadtalkerInput;
    output: sadtalkerOutput;
  };
  "fal-ai/sadtalker/reference": {
    input: SadtalkerReferenceInput;
    output: SadtalkerReferenceOutput;
  };
  "fal-ai/sam-3-1/image": {
    input: Sam31ImageInput;
    output: Sam31ImageOutput;
  };
  "fal-ai/sam-3-1/image-rle": {
    input: Sam31ImageRleInput;
    output: Sam31ImageRleOutput;
  };
  "fal-ai/sam-3-1/video": {
    input: Sam31VideoInput;
    output: Sam31VideoOutput;
  };
  "fal-ai/sam-3-1/video-rle": {
    input: Sam31VideoRleInput;
    output: Sam31VideoRleOutput;
  };
  "fal-ai/sam-3/3d-align": {
    input: Sam33dAlignInput;
    output: Sam33dAlignOutput;
  };
  "fal-ai/sam-3/3d-body": {
    input: Sam33dBodyInput;
    output: Sam33dBodyOutput;
  };
  "fal-ai/sam-3/3d-objects": {
    input: Sam33dObjectsInput;
    output: Sam33dObjectsOutput;
  };
  "fal-ai/sam-3/image": {
    input: Sam3ImageInput;
    output: Sam3ImageOutput;
  };
  "fal-ai/sam-3/image-rle": {
    input: Sam3ImageRleInput;
    output: Sam3ImageRleOutput;
  };
  "fal-ai/sam-3/image/embed": {
    input: Sam3ImageEmbedInput;
    output: Sam3ImageEmbedOutput;
  };
  "fal-ai/sam-3/video": {
    input: Sam3VideoInput;
    output: Sam3VideoOutput;
  };
  "fal-ai/sam-3/video-rle": {
    input: Sam3VideoRleInput;
    output: Sam3VideoRleOutput;
  };
  "fal-ai/sam-audio/separate": {
    input: SamAudioSeparateInput;
    output: SamAudioSeparateOutput;
  };
  "fal-ai/sam-audio/span-separate": {
    input: SamAudioSpanSeparateInput;
    output: SamAudioSpanSeparateOutput;
  };
  "fal-ai/sam-audio/visual-separate": {
    input: SamAudioVisualSeparateInput;
    output: SamAudioVisualSeparateOutput;
  };
  "fal-ai/sam2/auto-segment": {
    input: Sam2AutoSegmentInput;
    output: Sam2AutoSegmentOutput;
  };
  "fal-ai/sam2/image": {
    input: Sam2ImageInput;
    output: Sam2ImageOutput;
  };
  "fal-ai/sam2/video": {
    input: Sam2VideoInput;
    output: Sam2VideoOutput;
  };
  "fal-ai/sana": {
    input: sanaInput;
    output: sanaOutput;
  };
  "fal-ai/sana-video": {
    input: SanaVideoInput;
    output: SanaVideoOutput;
  };
  "fal-ai/sana/sprint": {
    input: SanaSprintInput;
    output: SanaSprintOutput;
  };
  "fal-ai/sana/v1.5/1.6b": {
    input: SanaV1516bInput;
    output: SanaV1516bOutput;
  };
  "fal-ai/sana/v1.5/4.8b": {
    input: SanaV1548bInput;
    output: SanaV1548bOutput;
  };
  "fal-ai/scail": {
    input: scailInput;
    output: scailOutput;
  };
  "fal-ai/sd15-depth-controlnet": {
    input: Sd15DepthControlnetInput;
    output: Sd15DepthControlnetOutput;
  };
  "fal-ai/sdxl-controlnet-union": {
    input: SdxlControlnetUnionInput;
    output: SdxlControlnetUnionOutput;
  };
  "fal-ai/sdxl-controlnet-union/image-to-image": {
    input: SdxlControlnetUnionImageToImageInput;
    output: SdxlControlnetUnionImageToImageOutput;
  };
  "fal-ai/sdxl-controlnet-union/inpainting": {
    input: SdxlControlnetUnionInpaintingInput;
    output: SdxlControlnetUnionInpaintingOutput;
  };
  "fal-ai/seedvr/upscale/image": {
    input: SeedvrUpscaleImageInput;
    output: SeedvrUpscaleImageOutput;
  };
  "fal-ai/seedvr/upscale/image/seamless": {
    input: SeedvrUpscaleImageSeamlessInput;
    output: SeedvrUpscaleImageSeamlessOutput;
  };
  "fal-ai/seedvr/upscale/video": {
    input: SeedvrUpscaleVideoInput;
    output: SeedvrUpscaleVideoOutput;
  };
  "fal-ai/silero-vad": {
    input: SileroVadInput;
    output: SileroVadOutput;
  };
  "fal-ai/skyreels-i2v": {
    input: SkyreelsI2vInput;
    output: SkyreelsI2vOutput;
  };
  "fal-ai/smart-turn": {
    input: SmartTurnInput;
    output: SmartTurnOutput;
  };
  "fal-ai/sora-2/characters": {
    input: Sora2CharactersInput;
    output: Sora2CharactersOutput;
  };
  "fal-ai/sora-2/image-to-video": {
    input: Sora2ImageToVideoInput;
    output: Sora2ImageToVideoOutput;
  };
  "fal-ai/sora-2/image-to-video/pro": {
    input: Sora2ImageToVideoProInput;
    output: Sora2ImageToVideoProOutput;
  };
  "fal-ai/sora-2/text-to-video": {
    input: Sora2TextToVideoInput;
    output: Sora2TextToVideoOutput;
  };
  "fal-ai/sora-2/text-to-video/pro": {
    input: Sora2TextToVideoProInput;
    output: Sora2TextToVideoProOutput;
  };
  "fal-ai/sora-2/video-to-video/remix": {
    input: Sora2VideoToVideoRemixInput;
    output: Sora2VideoToVideoRemixOutput;
  };
  "fal-ai/speech-to-text": {
    input: SpeechToTextInput;
    output: SpeechToTextOutput;
  };
  "fal-ai/speech-to-text/turbo": {
    input: SpeechToTextTurboInput;
    output: SpeechToTextTurboOutput;
  };
  "fal-ai/stable-audio": {
    input: StableAudioInput;
    output: StableAudioOutput;
  };
  "fal-ai/stable-audio-25/audio-to-audio": {
    input: StableAudio25AudioToAudioInput;
    output: StableAudio25AudioToAudioOutput;
  };
  "fal-ai/stable-audio-25/inpaint": {
    input: StableAudio25InpaintInput;
    output: StableAudio25InpaintOutput;
  };
  "fal-ai/stable-audio-25/text-to-audio": {
    input: StableAudio25TextToAudioInput;
    output: StableAudio25TextToAudioOutput;
  };
  "fal-ai/stable-avatar": {
    input: StableAvatarInput;
    output: StableAvatarOutput;
  };
  "fal-ai/stable-cascade": {
    input: StableCascadeInput;
    output: StableCascadeOutput;
  };
  "fal-ai/stable-cascade/sote-diffusion": {
    input: StableCascadeSoteDiffusionInput;
    output: StableCascadeSoteDiffusionOutput;
  };
  "fal-ai/stable-diffusion-v15": {
    input: StableDiffusionV15Input;
    output: StableDiffusionV15Output;
  };
  "fal-ai/stable-diffusion-v3-medium": {
    input: StableDiffusionV3MediumInput;
    output: StableDiffusionV3MediumOutput;
  };
  "fal-ai/stable-diffusion-v3-medium/image-to-image": {
    input: StableDiffusionV3MediumImageToImageInput;
    output: StableDiffusionV3MediumImageToImageOutput;
  };
  "fal-ai/stable-diffusion-v35-large": {
    input: StableDiffusionV35LargeInput;
    output: StableDiffusionV35LargeOutput;
  };
  "fal-ai/stable-diffusion-v35-medium": {
    input: StableDiffusionV35MediumInput;
    output: StableDiffusionV35MediumOutput;
  };
  "fal-ai/stable-video": {
    input: StableVideoInput;
    output: StableVideoOutput;
  };
  "fal-ai/star-vector": {
    input: StarVectorInput;
    output: StarVectorOutput;
  };
  "fal-ai/steady-dancer": {
    input: SteadyDancerInput;
    output: SteadyDancerOutput;
  };
  "fal-ai/step1x-edit": {
    input: Step1xEditInput;
    output: Step1xEditOutput;
  };
  "fal-ai/stepx-edit2": {
    input: StepxEdit2Input;
    output: StepxEdit2Output;
  };
  "fal-ai/swin2sr": {
    input: swin2srInput;
    output: swin2srOutput;
  };
  "fal-ai/switti": {
    input: swittiInput;
    output: swittiOutput;
  };
  "fal-ai/switti/512": {
    input: Switti512Input;
    output: Switti512Output;
  };
  "fal-ai/sync-lipsync": {
    input: SyncLipsyncInput;
    output: SyncLipsyncOutput;
  };
  "fal-ai/sync-lipsync/react-1": {
    input: SyncLipsyncReact1Input;
    output: SyncLipsyncReact1Output;
  };
  "fal-ai/sync-lipsync/v2": {
    input: SyncLipsyncV2Input;
    output: SyncLipsyncV2Output;
  };
  "fal-ai/sync-lipsync/v2/pro": {
    input: SyncLipsyncV2ProInput;
    output: SyncLipsyncV2ProOutput;
  };
  "fal-ai/t2v-turbo": {
    input: T2vTurboInput;
    output: T2vTurboOutput;
  };
  "fal-ai/tada/1b/text-to-speech": {
    input: Tada1bTextToSpeechInput;
    output: Tada1bTextToSpeechOutput;
  };
  "fal-ai/tada/3b/text-to-speech": {
    input: Tada3bTextToSpeechInput;
    output: Tada3bTextToSpeechOutput;
  };
  "fal-ai/thera": {
    input: theraInput;
    output: theraOutput;
  };
  "fal-ai/thinksound": {
    input: thinksoundInput;
    output: thinksoundOutput;
  };
  "fal-ai/thinksound/audio": {
    input: ThinksoundAudioInput;
    output: ThinksoundAudioOutput;
  };
  "fal-ai/topaz/upscale/image": {
    input: TopazUpscaleImageInput;
    output: TopazUpscaleImageOutput;
  };
  "fal-ai/topaz/upscale/video": {
    input: TopazUpscaleVideoInput;
    output: TopazUpscaleVideoOutput;
  };
  "fal-ai/transpixar": {
    input: transpixarInput;
    output: transpixarOutput;
  };
  "fal-ai/trellis": {
    input: trellisInput;
    output: trellisOutput;
  };
  "fal-ai/trellis-2": {
    input: Trellis2Input;
    output: Trellis2Output;
  };
  "fal-ai/trellis-2/retexture": {
    input: Trellis2RetextureInput;
    output: Trellis2RetextureOutput;
  };
  "fal-ai/trellis/multi": {
    input: TrellisMultiInput;
    output: TrellisMultiOutput;
  };
  "fal-ai/triposr": {
    input: triposrInput;
    output: triposrOutput;
  };
  "fal-ai/turbo-flux-trainer": {
    input: TurboFluxTrainerInput;
    output: TurboFluxTrainerOutput;
  };
  "fal-ai/ultrashape": {
    input: ultrashapeInput;
    output: ultrashapeOutput;
  };
  "fal-ai/uno": {
    input: unoInput;
    output: unoOutput;
  };
  "fal-ai/uso": {
    input: usoInput;
    output: usoOutput;
  };
  "fal-ai/vecglypher": {
    input: vecglypherInput;
    output: vecglypherOutput;
  };
  "fal-ai/vecglypher/image-to-svg": {
    input: VecglypherImageToSvgInput;
    output: VecglypherImageToSvgOutput;
  };
  "fal-ai/veo2": {
    input: veo2Input;
    output: veo2Output;
  };
  "fal-ai/veo2/image-to-video": {
    input: Veo2ImageToVideoInput;
    output: Veo2ImageToVideoOutput;
  };
  "fal-ai/veo3": {
    input: veo3Input;
    output: veo3Output;
  };
  "fal-ai/veo3.1": {
    input: Veo31Input;
    output: Veo31Output;
  };
  "fal-ai/veo3.1/extend-video": {
    input: Veo31ExtendVideoInput;
    output: Veo31ExtendVideoOutput;
  };
  "fal-ai/veo3.1/fast": {
    input: Veo31FastInput;
    output: Veo31FastOutput;
  };
  "fal-ai/veo3.1/fast/extend-video": {
    input: Veo31FastExtendVideoInput;
    output: Veo31FastExtendVideoOutput;
  };
  "fal-ai/veo3.1/fast/first-last-frame-to-video": {
    input: Veo31FastFirstLastFrameToVideoInput;
    output: Veo31FastFirstLastFrameToVideoOutput;
  };
  "fal-ai/veo3.1/fast/image-to-video": {
    input: Veo31FastImageToVideoInput;
    output: Veo31FastImageToVideoOutput;
  };
  "fal-ai/veo3.1/first-last-frame-to-video": {
    input: Veo31FirstLastFrameToVideoInput;
    output: Veo31FirstLastFrameToVideoOutput;
  };
  "fal-ai/veo3.1/image-to-video": {
    input: Veo31ImageToVideoInput;
    output: Veo31ImageToVideoOutput;
  };
  "fal-ai/veo3.1/lite": {
    input: Veo31LiteInput;
    output: Veo31LiteOutput;
  };
  "fal-ai/veo3.1/lite/first-last-frame-to-video": {
    input: Veo31LiteFirstLastFrameToVideoInput;
    output: Veo31LiteFirstLastFrameToVideoOutput;
  };
  "fal-ai/veo3.1/lite/image-to-video": {
    input: Veo31LiteImageToVideoInput;
    output: Veo31LiteImageToVideoOutput;
  };
  "fal-ai/veo3.1/reference-to-video": {
    input: Veo31ReferenceToVideoInput;
    output: Veo31ReferenceToVideoOutput;
  };
  "fal-ai/veo3/fast": {
    input: Veo3FastInput;
    output: Veo3FastOutput;
  };
  "fal-ai/veo3/fast/image-to-video": {
    input: Veo3FastImageToVideoInput;
    output: Veo3FastImageToVideoOutput;
  };
  "fal-ai/veo3/image-to-video": {
    input: Veo3ImageToVideoInput;
    output: Veo3ImageToVideoOutput;
  };
  "fal-ai/vibevoice": {
    input: vibevoiceInput;
    output: vibevoiceOutput;
  };
  "fal-ai/vibevoice/0.5b": {
    input: Vibevoice05bInput;
    output: Vibevoice05bOutput;
  };
  "fal-ai/vibevoice/7b": {
    input: Vibevoice7bInput;
    output: Vibevoice7bOutput;
  };
  "fal-ai/video-as-prompt": {
    input: VideoAsPromptInput;
    output: VideoAsPromptOutput;
  };
  "fal-ai/video-prompt-generator": {
    input: VideoPromptGeneratorInput;
    output: VideoPromptGeneratorOutput;
  };
  "fal-ai/video-understanding": {
    input: VideoUnderstandingInput;
    output: VideoUnderstandingOutput;
  };
  "fal-ai/video-upscaler": {
    input: VideoUpscalerInput;
    output: VideoUpscalerOutput;
  };
  "fal-ai/vidu/image-to-video": {
    input: ViduImageToVideoInput;
    output: ViduImageToVideoOutput;
  };
  "fal-ai/vidu/q1/image-to-video": {
    input: ViduQ1ImageToVideoInput;
    output: ViduQ1ImageToVideoOutput;
  };
  "fal-ai/vidu/q1/reference-to-video": {
    input: ViduQ1ReferenceToVideoInput;
    output: ViduQ1ReferenceToVideoOutput;
  };
  "fal-ai/vidu/q1/start-end-to-video": {
    input: ViduQ1StartEndToVideoInput;
    output: ViduQ1StartEndToVideoOutput;
  };
  "fal-ai/vidu/q1/text-to-video": {
    input: ViduQ1TextToVideoInput;
    output: ViduQ1TextToVideoOutput;
  };
  "fal-ai/vidu/q2/image-to-video/pro": {
    input: ViduQ2ImageToVideoProInput;
    output: ViduQ2ImageToVideoProOutput;
  };
  "fal-ai/vidu/q2/image-to-video/turbo": {
    input: ViduQ2ImageToVideoTurboInput;
    output: ViduQ2ImageToVideoTurboOutput;
  };
  "fal-ai/vidu/q2/reference-to-image": {
    input: ViduQ2ReferenceToImageInput;
    output: ViduQ2ReferenceToImageOutput;
  };
  "fal-ai/vidu/q2/reference-to-video/pro": {
    input: ViduQ2ReferenceToVideoProInput;
    output: ViduQ2ReferenceToVideoProOutput;
  };
  "fal-ai/vidu/q2/text-to-image": {
    input: ViduQ2TextToImageInput;
    output: ViduQ2TextToImageOutput;
  };
  "fal-ai/vidu/q2/text-to-video": {
    input: ViduQ2TextToVideoInput;
    output: ViduQ2TextToVideoOutput;
  };
  "fal-ai/vidu/q2/video-extension/pro": {
    input: ViduQ2VideoExtensionProInput;
    output: ViduQ2VideoExtensionProOutput;
  };
  "fal-ai/vidu/q3/image-to-video": {
    input: ViduQ3ImageToVideoInput;
    output: ViduQ3ImageToVideoOutput;
  };
  "fal-ai/vidu/q3/image-to-video/turbo": {
    input: ViduQ3ImageToVideoTurboInput;
    output: ViduQ3ImageToVideoTurboOutput;
  };
  "fal-ai/vidu/q3/text-to-video": {
    input: ViduQ3TextToVideoInput;
    output: ViduQ3TextToVideoOutput;
  };
  "fal-ai/vidu/q3/text-to-video/turbo": {
    input: ViduQ3TextToVideoTurboInput;
    output: ViduQ3TextToVideoTurboOutput;
  };
  "fal-ai/vidu/reference-to-image": {
    input: ViduReferenceToImageInput;
    output: ViduReferenceToImageOutput;
  };
  "fal-ai/vidu/reference-to-video": {
    input: ViduReferenceToVideoInput;
    output: ViduReferenceToVideoOutput;
  };
  "fal-ai/vidu/start-end-to-video": {
    input: ViduStartEndToVideoInput;
    output: ViduStartEndToVideoOutput;
  };
  "fal-ai/vidu/template-to-video": {
    input: ViduTemplateToVideoInput;
    output: ViduTemplateToVideoOutput;
  };
  "fal-ai/wan-22-image-trainer": {
    input: Wan22ImageTrainerInput;
    output: Wan22ImageTrainerOutput;
  };
  "fal-ai/wan-22-trainer/i2v-a14b": {
    input: Wan22TrainerI2vA14bInput;
    output: Wan22TrainerI2vA14bOutput;
  };
  "fal-ai/wan-22-trainer/t2v-a14b": {
    input: Wan22TrainerT2vA14bInput;
    output: Wan22TrainerT2vA14bOutput;
  };
  "fal-ai/wan-22-vace-fun-a14b/depth": {
    input: Wan22VaceFunA14bDepthInput;
    output: Wan22VaceFunA14bDepthOutput;
  };
  "fal-ai/wan-22-vace-fun-a14b/inpainting": {
    input: Wan22VaceFunA14bInpaintingInput;
    output: Wan22VaceFunA14bInpaintingOutput;
  };
  "fal-ai/wan-22-vace-fun-a14b/outpainting": {
    input: Wan22VaceFunA14bOutpaintingInput;
    output: Wan22VaceFunA14bOutpaintingOutput;
  };
  "fal-ai/wan-22-vace-fun-a14b/pose": {
    input: Wan22VaceFunA14bPoseInput;
    output: Wan22VaceFunA14bPoseOutput;
  };
  "fal-ai/wan-22-vace-fun-a14b/reframe": {
    input: Wan22VaceFunA14bReframeInput;
    output: Wan22VaceFunA14bReframeOutput;
  };
  "fal-ai/wan-25-preview/image-to-image": {
    input: Wan25PreviewImageToImageInput;
    output: Wan25PreviewImageToImageOutput;
  };
  "fal-ai/wan-25-preview/image-to-video": {
    input: Wan25PreviewImageToVideoInput;
    output: Wan25PreviewImageToVideoOutput;
  };
  "fal-ai/wan-25-preview/text-to-image": {
    input: Wan25PreviewTextToImageInput;
    output: Wan25PreviewTextToImageOutput;
  };
  "fal-ai/wan-25-preview/text-to-video": {
    input: Wan25PreviewTextToVideoInput;
    output: Wan25PreviewTextToVideoOutput;
  };
  "fal-ai/wan-alpha": {
    input: WanAlphaInput;
    output: WanAlphaOutput;
  };
  "fal-ai/wan-ati": {
    input: WanAtiInput;
    output: WanAtiOutput;
  };
  "fal-ai/wan-effects": {
    input: WanEffectsInput;
    output: WanEffectsOutput;
  };
  "fal-ai/wan-flf2v": {
    input: WanFlf2vInput;
    output: WanFlf2vOutput;
  };
  "fal-ai/wan-fun-control": {
    input: WanFunControlInput;
    output: WanFunControlOutput;
  };
  "fal-ai/wan-i2v": {
    input: WanI2vInput;
    output: WanI2vOutput;
  };
  "fal-ai/wan-i2v-lora": {
    input: WanI2vLoraInput;
    output: WanI2vLoraOutput;
  };
  "fal-ai/wan-motion": {
    input: WanMotionInput;
    output: WanMotionOutput;
  };
  "fal-ai/wan-move": {
    input: WanMoveInput;
    output: WanMoveOutput;
  };
  "fal-ai/wan-pro/image-to-video": {
    input: WanProImageToVideoInput;
    output: WanProImageToVideoOutput;
  };
  "fal-ai/wan-pro/text-to-video": {
    input: WanProTextToVideoInput;
    output: WanProTextToVideoOutput;
  };
  "fal-ai/wan-t2v": {
    input: WanT2vInput;
    output: WanT2vOutput;
  };
  "fal-ai/wan-t2v-lora": {
    input: WanT2vLoraInput;
    output: WanT2vLoraOutput;
  };
  "fal-ai/wan-trainer": {
    input: WanTrainerInput;
    output: WanTrainerOutput;
  };
  "fal-ai/wan-trainer/flf2v-720p": {
    input: WanTrainerFlf2v720pInput;
    output: WanTrainerFlf2v720pOutput;
  };
  "fal-ai/wan-trainer/i2v-720p": {
    input: WanTrainerI2v720pInput;
    output: WanTrainerI2v720pOutput;
  };
  "fal-ai/wan-trainer/t2v": {
    input: WanTrainerT2vInput;
    output: WanTrainerT2vOutput;
  };
  "fal-ai/wan-trainer/t2v-14b": {
    input: WanTrainerT2v14bInput;
    output: WanTrainerT2v14bOutput;
  };
  "fal-ai/wan-vace": {
    input: WanVaceInput;
    output: WanVaceOutput;
  };
  "fal-ai/wan-vace-1-3b": {
    input: WanVace13bInput;
    output: WanVace13bOutput;
  };
  "fal-ai/wan-vace-14b": {
    input: WanVace14bInput;
    output: WanVace14bOutput;
  };
  "fal-ai/wan-vace-14b/depth": {
    input: WanVace14bDepthInput;
    output: WanVace14bDepthOutput;
  };
  "fal-ai/wan-vace-14b/inpainting": {
    input: WanVace14bInpaintingInput;
    output: WanVace14bInpaintingOutput;
  };
  "fal-ai/wan-vace-14b/outpainting": {
    input: WanVace14bOutpaintingInput;
    output: WanVace14bOutpaintingOutput;
  };
  "fal-ai/wan-vace-14b/pose": {
    input: WanVace14bPoseInput;
    output: WanVace14bPoseOutput;
  };
  "fal-ai/wan-vace-14b/reframe": {
    input: WanVace14bReframeInput;
    output: WanVace14bReframeOutput;
  };
  "fal-ai/wan-vace-apps/long-reframe": {
    input: WanVaceAppsLongReframeInput;
    output: WanVaceAppsLongReframeOutput;
  };
  "fal-ai/wan-vace-apps/video-edit": {
    input: WanVaceAppsVideoEditInput;
    output: WanVaceAppsVideoEditOutput;
  };
  "fal-ai/wan-vision-enhancer": {
    input: WanVisionEnhancerInput;
    output: WanVisionEnhancerOutput;
  };
  "fal-ai/wan/v2.2-14b/animate/move": {
    input: WanV2214bAnimateMoveInput;
    output: WanV2214bAnimateMoveOutput;
  };
  "fal-ai/wan/v2.2-14b/animate/replace": {
    input: WanV2214bAnimateReplaceInput;
    output: WanV2214bAnimateReplaceOutput;
  };
  "fal-ai/wan/v2.2-14b/speech-to-video": {
    input: WanV2214bSpeechToVideoInput;
    output: WanV2214bSpeechToVideoOutput;
  };
  "fal-ai/wan/v2.2-5b/image-to-video": {
    input: WanV225bImageToVideoInput;
    output: WanV225bImageToVideoOutput;
  };
  "fal-ai/wan/v2.2-5b/text-to-image": {
    input: WanV225bTextToImageInput;
    output: WanV225bTextToImageOutput;
  };
  "fal-ai/wan/v2.2-5b/text-to-video": {
    input: WanV225bTextToVideoInput;
    output: WanV225bTextToVideoOutput;
  };
  "fal-ai/wan/v2.2-5b/text-to-video/distill": {
    input: WanV225bTextToVideoDistillInput;
    output: WanV225bTextToVideoDistillOutput;
  };
  "fal-ai/wan/v2.2-5b/text-to-video/fast-wan": {
    input: WanV225bTextToVideoFastWanInput;
    output: WanV225bTextToVideoFastWanOutput;
  };
  "fal-ai/wan/v2.2-a14b/image-to-image": {
    input: WanV22A14bImageToImageInput;
    output: WanV22A14bImageToImageOutput;
  };
  "fal-ai/wan/v2.2-a14b/image-to-video/lora": {
    input: WanV22A14bImageToVideoLoraInput;
    output: WanV22A14bImageToVideoLoraOutput;
  };
  "fal-ai/wan/v2.2-a14b/image-to-video/turbo": {
    input: WanV22A14bImageToVideoTurboInput;
    output: WanV22A14bImageToVideoTurboOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-image": {
    input: WanV22A14bTextToImageInput;
    output: WanV22A14bTextToImageOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-image/lora": {
    input: WanV22A14bTextToImageLoraInput;
    output: WanV22A14bTextToImageLoraOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-video": {
    input: WanV22A14bTextToVideoInput;
    output: WanV22A14bTextToVideoOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-video/lora": {
    input: WanV22A14bTextToVideoLoraInput;
    output: WanV22A14bTextToVideoLoraOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-video/turbo": {
    input: WanV22A14bTextToVideoTurboInput;
    output: WanV22A14bTextToVideoTurboOutput;
  };
  "fal-ai/wan/v2.2-a14b/video-to-video": {
    input: WanV22A14bVideoToVideoInput;
    output: WanV22A14bVideoToVideoOutput;
  };
  "fal-ai/wan/v2.7/edit": {
    input: WanV27EditInput;
    output: WanV27EditOutput;
  };
  "fal-ai/wan/v2.7/edit-video": {
    input: WanV27EditVideoInput;
    output: WanV27EditVideoOutput;
  };
  "fal-ai/wan/v2.7/image-to-video": {
    input: WanV27ImageToVideoInput;
    output: WanV27ImageToVideoOutput;
  };
  "fal-ai/wan/v2.7/pro/edit": {
    input: WanV27ProEditInput;
    output: WanV27ProEditOutput;
  };
  "fal-ai/wan/v2.7/pro/text-to-image": {
    input: WanV27ProTextToImageInput;
    output: WanV27ProTextToImageOutput;
  };
  "fal-ai/wan/v2.7/reference-to-video": {
    input: WanV27ReferenceToVideoInput;
    output: WanV27ReferenceToVideoOutput;
  };
  "fal-ai/wan/v2.7/text-to-image": {
    input: WanV27TextToImageInput;
    output: WanV27TextToImageOutput;
  };
  "fal-ai/wan/v2.7/text-to-video": {
    input: WanV27TextToVideoInput;
    output: WanV27TextToVideoOutput;
  };
  "fal-ai/whisper": {
    input: whisperInput;
    output: whisperOutput;
  };
  "fal-ai/wizper": {
    input: wizperInput;
    output: wizperOutput;
  };
  "fal-ai/workflow-utilities/audio-compressor": {
    input: WorkflowUtilitiesAudioCompressorInput;
    output: WorkflowUtilitiesAudioCompressorOutput;
  };
  "fal-ai/workflow-utilities/auto-subtitle": {
    input: WorkflowUtilitiesAutoSubtitleInput;
    output: WorkflowUtilitiesAutoSubtitleOutput;
  };
  "fal-ai/workflow-utilities/blend-video": {
    input: WorkflowUtilitiesBlendVideoInput;
    output: WorkflowUtilitiesBlendVideoOutput;
  };
  "fal-ai/workflow-utilities/extract-nth-frame": {
    input: WorkflowUtilitiesExtractNthFrameInput;
    output: WorkflowUtilitiesExtractNthFrameOutput;
  };
  "fal-ai/workflow-utilities/impulse-response": {
    input: WorkflowUtilitiesImpulseResponseInput;
    output: WorkflowUtilitiesImpulseResponseOutput;
  };
  "fal-ai/workflow-utilities/reverse-video": {
    input: WorkflowUtilitiesReverseVideoInput;
    output: WorkflowUtilitiesReverseVideoOutput;
  };
  "fal-ai/workflow-utilities/scale-video": {
    input: WorkflowUtilitiesScaleVideoInput;
    output: WorkflowUtilitiesScaleVideoOutput;
  };
  "fal-ai/workflow-utilities/trim-video": {
    input: WorkflowUtilitiesTrimVideoInput;
    output: WorkflowUtilitiesTrimVideoOutput;
  };
  "fal-ai/x-ailab/nsfw": {
    input: XAilabNsfwInput;
    output: XAilabNsfwOutput;
  };
  "fal-ai/yue": {
    input: yueInput;
    output: yueOutput;
  };
  "fal-ai/z-image-base-trainer": {
    input: ZImageBaseTrainerInput;
    output: ZImageBaseTrainerOutput;
  };
  "fal-ai/z-image-trainer": {
    input: ZImageTrainerInput;
    output: ZImageTrainerOutput;
  };
  "fal-ai/z-image-turbo-trainer-v2": {
    input: ZImageTurboTrainerV2Input;
    output: ZImageTurboTrainerV2Output;
  };
  "fal-ai/z-image/base": {
    input: ZImageBaseInput;
    output: ZImageBaseOutput;
  };
  "fal-ai/z-image/base/lora": {
    input: ZImageBaseLoraInput;
    output: ZImageBaseLoraOutput;
  };
  "fal-ai/z-image/turbo": {
    input: ZImageTurboInput;
    output: ZImageTurboOutput;
  };
  "fal-ai/z-image/turbo/controlnet": {
    input: ZImageTurboControlnetInput;
    output: ZImageTurboControlnetOutput;
  };
  "fal-ai/z-image/turbo/controlnet/lora": {
    input: ZImageTurboControlnetLoraInput;
    output: ZImageTurboControlnetLoraOutput;
  };
  "fal-ai/z-image/turbo/image-to-image": {
    input: ZImageTurboImageToImageInput;
    output: ZImageTurboImageToImageOutput;
  };
  "fal-ai/z-image/turbo/image-to-image/lora": {
    input: ZImageTurboImageToImageLoraInput;
    output: ZImageTurboImageToImageLoraOutput;
  };
  "fal-ai/z-image/turbo/inpaint": {
    input: ZImageTurboInpaintInput;
    output: ZImageTurboInpaintOutput;
  };
  "fal-ai/z-image/turbo/inpaint/lora": {
    input: ZImageTurboInpaintLoraInput;
    output: ZImageTurboInpaintLoraOutput;
  };
  "fal-ai/z-image/turbo/lora": {
    input: ZImageTurboLoraInput;
    output: ZImageTurboLoraOutput;
  };
  "fal-ai/z-image/turbo/tiling": {
    input: ZImageTurboTilingInput;
    output: ZImageTurboTilingOutput;
  };
  "fal-ai/z-image/turbo/tiling/lora": {
    input: ZImageTurboTilingLoraInput;
    output: ZImageTurboTilingLoraOutput;
  };
  "fal-ai/zonos": {
    input: zonosInput;
    output: zonosOutput;
  };
  "imagineart/imagineart-1.5-preview/text-to-image": {
    input: Imagineart15PreviewTextToImageInput;
    output: Imagineart15PreviewTextToImageOutput;
  };
  "imagineart/imagineart-1.5-pro-preview/text-to-image": {
    input: Imagineart15ProPreviewTextToImageInput;
    output: Imagineart15ProPreviewTextToImageOutput;
  };
  "mirelo-ai/sfx-v1.5/video-to-audio": {
    input: SfxV15VideoToAudioInput;
    output: SfxV15VideoToAudioOutput;
  };
  "mirelo-ai/sfx-v1.5/video-to-video": {
    input: SfxV15VideoToVideoInput;
    output: SfxV15VideoToVideoOutput;
  };
  "mirelo-ai/sfx-v1/video-to-audio": {
    input: SfxV1VideoToAudioInput;
    output: SfxV1VideoToAudioOutput;
  };
  "mirelo-ai/sfx-v1/video-to-video": {
    input: SfxV1VideoToVideoInput;
    output: SfxV1VideoToVideoOutput;
  };
  "moonvalley/marey/i2v": {
    input: MareyI2vInput;
    output: MareyI2vOutput;
  };
  "moonvalley/marey/motion-transfer": {
    input: MareyMotionTransferInput;
    output: MareyMotionTransferOutput;
  };
  "moonvalley/marey/pose-transfer": {
    input: MareyPoseTransferInput;
    output: MareyPoseTransferOutput;
  };
  "moonvalley/marey/t2v": {
    input: MareyT2vInput;
    output: MareyT2vOutput;
  };
  "openrouter/router": {
    input: routerInput;
    output: routerOutput;
  };
  "openrouter/router/audio": {
    input: RouterAudioInput;
    output: RouterAudioOutput;
  };
  "openrouter/router/video": {
    input: RouterVideoInput;
    output: RouterVideoOutput;
  };
  "openrouter/router/video/enterprise": {
    input: RouterVideoEnterpriseInput;
    output: RouterVideoEnterpriseOutput;
  };
  "openrouter/router/vision": {
    input: RouterVisionInput;
    output: RouterVisionOutput;
  };
  "perceptron/isaac-01": {
    input: Isaac01Input;
    output: Isaac01Output;
  };
  "pixelcut/background-removal": {
    input: BackgroundRemovalInput;
    output: BackgroundRemovalOutput;
  };
  "resemble-ai/chatterboxhd/speech-to-speech": {
    input: ChatterboxhdSpeechToSpeechInput;
    output: ChatterboxhdSpeechToSpeechOutput;
  };
  "resemble-ai/chatterboxhd/text-to-speech": {
    input: ChatterboxhdTextToSpeechInput;
    output: ChatterboxhdTextToSpeechOutput;
  };
  "rundiffusion-fal/juggernaut-flux-lora": {
    input: JuggernautFluxLoraInput;
    output: JuggernautFluxLoraOutput;
  };
  "rundiffusion-fal/juggernaut-flux-lora/inpainting": {
    input: JuggernautFluxLoraInpaintingInput;
    output: JuggernautFluxLoraInpaintingOutput;
  };
  "rundiffusion-fal/juggernaut-flux/base": {
    input: JuggernautFluxBaseInput;
    output: JuggernautFluxBaseOutput;
  };
  "rundiffusion-fal/juggernaut-flux/base/image-to-image": {
    input: JuggernautFluxBaseImageToImageInput;
    output: JuggernautFluxBaseImageToImageOutput;
  };
  "rundiffusion-fal/juggernaut-flux/lightning": {
    input: JuggernautFluxLightningInput;
    output: JuggernautFluxLightningOutput;
  };
  "rundiffusion-fal/juggernaut-flux/pro": {
    input: JuggernautFluxProInput;
    output: JuggernautFluxProOutput;
  };
  "rundiffusion-fal/juggernaut-flux/pro/image-to-image": {
    input: JuggernautFluxProImageToImageInput;
    output: JuggernautFluxProImageToImageOutput;
  };
  "rundiffusion-fal/rundiffusion-photo-flux": {
    input: RundiffusionPhotoFluxInput;
    output: RundiffusionPhotoFluxOutput;
  };
  "simalabs/sima-video-upscaler-lite": {
    input: SimaVideoUpscalerLiteInput;
    output: SimaVideoUpscalerLiteOutput;
  };
  "smoretalk-ai/rembg-enhance": {
    input: RembgEnhanceInput;
    output: RembgEnhanceOutput;
  };
  "sonauto/v2/extend": {
    input: V2ExtendInput;
    output: V2ExtendOutput;
  };
  "sonauto/v2/inpaint": {
    input: V2InpaintInput;
    output: V2InpaintOutput;
  };
  "sonauto/v2/text-to-music": {
    input: V2TextToMusicInput;
    output: V2TextToMusicOutput;
  };
  "tripo3d/tripo/v2.5/image-to-3d": {
    input: TripoV25ImageTo3dInput;
    output: TripoV25ImageTo3dOutput;
  };
  "tripo3d/tripo/v2.5/multiview-to-3d": {
    input: TripoV25MultiviewTo3dInput;
    output: TripoV25MultiviewTo3dOutput;
  };
  "veed/avatars/audio-to-video": {
    input: AvatarsAudioToVideoInput;
    output: AvatarsAudioToVideoOutput;
  };
  "veed/avatars/text-to-video": {
    input: AvatarsTextToVideoInput;
    output: AvatarsTextToVideoOutput;
  };
  "veed/fabric-1.0": {
    input: Fabric10Input;
    output: Fabric10Output;
  };
  "veed/fabric-1.0/fast": {
    input: Fabric10FastInput;
    output: Fabric10FastOutput;
  };
  "veed/fabric-1.0/text": {
    input: Fabric10TextInput;
    output: Fabric10TextOutput;
  };
  "veed/lipsync": {
    input: lipsyncInput;
    output: lipsyncOutput;
  };
  "veed/video-background-removal": {
    input: VideoBackgroundRemovalInput;
    output: VideoBackgroundRemovalOutput;
  };
  "veed/video-background-removal/fast": {
    input: VideoBackgroundRemovalFastInput;
    output: VideoBackgroundRemovalFastOutput;
  };
  "veed/video-background-removal/green-screen": {
    input: VideoBackgroundRemovalGreenScreenInput;
    output: VideoBackgroundRemovalGreenScreenOutput;
  };
  "wan/v2.6/image-to-image": {
    input: V26ImageToImageInput;
    output: V26ImageToImageOutput;
  };
  "wan/v2.6/image-to-video": {
    input: V26ImageToVideoInput;
    output: V26ImageToVideoOutput;
  };
  "wan/v2.6/image-to-video/flash": {
    input: V26ImageToVideoFlashInput;
    output: V26ImageToVideoFlashOutput;
  };
  "wan/v2.6/reference-to-video": {
    input: V26ReferenceToVideoInput;
    output: V26ReferenceToVideoOutput;
  };
  "wan/v2.6/reference-to-video/flash": {
    input: V26ReferenceToVideoFlashInput;
    output: V26ReferenceToVideoFlashOutput;
  };
  "wan/v2.6/text-to-image": {
    input: V26TextToImageInput;
    output: V26TextToImageOutput;
  };
  "wan/v2.6/text-to-video": {
    input: V26TextToVideoInput;
    output: V26TextToVideoOutput;
  };
  "xai/grok-imagine-image": {
    input: GrokImagineImageInput;
    output: GrokImagineImageOutput;
  };
  "xai/grok-imagine-image/edit": {
    input: GrokImagineImageEditInput;
    output: GrokImagineImageEditOutput;
  };
  "xai/grok-imagine-video/edit-video": {
    input: GrokImagineVideoEditVideoInput;
    output: GrokImagineVideoEditVideoOutput;
  };
  "xai/grok-imagine-video/extend-video": {
    input: GrokImagineVideoExtendVideoInput;
    output: GrokImagineVideoExtendVideoOutput;
  };
  "xai/grok-imagine-video/image-to-video": {
    input: GrokImagineVideoImageToVideoInput;
    output: GrokImagineVideoImageToVideoOutput;
  };
  "xai/grok-imagine-video/reference-to-video": {
    input: GrokImagineVideoReferenceToVideoInput;
    output: GrokImagineVideoReferenceToVideoOutput;
  };
  "xai/grok-imagine-video/text-to-video": {
    input: GrokImagineVideoTextToVideoInput;
    output: GrokImagineVideoTextToVideoOutput;
  };
  "xai/tts/v1": {
    input: TtsV1Input;
    output: TtsV1Output;
  };
};
